import { bindStudioOverlayMedia, prepareStudioOverlayMedia } from './studioOverlayMedia';
import StudioSceneActionsPanel from './StudioSceneActionsPanel';
import { prepareStudioSceneActions } from './studioSceneActions';
import { compileStudioAdjustmentStack } from './studioAdjustmentGrade';
/* eslint-disable no-unused-vars, no-control-regex */
import StudioPerspectiveSurface from './StudioPerspectiveSurface';
import StudioProgrammeFrame from './StudioProgrammeFrame';
import { validateStudioPerspectiveLayer, encodeStudioPerspective } from './studioPerspective';
import StudioCompWorkbench from "./StudioCompWorkbench";
import StudioRigController from './StudioRigController';
import StudioNodeFlow from "./StudioNodeFlowPanel";
import {
  buildTrackMatteStageSvgDataUri,
  evaluateLocalLayerPose,
  evaluateWorldLayerPose,
  isHiddenTrackMatteSource,
  layerHasActiveMotionDrivers,
  validateStudioTrackMattes,
} from "./studioCompRigging";
import {
  applySafeMediaSource,
  createSecureId,
  getSafeMediaSource,
  sanitizeUrl,
} from "../utils/security";
import { API_BASE_URL, API_ENDPOINTS } from "../config";
import { uploadSourceFileViaBackend } from "../utils/sourceUpload";
import StudioLaunchTemplatePanel from "./StudioLaunchTemplatePanel";
import ProductHelpButton from "./ProductHelpButton";
import StudioLayerMask from "./StudioLayerMask";
import { studioMediaEffectFilter } from "./studioMediaEffects";
import { studioLayerEntries, moveStudioLayer, orderStudioCollection } from "./studioLayerOrder";
import { studioGroupTree, createStudioGroup, moveStudioGroupNode, removeStudioGroup, removeStudioGroupMembers, studioGroupLocalPoint, studioGroupProgrammePoint, GROUP_TRANSFORM, evaluateStudioGroupTransform, validateStudioGroupKeyframes, pruneRemovedStudioGroupKeys } from "./studioGroups";
import StudioMotionPathEditor from './StudioMotionPathEditor';
import StudioKineticTypePanel from './StudioKineticTypePanel';
import { buildStudioKineticType } from './studioKineticType';
import StudioGraphicsImport from './StudioGraphicsImport';
import { buildStudioGraphicsPackage, rasterizeStudioArtwork } from './studioGraphicsPackage';
import StudioVectorShapePanel from './StudioVectorShapePanel';
import StudioLogoDesignPanel from './StudioLogoDesignPanel';
import { studioVectorSvg, studioVectorUri, validateStudioVectorShape } from './studioVectorShape';
import StudioLogoMotionPanel from './StudioLogoMotionPanel';
import { buildStudioLogoMotion } from './studioLogoMotion';
import { editStudioPositionPose, editStudioSpatialPath } from './studioMotionEditing';
import StudioGroupPreview from "./StudioGroupPreview";
import { hasSoftStudioMask, studioMaskPointProperty, parseStudioMaskPolygon, studioMaskPolygonPath, validateStudioMaskAnimation } from "./studioMaskGeometry";
import { primeStudioSourceVideo, watchStudioProgrammeFrames, studioPresentedSourceTime, watchStudioClipBoundary, watchStudioAudioClock, studioJoinFade, mapStudioSourceAccess, isStudioSourceClockCurrent, pausedStudioMappedFrameRequest, pausedStudioFrameKey } from "./studioSourcePreview";
import { loadStudioSourceFrame } from "./studioSourceFrameClient";
import { musicBufferNeedsResync, musicBufferPosition, prepareStudioMusicPlayback, studioMusicBufferIdentity } from "./studioMusicBufferClock";
import { loadStudioVoiceBus, scheduleStudioPreparedDuck } from './studioPreparedVoiceBus';
import { studioCueSourceKey, createStudioCueBuffer, prepareStudioCuePlayback, prepareStudioBuiltInCue } from "./studioCueBuffer";
import {
  MAX_STUDIO_ANGLES,
  createStudioAngleGroup,
  createStudioAngleShot,
  getStudioAngleSharedRange,
  setStudioAngleOffset,
} from "./studioAngleGroups";
import React, { useState, useRef, useEffect, useLayoutEffect, useMemo, useCallback } from "react";
import { createPortal, flushSync } from "react-dom";
import { useSubscription } from "../hooks/useSubscription";
import { storage } from "../firebaseClient";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { getAuth } from "firebase/auth";
import html2canvas from "html2canvas"; // For rendering styled captions
import { buildCaptionRenderPreviewChunks, getCaptionPreviewStyles, getCaptionPreviewOutputClock } from "./studioCaptionPreviewStyle";
import { studioOverlayBelongsToClip } from "./studioOverlayBinding";
import { trackClipWorkflowEvent } from "../utils/clipWorkflowAnalytics";
import { playMediaSafely } from "../utils/mediaPlayback";
import toast from "react-hot-toast";
import { SafeAudio, SafeImage, SafeVideo } from "./SafeMedia";
import { sanitizeLogoSvg } from "./motion/sanitizeLogoSvg";
import {
  applySilenceKeepSegmentsToTimeline,
  mapCaptionSegmentsToTimeline,
} from "./viralRenderPayload";
import { splitCaptionSegmentsForReadability } from "./captionReadability";
import { buildTimedScriptDraft } from "../utils/captionDrafts";
import { buildTranscriptGroundedBRollSuggestions } from "./storyBeatPlanner";
import StudioGeneratedBRollPanel from "./StudioGeneratedBRollPanel";
import { applyCaptionBulkEdit } from "./studioCaptionBulkEdit";
import { captionsForSource, mergeSourceCaptions } from "./studioCaptionGeneration";
import { getMediaAuthToken } from "../utils/mediaAuth";
import { runStudioSourceShotJob } from "./studioAnalysisJobClient";
import useCinematicEffects, { CINEMATIC_PRESETS, buildCinematicCssFilter } from "../hooks/useCinematicEffects";
import StudioFinishLayers from "./StudioFinishLayers";
import StudioFinishRack from "./StudioFinishRack";
import { buildPrecisionGrade, applyPrecisionGrade } from "./studioPrecisionGrade";
import { parseColorCube, buildAdvancedColorCube, applyColorCube, hasAdvancedGrade } from "./studioColorCube";
import { soloTrackingCoordinates, reprojectDetectedReframeKeys } from "./studioTrackingCoordinates";
import { interpolateReframeKeyframes } from "./studioReframeInterpolation";
import StudioProTimeline from "./StudioProTimeline";
import {
  generateSmartZoomKeyframes,
  createSinglePunchKeyframes,
  extractPunchZonesFromKeyframes,
  isTimePunched,
  PUNCH_SCALES,
  PUNCH_STYLES,
  PUNCH_MODES,
} from "./studioSmartZoom";
import {
  SPLIT_AUDIO_PRESETS,
  normalizeSplitAudioClip,
  buildAudioSplitSequence,
  applySplitAudioPreset,
  autoApplyDialoguePreLaps,
  calculateAudioProxyState,
} from "./studioAudioSplit";
import {
  normalizeBeatMarkers,
  calculateBpm,
  findNearestBeat,
  alignTimelineCutsToMusicBeats,
  alignBRollToMusicBeats,
  generateBeatPunchIns,
} from "./studioBeatEngine";
import StudioVoiceoverPreview from "./StudioVoiceoverPreview";
import {
  COMMON_FILLER_WORDS,
  detectFillerWords,
  sliceTimelineTimeRange,
  sliceMultipleTimeRanges,
  retimeCaptionsAfterCut,
} from "./studioScriptSlicer";
import { uploadVoiceoversForRender, voiceoverTrackAudible, voiceoverGain, readVoiceoverDuration, constantMusicDuckGain } from "./studioVoiceover";
import { isStudioAudioTrackAudible } from "./studioAudioTracks";
import { mapStudioOverlayTiming } from "./studioOverlayTiming";
import {
  DEFAULT_CREATOR_PREVIEW,
  StudioCreatorPreviewLayer,
  StudioCreatorWorkbench,
  getCreatorCanvasClass,
} from "./StudioCreatorWorkbench";
import {
  AudioRestorationPanel,
  CompositeInspector,
  MotionInspector,
} from "./StudioAdvancedInspectors";
import SoundWaveform from "./motion/SoundWaveform";
import MotionPanel from "./motion/MotionPanel";
import MotionCanvas from "./motion/MotionCanvas";
import { motionCues, normalizeMotion, generateSmartMotionBeats } from "./motion/motionModel";
import Studio3DPanel from "./threeD/Studio3DPanel";
import StudioSubjectVfxPanel from "./StudioSubjectVfxPanel";
import { findUncoveredCaptionSpeechRanges, mapCaptionSilencesToProgramme } from "./studioCaptionCoverage";
import { historyMediaIdentity, historySubjectVfxIdentity } from "./studioHistoryMedia";
import { packageStudioBackup } from "./studioProjectBackupMedia";
import StudioAnimatedText from "./StudioAnimatedText";
import StudioTextAnimatorPanel from "./StudioTextAnimatorPanel";
import { validateStudioTextAnimation } from "./studioTextAnimator";
import { titleAnimationPose, normalizeStudioTitleText } from "./studioTitleLayout";
import { studioMotionPropertiesForTarget, motionKeyAtPlayhead, motionKeyframeTolerance } from "./studioMotionProperties";
import { validateSubjectVfxExport, resolveSubjectVfxProgrammePreview, subjectVfxRevision } from "./studioSubjectVfxContract";
import { normalizeStudio3DScene, studio3DSceneRevision, studio3DRenderIdentity } from "./threeD/studio3DModel";
import { SOUND_EFFECT_PRESETS, synthesizeEffect } from "./motion/soundDesign";
import VideoScopes from "./VideoScopes";
import {
  STUDIO_EASINGS,
  buildMotionAutomationStyle,
  interpolateAutomationValue,
  upsertAutomationKeyframe,
} from "./studioAutomation";
import {
  buildCreatorMotionRecipe,
  buildCreatorShakeRecipe,
  buildCreatorSpeedRecipe,
  buildSpeedSegmentsFromKeyframes,
  mergeCreatorRecipe,
  findCreatorBeat,
} from "./studioCreatorRecipes";
import { studioMusicFadeGain } from "./studioAudioFades";
import { applyStudioClipMetadata } from "./studioTimelineEdits";
import { rippleStudioSnapshotLinkedTimeline, splitStudioOverlayAtProgrammeTime } from "./studioTrimTransaction";
import { editStudioSequence, removeStudioProgrammeRanges } from './studioSequenceEdits';
import { studioGroupTimingRows, editStudioGroupTiming, clipStudioGroupMotionCues } from "./studioGroupTiming";
import { editStudioKeyframeSelection } from "./studioKeyframeSelection";
import {
  reconcileStudioDocument,
  buildStudioOutputTimeMap,
  rebaseStudioHistoryRestore,
  restoreStudioProjectCheckpoint,
  projectStudioEditableStateToSnapshot,
} from "./studioProjectDocument";
import { MINIMUM_SPLIT_DISTANCE_TICKS, runStudioCommandOnSnapshot } from "./studioCommands";
import {
  createStudioDirectorReviewReceipt,
  prepareStudioDirectorProposal,
  recordStudioDirectorReviewRejectionOnSnapshot,
  runStudioDirectorProposalOnSnapshot,
} from "./studioDirectorProposals";
import { describeStudioDirectorReview } from "./studioDirectorReviewDiff";
import { postStudioDirectorReviewDecision } from "./studioDirectorReviewClient";
import { registerStudioDirectorProjectRevision } from "./studioDirectorProjectRevisionClient";
import { buildSourceShotDirectorSplitRequest } from "./studioDirectorEvidenceProposals";
import { secondsToTicks, ticksToSeconds, mapOutputTickToProgrammeTick, mapProgrammeTickToOutputTick } from "./studioTime";
import { advanceStudioCueStartPlan, scheduleStudioCueGain, scheduleStudioCueRate, studioCueProgrammeTime, studioCueStartPlan } from "./studioCueGain";
import { bindStudioAudioSource, bindStudioAudioSources, studioUploadedAudioBinding } from "./studioAudioSourceBindings";
import {
  assertStudioExportTimelineMatchesDocument,
  assertStudioSpeedPlanMatchesDocument,
  compileStudioFinishForRender,
  compileStudioCompositionForRender,
} from "./studioRenderCompiler";
import { detectAudioBeats } from "./flowEditUtils";
import {
  analyzeAudioBufferBeats,
  interpolateFinishKeyframes,
  snapTimeToBeat,
  upsertFinishKeyframe,
} from "./studioFinishKeyframes";
import {
  CreativeToolRail,
  StudioInspectorTabs,
  ViralStudioHeader,
  normalizeViralStudioWorkspaceMode,
} from "./ViralStudioChrome";
import {
  deleteViralStudioProject,
  listViralStudioProjects,
  saveViralStudioProject,
} from "./viralStudioProjectStore";
import AudioRemixPanel from "./audio/AudioRemixPanel";
import {
  AUDIO_REMIX_PRESETS,
  DEFAULT_AUDIO_REMIX,
  audioRemixForRender,
  normalizeAudioRemix,
} from "./audio/audioRemixModel";
import {
  ensureAudioRemixPreview,
  disposeAudioRemixPreview,
  unlockAudioRemixContext,
  updateAudioRemixPreview,
} from "./audio/audioRemixPreview";
import "./audio/audioRemixTimeline.css";
import "./ViralClipStudio.css";
import "./StudioControls.css";
import TimelineSourceFilmstrip from "./TimelineSourceFilmstrip";
import StudioControlMenu from "./StudioControlMenu";

const Studio3DCanvas = React.lazy(() => import("./threeD/Studio3DCanvas"));

const RAINBOW_COLORS = [
  "#FF9AA2", // Soft Red
  "#FFB7B2", // Salmon
  "#FFDAC1", // Peach
  "#E2F0CB", // Lime Green
  "#B5EAD7", // Mint
  "#C7CEEA", // Lavender
  "#F4C2C2", // Baby Pink
  "#89CFF0", // Baby Blue
];

const normalizePlainText = value =>
  String(value ?? "")
    .replace(/[\u0000-\u001f\u007f]/g, " ")
    .replace(/[<>]/g, "")
    .trim();

const getCaptionServiceError = (payload, fallback) => {
  const detail = payload?.error ?? payload?.message ?? payload?.detail;
  if (typeof detail === "string" && detail.trim()) return detail.trim();
  if (Array.isArray(detail)) {
    const messages = detail.map(item => item?.msg).filter(Boolean);
    if (messages.length) return messages.join("; ");
  }
  return fallback;
};

const normalizeOverlayFrameShape = (value, bRollMode = "pip") => {
  if (bRollMode === "fullscreen") return "edge";
  if (["edge", "none", "square"].includes(value)) return "edge";
  return "round";
};

const getOverlayFrameRadius = overlay => {
  const shape = normalizeOverlayFrameShape(overlay?.frameShape, overlay?.bRollMode);
  if (shape === "round") {
    const radius = Math.max(12, Math.min(48, Number(overlay?.borderRadius ?? 28)));
    // A percentage keeps the output-radius visually consistent when the full
    // resolution composition is scaled down into the editor preview.
    return `${(radius / 4).toFixed(2).replace(/\.00$/, "")}%`;
  }
  return "0";
};

const getOverlayMaskClipPath = overlay => {
  if (hasSoftStudioMask(overlay?.mask)) return undefined;
  const cropX = clampNumber(overlay?.cropX, 0, 45, 0);
  const cropY = clampNumber(overlay?.cropY, 0, 45, 0);
  if (!overlay?.mask?.enabled) {
    return cropX || cropY ? `inset(${cropY}% ${cropX}% ${cropY}% ${cropX}%)` : undefined;
  }
  if (overlay.mask.shape === "ellipse") return "ellipse(48% 48% at 50% 50%)";
  if (overlay.mask.shape === "polygon") {
    return overlay.mask.path || "polygon(50% 0%, 100% 38%, 82% 100%, 18% 100%, 0% 38%)";
  }
  return `inset(${cropY}% ${cropX}% ${cropY}% ${cropX}% round ${Number(overlay.mask.radius ?? 6)}%)`;
};

const CAPTION_LANGUAGE_OPTIONS = [
  { value: "und", label: "Language needs review" },
  { value: "xh", label: "isiXhosa" },
  { value: "en", label: "English" },
  { value: "zu", label: "isiZulu" },
  { value: "mixed", label: "Mixed languages" },
];

const CAPTION_SPEAKER_OPTIONS = [
  { value: "unknown", label: "Speaker needs review" },
  { value: "choir", label: "Choir" },
  { value: "host", label: "Host" },
  { value: "guest", label: "Guest" },
  { value: "A", label: "Speaker A" },
  { value: "B", label: "Speaker B" },
];

const CAPTION_PLACEMENT_OPTIONS = [
  { value: "auto", label: "Auto safe region" },
  { value: "shoulder_left", label: "Left shoulder" },
  { value: "shoulder_right", label: "Right shoulder" },
  { value: "background_left", label: "Background wall · left" },
  { value: "background_center", label: "Background wall · centre" },
  { value: "background_right", label: "Background wall · right" },
  { value: "top_left", label: "Top left" },
  { value: "top_center", label: "Top centre" },
  { value: "top_right", label: "Top right" },
  { value: "middle_left", label: "Middle left" },
  { value: "middle_center", label: "Dead centre" },
  { value: "middle_right", label: "Middle right" },
  { value: "bottom_left", label: "Bottom left" },
  { value: "bottom_center", label: "Bottom centre" },
  { value: "bottom_right", label: "Bottom right" },
  { value: "custom", label: "Free position" },
];

const CAPTION_STYLE_OPTIONS = [
  ["rainbow", "Rainbow Flow", "Every spoken word carries the colour beat"],
  ["watch_me", "Watch Me", "Dims the sentence and makes the live word command the frame"],
  ["wall_type", "Wall Type", "Low-profile typography for open background space"],
  ["story_pop", "Story Pop", "Editorial card + semantic accent"],
  ["bold_pop", "Bold Pop", "Punchy active-word scale"],
  ["karaoke", "Karaoke", "Progressive spoken-word fill"],
  ["glow", "Neon Glow", "Electric outlined emphasis"],
  ["bounce", "Bounce", "Playful vertical word punch"],
  ["minimal", "Minimal", "Quiet premium subtitle"],
  ["headline", "Big Headline", "Oversized uppercase impact"],
  ["boxed", "Subtitle Card", "Broadcast-style readable card"],
  ["comic", "Comic Punch", "Bold yellow social energy"],
  ["gradient", "Gradient Pop", "Colour-rich creator highlight"],
  ["typewriter", "Typewriter", "Measured word-by-word reveal"],
  ["editorial", "Editorial Serif", "Elegant documentary voice"],
  ["sticker", "Sticker Stack", "Layered creator sticker treatment"],
  ["marker", "Marker Swipe", "Highlighter-strip emphasis"],
  ["glass", "Glass Caption", "Frosted premium floating card"],
  ["newsroom", "News Flash", "Urgent broadcast headline energy"],
  ["luxury", "Luxury Quote", "Refined fashion-film typography"],
  ["retro", "Retro Tape", "Warm analogue poster character"],
];

const CAPTION_ACCENT_OPTIONS = [
  ["#ffb33d", "Amber"],
  ["#8b5cf6", "Violet"],
  ["#3ce0d0", "Aqua"],
  ["#ff5d8f", "Rose"],
  ["#f6e652", "Lemon"],
  ["#72f59b", "Mint"],
  ["#54a8ff", "Blue"],
  ["#ffffff", "White"],
];

const normalizeCaptionDefaultPlacement = value => ({
  top: "top_center",
  center: "middle_center",
  middle: "middle_center",
  lower: "bottom_center",
  bottom: "bottom_center",
}[String(value || "").trim().toLowerCase()] || String(value || "bottom_center"));

const CAPTION_ICON_OPTIONS = [
  { value: "auto", glyph: "✨", label: "Auto reaction" },
  { value: "laugh", glyph: "😂", label: "Laugh" },
  { value: "fire", glyph: "🔥", label: "Fire" },
  { value: "heart", glyph: "❤️", label: "Love" },
  { value: "wow", glyph: "😮", label: "Wow" },
  { value: "clap", glyph: "👏", label: "Applause" },
  { value: "hundred", glyph: "💯", label: "One hundred" },
  { value: "eyes", glyph: "👀", label: "Watch this" },
  { value: "crown", glyph: "👑", label: "Crown" },
  { value: "mic", glyph: "🎙️", label: "Mic" },
  { value: "phone", glyph: "📱", label: "Phone / online" },
  { value: "music", glyph: "🎵", label: "Music / choir" },
  { value: "place", glyph: "📍", label: "Place / journey" },
  { value: "payoff", glyph: "⚡", label: "Payoff" },
  { value: "none", glyph: "∅", label: "No emoji" },
];

const REFRAME_ASPECT_OPTIONS = [
  { value: "9:16", label: "9:16", helper: "TikTok · Reels · Shorts" },
  { value: "4:5", label: "4:5", helper: "Instagram feed" },
  { value: "1:1", label: "1:1", helper: "Square social" },
  { value: "16:9", label: "16:9", helper: "YouTube · landscape" },
];

const DEFAULT_SPEAKER_STACK_FRAMING = {
  dividerPercent: 50,
  gapPercent: 1.2,
  cardGapPercent: 2.5,
  roundedCards: true,
  cardInsetPercent: 2.5,
  cardRadiusPercent: 8,
  secondaryXPercent: 80,
  secondaryYPercent: 20,
  secondarySizePercent: 30,
  // Independent camera angles start centred. Source-split framing uses its own
  // presets below; left/right programme anchors made uploaded cameras open off-centre.
  top: { x: 50, y: 50, zoom: 1 },
  bottom: { x: 50, y: 50, zoom: 1 },
  third: { x: 50, y: 50, zoom: 1 },
  fourth: { x: 50, y: 50, zoom: 1 },
};

const SOURCE_SPLIT_SIDE_BY_SIDE_FRAMING = {
  top: { x: 27, y: 50, zoom: 1.45 },
  bottom: { x: 73, y: 50, zoom: 1.45 },
};

const MULTICAM_SLOT_KEYS = ["top", "bottom", "third", "fourth"];
const MULTICAM_SLOT_LABELS = {
  top: "Camera 1",
  bottom: "Camera 2",
  third: "Camera 3",
  fourth: "Camera 4",
};
const getDefaultMulticamLayout = cameraCount =>
  Number(cameraCount) >= 4 ? "grid_4" : Number(cameraCount) === 3 ? "hero_3" : "stack_2";

const normalizeMainFrame = frame => {
  const normalized = {
    insetPercent: 2.5,
    radiusPercent: 6,
    background: "studio_black",
    ...(frame || {}),
  };
  return {
    ...normalized,
    // Legacy projects without an explicit choice retain their existing frame.
    // A saved false value must survive hydration and export.
    enabled: normalized.enabled !== false,
    radiusPercent: Math.max(3, Number(normalized.radiusPercent || 6)),
  };
};

const normalizeSpeakerFocusCuts = cuts =>
  [...(Array.isArray(cuts) ? cuts : [])]
    .filter(cut =>
      ["top", "bottom"].includes(cut?.slot) && Number.isFinite(Number(cut?.time))
    )
    .map(cut => ({
      id: cut.id || createSecureId("speaker-focus"),
      time: Math.max(0, Number(cut.time || 0)),
      slot: cut.slot,
    }))
    .sort((left, right) => left.time - right.time);

const resolveSpeakerFocusSlot = (cuts, time, availableSlots = ["top", "bottom"]) => {
  const fallback = availableSlots[0] || "top";
  return normalizeSpeakerFocusCuts(cuts).reduce(
    (current, cut) =>
      cut.time <= Math.max(0, Number(time || 0)) && availableSlots.includes(cut.slot)
        ? cut.slot
        : current,
    fallback
  );
};

const normalizeReframeModeCuts = cuts =>
  [...(Array.isArray(cuts) ? cuts : [])]
    .filter(
      cut =>
        ["off", "speaker_track", "center", "group_stack"].includes(cut?.mode) &&
        Number.isFinite(Number(cut?.time))
    )
    .map(cut => ({
      id: cut.id || createSecureId("reframe-mode"),
      time: Math.max(0, Number(cut.time || 0)),
      mode: cut.mode,
      ...(Number(cut.zoom) > 1 ? { zoom: Math.min(2, Number(cut.zoom)) } : {}),
      ...(Number.isFinite(Number(cut.sourceTimeOffsetSeconds ?? cut.source_time_offset_seconds)) &&
      Math.abs(Number(cut.sourceTimeOffsetSeconds ?? cut.source_time_offset_seconds)) > 0.001
        ? { sourceTimeOffsetSeconds: Number(cut.sourceTimeOffsetSeconds ?? cut.source_time_offset_seconds) }
        : {}),
    }))
    .sort((left, right) => left.time - right.time);

const resolveReframeModeAtTime = (cuts, time, fallback) =>
  normalizeReframeModeCuts(cuts).reduce(
    (mode, cut) => (cut.time <= Math.max(0, Number(time || 0)) ? cut.mode : mode),
    fallback
  );

const resolveReframeCutAtTime = (cuts, time) =>
  normalizeReframeModeCuts(cuts).reduce(
    (active, cut) => (cut.time <= Math.max(0, Number(time || 0)) ? cut : active),
    null
  );

const normalizeCaptionLanguage = value => {
  const normalized = String(value || "")
    .trim()
    .toLowerCase();
  if (["xh", "xho", "xhosa", "isixhosa"].includes(normalized)) return "xh";
  if (["zu", "zul", "zulu", "isizulu"].includes(normalized)) return "zu";
  if (["en", "eng", "english"].includes(normalized)) return "en";
  if (["mixed", "multilingual", "code-switched", "code_switched"].includes(normalized))
    return "mixed";
  return "und";
};

const getCaptionLanguageLabel = language =>
  CAPTION_LANGUAGE_OPTIONS.find(option => option.value === language)?.label ||
  "Language needs review";

const normalizeCaptionSpeaker = value => {
  const speaker = normalizePlainText(value || "unknown");
  if (!speaker || ["und", "unknown", "speaker needs review"].includes(speaker.toLowerCase()))
    return "unknown";
  return speaker;
};

const getCaptionSpeakerLabel = speaker => {
  const matched = CAPTION_SPEAKER_OPTIONS.find(option => option.value === speaker);
  return matched?.label || (speaker === "unknown" ? "Speaker needs review" : `Speaker ${speaker}`);
};

const normalizeHookText = value =>
  String(value ?? "")
    .replace(/\r\n?/g, "\n")
    .replace(/[\u0000-\u0008\u000b-\u001f\u007f]/g, " ")
    .replace(/[<>]/g, "")
    .split("\n")
    .map(line => line.trim())
    .join("\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();

const DEFAULT_HOOK_TEXT = "THIS CHANGES FAST";

const HOOK_MIN_SEGMENT_DURATION = 2;
const HOOK_MAX_SEGMENT_DURATION = 5;

const GENERIC_HOOK_TEXTS = new Set([
  "WAIT FOR IT...",
  "WAIT FOR IT",
  "THIS CHANGES FAST",
  "WATCH THIS PART",
  "DON'T MISS THIS",
]);

const sleep = ms => new Promise(resolve => setTimeout(resolve, ms));

const clampAudioControl = (value, minimum, maximum, fallback) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return fallback;
  return Math.max(minimum, Math.min(maximum, numeric));
};

const clampNumber = (value, minimum, maximum, fallback) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return fallback;
  return Math.max(minimum, Math.min(maximum, numeric));
};

const getAudioFadeGain = (timelineTime, timelineDuration, fadeIn, fadeOut) => {
  const safeTime = Math.max(0, Number(timelineTime) || 0);
  const safeDuration = Math.max(0, Number(timelineDuration) || 0);
  const safeFadeIn = Math.max(0, Number(fadeIn) || 0);
  const safeFadeOut = Math.max(0, Number(fadeOut) || 0);
  const fadeInGain = safeFadeIn > 0 ? Math.min(1, safeTime / safeFadeIn) : 1;
  const remaining = Math.max(0, safeDuration - safeTime);
  const fadeOutGain =
    safeFadeOut > 0 && safeDuration > 0 ? Math.min(1, remaining / safeFadeOut) : 1;
  return Math.max(0, Math.min(fadeInGain, fadeOutGain));
};

const normalizeAudioMode = value => {
  const normalized = String(value || "mix")
    .trim()
    .toLowerCase();
  if (["mix", "replace", "duck_original"].includes(normalized)) return normalized;
  return "mix";
};

const isPresetMusicSelection = value => /\.mp3$/i.test(String(value || "").trim());

const HOOK_TEMPLATES = {
  blur_reveal: {
    label: "Blur Reveal",
    description: "A sharp blur-to-clear reveal with high-contrast text that lands immediately.",
    duration: 3,
    blurBackground: true,
    darkOverlay: true,
    freezeFrame: false,
    zoomScale: 1.1,
    textAnimation: "slide-up",
  },
  zoom_focus: {
    label: "Zoom Focus",
    description: "A fast focal push with extra contrast for energetic spoken openings.",
    duration: 3,
    blurBackground: false,
    darkOverlay: true,
    freezeFrame: false,
    zoomScale: 1.16,
    textAnimation: "fade-in",
  },
  freeze_text: {
    label: "Freeze + Text",
    description: "A confident freeze, headline hit, then a smooth release back into motion.",
    duration: 3.2,
    blurBackground: true,
    darkOverlay: true,
    freezeFrame: true,
    zoomScale: 1.08,
    textAnimation: "fade-in",
  },
};

const BROLL_CADENCE_PRESETS = {
  sparse: {
    label: "Sparse",
    interval: 18,
    maxBeats: 12,
    helper: "Leaves longer stretches on the speaker.",
  },
  balanced: {
    label: "Balanced",
    interval: 10,
    maxBeats: 24,
    helper: "Adds proof without interrupting the conversation.",
  },
  frequent: {
    label: "Frequent",
    interval: 6,
    maxBeats: 32,
    helper: "Keeps a fast visual rhythm for high-energy clips.",
  },
};

const DEFAULT_HOOK_FOCUS_POINT = Object.freeze({ x: 50, y: 42 });

const normalizeHookFocusPoint = point => ({
  x: clampNumber(point?.x, 0, 100, DEFAULT_HOOK_FOCUS_POINT.x),
  y: clampNumber(point?.y, 0, 100, DEFAULT_HOOK_FOCUS_POINT.y),
});

const getHookTemplateConfig = templateKey =>
  HOOK_TEMPLATES[templateKey] || HOOK_TEMPLATES.blur_reveal;

const getHookSuggestion = clip => {
  const reason = normalizePlainText(clip?.reason || "");
  const clipStart = clampNumber(clip?.start, 0, 1.5, 0.8);
  const motionHint = /(movement|motion|energy|laugh|spike|action|impact|fast|reveal)/i.test(reason);
  const staticHint = /(intro|setup|calm|question|talking|story|explains|static)/i.test(reason);

  if (motionHint) {
    return {
      suggestedStart: Math.max(0.5, Math.min(1.1, clipStart || 0.5)),
      templateKey: "zoom_focus",
      message: "Early motion detected in the selected moment. Start quickly and push the frame in.",
    };
  }

  if (clipStart >= 0.5 && clipStart <= 1.5) {
    return {
      suggestedStart: clipStart,
      templateKey: "freeze_text",
      message:
        "There is an early beat change near the opening. Freeze briefly and land the message on it.",
    };
  }

  return {
    suggestedStart: staticHint ? 0.9 : 0.7,
    templateKey: "blur_reveal",
    message: "The opening looks calmer, so add blur and contrast before the reveal.",
  };
};

const getHookCopySuggestions = clip => {
  const reason = normalizePlainText(clip?.reason || "");
  const hasQuestion = /\?/.test(reason) || /(why|how|what|when)/i.test(reason);
  const hasAction =
    /(movement|motion|energy|laugh|spike|action|impact|fast|reveal|switch|flip)/i.test(reason);
  const hasStory = /(story|talking|explains|setup|intro|lesson|mistake|truth|secret)/i.test(reason);

  const suggestions = [];

  if (hasQuestion) {
    suggestions.push("THE ANSWER HITS HERE");
    suggestions.push("WAIT UNTIL THIS PART");
  }

  if (hasAction) {
    suggestions.push("THIS IS WHERE IT FLIPS");
    suggestions.push("DON'T BLINK HERE");
  }

  if (hasStory) {
    suggestions.push("THIS PART CHANGES THE STORY");
    suggestions.push("HERE'S THE PART THAT MATTERS");
  }

  suggestions.push("WATCH WHAT HAPPENS NEXT");
  suggestions.push("THIS CHANGES FAST");
  suggestions.push("THE NEXT 3 SECONDS MATTER");

  return [...new Set(suggestions)].slice(0, 4).map(text => normalizeHookText(text));
};

const getClipDescriptorText = clip =>
  normalizePlainText(
    [clip?.reason, clip?.label, clip?.transcript, clip?.text, clip?.title].filter(Boolean).join(" ")
  );

const getClipDurationSeconds = clip => {
  const explicitDuration = Number(clip?.duration);
  if (Number.isFinite(explicitDuration) && explicitDuration > 0) return explicitDuration;

  const start = Number(clip?.start || 0);
  const end = Number(clip?.end || 0);
  return Math.max(0, end - start);
};

const CATEGORY_TAG_RULES = [
  {
    label: "High Energy",
    icon: "🔥",
    pattern:
      /(motion|movement|fast|energy|action|impact|laugh|dance|switch|cut|dynamic|spike|reveal)/i,
  },
  {
    label: "Emotional",
    icon: "😳",
    pattern: /(emotional|cry|reaction|heart|shock|confession|surprise|love|angry|fear|dramatic)/i,
  },
  {
    label: "Educational",
    icon: "🎓",
    pattern: /(how|why|lesson|learn|tutorial|guide|tip|explains|education|mistake|truth|secret)/i,
  },
  {
    label: "Funny",
    icon: "😂",
    pattern: /(funny|laugh|joke|prank|comedy|hilarious|meme)/i,
  },
  {
    label: "Promotional",
    icon: "💰",
    pattern: /(promo|promotional|offer|sale|product|launch|brand|ad|subscribe|buy|deal)/i,
  },
];

const NARRATIVE_ROLE_RULES = [
  {
    id: "payoff",
    label: "Payoff",
    pattern:
      /(result|reveal|payoff|before|after|final|ending|turned out|this happened|proof|transformation)/i,
  },
  {
    id: "hook",
    label: "Hook",
    pattern:
      /(\?|why|how|what|wait|watch|secret|mistake|truth|don't|stop|before|until|never|crazy|wild)/i,
  },
  {
    id: "reaction",
    label: "Reaction",
    pattern: /(reaction|laugh|shock|surprise|cry|stunned|face|emotional|crowd|applause)/i,
  },
  {
    id: "proof",
    label: "Proof",
    pattern: /(shows|demo|proves|example|evidence|breakdown|explains|walkthrough|tutorial|guide)/i,
  },
  {
    id: "story",
    label: "Story Beat",
    pattern: /(story|moment|confession|lesson|journey|remember|told me|happened|realized)/i,
  },
];

const AUDIENCE_PROFILE_RULES = [
  {
    id: "performance",
    label: "Performance",
    pattern:
      /(choir|singer|singing|vocal|harmony|chorus|worship|performance|concert|band|stage|music video)/i,
  },
  {
    id: "education",
    label: "Education",
    pattern:
      /(tutorial|lesson|guide|how to|mistake|truth|secret|explains|learn|teaches|education)/i,
  },
  {
    id: "reaction",
    label: "Reaction",
    pattern: /(reaction|laugh|prank|funny|shock|surprise|face|commentary|responding)/i,
  },
  {
    id: "product",
    label: "Product",
    pattern: /(product|offer|sale|brand|launch|ad|promo|review|feature|tool|app)/i,
  },
  {
    id: "story",
    label: "Story",
    pattern: /(story|confession|journey|moment|experience|realized|happened|behind the scenes)/i,
  },
];

const BROLL_SHOT_STYLES = {
  proof: {
    kicker: "PROOF CUTAWAY",
    title: "Show the receipt",
    subtitle: "Use a screen, result, detail, or object that proves the line.",
    tone: "proof",
    bg: "rgba(9, 18, 32, 0.92)",
  },
  detail: {
    kicker: "DETAIL SHOT",
    title: "Get closer",
    subtitle: "Punch into hands, product, face, timeline, or the thing being named.",
    tone: "detail",
    bg: "rgba(12, 17, 24, 0.92)",
  },
  reaction: {
    kicker: "REACTION CUT",
    title: "Catch the face",
    subtitle: "Drop in the look, laugh, pause, or crowd response that sells it.",
    tone: "reaction",
    bg: "rgba(18, 13, 25, 0.92)",
  },
  payoff: {
    kicker: "PAYOFF SHOT",
    title: "Show the outcome",
    subtitle: "Cut to before/after, final reveal, transformation, or the win.",
    tone: "payoff",
    bg: "rgba(26, 13, 10, 0.92)",
  },
};

const MOMENT_TAG_RULES = [
  "reveal",
  "mistake",
  "truth",
  "secret",
  "reaction",
  "laugh",
  "result",
  "proof",
  "lesson",
  "before",
  "after",
  "performance",
  "vocal",
  "energy",
  "switch",
];

const inferRuleMatch = (rules, text, fallback) =>
  rules.find(rule => rule.pattern.test(text)) || fallback;

const getSemanticTokens = text => {
  const normalized = normalizePlainText(text).toLowerCase();
  const matches = MOMENT_TAG_RULES.filter(token => normalized.includes(token));

  if (matches.length) return matches;

  return normalized
    .split(/[^a-z0-9]+/)
    .filter(token => token.length >= 5)
    .slice(0, 3);
};

const buildTravelReason = (narrativeRole, audienceProfile, signals) => {
  if (audienceProfile.id === "performance") {
    return "This moment can travel because the emotional rise is visual even before captions land.";
  }
  if (narrativeRole.id === "payoff") {
    return "This moment travels because it gets to proof fast and rewards the click quickly.";
  }
  if (signals.hook && signals.motion) {
    return "This moment travels because the promise lands early and the pacing keeps people moving.";
  }
  if (signals.speech) {
    return "This moment travels because the spoken idea is clear enough to package in one sentence.";
  }
  return "This moment travels because it has enough clarity to recut into multiple social angles.";
};

const buildRecutVariants = (clip, context) => {
  const start = Number(clip?.start || 0);
  const end = Math.max(start + 0.6, Number(clip?.end || start));
  const duration = Math.max(0.6, end - start);
  const hookOptions = getHookCopySuggestions(clip);
  const boundedVariant = (nextStart, nextEnd) => {
    const safeStart = Math.max(start, Math.min(nextStart, end - 0.6));
    const safeEnd = Math.max(safeStart + 0.6, Math.min(end, nextEnd));
    return { start: safeStart, end: safeEnd, duration: safeEnd - safeStart };
  };

  const curiosityBounds = boundedVariant(start, end - Math.min(1.2, duration * 0.06));
  const authorityBounds = boundedVariant(
    start + Math.min(0.55, duration * 0.08),
    end - Math.min(0.4, duration * 0.03)
  );
  const payoffBounds = boundedVariant(
    start + Math.min(Math.max(0.9, duration * 0.18), Math.max(1.2, duration - 2.4)),
    end
  );

  return [
    {
      id: "curiosity",
      label: "Curiosity Cut",
      summary: "Lead with tension and let the answer arrive a beat later.",
      openingMove:
        context.narrativeRole.id === "payoff"
          ? "Hold back the proof for one beat so the viewer leans in."
          : "Start on the question or strange moment before the explanation lands.",
      hookText: hookOptions[0] || "WAIT FOR THIS",
      templateKey: "blur_reveal",
      ...curiosityBounds,
    },
    {
      id: "authority",
      label: "Authority Cut",
      summary: "Trim the setup and open on the clearest confident statement.",
      openingMove:
        context.audienceProfile.id === "education"
          ? "Open directly on the lesson and let the clip prove it."
          : "Use the strongest claim frame first so the clip feels decisive.",
      hookText: hookOptions[1] || "HERE'S WHAT CHANGED",
      templateKey: "freeze_text",
      ...authorityBounds,
    },
    {
      id: "payoff",
      label: "Payoff First",
      summary: "Jump closer to the reward and use contrast to keep it replayable.",
      openingMove:
        context.audienceProfile.id === "performance"
          ? "Start near the vocal lift or emotional swell, then let the room react."
          : "Open close to the visual or spoken payoff and let the rest explain itself.",
      hookText: hookOptions[2] || "THIS IS THE PAYOFF",
      templateKey: "zoom_focus",
      ...payoffBounds,
    },
  ];
};

const getClipMidpoint = clip => {
  const start = Number(clip?.start || 0);
  const end = Number(clip?.end || start);
  return start + Math.max(0, end - start) / 2;
};

const countTokenOverlap = (leftTokens, rightTokens) => {
  const left = new Set(leftTokens || []);
  const right = new Set(rightTokens || []);
  let overlap = 0;
  left.forEach(token => {
    if (right.has(token)) overlap += 1;
  });
  return overlap;
};

const buildMomentFamilies = rankedEntries => {
  const families = [];

  rankedEntries.forEach(entry => {
    const midpoint = getClipMidpoint(entry.clip);
    const semanticTokens = entry.semanticTokens || [];

    const existingFamily = families.find(family => {
      const overlap = countTokenOverlap(family.semanticTokens, semanticTokens);
      const proximity = Math.abs(family.anchorMidpoint - midpoint);
      const sameAudience = family.audienceProfileId === entry.audienceProfile.id;
      const sameNarrative = family.narrativeRoleId === entry.narrativeRole.id;

      return (
        overlap >= 2 ||
        (overlap >= 1 && sameAudience) ||
        (sameAudience && sameNarrative && proximity <= 24) ||
        (sameAudience && proximity <= 12)
      );
    });

    if (existingFamily) {
      existingFamily.members.push(entry);
      existingFamily.semanticTokens = [
        ...new Set([...existingFamily.semanticTokens, ...semanticTokens]),
      ];
      existingFamily.anchorMidpoint =
        (existingFamily.anchorMidpoint * (existingFamily.members.length - 1) + midpoint) /
        existingFamily.members.length;
      if (entry.score > existingFamily.score) {
        existingFamily.topEntry = entry;
        existingFamily.score = entry.score;
      }
      return;
    }

    families.push({
      id: `family-${families.length + 1}`,
      score: entry.score,
      topEntry: entry,
      members: [entry],
      semanticTokens: [...semanticTokens],
      anchorMidpoint: midpoint,
      audienceProfileId: entry.audienceProfile.id,
      audienceProfileLabel: entry.audienceProfile.label,
      narrativeRoleId: entry.narrativeRole.id,
      narrativeRoleLabel: entry.narrativeRole.label,
    });
  });

  return families
    .map(family => {
      const topEntry = family.topEntry;
      const leadToken = family.semanticTokens[0] || family.narrativeRoleLabel.toLowerCase();
      return {
        ...family,
        label: `${family.audienceProfileLabel} • ${family.narrativeRoleLabel}`,
        headline: `${family.narrativeRoleLabel} around ${leadToken}`,
        summary: topEntry.travelReason,
        clipIds: family.members.map(member => member.clip.id),
        members: family.members.sort(
          (left, right) =>
            right.score - left.score ||
            right.backendScore - left.backendScore ||
            left.index - right.index
        ),
      };
    })
    .sort((left, right) => right.score - left.score);
};

const pickCampaignEntry = (entries, preferredIds = [], excludedIds = new Set()) => {
  for (const id of preferredIds) {
    const match = entries.find(entry => entry.clip.id === id && !excludedIds.has(entry.clip.id));
    if (match) return match;
  }
  return entries.find(entry => !excludedIds.has(entry.clip.id)) || null;
};

const buildCampaignSet = (rankedEntries, families) => {
  if (!rankedEntries.length) return [];

  const usedClipIds = new Set();
  const bestOverall = rankedEntries[0];
  if (bestOverall) usedClipIds.add(bestOverall.clip.id);

  const proofCandidate = pickCampaignEntry(
    [...rankedEntries].sort(
      (left, right) =>
        right.scoreBreakdown.find(item => item.label === "Conversion")?.value -
          left.scoreBreakdown.find(item => item.label === "Conversion")?.value ||
        right.semanticArcScore - left.semanticArcScore
    ),
    [],
    usedClipIds
  );
  if (proofCandidate) usedClipIds.add(proofCandidate.clip.id);

  const replayCandidate = pickCampaignEntry(
    [...rankedEntries].sort(
      (left, right) =>
        right.scoreBreakdown.find(item => item.label === "Retention")?.value -
          left.scoreBreakdown.find(item => item.label === "Retention")?.value ||
        right.packagingPotential - left.packagingPotential
    ),
    families[1]?.clipIds || [],
    usedClipIds
  );
  if (replayCandidate) usedClipIds.add(replayCandidate.clip.id);

  return [
    bestOverall
      ? {
          id: "stop-scroll",
          label: "Stop Scroll",
          summary: "Lead with the moment most likely to earn the first pause.",
          entry: bestOverall,
        }
      : null,
    proofCandidate
      ? {
          id: "proof",
          label: "Proof Angle",
          summary: "Use the strongest explanatory or convincing version for trust.",
          entry: proofCandidate,
        }
      : null,
    replayCandidate
      ? {
          id: "replay",
          label: "Replay Angle",
          summary: "Use the most replayable or loop-friendly version for retention.",
          entry: replayCandidate,
        }
      : null,
  ].filter(Boolean);
};

const buildClipGuidance = clip => {
  const studioPlan = clip?.studioEditPlan || clip?.studio_edit_plan || null;
  const planEvidence = studioPlan?.evidence || {};
  const planClassification = studioPlan?.classification || {};
  const descriptorText = getClipDescriptorText(clip);
  const duration = getClipDurationSeconds(clip);
  const transcriptText = normalizePlainText(clip?.transcript || clip?.text || "");
  const transcriptWordCount = transcriptText
    ? transcriptText.split(/\s+/).filter(Boolean).length
    : 0;
  const backendScore = clampNumber(clip?.viralScore ?? clip?.viral_score ?? clip?.score, 0, 100, 0);

  const signals = {
    speech:
      transcriptWordCount >= 4 ||
      /(question|asks|says|voice|speaks|talks|explains|dialogue|quote|story|lesson|statement|answer)/i.test(
        descriptorText
      ),
    subject:
      /(face|speaker|person|host|reaction|close[- ]?up|portrait|eye contact|subject|center|centered|framed)/i.test(
        descriptorText
      ),
    motion:
      /(motion|movement|fast|scene|cut|switch|laugh|energy|action|impact|reveal|pace|pacing|dynamic|spike|transition|surprise)/i.test(
        descriptorText
      ),
    idealLength: duration >= 10 && duration <= 25,
    hook:
      /(\?|why|how|what|wait|watch|stop|secret|mistake|truth|never|before|after|until|confession|shocking|emotional|reveal)/i.test(
        descriptorText
      ) ||
      /!/.test(descriptorText) ||
      transcriptWordCount >= 8,
  };

  const narrativeRole = inferRuleMatch(NARRATIVE_ROLE_RULES, descriptorText, {
    id: signals.hook ? "hook" : signals.motion ? "reaction" : "proof",
    label: signals.hook ? "Hook" : signals.motion ? "Reaction" : "Proof",
  });
  const audienceProfile = inferRuleMatch(AUDIENCE_PROFILE_RULES, descriptorText, {
    id: signals.speech ? "education" : signals.motion ? "reaction" : "story",
    label: signals.speech ? "Education" : signals.motion ? "Reaction" : "Story",
  });
  const semanticTokens = getSemanticTokens(descriptorText);
  const familyAnchor = semanticTokens[0] || (signals.motion ? "energy" : "clarity");
  const momentFamilyKey = `${audienceProfile.id}:${narrativeRole.id}:${familyAnchor}`;
  const momentFamilyLabel = `${audienceProfile.label} • ${narrativeRole.label}`;

  const reasons = [];
  if (Array.isArray(studioPlan?.evidence_notes)) {
    reasons.push(...studioPlan.evidence_notes.map(normalizePlainText).filter(Boolean));
  }
  if (signals.speech) reasons.push("Strong speech or a spoken setup lands in the opening seconds");
  if (signals.subject)
    reasons.push("Clear face or centered subject gives viewers something to lock onto");
  if (signals.motion) reasons.push("Fast pacing or a scene change adds momentum early");
  if (signals.idealLength) reasons.push("Length sits in the 10-25 second sweet spot for shorts");
  if (signals.hook) reasons.push("The first beats carry curiosity or hook potential");

  const normalizedReason = normalizePlainText(clip?.reason || "");
  if (normalizedReason && reasons.length < 5) {
    reasons.push(normalizedReason);
  }
  if (clip?.strategyIntent && reasons.length < 5) {
    reasons.push(normalizePlainText(clip.strategyIntent));
  }

  while (reasons.length < 3) {
    reasons.push(
      reasons.length === 0
        ? "The moment is already isolated enough to move into editing quickly"
        : "The segment has clean timing boundaries for short-form packaging"
    );
  }

  const improvements = [];
  if (Array.isArray(studioPlan?.proposed_edits)) {
    improvements.push(
      ...studioPlan.proposed_edits
        .filter(edit => edit?.status === "required")
        .map(edit => normalizePlainText(edit?.reason))
        .filter(Boolean)
    );
  }
  if (!signals.speech || !signals.hook) {
    improvements.push("Cut the first 2 seconds so the first spoken beat lands faster");
  }
  if (!signals.hook) {
    improvements.push("Add a bold hook to sharpen the opening promise");
  }
  if (!signals.speech) {
    improvements.push("Add captions so the opening still lands on mute");
  }
  if (!signals.subject) {
    improvements.push("Use zoom or smart crop to center the main subject");
  }
  if (!signals.motion) {
    improvements.push("Trim into a faster beat or start after the setup");
  }
  if (!signals.idealLength) {
    improvements.push(
      duration < 10
        ? "Extend the clip toward the payoff if a stronger ending is nearby"
        : "Trim the clip closer to the 10-25 second sweet spot"
    );
  }
  if (clip?.studioMove) {
    improvements.unshift(normalizePlainText(clip.studioMove));
  }

  const categories = CATEGORY_TAG_RULES.filter(rule => rule.pattern.test(descriptorText)).slice(
    0,
    3
  );
  if (categories.length === 0) {
    categories.push({
      label: signals.motion ? "High Energy" : "Educational",
      icon: signals.motion ? "🔥" : "🎓",
    });
  }

  const heuristicScore =
    (signals.speech ? 20 : 0) +
    (signals.subject ? 20 : 0) +
    (signals.motion ? 20 : 0) +
    (signals.idealLength ? 20 : 0) +
    (signals.hook ? 20 : 0);

  const captionReadiness = clampNumber(
    (transcriptWordCount >= 8 ? 42 : transcriptWordCount >= 4 ? 28 : 12) +
      (signals.speech ? 24 : 0) +
      (signals.hook ? 18 : 0) +
      (duration <= 32 ? 16 : 6),
    0,
    100,
    0
  );
  const thumbnailPotential = clampNumber(
    (signals.subject ? 34 : 14) +
      (signals.motion ? 14 : 0) +
      (signals.hook ? 22 : 0) +
      (/(shock|surprise|face|reaction|before|after|truth|secret|mistake|result)/i.test(
        descriptorText
      )
        ? 20
        : 8),
    0,
    100,
    0
  );
  const retentionPotential = clampNumber(
    (signals.hook ? 28 : 10) +
      (signals.motion ? 22 : 6) +
      (signals.idealLength ? 20 : duration < 8 ? 8 : 12) +
      (/(reveal|wait|until|next|watch|switch|flip|turning point|answer)/i.test(descriptorText)
        ? 18
        : 6),
    0,
    100,
    0
  );
  const conversionPotential = clampNumber(
    (signals.speech ? 26 : 10) +
      (/(tutorial|lesson|how|why|product|offer|sale|mistake|truth|secret|guide)/i.test(
        descriptorText
      )
        ? 34
        : 12) +
      (signals.subject ? 16 : 6) +
      (signals.idealLength ? 14 : 8),
    0,
    100,
    0
  );
  const semanticArcScore = clampNumber(
    (signals.hook ? 24 : 10) +
      (signals.speech ? 18 : 8) +
      (/(before|after|then|because|so|when|until|result|lesson|truth|secret|finally|instead)/i.test(
        descriptorText
      )
        ? 26
        : 8) +
      (narrativeRole.id === "payoff" ? 18 : narrativeRole.id === "proof" ? 14 : 10) +
      (audienceProfile.id === "performance" ? 12 : 8),
    0,
    100,
    0
  );
  const packagingPotential = Math.round(
    thumbnailPotential * 0.34 +
      captionReadiness * 0.24 +
      retentionPotential * 0.28 +
      conversionPotential * 0.14
  );
  const editorialScore = Math.round(
    heuristicScore * 0.34 +
      packagingPotential * 0.28 +
      semanticArcScore * 0.22 +
      backendScore * 0.16
  );
  const bestFor = [];
  if (clip?.bestFor) bestFor.push(`Best for ${clip.bestFor}`);
  if (retentionPotential >= 72) bestFor.push("Best for retention");
  if (thumbnailPotential >= 68) bestFor.push("Best for thumbnail-led packaging");
  if (conversionPotential >= 68) bestFor.push("Best for conversion");
  if (captionReadiness >= 68) bestFor.push("Best for caption-first cuts");
  if (!bestFor.length) bestFor.push("Best for balanced publishing");

  const openingMove = clip?.hookText
    ? `Open with "${normalizePlainText(clip.hookText)}" and make the first beat impossible to ignore.`
    : retentionPotential >= 74
      ? "Open on the strongest beat and hit it with a fast curiosity hook."
      : thumbnailPotential >= 72
        ? "Lead with the clearest face or object frame and let the visual promise carry the intro."
        : conversionPotential >= 70
          ? "Use a direct claim-and-proof opening instead of a vague teaser."
          : "Trim the setup and make the payoff arrive faster.";
  const exportFit =
    retentionPotential >= 74
      ? "Shorts and Reels"
      : conversionPotential >= 70
        ? "YouTube and conversion-led clips"
        : "Cross-platform short form";
  const travelReason = buildTravelReason(narrativeRole, audienceProfile, signals);
  const recutVariants = buildRecutVariants(clip, {
    signals,
    duration,
    narrativeRole,
    audienceProfile,
  });

  return {
    studioPlan,
    analysisSource: studioPlan ? "worker_evidence_plan" : "frontend_fallback",
    contentFamily: planClassification.content_family || null,
    evidenceStrength: Number(planEvidence.evidence_strength || 0),
    descriptorText,
    duration,
    backendScore,
    score: clampNumber(editorialScore, 0, 100, 0),
    heuristicScore,
    semanticArcScore,
    packagingPotential,
    reasons: reasons.slice(0, 5),
    improvements: [...new Set(improvements)].slice(0, 3),
    categories,
    signals,
    narrativeRole,
    audienceProfile,
    semanticTokens,
    momentFamilyKey,
    momentFamilyLabel,
    travelReason,
    bestFor: bestFor.slice(0, 2),
    exportFit,
    openingMove,
    recutVariants,
    scoreBreakdown: [
      {
        label: "Hook",
        value: clampNumber(
          Number(clip?.scoreBreakdown?.hook) ||
            Math.round((signals.hook ? 70 : 34) + retentionPotential * 0.3),
          0,
          100,
          0
        ),
      },
      { label: "Retention", value: retentionPotential },
      { label: "Thumbnail", value: thumbnailPotential },
      {
        label: "Captions",
        value: clampNumber(Number(clip?.scoreBreakdown?.speech) || captionReadiness, 0, 100, 0),
      },
      { label: "Conversion", value: conversionPotential },
      { label: "Story", value: semanticArcScore },
    ],
    hookText: clip?.hookText || getHookCopySuggestions(clip)[0] || DEFAULT_HOOK_TEXT,
    strategyLabel: clip?.strategyLabel || null,
    campaignRole: clip?.campaignRole || null,
  };
};

const isGenericHookText = value => GENERIC_HOOK_TEXTS.has(normalizeHookText(value).toUpperCase());

const formatPreviewTimePrecise = value => {
  const totalSeconds = Math.max(0, Number(value) || 0);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = (totalSeconds % 60).toFixed(totalSeconds >= 10 ? 1 : 2).padStart(4, "0");
  return `${minutes}:${seconds}`;
};

const formatDirectorReviewRange = range => {
  if (!range) return "—";
  if (Number.isSafeInteger(range.atTick)) {
    return `${range.atTick} ticks (${ticksToSeconds(range.atTick).toFixed(5)}s)`;
  }
  if (!Number.isSafeInteger(range.startTick) || !Number.isSafeInteger(range.endTick)) {
    return "—";
  }
  return `${range.startTick}–${range.endTick} ticks ` +
    `(${ticksToSeconds(range.startTick).toFixed(5)}–${ticksToSeconds(range.endTick).toFixed(5)}s)`;
};

const formatEditorDuration = value => {
  const totalSeconds = Math.max(0, Number(value) || 0);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  const secondsLabel = seconds.toFixed(seconds % 1 ? 1 : 0).padStart(2, "0");
  return hours > 0
    ? `${hours}:${String(minutes).padStart(2, "0")}:${secondsLabel}`
    : `${minutes}:${secondsLabel}`;
};

const parseEditorDuration = value => {
  const raw = String(value ?? "")
    .trim()
    .toLowerCase();
  if (!raw) return null;

  if (raw.includes(":")) {
    const parts = raw.split(":").map(Number);
    if (parts.some(part => !Number.isFinite(part) || part < 0) || parts.length > 3) return null;
    if (parts.length === 2) return parts[0] * 60 + parts[1];
    if (parts.length === 3) return parts[0] * 3600 + parts[1] * 60 + parts[2];
  }

  const unitPattern = /(\d+(?:\.\d+)?)\s*(hrs|hr|h|mins|min|m|secs|sec|s)/g;
  const unitMatches = [...raw.matchAll(unitPattern)];
  if (unitMatches.length) {
    const remaining = raw.replace(unitPattern, "").trim();
    if (remaining) return null;
    return unitMatches.reduce((total, match) => {
      const amount = Number(match[1]);
      const unit = match[2];
      if (unit.startsWith("h")) return total + amount * 3600;
      if (unit.startsWith("m")) return total + amount * 60;
      return total + amount;
    }, 0);
  }

  const numeric = Number(raw);
  return Number.isFinite(numeric) && numeric >= 0 ? numeric : null;
};

const normalizeBRollEndBehavior = value =>
  ["return", "loop", "hold"].includes(String(value || "").toLowerCase())
    ? String(value).toLowerCase()
    : "return";

const getOverlayAvailableSourceDuration = overlay => {
  if (!overlay || overlay.type !== "video") return Number.POSITIVE_INFINITY;
  const sourceDuration = Math.max(0, Number(overlay.sourceDuration || 0));
  if (!sourceDuration) return Number.POSITIVE_INFINITY;
  const sourceStart = clampNumber(
    overlay.sourceStartTime,
    0,
    Math.max(0, sourceDuration - 0.05),
    0
  );
  return Math.max(0.05, sourceDuration - sourceStart);
};

const getOverlayVisibleDuration = overlay => {
  const configuredDuration = Math.max(0, Number(overlay?.duration || 0));
  if (!overlay || overlay.type !== "video") return configuredDuration;
  if (normalizeBRollEndBehavior(overlay.sourceEndBehavior) !== "return") {
    return configuredDuration;
  }
  return Math.min(configuredDuration, getOverlayAvailableSourceDuration(overlay));
};

const getOverlayAudibleDuration = overlay => {
  const configuredDuration = Math.max(0, Number(overlay?.duration || 0));
  if (!overlay || overlay.type !== "video") return configuredDuration;
  if (normalizeBRollEndBehavior(overlay.sourceEndBehavior) === "loop") {
    return configuredDuration;
  }
  return Math.min(configuredDuration, getOverlayAvailableSourceDuration(overlay));
};

const waitForVideoEvent = (video, successEvent, failureEvent = "error") =>
  new Promise((resolve, reject) => {
    const handleSuccess = () => {
      video.removeEventListener(successEvent, handleSuccess);
      video.removeEventListener(failureEvent, handleFailure);
      resolve();
    };

    const handleFailure = () => {
      video.removeEventListener(successEvent, handleSuccess);
      video.removeEventListener(failureEvent, handleFailure);
      reject(new Error("Video analysis could not read the selected clip."));
    };

    video.addEventListener(successEvent, handleSuccess, { once: true });
    video.addEventListener(failureEvent, handleFailure, { once: true });
  });

const seekAnalysisVideo = async (video, targetTime) => {
  const boundedTime = Math.max(0, Number(targetTime) || 0);

  if (Math.abs((video.currentTime || 0) - boundedTime) < 0.025) {
    return;
  }

  await new Promise((resolve, reject) => {
    const handleSeeked = () => {
      video.removeEventListener("seeked", handleSeeked);
      video.removeEventListener("error", handleError);
      resolve();
    };

    const handleError = () => {
      video.removeEventListener("seeked", handleSeeked);
      video.removeEventListener("error", handleError);
      reject(new Error("Video analysis could not seek to the requested moment."));
    };

    video.addEventListener("seeked", handleSeeked, { once: true });
    video.addEventListener("error", handleError, { once: true });
    video.currentTime = boundedTime;
  });
};

const captureHookAnalysisFrame = (context, video, width, height, previousFrame) => {
  context.drawImage(video, 0, 0, width, height);
  const imageData = context.getImageData(0, 0, width, height).data;
  const grayscale = new Uint8Array(width * height);

  const centerStartX = Math.floor(width * 0.25);
  const centerEndX = Math.ceil(width * 0.75);
  const centerStartY = Math.floor(height * 0.18);
  const centerEndY = Math.ceil(height * 0.82);

  let luminanceTotal = 0;
  let luminanceSquareTotal = 0;
  let frameDeltaTotal = 0;
  let centerDeltaTotal = 0;
  let centerPixels = 0;

  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      const pixelIndex = y * width + x;
      const dataIndex = pixelIndex * 4;
      const luminance =
        (imageData[dataIndex] * 77 +
          imageData[dataIndex + 1] * 150 +
          imageData[dataIndex + 2] * 29) >>
        8;

      grayscale[pixelIndex] = luminance;
      luminanceTotal += luminance;
      luminanceSquareTotal += luminance * luminance;

      if (previousFrame) {
        const delta = Math.abs(luminance - previousFrame[pixelIndex]);
        frameDeltaTotal += delta;

        if (x >= centerStartX && x <= centerEndX && y >= centerStartY && y <= centerEndY) {
          centerDeltaTotal += delta;
          centerPixels += 1;
        }
      }
    }
  }

  const pixelCount = width * height;
  const averageLuminance = luminanceTotal / pixelCount;
  const variance = Math.max(
    0,
    luminanceSquareTotal / pixelCount - averageLuminance * averageLuminance
  );
  const contrast = Math.min(1, Math.sqrt(variance) / 72);
  const motion = previousFrame ? frameDeltaTotal / (pixelCount * 255) : 0;
  const centerMotion =
    previousFrame && centerPixels > 0 ? centerDeltaTotal / (centerPixels * 255) : motion;

  return {
    grayscale,
    brightness: averageLuminance / 255,
    contrast,
    motion,
    centerMotion,
  };
};

const buildFallbackHookRange = (clip, clipDuration = 0) => {
  const baseSuggestion = getHookSuggestion(clip);
  const template = getHookTemplateConfig(baseSuggestion.templateKey);
  const minimumDuration = Math.min(
    HOOK_MIN_SEGMENT_DURATION,
    Math.max(0.25, Number(clipDuration) || HOOK_MIN_SEGMENT_DURATION)
  );
  const maximumDuration = Math.min(
    HOOK_MAX_SEGMENT_DURATION,
    Math.max(minimumDuration, Number(clipDuration) || HOOK_MAX_SEGMENT_DURATION)
  );
  const boundedStart = clampNumber(
    baseSuggestion.suggestedStart,
    0,
    Math.max(0, Number(clipDuration || 0) - minimumDuration),
    0.7
  );
  const duration = clampNumber(template.duration, minimumDuration, maximumDuration, 3);
  const endTime =
    Number(clipDuration) > 0
      ? Math.min(Number(clipDuration), boundedStart + duration)
      : boundedStart + duration;

  return {
    ...baseSuggestion,
    startTime: boundedStart,
    endTime: Math.max(boundedStart + minimumDuration, endTime),
    duration: Math.max(minimumDuration, endTime - boundedStart),
    confidenceLabel: "Quick read",
    analysisSource: "metadata",
    score: 0,
  };
};

const getWatermarkPreviewRegions = mode => {
  switch (
    String(mode || "adaptive")
      .trim()
      .toLowerCase()
  ) {
    case "top_right":
      return [{ top: "4%", right: "4%", width: "24%", height: "8%", rotation: -2, opacity: 0.88 }];
    case "bottom_left":
      return [
        { bottom: "6%", left: "4%", width: "28%", height: "8%", rotation: 1.5, opacity: 0.9 },
      ];
    case "all":
      return [
        { top: "4%", left: "4%", width: "24%", height: "8%", rotation: 1, opacity: 0.84 },
        { top: "4%", right: "4%", width: "24%", height: "8%", rotation: -2, opacity: 0.88 },
        { bottom: "6%", left: "4%", width: "28%", height: "8%", rotation: 1.5, opacity: 0.9 },
        { bottom: "6%", right: "4%", width: "28%", height: "8%", rotation: -1.5, opacity: 0.86 },
      ];
    case "corners":
      return [
        { top: "4%", right: "4%", width: "24%", height: "8%", rotation: -2, opacity: 0.88 },
        { bottom: "6%", left: "4%", width: "28%", height: "8%", rotation: 1.5, opacity: 0.9 },
      ];
    case "adaptive":
    default:
      return [
        { top: "4%", right: "4%", width: "24%", height: "8%", rotation: -2, opacity: 0.88 },
        { bottom: "6%", left: "4%", width: "28%", height: "8%", rotation: 1.5, opacity: 0.9 },
      ];
  }
};

const getCaptionPreviewSourceText = clip => {
  const transcript = normalizePlainText(clip?.text || clip?.transcript || "");
  return transcript;
};

const captionLoopSignature = segment => {
  const tokens = normalizePlainText(segment?.text).match(/\p{L}+/gu)?.map(token => token.toLowerCase()) || [];
  const repeatedVocalization = tokens[0]?.length <= 2 || /^[\u0400-\u04ff]+$/u.test(tokens[0] || "");
  return tokens.length && repeatedVocalization && tokens.every(token => token === tokens[0])
    ? tokens[0]
    : null;
};

const filterClearlyCorruptCaptionSegments = segments => {
  const rows = Array.isArray(segments) ? segments : [];
  const repeatedVocalizationRuns = new Set();
  for (let start = 0; start < rows.length;) {
    const signature = captionLoopSignature(rows[start]);
    let end = start + 1;
    while (signature && end < rows.length && captionLoopSignature(rows[end]) === signature) {
      end += 1;
    }
    if (
      signature && end - start >= 3 &&
      Number(rows[end - 1]?.end ?? 0) - Number(rows[start]?.start ?? 0) >= 6
    ) {
      for (let index = start; index < end; index += 1) repeatedVocalizationRuns.add(index);
    }
    start = end;
  }
  return rows.filter((segment, index) => {
    if (repeatedVocalizationRuns.has(index)) return false;
    const text = normalizePlainText(segment?.text);
    const tokens = text.match(/\p{L}+/gu)?.map(token => token.toLowerCase()) || [];
    const shortLoop = tokens.length >= 4 && tokens[0].length <= 2 &&
      tokens.filter(token => token === tokens[0]).length / tokens.length >= 0.75;
    const cyrillicLoop = tokens.length >= 4 && /^[\u0400-\u04ff]+$/u.test(tokens[0]) &&
      tokens.filter(token => token === tokens[0]).length / tokens.length >= 0.75;
    const duration = Math.max(
      0.05,
      Number(segment?.end ?? segment?.end_time ?? 0) - Number(segment?.start ?? segment?.start_time ?? 0)
    );
    const compactLength = text.replace(/\s+/g, "").length;
    return !shortLoop && !cyrillicLoop && !/(\p{L}{2,8})\1{4,}/iu.test(text) &&
      !(compactLength >= 80 && compactLength / duration > 60);
  });
};

const normalizeCaptionSegments = segments =>
  (Array.isArray(segments) ? segments : [])
    .map((segment, index) => {
      const start = Math.max(0, Number(segment?.start ?? segment?.start_time ?? 0));
      const end = Math.max(start + 0.05, Number(segment?.end ?? segment?.end_time ?? start + 0.8));
      const text = normalizePlainText(segment?.text || "");
      if (!text) return null;
      const words = text.split(/\s+/).filter(Boolean);
      const language = normalizeCaptionLanguage(
        segment?.language ?? segment?.language_code ?? segment?.languageCode
      );
      const languages = Array.from(
        new Set(
          (Array.isArray(segment?.languages) ? segment.languages : [])
            .map(normalizeCaptionLanguage)
            .filter(item => item !== "und")
        )
      );
      const speaker = normalizeCaptionSpeaker(
        segment?.speaker ?? segment?.speaker_id ?? segment?.speakerId
      );
      const textReviewRequired = Boolean(
        segment?.textReviewRequired ?? segment?.text_review_required
      );
      const textReviewed = Boolean(segment?.textReviewed ?? segment?.text_reviewed);
      return {
        id: segment?.id || `transcript-caption-${index}`,
        start,
        end,
        duration: end - start,
        text,
        words,
        sourceClipId: segment?.sourceClipId ?? segment?.source_clip_id ?? null,
        speaker,
        speakerLabel:
          normalizePlainText(segment?.speakerLabel ?? segment?.speaker_label) ||
          getCaptionSpeakerLabel(speaker),
        language: languages.length > 1 ? "mixed" : language,
        languageLabel:
          normalizePlainText(segment?.languageLabel ?? segment?.language_label) ||
          getCaptionLanguageLabel(languages.length > 1 ? "mixed" : language),
        languages,
        languageConfidence: clampNumber(
          segment?.languageConfidence ?? segment?.language_confidence,
          0,
          1,
          0
        ),
        translatedToEnglish: Boolean(
          segment?.translatedToEnglish ?? segment?.translated_to_english
        ),
        transcriptConfidence: clampNumber(segment?.transcriptConfidence, 0, 1, 0),
        transcriptionReviewReasons: Array.isArray(segment?.transcriptionReviewReasons)
          ? segment.transcriptionReviewReasons
          : [],
        textReviewRequired,
        textReviewed,
        captionPlacement: normalizePlainText(
          segment?.captionPlacement ?? segment?.caption_placement ?? segment?.placement ?? "auto"
        ),
        captionIcon: normalizePlainText(
          segment?.captionIcon ?? segment?.caption_icon ?? segment?.icon ?? "auto"
        ),
        captionAccent: /^#[0-9a-f]{6}$/i.test(String(segment?.captionAccent ?? segment?.caption_accent ?? ""))
          ? String(segment?.captionAccent ?? segment?.caption_accent).toLowerCase()
          : "",
        captionX: clampNumber(segment?.captionX ?? segment?.caption_x, 5, 95, 50),
        captionY: clampNumber(segment?.captionY ?? segment?.caption_y, 5, 95, 82),
        reviewRequired:
          Boolean(segment?.reviewRequired ?? segment?.review_required) ||
          speaker === "unknown" ||
          language === "und" ||
          (textReviewRequired && !textReviewed),
      };
    })
    .filter(Boolean);

const ensureUniqueCaptionIds = segments => {
  const seen = new Set();
  return (Array.isArray(segments) ? segments : []).map((segment, index) => {
    const base = String(segment?.id || `caption-line-${index + 1}`);
    let id = base;
    let copy = 2;
    while (seen.has(id)) id = `${base}-copy-${copy++}`;
    seen.add(id);
    return id === segment?.id ? segment : { ...segment, id };
  });
};

const parseSrtOrVtt = text => {
  if (!text || typeof text !== "string") return [];
  const lines = text.replace(/\r\n/g, "\n").replace(/\r/g, "\n").split("\n");
  const segments = [];
  let currentStart = null;
  let currentEnd = null;
  let currentText = [];

  const timeRegex = /(?:(\d{1,2}):)?(\d{2}):(\d{2})[,.](\d{3})\s*-->\s*(?:(\d{1,2}):)?(\d{2}):(\d{2})[,.](\d{3})/;

  const parseTime = (h, m, s, ms) => {
    const hours = parseInt(h || "0", 10);
    const minutes = parseInt(m, 10);
    const seconds = parseInt(s, 10);
    const milliseconds = parseInt(ms, 10);
    return hours * 3600 + minutes * 60 + seconds + milliseconds / 1000;
  };

  for (let line of lines) {
    line = line.trim();
    if (!line) {
      if (currentStart !== null && currentText.length > 0) {
        const fullText = currentText.join(" ");
        segments.push({
          start: currentStart,
          end: currentEnd,
          duration: Math.max(0.1, currentEnd - currentStart),
          text: fullText,
          words: fullText.split(/\s+/).filter(Boolean),
        });
        currentStart = null;
        currentEnd = null;
        currentText = [];
      }
      continue;
    }

    const match = line.match(timeRegex);
    if (match) {
      if (currentStart !== null && currentText.length > 0) {
        const fullText = currentText.join(" ");
        segments.push({
          start: currentStart,
          end: currentEnd,
          duration: Math.max(0.1, currentEnd - currentStart),
          text: fullText,
          words: fullText.split(/\s+/).filter(Boolean),
        });
        currentText = [];
      }
      currentStart = parseTime(match[1], match[2], match[3], match[4]);
      currentEnd = parseTime(match[5], match[6], match[7], match[8]);
      continue;
    }

    if (/^\d+$/.test(line) && currentStart === null) continue;
    if (line.startsWith("WEBVTT")) continue;

    if (currentStart !== null) {
      currentText.push(line);
    }
  }

  if (currentStart !== null && currentText.length > 0) {
    const fullText = currentText.join(" ");
    segments.push({
      start: currentStart,
      end: currentEnd,
      duration: Math.max(0.1, currentEnd - currentStart),
      text: fullText,
      words: fullText.split(/\s+/).filter(Boolean),
    });
  }

  return segments;
};

const generateClientSideCaptionSegments = ({ text = "", duration = 30, sourceClipId = null }) => {
  const safeDuration = Math.max(2, Number(duration || 30));
  const rawText = (text || "").trim();
  const segments = [];

  if (rawText) {
    const words = rawText.split(/\s+/).filter(Boolean);
    const chunkSize = 4;
    const totalChunks = Math.max(1, Math.ceil(words.length / chunkSize));
    const segmentDuration = Math.min(3.2, Math.max(1.0, safeDuration / totalChunks));

    for (let i = 0; i < totalChunks; i++) {
      const chunkWords = words.slice(i * chunkSize, (i + 1) * chunkSize);
      if (!chunkWords.length) continue;
      const start = Math.round(i * segmentDuration * 10) / 10;
      const end = Math.round(Math.min(safeDuration, start + segmentDuration) * 10) / 10;
      const chunkText = chunkWords.join(" ");
      segments.push({
        id: createSecureId("caption-line"),
        start,
        end,
        duration: Math.max(0.1, end - start),
        text: chunkText,
        words: chunkWords,
        speaker: "speaker_1",
        speakerLabel: "Speaker 1",
        language: "en",
        languageLabel: "English",
        languages: ["en"],
        languageConfidence: 1,
        textReviewRequired: false,
        textReviewed: true,
        captionPlacement: "auto",
        captionIcon: "auto",
        reviewRequired: false,
        sourceClipId,
      });
    }
  } else {
    const step = 2.4;
    const count = Math.min(12, Math.max(3, Math.floor(safeDuration / step)));
    const defaultPhrases = [
      "Tap here to edit this line",
      "Add your spoken words here",
      "Dynamic viral caption line",
      "Keep viewers hooked to the end",
      "Customize text in the sidebar",
      "Kinetic text bounces with sound",
      "Edit timestamps or add lines",
      "Export with burned-in subtitles",
    ];

    for (let i = 0; i < count; i++) {
      const start = Math.round(i * step * 10) / 10;
      const end = Math.round(Math.min(safeDuration, start + step) * 10) / 10;
      const phrase = defaultPhrases[i % defaultPhrases.length];
      const phraseWords = phrase.split(" ");
      segments.push({
        id: createSecureId("caption-line"),
        start,
        end,
        duration: Math.max(0.1, end - start),
        text: phrase,
        words: phraseWords,
        speaker: "speaker_1",
        speakerLabel: "Speaker 1",
        language: "en",
        languageLabel: "English",
        languages: ["en"],
        languageConfidence: 1,
        textReviewRequired: true,
        textReviewed: false,
        captionPlacement: "auto",
        captionIcon: "auto",
        reviewRequired: true,
        sourceClipId,
      });
    }
  }

  return segments;
};

const resolveInitialCaptionSegments = (clip, duration) => {
  if (Array.isArray(clip?.segments) && clip.segments.length) {
    return normalizeCaptionSegments(splitCaptionSegmentsForReadability(clip.segments));
  }
  if (Array.isArray(clip?.captions) && clip.captions.length) {
    return normalizeCaptionSegments(splitCaptionSegmentsForReadability(clip.captions));
  }
  const text = normalizePlainText(
    clip?.transcript || clip?.text || clip?.hookText || ""
  );
  if (!text) {
    return [];
  }
  const clipDuration = Math.max(3, Number(duration || clip?.duration || 30));
  return generateClientSideCaptionSegments({
    text,
    duration: clipDuration,
    sourceClipId: clip?.id || null,
  });
};

const getTimedCaptionPreviewState = ({ segments, sourceTime, clipStartTime = 0, styleName = "bold_pop", captionScale = 1 }) => {
  const chunks = buildCaptionRenderPreviewChunks(normalizeCaptionSegments(segments), styleName, captionScale);
  if (!chunks.length) {
    return {
      chunks: [],
      currentChunk: null,
      nextChunk: null,
      activeWordIndex: 0,
      previewDuration: 0,
    };
  }

  const rawTime = Math.max(0, Number(sourceTime || 0));
  const clipStart = Math.max(0, Number(clipStartTime || 0));

  // 1. Direct match with rawTime (source video timestamp)
  let currentIndex = chunks.findIndex(
    segment => rawTime >= segment.start && rawTime < segment.end
  );

  // 2. Relative match with (rawTime - clipStart) (0-based clip timestamps)
  if (currentIndex === -1) {
    const relTime = Math.max(0, rawTime - clipStart);
    currentIndex = chunks.findIndex(
      segment => relTime >= segment.start && relTime < segment.end
    );
  }

  // 3. Absolute match with (rawTime + clipStart)
  if (currentIndex === -1 && clipStart > 0) {
    const absTime = rawTime + clipStart;
    currentIndex = chunks.findIndex(
      segment => absTime >= segment.start && absTime < segment.end
    );
  }

  // Reviewed cue ranges are half-open. A gap, pre-roll or post-roll cannot
  // display a held caption that the final renderer will not produce.
  const currentChunk = currentIndex >= 0 ? chunks[currentIndex] : null;
  const nextChunk =
    currentIndex >= 0 && currentIndex + 1 < chunks.length ? chunks[currentIndex + 1] : null;
  const chunkStart = currentChunk ? currentChunk.start : 0;
  const chunkDur = currentChunk ? Math.max(currentChunk.duration, 0.05) : 1;
  const effectiveTime =
    rawTime >= chunkStart ? rawTime : rawTime - clipStart >= chunkStart ? rawTime - clipStart : chunkStart;
  const elapsedInChunk = Math.max(0, effectiveTime - chunkStart);

  const activeWordIndex =
    currentChunk && Array.isArray(currentChunk.words) && currentChunk.words.length
      ? Math.min(
          currentChunk.words.length - 1,
          Math.floor(
            clampNumber(elapsedInChunk / chunkDur, 0, 0.999, 0) * currentChunk.words.length
          )
        )
      : 0;

  return {
    chunks,
    currentChunk,
    nextChunk,
    activeWordIndex,
    previewDuration: chunks[chunks.length - 1]?.end || 0,
  };
};

const resolveCaptionStoryPreviewTreatment = (segment, fallbackPosition = "lower") => {
  const text = normalizePlainText(segment?.text).toLowerCase();
  const requestedPlacement = normalizePlainText(segment?.captionPlacement || "auto").toLowerCase();
  let concept = "story";
  let placement = CAPTION_PLACEMENT_OPTIONS.some(
    option => option.value === fallbackPosition && !["auto", "custom"].includes(option.value)
  )
    ? fallbackPosition
    : {
      top: "top_left",
      center: "middle_left",
      middle: "middle_left",
      lower: "bottom_center",
      bottom: "bottom_center",
      }[fallbackPosition] || "bottom_center";

  if (/facebook|scroll|timeline|online/.test(text)) {
    concept = "phone";
  } else if (/cape town|ekapa|durban|johannesburg/.test(text)) {
    concept = "place";
  } else if (/i can do this|angivuke|decide|ngadecide/.test(text)) {
    concept = "payoff";
  } else if (/choir|sing|ngiyocula|egazini|brothers and sisters/.test(text)) {
    concept = "music";
  }

  if (CAPTION_PLACEMENT_OPTIONS.some(option => option.value === requestedPlacement)) {
    placement = requestedPlacement === "auto" ? placement : requestedPlacement;
  }

  const accents = {
    phone: "#6ad0f5",
    music: "#ff6ba8",
    place: "#ffe86c",
    payoff: "#a6f598",
    story: "#ffb33d",
  };
  const requestedIcon = normalizePlainText(segment?.captionIcon || "auto").toLowerCase();
  const iconConcept = requestedIcon && requestedIcon !== "auto" ? requestedIcon : concept;
  const icons = {
    laugh: "☼",
    fire: "♨",
    heart: "♥",
    wow: "❗",
    clap: "✋",
    hundred: "✹",
    eyes: "👁",
    crown: "♛",
    mic: "🎙",
    phone: "☎",
    music: "♫",
    place: "⌖",
    payoff: "⚡",
    story: "✦",
    none: "",
  };

  return {
    placement,
    accent: /^#[0-9a-f]{6}$/i.test(String(segment?.captionAccent || ""))
      ? segment.captionAccent
      : accents[concept] || accents.story,
    icon: icons[iconConcept] ?? icons.story,
    x: clampNumber(segment?.captionX, 5, 95, 50),
    y: clampNumber(segment?.captionY, 5, 95, 82),
  };
};

const buildCaptionPreviewChunks = text => {
  const words = normalizePlainText(text).split(/\s+/).filter(Boolean);

  if (!words.length) return [];

  const wordsPerChunk = words.length >= 16 ? 4 : words.length >= 9 ? 3 : 2;
  const chunks = [];

  for (let index = 0; index < words.length; index += wordsPerChunk) {
    const chunkWords = words.slice(index, index + wordsPerChunk);
    chunks.push({
      id: `caption-chunk-${index}`,
      text: chunkWords.join(" "),
      words: chunkWords,
    });
  }

  return chunks;
};

const getCaptionPreviewState = ({ text, localTime, duration, hideAfterEnd = false }) => {
  const chunks = buildCaptionPreviewChunks(text);
  if (!chunks.length)
    return {
      chunks: [],
      currentChunk: null,
      nextChunk: null,
      activeWordIndex: 0,
      previewDuration: 0,
    };

  const safeDuration = Math.max(Number(duration) || 0, chunks.length * 0.85, 1.8);
  if (hideAfterEnd && Number(localTime || 0) >= safeDuration) {
    return {
      chunks,
      currentChunk: null,
      nextChunk: null,
      activeWordIndex: 0,
      previewDuration: safeDuration,
    };
  }
  const clampedTime = clampNumber(localTime, 0, safeDuration, 0);
  const chunkProgress = clampNumber(clampedTime / safeDuration, 0, 0.999, 0);
  const currentChunkIndex = Math.min(chunks.length - 1, Math.floor(chunkProgress * chunks.length));
  const currentChunk = chunks[currentChunkIndex];
  const nextChunk = chunks[currentChunkIndex + 1] || null;
  const chunkStart = (safeDuration / chunks.length) * currentChunkIndex;
  const chunkDuration = safeDuration / chunks.length;
  const intraChunkProgress = clampNumber(
    (clampedTime - chunkStart) / Math.max(chunkDuration, 0.01),
    0,
    0.999,
    0
  );
  const activeWordIndex = Math.min(
    currentChunk.words.length - 1,
    Math.floor(intraChunkProgress * currentChunk.words.length)
  );

  return { chunks, currentChunk, nextChunk, activeWordIndex, previewDuration: safeDuration };
};

const clampManualWatermarkRegion = region => {
  const width = clampNumber(region?.width, 8, 58, 24);
  const height = clampNumber(region?.height, 4, 24, 8);
  const left = clampNumber(region?.left, 0, 100 - width, 4);
  const top = clampNumber(region?.top, 0, 100 - height, 4);

  return {
    id: region?.id || `watermark-${Date.now()}`,
    left,
    top,
    width,
    height,
    rotation: clampNumber(region?.rotation, -12, 12, 0),
    opacity: clampNumber(region?.opacity, 0.45, 1, 0.88),
    track: region?.track !== false,
    seedTime: clampNumber(region?.seedTime, 0, 36000, 0),
  };
};

const createManualWatermarkRegion = () =>
  clampManualWatermarkRegion({
    id: createSecureId("watermark"),
    left: 4,
    top: 4,
    width: 26,
    height: 8,
    rotation: 0,
    opacity: 0.88,
    track: true,
    seedTime: 0,
  });

const toWatermarkPreviewStyle = region => ({
  left: `${Number(region.left || 0)}%`,
  top: `${Number(region.top || 0)}%`,
  width: `${Number(region.width || 0)}%`,
  height: `${Number(region.height || 0)}%`,
  "--cleanup-rotation": `${Number(region.rotation || 0)}deg`,
  "--cleanup-opacity": Number(region.opacity || 0.88),
});

const serializeManualWatermarkRegions = regions =>
  (Array.isArray(regions) ? regions : []).map(region => ({
    left: clampNumber(region?.left, 0, 100, 0),
    top: clampNumber(region?.top, 0, 100, 0),
    width: clampNumber(region?.width, 0, 100, 0),
    height: clampNumber(region?.height, 0, 100, 0),
    track: region?.track !== false,
    seed_time: clampNumber(region?.seedTime, 0, 36000, 0),
  }));

const getAudioExtractionStageLabel = (stage, progress) => {
  const normalizedStage = String(stage || "")
    .trim()
    .toLowerCase();
  switch (normalizedStage) {
    case "queued_for_dispatch":
      return "Preparing extraction job...";
    case "queued_for_worker":
      return "Waiting for extraction worker...";
    case "downloading_source":
      return `Downloading donor video... ${Math.round(progress)}%`;
    case "extracting_audio":
      return `Extracting audio... ${Math.round(progress)}%`;
    case "uploading_audio":
      return `Uploading extracted audio... ${Math.round(progress)}%`;
    case "completed":
      return "Background audio added to the timeline.";
    default:
      return `Extracting audio... ${Math.round(progress)}%`;
  }
};

const RainbowText = ({ text, offset = 0 }) => {
  const safeText = normalizePlainText(text);
  if (!safeText) return null;
  return (
    <span
      style={{
        display: "inline-block",
        fontWeight: "900",
        textShadow: "3px 3px 0 #000", // Thicker outline
        WebkitTextStroke: "1.5px black", // Crisp outline
        fontFamily: '"Comic Sans MS", "Chalkboard SE", "Marker Felt", sans-serif',
        fontSize: "24px", // Bigger by default
      }}
    >
      {safeText.split("").map((char, index) => (
        <span
          key={index}
          style={{ color: RAINBOW_COLORS[(index + offset) % RAINBOW_COLORS.length] }}
        >
          {char}
        </span>
      ))}
    </span>
  );
};

const sidebarSectionTitleStyle = {
  margin: "0 0 10px 0",
  color: "#fff8ec",
  fontWeight: 800,
};

const sidebarCheckboxLabelStyle = {
  display: "block",
  cursor: "pointer",
  color: "#f8fafc",
  fontWeight: 700,
};

const sidebarBodyTextStyle = {
  fontSize: "13px",
  color: "rgba(247, 248, 251, 0.74)",
  fontWeight: 600,
  lineHeight: 1.45,
};

const VIRAL_STUDIO_WORKFLOW = [
  {
    label: "Moments",
    helper: "Choose the source clip",
  },
  {
    label: "Hook",
    helper: "Shape the opening seconds",
  },
  {
    label: "B-roll",
    helper: "Build the visual timeline",
  },
  {
    label: "Export",
    helper: "Review and render",
  },
];

const CREATIVE_STUDIO_TOOLS = [
  { id: "moments", label: "Moments", icon: "✦", helper: "Choose a source range. Review Director suggestions before applying them to the timeline." },
  { id: "cut", label: "Cut", icon: "✂", helper: "Select a video clip for trim, split, delete and precision edits. Ripple edits move the following clips; slip edits change the source range." },
  { id: "hook", label: "Hook", icon: "⌁", helper: "Set opening text, style, focus and timing. This changes the opening of the clip, not the underlying source file." },
  { id: "reframe", label: "Reframe", icon: "⌗", helper: "Set aspect, framing mode, subject position and zoom. Review the whole clip for faces cut off by the crop." },
  { id: "captions", label: "Captions", icon: "CC", helper: "Select a CC block to edit its words and timing. Set style, size and placement; review wording before publishing." },
  { id: "titles", label: "Graphics", icon: "T", helper: "Select a text or image layer to edit position, size, opacity and timing. The layer appears only during its timeline range." },
  { id: "motion", label: "Motion", icon: "◆", helper: "Edit a motion graphic’s text, timing and keyframes. Use the playhead to check its entrance, movement and exit." },
  { id: "composite", label: "Composite", icon: "◫", helper: "Choose the source and subject, then set masking, foreground or background treatment. Generate and review the processed layer before export." },
  { id: "flow", label: "Node Flow", icon: "☍", helper: "Wire AutoPromote signal nodes (Source, Transform, Masks, Null Rigs, Grade, Glow, Merge) and open Comp & Rig." },
  { id: "creatorfx", label: "Creator FX", icon: "✺", helper: "Tune the selected effect and its timing. Browser studies use simplified previews; inspect a rendered result before relying on the effect." },
  { id: "pacing", label: "Pacing", icon: "≋", helper: "Set speed and pacing on the selected range. Check the resulting duration and listen to speech after changing speed." },
  { id: "broll", label: "B-roll", icon: "▣", helper: "Add or select an insert. Set its source trim, layout, duration, end behavior and audio mix to control when it covers the main video." },
  { id: "finish", label: "Color", icon: "◐", helper: "Adjust color and finishing layers. Compare Before and After; check the rendered file for the final appearance." },
  { id: "sound", label: "Sound", icon: "♫", helper: "Select an audio clip to edit timing, level and fades. Adjust restoration and remix settings, then listen to the rendered mix." },
  { id: "export", label: "Export", icon: "⇧", helper: "Set resolution, frame rate, codec and quality. Render first, review the actual output, then download it. Studio exports have no AutoPromote watermark." },
];

const CREATIVE_INTENTS = [
  { id: "trim", label: "Remove boring parts", icon: "✂" },
  { id: "energy", label: "Increase energy", icon: "ϟ" },
  { id: "proof", label: "Show proof", icon: "▥" },
  { id: "loop", label: "Make it loop", icon: "↻" },
];

const SIGNATURE_CREATIVE_STYLES = [
  {
    id: "auto_story",
    label: "Auto Story",
    icon: "✦",
    helper: "Choreograph an opening break, movement build and transformed payoff automatically.",
  },
  {
    id: "motion_sculpture",
    label: "Motion Sculpture",
    icon: "≋",
    helper:
      "Sculpt detected human movement into coloured temporal silhouettes while the live subject stays sharp.",
    conceptImages: {
      clean: "/assets/motion-sculpture/motion-sculpture-clean-concept.png",
      bold: "/assets/motion-sculpture/motion-sculpture-bold-concept.png",
      unreal: "/assets/motion-sculpture/motion-sculpture-unreal-concept.png",
    },
  },
  {
    id: "beat_echo",
    label: "Beat Echo",
    icon: "◫",
    helper: "Turn real delayed frames into colour-sliced movement echoes on every beat.",
  },
  {
    id: "reality_break",
    label: "Reality Break",
    icon: "◇",
    helper:
      "Understand the isiZulu conversation, place evidence-backed visuals inside the real set, and preserve every face.",
  },
  {
    id: "tracked_reveal",
    label: "Tracked Reveal",
    icon: "◐",
    helper: "Let movement reveal the transformed colour world underneath.",
  },
];

const CREATIVE_INTENSITIES = [
  { id: "clean", label: "Clean" },
  { id: "bold", label: "Bold" },
  { id: "unreal", label: "Unreal" },
];

const CREATOR_CONTENT_PROFILES = [
  {
    id: "auto",
    label: "Auto",
    helper: "Let AutoPromote balance clarity, movement and payoff.",
    preset: "auto_story",
    intensity: "bold",
    transition: "soft_dip",
    pacing: "balanced",
    speed: 1,
  },
  {
    id: "talk_story",
    label: "Talk / Story",
    helper: "Natural jump cuts, clear speech and captions that do not fight the face.",
    preset: "tracked_reveal",
    intensity: "clean",
    transition: "clean_cut",
    pacing: "balanced",
    speed: 1.05,
  },
  {
    id: "beauty_fashion",
    label: "Beauty / Fashion",
    helper: "Polished reveals, colour transformation and softer joins.",
    preset: "tracked_reveal",
    intensity: "bold",
    transition: "soft_dip",
    pacing: "balanced",
    speed: 1,
  },
  {
    id: "fitness_dance",
    label: "Fitness / Dance",
    helper: "Movement echoes, beat-ready pacing and energetic joins.",
    preset: "beat_echo",
    intensity: "unreal",
    transition: "energy_flash",
    pacing: "energetic",
    speed: 1.15,
  },
  {
    id: "food_diy",
    label: "Food / DIY",
    helper: "Keep the useful steps, remove waiting, then reveal the result.",
    preset: "tracked_reveal",
    intensity: "bold",
    transition: "clean_cut",
    pacing: "energetic",
    speed: 1.1,
  },
  {
    id: "travel_lifestyle",
    label: "Travel / Lifestyle",
    helper: "Cinematic scene changes with room for locations to breathe.",
    preset: "reality_break",
    intensity: "bold",
    transition: "soft_dip",
    pacing: "balanced",
    speed: 1,
  },
  {
    id: "gaming_tech",
    label: "Gaming / Tech",
    helper: "Fast proof moments, screen energy and punchy transitions.",
    preset: "reality_break",
    intensity: "unreal",
    transition: "energy_flash",
    pacing: "energetic",
    speed: 1.15,
  },
];

const JOIN_TRANSITIONS = [
  { id: "auto", label: "Auto", helper: "Uses the creator mode recommendation." },
  { id: "clean_cut", label: "Clean Cut", helper: "Instant join with an audio-safe edge." },
  { id: "soft_dip", label: "Soft Dip", helper: "A short cinematic breath between moments." },
  {
    id: "energy_flash",
    label: "Energy Flash",
    helper: "A fast bright hit for action and reveals.",
  },
  { id: "cross_dissolve", label: "Dissolve", helper: "A smooth opacity blend between shots." },
  { id: "film_dip", label: "Film Dip", helper: "A soft filmic dip through black." },
  {
    id: "whip_pan",
    label: "Whip Pan",
    helper: "Directional motion blur carries the eye into the next shot.",
  },
  {
    id: "zoom_push",
    label: "Zoom Push",
    helper: "A fast push through the cut for energetic stories.",
  },
  { id: "blur_morph", label: "Blur Morph", helper: "Shape and blur bridge similar compositions." },
  {
    id: "luma_wipe",
    label: "Luma Wipe",
    helper: "The brightest parts of the next shot reveal first.",
  },
  { id: "light_leak", label: "Light Leak", helper: "Organic light washes over the edit point." },
  {
    id: "film_burn",
    label: "Film Burn",
    helper: "A textured analogue burn for stylized transitions.",
  },
  {
    id: "rgb_glitch",
    label: "RGB Glitch",
    helper: "A brief channel split for gaming and tech edits.",
  },
];

const buildSignatureCreativeEffects = ({ preset, intensity, duration }) => {
  const safeDuration = Math.max(0.1, Number(duration || 0));
  if (preset !== "auto_story") {
    return [
      {
        id: "signature-effect",
        preset,
        intensity,
        start_time: 0,
        end_time: safeDuration,
      },
    ];
  }

  const openingEnd = Math.min(safeDuration, Math.max(0.7, safeDuration * 0.22));
  const buildEnd = Math.min(safeDuration, Math.max(openingEnd + 0.1, safeDuration * 0.68));
  return [
    {
      id: "signature-opening",
      preset: "reality_break",
      intensity,
      start_time: 0,
      end_time: openingEnd,
    },
    {
      id: "signature-build",
      preset: "motion_sculpture",
      intensity,
      start_time: openingEnd,
      end_time: buildEnd,
    },
    {
      id: "signature-payoff",
      preset: "tracked_reveal",
      intensity,
      start_time: buildEnd,
      end_time: safeDuration,
    },
  ].filter(effect => effect.end_time - effect.start_time >= 0.05);
};

const PREVIEW_SPEED_OPTIONS = [0.5, 0.75, 1, 1.15, 1.25, 1.5, 2];
const DEFAULT_TRACK_STATES = {
  video: { visible: true, locked: false, muted: false, solo: false },
  adjustment: { visible: true, locked: false, muted: false, solo: false },
  graphics: { visible: true, locked: false, muted: false, solo: false },
  broll: { visible: true, locked: false, muted: false, solo: false },
  captions: { visible: true, locked: false, muted: false, solo: false },
  originalAudio: { visible: true, locked: false, muted: false, solo: false },
  voiceover: { visible: true, locked: false, muted: false, solo: false },
  music: { visible: true, locked: false, muted: false, solo: false },
  sfx: { visible: true, locked: false, muted: false, solo: false },
};
const STORY_BEAT_LABELS = ["Hook", "Problem", "Proof", "Payoff"];
const OVERLAY_PIP_SLOTS = [
  // Prefer the open mid-left editorial lane: top corners are commonly occupied
  // by faces in vertical podcast crops, while the lower lane is reserved for
  // captions and the moving brand mark.
  { x: 18, y: 56 },
  { x: 82, y: 30 },
  { x: 16, y: 84 },
  { x: 16, y: 28 },
  { x: 84, y: 28 },
  { x: 84, y: 84 },
  { x: 50, y: 50 },
];

const timedRangesOverlap = (startA, durationA, startB, durationB) => {
  const safeStartA = Number(startA || 0);
  const safeStartB = Number(startB || 0);
  const safeEndA = safeStartA + Math.max(0, Number(durationA || 0));
  const safeEndB = safeStartB + Math.max(0, Number(durationB || 0));
  return safeStartA < safeEndB && safeStartB < safeEndA;
};

const overlayRectsOverlap = (first, second, gutter = 4) => {
  const firstHalfWidth = Math.max(1, Number(first.width || 0)) / 2;
  const firstHalfHeight = Math.max(1, Number(first.height || 0)) / 2;
  const secondHalfWidth = Math.max(1, Number(second.width || 0)) / 2;
  const secondHalfHeight = Math.max(1, Number(second.height || 0)) / 2;
  return (
    Math.abs(Number(first.x || 0) - Number(second.x || 0)) <
      firstHalfWidth + secondHalfWidth + gutter &&
    Math.abs(Number(first.y || 0) - Number(second.y || 0)) <
      firstHalfHeight + secondHalfHeight + gutter
  );
};

const getCollisionSafePipPlacement = ({
  overlays,
  overlayId = null,
  startTime,
  duration,
  width,
  height,
}) => {
  const simultaneousPipOverlays = (overlays || []).filter(
    overlay =>
      String(overlay.id) !== String(overlayId) &&
      overlay.bRollMode === "pip" &&
      timedRangesOverlap(startTime, duration, overlay.startTime, overlay.duration)
  );
  const safeWidth = Math.max(10, Math.min(58, Number(width || 42)));
  const safeHeight = Math.max(10, Math.min(58, Number(height || 32)));
  const boundedSlot = slot => ({
    x: clampNumber(slot.x, safeWidth / 2, 100 - safeWidth / 2, 50),
    y: clampNumber(slot.y, safeHeight / 2, 100 - safeHeight / 2, 50),
    width: safeWidth,
    height: safeHeight,
  });

  return (
    OVERLAY_PIP_SLOTS.map(boundedSlot).find(candidate =>
      simultaneousPipOverlays.every(existing => !overlayRectsOverlap(candidate, existing))
    ) || boundedSlot(OVERLAY_PIP_SLOTS[simultaneousPipOverlays.length % OVERLAY_PIP_SLOTS.length])
  );
};

const ViralClipStudio = ({
  videoUrl,
  sourceStoragePath = null,
  sourceContentHash = null,
  sourceName,
  clips,
  images = [],
  onSave,
  onCancel,
  onStatusChange,
  renderStatus,
  renderedOutput,
  onEditInvalidated,
  onRenderedPreviewError,
  onProjectOpened,
  renderRecoveryPanel,
  renderRecoveryNeedsAttention = false,
  onDownloadRendered,
  onRestoreExport,
  onUseRendered,
  onOpenCameraAngles,
  importedCameraMaster,
  initialProjectFiles,
  initialSavedProject = null,
  isWorkflowPaused = false,
  currentMusic,
  onMusicChange,
  directorProposalRequest = null,
  directorSubjectVfxProposal = null,
  onDirectorReview,
}) => {
  const [orderedClips, setOrderedClips] = useState(clips || []);
  const [selectedClip, setSelectedClip] = useState((clips || [])[0]);
  const [overlays, setOverlays] = useState([]);
  const [activeOverlayId, setActiveOverlayId] = useState(null);
  const [videoTime, setVideoTime] = useState(0);
  const studioShortcutActionsRef = useRef({});
  const [programmeMediaAspectRatio, setProgrammeMediaAspectRatio] = useState(16 / 9);
  const [videoFit, setVideoFit] = useState("cover");
  const [safeFaceFraming, setSafeFaceFraming] = useState(false);
  const [faceAnchorPreset, setFaceAnchorPreset] = useState("center"); // center | face_top | face_mid

  // New AI Options for users
  const [autoCaptions, setAutoCaptions] = useState(() => {
    const firstClip = (clips || [])[0];
    if (firstClip?.autoCaptions !== undefined) return Boolean(firstClip.autoCaptions);
    return false;
  });
  const [captionStyle, setCaptionStyle] = useState("bold_pop");
  const [smartCrop, setSmartCrop] = useState(true);
  const [smartCropMode, setSmartCropMode] = useState("speaker_track");
  const [reframeAspect, setReframeAspect] = useState("9:16");
  const [speakerTrackZoom, setSpeakerTrackZoom] = useState(1);
  const [faceTrackingStatus, setFaceTrackingStatus] = useState("idle");
  const [faceTrackingMessage, setFaceTrackingMessage] = useState("");
  const faceTrackingGeneration = useRef(0);
  const [reframeKeyframes, setReframeKeyframes] = useState([]);
  const [speakerStackFraming, setSpeakerStackFraming] = useState(
    DEFAULT_SPEAKER_STACK_FRAMING
  );
  const [speakerStackSourceIds, setSpeakerStackSourceIds] = useState({
    top: null,
    bottom: null,
    third: null,
    fourth: null,
  });
  const [speakerStackOffsets, setSpeakerStackOffsets] = useState({
    top: 0,
    bottom: 0,
    third: 0,
    fourth: 0,
  });
  const [speakerStackSources, setSpeakerStackSources] = useState([]);
  const [speakerStackCameraCount, setSpeakerStackCameraCount] = useState(2);
  const [speakerStackLayout, setSpeakerStackLayout] = useState("stack_2");
  const [speakerStackSyncConfirmed, setSpeakerStackSyncConfirmed] = useState(false);
  const [speakerFocusCuts, setSpeakerFocusCuts] = useState([]);
  const [reframeModeCuts, setReframeModeCuts] = useState([]);
  const [activeReframeKeyframeId, setActiveReframeKeyframeId] = useState(null);
  const [enhanceQuality, setEnhanceQuality] = useState(false);
  const [silenceRemoval, setSilenceRemoval] = useState(false);
  const [silenceThreshold, setSilenceThreshold] = useState(-35);
  const [minSilenceDuration, setMinSilenceDuration] = useState(0.75);
  const [removeWatermark, setRemoveWatermark] = useState(false);
  const [watermarkMode, setWatermarkMode] = useState("adaptive");
  const [manualWatermarkRegions, setManualWatermarkRegions] = useState([]);
  const [activeWatermarkRegionId, setActiveWatermarkRegionId] = useState(null);
  const [watermarkCleanupPreview, setWatermarkCleanupPreview] = useState(null);
  const [isWatermarkCleanupPreviewLoading, setIsWatermarkCleanupPreviewLoading] = useState(false);
  const [watermarkCleanupPreviewError, setWatermarkCleanupPreviewError] = useState("");
  const [showWatermarkCleanupOnVideo, setShowWatermarkCleanupOnVideo] = useState(true);
  // Studio output is clean, including projects saved with legacy branding.
  const brandWatermark = false;
  const [mainFrame, setMainFrame] = useState(() => normalizeMainFrame({ enabled: false }));
  // Hooks are an independent edit. Selecting a Signature transformation must
  // never silently cover it with the default Blur Reveal treatment.
  const [addHook, setAddHook] = useState(false);
  const [hookText, setHookText] = useState(DEFAULT_HOOK_TEXT);
  const [hookTemplate, setHookTemplate] = useState("blur_reveal");
  const [hookIntroSeconds, setHookIntroSeconds] = useState(3);
  const [hookStartTime, setHookStartTime] = useState(0.8);
  const [hookEndTime, setHookEndTime] = useState(3.8);
  const [hookBlurBackground, setHookBlurBackground] = useState(true);
  const [hookDarkOverlay, setHookDarkOverlay] = useState(true);
  const [hookFreezeFrame, setHookFreezeFrame] = useState(false);
  const [hookZoomScale, setHookZoomScale] = useState(1.08);
  const [hookTextAnimation, setHookTextAnimation] = useState("slide-up");
  const [hookPreviewLoop, setHookPreviewLoop] = useState(false);
  const [hookSelectionMode, setHookSelectionMode] = useState(false);
  const [hookPickMode, setHookPickMode] = useState(false);
  const [hookFocusMode, setHookFocusMode] = useState(false);
  const [hookFocusPoint, setHookFocusPoint] = useState(DEFAULT_HOOK_FOCUS_POINT);
  const [hookAnalysisStatus, setHookAnalysisStatus] = useState("idle");
  const [hookAnalysisMessage, setHookAnalysisMessage] = useState("");
  const [hookSuggestedRange, setHookSuggestedRange] = useState(null);
  const [trimPreviewLoop, setTrimPreviewLoop] = useState(false);
  const [isPreviewPaused, setIsPreviewPaused] = useState(false);
  const [previewMuted, setPreviewMuted] = useState(false);
  const [previewVolume, setPreviewVolume] = useState(1);
  const [isPreviewFullscreen, setIsPreviewFullscreen] = useState(false);
  const [isAfterPreviewReady, setIsAfterPreviewReady] = useState(false);
  const [soloPreviewOverlayId, setSoloPreviewOverlayId] = useState(null);
  const [addMusic, setAddMusic] = useState(false);
  const [muteOriginalAudio, setMuteOriginalAudio] = useState(false);
  const [musicSelection, setMusicSelection] = useState(currentMusic || "upbeat_pop.mp3");
  const [musicSearchMode, setMusicSearchMode] = useState(() =>
    currentMusic ? !isPresetMusicSelection(currentMusic) : false
  );
  const [safeSearch, setSafeSearch] = useState(true);
  const [musicVolume, setMusicVolume] = useState(0.15);
  const [musicDucking, setMusicDucking] = useState(true);
  const [musicDuckingStrength, setMusicDuckingStrength] = useState(0.35);
  const [silencePreview, setSilencePreview] = useState(null);
  const [isRippleCuttingSilence, setIsRippleCuttingSilence] = useState(false);
  const [punchScale, setPunchScale] = useState(1.25);
  const [punchStyle, setPunchStyle] = useState("punch");
  const [punchMode, setPunchMode] = useState("cadence");
  const [punchDuration, setPunchDuration] = useState(2.5);
  const [musicPreviewUrl, setMusicPreviewUrl] = useState("");
  const [musicPreviewStatus, setMusicPreviewStatus] = useState("idle");
  const [musicPreviewStatusMessage, setMusicPreviewStatusMessage] = useState("");
  const [musicPreviewNeedsGesture, setMusicPreviewNeedsGesture] = useState(false);
  const [isBackgroundSoundPreviewing, setIsBackgroundSoundPreviewing] = useState(false);
  const [extractedAudio, setExtractedAudio] = useState(null);
  const [bRollCadence, setBRollCadence] = useState("balanced");
  const [bRollGenerationJobId, setBRollGenerationJobId] = useState("");
  const [autoBRollRequest, setAutoBRollRequest] = useState(null);
  const [captionBulkLanguage, setCaptionBulkLanguage] = useState("en");
  const [storyVisualSearchStatus, setStoryVisualSearchStatus] = useState("idle");
  const [storyVisualSearchMessage, setStoryVisualSearchMessage] = useState("");
  const [storyVisualCandidates, setStoryVisualCandidates] = useState({});
  const [studioInspectorTab, setStudioInspectorTab] = useState("hook");
  const [comparisonMode, setComparisonMode] = useState("split");
  const [activeCreativeTool, setActiveCreativeTool] = useState("moments");
  const [workspaceMode, setWorkspaceMode] = useState("creator");
  const [projectId, setProjectId] = useState(() => createSecureId("viral-project"));
  const [localDirectorProposal, setLocalDirectorProposal] = useState(null);
  const incomingDirectorProposalRequest = directorProposalRequest ||
    (localDirectorProposal?.projectId === projectId ? localDirectorProposal.request : null);
  const [projectName, setProjectName] = useState("Untitled viral edit");
  const [savedProjects, setSavedProjects] = useState([]);
  const [projectSaveState, setProjectSaveState] = useState("unsaved");
  const [backupIncludeVersions, setBackupIncludeVersions] = useState(false);
  const [projectSourcesRefreshing, setProjectSourcesRefreshing] = useState(false);
  const [sourceAccessGenerations, setSourceAccessGenerations] = useState({ projectId: null, byPath: {} });
  const projectSourceRefreshVersionRef = useRef(0);
  const [projectSavedAt, setProjectSavedAt] = useState(null);
  const [directorReview, setDirectorReview] = useState(null);
  const [directorReviewState, setDirectorReviewState] = useState("idle");
  const [directorReviewError, setDirectorReviewError] = useState("");
  const [timelineZoom, setTimelineZoom] = useState(1);
  const [timelineDockExpanded, setTimelineDockExpanded] = useState(true);
  const [timelineDockHeight, setTimelineDockHeight] = useState(280);
  const [studioViewportHeight, setStudioViewportHeight] = useState(() => window.innerHeight);
  // Preserve space for the programme monitor, but let a taller workspace show
  // all edited sound/layer lanes through the existing native resize control.
  const timelineHeightLimit = Math.max(148, studioViewportHeight - 350);
  const displayedTimelineHeight = Math.min(timelineDockHeight, timelineHeightLimit);
  useEffect(() => {
    const updateHeight = () => setStudioViewportHeight(window.innerHeight);
    window.addEventListener("resize", updateHeight);
    return () => window.removeEventListener("resize", updateHeight);
  }, []);
  const timelineResizeRef = useRef(null);
  const [previewDockSide, setPreviewDockSide] = useState("center");
  const [projectRailExpanded, setProjectRailExpanded] = useState(false);
  const [projectRailTab, setProjectRailTab] = useState("media");
  const [timelineEditTool, setTimelineEditTool] = useState("select");
  const [timelineSnapping, setTimelineSnapping] = useState(true);
  const [linkedSelection, setLinkedSelection] = useState(true);
  const [rippleMode, setRippleMode] = useState(false);
  const [precisionNudge, setPrecisionNudge] = useState(0.1);
  const [trackStates, setTrackStates] = useState(DEFAULT_TRACK_STATES);
  const [motionKeyframes, setMotionKeyframes] = useState([]);
  const [motionPathPreview, setMotionPathPreview] = useState(null);
  const previewMotionKeyframes = useMemo(() => !motionPathPreview ? motionKeyframes : motionPathPreview.keys
    ? [...motionKeyframes.filter(key => key.targetId !== motionPathPreview.targetId), ...motionPathPreview.keys]
    : editStudioPositionPose(motionKeyframes, motionPathPreview.targetId, motionPathPreview.time,
      motionPathPreview, () => 'transient-position'), [motionKeyframes, motionPathPreview]);
  const [motionScenes, setMotionScenes] = useState([]);
  const [compositionOrder, setCompositionOrder] = useState(null);
  const [selectedMotionId, setSelectedMotionId] = useState(null);
  const [threeDScenes, setThreeDScenes] = useState([]);
  const [subjectVfxScenes, setSubjectVfxScenes] = useState([]);
  const [selectedThreeDId, setSelectedThreeDId] = useState(null);
  const [threeDPreviewState, setThreeDPreviewState] = useState({ status: "idle" });
  const threeDScenesRef = useRef(threeDScenes);
  const threeDPreviewPollRef = useRef(null);
  threeDScenesRef.current = threeDScenes;
  useEffect(() => () => clearTimeout(threeDPreviewPollRef.current), []);
  const [motionWorkspace, setMotionWorkspace] = useState("design");
  const [motionTargetId, setMotionTargetId] = useState("main-video");
  const [focusedMotionKeyframeId, setFocusedMotionKeyframeId] = useState(null);
  const [showMotionPath, setShowMotionPath] = useState(true);
  const [compositeTargetId, setCompositeTargetId] = useState("main-video");
  const [mainTransform, setMainTransform] = useState({
    id: "main-video",
    name: "Main video",
    type: "video",
    x: 50,
    y: 50,
    scale: 1,
    rotation: 0,
    opacity: 1,
    cropX: 0,
    cropY: 0,
  });
  const [audioKeyframes, setAudioKeyframes] = useState({
    originalAudio: [],
    masterPodcast: [],
    voiceover: [],
    music: [],
    broll: [],
    sfx: [],
  });
  const [speedKeyframes, setSpeedKeyframes] = useState([]);
  const [voiceovers, setVoiceovers] = useState([]);
  const [adjustmentLayers, setAdjustmentLayers] = useState([]);
  const [nodeFlow, setNodeFlow] = useState({
    version: 1,
    viewerA: "MediaOut",
    viewerB: null,
    splitPosition: 50,
    positions: {},
    bypassed: {},
  });
  const [hideShyLayers, setHideShyLayers] = useState(false);
  const [bottomDockView, setBottomDockView] = useState("timeline");
  const [compoundClips, setCompoundClips] = useState([]);
  const [selectedGroupId, setSelectedGroupId] = useState(null);
  const [audioAutomationSource, setAudioAutomationSource] = useState("originalAudio");
  const [audioRestoration, setAudioRestoration] = useState({
    enabled: true,
    preset: "natural",
    voiceIsolation: false,
    denoise: 22,
    deEsser: 18,
    humFrequency: 50,
    compressor: 35,
    limiter: -1,
    loudness: -14,
    eq: { low: 0, mid: 0, high: 0 },
  });
  const [isVoiceoverRecording, setIsVoiceoverRecording] = useState(false);
  const [mediaBinSearch, setMediaBinSearch] = useState("");
  const [projectMedia, setProjectMedia] = useState(() => videoUrl ? [{
    id: `primary-${sourceStoragePath || "source"}`,
    name: sourceName || "Original source video",
    url: videoUrl,
    file: null,
    isLocal: false,
    isPrimary: true,
    storagePath: sourceStoragePath || "",
    sourceStoragePath: sourceStoragePath || "",
    ...(sourceContentHash ? {contentHash: sourceContentHash} : {}),
    duration: Math.max(0, Number(clips?.[0]?.sourceDuration || clips?.[0]?.end || clips?.[0]?.duration || 0)),
    status: sourceStoragePath ? "ready" : "existing",
    progress: 100,
  }] : []);
  const [mediaBinVisibleCount, setMediaBinVisibleCount] = useState(12);
  const [selectedProjectMediaId, setSelectedProjectMediaId] = useState(
    videoUrl ? `primary-${sourceStoragePath || "source"}` : null
  );
  const [selectedCameraMediaIds, setSelectedCameraMediaIds] = useState([]);
  const [cameraGroupStatus, setCameraGroupStatus] = useState("idle");
  const [angleGroups, setAngleGroups] = useState([]);
  const [activeAngleGroupId, setActiveAngleGroupId] = useState(null);
  const [angleGroupName, setAngleGroupName] = useState("");
  const [mediaImportMessage, setMediaImportMessage] = useState("");
  const [proxySettings, setProxySettings] = useState({
    enabled: true,
    resolution: "720p",
    codec: "h264",
    status: "source_ready",
  });
  const [stabilization, setStabilization] = useState({
    enabled: false,
    strength: 35,
    crop: "adaptive",
    rollingShutter: false,
  });
  const [advancedColor, setAdvancedColor] = useState({
    lutName: "",
    lutFile: null,
    lutIntensity: 100,
    curve: { shadows: 0, midtones: 0, highlights: 0 },
    hsl: { hue: 0, saturation: 0, luminance: 0 },
  });
  const [creatorPreview, setCreatorPreview] = useState(DEFAULT_CREATOR_PREVIEW);
  const [editTemplates, setEditTemplates] = useState([]);
  const [creativeTemplateInstance, setCreativeTemplateInstance] = useState(null);
  const [showSafeZones, setShowSafeZones] = useState(false);
  const [showCompositionGrid, setShowCompositionGrid] = useState(false);
  const [commandPaletteOpen, setCommandPaletteOpen] = useState(false);
  const [commandSearch, setCommandSearch] = useState("");
  const finishProgrammeClockRef = useRef(null);
  const {
    fx: finishFx,
    applyPreset: applyFinishPreset,
    updateFx: updateFinishFx,
    resetFx: resetFinishFx,
    replaceFx: replaceFinishFx,
    mediaStyle: finishMediaStyle,
    edgeBlurStyle: finishEdgeBlurStyle,
    vignetteStyle: finishVignetteStyle,
    overlayStyle: finishOverlayStyle,
    grainStyle: finishGrainStyle,
    letterboxStyle: finishLetterboxStyle,
    fadeStyle: finishFadeStyle,
    hasEffects: hasFinishEffects,
    attachVideo: attachFinishVideo,
  } = useCinematicEffects({ clockRef: finishProgrammeClockRef });
  const [podcastVisualizer, setPodcastVisualizer] = useState({
    enabled: false,
    mode: "wave",
    color: "#72f7ff",
    intensity: 0.75,
    position: "bottom",
  });
  const [finishKeyframes, setFinishKeyframes] = useState([]);
  const [scopeMode, setScopeMode] = useState("parade");
  const [musicBeatMarkers, setMusicBeatMarkers] = useState([]);
  const [beatSnapEnabled, setBeatSnapEnabled] = useState(true);
  const [creativeIntent, setCreativeIntent] = useState("energy");
  const [creativeEffectsEnabled, setCreativeEffectsEnabled] = useState(false);
  const [creativePreset, setCreativePreset] = useState("auto_story");
  const [creativeIntensity, setCreativeIntensity] = useState("bold");
  const [contentProfile, setContentProfile] = useState("auto");
  const [cutRangeStart, setCutRangeStart] = useState(null);
  const [cutRangeEnd, setCutRangeEnd] = useState(null);
  const [joinTransition, setJoinTransition] = useState("auto");
  const [joinTransitionDuration, setJoinTransitionDuration] = useState(0.18);
  const [pacingLevel, setPacingLevel] = useState("balanced");
  const [previewSpeed, setPreviewSpeed] = useState(1);
  const [captionPosition, setCaptionPosition] = useState("bottom_center");
  const [captionScale, setCaptionScale] = useState(1);
  const [captionTextOverride, setCaptionTextOverride] = useState("");
  const [captionCoverageMode, setCaptionCoverageMode] = useState("full");
  const [captionCoverageMessage, setCaptionCoverageMessage] = useState("");
  const [captionScriptDraft, setCaptionScriptDraft] = useState("");
  const [captionDraftLanguage, setCaptionDraftLanguage] = useState("und");
  const [captionDraftSpeaker, setCaptionDraftSpeaker] = useState("choir");
  const [captionSegments, setRawCaptionSegments] = useState(() =>
    ensureUniqueCaptionIds(resolveInitialCaptionSegments((clips || [])[0], (clips || [])[0]?.duration))
  );
  const setCaptionSegments = useCallback(update => {
    setRawCaptionSegments(previous => ensureUniqueCaptionIds(
      typeof update === "function" ? update(previous) : update
    ));
  }, []);
  const [translateCaptionsToEnglish, setTranslateCaptionsToEnglish] = useState(false);
  const [captionSourceLanguage, setCaptionSourceLanguage] = useState("auto");
  const [captionGenerationStatus, setCaptionGenerationStatus] = useState("idle");
  const [captionGenerationMessage, setCaptionGenerationMessage] = useState("");
  const captionGenerationInFlightRef = useRef(false);
  const captionSegmentsRef = useRef(captionSegments);
  captionSegmentsRef.current = captionSegments;
  const captionOverrideRef = useRef(captionTextOverride);
  captionOverrideRef.current = captionTextOverride;
  const [studioActionMessage, setStudioActionMessage] = useState(
    "Split preview is live. Edit on the right and compare the untouched source beside it."
  );
  const [exportErrorMessage, setExportErrorMessage] = useState("");
  const [motionKeyframeEditError, setMotionKeyframeEditError] = useState("");

  const [musicTrack, setMusicTrack] = useState(null);
  const [soundEffects, setSoundEffects] = useState([]);
  const [audioRemix, setAudioRemix] = useState(() => normalizeAudioRemix(DEFAULT_AUDIO_REMIX));
  const [audioRemixBypass, setAudioRemixBypass] = useState(false);
  const [audioRemixPlaybackRequest, setAudioRemixPlaybackRequest] = useState(0);
  const [audioRemixLiveError, setAudioRemixLiveError] = useState("");
  const remixPreviewMediaRef = useRef(new Set());
  const [audioRemixLoop, setAudioRemixLoop] = useState(null);
  const [exactAudioRemixPreview, setExactAudioRemixPreview] = useState({
    status: "idle",
    url: "",
    error: "",
    receipt: null,
  });
  const exactAudioRemixPreviewRef = useRef(null);

  const linkedMotionCues = React.useMemo(() => clipStudioGroupMotionCues(motionCues(motionScenes), compoundClips), [motionScenes, compoundClips]);
  const allSoundEffects = React.useMemo(
    () => [...soundEffects, ...linkedMotionCues],
    [soundEffects, linkedMotionCues]
  );

  const [activeSoundEffectId, setActiveSoundEffectId] = useState(null);
  const [previewingSoundEffectId, setPreviewingSoundEffectId] = useState(null);
  const [musicLibraryOpen, setMusicLibraryOpen] = useState(false);
  const musicFileInputRef = useRef(null);
  const soundEffectFileInputRef = useRef(null);
  const speechAnalyserRef = useRef(null);
  const speechAnalysisContextsRef = useRef(new Set());
  const speechDetectionRafRef = useRef(null);
  const voiceoverSpeechAnalyserRef = useRef(null);
  const failedSpeechSourcesRef = useRef(new WeakSet());

  const MUSIC_PRESETS = [
    { name: "Upbeat Pop", file: "upbeat_pop.mp3", category: "pop", emoji: "🎉" },
    { name: "Cinematic Epic", file: "cinematic_epic.mp3", category: "cinematic", emoji: "🎬" },
    { name: "Lo-Fi Chill", file: "lofi_chill.mp3", category: "lofi", emoji: "☕" },
    { name: "Hip Hop Beat", file: "hiphop_beat.mp3", category: "hiphop", emoji: "🎤" },
    { name: "Emotional Piano", file: "emotional_piano.mp3", category: "emotional", emoji: "🎹" },
    {
      name: "Motivational Anthem",
      file: "motivational_anthem.mp3",
      category: "motivational",
      emoji: "🔥",
    },
    { name: "Corporate Clean", file: "corporate_clean.mp3", category: "corporate", emoji: "💼" },
    { name: "Trap Banger", file: "trap_banger.mp3", category: "trap", emoji: "💎" },
    { name: "Acoustic Warm", file: "acoustic_warm.mp3", category: "acoustic", emoji: "🎸" },
    { name: "Retro Synthwave", file: "retro_synthwave.mp3", category: "retro", emoji: "🕹️" },
    { name: "Orchestral Rise", file: "orchestral_rise.mp3", category: "orchestral", emoji: "🎻" },
    { name: "Ambient Drone", file: "ambient_drone.mp3", category: "ambient", emoji: "🌌" },
  ];

  const { capabilities, credits, editing, canUseFeature, requiresUpgrade } = useSubscription();
  const canUseWatermarkRemoval = canUseFeature("watermarkRemoval");
  const canUseAudioExtract = canUseFeature("audioExtract");
  const canUseMulticam = canUseFeature("multicam");
  const clipFinderCost = editing?.features?.findViralClips?.creditCost || 8;
  const clipRenderCost = editing?.features?.clipRender?.creditCost || 5;
  const transcribeCost = editing?.features?.audioExtract?.creditCost || 3;
  const showCreditWarning = (credits?.monthlyRemaining || 0) < 20;
  const upgradeMessage = "Upgrade your subscription to unlock this feature.";

  // Collapsible section state — start with AI Enhancements collapsed to reduce overwhelm
  const [collapsedSections, setCollapsedSections] = useState({
    aiEnhancements: true,
    hookSettings: true,
    musicAudio: true,
  });
  const toggleSection = key => setCollapsedSections(prev => ({ ...prev, [key]: !prev[key] }));
  const [audioExtractionStatus, setAudioExtractionStatus] = useState("");
  const [isExtractingAudio, setIsExtractingAudio] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [exportStatusLabel, setExportStatusLabel] = useState("Render Final Clip");
  const [selectedExportDestination, setSelectedExportDestination] = useState("general");
  const [splitExportEnabled, setSplitExportEnabled] = useState(false);
  const [splitExportHooks, setSplitExportHooks] = useState([]);
  const [exportSettings, setExportSettings] = useState({
    resolution: "1080p",
    fps: "30",
    codec: "h264",
    quality: "high",
    videoBitrate: "auto",
    targetFileSizeMb: "",
    audioCodec: "aac",
    audioBitrate: "192",
    colorSpace: "rec709",
  });
  const loggedScannerEntryRef = useRef(new Set());
  const [previewFinishedFile, setPreviewFinishedFile] = useState(true);
  const completedOutputUrl = getSafeMediaSource(renderedOutput?.previewUrl || renderedOutput?.url);
  // Both monitor modes have the same identity when no completed movie exists.
  // The first edit must not turn undefined into a different source window and
  // rewind a paused authoring frame to the clip start.
  const renderedOutputUrl = previewFinishedFile ? completedOutputUrl || "" : "";
  const hasCompletedOutput = Boolean(completedOutputUrl);
  // Access recovery can clear and restore a completed URL. Preserve the
  // editor's explicit monitor choice through that transport transition.
  // A different project starts in its finished view; a successful explicit
  // export below also selects its newly rendered result.
  useEffect(() => { setPreviewFinishedFile(true); }, [projectId]);

  useEffect(() => {
    if (!isExporting || !renderStatus) return;
    setExportStatusLabel(renderStatus);
  }, [isExporting, renderStatus]);

  useEffect(() => {
    if (!renderedOutputUrl) return;
    setComparisonMode("after");
    // A completed-file recovery can finish after the editor chooses a tool.
    // Keep that tool open; the finished result is already visible in After.
    setExportStatusLabel("Render Again");
    setStudioActionMessage(
      "Render complete. After is now playing the finished video, not the live edit simulation."
    );
  }, [renderedOutputUrl]);

  const [timeline, setTimeline] = useState(() => {
    // Initial timeline is just the main video URL, effectively one clip
    return [{
      id: "main",
      name: sourceName || "Original source video",
      url: videoUrl,
      storagePath: sourceStoragePath || "",
      sourceStoragePath: sourceStoragePath || "",
      ...(sourceContentHash ? {contentHash: sourceContentHash} : {}),
      duration: 0,
      startRequest: null,
      endRequest: null,
    }];
  });
  const [activeTimelineIndex, setActiveTimelineIndex] = useState(0);
  const [draggedOverlayId, setDraggedOverlayId] = useState(null);
  const [draggedTimelineClipId, setDraggedTimelineClipId] = useState(null);
  const [draggedDetectedClipId, setDraggedDetectedClipId] = useState(null);
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);
  const [directorModeActive, setDirectorModeActive] = useState(false);
  const [activeDirectorAngle, setActiveDirectorAngle] = useState(0);
  const [directorSessionCuts, setDirectorSessionCuts] = useState([]);
  const [directorAngles, setDirectorAngles] = useState([
    { id: "cam1", label: "Cam 1 (Master)", type: "master", src: null, zoom: 1 },
    { id: "cam2", label: "Cam 2 (Tight Punch 1.35x)", type: "punch", src: null, zoom: 1.35 },
    { id: "cam3", label: "Cam 3 (Reaction 1.22x)", type: "punch", src: null, zoom: 1.22 },
    { id: "cam4", label: "Cam 4 (Wide 1.1x)", type: "punch", src: null, zoom: 1.1 },
  ]);

  const videoRef = useRef(null);
  const programmeVideoSlotsRef = useRef([null, null, null]);
  const afterCanvasRef = useRef(null);
  const [pausedSourceFrame, setPausedSourceFrame] = useState(null);
  const adjustmentScratchRef = useRef(null);
  const beforeVideoRef = useRef(null);
  const beforeCanvasRef = useRef(null);
  const speakerStackTopVideoRef = useRef(null);
  const speakerStackBottomVideoRef = useRef(null);
  const speakerStackThirdVideoRef = useRef(null);
  const speakerStackFourthVideoRef = useRef(null);
  const sourceSplitAlternateVideoRef = useRef(null);
  const subjectProgrammeVideoRef = useRef(null);
  const attemptedSubjectProgrammeRefreshRef = useRef(new Set());
  const sourceSplitAlternateFrameRef = useRef(null);
  const speakerStackSourceInputRef = useRef(null);
  const audioRef = useRef(null);
  const smartCropForegroundVideoRef = useRef(null);
  const hookBackdropVideoRef = useRef(null);
  const hookFreezeVideoRef = useRef(null);
  const beatEchoVideoRefsRef = useRef([]);
  const musicPreviewRef = useRef(null);
  const timelineAudioPoolRef = useRef(new Map());
  const soundEffectAudioRefsRef = useRef(new Map());
  const soundEffectAudioContextRef = useRef(null);
  const soundEffectNodesRef = useRef(new Set());
  const soundEffectTimelineGainsRef = useRef(new Map());
  const soundEffectGainTimeMapRef = useRef(null);
  const soundEffectDecodedSourcesRef = useRef(new Map());
  const soundEffectDecodePromisesRef = useRef(new Map());
  const soundEffectBuiltInBuffersRef = useRef(new Map());
  const programmeSoundEffectRoutesRef = useRef(new Map());
  const soundEffectStartVersionRef = useRef(0);
  const triggeredSoundEffectsRef = useRef(new Set());
  const soundEffectPreviewTimeoutRef = useRef(null);
  const soundEffectObjectUrlsRef = useRef(new Set());
  const watermarkCleanupPreviewImageRef = useRef(null);
  const musicPreviewObjectUrlRef = useRef(null);
  const musicPreviewAudioContextRef = useRef(null);
  const musicPreviewGainNodeRef = useRef(null);
  const musicPreviewDuckGainNodeRef = useRef(null);
  const preparedVoiceBusRef = useRef(null);
  const preparedSpeechDuckRef = useRef(null);
  const preparedSpeechDuckClockRef = useRef('');
  const preparedSpeechDuckSourceRef = useRef(null);
  const musicPreviewBufferRef = useRef(null);
  const musicPreviewBufferUrlRef = useRef("");
  const musicPreviewBufferIdentityRef = useRef("");
  const [decodedMusicPreviewUrl, setDecodedMusicPreviewUrl] = useState("");
  const musicPreviewStartVersionRef = useRef(0);
  const musicPreviewDecodePromiseRef = useRef(null);
  const programmeMusicRouteRef = useRef({ sourceUrl: "", decoded: false });
  const isolatedMusicPreviewIntentRef = useRef(false);
  const musicPreviewSourceRef = useRef(null);
  const musicPreviewSourceStateRef = useRef({ offset: 0, playbackRate: 1 });
  const backgroundSoundPreviewSuppressedRef = useRef(false);
  const overlayMediaRefsRef = useRef(new Map());
  const imageInputRef = useRef(null);
  const logoMotionInputRef = useRef(null);
  const brollVideoInputRef = useRef(null);
  const projectMediaInputRef = useRef(null);
  const projectMediaObjectUrlsRef = useRef(new Set());
  const filmAnglePreviewRefsRef = useRef(new Map());
  const consumedCameraMasterIdsRef = useRef(new Set());
  const consumedInitialFilesRef = useRef(new WeakSet());
  const activeProjectIdRef = useRef(projectId);
  const directorProposalRequestRef = useRef(incomingDirectorProposalRequest);
  const attemptedSourceRefreshRef = useRef(new Set());
  activeProjectIdRef.current = projectId;
  directorProposalRequestRef.current = incomingDirectorProposalRequest;
  const quickMusicFileInputRef = useRef(null);
  const audioSourceInputRef = useRef(null);
  const previewSourceCacheRef = useRef(new Map());
  const undoStackRef = useRef([]);
  const studioDocumentRef = useRef(null);
  const latestEditorSnapshotRef = useRef(null);
  const redoStackRef = useRef([]);
  const lastSnapshotRef = useRef(null);
  const lastSnapshotSignatureRef = useRef(null);
  const isRestoringHistoryRef = useRef(false);
  const pendingHistoryBaselineRef = useRef(null);
  const cutHistoryTransactionRef = useRef(null);
  const previewPlaybackIntentRef = useRef(true);
  const previewPlaybackStartRef = useRef(0);
  const previewPlaybackPreparingRef = useRef(null);
  const preparedCutHandoffRef = useRef(null);
  const retiringMusicSourcesRef = useRef(new Set());
  const previewWindowRef = useRef(null);
  const programmeSeekIntentRef = useRef({ version: 0 });
  const phoneFrameRef = useRef(null);
  const studioSidebarRef = useRef(null);
  const watermarkDragRef = useRef(null);
  const hookSegmentTrackRef = useRef(null);
  const hookSelectionDragRef = useRef(null);
  const hookPlayheadDragRef = useRef(null);
  const hookPreviewSequenceRef = useRef({ active: false });
  const hookAnalysisRequestRef = useRef(0);
  const hookSuggestionCycleRef = useRef(0);
  const pendingClipActionRef = useRef(null);
  const pendingTimelineSeekRef = useRef(null);
  const projectAutosaveTimerRef = useRef(null);
  const projectCreatedAtRef = useRef(Date.now());
  const voiceoverRecorderRef = useRef(null);
  const voiceoverStreamRef = useRef(null);
  const voiceoverRequestPendingRef = useRef(false);
  const voiceoverMountedRef = useRef(true);
  useEffect(() => {
    voiceoverMountedRef.current = true;
    return () => { voiceoverMountedRef.current = false; };
  }, []);
  const voiceoverChunksRef = useRef([]);
  const voiceoverStartTimeRef = useRef(0);
  const voiceoverStartedAtRef = useRef(0);

  useEffect(() => {
    attachFinishVideo(videoRef.current);
  }, [attachFinishVideo, selectedClip?.id, renderedOutputUrl]);

  const normalizeAssetUrl = asset => {
    if (!asset) return "";
    if (typeof asset === "string") return asset;
    return asset.url || asset.src || asset.downloadURL || asset.mediaUrl || asset.thumbnail || "";
  };

  const cloneSnapshot = value => {
    if (value === null || typeof value !== "object") return value;
    if (typeof Blob !== "undefined" && value instanceof Blob) return value;
    if (Array.isArray(value)) return value.map(cloneSnapshot);

    return Object.fromEntries(
      Object.entries(value).map(([key, nestedValue]) => [key, cloneSnapshot(nestedValue)])
    );
  };

  const serializeSnapshot = value =>
    JSON.stringify(value, (_key, nestedValue) => {
      if (typeof Blob !== "undefined" && nestedValue instanceof Blob) {
        return {
          __mediaType: typeof File !== "undefined" && nestedValue instanceof File ? "File" : "Blob",
          name: nestedValue.name || "",
          size: nestedValue.size,
          type: nestedValue.type,
          lastModified: nestedValue.lastModified || 0,
        };
      }
      return nestedValue;
    });

  const releaseMusicPreviewObjectUrl = () => {
    if (musicPreviewObjectUrlRef.current) {
      URL.revokeObjectURL(musicPreviewObjectUrlRef.current);
      musicPreviewObjectUrlRef.current = null;
    }
  };

  const materializeMusicPreviewUrl = async sourceUrl => {
    const normalizedUrl = typeof sourceUrl === "string" ? sourceUrl.trim() : "";
    if (!normalizedUrl) return "";

    releaseMusicPreviewObjectUrl();

    if (!normalizedUrl.startsWith("data:")) {
      return normalizedUrl;
    }

    const response = await fetch(normalizedUrl);
    const blob = await response.blob();
    const objectUrl = URL.createObjectURL(blob);
    musicPreviewObjectUrlRef.current = objectUrl;
    return objectUrl;
  };

  const stopMusicPreviewBufferPlayback = stopAt => {
    musicPreviewStartVersionRef.current++;
    const deferred = Number.isFinite(stopAt) && stopAt > musicPreviewAudioContextRef.current?.currentTime;
    if (!deferred) {
      retiringMusicSourcesRef.current.forEach(source => { try { source.stop(); source.disconnect(); } catch (_) {} });
      retiringMusicSourcesRef.current.clear();
    }
    if (!musicPreviewSourceRef.current) return;

    try {
      musicPreviewSourceRef.current.stop(deferred ? stopAt : undefined);
    } catch (error) {
      console.log("Music preview buffer stop skipped", error);
    }

    try {
      if (deferred) retiringMusicSourcesRef.current.add(musicPreviewSourceRef.current);
      else musicPreviewSourceRef.current.disconnect();
    } catch (error) {
      console.log("Music preview buffer disconnect skipped", error);
    }

    musicPreviewSourceRef.current = null;
    setIsBackgroundSoundPreviewing(false);
  };

  useEffect(() => {
    if (!isWorkflowPaused) return;
    previewPlaybackIntentRef.current = false;
    [videoRef.current, beforeVideoRef.current, musicPreviewRef.current].forEach(media => {
      if (media && typeof media.pause === "function") media.pause();
    });
    timelineAudioPoolRef.current.forEach(media => media?.pause?.());
    soundEffectAudioRefsRef.current.forEach(media => media?.pause?.());
    stopMusicPreviewBufferPlayback();
    setIsPreviewPaused(true);
  }, [isWorkflowPaused]);

  const ensureMusicPreviewAudioContext = () => {
    const AudioContextCtor = window.AudioContext || window.webkitAudioContext;
    if (!AudioContextCtor) {
      throw new Error("This browser does not support Web Audio preview");
    }

    if (!musicPreviewAudioContextRef.current) {
      const audioContext = soundEffectAudioContextRef.current || new AudioContextCtor();
      const gainNode = audioContext.createGain();
      const duckGainNode = audioContext.createGain();
      gainNode.connect(duckGainNode);
      duckGainNode.connect(audioContext.destination);
      musicPreviewAudioContextRef.current = audioContext;
      musicPreviewGainNodeRef.current = gainNode;
      musicPreviewDuckGainNodeRef.current = duckGainNode;
    }

    return musicPreviewAudioContextRef.current;
  };

  const syncMusicPreviewGain = (programmeTime = getPreviewTimelineTime(videoRef.current?.currentTime || 0)) => {
    const gainNode = musicPreviewGainNodeRef.current;
    if (!gainNode) return;
    if (!(musicTrack?.ducking && musicTrack.duckingMode === "speech") && musicPreviewDuckGainNodeRef.current)
      musicPreviewDuckGainNodeRef.current.gain.value = 1;
    const preparedDuck = preparedSpeechDuckRef.current;
    const musicClock = musicPreviewSourceRef.current && musicPreviewSourceStateRef.current;
    if (preparedDuck && musicClock && musicTrack?.ducking && musicTrack.duckingMode === 'speech') {
      const identity = JSON.stringify([preparedDuck.key, musicClock.startedAt, musicClock.offset]);
      if (preparedSpeechDuckClockRef.current !== identity) {
        const contextTime = musicPreviewAudioContextRef.current.currentTime;
        const when = Math.max(contextTime, musicClock.startedAt);
        const old = preparedSpeechDuckSourceRef.current;
        if (old) { try { old.stop(); } catch (_) {} old.disconnect(); }
        preparedSpeechDuckSourceRef.current = scheduleStudioPreparedDuck(musicPreviewAudioContextRef.current,
          musicPreviewDuckGainNodeRef.current.gain, preparedDuck, { when, offset: musicBufferPosition(musicClock, when) });
        if (preparedSpeechDuckSourceRef.current) preparedSpeechDuckClockRef.current = identity;
      }
    }
    const previewGain = previewMuted || !isStudioAudioTrackAudible(trackStates, "music")
      ? 0 : clampAudioControl(previewVolume, 0, 1, 1);
    const effectiveVol = musicTrack?.volume ?? musicVolume;
    // The fade applies to the samples actually leaving the music decoder.
    // Picture-frame callbacks can trail its audio clock by tens of milliseconds;
    // using that lagging position makes a one-second tail audibly too quiet/loud.
    const musicTime = musicPreviewSourceRef.current && musicPreviewSourceStateRef.current
      ? musicBufferPosition(musicPreviewSourceStateRef.current, musicPreviewAudioContextRef.current.currentTime)
      : getPreviewMusicTime(programmeTime);
    const fadeGain = studioMusicFadeGain(musicTime, outputPlaybackDuration,
      musicTrack?.fadeIn, musicTrack?.fadeOut);
    const automationGain = interpolateAutomationValue({ keyframes: audioKeyframes.music,
      property: "volume", time: programmeTime, fallback: 100 }) / 100;
    const baseGain = clampAudioControl(effectiveVol, 0, 0.6, 0.15) * previewGain * automationGain * constantMusicDuckGain(musicTrack);
    const parameter = gainNode.gain;
    const contextTime = musicPreviewAudioContextRef.current.currentTime;
    const fadeIn = Math.max(0, Number(musicTrack?.fadeIn || 0));
    const fadeOut = Math.max(0, Number(musicTrack?.fadeOut || 0));
    // Schedule ordinary non-overlapping fades on the audio thread. Holding a
    // gain until the next picture frame leaves a measurable staircase tail.
    // Edited bus curves/overlapping fades retain their existing frame consumer.
    if (!(audioKeyframes.music || []).length && fadeIn + fadeOut <= outputPlaybackDuration &&
        parameter.cancelScheduledValues && parameter.setValueAtTime && parameter.linearRampToValueAtTime) {
      parameter.cancelScheduledValues(contextTime);
      parameter.setValueAtTime(baseGain * fadeGain, contextTime);
      if (musicTime < fadeIn) parameter.linearRampToValueAtTime(baseGain, contextTime + fadeIn - musicTime);
      const fadeStart = outputPlaybackDuration - fadeOut;
      if (fadeOut > 0 && musicTime < outputPlaybackDuration) {
        if (musicTime < fadeStart) parameter.setValueAtTime(baseGain, contextTime + fadeStart - musicTime);
        parameter.linearRampToValueAtTime(0, contextTime + outputPlaybackDuration - musicTime);
      }
    } else {
      parameter.cancelScheduledValues?.(contextTime);
      parameter.value = baseGain * fadeGain;
    }
  };

  const startMusicPreviewBufferPlayback = async (targetOffset, playbackRate = 1) => {
    const audioBuffer = musicPreviewBufferRef.current;
    if (!audioBuffer) return;
    musicPreviewStartVersionRef.current++;
    const startVersion = musicPreviewStartVersionRef.current;
    const sourceKey = musicPreviewBufferUrlRef.current;
    const video = videoRef.current;
    const projectId = activeProjectIdRef.current;
    const audioContext = ensureMusicPreviewAudioContext();
    if (audioContext.state === "suspended") {
      await audioContext.resume();
    }
    // An asynchronous browser resume must not start another project's song or
    // resurrect audio after Pause, a seek, source replacement or unmount.
    if (startVersion !== musicPreviewStartVersionRef.current ||
        sourceKey !== musicPreviewBufferUrlRef.current || audioBuffer !== musicPreviewBufferRef.current ||
        projectId !== activeProjectIdRef.current || video !== videoRef.current || video?.paused || video?.seeking) return;
    const clip = timeline[activeTimelineIndex];
    const window = getTimelineClipWindow(clip);
    if (!isStudioSourceClockCurrent(video, { activeVideo: videoRef.current, url: clip?.url,
      start: window.start, end: window.end, hasNext: activeTimelineIndex < timeline.length - 1 })) return;
    const programmeTime = getPreviewTimelineTime(video.currentTime || 0);
    // Schedule beyond the current audio quantum. A timestamp that has already
    // passed starts immediately but leaves a false song/cue phase anchor.
    const startedAt = audioContext.currentTime + .01;
    targetOffset = getPreviewMusicTime(programmeTime) + .01;
    // BufferSource rounds offsets to a decoded sample. Keep the shared music/
    // cue clock anchored to that actual sample, especially for noisy whooshes.
    const decodedSampleRate = Number(audioBuffer.sampleRate || audioContext.sampleRate);
    if (Number.isFinite(decodedSampleRate) && decodedSampleRate > 0)
      targetOffset = Math.round(targetOffset * decodedSampleRate) / decodedSampleRate;
    const loop = musicTrack?.loop !== false;
    if (!loop && targetOffset >= audioBuffer.duration) return;
    syncMusicPreviewGain(programmeTime);

    const source = audioContext.createBufferSource();
    source.buffer = audioBuffer;
    source.loop = loop;
    source.playbackRate.value = Number.isFinite(Number(playbackRate)) ? Number(playbackRate) : 1;
    source.connect(musicPreviewGainNodeRef.current);
    stopMusicPreviewBufferPlayback(startedAt);
    musicPreviewSourceRef.current = source;
    musicPreviewSourceStateRef.current = {
      offset: targetOffset,
      playbackRate: source.playbackRate.value,
      startedAt,
      sourceKey,
    };
    source.start(startedAt, loop ? targetOffset % Math.max(audioBuffer.duration, 0.001) : targetOffset);
    syncMusicPreviewGain(programmeTime);
    // A picture stall/cut can require a new song anchor. Overlapping cues must
    // move to that same clock, rather than retaining an earlier phase while
    // only the music jumps. This is a transport event, not a project edit.
    video.dispatchEvent(new Event("studio-audio-clock-resync"));
    source.onended = () => {
      if (musicPreviewSourceRef.current === source) {
        musicPreviewSourceRef.current = null;
        setIsBackgroundSoundPreviewing(false);
      }
      source.disconnect();
      retiringMusicSourcesRef.current.delete(source);
    };
    setIsBackgroundSoundPreviewing(true);
    setMusicPreviewNeedsGesture(false);
    setMusicPreviewStatus("ready");
    setMusicPreviewStatusMessage(`Preview audio is playing for ${currentMusicLabel}.`);
  };

  const ensurePreviewableClipUrl = async clip => {
    if (!clip) return "";

    const cacheKey = clip.id || clip.url || "main";
    if (previewSourceCacheRef.current.has(cacheKey)) {
      return previewSourceCacheRef.current.get(cacheKey);
    }

    if (typeof clip.url === "string" && /^https?:/i.test(clip.url)) {
      previewSourceCacheRef.current.set(cacheKey, clip.url);
      return clip.url;
    }

    const token = await getMediaAuthToken();
    if (!token) throw new Error("Please log in.");

    let sourceBlob = null;
    let fileName = `${cacheKey}.mp4`;

    if (clip.file instanceof Blob) {
      sourceBlob = clip.file;
      fileName = clip.file.name || fileName;
    } else if (typeof clip.url === "string" && clip.url.startsWith("blob:")) {
      const response = await fetch(clip.url);
      sourceBlob = await response.blob();
    }

    if (!(sourceBlob instanceof Blob)) {
      return typeof clip.url === "string" ? clip.url : "";
    }

    const sourceProjectId = activeProjectIdRef.current;
    const uploadResult = await uploadSourceFileViaBackend({
      file: sourceBlob,
      token,
      mediaType: "video",
      fileName,
    });

    if (!uploadResult.storagePath) throw new Error("Camera upload returned no owned media reference. Retry the upload.");
    if (activeProjectIdRef.current === sourceProjectId) {
      const binding = { url: uploadResult.url, storagePath: uploadResult.storagePath,
        sourceStoragePath: uploadResult.storagePath, file: null, isLocal: false,
        status: "ready", error: "",
        ...(uploadResult.contentHash || uploadResult.sha256 ? { contentHash: uploadResult.contentHash || uploadResult.sha256 } : {}) };
      const bind = current => current.map(item => item.id === clip.id ? { ...item, ...binding } : item);
      setSpeakerStackSources(bind);
      setTimeline(bind);
    }
    previewSourceCacheRef.current.set(cacheKey, uploadResult.url);
    return uploadResult.url;
  };

  const getEditorSnapshot = () => {
    const snapshot = {
    orderedClips,
    projectMedia,
    creativeTemplateInstance,
    angleGroups,
    activeAngleGroupId,
    selectedClipId: selectedClip?.id || null,
    overlays,
    activeOverlayId,
    videoFit,
    safeFaceFraming,
    faceAnchorPreset,
    autoCaptions,
    captionStyle,
    captionPosition,
    captionScale,
    captionTextOverride,
    captionCoverageMode,
    captionSegments,
    translateCaptionsToEnglish,
    captionSourceLanguage,
    previewSpeed,
    pacingLevel,
    creativeIntent,
    creativeEffectsEnabled,
    creativePreset,
    creativeIntensity,
    finishFx,
    podcastVisualizer,
    finishKeyframes,
    beatSnapEnabled,
    contentProfile,
    cutRangeStart,
    cutRangeEnd,
    joinTransition,
    joinTransitionDuration,
    smartCrop,
    smartCropMode,
    reframeAspect,
    speakerTrackZoom,
    reframeKeyframes,
    speakerStackFraming,
    speakerStackSourceIds,
    speakerStackOffsets,
    speakerStackSources,
    speakerStackCameraCount,
    speakerStackLayout,
    speakerStackSyncConfirmed,
    speakerFocusCuts,
    reframeModeCuts,
    activeReframeKeyframeId,
    enhanceQuality,
    silenceRemoval,
    silencePreview,
    silenceThreshold,
    minSilenceDuration,
    removeWatermark,
    watermarkMode,
    brandWatermark,
    mainFrame,
    exportSettings,
    manualWatermarkRegions,
    activeWatermarkRegionId,
    addHook,
    hookText,
    hookTemplate,
    hookIntroSeconds,
    hookStartTime,
    hookEndTime,
    hookBlurBackground,
    hookDarkOverlay,
    hookFreezeFrame,
    hookZoomScale,
    hookTextAnimation,
    hookPreviewLoop,
    hookFocusPoint,
    addMusic,
    muteOriginalAudio,
    musicSelection,
    musicSearchMode,
    safeSearch,
    musicVolume,
    musicDucking,
    musicDuckingStrength,
    musicTrack,
    soundEffects,
    audioRemix,
    activeSoundEffectId,
    extractedAudio,
    bRollCadence,
    bRollGenerationJobId,
    timeline,
    activeTimelineIndex,
    timelineZoom,
    timelineEditTool,
    timelineSnapping,
    linkedSelection,
    rippleMode,
    trackStates,
    motionKeyframes: pruneRemovedStudioGroupKeys(motionKeyframes, compoundClips,
      removeStudioGroupMembers(compoundClips, (compositionOrder || []).filter(id =>
        !studioLayerEntries({ overlays, motionScenes, threeDScenes }).some(entry => entry.id === id)))),
    motionScenes,
    ...(Array.isArray(compositionOrder) ? { compositionOrder } : {}),
    threeDScenes,
    subjectVfxScenes,
    motionTargetId,
    compositeTargetId,
    mainTransform,
    audioKeyframes,
    speedKeyframes,
    voiceovers,
    adjustmentLayers,
    nodeFlow,
    hideShyLayers,
    compoundClips: removeStudioGroupMembers(compoundClips, (compositionOrder || []).filter(id =>
      !studioLayerEntries({ overlays, motionScenes, threeDScenes }).some(entry => entry.id === id))),
    audioAutomationSource,
    audioRestoration,
    proxySettings,
    stabilization,
    advancedColor,
    creatorPreview,
    splitExportEnabled,
    splitExportHooks,
    };
    try {
      const studioDocument = reconcileStudioDocument({
        snapshot,
        projectId,
        storedDocument: studioDocumentRef.current,
      });
      studioDocumentRef.current = studioDocument;
      snapshot.studioDocument = studioDocument;
    } catch (error) {
      // A source may have no known duration while its media metadata loads.
      // The legacy editor can still hold that transient state; commands require
      // a validated document once the duration arrives.
      if (error.code !== "INVALID_SOURCE_RANGE") throw error;
      snapshot.studioDocument = null;
    }
    return snapshot.studioDocument
      ? projectStudioEditableStateToSnapshot(snapshot.studioDocument, snapshot)
      : snapshot;
  };
  latestEditorSnapshotRef.current = getEditorSnapshot;

  const refreshSavedProjects = async () => {
    try {
      const nextProjects = await listViralStudioProjects();
      if (nextProjects.length === 0 && savedProjects.length === 0) return;
      setSavedProjects(nextProjects);
    } catch (error) {
      console.warn("Could not read local Viral Clip Studio projects", error);
    }
  };

  const persistProjectSnapshot = async ({
    duplicate = false,
    snapshot = null,
    checkpoint = false,
  } = {}) => {
    const unfinishedMedia = [...(snapshot?.projectMedia || projectMedia), ...(snapshot?.speakerStackSources || speakerStackSources)].filter(item =>
      ["queued", "uploading", "failed", "needs_upload"].includes(item.status)
    );
    if (unfinishedMedia.length) {
      setProjectSaveState("unsaved");
      setMediaImportMessage(`Finish or retry ${unfinishedMedia.length} video upload${unfinishedMedia.length === 1 ? "" : "s"} before saving this project.`);
      return null;
    }
    // A delayed autosave captured before a rename must not overwrite this
    // explicit checkpoint after it reports "Saved locally".
    if (projectAutosaveTimerRef.current) {
      window.clearTimeout(projectAutosaveTimerRef.current);
      projectAutosaveTimerRef.current = null;
    }
    const nextId = duplicate ? createSecureId("viral-project") : projectId;
    const nextName = normalizePlainText(projectName) || "Untitled viral edit";
    const existingProject = savedProjects.find(project => project.id === projectId);
    const versions = duplicate
      ? []
      : [
          ...(existingProject?.versions || []),
          ...(checkpoint && existingProject?.snapshot
            ? [
                {
                  id: createSecureId("project-version"),
                  name: `Version ${(existingProject.versions || []).length + 1}`,
                  createdAt: Number(existingProject.updatedAt || Date.now()),
                  snapshot: existingProject.snapshot,
                },
              ]
            : []),
        ].slice(-12);
    setProjectSaveState("saving");
    try {
      const savedSnapshot = cloneSnapshot(snapshot || getEditorSnapshot());
      if (duplicate && savedSnapshot.studioDocument) {
        savedSnapshot.studioDocument = reconcileStudioDocument({
          snapshot: savedSnapshot,
          projectId: nextId,
        });
      }
      const saved = await saveViralStudioProject({
        id: nextId,
        name: duplicate ? `${nextName} copy` : nextName,
        createdAt: duplicate ? Date.now() : projectCreatedAtRef.current,
        snapshot: savedSnapshot,
        versions,
      });
      if (duplicate) {
        studioDocumentRef.current = savedSnapshot.studioDocument || null;
        activeProjectIdRef.current = saved.id;
        setProjectId(saved.id);
        setProjectName(saved.name);
        projectCreatedAtRef.current = saved.createdAt;
        // A pending refresh belongs to the previous project. Start the owned
        // duplicate's refresh so the stale finally cannot leave Play disabled.
        void refreshStoredSourceUrls(savedSnapshot);
      }
      setProjectSaveState("saved");
      setProjectSavedAt(saved.updatedAt);
      await refreshSavedProjects();
      setStudioActionMessage(
        duplicate
          ? "Project duplicated with its complete editable timeline."
          : checkpoint
            ? "Project checkpoint saved. Earlier versions remain restorable."
            : "Project saved locally with media, captions, layers and render settings."
      );
      return saved;
    } catch (error) {
      console.error("Could not save Viral Clip Studio project", error);
      setProjectSaveState("error");
      setStudioActionMessage(error.message || "Project could not be saved locally.");
      return null;
    }
  };

  const scheduleProjectAutosave = snapshot => {
    // IndexedDB is the durable project store. Browsers that block it (and
    // lightweight test DOMs that do not provide it) should keep editing
    // normally instead of scheduling a save that can never succeed.
    if (typeof indexedDB === "undefined") return;
    setProjectSaveState("unsaved");
    if (projectAutosaveTimerRef.current) {
      window.clearTimeout(projectAutosaveTimerRef.current);
      projectAutosaveTimerRef.current = null;
    }
    if ((snapshot?.projectMedia || []).some(item =>
      ["queued", "uploading", "failed", "needs_upload"].includes(item.status)
    )) return;
    projectAutosaveTimerRef.current = window.setTimeout(() => {
      projectAutosaveTimerRef.current = null;
      void persistProjectSnapshot({ snapshot });
    }, 3500);
  };

  const rehydrateStoredProjectSnapshot = snapshot => {
    const restoreItem = item => {
      if (!item || typeof item !== "object") return item;
      const file = item.file;
      if (!(typeof Blob !== "undefined" && file instanceof Blob)) return item;
      const mediaUrl = URL.createObjectURL(file);
      projectMediaObjectUrlsRef.current.add(mediaUrl);
      return {
        ...item,
        ...(Object.prototype.hasOwnProperty.call(item, "src") ? { src: mediaUrl } : {}),
        ...(Object.prototype.hasOwnProperty.call(item, "url") ? { url: mediaUrl } : {}),
      };
    };

    return {
      ...snapshot,
      orderedClips: (snapshot?.orderedClips || []).map(restoreItem),
      projectMedia: (snapshot?.projectMedia || []).map(item => {
        const restored = restoreItem(item);
        return restored.status === "uploading" || restored.status === "queued"
          ? { ...restored, status: "needs_upload", progress: 0 }
          : restored;
      }),
      timeline: (snapshot?.timeline || []).map(restoreItem),
      overlays: (snapshot?.overlays || []).map(restoreItem),
      soundEffects: (snapshot?.soundEffects || []).map(restoreItem),
      voiceovers: (snapshot?.voiceovers || []).map(restoreItem),
      musicTrack: restoreItem(snapshot?.musicTrack),
      extractedAudio: restoreItem(snapshot?.extractedAudio),
      speakerStackSources: (snapshot?.speakerStackSources || []).map(restoreItem),
    };
  };

  const refreshSourceUrlByPath = async (storagePath, expectedProjectId = activeProjectIdRef.current) => {
    if (!storagePath) return false;
    try {
      const token = await getMediaAuthToken();
      if (!token) throw new Error("Sign in to reopen saved videos.");
      const response = await fetch(`${API_BASE_URL}/api/media/studio-assets/resolve`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ storagePath }),
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok || !result.url) throw new Error(result.message || result.error || "Saved video is unavailable.");
      if (activeProjectIdRef.current !== expectedProjectId) return false;
      if (typeof result.generation === "string" && /^[1-9]\d{0,31}$/.test(result.generation)) {
        setSourceAccessGenerations(current => activeProjectIdRef.current !== expectedProjectId ? current : {
          projectId: expectedProjectId,
          byPath: { ...(current.projectId === expectedProjectId ? current.byPath : {}), [storagePath]: result.generation },
        });
      }
      setProjectMedia(current => mapStudioSourceAccess(current, storagePath, item =>
        item.url === result.url && item.status === "ready" && !item.error
          ? item : { ...item, url: result.url, status: "ready", error: "" }));
      setTimeline(current => mapStudioSourceAccess(current, storagePath, clip =>
        clip.url === result.url ? clip : { ...clip, url: result.url }));
      setOrderedClips(current => current.map(clip =>
        (clip.sourceStoragePath || clip.storagePath) === storagePath ||
        (storagePath === sourceStoragePath && clip.url === videoUrl)
          ? { ...clip, url: result.url }
          : clip));
      setSelectedClip(current => current && (
        (current.sourceStoragePath || current.storagePath) === storagePath ||
        (storagePath === sourceStoragePath && current.url === videoUrl)
      ) ? { ...current, url: result.url } : current);
      setOverlays(current => mapStudioSourceAccess(current, storagePath, overlay =>
        overlay.src === result.url && (!overlay.url || overlay.url === result.url)
          ? overlay : { ...overlay, src: result.url, ...(overlay.url ? { url: result.url } : {}) }));
      const refresh = current => mapStudioSourceAccess(current, storagePath, item =>
        item.url === result.url ? item : { ...item, url: result.url });
      setSpeakerStackSources(refresh);
      setVoiceovers(refresh);
      setSoundEffects(refresh);
      for (const setter of [setMusicTrack, setExtractedAudio]) setter(current =>
        current && (current.sourceStoragePath || current.storagePath) === storagePath ? { ...current, url: result.url } : current);
      // 3D asset access is recovered separately: legacy HQ revisions include
      // assetUrl and must not be invalidated by this media-only refresh.
      setThreeDScenes(current=>current.map(scene=>scene.assetType==='video'&&scene.assetStoragePath===storagePath
        ? normalizeStudio3DScene({...scene,assetUrl:result.url}):scene));
      return result.url;
    } catch (error) {
      if (activeProjectIdRef.current !== expectedProjectId) return false;
      setProjectMedia(current => current.map(item => item.storagePath === storagePath
        ? { ...item, status: "unavailable", error: error.message || "Saved video is unavailable. Refresh its link." }
        : item));
      setMediaImportMessage("Some saved videos could not be reopened. Use Refresh link on an affected video.");
      return false;
    }
  };

  const refreshStoredSourceUrls = async snapshot => {
    const expectedProjectId = activeProjectIdRef.current;
    const version = ++projectSourceRefreshVersionRef.current;
    const paths = new Set([
      ...(snapshot?.projectMedia || []).map(item => item.storagePath || item.sourceStoragePath),
      ...(snapshot?.timeline || []).map(item => item.sourceStoragePath || item.storagePath),
      ...(snapshot?.overlays || []).map(item => item.sourceStoragePath || item.storagePath),
      ...(snapshot?.speakerStackSources || []).map(item => item.sourceStoragePath || item.storagePath),
      ...(snapshot?.voiceovers || []).map(item => item.sourceStoragePath || item.storagePath),
      ...(snapshot?.soundEffects || []).map(item => item.sourceStoragePath || item.storagePath),
      snapshot?.musicTrack?.sourceStoragePath || snapshot?.musicTrack?.storagePath,
      snapshot?.extractedAudio?.sourceStoragePath || snapshot?.extractedAudio?.storagePath,
    ].filter(Boolean));
    setProjectSourcesRefreshing(true);
    try {
      const pending = [...paths];
      for (let offset = 0; offset < pending.length; offset += 4) {
        await Promise.all(pending.slice(offset, offset + 4).map(path =>
          refreshSourceUrlByPath(path, expectedProjectId)));
      }
    } finally {
      if (version === projectSourceRefreshVersionRef.current &&
          activeProjectIdRef.current === expectedProjectId) setProjectSourcesRefreshing(false);
    }
  };

  const openSavedProject = (project, { initialRestore = false } = {}) => {
    if (!project?.snapshot) return;
    // VideoEditor has already checked the recovered output's project/branding
    // binding. Initial hydration establishes the edit baseline; it is not an edit.
    lastSnapshotRef.current = null;
    lastSnapshotSignatureRef.current = null;
    if (!initialRestore) onProjectOpened?.(project.id);
    const restoredSnapshot = rehydrateStoredProjectSnapshot(project.snapshot);
    activeProjectIdRef.current = project.id;
    attemptedSourceRefreshRef.current.clear();
    applyEditorSnapshot(restoredSnapshot);
    setProjectId(project.id);
    setProjectName(project.name || "Untitled viral edit");
    projectCreatedAtRef.current = Number(project.createdAt || Date.now());
    setProjectSavedAt(Number(project.updatedAt || Date.now()));
    setProjectSaveState("saved");
    setComparisonMode("after");
    setStudioActionMessage(
      `Opened “${project.name || "Untitled viral edit"}” with editable layers.`
    );
    refreshStoredSourceUrls(restoredSnapshot);
  };

  const restoreProjectVersion = (project, version) => {
    if (!version?.snapshot) return;
    let restoredSnapshot;
    try {
      restoredSnapshot = restoreStudioProjectCheckpoint({
        projectId: project.id,
        restoredSnapshot: rehydrateStoredProjectSnapshot(version.snapshot),
        currentDocument: project.id === projectId
          ? getEditorSnapshot().studioDocument : null,
        savedDocument: project.snapshot?.studioDocument || null,
      });
    } catch (error) {
      setStudioActionMessage(error.message || "This checkpoint could not be restored.");
      return;
    }
    activeProjectIdRef.current = project.id;
    onEditInvalidated?.();
    attemptedSourceRefreshRef.current.clear();
    applyEditorSnapshot(restoredSnapshot);
    setProjectId(project.id);
    setProjectName(project.name || "Untitled viral edit");
    projectCreatedAtRef.current = Number(project.createdAt || Date.now());
    setProjectSaveState("unsaved");
    setProjectSavedAt(null);
    setComparisonMode("after");
    setStudioActionMessage(
      `Restored ${version.name || "an earlier version"}. Save a new checkpoint to keep it.`
    );
    refreshStoredSourceUrls(restoredSnapshot);
  };

  const removeSavedProject = async project => {
    if (!project?.id) return;
    if (!window.confirm(`Delete the saved project “${project.name || "Untitled viral edit"}”?`)) {
      return;
    }
    await deleteViralStudioProject(project.id);
    if (project.id === projectId) {
      setProjectId(createSecureId("viral-project"));
      projectCreatedAtRef.current = Date.now();
      setProjectSaveState("unsaved");
      setProjectSavedAt(null);
    }
    await refreshSavedProjects();
  };

  const saveEditTemplate = () => {
    const requestedName = window.prompt("Template name", `${projectName || "Viral edit"} style`);
    const name = normalizePlainText(requestedName);
    if (!name) return;
    const template = {
      id: createSecureId("edit-template"),
      name,
      createdAt: Date.now(),
      captionStyle,
      captionPosition,
      captionScale,
      creativePreset,
      creativeIntensity,
      finishFx: { ...finishFx },
      mainFrame: { ...mainFrame },
      audioRestoration: { ...audioRestoration },
      creatorPreview: { ...creatorPreview },
      layout: {
        smartCrop,
        smartCropMode,
        speakerStackCameraCount,
        speakerStackLayout,
        speakerStackFraming: { ...speakerStackFraming },
        speakerFocusCuts: normalizeSpeakerFocusCuts(speakerFocusCuts),
        reframeModeCuts: normalizeReframeModeCuts(reframeModeCuts),
      },
      transition: joinTransition,
      soundEffects: soundEffects.map(({ file: _file, url: _url, ...effect }) => ({ ...effect })),
      exportSettings: { ...exportSettings },
      titleLayers: overlays
        .filter(overlay => overlay.type === "text" && !overlay.isCaption)
        .map(({ file: _file, src: _src, ...overlay }) => ({ ...overlay })),
    };
    const nextTemplates = [...editTemplates, template].slice(-20);
    setEditTemplates(nextTemplates);
    window.localStorage.setItem("autopromote-viral-edit-templates", JSON.stringify(nextTemplates));
    setStudioActionMessage(
      "Full edit template saved with titles, captions, grade and audio treatment."
    );
  };

  const applyEditTemplate = template => {
    if (!template) return;
    setCaptionStyle(template.captionStyle || "bold_pop");
    setCaptionPosition(normalizeCaptionDefaultPlacement(template.captionPosition));
    setCaptionScale(Number(template.captionScale || 1));
    setCreativePreset(template.creativePreset || "auto_story");
    setCreativeIntensity(template.creativeIntensity || "bold");
    replaceFinishFx(template.finishFx || {});
    setMainFrame(current => normalizeMainFrame({ ...current, ...(template.mainFrame || {}) }));
    setAudioRestoration(current => ({ ...current, ...(template.audioRestoration || {}) }));
    setCreatorPreview(current => ({ ...current, ...(template.creatorPreview || {}) }));
    if (template.layout) {
      setSmartCrop(!!template.layout.smartCrop);
      setSmartCropMode(template.layout.smartCropMode || "center");
      setSpeakerStackCameraCount(Number(template.layout.speakerStackCameraCount || 2));
      setSpeakerStackLayout(template.layout.speakerStackLayout || "stack_2");
      setSpeakerFocusCuts(normalizeSpeakerFocusCuts(template.layout.speakerFocusCuts));
      setReframeModeCuts(normalizeReframeModeCuts(template.layout.reframeModeCuts));
      setSpeakerStackFraming(current => ({
        ...current,
        ...(template.layout.speakerStackFraming || {}),
      }));
    }
    setJoinTransition(template.transition || "auto");
    setExportSettings(current => ({ ...current, ...(template.exportSettings || {}) }));
    if (template.titleLayers?.length) {
      setOverlays(current => [
        ...current,
        ...template.titleLayers.map(layer => ({
          ...layer,
          id: createSecureId("template-title"),
        })),
      ]);
    }
    setComparisonMode("after");
    setStudioActionMessage(`Applied “${template.name}” as a fully editable style.`);
  };

  const getHistoryRelevantSnapshot = snapshot => {
    const {
      selectedClipId: _selectedClipId,
      activeOverlayId: _activeOverlayId,
      activeWatermarkRegionId: _activeWatermarkRegionId,
      activeSoundEffectId: _activeSoundEffectId,
      activeTimelineIndex: _activeTimelineIndex,
      cutRangeStart: _cutRangeStart,
      cutRangeEnd: _cutRangeEnd,
      timelineZoom: _timelineZoom,
      timelineEditTool: _timelineEditTool,
      timelineSnapping: _timelineSnapping,
      linkedSelection: _linkedSelection,
      rippleMode: _rippleMode,
      motionTargetId: _motionTargetId,
      compositeTargetId: _compositeTargetId,
      audioAutomationSource: _audioAutomationSource,
      activeReframeKeyframeId: _activeReframeKeyframeId,
      activeAngleGroupId: _activeAngleGroupId,
      hookPreviewLoop: _hookPreviewLoop,
      proxySettings: _proxySettings,
      projectMedia: snapshotProjectMedia,
      timeline: snapshotTimeline,
      orderedClips: snapshotOrderedClips,
      overlays: snapshotOverlays,
      ...editableSnapshot
    } = snapshot;
    const { revision, journal, idempotency, ...documentDecisions } = snapshot.studioDocument || {};
    const cleanCreator = value => {
      if (!value) return value;
      const { section, enabled, ...decisions } = value;
      return decisions;
    };
    const cleanTrackState = state => Object.fromEntries(Object.entries(state || {}).filter(([key]) => !['visible','locked'].includes(key)));
    const cleanTracks = tracks => Object.fromEntries(Object.entries(tracks || {}).map(([key,state]) => [key,cleanTrackState(state)]));
    if (documentDecisions.editGraph?.settings) {
      const {proxySettings, ...settings} = documentDecisions.editGraph.settings;
      documentDecisions.editGraph = {...documentDecisions.editGraph,settings};
    }
    if (documentDecisions.audioGraph) {
      const {audioAutomationSource,...audioSettings}=documentDecisions.audioGraph.settings || {};
      documentDecisions.audioGraph = {...documentDecisions.audioGraph,settings:audioSettings,
        routes:(documentDecisions.audioGraph.routes || []).map(route => ({...route,state:cleanTrackState(route.state)}))};
    }
    if (documentDecisions.editGraph?.settings?.creatorPreview) documentDecisions.editGraph = {
      ...documentDecisions.editGraph, settings: { ...documentDecisions.editGraph.settings,
        creatorPreview: cleanCreator(documentDecisions.editGraph.settings.creatorPreview) },
    };
    return {
      ...editableSnapshot,
      creatorPreview: cleanCreator(snapshot.creatorPreview),
      trackStates: cleanTracks(snapshot.trackStates),
      studioDocument: documentDecisions,
      timeline: (snapshotTimeline || []).map(historyMediaIdentity),
      orderedClips: (snapshotOrderedClips || []).map(historyMediaIdentity),
      overlays: (snapshotOverlays || []).map(historyMediaIdentity),
      speakerStackSources: (snapshot.speakerStackSources || []).map(historyMediaIdentity),
      voiceovers: (snapshot.voiceovers || []).map(historyMediaIdentity),
      soundEffects: (snapshot.soundEffects || []).map(historyMediaIdentity),
      musicTrack: historyMediaIdentity(snapshot.musicTrack),
      extractedAudio: historyMediaIdentity(snapshot.extractedAudio),
      subjectVfxScenes: (snapshot.subjectVfxScenes || []).map(historySubjectVfxIdentity),
      projectMedia: (snapshotProjectMedia || []).map(
        ({ status: _status, progress: _progress, error: _error, ...asset }) => historyMediaIdentity(asset)
      ),
    };
  };

  const syncHistoryAvailability = () => {
    setCanUndo(undoStackRef.current.length > 0);
    setCanRedo(redoStackRef.current.length > 0);
  };

  const applyEditorSnapshot = snapshot => {
    setExportErrorMessage("");
    setCreativeTemplateInstance(snapshot.creativeTemplateInstance || snapshot.studioDocument?.editGraph?.settings?.creativeTemplateInstance || null);
    if (isRestoringHistoryRef.current && studioDocumentRef.current) {
      try {
        studioDocumentRef.current = rebaseStudioHistoryRestore({
          currentDocument: studioDocumentRef.current,
          restoredSnapshot: snapshot,
          projectId,
        });
      } catch (error) {
        if (error.code !== "INVALID_SOURCE_RANGE") throw error;
        studioDocumentRef.current = null;
      }
    } else {
      studioDocumentRef.current = snapshot.studioDocument || null;
    }
    if (studioDocumentRef.current?.compatibility?.editableStateVersion === 1) {
      snapshot = projectStudioEditableStateToSnapshot(studioDocumentRef.current, snapshot);
    }
    const normalizedClips = snapshot.orderedClips || [];
    const normalizedOverlays = snapshot.overlays || [];
    setOrderedClips(normalizedClips);
    setProjectMedia(Array.isArray(snapshot.projectMedia)
      ? snapshot.projectMedia
      : (snapshot.timeline || []).slice(0, 1).filter(item => item?.url).map(item => ({
          id: `primary-${item.sourceStoragePath || item.storagePath || "source"}`,
          name: item.name || "Original source video",
          url: item.url,
          storagePath: item.sourceStoragePath || item.storagePath || "",
          sourceStoragePath: item.sourceStoragePath || item.storagePath || "",
          duration: Number(item.duration || 0),
          status: item.sourceStoragePath || item.storagePath ? "ready" : "existing",
          isPrimary: true,
        })));
    setAngleGroups(Array.isArray(snapshot.angleGroups) ? snapshot.angleGroups : []);
    setActiveAngleGroupId(snapshot.activeAngleGroupId || null);
    setSelectedProjectMediaId(null);
    setSelectedCameraMediaIds([]);
    setSelectedClip(
      normalizedClips.find(clip => clip.id === snapshot.selectedClipId) ||
        normalizedClips[0] ||
        null
    );
    setOverlays(normalizedOverlays);
    setActiveOverlayId(
      normalizedOverlays.some(overlay => overlay.id === snapshot.activeOverlayId)
        ? snapshot.activeOverlayId
        : normalizedOverlays[normalizedOverlays.length - 1]?.id || null
    );
    setVideoFit(snapshot.videoFit || "cover");
    setSafeFaceFraming(snapshot.safeFaceFraming !== undefined ? !!snapshot.safeFaceFraming : false);
    setFaceAnchorPreset(snapshot.faceAnchorPreset || "center");
    setAutoCaptions(!!snapshot.autoCaptions);
    setCaptionStyle(snapshot.captionStyle || "bold_pop");
    setCaptionPosition(normalizeCaptionDefaultPlacement(snapshot.captionPosition));
    setCaptionScale(Number(snapshot.captionScale ?? 1));
    setCaptionTextOverride(snapshot.captionTextOverride || "");
    setCaptionCoverageMode(snapshot.captionCoverageMode === "selected" ? "selected" : "full");
    setCaptionCoverageMessage("");
    setCaptionSegments(
      normalizeCaptionSegments(splitCaptionSegmentsForReadability(snapshot.captionSegments))
    );
    setTranslateCaptionsToEnglish(!!snapshot.translateCaptionsToEnglish);
    setCaptionSourceLanguage(snapshot.captionSourceLanguage || "auto");
    setPreviewSpeed(Number(snapshot.previewSpeed ?? 1));
    setPacingLevel(snapshot.pacingLevel || "balanced");
    setCreativeIntent(snapshot.creativeIntent || "energy");
    setCreativeEffectsEnabled(!!snapshot.creativeEffectsEnabled);
    setCreativePreset(snapshot.creativePreset || "auto_story");
    setCreativeIntensity(snapshot.creativeIntensity || "bold");
    replaceFinishFx(snapshot.finishFx);
    setPodcastVisualizer({
      enabled: false,
      mode: "wave",
      color: "#72f7ff",
      intensity: 0.75,
      position: "bottom",
      ...(snapshot.podcastVisualizer || {}),
    });
    setFinishKeyframes(Array.isArray(snapshot.finishKeyframes) ? snapshot.finishKeyframes : []);
    setBeatSnapEnabled(snapshot.beatSnapEnabled !== undefined ? !!snapshot.beatSnapEnabled : true);
    setContentProfile(snapshot.contentProfile || "auto");
    setCutRangeStart(
      snapshot.cutRangeStart !== undefined && snapshot.cutRangeStart !== null
        ? Number(snapshot.cutRangeStart)
        : null
    );
    setCutRangeEnd(
      snapshot.cutRangeEnd !== undefined && snapshot.cutRangeEnd !== null
        ? Number(snapshot.cutRangeEnd)
        : null
    );
    setJoinTransition(snapshot.joinTransition || "auto");
    setJoinTransitionDuration(Number(snapshot.joinTransitionDuration ?? 0.18));
    setSmartCrop(snapshot.smartCrop !== undefined ? !!snapshot.smartCrop : true);
    setSmartCropMode(snapshot.smartCropMode || "speaker_track");
    setReframeAspect(snapshot.reframeAspect || "9:16");
    setSpeakerTrackZoom(clampNumber(snapshot.speakerTrackZoom, 1, 3, 1));
    setReframeKeyframes(Array.isArray(snapshot.reframeKeyframes) ? snapshot.reframeKeyframes : []);
    setSpeakerStackFraming({
      ...DEFAULT_SPEAKER_STACK_FRAMING,
      ...(snapshot.speakerStackFraming || {}),
      cardGapPercent: clampNumber(snapshot.speakerStackFraming?.cardGapPercent, 0, 3, 2.5),
      top: {
        ...DEFAULT_SPEAKER_STACK_FRAMING.top,
        ...(snapshot.speakerStackFraming?.top || {}),
      },
      bottom: {
        ...DEFAULT_SPEAKER_STACK_FRAMING.bottom,
        ...(snapshot.speakerStackFraming?.bottom || {}),
      },
      third: {
        ...DEFAULT_SPEAKER_STACK_FRAMING.third,
        ...(snapshot.speakerStackFraming?.third || {}),
      },
      fourth: {
        ...DEFAULT_SPEAKER_STACK_FRAMING.fourth,
        ...(snapshot.speakerStackFraming?.fourth || {}),
      },
    });
    setSpeakerStackSourceIds({
      top: snapshot.speakerStackSourceIds?.top ?? null,
      bottom: snapshot.speakerStackSourceIds?.bottom ?? null,
      third: snapshot.speakerStackSourceIds?.third ?? null,
      fourth: snapshot.speakerStackSourceIds?.fourth ?? null,
    });
    setSpeakerStackOffsets({
      top: Number(snapshot.speakerStackOffsets?.top || 0),
      bottom: Number(snapshot.speakerStackOffsets?.bottom || 0),
      third: Number(snapshot.speakerStackOffsets?.third || 0),
      fourth: Number(snapshot.speakerStackOffsets?.fourth || 0),
    });
    setSpeakerStackSources(Array.isArray(snapshot.speakerStackSources) ? snapshot.speakerStackSources : []);
    const restoredCameraCount = clampNumber(snapshot.speakerStackCameraCount, 2, 4, 2);
    setSpeakerStackCameraCount(restoredCameraCount);
    setSpeakerStackLayout(
      snapshot.speakerStackLayout || getDefaultMulticamLayout(restoredCameraCount)
    );
    setSpeakerStackSyncConfirmed(!!snapshot.speakerStackSyncConfirmed);
    setSpeakerFocusCuts(normalizeSpeakerFocusCuts(snapshot.speakerFocusCuts));
    setReframeModeCuts(normalizeReframeModeCuts(snapshot.reframeModeCuts));
    setActiveReframeKeyframeId(snapshot.activeReframeKeyframeId || null);
    setEnhanceQuality(!!snapshot.enhanceQuality);
    setSilenceRemoval(!!snapshot.silenceRemoval);
    setSilenceThreshold(Number(snapshot.silenceThreshold ?? -35));
    setMinSilenceDuration(Number(snapshot.minSilenceDuration ?? 0.75));
    setRemoveWatermark(!!snapshot.removeWatermark);
    setWatermarkMode(snapshot.watermarkMode || "adaptive");
    setMainFrame(normalizeMainFrame({
      insetPercent: 3,
      radiusPercent: 8,
      background: "studio_black",
      ...(snapshot.mainFrame || {}),
    }));
    setExportSettings({
      resolution: "1080p",
      fps: "30",
      codec: "h264",
      quality: "high",
      videoBitrate: "auto",
      targetFileSizeMb: "",
      audioCodec: "aac",
      audioBitrate: "192",
      colorSpace: "rec709",
      ...(snapshot.exportSettings || {}),
    });
    setManualWatermarkRegions(
      Array.isArray(snapshot.manualWatermarkRegions)
        ? snapshot.manualWatermarkRegions.map(clampManualWatermarkRegion)
        : []
    );
    setActiveWatermarkRegionId(snapshot.activeWatermarkRegionId || null);
    setAddHook(!!snapshot.addHook);
    setHookText(snapshot.hookText ?? DEFAULT_HOOK_TEXT);
    setHookTemplate(snapshot.hookTemplate || "blur_reveal");
    setHookIntroSeconds(Number(snapshot.hookIntroSeconds ?? 3));
    setHookStartTime(Number(snapshot.hookStartTime ?? 0.8));
    setHookEndTime(
      Number(
        snapshot.hookEndTime ??
          Number(snapshot.hookStartTime ?? 0.8) + Number(snapshot.hookIntroSeconds ?? 3)
      )
    );
    setHookBlurBackground(
      snapshot.hookBlurBackground !== undefined ? !!snapshot.hookBlurBackground : true
    );
    setHookDarkOverlay(snapshot.hookDarkOverlay !== undefined ? !!snapshot.hookDarkOverlay : true);
    setHookFreezeFrame(!!snapshot.hookFreezeFrame);
    setHookZoomScale(Number(snapshot.hookZoomScale ?? 1.08));
    setHookTextAnimation(snapshot.hookTextAnimation || "slide-up");
    setHookFocusPoint(normalizeHookFocusPoint(snapshot.hookFocusPoint));
    setHookPreviewLoop(!!snapshot.hookPreviewLoop);
    setAddMusic(!!snapshot.addMusic);
    setMuteOriginalAudio(!!snapshot.muteOriginalAudio);
    setMusicSelection(snapshot.musicSelection || currentMusic || "upbeat_pop.mp3");
    setMusicSearchMode(!!snapshot.musicSearchMode);
    setSafeSearch(snapshot.safeSearch !== undefined ? !!snapshot.safeSearch : true);
    setMusicVolume(Number(snapshot.musicVolume ?? 0.15));
    setMusicDucking(snapshot.musicDucking !== undefined ? !!snapshot.musicDucking : true);
    setMusicDuckingStrength(Number(snapshot.musicDuckingStrength ?? 0.35));
    setMusicTrack(snapshot.musicTrack || null);
    setSoundEffects(Array.isArray(snapshot.soundEffects) ? snapshot.soundEffects : []);
    setAudioRemix(normalizeAudioRemix(snapshot.audioRemix || DEFAULT_AUDIO_REMIX));
    setAudioRemixBypass(false);
    setAudioRemixLoop(null);
    setActiveSoundEffectId(snapshot.activeSoundEffectId || null);
    setExtractedAudio(snapshot.extractedAudio || null);
    setSilencePreview(snapshot.silencePreview || null);
    setBRollCadence(snapshot.bRollCadence || "balanced");
    setBRollGenerationJobId(snapshot.bRollGenerationJobId || "");
    setTimeline(snapshot.timeline || []);
    setActiveTimelineIndex(Math.max(0, Number(snapshot.activeTimelineIndex || 0)));
    if (!isRestoringHistoryRef.current) {
      setTimelineZoom(Number(snapshot.timelineZoom || 1));
      setTimelineEditTool(snapshot.timelineEditTool || "select");
      setTimelineSnapping(snapshot.timelineSnapping !== false);
      setLinkedSelection(snapshot.linkedSelection !== false);
      setRippleMode(!!snapshot.rippleMode);
    }
    setTrackStates(current => Object.fromEntries(Object.entries({...DEFAULT_TRACK_STATES,...(snapshot.trackStates || {})})
      .map(([id,state]) => [id, {...state,...(isRestoringHistoryRef.current ? {
        visible:current[id]?.visible,locked:current[id]?.locked,
      } : {})}])));
    setMotionKeyframes(Array.isArray(snapshot.motionKeyframes) ? snapshot.motionKeyframes : []);
    setMotionScenes(Array.isArray(snapshot.motionScenes) ? snapshot.motionScenes.map(normalizeMotion) : []);
    setCompositionOrder(Array.isArray(snapshot.compositionOrder) ? snapshot.compositionOrder : null);
    setThreeDScenes(Array.isArray(snapshot.threeDScenes) ? snapshot.threeDScenes.map(normalizeStudio3DScene) : []);
    setSubjectVfxScenes(Array.isArray(snapshot.subjectVfxScenes) ? snapshot.subjectVfxScenes : []);
    setSelectedMotionId(null);
    setSelectedThreeDId(null);
    setMotionTargetId(snapshot.motionTargetId || "main-video");
    setCompositeTargetId(snapshot.compositeTargetId || "main-video");
    setMainTransform({
      id: "main-video",
      name: "Main video",
      type: "video",
      x: 50,
      y: 50,
      scale: 1,
      rotation: 0,
      opacity: 1,
      cropX: 0,
      cropY: 0,
      ...(snapshot.mainTransform || {}),
    });
    setAudioKeyframes({
      originalAudio: [],
      masterPodcast: [],
      voiceover: [],
      music: [],
      broll: [],
      sfx: [],
      ...(snapshot.audioKeyframes || {}),
    });
    setSpeedKeyframes(Array.isArray(snapshot.speedKeyframes) ? snapshot.speedKeyframes : []);
    setVoiceovers(Array.isArray(snapshot.voiceovers) ? snapshot.voiceovers : []);
    setAdjustmentLayers(Array.isArray(snapshot.adjustmentLayers) ? snapshot.adjustmentLayers : []);
    setNodeFlow({
      version: 1,
      viewerA: "MediaOut",
      viewerB: null,
      splitPosition: 50,
      positions: {},
      bypassed: {},
      ...(snapshot.nodeFlow || {}),
    });
    setHideShyLayers(Boolean(snapshot.hideShyLayers));
    setCompoundClips(Array.isArray(snapshot.compoundClips) ? snapshot.compoundClips : []);
    setAudioAutomationSource(snapshot.audioAutomationSource || "originalAudio");
    setAudioRestoration({
      preset: "natural",
      voiceIsolation: false,
      denoise: 22,
      deEsser: 18,
      humFrequency: 50,
      compressor: 35,
      limiter: -1,
      loudness: -14,
      eq: { low: 0, mid: 0, high: 0 },
      ...(snapshot.audioRestoration || {}),
    });
    setProxySettings({
      enabled: true,
      resolution: "720p",
      codec: "h264",
      status: "source_ready",
      ...(snapshot.proxySettings || {}),
    });
    setStabilization({
      enabled: false,
      strength: 35,
      crop: "adaptive",
      rollingShutter: false,
      ...(snapshot.stabilization || {}),
    });
    setAdvancedColor({
      lutName: "",
      lutFile: null,
      lutIntensity: 100,
      curve: { shadows: 0, midtones: 0, highlights: 0 },
      hsl: { hue: 0, saturation: 0, luminance: 0 },
      ...(snapshot.advancedColor || {}),
    });
    setCreatorPreview({
      ...DEFAULT_CREATOR_PREVIEW,
      ...(snapshot.creatorPreview || {}),
      ...(isRestoringHistoryRef.current ? { section: creatorPreview.section } : {}),
    });
    setSplitExportEnabled(!!snapshot.splitExportEnabled);
    setSplitExportHooks(Array.isArray(snapshot.splitExportHooks) ? snapshot.splitExportHooks : []);
  };

  useEffect(() => {
    if (!currentMusic) return;
    setMusicSelection(currentMusic);
    setMusicSearchMode(!isPresetMusicSelection(currentMusic));
  }, [currentMusic]);

  useEffect(() => {
    void refreshSavedProjects();
    try {
      const storedTemplates = JSON.parse(
        window.localStorage.getItem("autopromote-viral-edit-templates") || "[]"
      );
      setEditTemplates(Array.isArray(storedTemplates) ? storedTemplates : []);
    } catch (error) {
      console.warn("Could not read Viral Clip Studio templates", error);
    }
    return () => {
      if (projectAutosaveTimerRef.current) {
        window.clearTimeout(projectAutosaveTimerRef.current);
        projectAutosaveTimerRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (!initialSavedProject?.snapshot) return;
    // Restore after source initialization effects have run. Reuse the same
    // project rehydration and owner-bound URL refresh as the editor's list.
    const frame = window.requestAnimationFrame(() => openSavedProject(initialSavedProject, { initialRestore: true }));
    return () => window.cancelAnimationFrame(frame);
    // Only a different requested project should replace the live edit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialSavedProject?.id]);

  useEffect(() => {
    // The Studio is a full-screen editor. Lock the dashboard document behind it
    // so deep inspector controls cannot scroll the entire fixed workspace and
    // visually push the header off-screen.
    const root = document.documentElement;
    const body = document.body;
    const previousRootOverflow = root.style.overflow;
    const previousBodyOverflow = body.style.overflow;
    const previousRootScroll = root.scrollTop;
    const previousBodyScroll = body.scrollTop;
    root.classList.add("viral-studio-open");
    body.classList.add("viral-studio-open");
    root.style.overflow = "hidden";
    body.style.overflow = "hidden";
    root.scrollTop = 0;
    body.scrollTop = 0;
    return () => {
      root.classList.remove("viral-studio-open");
      body.classList.remove("viral-studio-open");
      root.style.overflow = previousRootOverflow;
      body.style.overflow = previousBodyOverflow;
      root.scrollTop = previousRootScroll;
      body.scrollTop = previousBodyScroll;
    };
  }, []);

  useEffect(() => {
    if (projectName !== "Untitled viral edit") return;
    const suggestedName = normalizePlainText(
      selectedClip?.hookText || selectedClip?.reason || timeline[0]?.name || ""
    );
    if (suggestedName) setProjectName(suggestedName.slice(0, 64));
  }, [projectName, selectedClip, timeline]);

  useEffect(() => {
    const handleCommandShortcut = event => {
      const targetTag = event.target?.tagName;
      const isTypingTarget =
        event.target?.isContentEditable || ["INPUT", "TEXTAREA", "SELECT"].includes(targetTag);
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        setCommandPaletteOpen(current => !current);
      }
      if (!isTypingTarget && !event.metaKey && !event.ctrlKey && event.key.toLowerCase() === "m") {
        event.preventDefault();
        setPreviewMuted(current => !current);
      }
      if (!isTypingTarget && !event.metaKey && !event.ctrlKey && event.key.toLowerCase() === "z") {
        event.preventDefault();
        studioShortcutActionsRef.current.punch?.();
      }
      if (!isTypingTarget && !event.metaKey && !event.ctrlKey && event.key.toLowerCase() === "j") {
        event.preventDefault();
        studioShortcutActionsRef.current.jcut?.();
      }
      if (!isTypingTarget && !event.metaKey && !event.ctrlKey && event.key.toLowerCase() === "l") {
        event.preventDefault();
        studioShortcutActionsRef.current.lcut?.();
      }
      if (!isTypingTarget && !event.metaKey && !event.ctrlKey && event.shiftKey && event.key.toLowerCase() === "b") {
        event.preventDefault();
        setBeatSnapEnabled(current => !current);
      }
      const tool = { v: 'select', c: 'razor', b: 'ripple', n: 'roll', y: 'slip', u: 'slide', h: 'hand' }[event.key.toLowerCase()];
      if (!isTypingTarget && !event.metaKey && !event.ctrlKey && !event.altKey && !event.shiftKey && tool) {
        event.preventDefault(); setTimelineEditTool(tool);
      }
      if (event.key === "Escape") setCommandPaletteOpen(false);
    };
    window.addEventListener("keydown", handleCommandShortcut);
    return () => window.removeEventListener("keydown", handleCommandShortcut);
  }, []);

  useEffect(
    () => () => {
      if (voiceoverRecorderRef.current) {
        voiceoverRecorderRef.current.onstop = null;
        voiceoverRecorderRef.current.ondataavailable = null;
        if (voiceoverRecorderRef.current.state !== "inactive") voiceoverRecorderRef.current.stop();
      }
      voiceoverStreamRef.current?.getTracks().forEach(track => track.stop());
      stopMusicPreviewBufferPlayback();
      if (musicPreviewAudioContextRef.current) {
        musicPreviewAudioContextRef.current.close().catch(() => {});
      }
      soundEffectAudioRefsRef.current.forEach(audio => audio?.pause());
      soundEffectStartVersionRef.current += 1;
      soundEffectNodesRef.current.forEach(node => {
        try {
          node.stop?.();
          node.disconnect?.();
        } catch (error) {
          // The node may already have completed naturally.
        }
      });
      soundEffectNodesRef.current.clear();
      soundEffectTimelineGainsRef.current.clear();
      if (soundEffectAudioContextRef.current && soundEffectAudioContextRef.current !== musicPreviewAudioContextRef.current) {
        soundEffectAudioContextRef.current.close().catch(() => {});
      }
      for (const context of speechAnalysisContextsRef.current) context.close().catch(() => {});
      speechAnalysisContextsRef.current.clear();
      // Real unmount releases each bounded decoder's audio graph. React's
      // development effect replay keeps the connected elements alive.
      const media = [...remixPreviewMediaRef.current];
      queueMicrotask(() => {
        for (const element of media) if (!element.isConnected) disposeAudioRemixPreview(element);
      });
      speechAnalyserRef.current = null;
      soundEffectObjectUrlsRef.current.forEach(url => URL.revokeObjectURL(url));
      soundEffectObjectUrlsRef.current.clear();
      releaseMusicPreviewObjectUrl();
    },
    []
  );

  const applyHookTemplate = templateKey => {
    const template = getHookTemplateConfig(templateKey);
    setHookTemplate(templateKey);
    setHookBlurBackground(template.blurBackground);
    setHookDarkOverlay(template.darkOverlay);
    setHookFreezeFrame(template.freezeFrame);
    setHookTextAnimation(template.textAnimation);
  };

  const setOpeningHookEnabled = enabled => {
    // Choose the initial copy in the same user action. A later effect would
    // create an extra Undo step, and could rewrite text restored by Undo.
    setAddHook(enabled);
    if (enabled && isGenericHookText(hookText)) {
      setHookText(currentHookCopySuggestions[0] || DEFAULT_HOOK_TEXT);
    }
  };

  const setHookDuration = durationSeconds => {
    const nextDuration = clampNumber(
      durationSeconds,
      hookMinDuration,
      hookMaxDuration,
      hookIntroSeconds || 3
    );
    setHookSegmentRange(resolvedHookStart, resolvedHookStart + nextDuration);
  };

  const seekHookTimelineTime = targetTime => {
    const video = videoRef.current;
    const boundedTime = clampNumber(
      targetTime,
      0,
      Math.max(0, Number(currentTimelineWindow.duration || 0)),
      0
    );

    if (!video) return;

    video.currentTime = Number(currentTimelineWindow.start || 0) + boundedTime;
    setVideoTime(video.currentTime);
  };

  const setCurrentTimeAsHook = () => {
    const currentVideoTime = Number(videoRef.current?.currentTime || 0);
    const localHookTime = Math.max(0, currentVideoTime - Number(currentTimelineWindow.start || 0));
    const nextStart = clampNumber(localHookTime, 0, hookStartLimit, resolvedHookStart);
    setAddHook(true);
    applyHookTemplate("freeze_text");
    setHookFreezeFrame(true);
    setHookAnalysisStatus("ready");
    setHookAnalysisMessage("Selected moment saved as the frozen opening hook.");
    setHookSegmentRange(nextStart, nextStart + hookDuration, { preview: false });
    setHookFocusMode(false);
    setHookPickMode(false);
  };

  const handlePreviewFrameClick = event => {
    setActiveOverlayId(null);
    setActiveWatermarkRegionId(null);

    if (!hookFocusMode || isDragging) return;

    const frame = phoneFrameRef.current;
    if (!frame) return;

    const rect = frame.getBoundingClientRect();
    const nextFocusPoint = normalizeHookFocusPoint({
      x: ((event.clientX - rect.left) / Math.max(1, rect.width)) * 100,
      y: ((event.clientY - rect.top) / Math.max(1, rect.height)) * 100,
    });

    setAddHook(true);
    setHookFocusPoint(nextFocusPoint);
    setHookFocusMode(false);
    setHookZoomScale(current => Math.max(1.12, Number(current || 0)));
    setHookAnalysisStatus("ready");
    setHookAnalysisMessage("Focus target locked for the frozen opening frame.");
    event.preventDefault();
    event.stopPropagation();
  };

  const setHookSegmentRange = (startTime, endTime, options = {}) => {
    const clipDuration = Math.max(0, Number(currentTimelineWindow.duration || 0));
    const minimumDuration = Math.min(
      HOOK_MIN_SEGMENT_DURATION,
      Math.max(0.25, clipDuration || HOOK_MIN_SEGMENT_DURATION)
    );
    const maximumDuration = Math.min(
      HOOK_MAX_SEGMENT_DURATION,
      Math.max(minimumDuration, clipDuration || HOOK_MAX_SEGMENT_DURATION)
    );
    const boundedStart = clampNumber(startTime, 0, Math.max(0, clipDuration - minimumDuration), 0);
    const fallbackDuration = clampNumber(
      endTime - startTime,
      minimumDuration,
      maximumDuration,
      hookIntroSeconds || 3
    );
    const boundedEnd =
      clipDuration > 0
        ? clampNumber(
            endTime,
            Math.min(clipDuration, boundedStart + minimumDuration),
            Math.min(clipDuration, boundedStart + maximumDuration),
            Math.min(clipDuration, boundedStart + fallbackDuration)
          )
        : boundedStart + fallbackDuration;

    setHookStartTime(Number(boundedStart.toFixed(2)));
    setHookEndTime(Number(boundedEnd.toFixed(2)));
    setHookIntroSeconds(Number(Math.max(0.25, boundedEnd - boundedStart).toFixed(2)));

    if (options.textSuggestion && isGenericHookText(hookText)) {
      setHookText(normalizeHookText(options.textSuggestion));
    }

    if (options.preview) {
      const video = videoRef.current;
      if (video) {
        video.currentTime = Number(currentTimelineWindow.start || 0) + boundedStart;
        setVideoTime(video.currentTime);
      }
    }
  };

  const beginHookSegmentDrag = (event, target) => {
    const track = hookSegmentTrackRef.current;
    if (!track || !currentTimelineWindow.duration) return;

    event.preventDefault();
    event.stopPropagation();

    hookSelectionDragRef.current = {
      target,
      startClientX: event.clientX,
      anchorStart: resolvedHookStart,
      anchorEnd: hookEnd,
      rect: track.getBoundingClientRect(),
    };
  };

  const beginHookPlayheadDrag = event => {
    const track = hookSegmentTrackRef.current;
    if (!track || !currentTimelineWindow.duration) return;

    event.preventDefault();
    event.stopPropagation();

    hookPlayheadDragRef.current = {
      startClientX: event.clientX,
      anchorTime: trimAwareCurrentTime,
      rect: track.getBoundingClientRect(),
    };
  };

  const handleHookTrackPointerDown = event => {
    if (hookPickMode) {
      const track = hookSegmentTrackRef.current;
      if (!track || !currentTimelineWindow.duration) return;

      const rect = track.getBoundingClientRect();
      const ratio = clampNumber((event.clientX - rect.left) / Math.max(1, rect.width), 0, 1, 0);
      const clickedTime = ratio * Number(currentTimelineWindow.duration || 0);
      seekHookTimelineTime(clickedTime);
      return;
    }

    if (!hookSelectionMode) return;

    const track = hookSegmentTrackRef.current;
    if (!track || !currentTimelineWindow.duration) return;

    const rect = track.getBoundingClientRect();
    const ratio = clampNumber((event.clientX - rect.left) / Math.max(1, rect.width), 0, 1, 0);
    const clickedTime = ratio * Number(currentTimelineWindow.duration || 0);
    const distanceToStart = Math.abs(clickedTime - resolvedHookStart);
    const distanceToEnd = Math.abs(clickedTime - hookEnd);

    if (clickedTime >= resolvedHookStart && clickedTime <= hookEnd) {
      beginHookSegmentDrag(event, "range");
      return;
    }

    setHookSegmentRange(
      distanceToStart <= distanceToEnd ? clickedTime : resolvedHookStart,
      distanceToStart <= distanceToEnd ? hookEnd : clickedTime,
      { preview: true }
    );
  };

  const runSmartHookSuggestion = async () => {
    const clip = currentTimelineClip;
    if (!clip) {
      setHookAnalysisStatus("failed");
      setHookAnalysisMessage("Select a clip before running hook analysis.");
      return;
    }

    const clipWindow = getTimelineClipWindow(clip);
    const requestId = Date.now();
    const suggestionAttempt = hookSuggestionCycleRef.current;
    hookSuggestionCycleRef.current += 1;
    hookAnalysisRequestRef.current = requestId;
    setHookAnalysisStatus("analyzing");
    setHookAnalysisMessage(
      "Scanning motion, scene changes, and visual contrast in the selected clip..."
    );

    let analysisUrl = clip.url;
    let temporaryObjectUrl = "";

    try {
      if (clip.file instanceof Blob) {
        temporaryObjectUrl = URL.createObjectURL(clip.file);
        analysisUrl = temporaryObjectUrl;
      }

      if (!analysisUrl) {
        throw new Error("This clip does not have a previewable source yet.");
      }

      const analysisVideo = document.createElement("video");
      analysisVideo.muted = true;
      analysisVideo.playsInline = true;
      analysisVideo.preload = "auto";
      analysisVideo.crossOrigin = "anonymous";
      if (!applySafeMediaSource(analysisVideo, analysisUrl)) {
        throw new Error("This clip source uses an unsupported preview URL.");
      }

      if (Number.isNaN(analysisVideo.duration) || !analysisVideo.duration) {
        await waitForVideoEvent(analysisVideo, "loadedmetadata");
      }

      const sourceDuration = Number(analysisVideo.duration || clipWindow.end || 0);
      const analysisStart = Math.max(0, Number(clipWindow.start || 0));
      const fullWindowDuration = Math.max(0.25, Number(clipWindow.duration || sourceDuration || 0));
      const analysisDuration = Math.min(fullWindowDuration, 18);
      const analysisEnd = Math.min(
        sourceDuration || analysisStart + analysisDuration,
        analysisStart + analysisDuration
      );
      const sampleCount = Math.max(12, Math.min(34, Math.round(analysisDuration * 3.2)));
      const step = Math.max(0.18, analysisDuration / Math.max(1, sampleCount - 1));
      const canvas = document.createElement("canvas");
      canvas.width = 96;
      canvas.height = 54;
      const context = canvas.getContext("2d", { willReadFrequently: true });

      if (!context) {
        throw new Error("The browser could not open a frame analysis context.");
      }

      const samples = [];
      let previousFrame = null;

      for (let sampleIndex = 0; sampleIndex < sampleCount; sampleIndex += 1) {
        const targetTime = Math.min(analysisEnd, analysisStart + sampleIndex * step);
        await seekAnalysisVideo(analysisVideo, targetTime);

        const frameMetrics = captureHookAnalysisFrame(
          context,
          analysisVideo,
          canvas.width,
          canvas.height,
          previousFrame
        );

        samples.push({ time: targetTime, ...frameMetrics });
        previousFrame = frameMetrics.grayscale;
      }

      const motionValues = samples.map(sample => sample.motion || 0);
      const averageMotion =
        motionValues.reduce((sum, value) => sum + value, 0) / Math.max(1, motionValues.length);
      const motionVariance =
        motionValues.reduce((sum, value) => sum + Math.pow(value - averageMotion, 2), 0) /
        Math.max(1, motionValues.length);
      const motionDeviation = Math.sqrt(motionVariance);
      const sceneCutThreshold = averageMotion + Math.max(0.045, motionDeviation * 1.15);

      const scoredSamples = samples.map((sample, index) => {
        const nextMotion = samples[index + 1]?.motion || sample.motion || 0;
        const sceneCut = index > 0 && sample.motion > sceneCutThreshold;
        const rise = Math.max(0, nextMotion - (samples[index - 1]?.motion || 0));

        return {
          ...sample,
          sceneCut,
          rise,
          score:
            (sample.motion || 0) * 1.35 +
            (sample.centerMotion || 0) * 0.85 +
            (sample.contrast || 0) * 0.48 +
            rise * 0.8 +
            (sceneCut ? 1.8 : 0),
        };
      });

      const candidateDurations = [2, 2.4, 3, 3.6, 4.2, 5]
        .filter(duration => duration <= Math.min(HOOK_MAX_SEGMENT_DURATION, fullWindowDuration))
        .map(duration => Number(duration.toFixed(2)));

      const candidateWindows = [];
      scoredSamples.forEach(sample => {
        candidateDurations.forEach(duration => {
          const windowEnd = sample.time + duration;
          if (windowEnd > analysisEnd + 0.02) return;

          const windowSamples = scoredSamples.filter(
            candidate => candidate.time >= sample.time && candidate.time <= windowEnd + 0.001
          );
          if (!windowSamples.length) return;

          const score = windowSamples.reduce((sum, candidate) => sum + candidate.score, 0);
          const motionAverage =
            windowSamples.reduce((sum, candidate) => sum + candidate.motion, 0) /
            windowSamples.length;
          const sceneCuts = windowSamples.filter(candidate => candidate.sceneCut).length;
          const focusEnergy =
            windowSamples.reduce((sum, candidate) => sum + candidate.centerMotion, 0) /
            windowSamples.length;
          const earlyBias =
            Math.max(0, 1 - (sample.time - analysisStart) / Math.max(1, analysisDuration)) * 0.35;
          const totalScore =
            score + sceneCuts * 1.2 + motionAverage * 1.4 + focusEnergy * 0.6 + earlyBias;

          candidateWindows.push({
            startTime: sample.time,
            endTime: windowEnd,
            duration,
            score: totalScore,
            motionAverage,
            focusEnergy,
            sceneCuts,
          });
        });
      });

      const distinctCandidates = candidateWindows
        .sort((left, right) => right.score - left.score)
        .filter(
          (candidate, index, candidates) =>
            !candidates
              .slice(0, index)
              .some(
                prior =>
                  Math.abs(prior.startTime - candidate.startTime) < 0.55 &&
                  Math.abs(prior.duration - candidate.duration) < 0.55
              )
        )
        .slice(0, 4);
      const bestWindow = distinctCandidates.length
        ? distinctCandidates[suggestionAttempt % distinctCandidates.length]
        : null;

      if (!bestWindow) {
        throw new Error("The clip was too short to suggest a hook segment.");
      }

      const relativeStart = Math.max(0, bestWindow.startTime - analysisStart);
      const relativeEnd = Math.min(fullWindowDuration, bestWindow.endTime - analysisStart);
      const templateKey =
        bestWindow.motionAverage > averageMotion + motionDeviation * 0.55
          ? "zoom_focus"
          : bestWindow.sceneCuts > 0
            ? "freeze_text"
            : "blur_reveal";
      const confidenceLabel =
        bestWindow.score >= 7.5
          ? "High confidence"
          : bestWindow.score >= 4.75
            ? "Good confidence"
            : "Useful lead";
      const analysisReason =
        bestWindow.sceneCuts > 0
          ? "Detected a clean beat change with a visible motion spike in this window."
          : bestWindow.motionAverage > averageMotion + 0.02
            ? "Detected the strongest sustained movement and visual contrast in this window."
            : "Detected the most visually stable attention peak near the opening of the clip.";
      const preferredCopy =
        bestWindow.sceneCuts > 0
          ? currentHookCopySuggestions.find(copy => /flip|changes|matters/i.test(copy)) ||
            currentHookCopySuggestions[0]
          : bestWindow.motionAverage > averageMotion + 0.02
            ? currentHookCopySuggestions.find(copy => /blink|watch|fast/i.test(copy)) ||
              currentHookCopySuggestions[0]
            : currentHookCopySuggestions[0];
      const preferredCopyIndex = Math.max(0, currentHookCopySuggestions.indexOf(preferredCopy));
      let suggestedCopy =
        currentHookCopySuggestions[
          (preferredCopyIndex + suggestionAttempt) % Math.max(1, currentHookCopySuggestions.length)
        ] || preferredCopy;
      const currentSuggestedCopy = normalizeHookText(
        hookSuggestedRange?.textSuggestion || hookText
      );
      if (
        currentHookCopySuggestions.length > 1 &&
        normalizeHookText(suggestedCopy) === currentSuggestedCopy
      ) {
        suggestedCopy =
          currentHookCopySuggestions[
            (preferredCopyIndex + suggestionAttempt + 1) % currentHookCopySuggestions.length
          ];
      }

      if (hookAnalysisRequestRef.current !== requestId) return;

      setHookSuggestedRange({
        startTime: Number(relativeStart.toFixed(2)),
        endTime: Number(relativeEnd.toFixed(2)),
        duration: Number((relativeEnd - relativeStart).toFixed(2)),
        templateKey,
        textSuggestion: normalizeHookText(suggestedCopy || DEFAULT_HOOK_TEXT),
        confidenceLabel,
        analysisSource: "video_scan",
        score: bestWindow.score,
        message: analysisReason,
      });
      setHookAnalysisStatus("ready");
      setHookAnalysisMessage(`${confidenceLabel}. ${analysisReason}`);
    } catch (error) {
      console.error("Hook suggestion analysis failed", error);
      if (hookAnalysisRequestRef.current !== requestId) return;

      const fallbackRange = buildFallbackHookRange(selectedClip, clipWindow.duration);
      setHookSuggestedRange(fallbackRange);
      setHookAnalysisStatus("failed");
      setHookAnalysisMessage(
        `${error?.message || "Hook analysis failed."} Falling back to the existing metadata-based suggestion.`
      );
    } finally {
      if (temporaryObjectUrl) {
        URL.revokeObjectURL(temporaryObjectUrl);
      }
    }
  };

  const previewHookSegment = (shouldLoop, rangeOverride = null) => {
    const video = videoRef.current;
    if (!video) return;

    const previewStart = Number(rangeOverride?.startTime ?? resolvedHookStart);
    const previewEnd = Number(rangeOverride?.endTime ?? hookEnd);
    const absoluteHookStart = Number(currentTimelineWindow.start || 0) + previewStart;
    const absoluteHookEnd = Number(currentTimelineWindow.start || 0) + previewEnd;

    setTrimPreviewLoop(false);
    setHookPreviewLoop(!!shouldLoop);
    hookPreviewSequenceRef.current =
      shouldLoop || absoluteHookEnd <= absoluteHookStart + 0.05
        ? { active: false }
        : {
            active: true,
            mode: "manual-preview",
            timelineIndex: activeTimelineIndex,
            absoluteStart: absoluteHookStart,
            absoluteEnd: absoluteHookEnd,
          };
    video.currentTime = absoluteHookStart;
    previewPlaybackIntentRef.current = true;
    safePlayMediaElement(video);
  };

  const previewTrimWindow = shouldLoop => {
    const video = videoRef.current;
    if (!video || !currentTimelineClip) return;

    hookPreviewSequenceRef.current = { active: false };
    setHookPreviewLoop(false);
    setTrimPreviewLoop(!!shouldLoop);
    video.currentTime = Number(currentTimelineWindow.start || 0);
    previewPlaybackIntentRef.current = true;
    safePlayMediaElement(video);
  };

  const pauseSynchronizedPreview = () => {
    previewPlaybackStartRef.current += 1;
    previewPlaybackPreparingRef.current = null;
    preparedCutHandoffRef.current = null;
    previewPlaybackIntentRef.current = false;
    setIsPreviewPaused(true);
    videoRef.current?.pause();
    beforeVideoRef.current?.pause();
    audioRef.current?.pause();
    hookBackdropVideoRef.current?.pause();
    hookFreezeVideoRef.current?.pause();
    smartCropForegroundVideoRef.current?.pause();
    musicPreviewRef.current?.pause();
    exactAudioRemixPreviewRef.current?.pause();
    stopMusicPreviewBufferPlayback();
    stopSoundEffectPlayback();
    overlayMediaRefsRef.current.forEach(media => media.pause());
  };

  const togglePreviewPlayback = () => {
    const video = videoRef.current;
    if (!video) return;

    // Match the action shown by the control. Loading a new source can pause
    // the decoder while the operator's playback intention is still Play.
    if (isPreviewPaused) {
      backgroundSoundPreviewSuppressedRef.current = false;
      isolatedMusicPreviewIntentRef.current = false;
      programmeMusicRouteRef.current = { sourceUrl: effectiveMusicPreviewUrl,
        decoded: Boolean(musicPreviewBufferRef.current && musicPreviewBufferUrlRef.current === effectiveMusicPreviewUrl) };
      setIsBackgroundSoundPreviewing(false);
      previewPlaybackIntentRef.current = true;
      setIsPreviewPaused(false);
      safePlayMediaElement(video);
    } else {
      pauseSynchronizedPreview();
    }
  };

  const handleTrimAwareScrub = event => {
    const video = videoRef.current;
    if (!video || !currentTimelineClip) return;

    const relativeTime = clampNumber(
      event.target.value,
      0,
      Math.max(0, Number(currentTimelineWindow.duration || 0)),
      0
    );
    video.currentTime = Number(currentTimelineWindow.start || 0) + relativeTime;
    setVideoTime(video.currentTime);
  };

  const applyCurrentHookSuggestion = shouldLoopPreview => {
    setHookSegmentRange(currentHookSuggestion.startTime, currentHookSuggestion.endTime, {
      textSuggestion: currentHookSuggestion.textSuggestion,
    });
    previewHookSegment(shouldLoopPreview, {
      startTime: currentHookSuggestion.startTime,
      endTime: currentHookSuggestion.endTime,
    });
  };

  const updateManualWatermarkRegion = (id, updates) => {
    setManualWatermarkRegions(prev =>
      prev.map(region =>
        region.id === id ? clampManualWatermarkRegion({ ...region, ...updates }) : region
      )
    );
  };

  const addManualWatermarkRegion = preset => {
    const nextRegion = clampManualWatermarkRegion({
      ...createManualWatermarkRegion(),
      seedTime: clampNumber(videoTime, 0, 36000, 0),
      ...(preset || {}),
    });
    setManualWatermarkRegions(prev => [...prev, nextRegion]);
    setActiveWatermarkRegionId(nextRegion.id);
  };

  const deleteManualWatermarkRegion = id => {
    setManualWatermarkRegions(prev => prev.filter(region => region.id !== id));
    setActiveWatermarkRegionId(currentId => (currentId === id ? null : currentId));
  };

  const handleUndo = () => {
    if (!undoStackRef.current.length) return;

    const currentSnapshot = cloneSnapshot(getEditorSnapshot());
    const currentSignature = serializeSnapshot(getHistoryRelevantSnapshot(currentSnapshot));
    const cutTransaction = cutHistoryTransactionRef.current;
    if (cutTransaction?.appliedSignature === currentSignature) {
      const baselineSignature = serializeSnapshot(
        getHistoryRelevantSnapshot(cutTransaction.baseline)
      );
      let baselineIndex = -1;
      for (let index = undoStackRef.current.length - 1; index >= 0; index -= 1) {
        const candidateSignature = serializeSnapshot(
          getHistoryRelevantSnapshot(undoStackRef.current[index])
        );
        if (candidateSignature === baselineSignature) {
          baselineIndex = index;
          break;
        }
      }
      if (baselineIndex >= 0) undoStackRef.current.splice(baselineIndex);
      redoStackRef.current.push(currentSnapshot);
      isRestoringHistoryRef.current = true;
      applyEditorSnapshot(cloneSnapshot(cutTransaction.baseline));
      cutHistoryTransactionRef.current = null;
      setStudioActionMessage("Undo restored the removed section and its synchronized media.");
      syncHistoryAvailability();
      return;
    }
    let previousSnapshot = null;
    while (undoStackRef.current.length && !previousSnapshot) {
      const candidate = undoStackRef.current.pop();
      const candidateSignature = serializeSnapshot(getHistoryRelevantSnapshot(candidate));
      if (candidateSignature !== currentSignature) previousSnapshot = candidate;
    }
    if (!previousSnapshot) {
      syncHistoryAvailability();
      return;
    }
    redoStackRef.current.push(currentSnapshot);
    isRestoringHistoryRef.current = true;
    applyEditorSnapshot(cloneSnapshot(previousSnapshot));
    setStudioActionMessage("Undo restored the previous edit, including uploaded media.");
    syncHistoryAvailability();
  };

  const handleRedo = () => {
    if (!redoStackRef.current.length) return;

    const currentSnapshot = cloneSnapshot(getEditorSnapshot());
    const currentSignature = serializeSnapshot(getHistoryRelevantSnapshot(currentSnapshot));
    let nextSnapshot = null;
    while (redoStackRef.current.length && !nextSnapshot) {
      const candidate = redoStackRef.current.pop();
      const candidateSignature = serializeSnapshot(getHistoryRelevantSnapshot(candidate));
      if (candidateSignature !== currentSignature) nextSnapshot = candidate;
    }
    if (!nextSnapshot) {
      syncHistoryAvailability();
      return;
    }
    undoStackRef.current.push(currentSnapshot);
    isRestoringHistoryRef.current = true;
    applyEditorSnapshot(cloneSnapshot(nextSnapshot));
    setStudioActionMessage("Redo restored the next edit, including uploaded media.");
    syncHistoryAvailability();
  };

  studioShortcutActionsRef.current.undo = handleUndo;
  studioShortcutActionsRef.current.redo = handleRedo;

  const addOverlayAsset = ({
    type,
    src,
    file = null,
    isLocal = false,
    width = 40,
    height = 30,
    bRollMode = null, // "fullscreen" | "pip" | "sideBySide" | null (legacy)
  }) => {
    if (!src) return;
    const currentVideoTime = videoRef.current ? videoRef.current.currentTime : 0;
    const rawRelativeStartTime = getPreviewTimelineTime(currentVideoTime);
    const relativeStartTime = resolveMagneticTimelineTime(rawRelativeStartTime).time;
    const pipPlacement = getCollisionSafePipPlacement({
      overlays,
      startTime: relativeStartTime,
      duration: 3,
      width,
      height,
    });
    const defaultPlacement =
      bRollMode === "fullscreen"
        ? { x: 50, y: 50, width: 100, height: 100 }
        : bRollMode === "sideBySide"
          ? { x: 75, y: 50, width: 50, height: 100 }
          : bRollMode === "pip"
            ? pipPlacement
            : { x: 50, y: 50, width, height };

    const newOverlay = {
      id: createSecureId("overlay"),
      type,
      src,
      file,
      isLocal,
      x: defaultPlacement.x,
      y: defaultPlacement.y,
      width: defaultPlacement.width,
      height: defaultPlacement.height,
      aspectRatioLocked: type === "video" || type === "image",
      aspectRatio: height ? width / height : 1,
      clipId: timeline[activeTimelineIndex]?.id || "main",
      // B-roll / cutaway fields
      startTime: bRollMode ? relativeStartTime : undefined,
      duration: bRollMode ? 3.0 : undefined,
      bRollMode: bRollMode || undefined,
      animation: bRollMode
        ? { enter: "fade", exit: "fade", enterDuration: 0.3, exitDuration: 0.3 }
        : undefined,
      opacity: bRollMode ? 1.0 : undefined,
      mediaFit: bRollMode || type === "image" ? "cover" : "contain",
      frameShape: bRollMode === "fullscreen" ? "edge" : "round",
      borderRadius: 28,
      shadow: type === "image" ? "soft" : "none",
      rotation: 0,
      coverMainVideo: bRollMode === "fullscreen",
      muteMainAudio: false,
      useOverlayAudio: false,
      mixAudio: false,
      overlayAudioVolume: 0.7,
      audioDucking: false,
      audioDuckingStrength: 0.35,
    };
    setOverlays(prev => [...prev, newOverlay]);
    setActiveOverlayId(newOverlay.id);
    return newOverlay;
  };

  const clampOverlayDimension = value => Math.max(10, Math.min(100, Number(value) || 10));
  const clampOverlayCoordinate = value => Math.max(0, Math.min(100, Number(value) || 0));

  const clampOverlayPlacement = overlay => {
    const width = clampOverlayDimension(overlay.width ?? 40);
    const height = clampOverlayDimension(overlay.height ?? 30);
    const halfWidth = width / 2;
    const halfHeight = height / 2;
    return {
      ...overlay,
      width,
      height,
      x: clampNumber(overlay.x, halfWidth, 100 - halfWidth, 50),
      y: clampNumber(overlay.y, halfHeight, 100 - halfHeight, 50),
    };
  };

  const getOverlayAspectRatio = overlay => {
    const storedRatio = Number(overlay.aspectRatio);
    if (Number.isFinite(storedRatio) && storedRatio > 0) return storedRatio;

    const width = clampOverlayDimension(overlay.width ?? 40);
    const height = clampOverlayDimension(overlay.height ?? 30);
    return width / height;
  };

  const allowTimelineTimingEdit = trackId => {
    if (!trackStates[trackId]?.locked) return true;
    setStudioActionMessage("Unlock this track before changing its timeline timing.");
    return false;
  };

  const handleOverlayTimelineMove = (id, newStartTime) => {
    const layer = overlays.find(item => item.id === id);
    if (!layer || !allowTimelineTimingEdit(layer.bRollMode || ['image', 'video'].includes(layer.type) ? 'broll' : 'graphics')) return;
    const ownedKeys = motionKeyframes.filter(key => key.targetId === id);
    const start = Number(layer.startTime || 0);
    const earliest = Math.min(start, ...ownedKeys.map(key => key.time));
    const latest = Math.max(start + Number(layer.duration || 0), ...ownedKeys.map(key => key.time));
    const delta = Math.max(-earliest, Math.min(newStartTime - start, Math.max(0, liveTimelineDuration - latest)));
    setPreviewFinishedFile(false);
    setOverlays(prev =>
      prev.map(overlay =>
        overlay.id === id
          ? { ...overlay, startTime: Math.round((start + delta) * 90000) / 90000 }
          : overlay
      )
    );
    setMotionKeyframes(current => current.map(key => key.targetId === id
      ? { ...key, time: Math.round((key.time + delta) * 90000) / 90000 } : key));
  };

  const handleOverlayTimelineTrim = (id, edge, value) => {
    const layer = overlays.find(item => item.id === id);
    if (!layer || !allowTimelineTimingEdit(layer.bRollMode || ['image', 'video'].includes(layer.type) ? 'broll' : 'graphics')) return;
    setPreviewFinishedFile(false);
    setOverlays(prev =>
      prev.map(overlay => {
        if (overlay.id !== id) return overlay;
        if (edge === "start") {
          const oldStart = Number(overlay.startTime || 0);
          const oldEnd = oldStart + Number(overlay.duration || 3);
          const newStart = Math.max(0, Math.min(value, oldEnd - 0.1));
          return { ...overlay, startTime: newStart, duration: oldEnd - newStart };
        }
        if (edge === "end") {
          const newDuration = Math.max(0.1, value - Number(overlay.startTime || 0));
          return { ...overlay, duration: newDuration };
        }
        return overlay;
      })
    );
  };

  const handleAdjustmentTimelineMove = (id, newStartTime) => {
    if (!allowTimelineTimingEdit("adjustment")) return;
    setAdjustmentLayers(current => current.map(layer => layer.id === id
      ? { ...layer, startTime: Math.max(0, Math.min(newStartTime, liveTimelineDuration - Number(layer.duration || 0.2))) }
      : layer));
  };

  const handleAdjustmentTimelineTrim = (id, edge, value) => {
    if (!allowTimelineTimingEdit("adjustment")) return;
    setAdjustmentLayers(current => current.map(layer => {
      if (layer.id !== id) return layer;
      const start = Number(layer.startTime || 0);
      const end = start + Number(layer.duration || 0.2);
      if (edge === "start") {
        const nextStart = Math.max(0, Math.min(Number(value), end - 0.2));
        return { ...layer, startTime: nextStart, duration: end - nextStart };
      }
      return { ...layer, duration: Math.max(0.2, Math.min(Number(value) - start, liveTimelineDuration - start)) };
    }));
  };

  const updateAdjustmentLayer = (id, field, value) => {
    setAdjustmentLayers(current => current.map(layer => {
      if (layer.id !== id) return layer;
      if (field === "startTime" || field === "duration") {
        const nextValue = Math.max(field === "duration" ? 0.2 : 0, Number(value) || 0);
        return { ...layer, [field]: nextValue };
      }
      return {
        ...layer,
        effects: {
          ...layer.effects,
          color: { ...layer.effects?.color, [field]: Number(value), preset: null },
        },
      };
    }));
  };

  const handleOverlayTimelineSlip = (id, newSourceStart) => {
    if (!allowTimelineTimingEdit("broll")) return;
    setOverlays(prev =>
      prev.map(overlay =>
        overlay.id === id
          ? { ...overlay, sourceStartTime: Math.max(0, newSourceStart) }
          : overlay
      )
    );
  };

  const handleMotionTimelineMove = (id, newStart) => {
    if (!allowTimelineTimingEdit("motion")) return;
    setPreviewFinishedFile(false);
    if (threeDScenes.some(scene => scene.id === id)) {
      setThreeDScenes(prev => prev.map(scene => {
        if (scene.id !== id) return scene;
        const startTime = Math.max(0, newStart);
        const delta = startTime - Number(scene.startTime || 0);
        return normalizeStudio3DScene({ ...scene, startTime,
          keyframes: (scene.keyframes || []).map(frame => ({ ...frame, time: frame.time + delta })) });
      }));
      return;
    }
    setMotionScenes(prev =>
      prev.map(scene =>
        scene.id === id
          ? { ...scene, startTime: Math.max(0, newStart) }
          : scene
      )
    );
  };

  const handleMotionTimelineTrim = (id, edge, value) => {
    if (!allowTimelineTimingEdit("motion")) return;
    setPreviewFinishedFile(false);
    if (threeDScenes.some(scene => scene.id === id)) {
      setThreeDScenes(prev => prev.map(scene => {
        if (scene.id !== id) return scene;
        const end = Number(scene.startTime) + Number(scene.duration);
        const startTime = edge === "start" ? Math.max(0, Math.min(value, end - 0.5)) : Number(scene.startTime);
        const duration = edge === "start" ? end - startTime : Math.max(0.5, value - startTime);
        return normalizeStudio3DScene({ ...scene, startTime, duration,
          keyframes: (scene.keyframes || []).filter(frame => frame.time >= startTime && frame.time <= startTime + duration) });
      }));
      return;
    }
    setMotionScenes(prev =>
      prev.map(scene => {
        if (scene.id !== id) return scene;
        if (edge === "start") {
          const oldStart = Number(scene.startTime || 0);
          const oldEnd = oldStart + Number(scene.duration || 4);
          const newStart = Math.max(0, Math.min(value, oldEnd - 0.5));
          return { ...scene, startTime: newStart, duration: Math.max(0.5, oldEnd - newStart) };
        }
        if (edge === "end") {
          const newDuration = Math.max(0.5, value - Number(scene.startTime || 0));
          return { ...scene, duration: newDuration };
        }
        return scene;
      })
    );
  };

  const uploadThreeDAsset = async (sceneId, file) => {
    if (!["image/png", "image/jpeg", "image/webp"].includes(file.type) || file.size <= 0 || file.size > 5 * 1024 * 1024) {
      toast.error("Use a PNG, JPEG or WebP image under 5 MB.");
      return;
    }
    const localUrl = URL.createObjectURL(file);
    setThreeDScenes(current => current.map(scene => scene.id === sceneId ? normalizeStudio3DScene({ ...scene, assetType: undefined, assetUrl: localUrl, assetName: file.name, assetStoragePath: "" }) : scene));
    try {
      const token = await getMediaAuthToken();
      if (!token) throw new Error("Sign in to save and render logo assets.");
      const uploaded = await uploadSourceFileViaBackend({ file, token, mediaType: "image", fileName: file.name });
      if (!uploaded?.url || !uploaded?.storagePath) throw new Error("Logo upload did not return a secure storage reference.");
      setThreeDScenes(current => current.map(scene => scene.id === sceneId && scene.assetUrl === localUrl ? normalizeStudio3DScene({ ...scene, assetUrl: uploaded.url, assetStoragePath: uploaded.storagePath }) : scene));
      URL.revokeObjectURL(localUrl);
    } catch (error) {
      toast.error(error.message || "Logo upload failed. Live preview remains available, but HQ render needs a saved asset.");
    }
  };

  const generateThreeDPreview = async scene => {
    clearTimeout(threeDPreviewPollRef.current);
    const revision = studio3DSceneRevision(scene);
    const sceneId = scene.id;
    setThreeDPreviewState({ sceneId, revision, status: "queued" });
    try {
      const token = await getMediaAuthToken();
      if (!token) throw new Error("Sign in to generate an HQ 3D preview.");
      const response = await fetch(API_ENDPOINTS.STUDIO_3D_PREVIEW, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        // The same authored scene/aspect recovers its accepted request after a
        // reload or lost response instead of launching duplicate Blender work.
        body: JSON.stringify({ scene: normalizeStudio3DScene(scene), aspect: reframeAspect,
          clientRequestId: `studio-3d-${revision}-${reframeAspect}`.replace(/[^A-Za-z0-9_-]/g, "_") }),
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok || !result.jobId) throw new Error(result.error || "3D preview could not be queued.");
      setThreeDScenes(current => current.map(item => item.id === sceneId && studio3DSceneRevision(item) === revision
        ? { ...item, hqPreviewJobId: result.jobId, hqPreviewRevision: revision } : item));
      const poll = async () => {
        try {
          const statusResponse = await fetch(API_ENDPOINTS.STUDIO_3D_PREVIEW_STATUS(result.jobId), { headers: { Authorization: `Bearer ${token}` } });
          const status = await statusResponse.json().catch(() => ({}));
          if (!statusResponse.ok) throw new Error(status.error || "Preview status is unavailable.");
          const currentScene = threeDScenesRef.current.find(item => item.id === sceneId);
          if (!currentScene || studio3DSceneRevision(currentScene) !== revision) {
            setThreeDPreviewState({ sceneId, status: "idle", message: "Scene changed; generate a new HQ preview." });
            return;
          }
          if (status.status === "completed" && status.url) {
            setThreeDScenes(current => current.map(item => item.id === sceneId ? { ...item, hqPreviewJobId: result.jobId, hqPreviewRevision: revision } : item));
            setThreeDPreviewState({ sceneId, revision, status: "completed", url: status.url, jobId: result.jobId, hash: status.hash, renderProfile: status.renderProfile });
            return;
          }
          if (status.status === "failed") throw new Error("The HQ 3D render failed. Retry after checking the scene asset.");
          setThreeDPreviewState({ sceneId, revision, status: status.status === "rendering" ? "rendering" : "queued", jobId: result.jobId, renderProfile: status.renderProfile });
          threeDPreviewPollRef.current = setTimeout(poll, 3000);
        } catch (error) {
          setThreeDPreviewState({ sceneId, revision, status: "failed", message: error.message });
        }
      };
      void poll();
    } catch (error) {
      setThreeDPreviewState({ sceneId, revision, status: "failed", message: error.message });
    }
  };

  const restoreThreeDPreview = async (scene, file) => {
    const sceneId=scene.id,revision=studio3DSceneRevision(scene);
    try {
      if(file.size>4096)throw new Error('Use a saved 3D preview receipt.');
      const receipt=JSON.parse(await file.text());
      if(receipt.format!=='autopromote-3d-preview-receipt'||receipt.version!==1||receipt.sceneId!==sceneId||
        !/^[A-Za-z0-9_-]{8,100}$/.test(receipt.jobId||'')||!/^[a-f0-9]{64}$/.test(receipt.sceneHash||''))throw new Error('Invalid 3D preview receipt.');
      const digest=await crypto.subtle.digest('SHA-256',new TextEncoder().encode(studio3DRenderIdentity(scene)));
      const expected=[...new Uint8Array(digest)].map(v=>v.toString(16).padStart(2,'0')).join('');
      if(receipt.sceneHash!==expected)throw new Error('This preview belongs to different scene settings.');
      const token=await getMediaAuthToken();if(!token)throw new Error('Sign in to reconnect a preview.');
      const response=await fetch(API_ENDPOINTS.STUDIO_3D_PREVIEW_STATUS(receipt.jobId),{headers:{Authorization:`Bearer ${token}`}});
      const status=await response.json().catch(()=>({}));
      if(!response.ok||status.status!=='completed'||status.hash!==expected||!status.url)throw new Error('An owned completed preview matching this scene is required.');
      const current=threeDScenesRef.current.find(item=>item.id===sceneId);
      if(!current||studio3DSceneRevision(current)!==revision)throw new Error('Scene changed while reconnecting its preview.');
      setThreeDScenes(items=>items.map(item=>item.id===sceneId?{...item,hqPreviewJobId:receipt.jobId,hqPreviewRevision:revision}:item));
      setThreeDPreviewState({sceneId,revision,status:'completed',jobId:receipt.jobId,url:status.url,hash:status.hash});
    }catch(error){setThreeDPreviewState({sceneId,revision,status:'failed',message:error.message||'Preview recovery failed.'});}
  };

  const rippleInsertOverlayIntoSequence = (overlayId) => {
    const overlay = overlays.find(item => item.id === overlayId);
    if (!overlay) return;

    const overlayStart = Number(overlay.startTime ?? overlay.start_time ?? 0);
    const overlayDuration = Number(overlay.duration || 3);
    const sourceStart = Number(overlay.sourceStartTime || 0);

    const cutHistoryBaseline = cloneSnapshot(getEditorSnapshot());
    pendingHistoryBaselineRef.current = cutHistoryBaseline;
    cutHistoryTransactionRef.current = {
      baseline: cutHistoryBaseline,
      appliedSignature: null,
      appliedTimelineSignature: null,
    };

    let accumulatedTime = 0;
    let targetIndex = -1;
    let localOffsetInClip = 0;

    for (let i = 0; i < timeline.length; i++) {
      const clip = timeline[i];
      const win = getTimelineClipWindow(clip);
      const clipDuration = Number(win.duration || 0);
      if (overlayStart >= accumulatedTime && overlayStart < accumulatedTime + clipDuration) {
        targetIndex = i;
        localOffsetInClip = overlayStart - accumulatedTime;
        break;
      }
      accumulatedTime += clipDuration;
    }

    if (targetIndex === -1) {
      targetIndex = Math.max(0, timeline.length - 1);
      const lastClip = timeline[targetIndex];
      const win = getTimelineClipWindow(lastClip);
      localOffsetInClip = Number(win?.duration || 0);
    }

    const targetClip = timeline[targetIndex];
    const targetWin = getTimelineClipWindow(targetClip);
    const targetClipStart = Number(targetWin?.start || 0);
    const targetClipDuration = Number(targetWin?.duration || 0);

    const newBrollSegment = {
      id: createSecureId("broll-angle"),
      name: overlay.bRollTitle || overlay.text || "Angle Cut",
      url: overlay.src,
      sourceClipId: overlay.id,
      duration: overlayDuration,
      startRequest: sourceStart,
      endRequest: sourceStart + overlayDuration,
      isLocal: overlay.isLocal || false,
      file: overlay.file || null,
      audioTrimOffsetStart: 0,
      audioTrimOffsetEnd: 0,
    };

    let nextTimeline;
    let nextActiveIndex;

    if (localOffsetInClip <= 0.1) {
      nextTimeline = [
        ...timeline.slice(0, targetIndex),
        newBrollSegment,
        ...timeline.slice(targetIndex),
      ];
      nextActiveIndex = targetIndex;
    } else if (localOffsetInClip >= targetClipDuration - 0.1) {
      nextTimeline = [
        ...timeline.slice(0, targetIndex + 1),
        newBrollSegment,
        ...timeline.slice(targetIndex + 1),
      ];
      nextActiveIndex = targetIndex + 1;
    } else {
      const clipLeft = {
        ...targetClip,
        id: createSecureId("split-left"),
        startRequest: targetClipStart,
        endRequest: targetClipStart + localOffsetInClip,
      };
      const clipRight = {
        ...targetClip,
        id: createSecureId("split-right"),
        startRequest: targetClipStart + localOffsetInClip,
        endRequest: targetClipStart + targetClipDuration,
      };
      nextTimeline = [
        ...timeline.slice(0, targetIndex),
        clipLeft,
        newBrollSegment,
        clipRight,
        ...timeline.slice(targetIndex + 1),
      ];
      nextActiveIndex = targetIndex + 1;
    }

    setOverlays(prev => prev.filter(o => o.id !== overlayId));
    setActiveOverlayId(null);
    setTimeline(nextTimeline);
    setActiveTimelineIndex(nextActiveIndex);
    setStudioActionMessage("B-roll ripple-inserted into story sequence! Main video paused at cut and resumes after.");
  };

  const switchDirectorAngle = (targetAngleIndex) => {
    if (targetAngleIndex === activeDirectorAngle) return;

    const currentTime = Number(previewTimelineTime || 0);
    const prevAngle = activeDirectorAngle;
    setActiveDirectorAngle(targetAngleIndex);

    const video = videoRef.current;
    const isPlaying = video && !video.paused;

    if (isPlaying) {
      setDirectorSessionCuts(prev => [
        ...prev,
        {
          id: createSecureId("dcut"),
          time: currentTime,
          fromAngle: prevAngle,
          toAngle: targetAngleIndex,
          label: directorAngles[targetAngleIndex]?.label || `Cam ${targetAngleIndex + 1}`,
        },
      ]);
      setStudioActionMessage(
        `🎬 LIVE CUT: ${directorAngles[targetAngleIndex]?.label || `Cam ${targetAngleIndex + 1}`} @ ${formatPreviewTimePrecise(currentTime)}`
      );
    } else {
      setStudioActionMessage(
        `Switched monitor to ${directorAngles[targetAngleIndex]?.label || `Cam ${targetAngleIndex + 1}`}`
      );
    }
  };

  const commitDirectorCutsToTimeline = () => {
    if (!directorSessionCuts.length) {
      setStudioActionMessage("No live cuts recorded yet. Hit Play and tap 1-4 to switch angles live.");
      return;
    }

    const cutHistoryBaseline = cloneSnapshot(getEditorSnapshot());
    pendingHistoryBaselineRef.current = cutHistoryBaseline;
    cutHistoryTransactionRef.current = {
      baseline: cutHistoryBaseline,
      appliedSignature: null,
      appliedTimelineSignature: null,
    };

    const cuts = [...directorSessionCuts].sort((a, b) => a.time - b.time);
    const timelineDuration = Math.max(1, getTimelineDuration());
    const newOverlays = [];
    const newFramingCuts = [];

    for (let i = 0; i < cuts.length; i++) {
      const cut = cuts[i];
      const nextTime = i + 1 < cuts.length ? cuts[i + 1].time : timelineDuration;
      const duration = Math.max(0.2, nextTime - cut.time);

      const angle = directorAngles[cut.toAngle];
      if (angle?.type === "video" && angle.src) {
          newOverlays.push({
            id: createSecureId("broll-angle"),
            type: "video",
            name: angle.label,
            src: angle.src,
            startTime: cut.time,
            duration,
            sourceStartTime: cut.time,
            bRollMode: "fullscreen",
            opacity: 1,
            useOverlayAudio: false,
            muteMainAudio: false,
            animation: { enter: "fade", exit: "fade", enterDuration: 0.15, exitDuration: 0.15 },
          });
      } else {
        // A virtual camera is a punch on the current programme, not a change
        // to Show Everyone/Solo framing. Record the zoom and the return to
        // the master camera so the exported cut matches the monitor.
        newFramingCuts.push({
          id: createSecureId("reframe-cut"),
          time: cut.time,
          mode: resolveReframeModeAtTime(reframeModeCuts, cut.time, smartCrop ? smartCropMode : "off"),
          zoom: Number(angle?.zoom || 1),
        });
      }
    }

    if (newOverlays.length) {
      setOverlays(prev => [...prev, ...newOverlays]);
    }
    if (newFramingCuts.length) {
      setReframeModeCuts(prev => [...prev, ...newFramingCuts]);
    }

    const totalCuts = newOverlays.length + newFramingCuts.length;
    setStudioActionMessage(`✓ Successfully baked ${totalCuts} camera cuts into story timeline!`);
    setDirectorSessionCuts([]);
    setActiveDirectorAngle(0);
    setDirectorModeActive(false);
  };

  const clearDirectorCuts = () => {
    setDirectorSessionCuts([]);
    setActiveDirectorAngle(0);
    setStudioActionMessage("Live director session cuts cleared.");
  };

  useEffect(() => {
    const videoOverlays = overlays.filter(o => o.type === "video" && o.src);
    setDirectorAngles([
      { id: "cam1", label: "Cam 1 (Master Angle)", type: "master", src: null, zoom: 1 },
      videoOverlays[0]
        ? { id: "cam2", label: videoOverlays[0].name || "Cam 2 (B-Roll Angle)", type: "video", src: videoOverlays[0].src, zoom: 1 }
        : { id: "cam2", label: "Cam 2 (Tight Punch 1.35x)", type: "punch", src: null, zoom: 1.35 },
      videoOverlays[1]
        ? { id: "cam3", label: videoOverlays[1].name || "Cam 3 (B-Roll Angle)", type: "video", src: videoOverlays[1].src, zoom: 1 }
        : { id: "cam3", label: "Cam 3 (Reaction 1.22x)", type: "punch", src: null, zoom: 1.22 },
      videoOverlays[2]
        ? { id: "cam4", label: videoOverlays[2].name || "Cam 4 (B-Roll Angle)", type: "video", src: videoOverlays[2].src, zoom: 1 }
        : { id: "cam4", label: "Cam 4 (Wide 1.1x)", type: "punch", src: null, zoom: 1.1 },
    ]);
  }, [overlays]);

  const updateOverlaySize = (id, dimension, delta) => {
    setOverlays(prev =>
      prev.map(overlay => {
        if (overlay.id !== id) return overlay;
        const currentWidth = clampOverlayDimension(overlay.width ?? 40);
        const currentHeight = clampOverlayDimension(overlay.height ?? 30);
        const nextValue = clampOverlayDimension(
          Number(overlay[dimension] ?? (dimension === "width" ? currentWidth : currentHeight)) +
            delta
        );

        if (overlay.aspectRatioLocked && (overlay.type === "video" || overlay.type === "image")) {
          const ratio = getOverlayAspectRatio(overlay);
          if (dimension === "width") {
            return clampOverlayPlacement({
              ...overlay,
              width: nextValue,
              height: clampOverlayDimension(nextValue / ratio),
            });
          }

          return clampOverlayPlacement({
            ...overlay,
            height: nextValue,
            width: clampOverlayDimension(nextValue * ratio),
          });
        }

        const nextWidth = dimension === "width" ? nextValue : currentWidth;
        const nextHeight = dimension === "height" ? nextValue : currentHeight;
        return clampOverlayPlacement({
          ...overlay,
          [dimension]: nextValue,
          aspectRatio: nextWidth / Math.max(nextHeight, 1),
        });
      })
    );
  };

  const toggleOverlayAspectRatioLock = id => {
    setOverlays(prev =>
      prev.map(overlay => {
        if (overlay.id !== id) return overlay;
        return {
          ...overlay,
          aspectRatioLocked: !overlay.aspectRatioLocked,
          aspectRatio: getOverlayAspectRatio(overlay),
        };
      })
    );
  };

  const compositionEntries = studioLayerEntries({ overlays, motionScenes, threeDScenes, compositionOrder });
  const compositionIds = compositionEntries.map(entry => entry.id);
  // Deletions reconcile membership with the previous owned order. Unknown IDs
  // in imported group records still fail validation rather than disappearing.
  const effectiveCompoundClips = removeStudioGroupMembers(compoundClips,
    (compositionOrder || []).filter(id => !compositionIds.includes(id)));
  const compositionGroups = studioGroupTree(effectiveCompoundClips, compositionIds);
  const editGroupClipTiming = (id, action, requested) => {
    const frame = (Number(previewSpeed) || 1) / (Number(exportSettings.fps) || 30);
    try {
      const next = editStudioGroupTiming({ compounds: effectiveCompoundClips, order: compositionIds,
        overlays, motionScenes, threeDScenes, motionKeyframes, trackStates, frameStep: frame },
        id, action, Math.round(requested / frame) * frame, liveTimelineDuration);
      setPreviewFinishedFile(false);
      setCompoundClips(next.compounds); setOverlays(next.overlays); setMotionScenes(next.motionScenes);
      setThreeDScenes(next.threeDScenes); setMotionKeyframes(next.motionKeyframes);
      setStudioActionMessage(action === 'move' ? 'Group moved with its child clips and keys.' : 'Group visibility trimmed; child edits and keys are preserved.');
    } catch (error) { setStudioActionMessage(error.message); }
  };
  const fitGroupClipTiming = id => {
    if (groupClipRows.find(group => group.id === id)?.locked) { setStudioActionMessage('Unlock the group and its child tracks first.'); return; }
    setCompoundClips(current => current.map(group => {
      if (group.id !== id) return group;
      const { timing, ...rest } = group; return rest;
    }));
  };
  const deleteGroupClip = id => {
    if (groupClipRows.find(group => group.id === id)?.locked) { setStudioActionMessage('Unlock the group and its child tracks first.'); return; }
    const leaves = new Set(compositionGroups.leaves(id));
    const next = removeStudioGroupMembers(effectiveCompoundClips, [...leaves]);
    const removed = new Set([...leaves, ...effectiveCompoundClips.filter(group => !next.some(item => item.id === group.id)).map(group => group.id)]);
    setCompoundClips(next); setOverlays(current => current.filter(item => !leaves.has(item.id)));
    setMotionScenes(current => current.filter(item => !leaves.has(item.id)));
    setThreeDScenes(current => current.filter(item => !leaves.has(item.id)));
    setMotionKeyframes(current => current.filter(key => !removed.has(key.targetId)));
    setCompositionOrder(compositionIds.filter(identity => !leaves.has(identity)));
    setSelectedGroupId(null); setStudioActionMessage('Group and its child layers deleted. Undo restores the complete group.');
  };
  const compositionRoots = compositionGroups.roots.map(id => compositionGroups.byId.has(id)
    ? { id, kind: "group", label: compositionGroups.byId.get(id).name }
    : compositionEntries.find(entry => entry.id === id));
  const compositionRank = new Map(compositionEntries.map((entry, index) => [entry.id, 100 + index]));
  const applyCompositionOrder = ids => {
    setCompositionOrder(ids);
    setOverlays(current => orderStudioCollection(current, ids));
    setMotionScenes(current => orderStudioCollection(current, ids));
    setThreeDScenes(current => orderStudioCollection(current, ids));
  };
  const moveCompositionLayer = (id, direction) => {
    if (!compositionGroups.groups.length) { applyCompositionOrder(moveStudioLayer(compositionEntries, id, direction)); return; }
    const next = moveStudioGroupNode(effectiveCompoundClips, compositionIds, id, direction);
    setCompoundClips(next.compounds); applyCompositionOrder(next.order);
  };
  const moveOverlay = (id, direction) => {
    if (Array.isArray(compositionOrder)) { moveCompositionLayer(id, direction); return; }
    setOverlays(prev => {
      const currentIndex = prev.findIndex(overlay => overlay.id === id);
      if (currentIndex === -1) return prev;

      const reordered = [...prev];
      const [overlay] = reordered.splice(currentIndex, 1);
      let nextIndex = currentIndex;

      if (direction === "forward") nextIndex = Math.min(reordered.length, currentIndex + 1);
      if (direction === "backward") nextIndex = Math.max(0, currentIndex - 1);
      if (direction === "front") nextIndex = reordered.length;
      if (direction === "back") nextIndex = 0;

      reordered.splice(nextIndex, 0, overlay);
      return reordered;
    });
  };

  const moveOverlayToIndex = (id, nextIndex) => {
    if (compositionGroups.groups.length) {
      setStudioActionMessage("Reorder grouped layers in Composite."); return;
    }
    if (Array.isArray(compositionOrder)) {
      const ordinary = compositionEntries.filter(entry => entry.kind === "overlay").map(entry => entry.id);
      const index = ordinary.indexOf(id);
      if (index < 0) return;
      ordinary.splice(index, 1); ordinary.splice(Math.max(0, Math.min(ordinary.length, nextIndex)), 0, id);
      let cursor = 0;
      applyCompositionOrder(compositionEntries.map(entry => entry.kind === "overlay" ? ordinary[cursor++] : entry.id));
      return;
    }
    setOverlays(prev => {
      const currentIndex = prev.findIndex(overlay => overlay.id === id);
      if (currentIndex === -1) return prev;

      const boundedIndex = Math.max(0, Math.min(prev.length - 1, nextIndex));
      if (currentIndex === boundedIndex) return prev;

      const reordered = [...prev];
      const [overlay] = reordered.splice(currentIndex, 1);
      reordered.splice(boundedIndex, 0, overlay);
      return reordered;
    });
  };

  const moveTimelineClip = (clipId, direction) => {
    setTimeline(prev => {
      const currentIndex = prev.findIndex(clip => clip.id === clipId);
      if (currentIndex === -1) return prev;

      const reordered = [...prev];
      const [clip] = reordered.splice(currentIndex, 1);
      let nextIndex = currentIndex;

      if (direction === "forward") nextIndex = Math.min(reordered.length, currentIndex + 1);
      if (direction === "backward") nextIndex = Math.max(0, currentIndex - 1);
      if (direction === "front") nextIndex = reordered.length;
      if (direction === "back") nextIndex = 0;

      reordered.splice(nextIndex, 0, clip);

      setActiveTimelineIndex(prevActiveIndex => {
        const activeClipId = prev[prevActiveIndex]?.id;
        const resolvedIndex = reordered.findIndex(item => item.id === activeClipId);
        return resolvedIndex >= 0 ? resolvedIndex : 0;
      });

      return reordered;
    });
  };

  const moveTimelineClipToIndex = (clipId, nextIndex) => {
    setTimeline(prev => {
      const currentIndex = prev.findIndex(clip => clip.id === clipId);
      if (currentIndex === -1) return prev;

      const boundedIndex = Math.max(0, Math.min(prev.length - 1, nextIndex));
      if (boundedIndex === currentIndex) return prev;

      const reordered = [...prev];
      const [clip] = reordered.splice(currentIndex, 1);
      reordered.splice(boundedIndex, 0, clip);

      setActiveTimelineIndex(prevActiveIndex => {
        const activeClipId = prev[prevActiveIndex]?.id;
        const resolvedIndex = reordered.findIndex(item => item.id === activeClipId);
        return resolvedIndex >= 0 ? resolvedIndex : 0;
      });

      return reordered;
    });
  };

  const reorderDetectedClips = updater => {
    setOrderedClips(prev => {
      const nextClips = updater(prev);
      setSelectedClip(prevSelected => {
        const selectedId = prevSelected?.id;
        if (!selectedId) return nextClips[0] || null;
        return nextClips.find(clip => clip.id === selectedId) || nextClips[0] || null;
      });
      return nextClips;
    });
  };

  const moveDetectedClip = (clipId, direction) => {
    reorderDetectedClips(prev => {
      const currentIndex = prev.findIndex(clip => clip.id === clipId);
      if (currentIndex === -1) return prev;

      const reordered = [...prev];
      const [clip] = reordered.splice(currentIndex, 1);
      let nextIndex = currentIndex;

      if (direction === "forward") nextIndex = Math.min(reordered.length, currentIndex + 1);
      if (direction === "backward") nextIndex = Math.max(0, currentIndex - 1);
      if (direction === "front") nextIndex = reordered.length;
      if (direction === "back") nextIndex = 0;

      reordered.splice(nextIndex, 0, clip);
      return reordered;
    });
  };

  const moveDetectedClipToIndex = (clipId, nextIndex) => {
    reorderDetectedClips(prev => {
      const currentIndex = prev.findIndex(clip => clip.id === clipId);
      if (currentIndex === -1) return prev;

      const boundedIndex = Math.max(0, Math.min(prev.length - 1, nextIndex));
      if (currentIndex === boundedIndex) return prev;

      const reordered = [...prev];
      const [clip] = reordered.splice(currentIndex, 1);
      reordered.splice(boundedIndex, 0, clip);
      return reordered;
    });
  };

  const safePlayMediaElement = mediaElement => {
    if (!mediaElement || typeof mediaElement.play !== "function") return Promise.resolve(false);
    // Source readiness can fire while the transport is resuming its audio
    // context. Keep the picture stationary until the same request is ready.
    if (mediaElement === videoRef.current && previewPlaybackPreparingRef.current !== null) {
      return Promise.resolve(false);
    }
    const rawSource =
      (typeof mediaElement.currentSrc === "string" && mediaElement.currentSrc) ||
      (typeof mediaElement.src === "string" && mediaElement.src) ||
      "";
    if (!rawSource.trim()) return Promise.resolve(false);
    if (
      typeof HTMLMediaElement !== "undefined" &&
      mediaElement.networkState === HTMLMediaElement.NETWORK_NO_SOURCE
    ) {
      return Promise.resolve(false);
    }

    // Tool preview actions also request playback through this helper. Mark
    // each new programme request explicitly; a subsequent Pause still wins
    // over its delayed native play event or a pending source-load callback.
    if (mediaElement === videoRef.current) {
      previewPlaybackIntentRef.current = true;
      setIsPreviewPaused(false);
    }

    return playMediaSafely(mediaElement, {
      onUnexpectedError: error => {
        if (error?.name === "NotSupportedError") return;
        console.log("Auto-play prevented", error);
      },
    });
  };

  const jumpToSourceTime = targetTime => {
    const video = videoRef.current;
    if (!video) return;

    const boundedTime = Math.max(0, Number(targetTime) || 0);
    programmeSeekIntentRef.current = { version: programmeSeekIntentRef.current.version + 1,
      sourceId: currentTimelineClip?.sourceClipId || currentTimelineClip?.id, time: boundedTime };
    video.currentTime = boundedTime;
    setVideoTime(boundedTime);
  };

  const jumpToOutputTimelineTime = outputTime => {
    if (renderedOutputUrl && videoRef.current) {
      const video = videoRef.current;
      if (video.paused) {
        // An ended result can retain the preceding Play intent. A paused
        // scrub must not let its new canplay event restart the result.
        previewPlaybackIntentRef.current = false;
        setIsPreviewPaused(true);
      }
      const programmeTick = Math.min(previewOutputTimeMap.at(-1)?.programmeRange.endTick || 0,
        secondsToTicks(Math.max(0, Number(outputTime || 0))));
      const time = ticksToSeconds(mapProgrammeTickToOutputTick(previewOutputTimeMap, programmeTick) || 0);
      video.currentTime = time;
      setVideoTime(time);
      return;
    }
    const position = resolveOutputTimelinePosition(outputTime);
    if (!position) return;
    programmeSeekIntentRef.current = { version: programmeSeekIntentRef.current.version + 1,
      sourceId: position.clip?.sourceClipId || position.clip?.id, time: position.sourceTime };

    hookPreviewSequenceRef.current = { active: false };
    setHookPreviewLoop(false);
    setTrimPreviewLoop(false);
    const video = videoRef.current;
    pendingTimelineSeekRef.current =
      position.index === activeTimelineIndex
        ? null
        : {
            index: position.index,
            sourceTime: position.sourceTime,
            play: Boolean(video && !video.paused && previewPlaybackIntentRef.current),
          };
    setActiveTimelineIndex(position.index);
    if (!video) return;
    // A cross-clip seek is applied by the source-switch effect. Mutating the
    // source here as well lets the old clip's media events rewrite the clock.
    if (position.index !== activeTimelineIndex) {
      setVideoTime(position.sourceTime);
      return;
    }
    if (position.clip?.url && video.src !== getSafeMediaSource(position.clip.url)) {
      applySafeMediaSource(video, position.clip.url);
    }
    try {
      video.currentTime = position.sourceTime;
      setVideoTime(position.sourceTime);
    } catch (error) {
      console.log("Output timeline seek skipped", error);
    }
  };

  const seekLiveEditTimeline = event => {
    const trackBounds = event.currentTarget.getBoundingClientRect();
    const duration = Math.max(0, getTimelineDuration());
    if (!duration || !trackBounds.width) return;

    const progress = clampNumber((event.clientX - trackBounds.left) / trackBounds.width, 0, 1, 0);
    const targetTime = progress * duration;
    jumpToOutputTimelineTime(targetTime);
    setStudioActionMessage(
      `Preview moved to ${formatPreviewTimePrecise(progress * duration)}. Timeline and After are on the same frame.`
    );
  };

  const seekLiveEditTimelineItem = (outputTime, toolId, overlayId = null) => {
    const duration = Math.max(0, getTimelineDuration());
    const boundedOutputTime = clampNumber(outputTime, 0, duration || Number(outputTime || 0), 0);

    if (overlayId) setActiveOverlayId(overlayId);
    if (toolId) {
      setStudioInspectorTab(toolId);
      setActiveCreativeTool(toolId);
    }

    jumpToOutputTimelineTime(boundedOutputTime);
    setStudioActionMessage(
      `${toolId === "broll" ? "B-roll" : toolId === "hook" ? "Hook" : "Edit"} selected at ${formatPreviewTimePrecise(boundedOutputTime)} in the After preview.`
    );
  };

  const selectLiveTimelineOverlay = overlayId => {
    const selectedOverlay = overlays.find(overlay => String(overlay.id) === String(overlayId));
    if (!selectedOverlay) return;

    setSoloPreviewOverlayId(null);
    setActiveOverlayId(selectedOverlay.id);
    setStudioInspectorTab("broll");
    setActiveCreativeTool("broll");
    setComparisonMode("after");
    jumpToOutputTimelineTime(Number(selectedOverlay.startTime || 0));
    setStudioActionMessage(
      `${selectedOverlay.type === "image" ? "Image" : "B-roll"} selected at ${formatPreviewTimePrecise(
        selectedOverlay.startTime
      )}. Edit controls are open in After.`
    );
  };

  const focusClipInEditor = (clip, options = {}) => {
    if (!clip) return;

    setSelectedClip(clip);
    setActiveTimelineIndex(0);
    setTrimPreviewLoop(false);
    setHookPreviewLoop(false);

    const initialForClip = resolveInitialCaptionSegments(clip, clip?.duration);
    if (initialForClip.length > 0) {
      setCaptionSegments(initialForClip);
      if (clip.autoCaptions !== undefined) {
        setAutoCaptions(Boolean(clip.autoCaptions));
      } else {
        setAutoCaptions(true);
      }
    }

    const boundaryTime =
      options.boundary === "end" ? Number(clip.end || clip.start || 0) : Number(clip.start || 0);

    const video = videoRef.current;
    if (video) {
      if (!applySafeMediaSource(video, videoUrl)) return;
      video.currentTime = boundaryTime;
      setVideoTime(boundaryTime);
      if (options.play) {
        previewPlaybackIntentRef.current = true;
        safePlayMediaElement(video);
      }
    }
  };

  const applyGuidedHookToClip = (clip, options = {}) => {
    if (!clip) return;

    const clipWindowDuration = Math.max(0, getClipDurationSeconds(clip));
    const suggestion = buildFallbackHookRange(clip, clipWindowDuration);
    const guidance = clipGuidanceById.get(clip.id);

    setAddHook(true);
    applyHookTemplate(suggestion.templateKey);
    setHookSuggestedRange(suggestion);
    setHookAnalysisStatus("ready");
    setHookAnalysisMessage("Previewing this clip with a suggested hook treatment.");
    setHookSegmentRange(suggestion.startTime, suggestion.endTime, {
      textSuggestion: guidance?.hookText || suggestion.textSuggestion,
      preview: false,
    });

    if (options.preview !== false) {
      previewHookSegment(false, {
        startTime: suggestion.startTime,
        endTime: suggestion.endTime,
      });
    }
  };

  const applyClipImprovements = clip => {
    if (!clip) return;

    const guidance = clipGuidanceById.get(clip.id);
    const clipStart = Number(clip.start || 0);
    const clipEnd = Number(clip.end || clipStart);
    const clipDuration = Math.max(0, clipEnd - clipStart);
    const shouldCutOpening =
      (!guidance?.signals.speech || !guidance?.signals.hook) && clipDuration > 3.2;
    const improvedStart = shouldCutOpening ? Math.min(clipEnd - 0.5, clipStart + 2) : clipStart;
    const improvedEnd =
      guidance?.signals.idealLength || clipDuration <= 25
        ? clipEnd
        : Math.min(clipEnd, improvedStart + 22);

    setTimeline(prev =>
      prev.map((item, index) =>
        index === 0 && item.id === "main"
          ? {
              ...item,
              startRequest: improvedStart,
              endRequest: Math.max(improvedStart + 0.5, improvedEnd),
            }
          : item
      )
    );

    setAutoCaptions(true);
    if (!guidance?.signals.subject) {
      setSmartCrop(true);
    }
    applyGuidedHookToClip(clip, { preview: true });
    onStatusChange?.("Applied guided improvements to strengthen the selected clip.");
  };

  const applyClipVariant = (clip, variant) => {
    if (!clip || !variant) return;

    setTimeline(prev =>
      prev.map((item, index) =>
        index === 0 && item.id === "main"
          ? {
              ...item,
              startRequest: variant.start,
              endRequest: variant.end,
            }
          : item
      )
    );

    setAutoCaptions(true);
    if (variant.id !== "authority") {
      setSmartCrop(true);
    }

    setAddHook(true);
    applyHookTemplate(variant.templateKey || "blur_reveal");
    setHookAnalysisStatus("ready");
    setHookAnalysisMessage(`Loaded ${variant.label.toLowerCase()} for the selected moment.`);
    setHookSegmentRange(
      Math.max(0, Number(variant.start || 0) - Number(clip.start || 0)),
      Math.max(
        Math.max(0.5, Number(variant.end || 0) - Number(clip.start || 0)),
        Math.max(0.6, Number(variant.end || 0) - Number(variant.start || 0))
      ),
      {
        textSuggestion: variant.hookText || clipGuidanceById.get(clip.id)?.hookText,
        preview: false,
      }
    );

    focusClipInEditor(
      {
        ...clip,
        start: variant.start,
        end: variant.end,
      },
      { boundary: "start", play: false }
    );

    onStatusChange?.(`Applied ${variant.label} to give this moment a sharper editorial angle.`);
  };

  const handleClipAction = (clip, action) => {
    if (!clip || !action) return;

    if (action.type === "use") {
      focusClipInEditor(clip, { boundary: "start", play: false });
      return;
    }

    if (action.type === "jump") {
      focusClipInEditor(clip, { boundary: action.boundary, play: false });
      return;
    }

    if (selectedClip?.id !== clip.id) {
      pendingClipActionRef.current = { clipId: clip.id, ...action };
      focusClipInEditor(clip, { boundary: "start", play: false });
      return;
    }

    if (action.type === "apply-hook") {
      applyGuidedHookToClip(clip, { preview: true });
      return;
    }

    if (action.type === "improve") {
      applyClipImprovements(clip);
      return;
    }

    if (action.type === "apply-variant") {
      applyClipVariant(clip, action.variant);
      return;
    }

    if (action.type === "export") {
      pendingClipActionRef.current = null;
      void handleExportRender(action.destination);
    }
  };

  const updateOverlayPosition = (id, axis, delta) => {
    setOverlays(prev =>
      prev.map(overlay =>
        overlay.id === id
          ? clampOverlayPlacement({
              ...overlay,
              [axis]: clampOverlayCoordinate(Number(overlay[axis] ?? 50) + delta),
            })
          : overlay
      )
    );
  };

  const centerOverlay = id => {
    setOverlays(prev =>
      prev.map(overlay => (overlay.id === id ? { ...overlay, x: 50, y: 50 } : overlay))
    );
  };

  const duplicateOverlay = id => {
    const overlay = overlays.find(item => item.id === id);
    if (!overlay) return;
    const duplicateId = createSecureId("overlay");
    const duplicate = {
      ...overlay,
      id: duplicateId,
      name: overlay.name ? `${overlay.name} copy` : overlay.name,
      x: clampOverlayCoordinate(Number(overlay.x ?? 50) + 4),
      y: clampOverlayCoordinate(Number(overlay.y ?? 50) + 4),
    };
    setOverlays(current => [...current, duplicate]);
    setMotionKeyframes(current => [
      ...current,
      ...current
        .filter(keyframe => String(keyframe.targetId) === String(id))
        .map(keyframe => ({
          ...keyframe,
          id: createSecureId("motion-key"),
          targetId: duplicateId,
        })),
    ]);
    setActiveOverlayId(duplicate.id);
    setMotionTargetId(duplicate.id);
  };

  const activeOverlay = overlays.find(overlay => overlay.id === activeOverlayId) || null;
  const activeOverlayHasTiming = Boolean(
    activeOverlay && activeOverlay.startTime !== undefined && activeOverlay.duration !== undefined
  );
  const activeOverlayIsVideoBRoll = Boolean(
    activeOverlayHasTiming && activeOverlay?.type === "video"
  );
  const activeOverlayIsTimedMedia = Boolean(
    activeOverlayHasTiming && ["video", "image"].includes(activeOverlay?.type)
  );
  const activeOverlayIsFullscreenBRoll = Boolean(
    activeOverlayIsTimedMedia && activeOverlay?.bRollMode === "fullscreen"
  );
  const activeOverlayStartTime = activeOverlayHasTiming ? Number(activeOverlay.startTime || 0) : 0;
  const activeOverlayDuration = activeOverlayHasTiming ? Number(activeOverlay.duration || 0) : 0;
  const activeOverlayEndTime = activeOverlayStartTime + activeOverlayDuration;
  const activeOverlayVisibleDuration = activeOverlayHasTiming
    ? getOverlayVisibleDuration(activeOverlay)
    : 0;
  const activeOverlayAvailableSourceDuration = activeOverlayIsVideoBRoll
    ? getOverlayAvailableSourceDuration(activeOverlay)
    : Number.POSITIVE_INFINITY;
  const activeOverlaySafeSrc = activeOverlay ? getSafeMediaSource(activeOverlay.src) : "";
  const activeOverlayDisplayName = activeOverlay
    ? activeOverlay.file?.name ||
      activeOverlay.name ||
      (activeOverlay.type === "text"
        ? activeOverlay.text || "Text layer"
        : `${activeOverlay.type === "image" ? "Image" : "Video"} layer`)
    : "";

  const getTimelineClipWindow = clip => {
    if (!clip) return { start: 0, end: 0, duration: 0 };
    const isPrimaryClip = clip.id === "main" && selectedClip;
    const start =
      clip.startRequest !== null && clip.startRequest !== undefined
        ? clip.startRequest
        : isPrimaryClip
          ? selectedClip.start
          : 0;
    const end =
      clip.endRequest !== null && clip.endRequest !== undefined
        ? clip.endRequest
        : isPrimaryClip
          ? selectedClip.end
          : clip.duration || 0;
    return {
      start,
      end,
      duration: Math.max(0, end - start),
    };
  };

  const getPreviewTimelineTime = sourceTime => {
    if (renderedOutputUrl) {
      const outputTick = Math.min(previewOutputTimeMap.at(-1)?.outputRange.endTick || 0,
        secondsToTicks(Math.max(0, Number(sourceTime || 0))));
      return ticksToSeconds(mapOutputTickToProgrammeTick(previewOutputTimeMap, outputTick) || 0);
    }
    let elapsed = 0;
    for (let index = 0; index < activeTimelineIndex; index += 1) {
      elapsed += Math.max(0, Number(getTimelineClipWindow(timeline[index]).duration || 0));
    }

    const currentClip = timeline[activeTimelineIndex];
    const currentWindow = getTimelineClipWindow(currentClip);
    const localStart = Number(currentWindow.start || 0);
    const localDuration = Math.max(0, Number(currentWindow.duration || 0));
    const localTime = Math.max(0, Number(sourceTime || 0) - localStart);

    return elapsed + Math.min(localTime, localDuration || localTime);
  };

  const getTimelineDuration = () =>
    timeline.reduce(
      (total, clip) => total + Math.max(0, Number(getTimelineClipWindow(clip).duration || 0)),
      0
    );

  const previewOutputTimeMap = useMemo(() => buildStudioOutputTimeMap({
    durationTick: secondsToTicks(Math.max(.1, getTimelineDuration())),
    fallbackSpeed: previewSpeed,
    programmeSpeedKeys: speedKeyframes.filter(key => key.property === "speed").map(key => ({
      atProgrammeTick: secondsToTicks(Number(key.time || 0)),
      rate: Number(key.value ?? key.rate ?? 1), easing: key.easing || "linear",
    })),
  }), [timeline, previewSpeed, speedKeyframes]);
  soundEffectGainTimeMapRef.current = previewOutputTimeMap;
  // The renderer lays the music bed over the finished output clock at 1x.
  // Picture ramps change its seek position/fade window, not its tempo or pitch.
  const getPreviewMusicTime = programmeTime => ticksToSeconds(mapProgrammeTickToOutputTick(
    previewOutputTimeMap, Math.min(previewOutputTimeMap.at(-1)?.programmeRange.endTick || 0,
      secondsToTicks(Math.max(0, Number(programmeTime || 0))))) || 0);

  const getSoundEffectProgrammeTime = sourceTime => studioCueProgrammeTime({
    sourceProgrammeTime: getPreviewTimelineTime(sourceTime),
    musicState: musicPreviewSourceStateRef.current,
    contextTime: musicPreviewAudioContextRef.current?.currentTime,
    trimStart: Number(musicTrack?.trimStart || 0), outputTimeMap: soundEffectGainTimeMapRef.current,
    musicClockActive: Boolean(musicPreviewSourceRef.current && !isolatedMusicPreviewIntentRef.current &&
      musicPreviewAudioContextRef.current === soundEffectAudioContextRef.current),
  });

  const getTimelineOffsetForIndex = index =>
    timeline
      .slice(0, Math.max(0, Number(index) || 0))
      .reduce(
        (total, clip) => total + Math.max(0, Number(getTimelineClipWindow(clip).duration || 0)),
        0
      );

  const resolveOutputTimelinePosition = outputTime => {
    const totalDuration = getTimelineDuration();
    const boundedTime = clampNumber(outputTime, 0, totalDuration || Number(outputTime || 0), 0);
    let elapsed = 0;

    for (let index = 0; index < timeline.length; index += 1) {
      const clip = timeline[index];
      const clipWindow = getTimelineClipWindow(clip);
      const clipDuration = Math.max(0, Number(clipWindow.duration || 0));
      const isLastClip = index === timeline.length - 1;
      if (boundedTime < elapsed + clipDuration || isLastClip) {
        const localTime = clampNumber(boundedTime - elapsed, 0, clipDuration, 0);
        return {
          index,
          clip,
          clipWindow,
          outputTime: boundedTime,
          sourceTime: Number(clipWindow.start || 0) + localTime,
          localTime,
        };
      }
      elapsed += clipDuration;
    }

    return null;
  };

  const normalizeBackgroundAudioForExport = audioTrack => {
    if (!audioTrack?.url || audioTrack.enabled === false) return null;

    return {
      url: audioTrack.url,
      trim_start: clampAudioControl(audioTrack.trimStart, 0, audioTrack.duration || 36000, 0),
      volume: clampAudioControl(audioTrack.volume, 0, 1.5, 0.7),
      mode: normalizeAudioMode(audioTrack.mode),
      ducking_strength: clampAudioControl(audioTrack.duckingStrength, 0.15, 0.95, 0.45),
      enabled: true,
    };
  };

  // ── Music track helpers ──
  const setMusicTrackField = (key, value) => {
    setMusicTrack(prev => (prev ? { ...prev, [key]: value } : null));
  };

  const handleMusicFileUpload = event => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("audio/") && !file.name.match(/\.(mp3|wav|ogg|m4a|aac)$/i)) {
      toast.error("Please select an audio file (MP3, WAV, OGG, M4A, AAC).");
      return;
    }
    const url = URL.createObjectURL(file);
    setMusicTrack({
      url,
      file,
      name: file.name.replace(/\.[^.]+$/, ""),
      trimStart: 0,
      trimEnd: null,
      fadeIn: 0.5,
      fadeOut: 0.5,
      loop: true,
      volume: 0.25,
      ducking: true,
      duckingStrength: 0.4,
      duckingMode: "speech",
    });
    setMusicSelection("custom");
    backgroundSoundPreviewSuppressedRef.current = false;
    setAddMusic(true);
    setMusicSearchMode(false);
    releaseMusicPreviewObjectUrl();
    setMusicPreviewUrl(url);
    setPreviewMuted(false);
    setMusicPreviewStatus("ready");
    setMusicPreviewStatusMessage(`Background sound ready: ${file.name}`);
    setMusicPreviewNeedsGesture(false);
    setIsBackgroundSoundPreviewing(false);
    setStudioInspectorTab("sound");
    setStudioActionMessage(
      "Background sound is ready with speech-aware ducking, so dialogue stays clear."
    );
    toast.success(`Added "${file.name}" as background music`);
    event.target.value = null;
  };

  const selectPresetMusic = preset => {
    setMusicTrack({
      url: null,
      file: null,
      name: preset.name,
      trimStart: 0,
      trimEnd: null,
      fadeIn: 0.5,
      fadeOut: 0.5,
      loop: true,
      volume: 0.25,
      ducking: true,
      duckingStrength: 0.4,
      duckingMode: "speech",
    });
    setMusicSelection(preset.file);
    backgroundSoundPreviewSuppressedRef.current = false;
    setAddMusic(true);
    setMusicSearchMode(false);
    setMusicPreviewUrl("");
    setMusicPreviewStatus("idle");
    setMusicLibraryOpen(false);
    setStudioInspectorTab("sound");
    setStudioActionMessage(
      `${preset.name} is ready with speech-aware ducking, so dialogue stays clear.`
    );
    toast.success(`Selected "${preset.name}"`);
  };

  const removeMusic = () => {
    setMusicTrack(null);
    setAddMusic(false);
    setMusicSelection("upbeat_pop.mp3");
    setMusicSearchMode(false);
    setMusicPreviewUrl("");
    setMusicPreviewStatus("idle");
    setMusicPreviewStatusMessage("");
    setMusicPreviewNeedsGesture(false);
    setIsBackgroundSoundPreviewing(false);
    stopMusicPreviewBufferPlayback();
    if (musicPreviewRef.current) {
      musicPreviewRef.current.pause();
      musicPreviewRef.current.removeAttribute("src");
      musicPreviewRef.current.load();
    }
    toast("Background music removed");
  };

  const getSoundEffectDuration = effect =>
    clampAudioControl(effect?.duration, 0.05, 15, effect?.builtIn ? 0.6 : 2);

  const stopSynthesizedSoundEffects = stopAt => {
    soundEffectStartVersionRef.current += 1;
    const deferred = Number.isFinite(stopAt) && stopAt > soundEffectAudioContextRef.current?.currentTime;
    soundEffectNodesRef.current.forEach(node => {
      try {
        node.stop?.(deferred ? stopAt : undefined);
        if (!deferred) node.disconnect?.();
      } catch (error) {
        // A scheduled Web Audio node can finish before the UI asks it to stop.
      }
    });
    if (!deferred) soundEffectNodesRef.current.clear();
    soundEffectTimelineGainsRef.current.clear();
  };

  const stopSoundEffectPlayback = (clearPreviewState = true) => {
    if (soundEffectPreviewTimeoutRef.current) {
      window.clearTimeout(soundEffectPreviewTimeoutRef.current);
      soundEffectPreviewTimeoutRef.current = null;
    }
    soundEffectAudioRefsRef.current.forEach(audio => {
      if (!audio) return;
      audio.pause();
    });
    stopSynthesizedSoundEffects();
    triggeredSoundEffectsRef.current.clear();
    if (clearPreviewState) setPreviewingSoundEffectId(null);
  };

  const ensureSoundEffectAudioContext = () => {
    if (soundEffectAudioContextRef.current) return soundEffectAudioContextRef.current;
    const AudioContextCtor = window.AudioContext || window.webkitAudioContext;
    if (!AudioContextCtor) return null;
    // Decoded music and synthesized cues share one audio-thread clock. Separate
    // contexts have independent output latency, even at the same sample rate.
    soundEffectAudioContextRef.current = musicPreviewAudioContextRef.current || new AudioContextCtor();
    return soundEffectAudioContextRef.current;
  };

  const playBuiltInSoundEffect = async (effect, elapsed = 0, playbackRate = 1, timelinePlayback = false, decodedCue = null) => {
    const startVersion = soundEffectStartVersionRef.current;
    const projectId = activeProjectIdRef.current;
    const seekVersion = programmeSeekIntentRef.current.version;
    const video = videoRef.current;
    const audioContext = ensureSoundEffectAudioContext();
    if (!audioContext) {
      setStudioActionMessage("This browser cannot preview synthesized sound effects.");
      return false;
    }
    if (audioContext.state === "suspended") await audioContext.resume();
    if (startVersion !== soundEffectStartVersionRef.current || projectId !== activeProjectIdRef.current ||
        seekVersion !== programmeSeekIntentRef.current.version) return false;

    let automationGain = 1;
    if (timelinePlayback) {
      const clip = timeline[activeTimelineIndex];
      const window = getTimelineClipWindow(clip);
      if (video !== videoRef.current || video?.paused || video?.seeking || video?.readyState < 2 ||
          !isStudioAudioTrackAudible(trackStates, "sfx") ||
          !isStudioSourceClockCurrent(video, { activeVideo: videoRef.current, url: clip?.url,
            start: window.start, end: window.end, hasNext: activeTimelineIndex < timeline.length - 1 })) return false;
      const outputTime = getSoundEffectProgrammeTime(video.currentTime || 0);
      elapsed = outputTime - Number(effect.startTime || 0);
      playbackRate = video.playbackRate || 1;
      automationGain = interpolateAutomationValue({ keyframes: audioKeyframes.sfx,
        property: "volume", time: outputTime, fallback: 100 }) / 100;
    }

    const fullDuration = getSoundEffectDuration(effect);
    const offset = Math.max(0, elapsed);
    if (offset >= fullDuration) return false;
    const rate = clampAudioControl(playbackRate, 0.1, 16, 1);
    const volume =
      clampAudioControl(effect.volume, 0, 1, 0.8) *
      (previewMuted ? 0 : clampAudioControl(previewVolume, 0, 1, 1)) * automationGain;
    const gain = audioContext.createGain();
    gain.connect(audioContext.destination);

    const registerNode = node => {
      soundEffectNodesRef.current.add(node);
      node.addEventListener?.("ended", () => soundEffectNodesRef.current.delete(node), {
        once: true,
      });
      return node;
    };

    let buffer = decodedCue || prepareStudioBuiltInCue(audioContext,
      { ...effect,duration:fullDuration },soundEffectBuiltInBuffersRef.current,projectId);
    if (!buffer) {
      const samples = synthesizeEffect({ ...effect, duration: fullDuration }, audioContext.sampleRate);
      buffer = audioContext.createBuffer(1, samples.length, audioContext.sampleRate);
      buffer.getChannelData(0).set(samples);
    }
    let startAt = audioContext.currentTime;
    if (timelinePlayback && musicPreviewSourceRef.current &&
        audioContext === musicPreviewAudioContextRef.current && !isolatedMusicPreviewIntentRef.current) {
      startAt = Math.max(startAt, musicPreviewSourceStateRef.current?.startedAt || startAt);
    }
    let sourceOffset = timelinePlayback
      ? getSoundEffectProgrammeTime(video.currentTime || 0) - Number(effect.startTime || 0) : offset;
    // The same helper selects and validates the sample clock both before and
    // after preparation. A second programme-seconds tolerance here previously
    // disagreed with the song's output-seconds tolerance during speed ramps.
    // getSoundEffectProgrammeTime retains source time for audition/discontinuity.
    let cueStartPlan;
    if (timelinePlayback) {
      const plan = studioCueStartPlan({ contextTime: startAt,
        programmeTime: Number(effect.startTime || 0) + sourceOffset,
        cueStart: Number(effect.startTime || 0), duration: fullDuration,
        outputTimeMap: previewOutputTimeMap });
      if (!plan) { gain.disconnect(); return false; }
      startAt = plan.when;
      sourceOffset = plan.offset;
      cueStartPlan = plan;
    }
    gain.gain.setValueAtTime(volume, startAt);
    const scheduledGain = timelinePlayback && scheduleStudioCueGain({
      param: gain.gain, contextTime: startAt,
      programmeStart: Number(effect.startTime || 0) + sourceOffset,
      programmeEnd: Number(effect.startTime || 0) + fullDuration,
      outputTimeMap: previewOutputTimeMap, keyframes: audioKeyframes.sfx,
      baseGain: clampAudioControl(effect.volume, 0, 1, 0.8) *
        (previewMuted ? 0 : clampAudioControl(previewVolume, 0, 1, 1)),
    });
    const source = registerNode(audioContext.createBufferSource());
    source.buffer = buffer;
    source.playbackRate.setValueAtTime(rate, startAt);
    const scheduledRate = timelinePlayback && scheduleStudioCueRate({ param: source.playbackRate,
      contextTime: startAt, programmeStart: Number(effect.startTime || 0) + sourceOffset,
      programmeEnd: Number(effect.startTime || 0) + fullDuration, outputTimeMap: previewOutputTimeMap });
    source.connect(gain);
    if (cueStartPlan) {
      const readyPlan = advanceStudioCueStartPlan(cueStartPlan, audioContext.currentTime,
        previewOutputTimeMap, fullDuration);
      if (!readyPlan) { source.disconnect(); gain.disconnect(); return false; }
      startAt = readyPlan.when;
      sourceOffset = readyPlan.offset;
    }
    if (sourceOffset >= buffer.duration) { source.disconnect(); gain.disconnect(); return false; }
    source.start(startAt, sourceOffset);
    if (timelinePlayback) {
      soundEffectTimelineGainsRef.current.set(effect.id, { source, gain, scheduledGain, scheduledRate,
        outputTimeMap: previewOutputTimeMap });
      source.addEventListener?.("ended", () => {
        if (soundEffectTimelineGainsRef.current.get(effect.id)?.source === source) {
          soundEffectTimelineGainsRef.current.delete(effect.id);
        }
      }, { once: true });
    }

    soundEffectNodesRef.current.add(gain);
    // A live ramp can extend a cue beyond its initial rate/duration. Release
    // gain with the actual source ending rather than an obsolete wall timer.
    source.addEventListener("ended", () => {
      soundEffectNodesRef.current.delete(gain);
      try { gain.disconnect(); } catch (_) {}
    }, { once: true });
    return true;
  };

  const addSoundEffectPreset = (preset, exactBeat = null) => {
    const magneticStart = exactBeat === null
      ? resolveMagneticTimelineTime(previewTimelineTime)
      : { time: exactBeat, snapped: true };
    const startTime = clampNumber(magneticStart.time, 0, outputTimelineDuration, 0);
    const effect = {
      id: createSecureId("sfx"),
      name: preset.name,
      emoji: preset.emoji,
      tone: preset.tone,
      builtIn: true,
      url: null,
      file: null,
      startTime,
      duration: preset.duration,
      trimStart: 0,
      volume: preset.volume ?? 0.8,
      fadeIn: 0.02,
      fadeOut: Math.min(0.18, preset.duration / 3),
      enabled: true,
    };
    setSoundEffects(previous => [...previous, effect]);
    setActiveSoundEffectId(effect.id);
    setStudioInspectorTab("sound");
    setActiveCreativeTool("sound");
    seekLiveEditTimelineItem(startTime, "sound");
    setStudioActionMessage(
      `${preset.name} added at ${formatPreviewTimePrecise(startTime)}${magneticStart.snapped ? " on the nearest music beat" : ""}. It is visible on the SFX lane and plays in After and Split.`
    );
  };

  const handleSoundEffectUpload = event => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("audio/") && !file.name.match(/\.(mp3|wav|ogg|m4a|aac)$/i)) {
      toast.error("Please select an audio effect (MP3, WAV, OGG, M4A, AAC).");
      return;
    }
    const url = URL.createObjectURL(file);
    soundEffectObjectUrlsRef.current.add(url);
    const magneticStart = resolveMagneticTimelineTime(previewTimelineTime);
    const startTime = clampNumber(magneticStart.time, 0, outputTimelineDuration, 0);
    const effect = {
      id: createSecureId("sfx"),
      name: normalizePlainText(file.name.replace(/\.[^.]+$/, "")) || "Custom effect",
      emoji: "🔊",
      tone: "custom",
      builtIn: false,
      url,
      file,
      startTime,
      duration: 2,
      trimStart: 0,
      volume: 0.8,
      fadeIn: 0.02,
      fadeOut: 0.12,
      enabled: true,
    };
    setSoundEffects(previous => [...previous, effect]);
    setActiveSoundEffectId(effect.id);
    setStudioInspectorTab("sound");
    setActiveCreativeTool("sound");
    seekLiveEditTimelineItem(startTime, "sound");
    setStudioActionMessage(
      `${effect.name} added at ${formatPreviewTimePrecise(startTime)}. Set its exact timing, trim, fades, and volume below.`
    );
    event.target.value = null;
  };

  const updateSoundEffect = (id, updates) => {
    setSoundEffects(previous =>
      previous.map(effect => {
        if (effect.id !== id) return effect;
        const next = { ...effect, ...updates };
        next.startTime = clampNumber(next.startTime, 0, outputTimelineDuration, 0);
        next.duration = clampAudioControl(next.duration, 0.05, 15, 0.6);
        next.trimStart = clampAudioControl(next.trimStart, 0, 36000, 0);
        next.volume = clampAudioControl(next.volume, 0, 1, 0.8);
        next.fadeIn = clampAudioControl(next.fadeIn, 0, next.duration, 0.02);
        next.fadeOut = clampAudioControl(next.fadeOut, 0, next.duration, 0.12);
        return next;
      })
    );
  };

  const removeSoundEffect = id => {
    const effect = soundEffects.find(item => item.id === id);
    soundEffectAudioRefsRef.current.get(id)?.pause();
    soundEffectAudioRefsRef.current.delete(id);
    setSoundEffects(previous => previous.filter(item => item.id !== id));
    setActiveSoundEffectId(current => (current === id ? null : current));
    setPreviewingSoundEffectId(current => (current === id ? null : current));
    setStudioActionMessage(`${effect?.name || "Sound effect"} removed. Undo restores it.`);
  };

  const duplicateSoundEffect = effect => {
    pendingHistoryBaselineRef.current = cloneSnapshot(getEditorSnapshot());
    const copy = { ...effect, id: createSecureId("sfx"),
      startTime: clampNumber(Number(effect.startTime || 0) + getSoundEffectDuration(effect),
        0, Math.max(0, outputTimelineDuration - getSoundEffectDuration(effect)), 0) };
    setSoundEffects(previous => [...previous, copy]);
    setActiveSoundEffectId(copy.id);
    seekLiveEditTimelineItem(copy.startTime, "sound");
    setStudioActionMessage(`${effect.name} duplicated at ${formatPreviewTimePrecise(copy.startTime)}. Edit this cue independently; Undo removes the copy.`);
  };

  const toggleSoundEffectPreview = async effect => {
    if (!effect) return;
    if (previewingSoundEffectId === effect.id) {
      stopSoundEffectPlayback();
      setStudioActionMessage(`${effect.name} preview stopped.`);
      return;
    }

    stopSoundEffectPlayback(false);
    setPreviewingSoundEffectId(effect.id);
    const duration = getSoundEffectDuration(effect);
    if (effect.builtIn) {
      const started = await playBuiltInSoundEffect(effect, 0);
      if (!started) {
        setPreviewingSoundEffectId(null);
        return;
      }
    } else {
      const audio = soundEffectAudioRefsRef.current.get(effect.id);
      if (!audio) {
        setPreviewingSoundEffectId(null);
        return;
      }
      try {
        audio.currentTime = clampAudioControl(effect.trimStart, 0, audio.duration || 36000, 0);
        audio.volume = clampAudioControl(effect.volume, 0, 1, 0.8);
        safePlayMediaElement(audio);
      } catch (error) {
        console.log("Sound effect preview skipped", error);
      }
    }
    setStudioActionMessage(`${effect.name} preview is playing at the configured mix level.`);
    soundEffectPreviewTimeoutRef.current = window.setTimeout(
      () => {
        stopSoundEffectPlayback();
      },
      Math.ceil(duration * 1000)
    );
  };

  // ── Speech-aware auto-ducking (Web Audio API) ──
  const startSpeechDetection = () => {
    if (muteOriginalAudio || !isStudioAudioTrackAudible(trackStates, "originalAudio")) return null;
    if (speechAnalyserRef.current?.analyser && speechAnalyserRef.current.media === videoRef.current) {
      speechAnalyserRef.current.ctx?.resume?.().catch(() => {});
      return speechAnalyserRef.current.analyser;
    }
    if (!videoRef.current) return null;
    const remixGraph = ensureAudioRemixPreview(videoRef.current);
    if (remixGraph?.analyser) {
      speechAnalysisContextsRef.current.add(remixGraph.context);
      speechAnalyserRef.current = { ctx: remixGraph.context, analyser: remixGraph.analyser, source: remixGraph.source, media: videoRef.current };
      return remixGraph.analyser;
    }
    if (failedSpeechSourcesRef.current.has(videoRef.current)) return null;
    try {
      const AudioContextCtor = window.AudioContext || window.webkitAudioContext;
      if (!AudioContextCtor) return null;
      const audioCtx = new AudioContextCtor();
      const analyser = audioCtx.createAnalyser();
      analyser.fftSize = 256;
      analyser.smoothingTimeConstant = 0.4;
      audioCtx.resume?.().catch(() => {});

      // Try to connect — may fail if video already has a MediaElementSource
      try {
        const source = audioCtx.createMediaElementSource(videoRef.current);
        source.connect(analyser);
        analyser.connect(audioCtx.destination);
        speechAnalysisContextsRef.current.add(audioCtx);
        speechAnalyserRef.current = { ctx: audioCtx, analyser, source, media: videoRef.current };
      } catch (_connectErr) {
        // An unconnected analyser cannot establish speech energy.
        failedSpeechSourcesRef.current.add(videoRef.current);
        audioCtx.close()?.catch(() => {});
        return null;
      }
      return analyser;
    } catch (e) {
      console.log("Speech detection setup skipped:", e);
      return null;
    }
  };

  const getSpeechEnergyLevel = () => {
    const analysers = [!muteOriginalAudio && isStudioAudioTrackAudible(trackStates, "originalAudio") &&
      speechAnalyserRef.current?.media === videoRef.current
      ? speechAnalyserRef.current?.analyser : null, voiceoverSpeechAnalyserRef.current].filter(Boolean);
    const energy = analysers.reduce((sum, analyser) => {
      const data = new Uint8Array(analyser.frequencyBinCount);
      analyser.getByteFrequencyData(data);
      const speechBins = data.slice(0, 18);
      return sum + speechBins.reduce((s, v) => s + v, 0) / speechBins.length / 128;
    }, 0);
    return Math.min(1, energy);
  };

  // Broader frame-driven composition remains a local diagnostic candidate
  // until native throughput and moving picture/sound qualification pass.
  const presentedFramePreview = workspaceMode === 'fxflow' || process.env.REACT_APP_STUDIO_PRESENTED_FRAME_PREVIEW === 'true';
  const currentTimelineClip = timeline[activeTimelineIndex] || null;
  const subjectProgrammePreview = useMemo(() => renderedOutputUrl ? null :
    resolveSubjectVfxProgrammePreview(subjectVfxScenes,
      currentTimelineClip?.sourceClipId || currentTimelineClip?.id, videoTime),
    [subjectVfxScenes, currentTimelineClip, videoTime, renderedOutputUrl]);
  const refreshSubjectProgrammePreview = async () => {
    const item = subjectProgrammePreview;
    if (!item) return;
    const { jobId, revision, url } = item.preview;
    const key = `${jobId}:${revision}:${url}`;
    if (attemptedSubjectProgrammeRefreshRef.current.has(key)) return;
    attemptedSubjectProgrammeRefreshRef.current.add(key);
    try {
      const token = await getMediaAuthToken();
      if (!token) throw new Error("Sign in to recover the reviewed subject preview.");
      const response = await fetch(`${API_BASE_URL}/api/media/studio-subject-vfx/${encodeURIComponent(jobId)}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const state = await response.json();
      if (!response.ok || state.jobId !== jobId || state.status !== "completed" ||
          !state.url || !state.receipt?.pixel_sha256) {
        throw new Error("The reviewed subject preview is unavailable. Check its saved job in Composite.");
      }
      setSubjectVfxScenes(current => current.map(value => {
        try {
          if (value.scene.id !== item.scene.id || value.preview?.jobId !== jobId ||
              subjectVfxRevision(value.scene) !== revision) return value;
        } catch { return value; }
        return { ...value, preview: { ...value.preview, url: state.url, receipt: state.receipt,
          reviewed: value.preview.reviewed === true &&
            value.preview.receipt?.pixel_sha256 === state.receipt.pixel_sha256 } };
      }));
    } catch (error) { setStudioActionMessage(error.message); }
  };
  const currentTimelineWindow = getTimelineClipWindow(currentTimelineClip);
  const silenceRegionsForTimeline = useMemo(() => {
    if (!silencePreview?.silenceSegments?.length || !currentTimelineClip) return [];
    if (silencePreview.clipId !== currentTimelineClip.id) return [];
    const sourceWindow = getTimelineClipWindow(currentTimelineClip);
    const clipStart = Number(sourceWindow.start || 0);
    const clipOffset = getTimelineOffsetForIndex(activeTimelineIndex);
    return silencePreview.silenceSegments
      .map(seg => ({
        timelineStart: clipOffset + (Number(seg.start) - clipStart),
        duration: Number(seg.duration ?? (seg.end - seg.start)),
        start: Number(seg.start),
        end: Number(seg.end),
      }))
      .filter(z => z.duration > 0.05);
  }, [silencePreview, currentTimelineClip, activeTimelineIndex, timeline]);
  const activePunchZones = useMemo(
    () => extractPunchZonesFromKeyframes(motionKeyframes),
    [motionKeyframes]
  );
  const speakerStackSourceOptions = useMemo(() => {
    const primarySource = getSafeMediaSource(currentTimelineClip?.url || videoUrl);
    const primaryOption = primarySource
      ? [
          {
            id: "programme-source",
            clip: currentTimelineClip,
            source: primarySource,
            label: currentTimelineClip?.name || "Current programme source",
            isProgrammeSource: true,
          },
        ]
      : [];
    return [
      ...primaryOption,
      ...speakerStackSources.flatMap((source, index) => {
        const safeSource = getSafeMediaSource(source?.url);
        if (!safeSource) return [];
        return [
          {
            id: String(source.id || `clean-angle-${index}`),
            clip: source,
            source: safeSource,
            label: source.name || `Clean speaker angle ${index + 1}`,
            isProgrammeSource: false,
          },
        ];
      }),
    ];
  }, [currentTimelineClip, speakerStackSources, videoUrl]);
  const activeSpeakerStackSlots = useMemo(
    () =>
      MULTICAM_SLOT_KEYS.slice(0, clampNumber(speakerStackCameraCount, 2, 4, 2)),
    [speakerStackCameraCount]
  );
  const resolvedSpeakerStackSourceIds = (() => {
    const usedIds = new Set();
    const primarySourceOption = speakerStackSourceOptions[0] || null;
    return MULTICAM_SLOT_KEYS.reduce((resolved, slot) => {
      if (!activeSpeakerStackSlots.includes(slot)) {
        resolved[slot] = null;
        return resolved;
      }
      const requestedId = String(speakerStackSourceIds[slot] || "");
      const requested = speakerStackSourceOptions.find(
        option => option.id === requestedId && !usedIds.has(option.id)
      );
      const fallback = speakerStackSourceOptions.find(option => !usedIds.has(option.id));
      const selected = requested || fallback || primarySourceOption || null;
      resolved[slot] = selected?.id || null;
      if (selected?.id) usedIds.add(selected.id);
      return resolved;
    }, {});
  })();
  const resolvedSpeakerStackSources = activeSpeakerStackSlots.map(slot =>
    speakerStackSourceOptions.find(option => option.id === resolvedSpeakerStackSourceIds[slot])
  );
  const speakerStackTopSource = speakerStackSourceOptions.find(
    option => option.id === resolvedSpeakerStackSourceIds.top
  );
  const speakerStackBottomSource = speakerStackSourceOptions.find(
    option => option.id === resolvedSpeakerStackSourceIds.bottom
  );
  const speakerStackThirdSource = speakerStackSourceOptions.find(
    option => option.id === resolvedSpeakerStackSourceIds.third
  );
  const speakerStackFourthSource = speakerStackSourceOptions.find(
    option => option.id === resolvedSpeakerStackSourceIds.fourth
  );
  const hasCleanSpeakerStackSources = Boolean(
    resolvedSpeakerStackSources.length === activeSpeakerStackSlots.length &&
      resolvedSpeakerStackSources.every(source => source?.source) &&
      new Set(resolvedSpeakerStackSources.map(source => source.source)).size ===
        activeSpeakerStackSlots.length
  );
  const speakerStackIsReady = Boolean(
    hasCleanSpeakerStackSources && speakerStackSyncConfirmed
  );
  const multicamExportNeedsSupport =
    smartCrop &&
    (smartCropMode === "group_stack" || reframeModeCuts.some(cut => cut.mode === "group_stack")) &&
    !["stack_2", "split_2", "pip_2", "active_2", "spotlight_2", "hero_3", "grid_4"].includes(speakerStackLayout);
  const currentAudioMode = normalizeAudioMode(extractedAudio?.mode);
  const previewClipTime = Math.max(
    0,
    Number(videoTime || 0) - Number(currentTimelineWindow.start || 0)
  );
  const previewTimelineTime = getPreviewTimelineTime(videoTime);
  const originalAudioWindow = useMemo(() => buildAudioSplitSequence(timeline)[activeTimelineIndex], [timeline, activeTimelineIndex]);
  const isPlayheadPunched = useMemo(
    () => isTimePunched(motionKeyframes, previewTimelineTime),
    [motionKeyframes, previewTimelineTime]
  );
  const hasActiveJCut = useMemo(() => {
    const activeClip = timeline[activeTimelineIndex];
    return Number(activeClip?.audioTrimOffsetStart || 0) < -0.04;
  }, [timeline, activeTimelineIndex]);
  const hasActiveLCut = useMemo(() => {
    const activeClip = timeline[activeTimelineIndex];
    return Number(activeClip?.audioTrimOffsetEnd || 0) > 0.04;
  }, [timeline, activeTimelineIndex]);

  // J-Cut & L-Cut Live Preview Audio Synchronization
  useEffect(() => {
    const video = videoRef.current;
    if (!video || !timeline.length) return;

    const syncSplitAudioPlayback = () => {
      if (video.paused || renderedOutputUrl || previewMuted || muteOriginalAudio || !isStudioAudioTrackAudible(trackStates, 'originalAudio')) {
        timelineAudioPoolRef.current.forEach(audio => {
          if (!audio.paused) audio.pause();
        });
        return;
      }

      const globalTime = getPreviewTimelineTime(video.currentTime || 0);
      const splitSequence = buildAudioSplitSequence(timeline);
      const activeProxies = calculateAudioProxyState(globalTime, splitSequence, activeTimelineIndex);
      const activeProxyIds = new Set(activeProxies.map(p => p.id));

      timelineAudioPoolRef.current.forEach((audio, id) => {
        if (!activeProxyIds.has(id) && !audio.paused) {
          audio.pause();
        }
      });

      activeProxies.forEach(proxy => {
        let audio = timelineAudioPoolRef.current.get(proxy.id);
        if (!audio) {
          audio = document.createElement("audio");
          audio.preload = "auto";
          applySafeMediaSource(audio, proxy.url);
          timelineAudioPoolRef.current.set(proxy.id, audio);
        }
        if (Math.abs(audio.currentTime - proxy.sourceSeekTime) > 0.15) {
          audio.currentTime = proxy.sourceSeekTime;
        }
        audio.playbackRate = video.playbackRate || 1;
        audio.preservesPitch = true;
        audio.volume = clampNumber(previewVolume * proxy.volume * interpolateAutomationValue({
          keyframes: audioKeyframes.originalAudio, property: 'volume', time: globalTime, fallback: 100 }) / 100, 0, 1, 0);
        if (audio.paused) {
          safePlayMediaElement(audio);
        }
      });
    };

    const videoEl = videoRef.current;
    videoEl.addEventListener("timeupdate", syncSplitAudioPlayback);
    videoEl.addEventListener("pause", syncSplitAudioPlayback);
    videoEl.addEventListener("seeking", syncSplitAudioPlayback);

    return () => {
      videoEl.removeEventListener("timeupdate", syncSplitAudioPlayback);
      videoEl.removeEventListener("pause", syncSplitAudioPlayback);
      videoEl.removeEventListener("seeking", syncSplitAudioPlayback);
      timelineAudioPoolRef.current.forEach(audio => {
        try {
          audio.pause();
        } catch (_) {}
      });
    };
  }, [timeline, activeTimelineIndex, renderedOutputUrl, previewMuted, muteOriginalAudio, trackStates, previewVolume, audioKeyframes.originalAudio]);
  const effectiveSmartCropMode = resolveReframeModeAtTime(
    reframeModeCuts,
    previewTimelineTime,
    smartCrop ? smartCropMode : "off"
  );
  const effectiveSmartCrop = effectiveSmartCropMode !== "off";
  const activeShowEveryonePrimarySlot = resolveSpeakerFocusSlot(
    speakerFocusCuts,
    previewTimelineTime
  );
  const resolveSourceSplitTimeOffset = (frame, time = previewTimelineTime) => {
    const keys = [...(frame?.sourceTimeOffsetKeyframes || frame?.source_time_offset_keyframes || [])]
      .sort((left, right) => Number(left?.time || 0) - Number(right?.time || 0));
    let offset = Number(frame?.sourceTimeOffsetSeconds ?? frame?.source_time_offset_seconds ?? 0) || 0;
    keys.forEach(key => {
      if (Number(key?.time || 0) <= time + 0.001) {
        offset = Number(key?.offsetSeconds ?? key?.offset_seconds ?? 0) || 0;
      }
    });
    return offset;
  };
  const sourceSplitTopOffset = resolveSourceSplitTimeOffset(speakerStackFraming.top);
  const sourceSplitBottomOffset = resolveSourceSplitTimeOffset(speakerStackFraming.bottom);
  const motionInspectorTarget =
    motionTargetId === "main-video"
      ? mainTransform
      : overlays.find(overlay => String(overlay.id) === String(motionTargetId)) || null;
  const compositeInspectorTarget =
    compositeTargetId === "main-video"
      ? mainTransform
      : overlays.find(overlay => String(overlay.id) === String(compositeTargetId)) || null;
  const mainMotionAutomation = evaluateLocalLayerPose(
    { id: "main-video", ...mainTransform },
    previewMotionKeyframes,
    previewTimelineTime
  );
  const activeMotionTargetKeyframes = motionInspectorTarget
    ? motionKeyframes.filter(
        keyframe => String(keyframe.targetId) === String(motionInspectorTarget.id)
      )
    : [];
  const outputTimelineDuration = Math.max(0.1, getTimelineDuration());
  const activeOverlayTimelineRemaining = Math.max(
    0.1,
    outputTimelineDuration - activeOverlayStartTime
  );
  const outputPlaybackDuration = ticksToSeconds(previewOutputTimeMap.at(-1)?.outputRange.endTick || 0);
  const previewPlaybackTime = renderedOutputUrl ? Math.max(0, videoTime)
    : ticksToSeconds(mapProgrammeTickToOutputTick(previewOutputTimeMap,
        Math.min(previewOutputTimeMap.at(-1)?.programmeRange.endTick || 0,
          secondsToTicks(Math.max(0, previewTimelineTime)))) || 0);
  const currentTimelineOffset = getTimelineOffsetForIndex(activeTimelineIndex);
  const activeContentProfile =
    CREATOR_CONTENT_PROFILES.find(profile => profile.id === contentProfile) ||
    CREATOR_CONTENT_PROFILES[0];
  const resolvedJoinTransition =
    joinTransition === "auto" ? activeContentProfile.transition : joinTransition;
  const cutRangeIsReady =
    cutRangeStart !== null &&
    cutRangeEnd !== null &&
    Math.abs(Number(cutRangeEnd) - Number(cutRangeStart)) >= 0.15;
  const normalizedPendingCutRange = cutRangeIsReady
    ? {
        start: Math.min(Number(cutRangeStart), Number(cutRangeEnd)),
        end: Math.max(Number(cutRangeStart), Number(cutRangeEnd)),
      }
    : null;
  const liveCreativeEffects = buildSignatureCreativeEffects({
    preset: creativePreset,
    intensity: creativeIntensity,
    duration: currentTimelineWindow.duration || selectedClip?.duration || 0,
  });
  const activeLiveCreativeEffect =
    liveCreativeEffects.find(
      effect => previewClipTime >= effect.start_time && previewClipTime <= effect.end_time
    ) || liveCreativeEffects[0];
  const creativeEffectIsLive =
    creativeEffectsEnabled && comparisonMode !== "before" && !renderedOutputUrl;
  const beatEchoPreviewIsLive =
    creativeEffectIsLive && activeLiveCreativeEffect?.preset === "beat_echo";
  const motionSculpturePreviewIsLive =
    creativeEffectIsLive && activeLiveCreativeEffect?.preset === "motion_sculpture";
  const temporalPreviewIsLive = beatEchoPreviewIsLive || motionSculpturePreviewIsLive;
  const creativePreviewClass = creativeEffectIsLive
    ? `creative-preview-${activeLiveCreativeEffect?.preset || "motion_sculpture"} creative-intensity-${creativeIntensity}`
    : "";
  const normalizedHookText = normalizeHookText(hookText);
  const hasHookText = !!normalizedHookText;
  const fallbackHookSuggestion = buildFallbackHookRange(
    selectedClip,
    currentTimelineWindow.duration
  );
  const currentHookSuggestion = hookSuggestedRange || fallbackHookSuggestion;
  const currentHookCopySuggestions = getHookCopySuggestions(selectedClip);
  const clipGuidanceEntries = orderedClips.map((clip, index) => {
    const guidance = buildClipGuidance(clip);
    return {
      clip,
      index,
      ...guidance,
    };
  });
  const rankedClipGuidance = [...clipGuidanceEntries].sort(
    (left, right) =>
      right.score - left.score || right.backendScore - left.backendScore || left.index - right.index
  );
  const momentFamilies = buildMomentFamilies(rankedClipGuidance);
  const bestClipId = rankedClipGuidance[0]?.clip?.id || null;
  const topFamilyKeys = new Set();
  const familyHighlights = [];
  momentFamilies.forEach(family => {
    const entry = family.topEntry;
    if (!entry || topFamilyKeys.has(entry.momentFamilyKey)) return;
    topFamilyKeys.add(entry.momentFamilyKey);
    familyHighlights.push(entry);
  });
  const topPickIds = new Set(familyHighlights.slice(0, 2).map(entry => entry.clip.id));
  const clipGuidanceById = new Map(clipGuidanceEntries.map(entry => [entry.clip.id, entry]));
  const selectedClipGuidance = selectedClip ? clipGuidanceById.get(selectedClip.id) || null : null;
  const selectedMomentFamily = selectedClipGuidance
    ? momentFamilies.find(family => family.clipIds.includes(selectedClipGuidance.clip.id)) || null
    : null;
  const familySiblingEntries = selectedMomentFamily
    ? selectedMomentFamily.members
        .filter(entry => entry.clip.id !== selectedClipGuidance?.clip?.id)
        .slice(0, 3)
    : [];
  const campaignSet = buildCampaignSet(rankedClipGuidance, momentFamilies);
  const hookTemplateConfig = getHookTemplateConfig(hookTemplate);
  const hookMinDuration = Math.min(
    HOOK_MIN_SEGMENT_DURATION,
    Math.max(0.25, Number(currentTimelineWindow.duration || 0) || HOOK_MIN_SEGMENT_DURATION)
  );
  const hookMaxDuration = Math.min(
    HOOK_MAX_SEGMENT_DURATION,
    Math.max(
      hookMinDuration,
      Number(currentTimelineWindow.duration || 0) || HOOK_MAX_SEGMENT_DURATION
    )
  );
  const hookStartLimit = Math.max(
    0,
    Math.max(0, Number(currentTimelineWindow.duration || 0)) - hookMinDuration
  );
  const resolvedHookStart = clampNumber(
    hookStartTime,
    0,
    hookStartLimit,
    currentHookSuggestion.startTime
  );
  const hookEndMinimum = Number(currentTimelineWindow.duration || 0)
    ? Math.min(Number(currentTimelineWindow.duration || 0), resolvedHookStart + hookMinDuration)
    : resolvedHookStart + hookMinDuration;
  const hookEndMaximum = Number(currentTimelineWindow.duration || 0)
    ? Math.min(Number(currentTimelineWindow.duration || 0), resolvedHookStart + hookMaxDuration)
    : resolvedHookStart + hookMaxDuration;
  const hookEnd = clampNumber(
    hookEndTime,
    hookEndMinimum,
    Math.max(hookEndMinimum, hookEndMaximum),
    Math.min(
      hookEndMaximum,
      resolvedHookStart +
        clampNumber(
          hookIntroSeconds,
          hookMinDuration,
          hookMaxDuration,
          currentHookSuggestion.duration || 3
        )
    )
  );
  const hookDuration = Math.max(0.1, hookEnd - resolvedHookStart);
  const hookLeadOut = 0.45;
  const isHookWithinPreviewWindow =
    addHook &&
    activeTimelineIndex === 0 &&
    previewClipTime >= resolvedHookStart &&
    previewClipTime <= hookEnd + hookLeadOut;
  const hookProgress = isHookWithinPreviewWindow
    ? clampNumber((previewClipTime - resolvedHookStart) / Math.max(hookDuration, 0.01), 0, 1, 0)
    : 0;
  const hookOutroOpacity =
    previewClipTime > hookEnd
      ? clampNumber(1 - (previewClipTime - hookEnd) / hookLeadOut, 0, 1, 0)
      : previewClipTime >= resolvedHookStart
        ? 1
        : 0;
  const hookTextIntroProgress = isHookWithinPreviewWindow
    ? clampNumber((previewClipTime - resolvedHookStart) / 0.24, 0, 1, 0)
    : 0;
  const isZoomFocusTemplate = hookTemplate === "zoom_focus";
  const isBlurRevealTemplate = hookTemplate === "blur_reveal";
  const isFreezeTextTemplate = hookTemplate === "freeze_text";
  const freezeReleaseProgress = hookFreezeFrame
    ? clampNumber((hookProgress - 0.72) / 0.28, 0, 1, 0)
    : 1;
  const resolvedHookFocusPoint = normalizeHookFocusPoint(hookFocusPoint);
  const hasCustomHookFocusPoint =
    Math.abs(resolvedHookFocusPoint.x - DEFAULT_HOOK_FOCUS_POINT.x) > 1 ||
    Math.abs(resolvedHookFocusPoint.y - DEFAULT_HOOK_FOCUS_POINT.y) > 1;
  const hookZoomTarget = Math.max(
    hookZoomScale,
    isZoomFocusTemplate ? hookZoomScale + 0.08 : hookZoomScale
  );
  const effectiveHookZoomTarget =
    hookFreezeFrame && hasCustomHookFocusPoint ? Math.max(hookZoomTarget, 1.12) : hookZoomTarget;
  const hookVisualScale = isHookWithinPreviewWindow
    ? 1 +
      Math.max(0, effectiveHookZoomTarget - 1) *
        (hookFreezeFrame ? 0 : 1 - Math.pow(1 - hookProgress, 2))
    : 1;
  const showHookPreview = !!isHookWithinPreviewWindow;
  const hookVideoBlur =
    showHookPreview && hookBlurBackground
      ? Math.max(
          0,
          (isBlurRevealTemplate ? 18 : 11) - hookProgress * (isBlurRevealTemplate ? 16 : 8.5)
        ) * hookOutroOpacity
      : 0;
  const hookVideoBrightness = showHookPreview && hookDarkOverlay ? 0.66 + hookProgress * 0.24 : 1;
  const hookVideoContrast = showHookPreview
    ? 1 + hookOutroOpacity * (isZoomFocusTemplate ? 0.22 : 0.12)
    : 1;
  const hookVideoSaturate = showHookPreview
    ? 1 + hookOutroOpacity * (isZoomFocusTemplate ? 0.24 : 0.14)
    : 1;
  const hookBackdropOpacity =
    showHookPreview && hookBlurBackground
      ? Math.max(
          0,
          (isBlurRevealTemplate ? 0.96 : 0.82) - hookProgress * (isBlurRevealTemplate ? 0.64 : 0.52)
        ) * hookOutroOpacity
      : 0;
  const hookOverlayOpacity = hookDarkOverlay
    ? ((isZoomFocusTemplate ? 0.28 : 0.18) +
        (1 - hookProgress) * (isZoomFocusTemplate ? 0.3 : 0.24)) *
      hookOutroOpacity
    : 0;
  const hookAccentOpacity = showHookPreview
    ? (isZoomFocusTemplate ? 0.54 : isBlurRevealTemplate ? 0.46 : 0.38) * hookOutroOpacity
    : 0;
  const hookAccentTranslate = isBlurRevealTemplate
    ? `${Math.round((1 - hookProgress) * 36)}px`
    : `${Math.round((1 - hookProgress) * 16)}px`;
  const hookBannerAccentOpacity = showHookPreview
    ? (0.22 + hookTextIntroProgress * 0.48) * hookOutroOpacity
    : 0;
  const hookBannerScale = showHookPreview
    ? 0.96 +
      hookTextIntroProgress * (isFreezeTextTemplate ? 0.08 : isZoomFocusTemplate ? 0.06 : 0.04)
    : 1;
  const hookTextGlowOpacity = showHookPreview
    ? (0.22 + (1 - hookProgress) * 0.26) * hookOutroOpacity
    : 0;
  const hookPrimaryVideoOpacity = showHookPreview
    ? hookBlurBackground
        ? 0.9
        : 1
    : 1;
  const hookFreezeOpacity =
    showHookPreview && hookFreezeFrame ? hookOutroOpacity * (1 - freezeReleaseProgress * 0.92) : 0;
  const hookVisualFocusPoint = showHookPreview ? resolvedHookFocusPoint : DEFAULT_HOOK_FOCUS_POINT;
  const hookTransformOrigin = `${hookVisualFocusPoint.x}% ${hookVisualFocusPoint.y}%`;
  const finishPreviewIsLive = comparisonMode !== "before" && !renderedOutputUrl;
  const compiledAdjustmentGrades = useMemo(() => compileStudioAdjustmentStack(adjustmentLayers), [adjustmentLayers]);
  const effectiveFinishFx = finishFx.precisionGrade ? finishFx : interpolateFinishKeyframes(
    finishFx,
    finishKeyframes,
    previewTimelineTime
  );
  const finishFilter = finishPreviewIsLive && !effectiveFinishFx.precisionGrade ? buildCinematicCssFilter(effectiveFinishFx) : "";
  const precisionPreviewGrade = useMemo(() => finishPreviewIsLive && finishFx.precisionGrade
    ? buildPrecisionGrade(finishFx) : null, [finishPreviewIsLive, finishFx]);
  const advancedColorCube = useMemo(() => buildAdvancedColorCube(advancedColor), [advancedColor]);
  const finishZoom = finishPreviewIsLive ? Math.max(1, Number(effectiveFinishFx.zoom || 1)) : 1;
  const finishTransformOrigin =
    effectiveFinishFx.zoomAnchor === "left"
      ? "15% 50%"
      : effectiveFinishFx.zoomAnchor === "right"
        ? "85% 50%"
        : hookTransformOrigin;
  const hookObjectPosition = `${hookVisualFocusPoint.x}% ${hookVisualFocusPoint.y}%`;
  const hookBannerSubjectType = hasCustomHookFocusPoint
    ? resolvedHookFocusPoint.y <= 42 && Math.abs(resolvedHookFocusPoint.x - 50) <= 18
      ? "face"
      : "object"
    : "neutral";
  const hookBannerSide = hasCustomHookFocusPoint
    ? hookBannerSubjectType === "face"
      ? resolvedHookFocusPoint.x >= 50
        ? "left"
        : "right"
      : resolvedHookFocusPoint.x >= 64
        ? "left"
        : resolvedHookFocusPoint.x <= 36
          ? "right"
          : "center"
    : "center";
  const hookBannerAnchorX =
    hookBannerSide === "left"
      ? hookBannerSubjectType === "face"
        ? 8
        : 12
      : hookBannerSide === "right"
        ? hookBannerSubjectType === "face"
          ? 92
          : 88
        : 50;
  const hookBannerTranslateX =
    hookBannerSide === "left" ? "0%" : hookBannerSide === "right" ? "-100%" : "-50%";
  const hookBannerBaseTop =
    resolvedHookFocusPoint.y <= 30
      ? 7.5
      : resolvedHookFocusPoint.y <= 42
        ? 9.25
        : resolvedHookFocusPoint.y <= 56
          ? 11.25
          : 12.75;
  const hookBannerTemplateOffset = isFreezeTextTemplate ? 1.15 : isZoomFocusTemplate ? 0.55 : -0.35;
  const hookBannerSubjectOffset =
    hookBannerSubjectType === "face" ? -0.45 : hookBannerSubjectType === "object" ? 0.3 : 0;
  const hookBannerTop = `${clampNumber(
    hookBannerBaseTop + hookBannerTemplateOffset + hookBannerSubjectOffset,
    7,
    16,
    10
  )}%`;
  const hookBannerTextAlign = hookBannerSide === "center" ? "center" : "left";
  const smartCropBackgroundBlur = 0;
  const smartCropBackgroundBrightness = 1;
  const smartCropBackgroundScale = 1;
  const trimAwareDuration = Math.max(0, Number(currentTimelineWindow.duration || 0));
  const trimAwareCurrentTime = clampNumber(
    Number(videoTime || 0) - Number(currentTimelineWindow.start || 0),
    0,
    trimAwareDuration || Number(videoTime || 0),
    0
  );
  const hookSelectionLeft = currentTimelineWindow.duration
    ? (resolvedHookStart / Math.max(0.0001, currentTimelineWindow.duration)) * 100
    : 0;
  const hookSelectionWidth = currentTimelineWindow.duration
    ? ((hookEnd - resolvedHookStart) / Math.max(0.0001, currentTimelineWindow.duration)) * 100
    : 0;
  const hookSuggestionLeft = currentTimelineWindow.duration
    ? ((currentHookSuggestion.startTime || 0) / Math.max(0.0001, currentTimelineWindow.duration)) *
      100
    : 0;
  const hookSuggestionWidth = currentTimelineWindow.duration
    ? (((currentHookSuggestion.endTime || 0) - (currentHookSuggestion.startTime || 0)) /
        Math.max(0.0001, currentTimelineWindow.duration)) *
      100
    : 0;
  const hookPlayheadLeft = currentTimelineWindow.duration
    ? (trimAwareCurrentTime / Math.max(0.0001, currentTimelineWindow.duration)) * 100
    : 0;
  // hookEnd/resolvedHookStart bound the current source's preview window.
  // Navigating or playing a different occurrence must not write those derived
  // bounds back into authored hook settings, history or the render revision.
  // Explicit hook edits use setHookSegmentRange to clamp and persist the range.

  useEffect(() => {
    setHookSuggestedRange(null);
    hookSuggestionCycleRef.current = 0;
    setHookAnalysisStatus("idle");
    setHookAnalysisMessage("");
    setHookSelectionMode(false);
    setHookPickMode(false);
    setHookFocusMode(false);
    hookPreviewSequenceRef.current = { active: false };
  }, [activeTimelineIndex]);

  useEffect(() => {
    const handlePointerMove = event => {
      const drag = hookSelectionDragRef.current;
      const playheadDrag = hookPlayheadDragRef.current;

      if (playheadDrag && currentTimelineWindow.duration) {
        const trackWidth = Math.max(1, playheadDrag.rect.width);
        const deltaRatio = (event.clientX - playheadDrag.startClientX) / trackWidth;
        const deltaTime = deltaRatio * Number(currentTimelineWindow.duration || 0);
        seekHookTimelineTime(playheadDrag.anchorTime + deltaTime);
        return;
      }

      if (!drag || !currentTimelineWindow.duration) return;

      const trackWidth = Math.max(1, drag.rect.width);
      const deltaRatio = (event.clientX - drag.startClientX) / trackWidth;
      const deltaTime = deltaRatio * Number(currentTimelineWindow.duration || 0);

      if (drag.target === "start") {
        setHookSegmentRange(drag.anchorStart + deltaTime, drag.anchorEnd);
        return;
      }

      if (drag.target === "end") {
        setHookSegmentRange(drag.anchorStart, drag.anchorEnd + deltaTime);
        return;
      }

      const rangeDuration = Math.max(hookMinDuration, drag.anchorEnd - drag.anchorStart);
      const boundedStart = clampNumber(
        drag.anchorStart + deltaTime,
        0,
        Math.max(0, Number(currentTimelineWindow.duration || 0) - rangeDuration),
        drag.anchorStart
      );
      setHookSegmentRange(boundedStart, boundedStart + rangeDuration);
    };

    const handlePointerUp = () => {
      hookSelectionDragRef.current = null;
      hookPlayheadDragRef.current = null;
    };

    window.addEventListener("mousemove", handlePointerMove);
    window.addEventListener("mouseup", handlePointerUp);

    return () => {
      window.removeEventListener("mousemove", handlePointerMove);
      window.removeEventListener("mouseup", handlePointerUp);
    };
  }, [currentTimelineWindow.duration, hookMinDuration]);

  const presetMusicPreviewUrl =
    addMusic && !musicSearchMode && !musicTrack?.url && musicSelection
      ? `/music/${musicSelection}`
      : "";
  const effectiveMusicPreviewUrl =
    musicTrack?.url || (musicSearchMode ? musicPreviewUrl : presetMusicPreviewUrl);
  const faceAnchorY =
    faceAnchorPreset === "face_top" ? 32 : faceAnchorPreset === "face_mid" ? 42 : 50;
  const safeObjectPosition = `${resolvedHookFocusPoint.x}% ${faceAnchorY}%`;
  const sideBySideObjectPosition = `${resolvedHookFocusPoint.x}% ${clampNumber(
    resolvedHookFocusPoint.y,
    24,
    58,
    42
  )}%`;
  const effectiveVideoFit = smartCrop || reframeModeCuts.length
    ? ["speaker_track", "group_stack"].includes(effectiveSmartCropMode) ? "cover" : "contain"
    : safeFaceFraming ? "contain" : videoFit;
  const usesPanelFraming = effectiveSmartCrop && !renderedOutputUrl &&
    (effectiveSmartCropMode === "group_stack" || (effectiveSmartCropMode === "center" && reframeAspect !== "16:9"));
  const speakerStackPreviewIsLive =
    effectiveSmartCrop && effectiveSmartCropMode === "group_stack" && speakerStackIsReady;
  const prepareSpeakerStackMedia = !renderedOutputUrl && smartCrop &&
    (smartCropMode === "group_stack" || reframeModeCuts.some(cut => cut.mode === "group_stack"));
  const prepareSourceAlternateMedia = !renderedOutputUrl && (
    reframeModeCuts.some(cut => Math.abs(Number(cut.sourceTimeOffsetSeconds || 0)) > .001) ||
    [speakerStackFraming.top, speakerStackFraming.bottom].some(frame =>
      Math.abs(Number(frame?.sourceTimeOffsetSeconds || frame?.source_time_offset_seconds || 0)) > .001 ||
      (frame?.sourceTimeOffsetKeyframes || frame?.source_time_offset_keyframes || []).some(key =>
        Math.abs(Number(key.offsetSeconds ?? key.offset_seconds ?? key.value ?? 0)) > .001)));
  const activeReframePosition = interpolateReframeKeyframes(reframeKeyframes, previewTimelineTime);
  const activeReframeKeyframe =
    reframeKeyframes.find(keyframe => keyframe.id === activeReframeKeyframeId) || null;
  useEffect(() => {
    const [width, height] = reframeAspect.split(":").map(Number);
    setReframeKeyframes(current => {
      const next = reprojectDetectedReframeKeys(current, width / height, speakerTrackZoom);
      return next.some((mark, index) => Math.abs(mark.x - current[index].x) > .0001 ||
        Math.abs(mark.y - current[index].y) > .0001) ? next : current;
    });
  }, [reframeAspect, speakerTrackZoom]);
  const reframeAspectClass = `reframe-aspect-${String(reframeAspect).replace(":", "-")}`;
  const [previewAspectWidth = 9, previewAspectHeight = 16] = String(reframeAspect)
    .split(":")
    .map(value => Math.max(1, Number(value) || 1));
  const previewAspectRatio = !renderedOutputUrl && smartCrop
    ? previewAspectWidth / previewAspectHeight : programmeMediaAspectRatio;
  // The renderer defines frame inset as a percentage of output width on all
  // four sides. Convert that same physical inset to a CSS height percentage so
  // a portrait preview does not get almost twice the top/bottom margin.
  const mainFrameInsetXPercent = clampNumber(mainFrame.insetPercent, 0, 10, 0);
  const mainFrameInsetYPercent = mainFrameInsetXPercent * previewAspectRatio;
  const previewInnerWidth = Math.max(0.01, 1 - (mainFrameInsetXPercent * 2) / 100);
  const previewInnerHeight = Math.max(
    0.01,
    1 / previewAspectRatio - (mainFrameInsetXPercent * 2) / 100
  );
  const previewInnerAspectRatio = previewInnerWidth / previewInnerHeight;
  const mainFrameRadiusXPercent = clampNumber(mainFrame.radiusPercent, 0, 25, 0);
  const mainFrameRadiusYPercent = mainFrameRadiusXPercent * previewInnerAspectRatio;
  const reframeObjectPosition = `${clampNumber(activeReframePosition.x, 5, 95, 50)}% ${clampNumber(
    activeReframePosition.y,
    8,
    92,
    50
  )}%`;
  const showCropRiskIndicator =
    !safeFaceFraming &&
    (effectiveVideoFit === "cover" || effectiveSmartCrop || hookFreezeFrame || isZoomFocusTemplate);
  // The Finish Rack grade is already applied to the programme canvas. Extra
  // clarity here made the preview brighter and more saturated than export.
  const previewClarityBrightness = 1;
  const previewClarityContrast = 1;
  const previewClaritySaturate = 1;
  const previewClarityHalo = " drop-shadow(0 0 0.45px rgba(255, 255, 255, 0.34))";
  const currentMusicLabel = musicSearchMode
    ? musicSelection || "Search query"
    : musicSelection
      ? musicSelection.replace(/\.mp3$/i, "").replace(/_/g, " ")
      : "None";
  const activeSoundEffect = soundEffects.find(effect => effect.id === activeSoundEffectId) || null;
  const musicPreviewStatusLabel =
    musicPreviewStatus === "processing"
      ? "Processing"
      : musicPreviewStatus === "ready"
        ? "Ready"
        : musicPreviewStatus === "failed"
          ? "Failed"
          : "Idle";
  const watermarkPreviewRegions = removeWatermark ? getWatermarkPreviewRegions(watermarkMode) : [];
  const resolvedWatermarkPreviewRegions =
    removeWatermark && watermarkMode === "manual"
      ? manualWatermarkRegions.map(region => ({
          ...region,
          style: toWatermarkPreviewStyle(region),
          isManual: true,
        }))
      : watermarkPreviewRegions.map(region => ({
          ...region,
          style: {
            ...region,
            "--cleanup-rotation": `${Number(region.rotation || 0)}deg`,
            "--cleanup-opacity": Number(region.opacity || 0.88),
          },
          isManual: false,
        }));
  const isWatermarkCleanupPreviewFrameAligned =
    !!watermarkCleanupPreview &&
    watermarkCleanupPreview.clipId === currentTimelineClip?.id &&
    Math.abs(Number(videoTime || 0) - Number(watermarkCleanupPreview.previewTime || 0)) <= 0.2;
  const shouldShowWatermarkCleanupOnVideo =
    !!watermarkCleanupPreview?.cleanedImageUrl &&
    showWatermarkCleanupOnVideo &&
    isPreviewPaused &&
    isWatermarkCleanupPreviewFrameAligned;
  const normalizedCaptionOverride = normalizePlainText(captionTextOverride);
  const normalizedTimedCaptionSegments = normalizeCaptionSegments(captionSegments);
  const currentCaptionSourceId = currentTimelineClip?.sourceClipId || currentTimelineClip?.id;
  const currentSourceCaptionSegments = normalizedTimedCaptionSegments.filter(segment =>
    !segment.sourceClipId || !currentCaptionSourceId ||
    String(segment.sourceClipId) === String(currentCaptionSourceId));
  const captionPreviewSourceText =
    normalizedCaptionOverride || getCaptionPreviewSourceText(selectedClip) || "";
  const manualCaptionChunkCount = buildCaptionPreviewChunks(normalizedCaptionOverride).length;
  const manualCaptionPreviewDuration = Math.min(
    Math.max(1.8, outputTimelineDuration || 1.8),
    Math.max(2.4, manualCaptionChunkCount * 1.35)
  );
  const captionTrackOffset = normalizedCaptionOverride ? 0 : currentTimelineOffset;
  const captionTrackDuration = normalizedCaptionOverride
    ? manualCaptionPreviewDuration
    : currentTimelineWindow.duration || selectedClip?.duration || 3;
  const resolvedCaptionPreviewState =
    !normalizedCaptionOverride && normalizedTimedCaptionSegments.length
      ? getTimedCaptionPreviewState({
          segments: currentSourceCaptionSegments,
          sourceTime: videoTime,
          clipStartTime: currentTimelineWindow.start || selectedClip?.start || 0,
          styleName: captionStyle,
          captionScale,
        })
      : getCaptionPreviewState({
          text: captionPreviewSourceText,
          localTime: normalizedCaptionOverride ? previewTimelineTime : previewClipTime,
          duration: captionTrackDuration,
          hideAfterEnd: !!normalizedCaptionOverride,
        });
  const captionPreviewState = resolvedCaptionPreviewState;
  const captionPreviewSegmentIndex = captionPreviewState.currentChunk
    ? normalizedTimedCaptionSegments.findIndex(
        segment => segment.id === captionPreviewState.currentChunk.id
      )
    : -1;
  const captionStoryPreviewTreatment =
    captionPreviewSegmentIndex >= 0
      ? resolveCaptionStoryPreviewTreatment(captionPreviewState.currentChunk, captionPosition)
      : null;
  const captionPreviewTimeMap = previewOutputTimeMap;
  const captionRenderPreviewStyles = getCaptionPreviewStyles(
    captionStyle, captionScale, captionStoryPreviewTreatment?.accent || "#ffb33d",
    { hasIcon: !!captionStoryPreviewTreatment?.icon,
      chunk: captionPreviewState.currentChunk,
      time: normalizedCaptionOverride ? previewTimelineTime : videoTime,
      outputClock: getCaptionPreviewOutputClock({
        chunk: captionPreviewState.currentChunk,
        time: normalizedCaptionOverride ? previewTimelineTime : videoTime,
        timeMap: captionPreviewTimeMap,
        sourceStart: normalizedCaptionOverride ? 0 : currentTimelineWindow.start,
        sourceEnd: normalizedCaptionOverride ? outputTimelineDuration : currentTimelineWindow.end,
        programmeStart: normalizedCaptionOverride ? 0 : currentTimelineOffset,
      }) }
  );
  const liveTimelineDuration = outputTimelineDuration;
  finishProgrammeClockRef.current = {
    currentTime: previewTimelineTime,
    duration: liveTimelineDuration,
  };
  const groupClipRows = studioGroupTimingRows({ compounds: effectiveCompoundClips, order: compositionIds,
    overlays, motionScenes, threeDScenes, duration: liveTimelineDuration, trackStates });
  const selectedGroupClip = groupClipRows.find(group => group.id === selectedGroupId);
  const liveTimelineCutMarkers = timeline.slice(0, -1).flatMap((clip, index) => {
    const nextClip = timeline[index + 1];
    const currentWindow = getTimelineClipWindow(clip);
    const nextWindow = getTimelineClipWindow(nextClip);
    const sameSource =
      (clip.sourceClipId && clip.sourceClipId === nextClip?.sourceClipId) ||
      (!!clip.url && clip.url === nextClip?.url);
    const removedDuration = Number(nextWindow.start || 0) - Number(currentWindow.end || 0);
    if (!sameSource || removedDuration < 0.05) return [];
    return [
      {
        id: `cut-${clip.id}-${nextClip.id}`,
        outputTime: getTimelineOffsetForIndex(index + 1),
        removedDuration,
      },
    ];
  });
  const liveTimelineBRoll = overlays.filter(
    overlay =>
      (overlay.bRollMode || overlay.type === 'image' || overlay.type === 'video') &&
      overlay.startTime !== undefined && Number(overlay.duration || 0) > 0
  );
  const fallbackTimelineCaptionDuration = captionPreviewState.chunks.length
    ? Math.max(0.1, Number(captionTrackDuration || 0)) / captionPreviewState.chunks.length
    : 0;
  const liveTimelineCaptionBlocks =
    !normalizedCaptionOverride && normalizedTimedCaptionSegments.length
      ? timeline.flatMap((clip, clipIndex) => {
          const clipWindow = getTimelineClipWindow(clip);
          const clipOffset = getTimelineOffsetForIndex(clipIndex);
          const clipStart = Number(clipWindow.start || 0);
          const clipEnd = Number(clipWindow.end || 0);
          const clipDuration = Math.max(0.1, clipEnd - clipStart);
          const captionsUseSourceTime = normalizedTimedCaptionSegments.some(segment =>
            (!segment.sourceClipId || String(segment.sourceClipId) === String(clip.sourceClipId || clip.id)) &&
            segment.end > clipStart && segment.start < clipEnd);
          return normalizedTimedCaptionSegments.flatMap(segment => {
            if (segment.sourceClipId &&
                String(segment.sourceClipId) !== String(clip.sourceClipId || clip.id)) return [];
            let outputStart = 0;
            let outputDuration = 0;

            if (captionsUseSourceTime && segment.end > clipStart && segment.start < clipEnd) {
              // Segment is in absolute source timestamps
              const visibleStart = Math.max(segment.start, clipStart);
              const visibleEnd = Math.min(segment.end, clipEnd);
              if (visibleEnd <= visibleStart) return [];
              outputStart = clipOffset + (visibleStart - clipStart);
              outputDuration = visibleEnd - visibleStart;
            } else if (!captionsUseSourceTime && clipStart > 0 && segment.start < clipStart && segment.start < clipDuration) {
              // Segment is in clip-relative timestamps
              const visibleStart = Math.max(0, segment.start);
              const visibleEnd = Math.min(clipDuration, segment.end);
              if (visibleEnd <= visibleStart) return [];
              outputStart = clipOffset + visibleStart;
              outputDuration = visibleEnd - visibleStart;
            } else if (clipStart === 0 && segment.start < clipEnd) {
              const visibleStart = Math.max(0, segment.start);
              const visibleEnd = Math.min(clipEnd, segment.end);
              if (visibleEnd <= visibleStart) return [];
              outputStart = clipOffset + visibleStart;
              outputDuration = visibleEnd - visibleStart;
            } else {
              return [];
            }

            return [
              {
                ...segment,
                blockId: `${clip.id}-${segment.id}`,
                outputStart,
                outputDuration,
              },
            ];
          });
        })
      : captionPreviewState.chunks.map((chunk, index) => ({
          ...chunk,
          blockId: chunk.id,
          outputStart: captionTrackOffset + index * fallbackTimelineCaptionDuration,
          outputDuration: fallbackTimelineCaptionDuration,
        }));
  const liveTimelineSource = getSafeMediaSource(currentTimelineClip?.url || videoUrl);
  const liveTimelinePlayheadLeft =
    (clampNumber(previewTimelineTime, 0, liveTimelineDuration, 0) / liveTimelineDuration) * 100;
  const liveTimelineFilmstripFrames = renderedOutputUrl ? Array.from({length:10},(_,index)=>({
    id:`finished-filmstrip-${index}`,src:renderedOutputUrl,
    previewTime:ticksToSeconds(mapProgrammeTickToOutputTick(previewOutputTimeMap,
      secondsToTicks(liveTimelineDuration*(index+.5)/10)) || 0),left:index*10,width:10,isJoin:false,
  })) : timeline.flatMap((clip, clipIndex) => {
    const window = getTimelineClipWindow(clip);
    const duration = Math.max(0, Number(window.duration || 0));
    const source = getSafeMediaSource(clip.url);
    if (!duration || !source) return [];
    const clipOffset = getTimelineOffsetForIndex(clipIndex);
    const frameCount = Math.max(1, Math.min(12, Math.round((duration / liveTimelineDuration) * 10)));
    return Array.from({ length: frameCount }, (_, frameIndex) => ({
      id: `${clip.id}-filmstrip-${frameIndex}`,
      src: source,
      previewTime: Number(window.start || 0) + ((frameIndex + 0.5) / frameCount) * duration,
      left: ((clipOffset + (frameIndex / frameCount) * duration) / liveTimelineDuration) * 100,
      width: (duration / frameCount / liveTimelineDuration) * 100,
      isJoin: frameIndex === 0 && clipIndex > 0,
    }));
  });
  const activeSideBySideOverlay = overlays.find(overlay => {
    const start = Number(overlay.startTime ?? overlay.start_time ?? -1);
    const end = start + getOverlayVisibleDuration(overlay);
    return (
      overlay.bRollMode === "sideBySide" &&
      previewTimelineTime >= start &&
      previewTimelineTime < end
    );
  });
  const previewProgrammeFit = renderedOutputUrl
    ? "contain"
    : activeSideBySideOverlay
      ? "cover"
      : effectiveVideoFit;
  // Prepare two cuts ahead with three bounded native decoders. A half-second
  // insert must not be the entire seek budget for the following source. Complex camera/audio
  // modes retain their established programme element and synchronization.
  // Speech analysis follows the active native element. Each bounded slot owns
  // its reusable Web Audio graph, so original dialogue also keeps ready cuts.
  const sourceSequenceDoubleBuffer = timeline.length > 1 && !renderedOutputUrl &&
    !speakerStackPreviewIsLive && !directorModeActive;
  const activeProgrammeSlot = sourceSequenceDoubleBuffer ? activeTimelineIndex % 3 : 0;
  useLayoutEffect(() => {
    const previous = videoRef.current;
    const next = programmeVideoSlotsRef.current[activeProgrammeSlot];
    videoRef.current = next;
    if (previous && previous !== next) { previous.pause(); previous.muted = true; }
  }, [activeProgrammeSlot]);
  const previewProgrammePosition = renderedOutputUrl
    ? "center center"
    : activeSideBySideOverlay
      ? sideBySideObjectPosition
      : smartCrop || reframeModeCuts.length
        ? effectiveSmartCropMode === "speaker_track"
          ? reframeObjectPosition
          : effectiveSmartCropMode === "group_stack"
            ? "center center"
          : "center center"
        : safeObjectPosition;
  const previewProgrammeFilter = renderedOutputUrl
    ? "none"
    : `blur(${(hookVideoBlur + smartCropBackgroundBlur).toFixed(2)}px) brightness(${(hookVideoBrightness * smartCropBackgroundBrightness * previewClarityBrightness).toFixed(3)}) contrast(${(hookVideoContrast * previewClarityContrast).toFixed(3)}) saturate(${(hookVideoSaturate * previewClaritySaturate).toFixed(3)})${finishFilter ? ` ${finishFilter}` : ""}`;
  const liveTimelineEditCount =
    Number(addHook) +
    Number(creativeEffectsEnabled) +
    Number(autoCaptions) +
    Number(previewSpeed !== 1 || silenceRemoval) +
    liveTimelineCutMarkers.length +
    liveTimelineBRoll.length +
    soundEffects.length +
    Number(addMusic || muteOriginalAudio);
  const retentionScore = clampNumber(
    54 +
      (addHook ? 9 : 0) +
      (autoCaptions ? 8 : 0) +
      (silenceRemoval ? 7 : 0) +
      (smartCrop ? 5 : 0) +
      (overlays.some(
        overlay =>
          overlay.bRollMode &&
          !overlay.bRollPlaceholder &&
          ["video", "image"].includes(overlay.type)
      )
        ? 6
        : 0) +
      (addMusic ? 4 : 0) +
      (hookPreviewLoop ? 4 : 0),
    0,
    97,
    54
  );
  const creativeImprovementsReady = [
    !autoCaptions && "animated captions",
    !silenceRemoval && "tighter pauses",
    !smartCrop && "face-safe framing",
    !overlays.some(
      overlay =>
        overlay.bRollMode && !overlay.bRollPlaceholder && ["video", "image"].includes(overlay.type)
    ) && "proof B-roll",
    !addMusic && "background energy",
    !hookPreviewLoop && "loop ending",
  ].filter(Boolean);
  const audioModeSummary =
    currentAudioMode === "replace"
      ? "Donor track replaces original audio"
      : currentAudioMode === "duck_original"
        ? "Donor track leads while original audio ducks"
        : "Donor track mixes with original audio";

  const getComparisonFocusSourceTime = preferredTool => {
    const timedBRoll =
      (activeOverlayIsVideoBRoll && activeOverlay) ||
      overlays.find(
        overlay =>
          overlay.bRollMode && overlay.startTime !== undefined && Number(overlay.duration || 0) > 0
      );
    const shouldFocusBRoll = preferredTool === "broll" && timedBRoll;
    const outputFocusTime = shouldFocusBRoll
      ? Number(timedBRoll.startTime || 0)
      : addHook || preferredTool === "hook"
        ? resolvedHookStart + Math.min(0.35, hookDuration * 0.15)
        : timedBRoll
          ? Number(timedBRoll.startTime || 0) + 0.2
          : currentTimelineOffset;
    return resolveOutputTimelinePosition(outputFocusTime);
  };

  const focusComparisonPreview = (preferredTool = studioInspectorTab, play = false) => {
    const afterVideo = videoRef.current;
    const beforeVideo = beforeVideoRef.current;
    if (!afterVideo) return;

    const target = getComparisonFocusSourceTime(preferredTool);
    if (!target) return;
    afterVideo.pause();
    beforeVideo?.pause();
    setActiveTimelineIndex(target.index);
    try {
      const afterSource = renderedOutputUrl || target.clip?.url;
      if (afterSource && afterVideo.src !== afterSource) {
        applySafeMediaSource(afterVideo, afterSource);
      }
      afterVideo.currentTime = renderedOutputUrl
        ? ticksToSeconds(mapProgrammeTickToOutputTick(previewOutputTimeMap,
            Math.min(previewOutputTimeMap.at(-1)?.programmeRange.endTick || 0,
              secondsToTicks(Math.max(0, Number(target.outputTime || 0))))) || 0)
        : target.sourceTime;
      if (beforeVideo) {
        applySafeMediaSource(beforeVideo, target.clip?.url);
        beforeVideo.currentTime = target.sourceTime;
      }
      setVideoTime(target.sourceTime);
    } catch (error) {
      console.log("Comparison preview seek skipped", error);
    }
    if (play) safePlayMediaElement(afterVideo);
  };

  const toggleComparisonPlayback = async ({ resumeOnly = false } = {}) => {
    const playRequest = ++previewPlaybackStartRef.current;
    // Pause must remain available while owned media links are being refreshed.
    if (!resumeOnly && !isPreviewPaused) {
      pauseSynchronizedPreview();
      return;
    }
    if (projectSourcesRefreshing) {
      setStudioActionMessage("Reopening saved media. Playback will be available when source links are ready.");
      return;
    }
    const video = videoRef.current;
    if (!video) return;
    const windowEnd = Number(currentTimelineWindow.end || video.duration || 0);
    if (resumeOnly || isPreviewPaused) {
      if (!renderedOutputUrl) {
        const expectedProject = activeProjectIdRef.current, expectedSeek = programmeSeekIntentRef.current.version;
        try { const pendingVoice = preparedVoiceBusRef.current?.prepare(); if (pendingVoice) await pendingVoice; }
        catch (error) { if (playRequest === previewPlaybackStartRef.current) setStudioActionMessage(error.message); return; }
        if (playRequest !== previewPlaybackStartRef.current || video !== videoRef.current ||
            expectedProject !== activeProjectIdRef.current || expectedSeek !== programmeSeekIntentRef.current.version) return;
      }
      if (!renderedOutputUrl && addMusic && !musicSearchMode && effectiveMusicPreviewUrl) {
        const expectedProject = activeProjectIdRef.current;
        const expectedSeek = programmeSeekIntentRef.current.version;
        const expectedMusic = new URL(getSafeMediaSource(effectiveMusicPreviewUrl), window.location.href).href;
        previewPlaybackIntentRef.current = false;
        try {
          if (musicPreviewRef.current?.readyState < 3) setStudioActionMessage("Loading background sound before playback…");
          await prepareStudioMusicPlayback({ media: musicPreviewRef.current,
            decodePromise: musicPreviewDecodePromiseRef.current,
            hasDecodedBuffer: () => Boolean(musicPreviewBufferRef.current &&
              musicPreviewBufferUrlRef.current === effectiveMusicPreviewUrl) });
        } catch (error) {
          if (playRequest === previewPlaybackStartRef.current) setStudioActionMessage(error.message);
          return;
        }
        if (playRequest !== previewPlaybackStartRef.current || video !== videoRef.current ||
            expectedProject !== activeProjectIdRef.current || expectedSeek !== programmeSeekIntentRef.current.version ||
            musicPreviewRef.current?.src !== expectedMusic) return;
      }
      if (windowEnd > 0 && Number(video.currentTime || 0) >= windowEnd - 0.1) {
        focusComparisonPreview(studioInspectorTab, false);
      }
      if (!renderedOutputUrl && !previewMuted) {
        const expectedProject = activeProjectIdRef.current;
        const expectedSeek = programmeSeekIntentRef.current.version;
        const pendingCues = soundEffects.filter(effect => effect.enabled !== false)
          .map(effect => soundEffectDecodePromisesRef.current.get(studioCueSourceKey(expectedProject, effect)))
          .filter(Boolean);
        if (pendingCues.length) {
          previewPlaybackPreparingRef.current = playRequest;
          previewPlaybackIntentRef.current = true;
          setIsPreviewPaused(false);
          await prepareStudioCuePlayback(pendingCues);
          if (previewPlaybackPreparingRef.current === playRequest) previewPlaybackPreparingRef.current = null;
          if (playRequest !== previewPlaybackStartRef.current || !previewPlaybackIntentRef.current ||
              expectedProject !== activeProjectIdRef.current || expectedSeek !== programmeSeekIntentRef.current.version ||
              video !== videoRef.current) return;
        }
        const contexts = [];
        if (addMusic && musicPreviewBufferRef.current) contexts.push(ensureMusicPreviewAudioContext());
        if (soundEffects.some(effect => effect.enabled !== false && (effect.builtIn ||
            soundEffectDecodedSourcesRef.current.has(studioCueSourceKey(activeProjectIdRef.current, effect)))) &&
            isStudioAudioTrackAudible(trackStates, "sfx")) contexts.push(ensureSoundEffectAudioContext());
        if (isStudioAudioTrackAudible(trackStates,"sfx")) {
          const cueContext = soundEffectAudioContextRef.current;
          for (const effect of soundEffects.filter(effect => effect.builtIn && effect.enabled !== false))
            prepareStudioBuiltInCue(cueContext,{ ...effect,duration:getSoundEffectDuration(effect) },
              soundEffectBuiltInBuffersRef.current,expectedProject);
        }
        const suspended = [...new Set(contexts)].filter(context => context?.state === "suspended");
        if (suspended.length) {
          // Pause represents transport intent and remains available while the
          // browser prepares its audio threads. No picture advances yet.
          previewPlaybackPreparingRef.current = playRequest;
          previewPlaybackIntentRef.current = true;
          setIsPreviewPaused(false);
          try { await Promise.all(suspended.map(context => context.resume())); }
          catch (error) {
            if (previewPlaybackPreparingRef.current === playRequest) previewPlaybackPreparingRef.current = null;
            if (playRequest === previewPlaybackStartRef.current) {
              pauseSynchronizedPreview(); setStudioActionMessage("Preview audio could not start. Try playback again.");
            }
            return;
          }
          if (previewPlaybackPreparingRef.current === playRequest) previewPlaybackPreparingRef.current = null;
          if (playRequest !== previewPlaybackStartRef.current || !previewPlaybackIntentRef.current ||
              expectedProject !== activeProjectIdRef.current || expectedSeek !== programmeSeekIntentRef.current.version ||
              video !== videoRef.current) return;
        }
      }
      backgroundSoundPreviewSuppressedRef.current = false;
      programmeSoundEffectRoutesRef.current = new Map(soundEffects.map(effect => [effect.id,
        soundEffectDecodedSourcesRef.current.get(studioCueSourceKey(activeProjectIdRef.current, effect)) || null]));
      programmeMusicRouteRef.current = { sourceUrl: effectiveMusicPreviewUrl,
        decoded: Boolean(musicPreviewBufferRef.current && musicPreviewBufferUrlRef.current === effectiveMusicPreviewUrl) };
      isolatedMusicPreviewIntentRef.current = false;
      setIsBackgroundSoundPreviewing(false);
      previewPlaybackIntentRef.current = true;
      setIsPreviewPaused(false);
      safePlayMediaElement(video);
    } else {
      pauseSynchronizedPreview();
    }
  };

  const setPreviewFillMode = mode => {
    // Reframing is painted from the framing track, not the hidden video's
    // object-fit style. Record the same fit/crop decision that export reads.
    if (smartCrop || reframeModeCuts.length) {
      const framingMode = mode === "cover" ? "speaker_track" : "off";
      setSmartCrop(true);
      // Keep the last enabled mode so Enable Auto Reframe can restore it.
      if (framingMode !== "off") setSmartCropMode(framingMode);
      recordReframeModeAtPlayhead(framingMode);
    }
    setComparisonMode("after");
    if (mode === "cover") {
      setSafeFaceFraming(false);
      setVideoFit("cover");
      setStudioActionMessage(
        "Fill canvas starts here on the framing track. Adjust the focal point if the crop hides something important."
      );
      return;
    }
    setVideoFit("contain");
    setSafeFaceFraming(true);
    setStudioActionMessage("Fit full starts here on the framing track. The entire source stays visible in preview and export.");
  };

  const recordReframeModeAtPlayhead = mode => {
    // A rounded-up millisecond is after a fractional paused source frame.
    // Start the decision on this frame, rather than waiting for playback.
    const time = Math.floor(Math.max(0, previewTimelineTime) * 1000) / 1000;
    setReframeModeCuts(current => {
      const normalized = normalizeReframeModeCuts(current);
      const baseline =
        !normalized.length && time > 0.08
          ? [{
              id: createSecureId("reframe-mode"),
              time: 0,
              mode: effectiveSmartCropMode,
            }]
          : normalized;
      const existing = baseline.find(cut => Math.abs(cut.time - time) < 0.08);
      return normalizeReframeModeCuts([
        ...baseline.filter(cut => cut.id !== existing?.id),
        {
          id: existing?.id || createSecureId("reframe-mode"),
          time,
          mode,
          ...(existing?.mode === mode && existing.zoom ? { zoom: existing.zoom } : {}),
        },
      ]);
    });
  };

  const setPictureFillForCurrentShot = value => {
    const fill = clampNumber(value, 1, 1.5, 1);
    const time = Number(Math.max(0, previewTimelineTime).toFixed(3));
    setReframeModeCuts(current => {
      const cuts = normalizeReframeModeCuts(current);
      const active = [...cuts].reverse().find(cut => cut.time <= time + 0.001);
      if (active?.mode === "speaker_track") {
        return cuts.map(cut => cut.id === active.id ? { ...cut, zoom: fill } : cut);
      }
      return normalizeReframeModeCuts([
        ...cuts,
        { id: createSecureId("reframe-mode"), time, mode: "speaker_track", zoom: fill },
      ]);
    });
  };

  const showEveryoneSplit = () => {
    // Switching the camera mode is a timeline edit. Keep the creator's
    // reviewed panel crops and alternate-angle timing instead of resetting
    // them every time this shortcut is used during playback. The separately
    // selected output aspect also remains unchanged when switching modes.
    setSmartCrop(true);
    setSmartCropMode("center");
    recordReframeModeAtPlayhead("center");
    setComparisonMode("after");
    selectCreativeTool("reframe");
    setStudioActionMessage(`Show Everyone starts at ${previewTimelineTime.toFixed(2)}s and is recorded on the framing track.`);
  };

  const openSpeakerTracking = () => {
    setSmartCrop(true);
    setSmartCropMode("speaker_track");
    recordReframeModeAtPlayhead("speaker_track");
    setSafeFaceFraming(false);
    setVideoFit("cover");
    setComparisonMode("after");
    selectCreativeTool("reframe");
    setStudioActionMessage(`Solo Speaker starts at ${previewTimelineTime.toFixed(2)}s and is recorded on the framing track.`);
  };

  const updateSplitSpeakerFraming = (slot, changes, addMark = false) => {
    setSpeakerStackFraming(current => {
      const frame = current[slot];
      const tracking = current.trackSpeakers;
      const position = tracking && frame.keyframes?.length
        ? interpolateReframeKeyframes(frame.keyframes, previewTimelineTime) : frame;
      const next = { ...frame, ...changes };
      if (tracking && (addMark || changes.x !== undefined || changes.y !== undefined)) {
        const time = Number(previewTimelineTime.toFixed(3));
        const marks = frame.keyframes?.length ? frame.keyframes : [{ time: 0, x: frame.x, y: frame.y }];
        next.keyframes = [...marks.filter(mark => Math.abs(mark.time - time) > .05),
          { time, x: changes.x ?? position.x, y: changes.y ?? position.y }].sort((a, b) => a.time - b.time);
      }
      return { ...current, [slot]: next };
    });
  };

  const analyzeSpeakerFaces = async (mode = "anchored") => {
    if (faceTrackingStatus === "processing") return;
    if (mode === "source_shots") setLocalDirectorProposal(null);
    const generation = ++faceTrackingGeneration.current;
    const clipId = currentTimelineClip?.id;
    const split = effectiveSmartCropMode === "center" && reframeAspect !== "16:9";
    const window = getTimelineClipWindow(currentTimelineClip);
    if (window.duration > 900) {
      setFaceTrackingMessage("Trim the analysis range to fifteen minutes or less.");
      return;
    }
    setFaceTrackingStatus("processing");
    setFaceTrackingMessage("Detecting real faces. Existing framing stays unchanged until results arrive…");
    try {
      let sourceShotAssetId = null;
      if (mode === "source_shots") {
        const sourceDocument = getEditorSnapshot().studioDocument;
        sourceShotAssetId = sourceDocument?.clipOccurrences.find(
          occurrence => occurrence.occurrenceId === String(clipId)
        )?.assetId || null;
        if (!sourceShotAssetId) {
          throw new Error("Source timing is still loading. Try camera-cut analysis again shortly.");
        }
      }
      const source = getSafeMediaSource(currentTimelineClip?.url || videoUrl);
      const durableStoragePath = currentTimelineClip?.sourceStoragePath ||
        currentTimelineClip?.storagePath ||
        (currentTimelineClip?.id === "main" ? sourceStoragePath : null);
      const useDurableSource = mode === "source_shots" &&
        process.env.REACT_APP_ENABLE_STUDIO_ANALYSIS_JOBS === "true" &&
        typeof durableStoragePath === "string" &&
        durableStoragePath.startsWith("studio/sources/");
      const file = currentTimelineClip?.file || selectedClip?.file;
      let blob = useDurableSource ? null : (file instanceof Blob ? file : null);
      if (!useDurableSource && !blob) {
        const response = await fetch(source);
        if (!response.ok) throw new Error("Source video could not be read.");
        blob = await response.blob();
      }
      const [aspectWidth, aspectHeight] = reframeAspect.split(":").map(Number);
      const sourceAspect = (videoRef.current?.videoWidth || 1920)/(videoRef.current?.videoHeight || 1080);
      const coordinates = soloTrackingCoordinates(activeReframePosition,
        sourceAspect,
        aspectWidth/aspectHeight, speakerTrackZoom);
      const anchors = split ? { top: speakerStackFraming.top, bottom: speakerStackFraming.bottom }
        : { solo: coordinates.anchor };
      let body = null;
      if (!useDurableSource) {
        body = new FormData();
        body.append("file", blob, file?.name || "studio-source.mp4");
        body.append("anchors", JSON.stringify(anchors));
        body.append("start", String(window.start));
        body.append("end", String(window.end));
        body.append("mode", mode);
        if (mode === "source_shots") {
          body.append("projectId", projectId);
          body.append("sourceAssetId", sourceShotAssetId);
        }
      }
      const token = await getMediaAuthToken();
      if (!token) throw new Error("Sign in to analyze this source.");
      let result;
      if (mode === "source_shots" &&
          process.env.REACT_APP_ENABLE_STUDIO_ANALYSIS_JOBS === "true" &&
          (useDurableSource || blob.size <= 100 * 1024 * 1024)) {
        const sourceRequestFields = useDurableSource ? {
          projectId, sourceAssetId: sourceShotAssetId,
          storagePath: durableStoragePath, anchors,
          mode, start: window.start, end: window.end,
        } : null;
        const resumeKey = sourceRequestFields
          ? `studio-analysis-resume:${JSON.stringify(sourceRequestFields)}` : null;
        let requestId = null;
        try { if (resumeKey) requestId = globalThis.sessionStorage.getItem(resumeKey); }
        catch (_) { /* Storage may be disabled in the browser. */ }
        if (!requestId || !/^[A-Za-z0-9][A-Za-z0-9_-]{7,127}$/.test(requestId))
          requestId = createSecureId("studio-analysis");
        try { if (resumeKey) globalThis.sessionStorage.setItem(resumeKey, requestId); }
        catch (_) { /* The job remains usable without local resume state. */ }
        if (body) body.append("requestId", requestId);
        try {
          result = await runStudioSourceShotJob({
            apiBaseUrl: API_BASE_URL, token, formData: body,
            ...(sourceRequestFields ? { sourceRequest: {
              ...sourceRequestFields, requestId,
            } } : {}),
            isCurrent: () => generation === faceTrackingGeneration.current,
            onStatus: status => setFaceTrackingMessage(
              status === "running"
                ? "Analyzing camera cuts. Your current framing stays available while this runs…"
                : "Camera-cut analysis is queued. Your current framing stays available…"
            ),
          });
          try { if (resumeKey) globalThis.sessionStorage.removeItem(resumeKey); }
          catch (_) { /* The result is already available. */ }
        } catch (error) {
          if (resumeKey && (error.terminal || ["STUDIO_ANALYSIS_IDEMPOTENCY_CONFLICT",
            "STUDIO_ANALYSIS_SOURCE_CHANGED"].includes(error.code))) {
            try { globalThis.sessionStorage.removeItem(resumeKey); }
            catch (_) { /* The next submission can still report the conflict. */ }
          }
          throw error;
        }
      } else {
        const response = await fetch(`${API_BASE_URL}/api/media/track-studio-faces`, {
          method: "POST", headers: { Authorization: `Bearer ${token}` }, body,
        });
        result = await response.json();
        if (!response.ok) throw new Error(result.error || result.detail || "Face analysis failed.");
      }
      if (generation !== faceTrackingGeneration.current) return;
      const offset = getTimelineOffsetForIndex(activeTimelineIndex);
      const keys = slot => (result.tracks?.[slot]?.keyframes || []).map(mark => ({ ...(split ? mark : coordinates.toPosition(mark)),
        ...(!split ? { sourceFocus: { x: Number(mark.x), y: Number(mark.y), aspect: sourceAspect } } : {}),
        id: createSecureId("detected-face"), time: offset + mark.time - window.start,
      }));
      const slots = split ? ["top", "bottom"] : ["solo"];
      if (slots.some(slot => keys(slot).length < 2)) throw new Error("Not enough faces detected in each selected region. Adjust anchors or keep manual framing.");
      if (slots.some(slot => result.tracks[slot].coverage < .65)) {
        throw new Error("A face was missing in too many frames. Existing framing is unchanged. Adjust the crop anchors, shorten the analysis range, or edit the follow path manually.");
      }
      if (split) {
        setSpeakerStackFraming(current => ({ ...current, trackSpeakers: true,
          ...Object.fromEntries(slots.map(slot => [slot, { ...current[slot], keyframes: keys(slot) }])),
          analysis: { clipId, engine: result.engine, reviewRequired: true, sceneCuts: result.sceneCuts },
        }));
      } else {
        const rangeStart = offset;
        const rangeEnd = offset + window.duration;
        const detectedKeys = keys("solo");
        setReframeKeyframes(current => [
          ...current.filter(mark => mark.time < rangeStart - .001 || mark.time > rangeEnd + .001),
          ...detectedKeys,
        ].sort((left, right) => left.time - right.time));
        setActiveReframeKeyframeId(null);
        setSafeFaceFraming(false);
        setVideoFit("cover");
        if (mode === "source_shots" && result.editPlan) {
          const plan = result.editPlan;
          const toTimelineTime = value => offset + Number(value || 0) - window.start;
          const plannedCuts = (plan.timelineCuts || []).map(cut => ({
            id: createSecureId("analyzed-shot"),
            time: toTimelineTime(cut.time),
            mode: cut.mode || "speaker_track",
            zoom: clampNumber(cut.zoom, 1, 1.5, 1),
            ...(Math.abs(Number(cut.sourceTimeOffsetSeconds || 0)) > .001
              ? { sourceTimeOffsetSeconds: Number(cut.sourceTimeOffsetSeconds) }
              : {}),
          }));
          const cleanSplits = (plan.splitSuggestions || []).filter(
            suggestion => suggestion.reason === "two_clean_foreground_faces"
          );
          cleanSplits.forEach(suggestion => {
            const activeShot = [...(plan.timelineCuts || [])]
              .filter(cut => Number(cut.time) <= Number(suggestion.end) + 1e-6)
              .sort((left, right) => Number(right.time) - Number(left.time))[0];
            plannedCuts.push({
              id: createSecureId("director-split"),
              time: toTimelineTime(suggestion.start),
              mode: "center",
              zoom: 1,
            }, {
              id: createSecureId("director-solo"),
              time: toTimelineTime(suggestion.end),
              mode: "speaker_track",
              zoom: clampNumber(activeShot?.zoom, 1, 1.5, 1),
            });
          });
          setReframeModeCuts(current => normalizeReframeModeCuts([
            ...current.filter(cut => cut.time < rangeStart - .001 || cut.time > rangeEnd + .001),
            ...plannedCuts,
          ]));
          if (cleanSplits.length) {
            const first = cleanSplits[0];
            setSpeakerStackFraming(current => ({
              ...current,
              trackSpeakers: true,
              roundedCards: true,
              cardInsetPercent: 2.5,
              cardGapPercent: 2.5,
              cardRadiusPercent: 8,
              top: {
                ...current.top,
                ...first.top,
                keyframes: cleanSplits.map(suggestion => ({
                  time: toTimelineTime(suggestion.start),
                  x: Number(suggestion.top?.x ?? first.top?.x ?? 50),
                  y: Number(suggestion.top?.y ?? first.top?.y ?? 50),
                  source_visible_top_percent: Number(suggestion.top?.source_visible_top_percent || 0),
                  source_visible_bottom_percent: Number(suggestion.top?.source_visible_bottom_percent || 0),
                  cut: true,
                })),
              },
              bottom: {
                ...current.bottom,
                ...first.bottom,
                keyframes: cleanSplits.map(suggestion => ({
                  time: toTimelineTime(suggestion.start),
                  x: Number(suggestion.bottom?.x ?? first.bottom?.x ?? 50),
                  y: Number(suggestion.bottom?.y ?? first.bottom?.y ?? 50),
                  source_visible_top_percent: Number(suggestion.bottom?.source_visible_top_percent || 0),
                  source_visible_bottom_percent: Number(suggestion.bottom?.source_visible_bottom_percent || 0),
                  cut: true,
                })),
                source_time_offset_keyframes: cleanSplits.map(suggestion => ({
                  time: toTimelineTime(suggestion.start),
                  offset_seconds: Number(suggestion.bottom?.sourceTimeOffsetSeconds || 0),
                })),
              },
            }));
            setSpeakerFocusCuts(current => normalizeSpeakerFocusCuts([
              ...current.filter(cut => cut.time < rangeStart - .001 || cut.time > rangeEnd + .001),
              ...cleanSplits.map((suggestion, index) => ({
                id: createSecureId("director-order"),
                time: toTimelineTime(suggestion.start),
                slot: index % 2 ? "bottom" : "top",
              })),
            ]));
          }
          const placementCuts = [...(plan.captionPlacementCuts || [])]
            .sort((left, right) => Number(left.time || 0) - Number(right.time || 0));
          if (placementCuts.length) {
            setCaptionPosition(placementCuts[0].placement || "bottom_center");
            setCaptionSegments(current => current.flatMap(segment => {
              const existing = String(segment.captionPlacement || "auto");
              if (existing !== "auto" && existing) return segment;
              const segmentStart = Number(segment.start || 0);
              const segmentEnd = Number(segment.end || segment.start || 0);
              // A caption cue often straddles the short Director beat. Split
              // it at those exact boundaries so the preview and export move
              // the same cue with the programme speaker at the cut itself.
              const boundaries = [segmentStart, segmentEnd];
              cleanSplits.forEach(suggestion => {
                [Number(suggestion.start), Number(suggestion.end)].forEach(boundary => {
                  if (segmentStart < boundary && boundary < segmentEnd) boundaries.push(boundary);
                });
              });
              const orderedBoundaries = [...new Set(boundaries)].sort((left, right) => left - right);
              return orderedBoundaries.slice(0, -1).map((pieceStart, index) => {
                const pieceEnd = orderedBoundaries[index + 1];
                const midpoint = (pieceStart + pieceEnd) / 2;
                const insideSplit = cleanSplits.some(suggestion =>
                  midpoint >= Number(suggestion.start || 0) && midpoint < Number(suggestion.end || 0)
                );
                const active = placementCuts.reduce(
                  (found, cut) => Number(cut.time || 0) <= midpoint + .001 ? cut : found,
                  placementCuts[0]
                );
                return {
                  ...segment,
                  id: orderedBoundaries.length > 2 ? `${segment.id}-layout-${index + 1}` : segment.id,
                  start: pieceStart,
                  end: pieceEnd,
                  duration: pieceEnd - pieceStart,
                  captionPlacement: insideSplit ? "custom" : active.placement || "bottom_center",
                  ...(insideSplit ? { captionX: 50, captionY: 40 } : {}),
                };
              });
            }));
          }
          setMainFrame(current => normalizeMainFrame({
            ...current,
            enabled: plan.mainFrame ? plan.mainFrame.enabled !== false : current.enabled,
            insetPercent: Number(plan.mainFrame?.insetPercent ?? 2.5),
            radiusPercent: Number(plan.mainFrame?.radiusPercent ?? 10),
            background: plan.mainFrame?.background || "studio_black",
          }));
          const naturalGrade = CINEMATIC_PRESETS.find(preset => preset.id === "studio_natural");
          if (naturalGrade) applyFinishPreset(naturalGrade);
        }
      }
      if (mode === "source_shots" && !split) {
        try {
          const snapshot = getEditorSnapshot();
          const request = snapshot.studioDocument && buildSourceShotDirectorSplitRequest({
            document: snapshot.studioDocument,
            occurrenceId: String(clipId),
            analysis: result,
            preferredSourceTick: secondsToTicks(Number(videoTime || window.start || 0)),
            proposalId: createSecureId("source-shot-proposal"),
            idempotencyKey: createSecureId("source-shot-command"),
          });
          setLocalDirectorProposal(request ? { projectId, request } : null);
        } catch (proposalError) {
          setLocalDirectorProposal(null);
          console.warn("Source-shot analysis had no reviewable clip boundary", proposalError);
        }
      }
      const coverage = slots.map(slot => `${slot}: ${Math.round((result.tracks[slot].coverage || 0)*100)}%`).join(" · ");
      setFaceTrackingStatus("ready");
      const editPlan = result.editPlan;
      setFaceTrackingMessage(mode === "source_shots"
        ? editPlan
          ? `Podcast layout draft applied (${coverage}; ${result.sceneCuts?.length || 0} camera cuts; ${editPlan.splitSuggestions?.length || 0} two-person layout suggestions). Picture fill, face-safe captions, Studio Natural grade and one rounded frame now match preview and export. Review marked cuts. Any proposed clip split below needs separate approval.`
          : `Source-shot draft applied (${coverage}; ${result.sceneCuts?.length || 0} cuts). Review every cut. Any proposed clip split below needs separate approval.`
        : `Face-follow draft applied (${coverage}). Review camera cuts and missed faces. This detects faces, not who is speaking.`);
    } catch (error) {
      if (generation !== faceTrackingGeneration.current) return;
      setFaceTrackingStatus("error");
      setFaceTrackingMessage(error.message || "Tracking failed; keep the existing framing.");
    }
  };

  useEffect(() => {
    faceTrackingGeneration.current += 1;
    setFaceTrackingStatus("idle");
    setFaceTrackingMessage("");
    return () => { faceTrackingGeneration.current += 1; };
  }, [currentTimelineClip?.id, currentTimelineClip?.startRequest, currentTimelineClip?.endRequest,
    selectedClip?.start, selectedClip?.end, effectiveSmartCropMode, reframeAspect, speakerTrackZoom]);

  const focusCaptionSegmentsForReview = segments => {
    const normalizedSegments = normalizeCaptionSegments(segments);
    const target = timeline.reduce((match, clip, index) => {
      if (match) return match;
      const clipWindow = getTimelineClipWindow(clip);
      const clipSourceId = clip.sourceClipId || clip.id || null;
      const isInsideClipWindow = segment =>
        segment.end > clipWindow.start && segment.start < clipWindow.end;
      const caption =
        normalizedSegments.find(segment => {
          if (
            segment.sourceClipId &&
            clipSourceId &&
            String(segment.sourceClipId) !== String(clipSourceId)
          ) {
            return false;
          }
          return isInsideClipWindow(segment);
        }) || normalizedSegments.find(isInsideClipWindow);
      if (!caption) return null;
      return {
        index,
        clip,
        sourceTime: Math.max(caption.start, Number(clipWindow.start || 0)),
      };
    }, null);

    if (!target) return;
    previewPlaybackIntentRef.current = false;
    if (target.index !== activeTimelineIndex) {
      pendingTimelineSeekRef.current = {
        index: target.index,
        sourceTime: target.sourceTime,
        play: false,
      };
    }
    setActiveTimelineIndex(target.index);
    window.setTimeout(() => {
      const video = videoRef.current;
      if (!video) return;
      video.pause();
      beforeVideoRef.current?.pause();
      applySafeMediaSource(video, target.clip.url);
      video.currentTime = target.sourceTime;
      setVideoTime(target.sourceTime);
    }, 0);
  };

  const activateCaptionScriptDraft = (script, sourceLabel = "your text") => {
    const sourceClipId =
      currentTimelineClip?.sourceClipId || currentTimelineClip?.id || selectedClip?.id || null;
    try {
      const windowStart = Number(currentTimelineWindow.start || 0);
      const windowDuration = Number(currentTimelineWindow.duration || selectedClip?.duration || 0);
      const draft = buildTimedScriptDraft(script, {
        start: windowStart,
        duration: windowDuration,
      });
      if (!draft.length) throw new Error("Enter lyrics or a script before creating timed lines.");
      const language = normalizeCaptionLanguage(captionDraftLanguage);
      const speaker = normalizeCaptionSpeaker(captionDraftSpeaker);
      const segments = normalizeCaptionSegments(draft.map(line => ({
        ...line,
        id: createSecureId("script-caption"),
        sourceClipId,
        speaker,
        speakerLabel: getCaptionSpeakerLabel(speaker),
        language,
        languageLabel: getCaptionLanguageLabel(language),
        languages: language === "und" ? [] : [language],
        textReviewRequired: true,
        textReviewed: false,
        reviewRequired: true,
      })));
      setCaptionSegments(previous => [
        ...previous.filter(segment =>
          sourceClipId && (
            String(segment.sourceClipId || "") !== String(sourceClipId) ||
            Number(segment.end || 0) <= windowStart ||
            Number(segment.start || 0) >= windowStart + windowDuration
          )
        ),
        ...segments,
      ]);
      setCaptionTextOverride("");
      setTranslateCaptionsToEnglish(false);
      setAutoCaptions(true);
      setComparisonMode("after");
      setCaptionGenerationStatus("review");
      setCaptionGenerationMessage(
        `${segments.length} timed draft line${segments.length === 1 ? "" : "s"} from ${sourceLabel}. Check the words and timing against the video, then approve each line before export.`
      );
      focusCaptionSegmentsForReview(segments);
      return true;
    } catch (error) {
      setCaptionGenerationStatus("failed");
      setCaptionGenerationMessage(error.message || "The caption draft could not be created.");
      return false;
    }
  };

  const addChoirMusicCue = () => {
    const start = Math.max(0, Number(currentTimelineWindow.start || 0));
    const duration = Math.max(0, Number(currentTimelineWindow.duration || selectedClip?.duration || 0));
    if (!duration) {
      setCaptionGenerationStatus("failed");
      setCaptionGenerationMessage("Choose a video clip before adding a choir cue.");
      return;
    }
    const cue = normalizeCaptionSegments([{
      id: createSecureId("caption-line"),
      start,
      end: start + Math.min(4, duration),
      text: "[Choir singing]",
      speaker: "choir",
      speakerLabel: "Choir",
      language: "en",
      languageLabel: "English",
      languages: ["en"],
      textReviewRequired: false,
      textReviewed: true,
      reviewRequired: false,
      sourceClipId: currentTimelineClip?.sourceClipId || currentTimelineClip?.id || selectedClip?.id || null,
    }])[0];
    setCaptionSegments(previous => [...previous, cue]);
    setCaptionTextOverride("");
    setTranslateCaptionsToEnglish(false);
    setAutoCaptions(true);
    setComparisonMode("after");
    setCaptionGenerationStatus("ready");
    setCaptionGenerationMessage("Choir sound cue added at the start of this clip. It does not claim to transcribe the lyrics.");
    focusCaptionSegmentsForReview([cue]);
  };

  const generateLiveTranscript = async ({
    translateToEnglish = translateCaptionsToEnglish,
    sourceClip = currentTimelineClip,
  } = {}) => {
    if (captionGenerationInFlightRef.current) return false;
    captionGenerationInFlightRef.current = true;

    const requestedTranslation = Boolean(translateToEnglish);
    const retainedSegments = normalizeCaptionSegments(captionSegments);
    const retainedOverride = captionTextOverride;
    const transcriptionProjectId = activeProjectIdRef.current;
    const activeSourceClip = sourceClip || currentTimelineClip;
    const captionSourceClipId = activeSourceClip?.sourceClipId || activeSourceClip?.id || selectedClip?.id || "main";
    const defaultCaptionSourceId = timeline.find(clip => clip.id === "main")?.sourceClipId || "main";
    const targetBefore = JSON.stringify(captionsForSource(retainedSegments, captionSourceClipId, defaultCaptionSourceId));

    setCaptionGenerationStatus("processing");
    setCaptionGenerationMessage(
      requestedTranslation
        ? "Translating the spoken audio into timestamped English captions…"
        : "Listening for speech and building timestamped captions…"
    );

    try {
      const sourceUrl = getSafeMediaSource(activeSourceClip?.url || videoUrl);
      let token = await getMediaAuthToken();
      if (!token) throw new Error("Please log in before generating captions.");

      const trustedStoragePath = String(
        activeSourceClip?.sourceStoragePath ||
          activeSourceClip?.storagePath ||
          (activeSourceClip?.id === "main" ? sourceStoragePath || selectedClip?.storagePath : "") ||
          ""
      ).trim();
      let transcriptionUrl = `${API_BASE_URL}/api/media/transcribe-source`;
      let transcriptionBody;
      let transcriptionHeaders = {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      };

      if (trustedStoragePath) {
        transcriptionBody = JSON.stringify({
          storage_path: trustedStoragePath,
          translate_to_english: requestedTranslation,
          source_language: captionSourceLanguage,
        });
        setCaptionGenerationMessage(
          requestedTranslation
            ? "Starting full-source English translation without re-uploading the video…"
            : "Starting full-source captions without re-uploading the video…"
        );
      } else {
        let sourceBlob =
          activeSourceClip?.file instanceof Blob
            ? activeSourceClip.file
            : activeSourceClip?.id === "main" && selectedClip?.file instanceof Blob
              ? selectedClip.file
              : null;

        if (!sourceBlob && sourceUrl) {
          const sourceResponse = await fetch(sourceUrl);
          if (!sourceResponse.ok) throw new Error("The source video could not be opened.");
          sourceBlob = await sourceResponse.blob();
        }

        if (!(sourceBlob instanceof Blob)) {
          throw new Error("Reload the source video, then try captions again.");
        }

        const formData = new FormData();
        formData.append(
          "file",
          sourceBlob,
          activeSourceClip?.file?.name || selectedClip?.file?.name || "viral-studio-source.mp4"
        );
        formData.append("translate_to_english", requestedTranslation ? "true" : "false");
        formData.append("source_language", captionSourceLanguage);
        transcriptionUrl = `${API_BASE_URL}/api/media/transcribe`;
        transcriptionBody = formData;
        transcriptionHeaders = { Authorization: `Bearer ${token}` };
      }

      let response = await fetch(transcriptionUrl, {
        method: "POST",
        headers: transcriptionHeaders,
        body: transcriptionBody,
      });

      if (response.status === 401) {
        token = await getMediaAuthToken(true);
        if (!token) throw new Error("Your session expired. Sign in, then try captions again.");
        response = await fetch(transcriptionUrl, {
          method: "POST",
          headers: { ...transcriptionHeaders, Authorization: `Bearer ${token}` },
          body: transcriptionBody,
        });
      }

      let payload = await response.json().catch(() => null);
      if (!response.ok) {
        throw new Error(
          getCaptionServiceError(
            payload,
            "Caption transcription could not start. Please try again."
          )
        );
      }

      if (payload?.jobId) {
        let completed = false;
        let failedPolls = 0;
        for (let attempt = 0; attempt < 1800; attempt += 1) {
          await sleep(2000);
          let statusResponse = await fetch(`${API_BASE_URL}/api/media/status/${payload.jobId}`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (statusResponse.status === 401) {
            token = await getMediaAuthToken(true);
            if (!token) throw new Error("Your session expired. Sign in, then try captions again.");
            statusResponse = await fetch(`${API_BASE_URL}/api/media/status/${payload.jobId}`, {
              headers: { Authorization: `Bearer ${token}` },
            });
          }
          const statusPayload = await statusResponse.json().catch(() => null);
          if (!statusResponse.ok) {
            if (statusResponse.status === 401) {
              throw new Error("Your session expired. Sign in, then try captions again.");
            }
            if ([403, 404].includes(statusResponse.status)) {
              throw new Error(
                getCaptionServiceError(
                  statusPayload,
                  "The caption job is no longer available. Please retry."
                )
              );
            }
            failedPolls += 1;
            if (failedPolls >= 3) {
              throw new Error(
                getCaptionServiceError(
                  statusPayload,
                  "The caption service is temporarily unavailable. Please retry."
                )
              );
            }
            continue;
          }
          if (!statusPayload || typeof statusPayload !== "object") {
            failedPolls += 1;
            if (failedPolls >= 3)
              throw new Error("The caption service returned an unreadable status. Please retry.");
            continue;
          }
          failedPolls = 0;
          if (["failed", "cancelled", "canceled"].includes(statusPayload.status)) {
            throw new Error(
              getCaptionServiceError(statusPayload, "Caption transcription failed. Please retry.")
            );
          }
          if (statusPayload.status === "completed") {
            payload = statusPayload.result || statusPayload;
            completed = true;
            break;
          }
          setCaptionGenerationMessage(
            `${requestedTranslation ? "Translating speech" : "Listening for speech"}… ${Math.round(
              Number(statusPayload.progress || 0)
            )}%`
          );
        }
        if (!completed) throw new Error("Caption transcription timed out. Please retry.");
      }

      const responseSegments = Array.isArray(payload?.segments) ? payload.segments : [];
      const languageMode = String(payload?.languageMode || payload?.language_mode || "")
        .trim()
        .toLowerCase();
      const translationProvenanceConfirmed =
        responseSegments.length > 0 &&
        responseSegments.every(
          segment =>
            segment?.translatedToEnglish === true || segment?.translated_to_english === true
        );
      const safeResponseSegments = filterClearlyCorruptCaptionSegments(
        responseSegments.filter(segment => {
          const text = normalizePlainText(segment?.text).toLowerCase();
          return ![
            "music outro",
            "music intro",
            "background music",
            "subtitles by",
            "captioned by",
            "transcribed by",
            "copyright",
            "all rights reserved",
          ].some(blocked => text.includes(blocked));
        })
      );
      const nextSegments = normalizeCaptionSegments(
        splitCaptionSegmentsForReadability(safeResponseSegments)
      ).map(segment => ({
        ...segment,
        // Older workers might return words with a rejected quality status.
        // Never silently present those words as verified subtitles.
        ...(payload?.transcriptionQuality?.status === "rejected" ||
        payload?.transcription_quality?.status === "rejected"
          ? { textReviewRequired: true, textReviewed: false, reviewRequired: true }
          : {}),
        sourceClipId: captionSourceClipId,
        translatedToEnglish: requestedTranslation,
        ...(requestedTranslation
          ? {
              language: "en",
              languageLabel: "English",
              languages: ["en"],
            }
          : {}),
      }));
      const transcriptionQuality =
        payload?.transcriptionQuality || payload?.transcription_quality || null;
      const identityReviewSegments = nextSegments.filter(segment => segment.reviewRequired).length;
      const wordingReviewSegments = nextSegments.filter(
        segment => segment.textReviewRequired && !segment.textReviewed
      ).length;
      const detectedLanguageLabels = Array.from(
        new Set(
          nextSegments
            .map(segment => segment.language)
            .filter(language => language && language !== "und")
            .map(getCaptionLanguageLabel)
        )
      );

      if (!nextSegments.length) {
        throw new Error(
          "Automatic transcription could not identify reliable words in this audio. Try a shorter, clearer section, or import/type the lyrics."
        );
      }
      if (
        requestedTranslation &&
        (languageMode !== "translated_to_english" || !translationProvenanceConfirmed)
      ) {
        throw new Error("The caption service did not confirm an English translation.");
      }

      if (activeProjectIdRef.current !== transcriptionProjectId) return false;
      const currentSegments = normalizeCaptionSegments(captionSegmentsRef.current);
      if (captionOverrideRef.current !== retainedOverride ||
          JSON.stringify(captionsForSource(currentSegments, captionSourceClipId, defaultCaptionSourceId)) !== targetBefore) {
        throw new Error("Captions for this source changed while speech was being processed. Your edits were kept; regenerate when ready.");
      }
      setCaptionSegments(previous => mergeSourceCaptions(previous, nextSegments, captionSourceClipId, defaultCaptionSourceId));
      setCaptionTextOverride("");
      setAutoCaptions(true);
      setComparisonMode("after");
      setCaptionGenerationStatus(identityReviewSegments ? "review" : "ready");
      setCaptionGenerationMessage(
        `${nextSegments.length} timestamped caption${nextSegments.length === 1 ? "" : "s"} ${
          identityReviewSegments ? "available as a preview draft" : "ready"
        } · ${
          requestedTranslation
            ? "translated to English"
            : "spoken languages preserved automatically"
        }${
          wordingReviewSegments
            ? ` · ${wordingReviewSegments} draft line${wordingReviewSegments === 1 ? "" : "s"} need wording review`
            : ""
        }${
          Number(transcriptionQuality?.rejected_segments || 0) > 0
            ? ` · ${transcriptionQuality.rejected_segments} corrupted line${Number(transcriptionQuality.rejected_segments) === 1 ? "" : "s"} excluded`
            : ""
        }${
          detectedLanguageLabels.length
            ? ` · ${detectedLanguageLabels.join(" + ")}`
            : " · language labels need review"
        }${
          identityReviewSegments
            ? ` · ${identityReviewSegments} line${identityReviewSegments === 1 ? "" : "s"} need review`
            : " · speakers and languages identified"
        }${identityReviewSegments ? " · approve flagged lines before final render" : ""}`
      );
      focusCaptionSegmentsForReview(nextSegments);
      setStudioActionMessage(
        requestedTranslation
          ? "English caption drafts are live. Verify translated wording before rendering."
          : "Caption drafts are live. Correct flagged wording and timing before rendering."
      );
      return true;
    } catch (error) {
      if (retainedSegments.length) {
        setCaptionGenerationStatus("failed");
        setCaptionGenerationMessage(
          `${requestedTranslation ? "Translation" : "Caption regeneration"} failed. Your existing captions were kept. ${error.message || "Try again."}`
        );
        setStudioActionMessage(
          `${requestedTranslation ? "Translation" : "Caption regeneration"} failed; the existing caption track was not changed.`
        );
        return false;
      }
      setCaptionGenerationStatus("failed");
      setCaptionGenerationMessage(error.message || "Caption transcription failed.");
      setStudioActionMessage(error.message || "Caption transcription failed.");
      return false;
    } finally {
      captionGenerationInFlightRef.current = false;
    }
  };

  const confirmAfterPreviewFrame = event => {
    const video = event.currentTarget;
    const expectedSource = getSafeMediaSource(renderedOutputUrl || currentTimelineClip?.url);
    if (video.readyState < 2 || video.currentSrc !== expectedSource) return;
    // Scrubbing must drive every timed overlay just like normal playback.
    // Keeping this in `seeked` also makes external/native media controls agree
    // with the editor timeline, captions, B-roll and watermark cues.
    setVideoTime(video.currentTime);
    setIsAfterPreviewReady(true);
    if (typeof video.requestVideoFrameCallback === "function") {
      video.requestVideoFrameCallback(() => setIsAfterPreviewReady(true));
    }
  };

  const togglePreviewFullscreen = async () => {
    if (isPreviewFullscreen) {
      setIsPreviewFullscreen(false);
      setStudioActionMessage("Fullscreen closed. All studio controls are available again.");
      return;
    }

    // Keep fullscreen inside the app. Native fullscreen is inconsistent in
    // embedded/cloud browsers and can trap its own exit control behind the
    // browser layer. This viewport-filling mode remains fully controllable.
    setComparisonMode("after");
    setIsPreviewFullscreen(true);
    setStudioActionMessage("The edited canvas now fills the viewport. Press Escape or Exit preview to return.");
  };

  const changePreviewSpeed = nextSpeed => {
    const normalizedSpeed = clampNumber(nextSpeed, 0.5, 2, 1);
    setPreviewSpeed(normalizedSpeed);
    if (videoRef.current) videoRef.current.playbackRate = normalizedSpeed;
    setStudioActionMessage(
      `Preview pacing set to ${normalizedSpeed.toFixed(2).replace(/0$/, "")}×. Captions, B-roll, and comparison stay synchronized.`
    );
  };

  const applyContentProfile = profileId => {
    const profile =
      CREATOR_CONTENT_PROFILES.find(item => item.id === profileId) || CREATOR_CONTENT_PROFILES[0];
    setContentProfile(profile.id);
    setCreativePreset(profile.preset);
    setCreativeIntensity(profile.intensity);
    setCreativeEffectsEnabled(true);
    setJoinTransition(profile.transition);
    setPacingLevel(profile.pacing);
    changePreviewSpeed(profile.speed);
    setStudioActionMessage(
      `${profile.label} guidance is ready. You can still change every suggestion before render.`
    );
  };

  // The finished monitor runs on output time; source decoders run on source
  // time. Resolve the displayed programme playhead before choosing an occurrence
  // or a source boundary. Never subtract a source trim from finished-file time.
  const getCutPlayheadPosition = () => {
    if (renderedOutputUrl) return resolveOutputTimelinePosition(previewTimelineTime);
    if (!currentTimelineClip) return null;
    const clipWindow = getTimelineClipWindow(currentTimelineClip);
    const localTime = clampNumber(Number(videoTime || 0) - Number(clipWindow.start || 0),
      0, Number(clipWindow.duration || 0), 0);
    return { index: activeTimelineIndex, clip: currentTimelineClip, clipWindow,
      sourceTime: Number(clipWindow.start || 0) + localTime, localTime };
  };

  const continueCutInSourcePreview = (position, index = position.index) => {
    programmeSeekIntentRef.current = { version: programmeSeekIntentRef.current.version + 1,
      sourceId: position.clip.sourceClipId || position.clip.id, time: position.sourceTime };
    pendingTimelineSeekRef.current = { index, sourceTime: position.sourceTime,
      play: previewPlaybackIntentRef.current };
    if (!renderedOutputUrl && index === activeTimelineIndex && videoRef.current) {
      videoRef.current.currentTime = position.sourceTime;
      pendingTimelineSeekRef.current = null;
    }
    setActiveTimelineIndex(index);
    setPreviewFinishedFile(false);
    setVideoTime(position.sourceTime);
  };

  const markCutBoundary = boundary => {
    const position = getCutPlayheadPosition();
    if (!position) return;
    const markedTime = Number(position.localTime.toFixed(2));
    // First marking a finished movie returns to its corresponding editable
    // source occurrence, so the second mark uses the same clock and clip.
    if (renderedOutputUrl) continueCutInSourcePreview(position);
    if (position.index !== activeTimelineIndex) {
      setCutRangeStart(null);
      setCutRangeEnd(null);
    }
    if (boundary === "start") {
      setCutRangeStart(markedTime);
      if (cutRangeEnd !== null && Number(cutRangeEnd) <= markedTime) setCutRangeEnd(null);
      setStudioActionMessage(
        `Removal starts at ${formatPreviewTimePrecise(markedTime)}. Play or seek to where the useful video returns.`
      );
      return;
    }
    setCutRangeEnd(markedTime);
    setStudioActionMessage(
      `Removal ends at ${formatPreviewTimePrecise(markedTime)}. Preview or remove the highlighted range.`
    );
  };

  const clearPendingCutRange = () => {
    setCutRangeStart(null);
    setCutRangeEnd(null);
  };

  const rippleLinkedTimelineMultiGaps = gaps => {
    const sortedGaps = (gaps || []).filter(gap => Number.isFinite(gap?.from) &&
      Number.isFinite(gap?.to) && gap.from >= 0 && gap.to > gap.from).sort((a, b) => b.from - a.from);
    if (!sortedGaps.length) return;
    // Script cuts, silence removal, manual trims and Director use one linked
    // edit transaction. Group gates/order and continuation keys move together.
    const transactionId = createSecureId("ripple");
    const next = sortedGaps.reduce((snapshot, gap) => rippleStudioSnapshotLinkedTimeline(snapshot,
      { fromTick: secondsToTicks(gap.from), toTick: secondsToTicks(gap.to) }, transactionId), getEditorSnapshot());
    setOverlays(next.overlays);
    setSoundEffects(next.soundEffects);
    setVoiceovers(next.voiceovers);
    setAdjustmentLayers(next.adjustmentLayers);
    setMotionScenes(next.motionScenes);
    setCompoundClips(next.compoundClips);
    setCompositionOrder(Array.isArray(next.compositionOrder) ? next.compositionOrder : null);
    setThreeDScenes(next.threeDScenes);
    setMotionKeyframes(next.motionKeyframes);
    setSpeedKeyframes(next.speedKeyframes);
    setFinishKeyframes(next.finishKeyframes);
    setReframeKeyframes(next.reframeKeyframes);
    setReframeModeCuts(next.reframeModeCuts);
    setSpeakerFocusCuts(next.speakerFocusCuts);
    setSpeakerStackFraming(next.speakerStackFraming);
    setAudioKeyframes(next.audioKeyframes);
  };

  const rippleLinkedTimeline = (from, to) => rippleLinkedTimelineMultiGaps([{ from, to }]);

  const removePendingCutRange = () => {
    if (!normalizedPendingCutRange || !currentTimelineClip) return;
    const offset=getTimelineOffsetForIndex(activeTimelineIndex), start=offset+normalizedPendingCutRange.start;
    const result=commitProgrammeRemoval([{start,end:offset+normalizedPendingCutRange.end}], 'Marked range removed', {
      transition:resolvedJoinTransition, transitionDuration:resolvedJoinTransition==='clean_cut'?.02:joinTransitionDuration,
      previewAtProgramme:Math.max(0,start-.65), play:true });
    if (result) setComparisonMode('split');
  };

  const splitCurrentClipAtPlayhead = (requestedProgrammeTime = null, layerId = activeOverlayId) => {
    const hasRequestedTime = typeof requestedProgrammeTime === "number" && Number.isFinite(requestedProgrammeTime);
    const position = hasRequestedTime
      ? resolveOutputTimelinePosition(requestedProgrammeTime) : getCutPlayheadPosition();
    const overlay = overlays.find(item => item.id === layerId);
    if (!allowTimelineTimingEdit(overlay ? (overlay.bRollMode ? "broll" : "graphics") : "video")) return;
    if (layerId) {
      const activeOverlay = overlay;
      if (activeOverlay) {
        const overlayStart = Number(activeOverlay.startTime ?? activeOverlay.start_time ?? 0);
        const overlayDuration = Number(activeOverlay.duration || 3);
        const overlayEnd = overlayStart + overlayDuration;
        const currentPlayhead = hasRequestedTime ? requestedProgrammeTime : Number(previewTimelineTime || 0);

        if (currentPlayhead > overlayStart + 0.15 && currentPlayhead < overlayEnd - 0.15) {
          const cutHistoryBaseline = cloneSnapshot(getEditorSnapshot());
          pendingHistoryBaselineRef.current = cutHistoryBaseline;
          cutHistoryTransactionRef.current = {
            baseline: cutHistoryBaseline,
            appliedSignature: null,
            appliedTimelineSignature: null,
          };

          const rightId = createSecureId("broll-split");
          const next = splitStudioOverlayAtProgrammeTime(cutHistoryBaseline, activeOverlay.id, currentPlayhead, rightId);
          setOverlays(next.overlays);
          setCompoundClips(next.compoundClips);
          setCompositionOrder(Array.isArray(next.compositionOrder) ? next.compositionOrder : null);
          setMotionKeyframes(next.motionKeyframes);
          setActiveOverlayId(rightId);
          if (position) continueCutInSourcePreview(position);
          setStudioActionMessage(`B-roll split at ${formatPreviewTimePrecise(currentPlayhead)} into 2 clips!`);
          return;
        }
        setStudioActionMessage("Move the playhead inside the selected layer (at least 0.15s from its edges) to split.");
        return;
      }
    }

    if (!position) return;
    const sourceWindow = position.clipWindow;
    const localDuration = Number(sourceWindow.duration || 0);
    const clipStart = Number(sourceWindow.start || 0);
    const splitPoint = position.localTime;
    if (splitPoint <= 0.2 || splitPoint >= localDuration - 0.2) {
      setStudioActionMessage("Move the playhead inside the clip (at least 0.2s from edges) to split.");
      return;
    }

    const splitTick = secondsToTicks(clipStart + splitPoint);
    if (splitTick - secondsToTicks(clipStart) < MINIMUM_SPLIT_DISTANCE_TICKS ||
        secondsToTicks(Number(sourceWindow.end)) - splitTick < MINIMUM_SPLIT_DISTANCE_TICKS) {
      setStudioActionMessage("Move the playhead at least 0.2s from either clip edge.");
      return;
    }
    const baseline = getEditorSnapshot();
    if (!baseline.studioDocument) {
      setStudioActionMessage("Clip source timing is still loading. Try the split again shortly.");
      return;
    }
    const cutHistoryBaseline = cloneSnapshot(baseline);
    pendingHistoryBaselineRef.current = cutHistoryBaseline;
    cutHistoryTransactionRef.current = {
      baseline: cutHistoryBaseline,
      appliedSignature: null,
      appliedTimelineSignature: null,
    };
    try {
      const result = runStudioCommandOnSnapshot({
        snapshot: baseline,
        projectId,
        batch: {
          projectId,
          baseRevision: baseline.studioDocument.revision,
          idempotencyKey: createSecureId("split-command"),
          actor: { type: "human", id: "viral-studio-ui" },
          operations: [{
            type: "split_clip",
            target: { occurrenceId: String(position.clip.id) },
            at: { space: "source", ticks: splitTick },
            newOccurrenceIds: {
              left: createSecureId("split-left"),
              right: createSecureId("split-right"),
            },
          }],
        },
      });
      studioDocumentRef.current = result.document;
      setTimeline(result.snapshot.timeline);
    } catch (error) {
      pendingHistoryBaselineRef.current = null;
      cutHistoryTransactionRef.current = null;
      setStudioActionMessage(error.message || "Clip could not be split.");
      return;
    }
    continueCutInSourcePreview(position, position.index + 1);
    setStudioActionMessage(`Clip split at ${formatPreviewTimePrecise(splitPoint)}! You now have 2 independent clips.`);
  };

  const deleteActiveTimelineClip = requestedIndex => {
    const index=typeof requestedIndex==='number'?requestedIndex:getCutPlayheadPosition()?.index;
    if (index===undefined || !timeline[index]) return;
    const start=getTimelineOffsetForIndex(index);
    commitProgrammeRemoval([{start,end:start+getTimelineClipWindow(timeline[index]).duration}],'Clip removed from timeline');
  };

  const applyLinkedCommandSnapshot = result => {
    const next = result.snapshot;
    studioDocumentRef.current = result.document;
    setTimeline(next.timeline);
    setCompoundClips(next.compoundClips || []);
    setOverlays(next.overlays);
    setSoundEffects(next.soundEffects);
    setVoiceovers(next.voiceovers);
    setAdjustmentLayers(next.adjustmentLayers);
    setMotionScenes(next.motionScenes);
    setCompositionOrder(Array.isArray(next.compositionOrder) ? next.compositionOrder : null);
    setThreeDScenes(next.threeDScenes);
    setMotionKeyframes(next.motionKeyframes);
    setSpeedKeyframes(next.speedKeyframes);
    setFinishKeyframes(next.finishKeyframes);
    setReframeKeyframes(next.reframeKeyframes);
    setReframeModeCuts(next.reframeModeCuts);
    setSpeakerFocusCuts(next.speakerFocusCuts);
    setSpeakerStackFraming(next.speakerStackFraming);
    setAudioKeyframes(next.audioKeyframes);
  };

  const openDirectorReview = async () => {
    if (!incomingDirectorProposalRequest || directorReviewState === "working") return;
    setDirectorReviewState("working");
    setDirectorReviewError("");
    try {
      const snapshot = getEditorSnapshot();
      if (!snapshot.studioDocument) {
        throw new Error("Clip source timing is still loading. Try the review again shortly.");
      }
      const { proposal, preview } = await prepareStudioDirectorProposal(
        snapshot.studioDocument,
        incomingDirectorProposalRequest
      );
      const prepared = {
        requestSignature: serializeSnapshot(incomingDirectorProposalRequest),
        document: cloneSnapshot(snapshot.studioDocument),
        proposal: cloneSnapshot(proposal),
        previewDocument: cloneSnapshot(preview.previewDocument),
      };
      prepared.diff = describeStudioDirectorReview({
        document: prepared.document,
        previewDocument: prepared.previewDocument,
        proposal: prepared.proposal,
      });
      setDirectorReview(prepared);
      setDirectorReviewState("ready");
    } catch (error) {
      setDirectorReview(null);
      setDirectorReviewState("error");
      setDirectorReviewError(error.message || "The incoming edit could not be reviewed.");
    }
  };

  const decideDirectorReview = async decision => {
    if (!directorReview || directorReviewState !== "ready") return;
    setDirectorReviewState("working");
    setDirectorReviewError("");
    let serverReview = null;
    try {
      const auth = getAuth();
      const reviewer = auth?.currentUser;
      if (!reviewer?.uid || typeof reviewer.getIdToken !== "function") {
        throw new Error("Sign in with Firebase before reviewing this edit.");
      }
      const token = await reviewer.getIdToken();
      if (!token || auth.currentUser?.uid !== reviewer.uid) {
        throw new Error("Your sign-in changed. Sign in again before reviewing this edit.");
      }
      const snapshot = getEditorSnapshot();
      if (!snapshot.studioDocument ||
          serializeSnapshot(snapshot.studioDocument) !== serializeSnapshot(directorReview.document)) {
        const stale = new Error("The project changed after this preview. Review the edit again.");
        stale.code = "STALE_REVIEW";
        throw stale;
      }
      const editorSignature = lastSnapshotSignatureRef.current;
      await registerStudioDirectorProjectRevision({
        document: directorReview.document,
        proposal: directorReview.proposal,
        token,
      });
      if (auth.currentUser?.uid !== reviewer.uid) {
        throw new Error("Your sign-in changed while saving the project version. No decision was recorded.");
      }
      const registeredSnapshot = latestEditorSnapshotRef.current?.();
      if (activeProjectIdRef.current !== projectId ||
          serializeSnapshot(directorProposalRequestRef.current) !== directorReview.requestSignature ||
          lastSnapshotSignatureRef.current !== editorSignature ||
          !registeredSnapshot?.studioDocument ||
          serializeSnapshot(registeredSnapshot.studioDocument) !== serializeSnapshot(snapshot.studioDocument)) {
        const stale = new Error("The project changed while saving its version. Review the edit again.");
        stale.code = "STALE_REVIEW";
        throw stale;
      }
      serverReview = await postStudioDirectorReviewDecision({
        proposal: directorReview.proposal,
        decision,
        token,
        reviewerUid: reviewer.uid,
      });
      if (auth.currentUser?.uid !== reviewer.uid) {
        throw new Error("Your sign-in changed during review. No local edit was applied.");
      }
      const latestSnapshot = latestEditorSnapshotRef.current?.();
      if (activeProjectIdRef.current !== projectId ||
          serializeSnapshot(directorProposalRequestRef.current) !== directorReview.requestSignature ||
          lastSnapshotSignatureRef.current !== editorSignature ||
          !latestSnapshot?.studioDocument ||
          serializeSnapshot(latestSnapshot.studioDocument) !== serializeSnapshot(snapshot.studioDocument)) {
        const stale = new Error(
          "The server recorded the decision, but this project changed. Request a new proposal before editing."
        );
        stale.code = "SERVER_REVIEW_LOCAL_STALE";
        throw stale;
      }
      const reviewReceipt = await createStudioDirectorReviewReceipt({
        proposal: directorReview.proposal,
        reviewerId: serverReview.reviewerUid,
        decision,
        reviewedAt: serverReview.reviewedAt,
      });
      const result = decision === "approve"
        ? await runStudioDirectorProposalOnSnapshot({
            snapshot: latestSnapshot, projectId, proposal: directorReview.proposal,
            reviewReceipt, serverReview,
          })
        : await recordStudioDirectorReviewRejectionOnSnapshot({
            snapshot: latestSnapshot, projectId, proposal: directorReview.proposal,
            reviewReceipt, serverReview,
          });
      const beforeCommit = latestEditorSnapshotRef.current?.();
      if (!beforeCommit?.studioDocument ||
          serializeSnapshot(beforeCommit.studioDocument) !== serializeSnapshot(latestSnapshot.studioDocument)) {
        throw new Error("The project changed while the server review was being applied locally.");
      }
      if (decision === "approve") {
        const baseline = cloneSnapshot(latestSnapshot);
        pendingHistoryBaselineRef.current = baseline;
        cutHistoryTransactionRef.current = {
          baseline,
          appliedSignature: null,
          appliedTimelineSignature: null,
        };
        applyLinkedCommandSnapshot(result);
        setStudioActionMessage("Reviewed Director edit applied to the timeline. Undo restores it.");
      } else {
        studioDocumentRef.current = result.document;
        const reviewedSnapshot = cloneSnapshot(result.snapshot);
        lastSnapshotRef.current = reviewedSnapshot;
        lastSnapshotSignatureRef.current = serializeSnapshot(
          getHistoryRelevantSnapshot(reviewedSnapshot)
        );
        scheduleProjectAutosave(reviewedSnapshot);
        setStudioActionMessage("Incoming Director edit rejected. The timeline was not changed.");
      }
      setDirectorReviewState(decision === "approve" ? "approved" : "rejected");
      try {
        await onDirectorReview?.({
          decision,
          proposal: directorReview.proposal,
          reviewReceipt,
          serverReview,
          document: result.document,
        });
      } catch (callbackError) {
        console.warn("Director review notification failed", callbackError);
      }
    } catch (error) {
      setDirectorReviewState(
        serverReview
          ? "server-recorded-stale"
          : ["STALE_REVISION", "DOCUMENT_CHANGED", "PROJECT_MISMATCH", "PREVIEW_CHANGED", "STALE_REVIEW",
            "PROJECT_REVISION_CONFLICT", "PROJECT_REVISION_STALE", "PROJECT_REVISION_MISSING", "PROJECT_REVISION_MISMATCH",
            "PROJECT_REVISION_TARGET_MISMATCH"]
          .includes(error.code)
          ? "stale"
          : "error"
      );
      setDirectorReviewError(serverReview
        ? `Decision recorded on the server; no local edit was applied. ${error.message || "Request a new proposal."}`
        : error.message || "The review could not be recorded.");
    }
  };

  const visibleDirectorReview = incomingDirectorProposalRequest && directorReview?.requestSignature ===
    serializeSnapshot(incomingDirectorProposalRequest) ? directorReview : null;

  const trimClipStartToPlayhead = () => {
    if (!allowTimelineTimingEdit("video")) return;
    const position = getCutPlayheadPosition();
    if (!position) return;
    const sourceWindow = position.clipWindow;
    const clipStart = Number(sourceWindow.start || 0);
    const localTime = position.localTime;
    if (localTime <= 0.1 || localTime >= Number(sourceWindow.duration || 0) - 0.2) {
      setStudioActionMessage("Move playhead to where you want the clip to start.");
      return;
    }
    const baseline = getEditorSnapshot();
    if (!baseline.studioDocument) {
      setStudioActionMessage("Clip source timing is still loading. Try the trim again shortly.");
      return;
    }
    const cutHistoryBaseline = cloneSnapshot(baseline);
    pendingHistoryBaselineRef.current = cutHistoryBaseline;
    cutHistoryTransactionRef.current = {
      baseline: cutHistoryBaseline,
      appliedSignature: null,
      appliedTimelineSignature: null,
    };
    try {
      const result = runStudioCommandOnSnapshot({
        snapshot: baseline,
        projectId,
        batch: {
          projectId,
          baseRevision: baseline.studioDocument.revision,
          idempotencyKey: createSecureId("trim-start-command"),
          actor: { type: "human", id: "viral-studio-ui" },
          operations: [{
            type: "trim_clip",
            target: { occurrenceId: String(position.clip.id) },
            keep: {
              space: "source",
              startTick: secondsToTicks(clipStart + localTime),
              endTick: secondsToTicks(Number(sourceWindow.end)),
            },
          }],
        },
      });
      applyLinkedCommandSnapshot(result);
      continueCutInSourcePreview(position);
    } catch (error) {
      pendingHistoryBaselineRef.current = null;
      cutHistoryTransactionRef.current = null;
      setStudioActionMessage(error.message || "Clip could not be trimmed.");
      return;
    }
    setStudioActionMessage(`Trimmed start to ${formatPreviewTimePrecise(localTime)}. Everything before it is removed.`);
  };

  const trimClipEndToPlayhead = () => {
    if (!allowTimelineTimingEdit("video")) return;
    const position = getCutPlayheadPosition();
    if (!position) return;
    const sourceWindow = position.clipWindow;
    const clipStart = Number(sourceWindow.start || 0);
    const localTime = position.localTime;
    if (localTime <= 0.2 || localTime >= Number(sourceWindow.duration || 0) - 0.000001) {
      setStudioActionMessage("Move playhead to where you want the clip to end.");
      return;
    }
    const baseline = getEditorSnapshot();
    if (!baseline.studioDocument) {
      setStudioActionMessage("Clip source timing is still loading. Try the trim again shortly.");
      return;
    }
    const cutHistoryBaseline = cloneSnapshot(baseline);
    pendingHistoryBaselineRef.current = cutHistoryBaseline;
    cutHistoryTransactionRef.current = {
      baseline: cutHistoryBaseline,
      appliedSignature: null,
      appliedTimelineSignature: null,
    };
    try {
      const result = runStudioCommandOnSnapshot({
        snapshot: baseline,
        projectId,
        batch: {
          projectId,
          baseRevision: baseline.studioDocument.revision,
          idempotencyKey: createSecureId("trim-end-command"),
          actor: { type: "human", id: "viral-studio-ui" },
          operations: [{
            type: "trim_clip",
            target: { occurrenceId: String(position.clip.id) },
            keep: {
              space: "source",
              startTick: secondsToTicks(clipStart),
              endTick: secondsToTicks(clipStart + localTime),
            },
          }],
        },
      });
      applyLinkedCommandSnapshot(result);
      continueCutInSourcePreview(position);
    } catch (error) {
      pendingHistoryBaselineRef.current = null;
      cutHistoryTransactionRef.current = null;
      setStudioActionMessage(error.message || "Clip could not be trimmed.");
      return;
    }
    setStudioActionMessage(`Trimmed end to ${formatPreviewTimePrecise(localTime)}. Everything after it is removed.`);
  };

  const detectSilenceSegments = async (targetClip = currentTimelineClip) => {
    if (!targetClip) return null;
    try {
      const token = await getMediaAuthToken();
      if (!token) throw new Error("Please log in to analyze audio");
      const fileUrl = await ensurePreviewableClipUrl(targetClip);
      if (!fileUrl) throw new Error("Clip video source is not ready");

      const response = await fetch(`${API_BASE_URL}/api/media/preview-silence`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          fileUrl,
          silenceThreshold,
          minSilenceDuration,
        }),
      });

      if (!response.ok) {
        throw new Error(`Silence analysis failed with status ${response.status}`);
      }
      const payload = await response.json();
      return {
        silenceSegments: payload.silence_segments || [],
        keepSegments: payload.keep_segments || [],
        duration: payload.duration || 0,
      };
    } catch (error) {
      console.warn("Silence preview API error, attempting client-side detection:", error);
      const clipWindow = getTimelineClipWindow(targetClip);
      const clipStart = Number(clipWindow.start || 0);
      const clipEnd = Number(clipWindow.end || clipStart + Number(clipWindow.duration || 0));
      const normalizedCaptions = normalizeCaptionSegments(captionSegments);

      if (normalizedCaptions.length > 0) {
        const matchingCaptions = normalizedCaptions
          .filter(c => (!c.sourceClipId || String(c.sourceClipId)===String(targetClip.sourceClipId||targetClip.id)) && c.end > clipStart && c.start < clipEnd)
          .sort((a, b) => a.start - b.start);

        if (matchingCaptions.length > 1) {
          const silences = [];
          const keeps = [];
          let cursor = clipStart;

          for (const cap of matchingCaptions) {
            const speechStart = Math.max(clipStart, Number(cap.start));
            const speechEnd = Math.min(clipEnd, Number(cap.end));
            if (speechStart - cursor >= minSilenceDuration) {
              silences.push({
                start: Number(cursor.toFixed(3)),
                end: Number(speechStart.toFixed(3)),
                duration: Number((speechStart - cursor).toFixed(3)),
              });
            }
            if (speechEnd > speechStart) {
              keeps.push({
                start: Number(speechStart.toFixed(3)),
                end: Number(speechEnd.toFixed(3)),
                duration: Number((speechEnd - speechStart).toFixed(3)),
              });
            }
            cursor = Math.max(cursor, speechEnd);
          }

          if (clipEnd - cursor >= minSilenceDuration) {
            silences.push({
              start: Number(cursor.toFixed(3)),
              end: Number(clipEnd.toFixed(3)),
              duration: Number((clipEnd - cursor).toFixed(3)),
            });
          }

          const mergedKeeps = [];
          for (const k of keeps) {
            if (!mergedKeeps.length) {
              mergedKeeps.push({ ...k });
            } else {
              const prev = mergedKeeps[mergedKeeps.length - 1];
              if (k.start - prev.end < minSilenceDuration) {
                prev.end = Math.max(prev.end, k.end);
                prev.duration = Number((prev.end - prev.start).toFixed(3));
              } else {
                mergedKeeps.push({ ...k });
              }
            }
          }

          if (silences.length > 0 && mergedKeeps.length > 0) {
            return {
              silenceSegments: silences,
              keepSegments: mergedKeeps,
              duration: clipEnd - clipStart,
            };
          }
        }
      }
      return null;
    }
  };

  const rippleCutAllSilences = async () => {
    const position=getCutPlayheadPosition();
    if (!position || !allowTimelineTimingEdit('video') || !allowTimelineTimingEdit('originalAudio')) return;
    const baseline=getEditorSnapshot(), signature=serializeSnapshot(getHistoryRelevantSnapshot(baseline)), requestProject=projectId;
    setIsRippleCuttingSilence(true);
    try {
      const data=silencePreview?.clipId===position.clip.id && silencePreview.keepSegments?.length
        ? silencePreview : await detectSilenceSegments(position.clip);
      const latest=latestEditorSnapshotRef.current?.();
      if (activeProjectIdRef.current!==requestProject || !latest || serializeSnapshot(getHistoryRelevantSnapshot(latest))!==signature)
        throw Error('The project changed during silence analysis. Your newer edits were kept; run it again.');
      if (!data?.keepSegments?.length) throw Error('No retained sound intervals were found; the clip was kept.');
      const window=position.clipWindow, keeps=[];
      for (const segment of data.keepSegments.map(item=>({start:Math.max(Number(window.start),Number(item.start??item.start_time)),
        end:Math.min(Number(window.end),Number(item.end??item.end_time))})).filter(item=>Number.isFinite(item.start)&&Number.isFinite(item.end)&&item.end>item.start)
        .sort((left,right)=>left.start-right.start)) {
        const previous=keeps.at(-1);
        if(previous&&segment.start<=previous.end) previous.end=Math.max(previous.end,segment.end);else keeps.push({...segment});
      }
      if (!keeps.length) throw Error('No retained sound intervals fall inside this source clip.');
      const offset=getTimelineOffsetForIndex(position.index), gaps=[];
      let cursor=Number(window.start);
      for (const keep of keeps) {
        if(keep.start>cursor+.00001)gaps.push({start:offset+cursor-Number(window.start),end:offset+keep.start-Number(window.start)});
        cursor=Math.max(cursor,keep.end);
      }
      if(Number(window.end)>cursor+.00001)gaps.push({start:offset+cursor-Number(window.start),end:offset+Number(window.end)-Number(window.start)});
      if (!gaps.length) {setStudioActionMessage('No dead air found at the current settings.');return;}
      if (commitProgrammeRemoval(gaps,'Silences cut')) {setSilenceRemoval(false);setSilencePreview(null);}
    } catch(error) {setStudioActionMessage(error.message);}
    finally {setIsRippleCuttingSilence(false);}
  };

  const commitSequenceEdit = request => {
    try {
      const baseline = getEditorSnapshot();
      if (!baseline.studioDocument) throw Error('Wait for source timing before editing.');
      let result = { snapshot: baseline };
      for (const operation of Array.isArray(request) ? request : [request]) result = editStudioSequence(result.snapshot, {
        minimumDuration: (Number(previewSpeed) || 1) / (Number(exportSettings.fps) || 30), ...operation, id: createSecureId('sequence-edit') });
      const position = getCutPlayheadPosition();
      const clip = result.snapshot.timeline[result.index], window = getTimelineClipWindow(clip);
      const localTime = position?.clip.id === clip.id ? Math.min(position.localTime, window.duration) : 0;
      const saved = cloneSnapshot(baseline);
      pendingHistoryBaselineRef.current = saved;
      cutHistoryTransactionRef.current = { baseline: saved, appliedSignature: null, appliedTimelineSignature: null };
      applyLinkedCommandSnapshot(result);
      continueCutInSourcePreview({ index: result.index, clip, sourceTime: Number(window.start) + localTime });
      setStudioActionMessage(`${Array.isArray(request) ? 'Audio pre-laps' : request.mode} edit applied${result.delta ? ` (${result.delta > 0 ? '+' : ''}${result.delta.toFixed(3)}s)` : ''}. Undo restores it.`);
      return true;
    } catch (error) { setStudioActionMessage(error.message); return false; }
  };
  const applyPrecisionTimelineEdit = (mode, direction = 1) => {
    const position = getCutPlayheadPosition();
    if (!position) return;
    const frame = (Number(previewSpeed) || 1) / (Number(exportSettings.fps) || 30);
    const step = precisionNudge === 'frame' ? frame : Number(precisionNudge);
    if (commitSequenceEdit({ mode, clipId: position.clip.id, edge: 'end', delta: step * (direction < 0 ? -1 : 1) }))
      setTimelineEditTool(['ripple', 'roll', 'slip', 'slide'].includes(mode) ? mode : 'select');
  };

  const selectCreativeTool = toolId => {
    setActiveCreativeTool(toolId);
    window.requestAnimationFrame(() => {
      const sidebar = studioSidebarRef.current;
      if (!sidebar) return;
      if (typeof sidebar.scrollTo === "function") {
        sidebar.scrollTo({ top: 0, behavior: "auto" });
      } else {
        sidebar.scrollTop = 0;
      }
    });
    if (
      [
        "cut",
        "hook",
        "reframe",
        "captions",
        "titles",
        "motion",
        "composite",
        "flow",
        "creatorfx",
        "pacing",
        "broll",
        "finish",
        "sound",
      ].includes(toolId)
    ) {
      setStudioInspectorTab(toolId);
    }
    if (toolId === "flow") {
      setBottomDockView("node_flow");
      setTimelineDockExpanded(true);
    }
    // Opening an inspector is navigation. Caption generation/enabling is an
    // explicit control inside it, and must not create an invisible Undo step.
    if (toolId === "moments") {
      setStudioActionMessage("Choose a story moment, then shape how it earns attention.");
    }
    if (toolId === "export") {
      setStudioActionMessage("Review the edit first. Rendering remains a separate final action.");
    }
  };

  const changeWorkspaceMode = requestedMode => {
    const nextMode = normalizeViralStudioWorkspaceMode(requestedMode);
    setWorkspaceMode(nextMode);

    if (nextMode === "quick") {
      setActiveCreativeTool("moments");
      setStudioInspectorTab("hook");
      setBottomDockView("timeline");
      setStudioActionMessage(
        "Quick Create keeps the story decisions visible and hides fine controls. Nothing in your edit was removed."
      );
      return;
    }

    if (nextMode === "fxflow") {
      setActiveCreativeTool("flow");
      setStudioInspectorTab("flow");
      setBottomDockView("comp_rig");
      setTimelineDockExpanded(true);
      setStudioActionMessage(
        "Motion & Flow is active: Pick-Whip parenting, Track Mattes, Motion Drivers, Speed/Value Graphs and Signal Node Flow stay synced to your canonical project."
      );
      return;
    }

    if (nextMode === "signature") {
      setActiveCreativeTool("moments");
      setComparisonMode("split");
      setStudioActionMessage(
        "Signature Lab is focused on transformation tuning. Before and After stay synchronized, and the clean fallback remains available."
      );
      return;
    }

    setStudioActionMessage(
      "Creator Studio is active with the complete timeline, captions, story visuals and sound controls."
    );
  };

  const updatePodcastVisualizer = (field, value) => {
    setPodcastVisualizer(current => ({ ...current, [field]: value }));
    setComparisonMode("after");
    setStudioActionMessage(
      field === "enabled" && value
        ? "The voice-reactive visual is reading the real audio signal in your browser."
        : "Finish & Motion updated instantly. No render credits used."
    );
  };

  const applyStudioFinishPreset = preset => {
    applyFinishPreset(preset);
    setMainFrame({
      enabled: true,
      insetPercent: 3,
      radiusPercent: 8,
      background: "studio_black",
    });
    setComparisonMode("split");
    setStudioActionMessage(
      `${preset.name} is live in After. The untouched source remains available in Before.`
    );
  };

  const resetStudioFinish = () => {
    resetFinishFx();
    setFinishKeyframes([]);
    setAdvancedColor({ lutName: "", lutFile: null, lutData: null, lutIntensity: 100,
      curve: { shadows: 0, midtones: 0, highlights: 0 }, hsl: { hue: 0, saturation: 0, luminance: 0 } });
    setPodcastVisualizer(current => ({ ...current, enabled: false }));
    setMainFrame({
      enabled: true,
      insetPercent: 3,
      radiusPercent: 8,
      background: "studio_black",
    });
    setStudioActionMessage("Finish & Motion reset. The underlying edit was not changed.");
  };

  const resolveMagneticTimelineTime = requestedTime => {
    if (!beatSnapEnabled || !musicBeatMarkers.length) {
      return { time: Math.max(0, Number(requestedTime || 0)), snapped: false, beat: null };
    }
    return snapTimeToBeat(requestedTime, musicBeatMarkers, 0.2);
  };

  const addMotionKeyframesAtPlayhead = () => {
    const target = motionInspectorTarget;
    if (!target) {
      setStudioActionMessage("Select a visual or graphic layer before adding motion keys.");
      return;
    }
    if (!allowTimelineTimingEdit(motionKeyframeTrack(target.id))) return;
    const resolved = timelineSnapping
      ? resolveMagneticTimelineTime(previewTimelineTime)
      : { time: previewTimelineTime, snapped: false };
    const values = buildMotionAutomationStyle(
      motionKeyframes.filter(key => key.targetId === target.id), resolved.time, target
    );
    setMotionKeyframes(current =>
      Object.entries(values).reduce(
        (next, [property, value]) =>
          upsertAutomationKeyframe(next, {
            id: createSecureId("motion-key"),
            targetId: target.id,
            property,
            value,
            time: Number(resolved.time || 0),
            easing: "ease_in_out",
          }, motionKeyframeTolerance(exportSettings.fps, previewSpeed)),
        current
      )
    );
    setPreviewFinishedFile(false);
    setStudioActionMessage(
      `Transform keyframe group added at ${Number(resolved.time || 0).toFixed(2)}s${resolved.snapped ? " and snapped to the beat" : ""}.`
    );
  };

  const updateMotionKeyframe = (keyframeId, changes) => {
    const key = motionKeyframes.find(item => item.id === keyframeId);
    if (!key || !allowTimelineTimingEdit(motionKeyframeTrack(key.targetId))) return;
    // An authored spatial interval travels through one shared timing curve.
    const timing = Object.fromEntries(Object.entries(changes).filter(([field]) => ['easing', 'curve'].includes(field)));
    // A pose updates several property keys in the same React event. Compose
    // each update against the latest state so later keys retain earlier edits.
    const applyChanges = current => current.map(keyframe => keyframe.id === keyframeId ? { ...keyframe, ...changes }
      : key.spatialCurve && keyframe.spatialCurve && keyframe.targetId === key.targetId && keyframe.time === key.time &&
        ['x', 'y'].includes(keyframe.property) ? { ...keyframe, ...timing } : keyframe);
    try { const next=applyChanges(motionKeyframes);validateStudioGroupKeyframes(compositionGroups.groups,next);
      const layer=overlays.find(l=>l.id===key.targetId);if(layer?.perspective)validateStudioPerspectiveLayer(layer,next.filter(k=>k.targetId===key.targetId)); }
    catch (error) { setStudioActionMessage(error.message); return; }
    setMotionKeyframes(applyChanges);
    setPreviewFinishedFile(false);
  };

  const motionKeyframeTrack = targetId => {
    if (targetId === "main-video") return "video";
    const layer = overlays.find(item => item.id === targetId);
    return layer?.bRollMode || ["image", "video"].includes(layer?.type) ? "broll" : "graphics";
  };
  const editMotionPositionPose = (time, position) => {
    if (!motionInspectorTarget || time > liveTimelineDuration || !allowTimelineTimingEdit(motionKeyframeTrack(motionTargetId))) return;
    try {
      const next = editStudioPositionPose(motionKeyframes, motionTargetId, time, position, () => createSecureId('motion-key'));
      validateStudioGroupKeyframes(compositionGroups.groups, next);
      setMotionKeyframes(next);
      setPreviewFinishedFile(false);
      jumpToOutputTimelineTime(time); setComparisonMode('after');
      setStudioActionMessage(`Position at ${time.toFixed(3)}s: X ${position.x.toFixed(1)}, Y ${position.y.toFixed(1)}.`);
    } catch (error) { setStudioActionMessage(error.message); }
  };
  const removeMotionKeyframe = id => {
    const key = motionKeyframes.find(item => item.id === id);
    if (!key || !allowTimelineTimingEdit(motionKeyframeTrack(key.targetId))) return;
    setMotionKeyframes(current => current.filter(item => item.id !== id));
    setPreviewFinishedFile(false);
  };

  const editMotionSpatialPath = (endTime, changes) => {
    if (!motionInspectorTarget || !allowTimelineTimingEdit(motionKeyframeTrack(motionTargetId))) return;
    try {
      const next = editStudioSpatialPath(motionKeyframes, motionTargetId, endTime, changes);
      validateStudioGroupKeyframes(compositionGroups.groups, next);
      setMotionKeyframes(next); setPreviewFinishedFile(false); setMotionPathPreview(null);
      setStudioActionMessage(changes.enabled === false ? 'Path straightened. Position keys and timing are retained.'
        : 'Spatial path updated. Drag the amber handles to shape its travel.');
    } catch (error) { setStudioActionMessage(error.message); }
  };

  const editMotionKeyframeSelection = (ids, action, options) => {
    try {
      const pasteTarget = action === 'paste' && options.pasteTargetId
        ? options.pasteTargetId === 'main-video' ? { ...mainTransform, id: 'main-video' }
          : compositionGroups.byId.get(options.pasteTargetId) || overlays.find(layer => layer.id === options.pasteTargetId && !layer.isCaption)
        : null;
      if (options.pasteTargetId && !pasteTarget) throw Error('Choose an existing destination layer.');
      const touched = action === 'paste' ? options.clipboard.map(key => pasteTarget ? { ...key, targetId: pasteTarget.id } : key)
        : motionKeyframes.filter(key => ids.includes(key.id));
      const known = new Set(['main-video', ...compositionIds, ...compositionGroups.byId.keys()]);
      if (touched.some(key => !known.has(key.targetId))) throw Error('The copied layers must exist in this project.');
      if (touched.some(key => trackStates[motionKeyframeTrack(key.targetId)]?.locked)) throw Error('Unlock every selected track before editing keys.');
      const result = editStudioKeyframeSelection(motionKeyframes, ids, action, {
        ...options, pasteTarget, duration: liveTimelineDuration, createId: () => createSecureId('motion-key'),
        timeTolerance: motionKeyframeTolerance(exportSettings.fps, previewSpeed),
      });
      validateStudioGroupKeyframes(compositionGroups.groups, result.keys);
      overlays.filter(l=>l.perspective).forEach(l=>validateStudioPerspectiveLayer(l,result.keys.filter(k=>k.targetId===l.id)));
      setMotionKeyframes(result.keys);
      setPreviewFinishedFile(false);
      setMotionKeyframeEditError('');
      setStudioActionMessage(`${action === 'paste' ? 'Pasted' : action === 'delete' ? 'Deleted' : 'Updated'} ${touched.length} keyframes in one edit.`);
      return result.selectedIds;
    } catch (error) { setStudioActionMessage(error.message); setMotionKeyframeEditError(error.message); return null; }
  };

  const editMotionPropertyAtPlayhead = (targetId, property, requestedValue, toggle = false) => {
    const group = compositionGroups.byId.get(targetId);
    const target = group ? { ...group, ...group.transform } : targetId === "main-video" ? mainTransform : overlays.find(item => item.id === targetId);
    const descriptor = studioMotionPropertiesForTarget(target).find(item => item.id === property);
    if (!target || !descriptor || (descriptor.mediaOnly && !["image", "video"].includes(target.type)) ||
        !allowTimelineTimingEdit(motionKeyframeTrack(targetId))) return;
    if (!toggle && !Number.isFinite(Number(requestedValue))) return;
    // The visible playhead is the authoring location. Beat snapping remains a
    // gesture aid, rather than silently moving a value edit to a different pose.
    const time = Number(previewTimelineTime || 0);
    const tolerance = motionKeyframeTolerance(exportSettings.fps, previewSpeed);
    const keys = motionKeyframes.filter(key => key.targetId === targetId && key.property === property);
    const existing = motionKeyAtPlayhead(keys, targetId, property, time, tolerance);
    const evaluated = group ? evaluateStudioGroupTransform(group, keys, time)[property] : buildMotionAutomationStyle(keys, time, target)[property];
    const value = toggle ? evaluated : Math.max(descriptor.min, Math.min(descriptor.max, Number(requestedValue)));
    if (toggle && existing) {
      removeMotionKeyframe(existing.id);
    } else if (toggle || keys.length) {
      const next = upsertAutomationKeyframe(motionKeyframes, {
        id: existing?.id || createSecureId("motion-key"), targetId, property,
        time: existing?.time ?? time, value, easing: existing?.easing || (descriptor.planeCorner ? 'linear' : "ease_in_out"),
        ...(existing?.curve ? { curve: existing.curve } : {}),
      }, tolerance);
      try { validateStudioGroupKeyframes(compositionGroups.groups, next);
        if(target.perspective)validateStudioPerspectiveLayer(target,next.filter(k=>k.targetId===targetId)); }
      catch (error) { setStudioActionMessage(error.message); return; }
      setMotionKeyframes(next);
    } else if (group) {
      setCompoundClips(current => current.map(item => item.id === targetId ? { ...item, transform: { ...item.transform, [property]: value } } : item));
    } else if (targetId === "main-video") {
      setMainTransform(current => ({ ...current, [property]: value }));
    } else if (descriptor.planeCorner) {
      const match=/^planeCorner([0-3])([XY])$/.exec(property),corners=target.perspective.corners.map(p=>[...p]);
      corners[Number(match[1])][match[2]==='Y'?1:0]=value;
      setOverlayStyleOption(targetId,'perspective',{...target.perspective,corners});
    } else if (descriptor.maskPoint) {
      const match=studioMaskPointProperty(property),points=parseStudioMaskPolygon(target.mask.path);
      points[Number(match[1])][match[2]==='Y'?1:0]=value;
      setOverlayStyleOption(targetId,'mask',{...target.mask,path:studioMaskPolygonPath(points)});
    } else {
      setOverlayStyleOption(targetId, property, value);
    }
    setComparisonMode("after");
    setPreviewFinishedFile(false);
    setStudioActionMessage(`${descriptor.label} ${toggle && existing ? "key removed" : toggle || keys.length ? "key saved" : "value updated"} at ${(time / Math.max(0.01, Number(previewSpeed) || 1)).toFixed(2)}s.`);
  };

  const autoGeneratePunchIns = () => {
    const cutHistoryBaseline = cloneSnapshot(getEditorSnapshot());
    pendingHistoryBaselineRef.current = cutHistoryBaseline;
    cutHistoryTransactionRef.current = {
      baseline: cutHistoryBaseline,
      appliedSignature: null,
      appliedTimelineSignature: null,
    };

    const keys = generateSmartZoomKeyframes({
      duration: outputTimelineDuration,
      captionSegments,
      punchScale,
      punchStyle,
      punchMode,
      punchDuration,
      baseScale: Number(mainTransform.scale || 1.0),
    });

    setMotionKeyframes(current => [
      ...current.filter(k => !(k.targetId === "main-video" && k.property === "scale")),
      ...keys,
    ]);

    const zones = extractPunchZonesFromKeyframes(keys);
    setStudioActionMessage(
      `⚡ Generated ${zones.length} viral punch-ins (${punchScale.toFixed(2)}× ${punchStyle} easing)!`
    );
    toast.success(`Generated ${zones.length} retention punch-ins!`);
  };

  const togglePunchInAtPlayhead = () => {
    const cutHistoryBaseline = cloneSnapshot(getEditorSnapshot());
    pendingHistoryBaselineRef.current = cutHistoryBaseline;
    cutHistoryTransactionRef.current = {
      baseline: cutHistoryBaseline,
      appliedSignature: null,
      appliedTimelineSignature: null,
    };

    const currentT = Number(previewTimelineTime || 0);

    if (isPlayheadPunched) {
      const zones = extractPunchZonesFromKeyframes(motionKeyframes);
      const activeZone = zones.find(z => currentT >= z.startTime - 0.05 && currentT <= z.endTime + 0.05);
      if (activeZone) {
        setMotionKeyframes(current =>
          current.filter(
            k =>
              k.punchId !== activeZone.punchId &&
              !(k.targetId === "main-video" && k.property === "scale" && k.time >= activeZone.startTime - 0.05 && k.time <= activeZone.endTime + 0.05)
          )
        );
        setStudioActionMessage(`Removed punch-in at ${formatPreviewTimePrecise(currentT)}.`);
        toast("Punch-in removed", { icon: "↺" });
        return;
      }
    }

    const keys = createSinglePunchKeyframes({
      startTime: currentT,
      duration: punchDuration,
      punchScale,
      punchStyle,
      baseScale: Number(mainTransform.scale || 1.0),
    });

    setMotionKeyframes(current => {
      const endT = currentT + punchDuration;
      const filtered = current.filter(
        k => !(k.targetId === "main-video" && k.property === "scale" && k.time >= currentT - 0.1 && k.time <= endT + 0.1)
      );
      return [...filtered, ...keys].sort((a, b) => Number(a.time) - Number(b.time));
    });

    setStudioActionMessage(
      `🎯 Punched in ${punchScale.toFixed(2)}× at ${formatPreviewTimePrecise(currentT)} for ${punchDuration}s (Press Z to toggle)`
    );
    toast.success(`Punched in ${punchScale.toFixed(2)}× at ${formatPreviewTimePrecise(currentT)}!`);
  };

  const clearAllPunchIns = () => {
    const cutHistoryBaseline = cloneSnapshot(getEditorSnapshot());
    pendingHistoryBaselineRef.current = cutHistoryBaseline;
    cutHistoryTransactionRef.current = {
      baseline: cutHistoryBaseline,
      appliedSignature: null,
      appliedTimelineSignature: null,
    };

    setMotionKeyframes(current =>
      current.filter(k => !(k.targetId === "main-video" && k.property === "scale"))
    );
    setStudioActionMessage("All camera punch-in scale keyframes cleared.");
    toast("Camera punch-ins cleared", { icon: "↺" });
  };

  const handleAudioTrim = (clipIndex, offsets) => {
    const clip = timeline[clipIndex];
    if (clip) commitSequenceEdit({ mode: 'audio-trim', clipId: clip.id, offsets });
  };
  const toggleJCutAtPlayhead = () => {
    const position = getCutPlayheadPosition();
    if (!position) return;
    handleAudioTrim(position.index, { audioTrimOffsetStart:
      Number(position.clip.audioTrimOffsetStart || 0) < 0 ? 0 : -0.8 });
  };
  const toggleLCutAtPlayhead = () => {
    const position = getCutPlayheadPosition();
    if (!position) return;
    handleAudioTrim(position.index, { audioTrimOffsetEnd:
      Number(position.clip.audioTrimOffsetEnd || 0) > 0 ? 0 : 0.8 });
  };
  Object.assign(studioShortcutActionsRef.current, { punch: togglePunchInAtPlayhead,
    jcut: toggleJCutAtPlayhead, lcut: toggleLCutAtPlayhead });

  const autoApplyAllDialoguePreLaps = () => {
    let elapsed = 0;
    const edits = timeline.flatMap((clip, index) => {
      const window = getTimelineClipWindow(clip);
      const lead = Math.min(.6, elapsed, Number(window.start));
      elapsed += Number(window.duration);
      return index && lead > 0 && Math.abs(Number(clip.audioTrimOffsetStart || 0) + lead) > .000001
        ? [{ mode: 'audio-trim', clipId: clip.id, offsets: { audioTrimOffsetStart: -lead } }] : [];
    });
    if (!edits.length) { setStudioActionMessage('No incoming clip has unused source audio for a new pre-lap.'); return; }
    commitSequenceEdit(edits);
  };

  const handleAlignCutsToBeat = () => {
    if (!timeline || timeline.length <= 1) {
      toast("Add at least 2 sequence clips to align cuts to beats", { icon: "ℹ️" });
      return;
    }
    if (!musicBeatMarkers || !musicBeatMarkers.length) {
      toast("Add a background music track to detect rhythm beats first", { icon: "🎵" });
      return;
    }
    const cutHistoryBaseline = cloneSnapshot(getEditorSnapshot());
    pendingHistoryBaselineRef.current = cutHistoryBaseline;
    cutHistoryTransactionRef.current = {
      baseline: cutHistoryBaseline,
      appliedSignature: null,
      appliedTimelineSignature: null,
    };
    const result = alignTimelineCutsToMusicBeats(timeline, musicBeatMarkers, { threshold: 0.35 });
    if (result.cutsAligned === 0) {
      setStudioActionMessage("All cuts are already in rhythm with music beats!");
      toast("Cuts are already locked to musical beats", { icon: "🎵" });
      return;
    }
    setTimeline(result.updatedTimeline);
    setStudioActionMessage(`🎵 Aligned ${result.cutsAligned} sequence cuts to nearest music beat transients.`);
    toast.success(`🎵 Aligned ${result.cutsAligned} cuts to music beats!`);
  };

  const handleAlignBRollToBeat = () => {
    if (!overlays || !overlays.some(o => o.bRollMode)) {
      toast("No B-roll overlays found to align", { icon: "ℹ️" });
      return;
    }
    if (!musicBeatMarkers || !musicBeatMarkers.length) {
      toast("Add a background music track to detect rhythm beats first", { icon: "🎵" });
      return;
    }
    const cutHistoryBaseline = cloneSnapshot(getEditorSnapshot());
    pendingHistoryBaselineRef.current = cutHistoryBaseline;
    cutHistoryTransactionRef.current = {
      baseline: cutHistoryBaseline,
      appliedSignature: null,
      appliedTimelineSignature: null,
    };
    const brollClips = overlays.filter(o => o.bRollMode);
    const nonBroll = overlays.filter(o => !o.bRollMode);
    const result = alignBRollToMusicBeats(brollClips, musicBeatMarkers, 0.35);
    setOverlays([...nonBroll, ...result.updatedBRoll]);
    setStudioActionMessage(`⚡ Snapped ${result.alignedCount} B-roll clips to musical beat markers.`);
    toast.success(`⚡ Snapped ${result.alignedCount} B-roll clips to beats!`);
  };

  const handleGenerateBeatPunchIns = (cadence = "bars") => {
    if (!musicBeatMarkers || !musicBeatMarkers.length) {
      toast("Add a background music track to detect rhythm beats first", { icon: "🎵" });
      return;
    }
    const cutHistoryBaseline = cloneSnapshot(getEditorSnapshot());
    pendingHistoryBaselineRef.current = cutHistoryBaseline;
    cutHistoryTransactionRef.current = {
      baseline: cutHistoryBaseline,
      appliedSignature: null,
      appliedTimelineSignature: null,
    };
    const keys = generateBeatPunchIns(musicBeatMarkers, liveTimelineDuration, cadence, punchScale);
    if (!keys.length) {
      toast("No beat drops found in timeline duration", { icon: "ℹ️" });
      return;
    }
    setMotionKeyframes(current => {
      const filtered = current.filter(
        k => !(k.targetId === "main-video" && k.property === "scale")
      );
      return [...filtered, ...keys].sort((a, b) => Number(a.time) - Number(b.time));
    });
    setStudioActionMessage(`🎯 Generated ${Math.floor(keys.length / 2)} punch-ins locked to musical beat drops!`);
    toast.success(`🎯 Generated ${Math.floor(keys.length / 2)} punch-ins on music beats!`);
  };

  const handleAutoGenerateMotionBeats = () => {
    const cutHistoryBaseline = cloneSnapshot(getEditorSnapshot());
    pendingHistoryBaselineRef.current = cutHistoryBaseline;
    cutHistoryTransactionRef.current = {
      baseline: cutHistoryBaseline,
      appliedSignature: null,
      appliedTimelineSignature: null,
    };

    const newScenes = generateSmartMotionBeats({
      transcript: captionSegments,
      duration: liveTimelineDuration || selectedClip?.duration || 30,
      existingScenes: motionScenes,
    });

    if (!newScenes.length) {
      toast("Motion graphics are already active across key moments", { icon: "ℹ️" });
      return;
    }

    setMotionScenes(prev => [...prev, ...newScenes]);
    setStudioActionMessage(`✨ Auto-generated ${newScenes.length} viral motion graphics from speech transcript!`);
    toast.success(`✨ Auto-generated ${newScenes.length} viral motion graphics!`);
  };

  // Feature #6: Descript-Style Script Slicer & Filler Word Killer
  const detectedFillerWords = useMemo(() => {
    return detectFillerWords(captionSegments);
  }, [captionSegments]);

  const resolveCaptionTimelineRange = segment => {
    const sourceId = segment.sourceClipId || timeline.find(clip => clip.id === 'main')?.sourceClipId || 'main';
    const indices = [activeTimelineIndex, ...timeline.map((_,index)=>index).filter(index=>index!==activeTimelineIndex)];
    for (const index of indices) {
      const clip=timeline[index], window=getTimelineClipWindow(clip);
      if (String(clip.sourceClipId || clip.id)!==String(sourceId)) continue;
      const start=Math.max(Number(segment.start),Number(window.start)), end=Math.min(Number(segment.end),Number(window.end));
      if (end>start) return {start:getTimelineOffsetForIndex(index)+start-Number(window.start),
        end:getTimelineOffsetForIndex(index)+end-Number(window.start)};
    }
    return null;
  };
  const commitProgrammeRemoval = (ranges, label, options = {}) => {
    try {
      const baseline=getEditorSnapshot();
      if (!baseline.studioDocument) throw Error('Wait for source timing before cutting.');
      const result=removeStudioProgrammeRanges(baseline,ranges,createSecureId('range-cut'),options);
      const saved=cloneSnapshot(baseline);
      pendingHistoryBaselineRef.current=saved;
      cutHistoryTransactionRef.current={baseline:saved,appliedSignature:null,appliedTimelineSignature:null};
      applyLinkedCommandSnapshot(result);
      let index=Math.min(activeTimelineIndex,result.snapshot.timeline.length-1),elapsed=0,localTime=0;
      if (options.previewAtProgramme!==undefined) for(let i=0;i<result.snapshot.timeline.length;i++) {
        const duration=getTimelineClipWindow(result.snapshot.timeline[i]).duration;
        if(options.previewAtProgramme<elapsed+duration || i===result.snapshot.timeline.length-1) {index=i;localTime=Math.max(0,Math.min(duration,options.previewAtProgramme-elapsed));break;}
        elapsed+=duration;
      }
      const clip=result.snapshot.timeline[index];
      if(options.play) {previewPlaybackIntentRef.current=true;setIsPreviewPaused(false);}
      continueCutInSourcePreview({index,clip,sourceTime:Number(getTimelineClipWindow(clip).start)+localTime});
      setCutRangeStart(null);setCutRangeEnd(null);
      setStudioActionMessage(`${label}: removed ${result.removed.toFixed(3)}s. Undo restores the complete edit.`);
      return result;
    } catch(error) {setStudioActionMessage(error.message);return null;}
  };
  const handleCutCaptionFromVideo = segment => {
    const range=segment && resolveCaptionTimelineRange(segment);
    if (!range) {setStudioActionMessage('This caption has no retained occurrence in the source timeline.');return;}
    commitProgrammeRemoval([range],'Caption cut');
  };
  const fillerRanges = fillers => fillers.flatMap(filler => {
    const segment=captionSegments.find(cue=>cue.id===filler.segmentId);
    const range=resolveCaptionTimelineRange({...segment,...filler,sourceClipId:segment?.sourceClipId});
    return range?[range]:[];
  });
  const handleRemoveAllFillerWords = () => {
    const ranges=fillerRanges(detectedFillerWords);
    if (!ranges.length) {setStudioActionMessage('No retained filler-word ranges to remove.');return;}
    commitProgrammeRemoval(ranges,'Filler words cut');
  };
  const handleCutSingleFillerWord = filler => {
    const ranges=fillerRanges([filler]);
    if (ranges.length) commitProgrammeRemoval(ranges,'Filler word cut');
    else setStudioActionMessage('This filler word has no retained source occurrence.');
  };

  const addAudioAutomationKeyframe = () => {
    const source = audioAutomationSource;
    if (!allowTimelineTimingEdit(source)) return;
    const resolved = timelineSnapping
      ? resolveMagneticTimelineTime(previewTimelineTime)
      : { time: previewTimelineTime, snapped: false };
    // Bus automation multiplies the existing clip/track fader. A new key
    // preserves the envelope here; copying the fader would apply it twice.
    const currentValue = Math.round(interpolateAutomationValue({
      keyframes: audioKeyframes[source] || [], property: "volume",
      time: Number(resolved.time || 0), fallback: 100,
    }));
    setAudioKeyframes(current => ({
      ...current,
      [source]: upsertAutomationKeyframe(current[source] || [], {
        id: createSecureId("audio-key"),
        targetId: source,
        property: "volume",
        value: currentValue,
        time: Number(resolved.time || 0),
        easing: "ease_in_out",
      }),
    }));
    setStudioActionMessage(
      `${source.replace(/([A-Z])/g, " $1")} volume keyframe added at ${Number(resolved.time || 0).toFixed(2)}s.`
    );
  };

  const removeAudioAutomationKeyframe = keyframeId => {
    if (!allowTimelineTimingEdit(audioAutomationSource)) return;
    setAudioKeyframes(current => ({
      ...current,
      [audioAutomationSource]: (current[audioAutomationSource] || []).filter(
        keyframe => keyframe.id !== keyframeId
      ),
    }));
  };

  const addSpeedKeyframeAtPlayhead = () => {
    const resolved = timelineSnapping
      ? resolveMagneticTimelineTime(previewTimelineTime)
      : { time: previewTimelineTime };
    setSpeedKeyframes(current =>
      upsertAutomationKeyframe(current, {
        id: createSecureId("speed-key"),
        targetId: "main",
        property: "speed",
        value: previewSpeed,
        time: Number(resolved.time || 0),
        easing: "bezier",
      })
    );
    setStudioActionMessage(
      `Speed point added at ${Number(resolved.time || 0).toFixed(2)}s with a smooth curve.`
    );
  };

  const addAdjustmentLayer = () => {
    const startTime = clampNumber(previewTimelineTime, 0, liveTimelineDuration, 0);
    setAdjustmentLayers(current => [
      ...current,
      {
        id: createSecureId("adjustment"),
        name: `Adjustment ${current.length + 1}`,
        startTime,
        duration: Math.max(0.2, liveTimelineDuration - startTime),
        effects: { color: { ...finishFx }, blendMode: "normal", opacity: 1 },
      },
    ]);
    setStudioActionMessage("Adjustment layer added above the video tracks at the playhead.");
  };

  const createCompoundFromSelection = selected => {
    try {
      if (selected.some(id=>overlays.find(layer=>layer.id===id)?.perspective))
        throw Error('Perspective planes must remain outside graphic groups.');
      const names = new Set(compositionGroups.groups.map(group => group.name));
      let index = compositionGroups.groups.length + 1;
      while (names.has(`Group ${index}`)) index += 1;
      const next = createStudioGroup(effectiveCompoundClips, compositionIds, selected,
        createSecureId("group"), `Group ${index}`);
      setCompoundClips(next.compounds); applyCompositionOrder(next.order);
      setStudioActionMessage("Group created. Its children keep their edits and keyframes.");
    } catch (error) { setStudioActionMessage(error.message); }
  };

  const importGraphicsComposition = async input => {
    if (trackStates.graphics?.locked) throw Error('Unlock the Graphics track before importing.');
    const projectId = activeProjectIdRef.current, signature = lastSnapshotSignatureRef.current;
    const stage = phoneFrameRef.current;
    const result = await buildStudioGraphicsPackage({ input, overlays, keyframes: motionKeyframes,
      compounds: effectiveCompoundClips, order: compositionIds, duration: liveTimelineDuration,
      aspect: (stage?.clientWidth || 1280) / (stage?.clientHeight || 720), createId: createSecureId });
    if (activeProjectIdRef.current !== projectId || lastSnapshotSignatureRef.current !== signature) {
      result.urls.forEach(url => URL.revokeObjectURL(url));
      throw Error('The edit changed during import. Import the composition again.');
    }
    result.urls.forEach(url => projectMediaObjectUrlsRef.current.add(url));
    flushSync(() => {
      setOverlays(result.overlays); setMotionKeyframes(result.keyframes);
      setCompoundClips(result.compounds); setCompositionOrder(result.order);
      setActiveOverlayId(result.layers.find(layer => layer.type === 'text')?.id || result.layers[0].id);
      setMotionTargetId(result.layers[0].id); setSelectedGroupId(null); setPreviewFinishedFile(false);
    });
    setComparisonMode('after'); setTimelineDockExpanded(true);
    setStudioActionMessage(`${result.name}: ${result.layers.length} editable graphic layers imported.`);
    return result.name;
  };

  const createKineticTitle = async options => {
    if (trackStates.graphics?.locked) throw Error('Unlock the Graphics track to edit text motion.');
    const source = overlays.find(layer => layer.id === activeOverlayId), projectId = activeProjectIdRef.current;
    const editSignature = lastSnapshotSignatureRef.current;
    if (!source) throw Error('Select a title first.');
    await document.fonts.load(`${Number(source.fontWeight || 900)} 36px "Studio Inter"`);
    if (activeProjectIdRef.current !== projectId) throw Error('The project changed. Select the title again.');
    if (lastSnapshotSignatureRef.current !== editSignature || latestEditorSnapshotRef.current?.().activeOverlayId !== source.id)
      throw Error('The edit changed while the font loaded. Select the title and build its motion again.');
    const canvas = document.createElement('canvas'), context = canvas.getContext('2d');
    context.font = `${Number(source.fontWeight || 900)} 36px "Studio Inter"`;
    const stage = phoneFrameRef.current;
    const result = buildStudioKineticType({ source, overlays, keyframes: motionKeyframes,
      compounds: effectiveCompoundClips, order: compositionIds,
      width: stage?.clientWidth || 1280, height: stage?.clientHeight || 720,
      measure: value => context.measureText(value).width, createId: createSecureId,
      fps: Number(exportSettings.fps) || 30, playbackRate: Number(previewSpeed) || 1, ...options });
    flushSync(() => {
      setOverlays(result.overlays); setMotionKeyframes(result.keyframes);
      setCompoundClips(result.compounds); setCompositionOrder(result.order);
      setActiveOverlayId(result.layers[0].id); setMotionTargetId(result.layers[0].id);
      setSelectedGroupId(null); setPreviewFinishedFile(false);
    });
    setComparisonMode('after'); setTimelineDockExpanded(true);
    setStudioActionMessage(`${result.layers.length} editable ${options.unit === 'letter' ? 'letters' : 'words'} created with staggered motion keys. Select each word in the timeline or move the complete Type group.`);
  };

  const updateVoiceoverTake = (id, changes) => setVoiceovers(current => current.map(take => {
    if (take.id !== id) return take;
    const next = { ...take, ...changes };
    const sourceDuration = Number(take.sourceDuration || Number(take.trimStart || 0) + take.duration);
    next.startTime = clampNumber(next.startTime, 0, Math.max(0, liveTimelineDuration - .05), 0);
    next.trimStart = clampNumber(next.trimStart, 0, Math.max(0, sourceDuration - .05), 0);
    next.duration = clampNumber(next.duration, .05, Math.max(.05, sourceDuration - next.trimStart), .05);
    next.volume = voiceoverGain(next.volume);
    return next;
  }));

  const importVoiceoverRecording = async file => {
    if (!file) return;
    const importingProject = activeProjectIdRef.current;
    const startTime = Number(previewTimelineTime || 0);
    const url = URL.createObjectURL(file);
    try {
      const duration = await readVoiceoverDuration(file, url);
      if (!voiceoverMountedRef.current || activeProjectIdRef.current !== importingProject) return;
      soundEffectObjectUrlsRef.current.add(url);
      setVoiceovers(current => [...current, { id: createSecureId("voiceover"), name: file.name,
        file, url, startTime, duration, sourceDuration: duration, trimStart: 0, volume: 1,
        fadeIn: 0, fadeOut: 0 }]);
      setStudioActionMessage("Recording imported onto the editable voice-over track.");
    } catch (error) { setStudioActionMessage(error.message); }
    finally { if (!soundEffectObjectUrlsRef.current.has(url)) URL.revokeObjectURL(url); }
  };

  const toggleVoiceoverRecording = async () => {
    if (isVoiceoverRecording) {
      voiceoverRecorderRef.current?.stop();
      return;
    }
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === "undefined") {
      setStudioActionMessage("Voice-over recording is not supported by this browser.");
      return;
    }
    if (voiceoverStreamRef.current || voiceoverRequestPendingRef.current) return;
    voiceoverRequestPendingRef.current = true;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      if (!voiceoverMountedRef.current) {
        stream.getTracks().forEach(track => track.stop());
        return;
      }
      voiceoverStreamRef.current = stream;
      const recorder = new MediaRecorder(stream);
      voiceoverChunksRef.current = [];
      voiceoverStartTimeRef.current = Number(previewTimelineTime || 0);
      voiceoverStartedAtRef.current = Date.now();
      recorder.ondataavailable = event => {
        if (event.data?.size) voiceoverChunksRef.current.push(event.data);
      };
      recorder.onstop = () => {
        stream.getTracks().forEach(track => track.stop());
        voiceoverStreamRef.current = null;
        voiceoverRecorderRef.current = null;
        setIsVoiceoverRecording(false);
        const mimeType = recorder.mimeType || "audio/webm";
        const blob = new Blob(voiceoverChunksRef.current, { type: mimeType });
        if (!blob.size) {
          setStudioActionMessage("The microphone returned no audio. Check your input and record another take.");
          return;
        }
        const duration = Math.max(0.1, (Date.now() - voiceoverStartedAtRef.current) / 1000);
        const extension = mimeType.includes("mp4") ? "m4a" : mimeType.includes("ogg") ? "ogg" : "webm";
        const file = new File([blob], `voiceover-${Date.now()}.${extension}`, { type: mimeType });
        const url = URL.createObjectURL(file);
        soundEffectObjectUrlsRef.current.add(url);
        const voiceover = {
          id: createSecureId("voiceover"),
          name: `Voice-over ${voiceovers.length + 1}`,
          file,
          url,
          startTime: voiceoverStartTimeRef.current,
          duration,
          sourceDuration: duration,
          trimStart: 0,
          volume: 1,
        };
        setVoiceovers(current => [...current, voiceover]);
        setStudioActionMessage("Voice-over take placed on its own timeline track.");
      };
      voiceoverRecorderRef.current = recorder;
      recorder.start(150);
      setIsVoiceoverRecording(true);
      setStudioActionMessage("Recording voice-over at the current playhead. Press stop when done.");
    } catch (error) {
      voiceoverStreamRef.current?.getTracks().forEach(track => track.stop());
      voiceoverStreamRef.current = null;
      setIsVoiceoverRecording(false);
      setStudioActionMessage(error.message || "Microphone permission was not granted.");
    } finally {
      voiceoverRequestPendingRef.current = false;
    }
  };

  const addFinishKeyframeAtPlayhead = () => {
    const resolved = resolveMagneticTimelineTime(previewTimelineTime);
    setFinishKeyframes(current => upsertFinishKeyframe(current, resolved.time, finishFx));
    setStudioActionMessage(
      resolved.snapped
        ? `Grade keyframe snapped to the ${resolved.time.toFixed(2)}s music beat.`
        : `Grade keyframe added at ${resolved.time.toFixed(2)}s.`
    );
  };

  const seekFinishKeyframe = keyframe => {
    seekLiveEditTimelineItem(Number(keyframe?.time || 0), null);
    setStudioActionMessage(
      `Finish keyframe selected at ${Number(keyframe?.time || 0).toFixed(2)}s.`
    );
  };

  const removeFinishKeyframe = keyframeId => {
    setFinishKeyframes(current => current.filter(keyframe => keyframe.id !== keyframeId));
    setStudioActionMessage("Finish keyframe removed. Undo restores it.");
  };

  const addReframeKeyframeAtPlayhead = () => {
    const time = Number(previewTimelineTime || 0);
    const currentPosition = interpolateReframeKeyframes(reframeKeyframes, time);
    const existing = reframeKeyframes.find(
      keyframe => Math.abs(Number(keyframe.time) - time) < 0.08
    );
    const id = existing?.id || createSecureId("reframe-keyframe");
    const keyframe = {
      id,
      time: Number(time.toFixed(3)),
      x: clampNumber(currentPosition.x, 5, 95, 50),
      y: clampNumber(currentPosition.y, 8, 92, 50),
    };
    setReframeKeyframes(current =>
      [...current.filter(item => item.id !== id), keyframe].sort(
        (left, right) => Number(left.time) - Number(right.time)
      )
    );
    setActiveReframeKeyframeId(id);
    setSmartCrop(true);
    setSmartCropMode("speaker_track");
    setStudioActionMessage(`Manual framing correction added at ${time.toFixed(2)}s.`);
  };

  const updateActiveReframeKeyframe = updates => {
    if (!activeReframeKeyframeId) return;
    setReframeKeyframes(current =>
      current.map(keyframe =>
        keyframe.id === activeReframeKeyframeId
          ? {
              ...keyframe,
              ...updates,
              ...(("x" in updates || "y" in updates) ? { sourceFocus: null } : {}),
              x: clampNumber(updates.x ?? keyframe.x, 5, 95, 50),
              y: clampNumber(updates.y ?? keyframe.y, 8, 92, 50),
            }
          : keyframe
      )
    );
  };

  const removeActiveReframeKeyframe = () => {
    if (!activeReframeKeyframeId) return;
    setReframeKeyframes(current =>
      current.filter(keyframe => keyframe.id !== activeReframeKeyframeId)
    );
    setActiveReframeKeyframeId(null);
    setStudioActionMessage(
      "Manual framing correction removed. Remaining reviewed points continue, or the crop returns to its centered fallback."
    );
  };

  const updateSpeakerStackFrame = (speaker, field, value) => {
    setSpeakerStackFraming(current => ({
      ...current,
      [speaker]: {
        ...current[speaker],
        [field]: Number(value),
      },
    }));
    setSmartCrop(true);
    setSmartCropMode("group_stack");
    setComparisonMode("after");
  };

  const addSpeakerFocusCutAtPlayhead = slot => {
    if (!["top", "bottom"].includes(slot)) return;
    const time = Math.max(0, Number(previewTimelineTime || 0));
    const roundedTime = Number(time.toFixed(3));
    const existing = speakerFocusCuts.find(cut => Math.abs(Number(cut.time) - time) < 0.08);
    const nextCut = {
      id: existing?.id || createSecureId("speaker-focus"),
      time: roundedTime,
      slot,
    };
    setSpeakerFocusCuts(current =>
      normalizeSpeakerFocusCuts([
        ...current.filter(cut => cut.id !== nextCut.id),
        nextCut,
      ])
    );
    setStudioActionMessage(
      `${MULTICAM_SLOT_LABELS[slot]} marked as the reviewed speaker at ${time.toFixed(2)}s.`
    );
  };

  const applyCreativeIntent = intentId => {
    setCreativeIntent(intentId);
    if (intentId === "trim") {
      setSilenceRemoval(true);
      setPacingLevel("energetic");
      changePreviewSpeed(1.15);
      setStudioInspectorTab("pacing");
      setActiveCreativeTool("pacing");
      setStudioActionMessage(
        "Boring pauses are marked for tightening and the preview is running at a sharper pace."
      );
      return;
    }
    if (intentId === "energy") {
      setAutoCaptions(true);
      setCaptionStyle("bold_pop");
      setPacingLevel("energetic");
      changePreviewSpeed(1.25);
      setHookZoomScale(current => Math.max(1.12, Number(current || 1.08)));
      setStudioActionMessage(
        "Energy pass ready: faster pacing, magnetic captions, and a stronger punch-in."
      );
      return;
    }
    if (intentId === "proof") {
      setStudioInspectorTab("broll");
      setActiveCreativeTool("broll");
      setStudioActionMessage(
        "Proof mode is open. Place evidence exactly where the claim needs visual support."
      );
      return;
    }
    setHookPreviewLoop(true);
    setStudioInspectorTab("hook");
    setActiveCreativeTool("hook");
    setStudioActionMessage(
      "Loop preview is ready. The ending can now be reviewed against the opening before render."
    );
  };

  const applyMakeItHit = () => {
    setAutoCaptions(true);
    setCaptionStyle("story_pop");
    setCaptionPosition("bottom_center");
    setCaptionScale(0.94);
    setSilenceRemoval(true);
    setSmartCrop(true);
    setSmartCropMode("speaker_track");
    setPacingLevel("balanced");
    setCreativeIntent("proof");
    setCreativeEffectsEnabled(false);
    setCreativeIntensity("bold");
    setHookZoomScale(current => Math.max(1.08, Number(current || 1.08)));
    changePreviewSpeed(1.05);
    setComparisonMode("after");

    const reviewedSegments = normalizeCaptionSegments(captionSegments);
    if (!reviewedSegments.length) {
      setStudioInspectorTab("captions");
      setActiveCreativeTool("captions");
      setStudioActionMessage(
        "Make It Hit paused at understanding: generating the real multilingual transcript before proposing visuals or effects."
      );
      void generateLiveTranscript();
      return;
    }
    const reviewBlockers = reviewedSegments.filter(segment => segment.reviewRequired);
    if (reviewBlockers.length) {
      setStudioInspectorTab("captions");
      setActiveCreativeTool("captions");
      focusCaptionSegmentsForReview(reviewedSegments);
      setStudioActionMessage(
        `Make It Hit paused: review ${reviewBlockers.length} uncertain speaker/language ${reviewBlockers.length === 1 ? "line" : "lines"} before story visuals are planned.`
      );
      return;
    }

    setStudioInspectorTab("broll");
    setActiveCreativeTool("broll");
    setAutoBRollRequest({projectId, nonce:createSecureId("broll-generation")});
    setStudioActionMessage(
      "Make It Hit is choosing useful cutaways from the reviewed transcript. Generated shots that pass visual checks can be continued with the normal B-roll controls."
    );
  };

  useEffect(() => {
    const programmeVideo = videoRef.current;
    const videosBySlot = {
      top: speakerStackTopVideoRef.current,
      bottom: speakerStackBottomVideoRef.current,
      third: speakerStackThirdVideoRef.current,
      fourth: speakerStackFourthVideoRef.current,
    };
    const sourcesBySlot = {
      top: speakerStackTopSource,
      bottom: speakerStackBottomSource,
      third: speakerStackThirdSource,
      fourth: speakerStackFourthSource,
    };
    const activeVideos = activeSpeakerStackSlots.map(slot => videosBySlot[slot]);
    if (
      !programmeVideo ||
      !prepareSpeakerStackMedia ||
      activeVideos.some(video => !video) ||
      !speakerStackIsReady
    ) {
      return undefined;
    }
    let driftCorrectionTimer = null;

    const syncAngle = (angleVideo, source, offset, forceSeek = false) => {
      if (!angleVideo || !source) return;
      const baseTime = source.isProgrammeSource
        ? Number(programmeVideo.currentTime || 0)
        : Math.max(
            0,
            Number(programmeVideo.currentTime || 0) - Number(selectedClip?.start || 0)
          );
      const requestedTime = Math.max(0, baseTime + Number(offset || 0));
      const duration = Number(angleVideo.duration || source.clip?.duration || 0);
      const targetTime = duration > 0 ? Math.min(requestedTime, Math.max(0, duration - 0.04)) : requestedTime;
      const shouldSeek =
        forceSeek || Math.abs(Number(angleVideo.currentTime || 0) - targetTime) > 0.14;
      if (shouldSeek) {
        try {
          angleVideo.currentTime = targetTime;
        } catch (error) {
          console.log("Speaker angle seek skipped", error);
        }
      }
      angleVideo.playbackRate = Number(programmeVideo.playbackRate || 1);
      if (!programmeVideo.paused && speakerStackPreviewIsLive) {
        if (angleVideo.seeking) {
          angleVideo.addEventListener(
            "seeked",
            () => safePlayMediaElement(angleVideo),
            { once: true }
          );
        } else {
          safePlayMediaElement(angleVideo);
        }
      } else {
        angleVideo.pause();
      }
    };
    const syncSpeakerStack = (forceSeek = false) => {
      activeSpeakerStackSlots.forEach(slot =>
        syncAngle(
          videosBySlot[slot],
          sourcesBySlot[slot],
          speakerStackOffsets[slot],
          forceSeek
        )
      );
    };
    const stopDriftCorrection = () => {
      if (driftCorrectionTimer !== null) {
        window.clearInterval(driftCorrectionTimer);
        driftCorrectionTimer = null;
      }
    };
    const startSpeakerStack = () => {
      syncSpeakerStack(true);
      stopDriftCorrection();
      driftCorrectionTimer = window.setInterval(() => syncSpeakerStack(false), 120);
    };
    const correctSpeakerStackDrift = () => syncSpeakerStack(false);
    const seekSpeakerStack = () => syncSpeakerStack(true);
    const pauseSpeakerStack = () => {
      stopDriftCorrection();
      activeVideos.forEach(video => video.pause());
      syncSpeakerStack(true);
    };

    programmeVideo.addEventListener("play", startSpeakerStack);
    programmeVideo.addEventListener("timeupdate", correctSpeakerStackDrift);
    programmeVideo.addEventListener("seeked", seekSpeakerStack);
    programmeVideo.addEventListener("ratechange", correctSpeakerStackDrift);
    programmeVideo.addEventListener("pause", pauseSpeakerStack);
    if (!programmeVideo.paused && speakerStackPreviewIsLive) {
      startSpeakerStack();
    } else {
      syncSpeakerStack(true);
    }

    return () => {
      programmeVideo.removeEventListener("play", startSpeakerStack);
      programmeVideo.removeEventListener("timeupdate", correctSpeakerStackDrift);
      programmeVideo.removeEventListener("seeked", seekSpeakerStack);
      programmeVideo.removeEventListener("ratechange", correctSpeakerStackDrift);
      programmeVideo.removeEventListener("pause", pauseSpeakerStack);
      stopDriftCorrection();
      activeVideos.forEach(video => video.pause());
    };
  }, [
    activeSpeakerStackSlots,
    speakerStackIsReady,
    currentTimelineWindow.start,
    selectedClip?.start,
    speakerStackBottomSource,
    speakerStackThirdSource,
    speakerStackFourthSource,
    speakerStackOffsets.bottom,
    speakerStackOffsets.third,
    speakerStackOffsets.fourth,
    speakerStackOffsets.top,
    speakerStackPreviewIsLive,
    prepareSpeakerStackMedia,
    speakerStackTopSource,
  ]);

  useEffect(() => {
    const programmeVideo = videoRef.current;
    const alternateVideo = sourceSplitAlternateVideoRef.current;
    if (!programmeVideo || !alternateVideo) return undefined;
    const cacheAlternateFrame = () => {
      if (alternateVideo.readyState < 2 || !alternateVideo.videoWidth || !alternateVideo.videoHeight) return;
      let cached = sourceSplitAlternateFrameRef.current;
      if (!cached?.canvas) {
        cached = { canvas: document.createElement("canvas"), sourceTime: 0 };
        sourceSplitAlternateFrameRef.current = cached;
      }
      if (
        cached.canvas.width !== alternateVideo.videoWidth ||
        cached.canvas.height !== alternateVideo.videoHeight
      ) {
        cached.canvas.width = alternateVideo.videoWidth;
        cached.canvas.height = alternateVideo.videoHeight;
      }
      const cachedContext = cached.canvas.getContext("2d");
      if (!cachedContext) return;
      try {
        cachedContext.drawImage(alternateVideo, 0, 0, cached.canvas.width, cached.canvas.height);
        cached.sourceTime = Number(alternateVideo.currentTime || 0);
      } catch (_) {}
    };
    const syncAlternateAngle = () => {
      const outputTime = getPreviewTimelineTime(programmeVideo.currentTime || 0);
      const mode = resolveReframeModeAtTime(
        reframeModeCuts, outputTime, smartCrop ? smartCropMode : "off"
      );
      const normalizedCuts = normalizeReframeModeCuts(reframeModeCuts);
      const activeCut = resolveReframeCutAtTime(normalizedCuts, outputTime);
      const soloOffset = mode === "speaker_track"
        ? Number(activeCut?.sourceTimeOffsetSeconds || 0)
        : 0;
      const nextAlternateCut = (mode === "center" || Math.abs(soloOffset) > .001) ? null : normalizedCuts.find(
        cut => (cut.mode === "center" || Math.abs(Number(cut.sourceTimeOffsetSeconds || 0)) > .001) &&
          cut.time > outputTime && cut.time - outputTime <= 12
      );
      if (mode !== "center" && Math.abs(soloOffset) <= .001 && !nextAlternateCut) {
        alternateVideo.pause();
        return;
      }
      const cueTime = nextAlternateCut ? nextAlternateCut.time : outputTime;
      const offsetCut = nextAlternateCut || activeCut;
      const offset = Number(offsetCut?.sourceTimeOffsetSeconds || 0) ||
        resolveSourceSplitTimeOffset(speakerStackFraming.bottom, cueTime) ||
        resolveSourceSplitTimeOffset(speakerStackFraming.top, cueTime);
      if (!offset) {
        alternateVideo.pause();
        return;
      }
      // Cue the clean, full-frame alternate speaker shot before the split. The programme
      // keeps its own source clock and remains the only audible media element.
      const target = Math.max(0, Number(programmeVideo.currentTime || 0) +
        Math.max(0, cueTime - outputTime) + offset);
      const duration = Number(alternateVideo.duration || 0);
      const bounded = duration > 0 ? Math.min(target, Math.max(0, duration - 0.04)) : target;
      // Avoid repeatedly flushing decoded frames while both videos are
      // playing. Small sub-frame drift is less disruptive than a blank card.
      if (Math.abs(Number(alternateVideo.currentTime || 0) - bounded) >
          (mode === "center" && !programmeVideo.paused ? 0.35 : 0.08)) {
        try {
          alternateVideo.currentTime = bounded;
        } catch (_) {}
      }
      alternateVideo.playbackRate = Number(programmeVideo.playbackRate || 1);
      if (!programmeVideo.paused && (mode === "center" || Math.abs(soloOffset) > .001)) {
        void safePlayMediaElement(alternateVideo);
      } else {
        alternateVideo.pause();
      }
    };
    programmeVideo.addEventListener("play", syncAlternateAngle);
    programmeVideo.addEventListener("pause", syncAlternateAngle);
    programmeVideo.addEventListener("seeking", syncAlternateAngle);
    programmeVideo.addEventListener("seeked", syncAlternateAngle);
    programmeVideo.addEventListener("timeupdate", syncAlternateAngle);
    alternateVideo.addEventListener("loadeddata", cacheAlternateFrame);
    alternateVideo.addEventListener("seeked", cacheAlternateFrame);
    alternateVideo.addEventListener("timeupdate", cacheAlternateFrame);
    syncAlternateAngle();
    return () => {
      programmeVideo.removeEventListener("play", syncAlternateAngle);
      programmeVideo.removeEventListener("pause", syncAlternateAngle);
      programmeVideo.removeEventListener("seeking", syncAlternateAngle);
      programmeVideo.removeEventListener("seeked", syncAlternateAngle);
      programmeVideo.removeEventListener("timeupdate", syncAlternateAngle);
      alternateVideo.removeEventListener("loadeddata", cacheAlternateFrame);
      alternateVideo.removeEventListener("seeked", cacheAlternateFrame);
      alternateVideo.removeEventListener("timeupdate", cacheAlternateFrame);
      alternateVideo.pause();
    };
  }, [
    activeTimelineIndex,
    currentTimelineClip,
    reframeModeCuts,
    smartCrop,
    smartCropMode,
    speakerStackFraming,
  ]);

  useLayoutEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    const syncAutomationRate = () => {
      // The completed movie already contains the canonical speed plan.
      if (renderedOutputUrl) { video.playbackRate = 1; return; }
      const outputTime = getPreviewTimelineTime(video.currentTime || 0);
      const tick = secondsToTicks(Math.max(0, outputTime));
      const segment = previewOutputTimeMap.find(item => tick >= item.programmeRange.startTick &&
        tick < item.programmeRange.endTick) || previewOutputTimeMap.at(-1);
      const rate = segment ? segment.rateNumerator / segment.rateDenominator : previewSpeed;
      video.playbackRate = clampNumber(rate, 0.25, 4, 1);
    };
    syncAutomationRate();
    video.addEventListener("timeupdate", syncAutomationRate);
    video.addEventListener("seeking", syncAutomationRate);
    const stopRateClock = !renderedOutputUrl ? watchStudioAudioClock(video, () => {
      if (video === videoRef.current) syncAutomationRate();
    }) : () => {};
    return () => {
      stopRateClock();
      video.removeEventListener("timeupdate", syncAutomationRate);
      video.removeEventListener("seeking", syncAutomationRate);
    };
  }, [activeTimelineIndex, currentTimelineClip, previewSpeed, previewOutputTimeMap, renderedOutputUrl]);

  useEffect(() => {
    if (!addHook) {
      setHookPreviewLoop(false);
      hookPreviewSequenceRef.current = { active: false };
    }
  }, [addHook]);

  useEffect(() => {
    const music = musicPreviewRef.current;
    if (!music) return undefined;

    const markPlaying = () => setIsBackgroundSoundPreviewing(true);
    const markStopped = () => {
      if (!musicPreviewSourceRef.current) setIsBackgroundSoundPreviewing(false);
    };

    music.addEventListener("play", markPlaying);
    music.addEventListener("playing", markPlaying);
    music.addEventListener("pause", markStopped);
    music.addEventListener("ended", markStopped);
    music.addEventListener("error", markStopped);
    music.addEventListener("abort", markStopped);

    return () => {
      music.removeEventListener("play", markPlaying);
      music.removeEventListener("playing", markPlaying);
      music.removeEventListener("pause", markStopped);
      music.removeEventListener("ended", markStopped);
      music.removeEventListener("error", markStopped);
      music.removeEventListener("abort", markStopped);
    };
  }, []);

  useEffect(() => {
    let isCancelled = false;

    if (!silenceRemoval || !currentTimelineClip) {
      setSilencePreview(null);
      return undefined;
    }

    const silenceBinding = { clipId: currentTimelineClip.id,
      sourceStoragePath: currentTimelineClip.sourceStoragePath || currentTimelineClip.storagePath || null,
      contentHash: currentTimelineClip.contentHash || currentTimelineClip.sha256 || null,
      silenceThreshold, minSilenceDuration };
    if (silencePreview && Object.entries(silenceBinding).every(([key, value]) => silencePreview[key] === value) &&
      Array.isArray(silencePreview.keepSegments)) return undefined;

    const loadSilencePreview = async () => {
      try {
        const token = await getMediaAuthToken();
        if (!token) return;
        const fileUrl = await ensurePreviewableClipUrl(currentTimelineClip);
        if (!fileUrl) return;

        const response = await fetch(`${API_BASE_URL}/api/media/preview-silence`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            fileUrl,
            silenceThreshold,
            minSilenceDuration,
          }),
        });

        const payload = await response.json();
        if (!isCancelled) {
          if (!response.ok) throw new Error(payload.error || "Silence analysis failed.");
          setSilencePreview({
            ...silenceBinding,
            silenceSegments: payload.silence_segments || [],
            keepSegments: payload.keep_segments || [],
          });
        }
      } catch (error) {
        console.error("Silence preview failed", error);
        if (!isCancelled) setSilencePreview(null);
      }
    };

    loadSilencePreview();
    return () => {
      isCancelled = true;
    };
  }, [silenceRemoval, silenceThreshold, minSilenceDuration, currentTimelineClip]);

  useEffect(() => {
    setWatermarkCleanupPreview(null);
    setWatermarkCleanupPreviewError("");
    setShowWatermarkCleanupOnVideo(true);
  }, [removeWatermark, watermarkMode, currentTimelineClip?.id, manualWatermarkRegions]);

  const handleGenerateWatermarkCleanupPreview = async () => {
    if (!currentTimelineClip || !removeWatermark) return;
    if (watermarkMode === "manual" && !manualWatermarkRegions.length) {
      setWatermarkCleanupPreview(null);
      setWatermarkCleanupPreviewError(
        "Add at least one cleanup box before requesting a real preview."
      );
      return;
    }

    setIsWatermarkCleanupPreviewLoading(true);
    setWatermarkCleanupPreviewError("");

    try {
      const auth = getAuth();
      const user = auth.currentUser;
      if (!user) throw new Error("Please login first");

      const token = await user.getIdToken();
      const fileUrl = await ensurePreviewableClipUrl(currentTimelineClip);
      if (!fileUrl) throw new Error("No previewable source clip available");

      const video = videoRef.current;
      const previewStart = Number(currentTimelineWindow.start || 0);
      const previewEnd = Number(
        currentTimelineWindow.end || currentTimelineWindow.duration || previewStart
      );
      const previewTime = clampNumber(
        Number(video?.currentTime ?? previewStart),
        previewStart,
        previewEnd || previewStart,
        previewStart
      );
      const syncedManualRegions =
        watermarkMode === "manual"
          ? manualWatermarkRegions.map(region =>
              region.track ? { ...region, seedTime: previewTime } : region
            )
          : manualWatermarkRegions;

      if (watermarkMode === "manual") {
        setManualWatermarkRegions(syncedManualRegions.map(clampManualWatermarkRegion));
      }

      const response = await fetch(`${API_BASE_URL}/api/media/preview-watermark-cleanup`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          fileUrl,
          watermarkMode,
          manualWatermarkRegions:
            watermarkMode === "manual" ? serializeManualWatermarkRegions(syncedManualRegions) : [],
          previewTime,
        }),
      });

      const payload = await response.json();
      if (!response.ok) {
        throw new Error(
          payload?.details || payload?.message || "Failed to render watermark cleanup preview"
        );
      }

      setWatermarkCleanupPreview({
        clipId: currentTimelineClip.id,
        mode: watermarkMode,
        previewTime: Number(payload.preview_time ?? previewTime),
        originalImageUrl: payload.original_image_url || "",
        cleanedImageUrl: payload.cleaned_image_url || "",
        filters: Array.isArray(payload.filters) ? payload.filters : [],
      });
      setShowWatermarkCleanupOnVideo(true);
    } catch (error) {
      console.error("Watermark cleanup preview failed", error);
      setWatermarkCleanupPreview(null);
      setWatermarkCleanupPreviewError(
        error.message || "Failed to render watermark cleanup preview"
      );
    } finally {
      setIsWatermarkCleanupPreviewLoading(false);
    }
  };

  useEffect(() => {
    let isCancelled = false;
    const abortController = new AbortController();

    if (!addMusic || !musicSearchMode || !musicSelection.trim()) {
      stopMusicPreviewBufferPlayback();
      musicPreviewBufferRef.current = null;
      releaseMusicPreviewObjectUrl();
      setMusicPreviewUrl("");
      setMusicPreviewNeedsGesture(false);
      if (!addMusic) {
        setMusicPreviewStatus("idle");
        setMusicPreviewStatusMessage("");
      } else if (!musicSearchMode) {
        setMusicPreviewStatus("ready");
        setMusicPreviewStatusMessage(
          musicSelection ? `Preset ready: ${currentMusicLabel}` : "Choose a music preset."
        );
      } else {
        setMusicPreviewStatus("idle");
        setMusicPreviewStatusMessage("Enter a search query to load preview audio.");
      }
      return undefined;
    }

    const timeoutId = window.setTimeout(async () => {
      try {
        if (!isCancelled) {
          setMusicPreviewStatus("processing");
          setMusicPreviewStatusMessage(
            `Searching and preparing preview audio for ${currentMusicLabel}...`
          );
        }
        const auth = getAuth();
        const user = auth.currentUser;
        if (!user) return;
        const token = await user.getIdToken();

        const response = await fetch(`${API_BASE_URL}/api/media/preview-music`, {
          method: "POST",
          signal: abortController.signal,
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            musicFile: musicSelection,
            isSearch: true,
            safeSearch,
            previewDuration: 20,
          }),
        });

        if (!response.ok) throw new Error("Failed to load music preview");
        const payload = await response.json();
        const nextPreviewUrl = await materializeMusicPreviewUrl(payload.preview_url || "");
        if (!nextPreviewUrl) {
          throw new Error("Music search completed but no preview audio was returned");
        }
        if (!isCancelled) {
          setMusicPreviewUrl(nextPreviewUrl);
          setMusicPreviewNeedsGesture(false);
          setMusicPreviewStatus("processing");
          setMusicPreviewStatusMessage(`Preview found for ${currentMusicLabel}. Loading audio...`);
        }
      } catch (error) {
        if (error?.name === "AbortError") {
          return;
        }
        console.error("Music preview failed", error);
        if (!isCancelled) {
          releaseMusicPreviewObjectUrl();
          setMusicPreviewUrl("");
          setMusicPreviewStatus("failed");
          setMusicPreviewStatusMessage(error.message || "Music preview failed");
        }
      }
    }, 550);

    return () => {
      isCancelled = true;
      abortController.abort();
      window.clearTimeout(timeoutId);
    };
  }, [addMusic, musicSearchMode, musicSelection, safeSearch]);

  useEffect(() => {
    const music = musicPreviewRef.current;
    if (!music) return;

    if (!addMusic || !effectiveMusicPreviewUrl) {
      stopMusicPreviewBufferPlayback();
      musicPreviewBufferRef.current = null;
      setMusicBeatMarkers([]);
      setMusicPreviewNeedsGesture(false);
      return;
    }

    if (musicSearchMode) {
      music.pause();
      music.removeAttribute("src");
      music.load();
      setMusicPreviewNeedsGesture(false);
      return;
    }

    music.pause();
    music.load();
    setMusicPreviewNeedsGesture(false);
  }, [addMusic, effectiveMusicPreviewUrl, musicSearchMode]);

  useEffect(() => {
    const beatSourceUrl = effectiveMusicPreviewUrl;
    // Private link refreshes change access, not the owned audio bytes. Reuse
    // only an exact project/owner/path/hash match; arbitrary URLs never qualify.
    const bufferIdentity = studioMusicBufferIdentity(projectId, musicTrack);
    musicPreviewDecodePromiseRef.current = null;
    if (addMusic && beatSourceUrl && bufferIdentity && musicPreviewBufferRef.current &&
        bufferIdentity === musicPreviewBufferIdentityRef.current) {
      musicPreviewBufferUrlRef.current = beatSourceUrl;
      setDecodedMusicPreviewUrl(beatSourceUrl);
      return undefined;
    }
    musicPreviewBufferIdentityRef.current = "";
    musicPreviewBufferUrlRef.current = "";
    setDecodedMusicPreviewUrl("");
    if (!addMusic || !beatSourceUrl) {
      stopMusicPreviewBufferPlayback();
      musicPreviewBufferRef.current = null;
      setMusicBeatMarkers([]);
      return undefined;
    }
    if (!(window.AudioContext || window.webkitAudioContext)) {
      musicPreviewBufferRef.current = null;
      setMusicBeatMarkers([]);
      return undefined;
    }

    let isCancelled = false;
    const abortController = new AbortController();

    const decodeMusicPreview = async () => {
      try {
        setMusicBeatMarkers([]);
        musicPreviewBufferRef.current = null;
        const audioContext = ensureMusicPreviewAudioContext();
        const response = await fetch(beatSourceUrl, { signal: abortController.signal });
        if (!response.ok) throw new Error("The selected music could not be loaded for beat analysis.");
        const arrayBuffer = await response.arrayBuffer();
        const decodedBuffer = await audioContext.decodeAudioData(arrayBuffer.slice(0));
        if (isCancelled) return;

        musicPreviewBufferRef.current = decodedBuffer;
        musicPreviewBufferIdentityRef.current = bufferIdentity;
        musicPreviewBufferUrlRef.current = beatSourceUrl;
        setDecodedMusicPreviewUrl(beatSourceUrl);
        const rhythmAnalysis = detectAudioBeats(analyzeAudioBufferBeats(decodedBuffer));
        setMusicBeatMarkers(rhythmAnalysis.beats.slice(0, 160));
        setMusicPreviewStatus("ready");
        setMusicPreviewStatusMessage(
          rhythmAnalysis.beats.length
            ? `${rhythmAnalysis.beats.length} magnetic beat points ready for ${currentMusicLabel}.`
            : `Preview audio ready for ${currentMusicLabel}. Smart timing will stay frame-based.`
        );
      } catch (error) {
        if (error?.name === "AbortError") return;

        console.error("Music preview decode failed", error);
        if (!isCancelled) {
          musicPreviewBufferRef.current = null;
          setMusicBeatMarkers([]);
          setMusicPreviewStatus("failed");
          setMusicPreviewStatusMessage(error.message || "Preview audio could not be decoded.");
        }
      }
    };

    musicPreviewDecodePromiseRef.current = decodeMusicPreview();

    return () => {
      isCancelled = true;
      abortController.abort();
      stopMusicPreviewBufferPlayback();
    };
  }, [addMusic, musicSearchMode, musicPreviewUrl, effectiveMusicPreviewUrl, currentMusicLabel,
    projectId, musicTrack?.ownerUid, musicTrack?.storagePath, musicTrack?.contentHash]);

  const importedCueBindings = JSON.stringify(soundEffects.filter(effect =>
    !effect.builtIn && effect.kind !== "voiceover" && effect.url && effect.enabled !== false)
    .map(effect => [studioCueSourceKey(projectId, effect), effect.url]));
  useEffect(() => {
    if (!(window.AudioContext || window.webkitAudioContext)) return undefined;
    const bindings = JSON.parse(importedCueBindings), keys = new Set(bindings.map(([key]) => key));
    const cache = soundEffectDecodedSourcesRef.current;
    for (const key of cache.keys()) if (!keys.has(key)) cache.delete(key);
    soundEffectDecodePromisesRef.current.clear();
    const controller = new AbortController();
    const lifecycle = { cancelled: false };
    for (const [key, url] of new Map(bindings)) {
      if (!key || cache.has(key)) continue;
      const promise = (async () => {
        try {
          const response = await fetch(url, { signal: controller.signal });
          if (!response.ok || Number(response.headers?.get('content-length') || 0) > 32 * 1024 * 1024) return null;
          const bytes = await response.arrayBuffer();
          if (bytes.byteLength > 32 * 1024 * 1024 || lifecycle.cancelled) return null;
          const context = ensureSoundEffectAudioContext();
          const decoded = await context.decodeAudioData(bytes.slice(0));
          const memory = decoded.length * decoded.numberOfChannels * 4;
          if (lifecycle.cancelled || decoded.numberOfChannels > 2 || memory > 32 * 1024 * 1024 ||
              [...cache.values()].reduce((sum, entry) => sum + entry.memory, 0) + memory > 96 * 1024 * 1024) return null;
          const entry = { key, decoded, memory, cues: new Map() };
          cache.set(key, entry);
          return entry;
        } catch (_) { return null; } // The real native decoder remains the fallback.
      })();
      soundEffectDecodePromisesRef.current.set(key, promise);
    }
    return () => { lifecycle.cancelled = true; controller.abort(); };
  }, [importedCueBindings]);

  useEffect(() => {
    const music = musicPreviewRef.current;
    if (!music || !addMusic || musicSearchMode || !effectiveMusicPreviewUrl) return undefined;

    const markReady = () => {
      setMusicPreviewStatus("ready");
      setMusicPreviewStatusMessage(`Preview audio ready for ${currentMusicLabel}.`);
    };

    const markPlaying = () => {
      setMusicPreviewNeedsGesture(false);
      setMusicPreviewStatus("ready");
      setMusicPreviewStatusMessage(`Preview audio is playing for ${currentMusicLabel}.`);
    };

    const markFailed = () => {
      setMusicPreviewStatus("failed");
      setMusicPreviewStatusMessage(`Preview audio failed to load for ${currentMusicLabel}.`);
    };

    music.addEventListener("loadeddata", markReady);
    music.addEventListener("canplay", markReady);
    music.addEventListener("canplaythrough", markReady);
    music.addEventListener("play", markPlaying);
    music.addEventListener("playing", markPlaying);
    music.addEventListener("error", markFailed);
    music.addEventListener("stalled", markFailed);
    music.addEventListener("abort", markFailed);

    if (music.readyState >= 2) {
      markReady();
    }

    return () => {
      music.removeEventListener("loadeddata", markReady);
      music.removeEventListener("canplay", markReady);
      music.removeEventListener("canplaythrough", markReady);
      music.removeEventListener("play", markPlaying);
      music.removeEventListener("playing", markPlaying);
      music.removeEventListener("error", markFailed);
      music.removeEventListener("stalled", markFailed);
      music.removeEventListener("abort", markFailed);
    };
  }, [addMusic, musicSearchMode, effectiveMusicPreviewUrl, currentMusicLabel]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return undefined;
    let current = true;
    remixPreviewMediaRef.current.add(video);
    const pitchFailure = () => setAudioRemixLiveError("Live voice pitch is unavailable in this browser. Use Preview Remix to hear the mastered voice.");
    video.addEventListener("studio-remix-pitch-error", pitchFailure);
    if (audioRemix.enabled && !renderedOutputUrl) {
      void updateAudioRemixPreview(video, audioRemix, audioRemixBypass, { playbackRate: previewSpeed }).then(ready => {
        if (current) setAudioRemixLiveError(!ready && !audioRemixBypass && !audioRemix.keepPitch && audioRemix.pitch
          ? "Live voice pitch is unavailable in this browser. Use Preview Remix to hear the mastered voice." : "");
      });
    } else {
      void updateAudioRemixPreview(video, { ...audioRemix, enabled: false }, true);
      video.playbackRate = renderedOutputUrl ? 1 : previewSpeed;
      video.preservesPitch = true;
      video.mozPreservesPitch = true;
      video.webkitPreservesPitch = true;
      setAudioRemixLiveError("");
    }
    return () => { current = false; video.removeEventListener("studio-remix-pitch-error", pitchFailure); };
  }, [activeTimelineIndex, currentTimelineClip, previewSpeed, audioRemix, audioRemixBypass, renderedOutputUrl]);

  useEffect(() => {
    if (!audioRemixPlaybackRequest) return;
    // Apply source/rate/ref changes first, then use the same preparation,
    // intention and transport as the ordinary Studio Play control.
    void toggleComparisonPlayback({ resumeOnly: true });
    // Each explicit Sound action is one request, not a restart on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [audioRemixPlaybackRequest]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !audioRemixLoop) return undefined;
    const keepLooping = () => {
      if (
        video.currentTime >= audioRemixLoop.end - 0.04 ||
        video.currentTime < audioRemixLoop.start - 0.25
      ) {
        video.currentTime = audioRemixLoop.start;
        safePlayMediaElement(video);
      }
    };
    video.addEventListener("timeupdate", keepLooping);
    return () => video.removeEventListener("timeupdate", keepLooping);
  }, [audioRemixLoop, activeTimelineIndex, currentTimelineClip]);

  useEffect(() => {
    const exactPreview = exactAudioRemixPreviewRef.current;
    exactPreview?.pause();
    backgroundSoundPreviewSuppressedRef.current = false;
    setExactAudioRemixPreview(current =>
      current.status === "idle" ? current : { status: "idle", url: "", error: "", receipt: null }
    );
  }, [audioRemix, activeTimelineIndex, currentTimelineClip?.id, musicTrack?.url]);

  useEffect(() => {
    const exactPreview = exactAudioRemixPreviewRef.current;
    if (exactAudioRemixPreview.status !== "ready" || !exactAudioRemixPreview.url || !exactPreview) {
      return undefined;
    }
    pauseSynchronizedPreview();
    backgroundSoundPreviewSuppressedRef.current = true;
    if (!applySafeMediaSource(exactPreview, exactAudioRemixPreview.url)) {
      setExactAudioRemixPreview(current => ({
        ...current,
        status: "failed",
        error: "The mastered preview URL was rejected by the browser.",
      }));
      return undefined;
    }
    exactPreview.load();
    void safePlayMediaElement(exactPreview);
    return undefined;
  }, [exactAudioRemixPreview.status, exactAudioRemixPreview.url]);

  const handleExactAudioRemixPreview = async () => {
    if (!currentTimelineClip || !audioRemix.enabled) return;
    if (exactAudioRemixPreview.status === "ready" && exactAudioRemixPreview.url) {
      const exactPreview = exactAudioRemixPreviewRef.current;
      if (exactPreview) {
        pauseSynchronizedPreview();
        backgroundSoundPreviewSuppressedRef.current = true;
        exactPreview.currentTime = 0;
        void safePlayMediaElement(exactPreview);
        setStudioActionMessage("Replaying the exact mastered worker preview.");
        return;
      }
    }
    const auth = getAuth();
    const user = auth.currentUser;
    if (!user) {
      setExactAudioRemixPreview({
        status: "failed",
        url: "",
        error: "Please log in before rendering an exact audio preview.",
        receipt: null,
      });
      return;
    }

    exactAudioRemixPreviewRef.current?.pause();
    setExactAudioRemixPreview({ status: "rendering", url: "", error: "", receipt: null });
    setAudioRemixBypass(false);
    setComparisonMode("after");

    try {
      let token = await user.getIdToken();
      const fileUrl = await ensurePreviewableClipUrl(currentTimelineClip);
      if (!fileUrl) throw new Error("No previewable source clip is available.");

      const windowStart = Math.max(0, Number(currentTimelineWindow.start || 0));
      const windowEnd = Math.max(
        windowStart + 0.5,
        Number(
          currentTimelineWindow.end || windowStart + Number(currentTimelineWindow.duration || 8)
        )
      );
      const requestedStart = Number(
        audioRemixLoop?.start ?? videoRef.current?.currentTime ?? windowStart
      );
      let previewStart = clampNumber(
        requestedStart,
        windowStart,
        Math.max(windowStart, windowEnd - 0.5),
        windowStart
      );
      let previewDuration = Math.min(8, Math.max(0.5, windowEnd - previewStart));
      if (previewDuration < 2) {
        previewStart = Math.max(windowStart, windowEnd - Math.min(8, windowEnd - windowStart));
        previewDuration = Math.min(8, Math.max(2, windowEnd - previewStart));
      }
      setAudioRemixLoop({ start: previewStart, end: previewStart + previewDuration });

      let backgroundAudioUrl = musicTrack?.url || "";
      if (
        addMusic &&
        musicTrack?.file &&
        (!backgroundAudioUrl || backgroundAudioUrl.startsWith("blob:"))
      ) {
        const extension = musicTrack.file.name?.split(".").pop() || "mp3";
        const upload = await uploadSourceFileViaBackend({
          file: musicTrack.file,
          token,
          mediaType: "audio",
          fileName: `${createSecureId("remix-preview-music")}.${extension}`,
        });
        backgroundAudioUrl = upload.url;
        setMusicTrack(previous => (previous ? { ...previous, url: backgroundAudioUrl } : previous));
      }

      if (audioRemix.target === "music" && !backgroundAudioUrl.startsWith("http")) {
        throw new Error("Add an uploaded background music track before previewing Music only.");
      }

      const body = {
        fileUrl,
        startTime: previewStart,
        duration: previewDuration,
        audioRemix: audioRemixForRender(audioRemix),
        includeVoice: !muteOriginalAudio,
        backgroundAudio:
          addMusic && backgroundAudioUrl.startsWith("http")
            ? {
                url: backgroundAudioUrl,
                volume: musicTrack?.volume ?? musicVolume,
                trimStart: musicTrack?.trimStart ?? 0,
              }
            : null,
      };

      let response = await fetch(`${API_BASE_URL}/api/media/preview-audio-remix`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(body),
      });
      if (response.status === 401) {
        token = await user.getIdToken(true);
        response = await fetch(`${API_BASE_URL}/api/media/preview-audio-remix`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(body),
        });
      }
      const payload = await response.json().catch(() => null);
      if (!response.ok || !payload?.preview_url) {
        throw new Error(
          payload?.details || payload?.message || "The worker could not render the audio preview."
        );
      }
      setExactAudioRemixPreview({
        status: "ready",
        url: payload.preview_url,
        error: "",
        receipt: payload.receipt || null,
      });
      setStudioActionMessage(
        `Exact ${previewDuration.toFixed(1)}s worker preview is ready and playing.`
      );
    } catch (error) {
      console.error("Exact Remix Audio preview failed", error);
      setExactAudioRemixPreview({
        status: "failed",
        url: "",
        error: error.message || "Exact Remix Audio preview failed.",
        receipt: null,
      });
      setStudioActionMessage(error.message || "Exact Remix Audio preview failed.");
    }
  };

  const handleAudioSourceUpload = async event => {
    const sourceFile = event.target.files && event.target.files[0];
    if (!sourceFile) return;

    const auth = getAuth();
    const user = auth.currentUser;
    if (!user) {
      toast.error("Please login first");
      event.target.value = "";
      return;
    }

    setIsExtractingAudio(true);
    setAudioExtractionStatus("Uploading source video...");
    if (onStatusChange) onStatusChange("Uploading source video for audio extraction...");

    try {
      let token = await user.getIdToken();
      const uploadResult = await uploadSourceFileViaBackend({
        file: sourceFile,
        token,
        mediaType: "video",
        fileName: sourceFile.name,
      });

      setAudioExtractionStatus("Queueing extraction...");
      if (onStatusChange) onStatusChange("Queueing background-audio extraction...");

      let response = await fetch(API_ENDPOINTS.MEDIA_EXTRACT_AUDIO, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          fileUrl: uploadResult.url,
          sourceLabel: sourceFile.name,
        }),
      });

      if (response.status === 401) {
        token = await user.getIdToken(true);
        response = await fetch(API_ENDPOINTS.MEDIA_EXTRACT_AUDIO, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            fileUrl: uploadResult.url,
            sourceLabel: sourceFile.name,
          }),
        });
      }

      const startPayload = await response.json().catch(() => null);
      if (!response.ok || !startPayload?.jobId) {
        throw new Error(
          startPayload?.details || startPayload?.message || "Failed to start audio extraction"
        );
      }

      const jobId = startPayload.jobId;
      let attempts = 0;
      while (attempts < 180) {
        attempts += 1;
        await sleep(2000);

        let statusResponse = await fetch(`${API_BASE_URL}/api/media/status/${jobId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (statusResponse.status === 401) {
          token = await user.getIdToken(true);
          statusResponse = await fetch(`${API_BASE_URL}/api/media/status/${jobId}`, {
            headers: { Authorization: `Bearer ${token}` },
          });
        }

        if (!statusResponse.ok) continue;
        const statusPayload = await statusResponse.json();

        if (statusPayload.status === "failed") {
          throw new Error(statusPayload.error || "Audio extraction failed on the server");
        }

        if (statusPayload.status === "completed") {
          const result = statusPayload.result || {};
          const audioUrl = result.audioUrl || statusPayload.audio_url;
          if (!audioUrl) {
            throw new Error("Audio extraction completed but no audio URL was returned");
          }

          const audioDuration = clampAudioControl(result.audioDuration, 0, 36000, 0);
          setExtractedAudio({
            id: jobId,
            url: audioUrl,
            sourceVideoUrl: uploadResult.url,
            sourceVideoName: sourceFile.name,
            trimStart: 0,
            volume: 0.7,
            mode: "mix",
            duckingStrength: 0.45,
            enabled: true,
            duration: audioDuration,
            format: result.format || "mp3",
          });
          setAudioExtractionStatus("Background audio added to the timeline.");
          if (onStatusChange)
            onStatusChange("Background audio extracted and added to the timeline.");
          return;
        }

        const progress = clampAudioControl(statusPayload.progress, 0, 100, 0);
        const stageLabel = getAudioExtractionStageLabel(statusPayload.stage, progress);
        setAudioExtractionStatus(stageLabel);
        if (onStatusChange) onStatusChange(stageLabel);
      }

      throw new Error("Audio extraction timed out");
    } catch (error) {
      console.error("Audio extraction failed", error);
      setAudioExtractionStatus(error.message || "Audio extraction failed");
      if (onStatusChange)
        onStatusChange(`Audio extraction failed: ${error.message || "Unknown error"}`);
      toast.error(`Audio extraction failed: ${error.message || "Unknown error"}`);
    } finally {
      setIsExtractingAudio(false);
      event.target.value = "";
    }
  };

  const buildExportTimeline = async () => {
    const token = await getMediaAuthToken();
    if (!token) throw new Error("Please login first");
    const sourceUploadPromises = new Map();

    const exportSegments = await Promise.all(
      timeline.map(async clip => {
        let clipUrl = clip.url;
        let clipStoragePath = clip.sourceStoragePath || clip.storagePath || null;
        let sourceFile = clip.file instanceof Blob ? clip.file : null;

        if (!sourceFile && typeof clip.url === "string" && clip.url.startsWith("blob:")) {
          const response = await fetch(clip.url);
          sourceFile = await response.blob();
        }

        if (
          sourceFile &&
          (clip.isLocal || (typeof clip.url === "string" && clip.url.startsWith("blob:")))
        ) {
          const extension =
            clip.file?.name?.split(".").pop() || sourceFile.type?.split("/").pop() || "mp4";
          const fileName = `${Date.now()}_${clip.id}.${extension}`;
          const sourceKey = clip.sourceClipId || clip.url || clip.id;
          if (!sourceUploadPromises.has(sourceKey)) {
            sourceUploadPromises.set(
              sourceKey,
              uploadSourceFileViaBackend({
                file: sourceFile,
                token,
                getToken: forceRefresh => getMediaAuthToken(forceRefresh),
                mediaType: "video",
                fileName,
                purpose: "studio_project",
                onProgress: (transferred, total) => {
                  const percent = total > 0 ? Math.round((transferred / total) * 100) : 0;
                  setExportStatusLabel(`Uploading source ${percent}%`);
                },
              })
            );
          }
          const uploadResult = await sourceUploadPromises.get(sourceKey);
          clipUrl = uploadResult.url;
          clipStoragePath = uploadResult.storagePath || null;
        }

        const window = getTimelineClipWindow(clip);
        return {
          id: clip.id,
          source_clip_id: clip.sourceClipId || clip.id,
          url: clipUrl,
          ...(clipStoragePath ? { sourceStoragePath: clipStoragePath } : {}),
          start_time: window.start,
          end_time: window.end,
          duration: window.duration,
          transition_in: clip.transitionIn || null,
          transition_out: clip.transitionOut || null,
          transition_duration: Number(clip.transitionDuration || 0),
          audioTrimOffsetStart: Number(clip.audioTrimOffsetStart || 0),
          audioTrimOffsetEnd: Number(clip.audioTrimOffsetEnd || 0),
          ...(clip.audioDetached ? { audioDetached: true, audioSourceStart: clip.audioSourceStart,
            audioSourceEnd: clip.audioSourceEnd, audioProgrammeStart: clip.audioProgrammeStart } : {}),
        };
      })
    );

    const canonicalDocument = getEditorSnapshot().studioDocument;
    if (canonicalDocument) {
      assertStudioExportTimelineMatchesDocument(canonicalDocument, exportSegments);
    }

    if (!addHook || !exportSegments[activeTimelineIndex]) {
      return exportSegments;
    }

    const activeSegment = exportSegments[activeTimelineIndex];
    const sourceWindow = getTimelineClipWindow(timeline[activeTimelineIndex]);
    const hookSourceStart = Number(sourceWindow.start || 0) + resolvedHookStart;
    const hookSourceEnd = Number(sourceWindow.start || 0) + hookEnd;
    const hookSegmentDuration = Math.max(0.1, hookSourceEnd - hookSourceStart);

    const beforeHookDuration = Math.max(0, hookSourceStart - Number(activeSegment.start_time || 0));
    const afterHookDuration = Math.max(0, Number(activeSegment.end_time || 0) - hookSourceEnd);
    const leadingSegments = exportSegments.slice(0, activeTimelineIndex);
    const trailingSegments = exportSegments.slice(activeTimelineIndex + 1);
    const hookAwareSegments = [
      ...leadingSegments,
      ...(beforeHookDuration > 0.05
        ? [
            {
              ...activeSegment,
              id: `${activeSegment.id}-before-hook`,
              end_time: hookSourceStart,
              duration: beforeHookDuration,
            },
          ]
        : []),
      ...(afterHookDuration > 0.05
        ? [
            {
              ...activeSegment,
              id: `${activeSegment.id}-after-hook`,
              start_time: hookSourceEnd,
              duration: afterHookDuration,
            },
          ]
        : []),
      ...trailingSegments,
    ];

    return [
      {
        id: `hook-intro-${activeSegment.id}`,
        source_clip_id: activeSegment.source_clip_id || activeSegment.id,
        url: activeSegment.url,
        ...(activeSegment.sourceStoragePath
          ? { sourceStoragePath: activeSegment.sourceStoragePath }
          : {}),
        start_time: hookSourceStart,
        end_time: hookSourceEnd,
        duration: hookSegmentDuration,
      },
      ...hookAwareSegments,
    ];
  };

  const normalizeOverlaysForExport = (exportTimeline, sourceOverlays) =>
    mapStudioOverlayTiming({ overlays: sourceOverlays, timeline, exportTimeline, getWindow: getTimelineClipWindow })
      .map(layer=>({...layer,...(layer.perspective?{perspective:encodeStudioPerspective(layer.perspective)}:{})}));

  const validateCaptionCoverage = async (exportTimeline, token) => {
    const analyses = new Map();
    for (const clip of exportTimeline) {
      const sourceId = String(clip.source_clip_id || clip.id);
      if (analyses.has(sourceId)) continue;
      const response = await fetch(`${API_BASE_URL}/api/media/preview-silence`, {
        method: "POST", headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ fileUrl: clip.url, silenceThreshold: -32, minSilenceDuration: .35 }),
      });
      const payload = await response.json();
      if (!response.ok) throw new Error(payload.message || payload.error || "Caption coverage could not be checked.");
      analyses.set(sourceId, payload.has_audio === false
        ? [{ start: 0, end: Number(payload.duration || clip.end_time) }]
        : payload.silence_segments || []);
    }
    const mapped = mapCaptionSilencesToProgramme(exportTimeline, analyses);
    const captions = mapCaptionSegmentsToTimeline({ captionSegments: normalizeCaptionSegments(captionSegments),
      timelineSegments: exportTimeline, fallbackSourceClipId: currentTimelineClip?.sourceClipId || currentTimelineClip?.id || selectedClip?.id });
    const gaps = findUncoveredCaptionSpeechRanges(captions, mapped.silences, mapped.duration);
    const message = gaps.length
      ? `Possible uncovered audio: ${gaps.slice(0, 8).map(gap => `${gap.start.toFixed(1)}–${gap.end.toFixed(1)}s`).join(", ")}. Caption these phrases, or choose Selected phrases if the gaps are intentional.`
      : "Audio coverage checked. Confirm the actual words and timing by listening before delivery.";
    setCaptionCoverageMessage(message);
    return { gaps, message };
  };

  const handleExportRender = async (destination, { captionReviewCopy = false } = {}) => {
    if (isExporting) return;
    setExportErrorMessage("");
    const exportEditState = getEditorSnapshot();
    const {
      autoCaptions, captionStyle, captionPosition, captionScale, captionTextOverride, captionCoverageMode,
      captionSegments, translateCaptionsToEnglish, advancedColor, smartCrop, smartCropMode,
      reframeAspect, speakerTrackZoom, speakerStackFraming, speakerStackOffsets, speakerStackLayout,
      speakerFocusCuts, reframeModeCuts, reframeKeyframes, motionKeyframes, finishKeyframes,
      speedKeyframes, previewSpeed, adjustmentLayers, trackStates, audioKeyframes,
      voiceovers, stabilization, exportSettings, contentProfile, joinTransition,
      joinTransitionDuration, silenceRemoval, silencePreview, silenceThreshold, minSilenceDuration,
      removeWatermark, watermarkMode, brandWatermark, manualWatermarkRegions, addHook,
      hookText, hookTemplate, hookIntroSeconds, hookStartTime, hookEndTime,
      hookBlurBackground, hookDarkOverlay, hookFreezeFrame, hookZoomScale, hookTextAnimation,
      hookFocusPoint, creativeEffectsEnabled, creativePreset, creativeIntensity, creativeIntent,
      pacingLevel, beatSnapEnabled, compoundClips, splitExportEnabled, splitExportHooks,
      musicTrack, addMusic, muteOriginalAudio, musicSelection, musicSearchMode,
      safeSearch, musicVolume, musicDucking, musicDuckingStrength, audioRemix,
      soundEffects, subjectVfxScenes, creatorPreview, extractedAudio,
    } = exportEditState;
    const unavailableTitle = overlays.find(overlay => overlay.type === "text" && !overlay.bRollMode &&
      (overlay.fontFamily || "Inter") !== "Inter");
    if (unavailableTitle) {
      setActiveOverlayId(unavailableTitle.id);
      setStudioInspectorTab("titles"); setActiveCreativeTool("titles");
      const message = `Render paused: ${unavailableTitle.fontFamily} is not available in the renderer. Choose Inter in Titles; the saved font choice has been preserved.`;
      setStudioActionMessage(message); toast.error(message); return;
    }
    try {
      validateSubjectVfxExport(subjectVfxScenes, timeline.map(clip => ({
        ...clip, source_clip_id: clip.sourceClipId || clip.id,
        start_time: getTimelineClipWindow(clip).start, end_time: getTimelineClipWindow(clip).end,
      })));
    } catch (error) {
      setStudioInspectorTab("composite"); setActiveCreativeTool("composite");
      setStudioActionMessage(error.message); toast.error(error.message); return;
    }

    const enabledThreeDScenes = exportEditState.threeDScenes.filter(scene => scene.enabled !== false);
    const staleThreeDScenes = enabledThreeDScenes.filter(scene => !scene.hqPreviewJobId || scene.hqPreviewRevision !== studio3DSceneRevision(scene));
    if (staleThreeDScenes.length) {
      setStudioInspectorTab("motion");
      setMotionWorkspace("3d");
      setSelectedThreeDId(staleThreeDScenes[0].id);
      setStudioActionMessage(`Render paused: generate an HQ 3D preview for ${staleThreeDScenes.length} new or changed 3D scene${staleThreeDScenes.length === 1 ? "" : "s"}. The final video will use those verified rendered frames.`);
      toast.error("Generate an HQ 3D preview for every active 3D scene before exporting.");
      return;
    }

    const unresolvedStoryBeats = overlays.filter(
      overlay => overlay.bRollMode && overlay.bRollPlaceholder
    );
    if (unresolvedStoryBeats.length) {
      setStudioInspectorTab("broll");
      setActiveCreativeTool("broll");
      setStudioActionMessage(
        `Render paused: replace or remove ${unresolvedStoryBeats.length} planned story ${unresolvedStoryBeats.length === 1 ? "visual" : "visuals"}. Text placeholders are planning evidence, not footage.`
      );
      toast.error("Replace unresolved B-roll plans with real moving footage before rendering.");
      return;
    }

    if (
      autoCaptions &&
      !normalizePlainText(captionTextOverride) &&
      !normalizeCaptionSegments(captionSegments).length
    ) {
      setStudioInspectorTab("captions");
      setActiveCreativeTool("captions");
      setCaptionGenerationMessage("Generate captions, review every timestamped line, then render.");
      setStudioActionMessage(
        "Render paused: captions must be generated and editable before rendering."
      );
      toast.error("Generate and review captions before rendering.");
      return;
    }

    const captionReviewBlockers = normalizeCaptionSegments(captionSegments).filter(
      segment => segment.reviewRequired
    );
    if (autoCaptions && !captionReviewCopy && !normalizePlainText(captionTextOverride) && captionReviewBlockers.length) {
      setStudioInspectorTab("captions");
      setActiveCreativeTool("captions");
      focusCaptionSegmentsForReview(captionReviewBlockers);
      setStudioActionMessage(
        `Render paused: approve or correct ${captionReviewBlockers.length} caption ${captionReviewBlockers.length === 1 ? "line" : "lines"}. Broken multilingual wording must never be burned into the video automatically.`
      );
      toast.error("Review every flagged caption line before rendering.");
      return;
    }

    if (
      smartCrop &&
      (smartCropMode === "group_stack" || reframeModeCuts.some(cut => cut.mode === "group_stack")) &&
      !speakerStackIsReady
    ) {
      setStudioInspectorTab("reframe");
      setActiveCreativeTool("reframe");
      setStudioActionMessage(
        `Render paused: Multi-Camera needs ${speakerStackCameraCount} distinct clean angles aligned to the same source timecode. Different excerpts and reaction insets are not valid camera feeds.`
      );
      toast.error(`Align and confirm ${speakerStackCameraCount} synchronized camera angles first.`);
      return;
    }

    if (multicamExportNeedsSupport) {
      setStudioInspectorTab("reframe");
      setActiveCreativeTool("reframe");
      setStudioActionMessage(
        "This camera composition is available in preview only. Choose Top / Bottom for a two-camera export; your reviewed cuts remain saved in the project."
      );
      toast.error("Export does not yet support this camera composition.");
      return;
    }

    if (creatorPreview.enabled && creatorPreview.activeDemo) {
      setStudioInspectorTab("creatorfx");
      setActiveCreativeTool("creatorfx");
      setStudioActionMessage("An exploratory effect is still on the preview. Add supported titles to the timeline, or clear the preview before exporting. Saved timeline edits will be kept.");
      toast.error("Review the exploratory effect before exporting.");
      return;
    }

    const scanSessionId = selectedClip?.scanSessionId || null;
    const selectedClipId = selectedClip?.id ?? null;

    if (scanSessionId) {
      void trackClipWorkflowEvent("scanner_clip_export_started", {
        scanSessionId,
        clipId: String(selectedClipId),
        score: Number(selectedClip?.guidedScore ?? selectedClip?.score ?? 0),
        destination: destination || "general",
      });
    }

    setIsExporting(true);
    setExportStatusLabel("Preparing media...");

    const token = await getMediaAuthToken();
    if (!token) {
      toast.error("Please login first");
      setIsExporting(false);
      setExportStatusLabel("Render Final Clip");
      return;
    }

    try {
      let exportTimeline = await buildExportTimeline();
      if (
        silenceRemoval &&
        silencePreview?.clipId === currentTimelineClip?.id &&
        Array.isArray(silencePreview.keepSegments)
      ) {
        if (!silencePreview.keepSegments.length) {
          throw new Error("Silence removal left no playable material. Adjust its threshold first.");
        }
        exportTimeline = applySilenceKeepSegmentsToTimeline({
          timelineSegments: exportTimeline,
          keepSegments: silencePreview.keepSegments,
          sourceClipId: currentTimelineClip?.sourceClipId || currentTimelineClip?.id,
        });
        if (!exportTimeline.length) {
          throw new Error("Silence removal left no playable material. Adjust its threshold first.");
        }
      }
      if (autoCaptions && captionCoverageMode !== "selected" && !captionReviewCopy &&
        !normalizePlainText(captionTextOverride)) {
        setExportStatusLabel("Checking caption coverage…");
        const coverage = await validateCaptionCoverage(exportTimeline, token);
        if (coverage.gaps.length) {
          setStudioInspectorTab("captions"); setActiveCreativeTool("captions");
          setStudioActionMessage(coverage.message);
          toast.error("Caption coverage needs review before final rendering.");
          return;
        }
      }
      let exportedSpeakerStackPlan = null;
      if (
        smartCrop &&
        (smartCropMode === "group_stack" || reframeModeCuts.some(cut => cut.mode === "group_stack"))
      ) {
        const resolveExportSpeakerSource = async sourceOption => {
          if (!sourceOption) return "";
          if (!sourceOption.isProgrammeSource) {
            return ensurePreviewableClipUrl(sourceOption.clip);
          }
          const currentSourceId = String(
            currentTimelineClip?.sourceClipId || currentTimelineClip?.id || ""
          );
          return (
            exportTimeline.find(
              segment => String(segment.source_clip_id || segment.id || "") === currentSourceId
            )?.url || ensurePreviewableClipUrl(currentTimelineClip)
          );
        };
        const sourceUrls = await Promise.all(
          resolvedSpeakerStackSources.map(resolveExportSpeakerSource)
        );
        if (
          sourceUrls.some(sourceUrl => !sourceUrl) ||
          new Set(sourceUrls).size !== sourceUrls.length
        ) {
          throw new Error(
            `Multi-Camera requires ${speakerStackCameraCount} distinct clean video sources.`
          );
        }
        const cameras = activeSpeakerStackSlots.map((slot, index) => {
          const source = resolvedSpeakerStackSources[index];
          return {
            slot,
            source_url: sourceUrls[index],
            source_id: resolvedSpeakerStackSourceIds[slot],
            offset_seconds: Math.max(0, Number(speakerStackOffsets[slot] || 0)),
            time_origin_seconds: source?.isProgrammeSource ? 0 : Number(selectedClip?.start || 0),
            x: clampNumber(
              speakerStackFraming[slot]?.x,
              0,
              100,
              DEFAULT_SPEAKER_STACK_FRAMING[slot].x
            ),
            y: clampNumber(
              speakerStackFraming[slot]?.y,
              0,
              100,
              DEFAULT_SPEAKER_STACK_FRAMING[slot].y
            ),
            zoom: clampNumber(speakerStackFraming[slot]?.zoom, 1, 7, 1),
          };
        });
        exportedSpeakerStackPlan = {
          orientation: speakerStackLayout,
          layout: speakerStackLayout,
          camera_count: cameras.length,
          divider_percent: clampNumber(speakerStackFraming.dividerPercent, 35, 65, 50),
          gap_percent: clampNumber(speakerStackFraming.gapPercent, 0, 3, 1.2),
          rounded_cards: speakerStackFraming.roundedCards !== false,
          card_inset_percent: clampNumber(speakerStackFraming.cardInsetPercent, 0, 8, 2.5),
          card_radius_percent: clampNumber(speakerStackFraming.cardRadiusPercent, 2, 18, 8),
          secondary_frame: {
            x_percent: clampNumber(speakerStackFraming.secondaryXPercent, 16, 84, 80),
            y_percent: clampNumber(speakerStackFraming.secondaryYPercent, 16, 84, 20),
            size_percent: clampNumber(speakerStackFraming.secondarySizePercent, 20, 46, 30),
          },
          speaker_focus_cuts: normalizeSpeakerFocusCuts(speakerFocusCuts).map(cut => ({
            time: cut.time,
            slot: cut.slot,
            source_id: resolvedSpeakerStackSourceIds[cut.slot] || null,
            review: "manual",
          })),
          cameras,
          // Keep legacy aliases so existing two-camera workers/projects remain readable.
          top: cameras[0],
          bottom: cameras[1],
        };
      }
      setExportStatusLabel("Uploading edit layers...");
      const overlaySourceUploads = new WeakMap();
      const newOverlays = await prepareStudioOverlayMedia(
        exportEditState.overlays, async overlay => {
          let fileToUpload = overlay.file instanceof Blob ? overlay.file : null;
          let isNewBlob = false;
          let generatedExtension = "png";
          let finalOverlay = { ...overlay };

          // Reopened native graphics can retain file metadata alongside a
          // rehydrated blob URL. Upload the actual owned preview bytes rather
          // than passing that plain metadata object to the source uploader.
          if (!fileToUpload && overlay.isLocal && typeof overlay.src === "string" &&
              overlay.src.startsWith("blob:")) {
            const response = await fetch(overlay.src);
            if (!response.ok) throw new Error(`The local artwork ${overlay.name || overlay.id} is unavailable. Restore or relink its source before exporting.`);
            fileToUpload = await response.blob();
            generatedExtension = fileToUpload.type === "image/jpeg" ? "jpg"
              : fileToUpload.type.split("/")[1] || "bin";
            isNewBlob = true;
          }

          if (!fileToUpload && overlay.type === "image" && overlay.vectorShape &&
              !String(overlay.src || "").startsWith("http")) {
            fileToUpload = await rasterizeStudioArtwork(
              studioVectorSvg(overlay.vectorShape),
              overlay.name || "Vector artwork"
            );
            generatedExtension = "png";
            isNewBlob = true;
          }

          // Native raster effects survive backups as embedded pixels. Send
          // those pixels through the ordinary owned image uploader before
          // quoting/rendering, just like an imported image. Inline media must
          // not become part of the credit operation's bounded descriptor.
          const embeddedRaster = overlay.type === "image" &&
            /^data:image\/(png|jpeg|webp);base64,([A-Za-z0-9+/=]+)$/.exec(overlay.src || "");
          if (embeddedRaster && !overlay.vectorShape) {
            const bytes = Uint8Array.from(atob(embeddedRaster[2]), char => char.charCodeAt(0));
            fileToUpload = new Blob([bytes], { type: `image/${embeddedRaster[1]}` });
            generatedExtension = embeddedRaster[1] === "jpeg" ? "jpg" : embeddedRaster[1];
            isNewBlob = true;
          }

          if (overlay.type === "text" && overlay.isRainbow) {
            const tempContainer = document.createElement("div");
            tempContainer.style.position = "absolute";
            tempContainer.style.left = "-9999px";
            tempContainer.style.background = "transparent";
            tempContainer.style.padding = "20px";
            tempContainer.style.fontFamily =
              '"Comic Sans MS", "Chalkboard SE", "Marker Felt", sans-serif';
            tempContainer.style.fontSize = "32px";
            tempContainer.style.fontWeight = "900";
            tempContainer.style.textShadow = "3px 3px 0 #000";
            tempContainer.style.webkitTextStroke = "1.5px black";
            tempContainer.style.whiteSpace = "pre-wrap";

            const chars = (overlay.text || "").split("");
            chars.forEach((char, idx) => {
              const span = document.createElement("span");
              span.textContent = char;
              const offset = overlay.rainbowOffset || 0;
              span.style.color = RAINBOW_COLORS[(idx + offset) % RAINBOW_COLORS.length];
              tempContainer.appendChild(span);
            });

            document.body.appendChild(tempContainer);

            try {
              const canvas = await html2canvas(tempContainer, {
                backgroundColor: null,
                scale: 2,
              });
              const blob = await new Promise(resolve => canvas.toBlob(resolve, "image/png"));
              fileToUpload = blob;
              isNewBlob = true;
              finalOverlay.type = "image";
              finalOverlay.text = undefined;
            } catch (error) {
              console.error("Failed to render caption:", error);
            } finally {
              document.body.removeChild(tempContainer);
            }
          }

          if (fileToUpload && (isNewBlob || overlay.isLocal)) {
            const ext = isNewBlob
              ? generatedExtension
              : fileToUpload.name
                ? fileToUpload.name.split(".").pop()
                : "bin";
            const fileName = `${createSecureId("overlay")}.${ext}`;
            let pendingUpload = overlaySourceUploads.get(fileToUpload);
            if (!pendingUpload) {
              pendingUpload = uploadSourceFileViaBackend({
                file: fileToUpload,
                token,
                getToken: forceRefresh => getMediaAuthToken(forceRefresh),
                mediaType:
                  finalOverlay.type === "video"
                    ? "video"
                    : finalOverlay.type === "image"
                      ? "image"
                      : "audio",
                fileName,
                purpose: finalOverlay.type === "video" ? "studio_project" : undefined,
                onProgress: (transferred, total) => {
                  const percent = total > 0 ? Math.round((transferred / total) * 100) : 0;
                  setExportStatusLabel(`Uploading layers ${percent}%`);
                },
              });
              overlaySourceUploads.set(fileToUpload, pendingUpload);
            }
            const uploadResult = await pendingUpload;
            const url = uploadResult.url;

            finalOverlay.src = url;
            if (uploadResult.storagePath) {
              finalOverlay.sourceStoragePath = uploadResult.storagePath;
            }
            finalOverlay.isLocal = false;
            finalOverlay.file = null;
            if(uploadResult.contentHash)finalOverlay.contentHash=uploadResult.contentHash;
            flushSync(()=>setOverlays(current=>current.map(item=>bindStudioOverlayMedia(item,overlay,finalOverlay))));
          }

          return finalOverlay;
        }
      );

      const musicTrackAudible = isStudioAudioTrackAudible(trackStates, "music");
      let exportedMusicUrl = musicTrackAudible ? exportEditState.musicTrack?.url || null : null;
      let uploadedMusicBinding = null;
      if (
        addMusic &&
        musicTrackAudible &&
        musicTrack?.file instanceof Blob &&
        (!exportedMusicUrl || exportedMusicUrl.startsWith("blob:"))
      ) {
        const musicExtension = musicTrack.file.name?.split(".").pop() || "mp3";
        const musicUpload = await uploadSourceFileViaBackend({
          file: musicTrack.file,
          token,
          mediaType: "audio",
          fileName: `${createSecureId("music")}.${musicExtension}`,
          onProgress: (transferred, total) => {
            const percent = total > 0 ? Math.round((transferred / total) * 100) : 0;
            setExportStatusLabel(`Uploading sound ${percent}%`);
          },
        });
        exportedMusicUrl = musicUpload.url;
        uploadedMusicBinding = studioUploadedAudioBinding(exportEditState.musicTrack, musicUpload);
      }

      const uploadedSoundMedia = new Map();
      const exportedSoundEffects = await Promise.all(
        [...exportEditState.soundEffects, ...clipStudioGroupMotionCues(motionCues(exportEditState.motionScenes), exportEditState.compoundClips)]
          .filter(effect => effect.enabled !== false && isStudioAudioTrackAudible(trackStates, "sfx"))
          .map(async effect => {
            let effectUrl = effect.url || null;
            if (effect.file instanceof Blob && (!effectUrl || effectUrl.startsWith("blob:"))) {
              const extension = effect.file.name?.split(".").pop() || "wav";
              const upload = await uploadSourceFileViaBackend({
                file: effect.file,
                token,
                mediaType: "audio",
                fileName: `${createSecureId("sfx")}.${extension}`,
                onProgress: (transferred, total) => {
                  const percent = total > 0 ? Math.round((transferred / total) * 100) : 0;
                  setExportStatusLabel(`Uploading sound effects ${percent}%`);
                },
              });
              effectUrl = upload.url;
              uploadedSoundMedia.set(String(effect.id), studioUploadedAudioBinding(effect, upload));
            }
            return {
              id: effect.id,
              name: effect.name,
              builtIn: !!effect.builtIn,
              tone: effect.tone || null,
              url: effectUrl,
              startTime: Number(effect.startTime || 0),
              duration: getSoundEffectDuration(effect),
              trimStart: Number(effect.trimStart || 0),
              volume: clampAudioControl(effect.volume, 0, 1, 0.8),
              fadeIn: Number(effect.fadeIn || 0),
              fadeOut: Number(effect.fadeOut || 0),
              enabled: true,
            };
          })
      );
      if (uploadedMusicBinding || uploadedSoundMedia.size) {
        const bindings = { music: uploadedMusicBinding, effects: uploadedSoundMedia };
        const materialized = bindStudioAudioSources(exportEditState, bindings);
        exportEditState.musicTrack = materialized.musicTrack;
        exportEditState.soundEffects = materialized.soundEffects;
        flushSync(() => {
          if (uploadedMusicBinding) setMusicTrack(current => bindStudioAudioSource(current, uploadedMusicBinding));
          if (uploadedSoundMedia.size) setSoundEffects(current =>
            current.map(effect => bindStudioAudioSource(effect, uploadedSoundMedia.get(String(effect.id)))));
        });
        // Reconcile the frozen decisions against the committed revision. When
        // only uploads changed, request and saved project now share one digest.
        // Concurrent creative edits stay in the live project, not this render.
        const committed = latestEditorSnapshotRef.current();
        exportEditState.studioDocument = reconcileStudioDocument({
          snapshot: exportEditState, projectId, storedDocument: committed.studioDocument,
        });
      }

      setExportStatusLabel("Preparing voice-over recordings");
      const exportedVoiceovers = await uploadVoiceoversForRender(exportEditState.voiceovers, {
        upload: options => uploadSourceFileViaBackend({
          ...options,
          onProgress: (transferred, total) => {
            const percent = total > 0 ? Math.round((transferred / total) * 100) : 0;
            setExportStatusLabel(`Uploading voice-over ${percent}%`);
          },
        }),
        token,
        audible: voiceoverTrackAudible(trackStates),
      });
      if (exportedVoiceovers.length) {
        const uploadedById = new Map(exportedVoiceovers.map(take => [String(take.id), {
          priorUrl: take.priorUrl, url: take.url, sourceStoragePath: take.sourceStoragePath,
          storagePath: take.storagePath, ownerUid: take.ownerUid, contentHash: take.contentHash,
        }]));
        const materialized = bindStudioAudioSources(exportEditState, { voices: uploadedById });
        exportEditState.voiceovers = materialized.voiceovers;
        flushSync(() => setVoiceovers(current => current.map(take =>
          bindStudioAudioSource(take, uploadedById.get(String(take.id))))));
        exportEditState.studioDocument = reconcileStudioDocument({ snapshot: exportEditState, projectId,
          storedDocument: latestEditorSnapshotRef.current().studioDocument });
      }
      const normalizedOverlays = normalizeOverlaysForExport(exportTimeline, newOverlays.filter(overlay => !overlay.isNullRig));
      const exportCaptionSegments = mapCaptionSegmentsToTimeline({
        captionSegments: normalizeCaptionSegments(captionSegments),
        timelineSegments: exportTimeline,
        fallbackSourceClipId:
          currentTimelineClip?.sourceClipId || currentTimelineClip?.id || selectedClip?.id || null,
      });
      const persistedHookFocusPoint = addHook ? normalizeHookFocusPoint(hookFocusPoint) : null;
      const coverFrame = addHook
        ? {
            timelineTime: 0,
            sourceTime: Number(currentTimelineWindow.start || 0) + resolvedHookStart,
            clipId: currentTimelineClip?.id || selectedClip?.id || null,
            focusPoint: persistedHookFocusPoint,
            template: hookTemplate,
            freezeFrame: hookFreezeFrame,
            strategy: hookFreezeFrame ? "hook_freeze_frame" : "hook_intro_start",
          }
        : null;
      const thumbnailFrame = coverFrame ? { ...coverFrame, purpose: "thumbnail" } : null;

      setOverlays(current=>current.map(item=>{
        const before=exportEditState.overlays.find(layer=>layer.id===item.id),uploaded=newOverlays.find(layer=>layer.id===item.id);
        return before&&uploaded?bindStudioOverlayMedia(item,before,uploaded):item;
      }));
      setExportStatusLabel("Starting render...");
      const speedDocument = exportEditState.studioDocument;
      const speedPlanDuration = speedDocument
        ? ticksToSeconds(speedDocument.clipOccurrences.at(-1)?.programmeRange.endTick || 0)
        : outputTimelineDuration;
      const exportSpeedSegments = speedKeyframes.length
        ? buildSpeedSegmentsFromKeyframes({
            keyframes: speedKeyframes,
            duration: speedPlanDuration,
            fallback: previewSpeed,
          })
        : [{
            startTime: 0,
            endTime: speedPlanDuration,
            rate: previewSpeed,
            pitchPreserved: true,
          }];
      if (speedDocument) {
        assertStudioSpeedPlanMatchesDocument(speedDocument, exportSpeedSegments);
      }
      // Upload bindings are part of the edit. Commit their pending state and
      // history invalidation before the parent captures its render revision.
      flushSync(() => setExportStatusLabel("Submitting render…"));
      const completedRender = await onSave(selectedClip, normalizedOverlays, {
        autoCaptions,
        captionReviewCopy,
        captionCoverageMode,
        captionStyle,
        captionPosition,
        captionScale,
        captionTextOverride: normalizePlainText(captionTextOverride) || null,
        captionSegments: exportCaptionSegments,
        translateCaptionsToEnglish,
        previewSpeed,
        speedSegments: exportSpeedSegments,
        pacingLevel,
        creativeIntent,
        studioPlan: selectedClip?.studioEditPlan || selectedClip?.studio_edit_plan || null,
        creativePlan: {
          version: 1,
          enabled: creativeEffectsEnabled,
          intensity: creativeIntensity,
          fallback: "clean",
          effects: creativeEffectsEnabled
            ? buildSignatureCreativeEffects({
                preset: creativePreset,
                intensity: creativeIntensity,
                duration: currentTimelineWindow.duration || selectedClip?.duration || 0,
              })
            : [],
        },
        finishPlan: {
          version: 1,
          enabled: true,
          ...compileStudioFinishForRender(exportEditState.studioDocument),

          magnetic_beats: {
            enabled: beatSnapEnabled,
            markers: musicBeatMarkers
              .map(marker => Number(marker.time || marker))
              .filter(Number.isFinite),
          },
          reframe: {
            enabled: smartCrop || reframeModeCuts.some(cut => cut.mode !== "off"),
            aspect: reframeAspect,
            mode: smartCropMode,
            zoom: speakerTrackZoom,
            ...(reframeModeCuts.length
              ? { timeline_cuts: normalizeReframeModeCuts(reframeModeCuts).map(cut => ({
                  time: cut.time,
                  mode: cut.mode,
                  zoom: cut.zoom,
                  ...(cut.sourceTimeOffsetSeconds
                    ? { source_time_offset_seconds: cut.sourceTimeOffsetSeconds }
                    : {}),
                })) }
              : {}),
            ...(speakerFocusCuts.length
              ? { speaker_order_cuts: normalizeSpeakerFocusCuts(speakerFocusCuts).map(cut => ({
                  time: cut.time,
                  slot: cut.slot,
                })) }
              : {}),
            ...((smartCropMode === "center" || reframeModeCuts.some(cut => cut.mode === "center")) && reframeAspect !== "16:9"
              ? { split_source: {
                  divider_percent: clampNumber(speakerStackFraming.dividerPercent, 35, 65, 50),
                  rounded_cards: speakerStackFraming.roundedCards !== false,
                  card_inset_percent: clampNumber(speakerStackFraming.cardInsetPercent, 0, 8, 2.5),
                  card_gap_percent: clampNumber(speakerStackFraming.cardGapPercent, 0, 3, 2.5),
                  card_radius_percent: clampNumber(speakerStackFraming.cardRadiusPercent, 2, 18, 8),
                  ...Object.fromEntries(["top", "bottom"].map(slot => [slot, {
                    ...speakerStackFraming[slot], keyframes: speakerStackFraming.trackSpeakers ? speakerStackFraming[slot].keyframes || [] : [],
                  }])),
                } }
              : {}),
            ...(smartCropMode === "group_stack" || reframeModeCuts.some(cut => cut.mode === "group_stack")
              ? {
                  group_stack: exportedSpeakerStackPlan,
                }
              : {}),
            keyframes: reframeKeyframes.map(keyframe => ({
              time: Number(keyframe.time || 0),
              ...(keyframe.cut === true ? { cut: true } : {}),
              x: clampNumber(keyframe.x, 0, 100, 50),
              y: clampNumber(keyframe.y, 0, 100, 50),
            })),
          },
        },
        editorTimeline: {
          track_states: trackStates,
          edit_tool: timelineEditTool,
          snapping: timelineSnapping,
          linked_selection: linkedSelection,
          ripple_mode: rippleMode,
          motion_keyframes: motionKeyframes,
          audio_keyframes: audioKeyframes,
          speed_keyframes: speedKeyframes,

          compound_clips: effectiveCompoundClips,
          voiceovers,
        },
        compositionPlan: compileStudioCompositionForRender(exportEditState.studioDocument),
        creativeTemplateInstance: exportEditState.creativeTemplateInstance,
        audioRestoration,
        audioAutomation: audioKeyframes,
        audioTrackStates: trackStates,
        stabilization,
        advancedColor,
        proxySettings,
        splitExport: {
          enabled: splitExportEnabled,
          hooks: splitExportEnabled ? splitExportHooks : [],
        },
        smartCrop,
        smartCropMode,
        reframeModeCuts: normalizeReframeModeCuts(reframeModeCuts),
        speakerStackFraming,
        speakerFocusCuts: normalizeSpeakerFocusCuts(speakerFocusCuts),
        enhanceQuality,
        professionalCleanup: enhanceQuality,
        silenceRemoval,
        silenceThreshold,
        minSilenceDuration,
        removeWatermark,
        watermarkMode,
        brandWatermark,
        studioProjectId: projectId,
        manualWatermarkRegions: serializeManualWatermarkRegions(manualWatermarkRegions),
        addHook,
        hookText,
        hookIntroSeconds: hookDuration,
        hookTemplate,
        hookStartTime: 0,
        hookEndTime: hookDuration,
        hookSourceStartTime: Number(currentTimelineWindow.start || 0) + resolvedHookStart,
        hookSourceEndTime: Number(currentTimelineWindow.start || 0) + hookEnd,
        hookFocusPoint: persistedHookFocusPoint,
        coverFrame,
        coverFrameTime: coverFrame ? Number(coverFrame.timelineTime || 0) : null,
        coverFrameSourceTime: coverFrame ? Number(coverFrame.sourceTime || 0) : null,
        thumbnailFrame,
        thumbnailTime: thumbnailFrame ? Number(thumbnailFrame.timelineTime || 0) : null,
        thumbnailSourceTime: thumbnailFrame ? Number(thumbnailFrame.sourceTime || 0) : null,
        hook: {
          startTime: 0,
          endTime: hookDuration,
          duration: hookDuration,
          sourceStartTime: Number(currentTimelineWindow.start || 0) + resolvedHookStart,
          sourceEndTime: Number(currentTimelineWindow.start || 0) + hookEnd,
          template: hookTemplate,
          text: normalizedHookText,
          focusPoint: persistedHookFocusPoint,
          coverFrame,
          effects: {
            blurBackground: hookBlurBackground,
            darkOverlay: hookDarkOverlay,
            freezeFrame: hookFreezeFrame,
            zoomScale: hookZoomScale,
            textAnimation: hookTextAnimation,
          },
        },
        hookBlurBackground,
        hookDarkOverlay,
        hookFreezeFrame,
        hookZoomScale,
        hookTextAnimation,
        addMusic: addMusic && !!musicTrack && musicTrackAudible,
        musicFile: null,
        musicUrl: exportedMusicUrl,
        musicName: musicTrack?.name || null,
        musicSelection: musicTrack ? "custom" : musicSelection,
        isSearch: musicTrack ? true : musicSearchMode,
        safeSearch,
        musicVolume: musicTrack?.volume ?? musicVolume,
        musicDucking: musicTrack?.ducking ?? musicDucking,
        musicDuckingStrength: musicTrack?.duckingStrength ?? musicDuckingStrength,
        musicDuckingMode: musicTrack?.duckingMode || "speech",
        musicFadeIn: musicTrack?.fadeIn ?? 0.5,
        musicFadeOut: musicTrack?.fadeOut ?? 0.5,
        musicLoop: musicTrack?.loop ?? true,
        soundEffects: [...exportedSoundEffects, ...exportedVoiceovers],
        audioRemix: audioRemixForRender(audioRemix),
        motionGraphics: { version: 1, scenes: exportEditState.motionScenes.map(normalizeMotion) },
        threeDGraphics: enabledThreeDScenes.map(scene => ({ jobId: scene.hqPreviewJobId, scene: normalizeStudio3DScene(scene), aspect: reframeAspect })),
        subjectVfx: validateSubjectVfxExport(subjectVfxScenes, exportTimeline),
        muteAudio: muteOriginalAudio || !isStudioAudioTrackAudible(trackStates, "originalAudio"),
        timelineSegments: exportTimeline,
        backgroundAudio: null,
        exportDestination: destination || "general",
        outputSettings: exportSettings,
      });
      if (completedRender?.url || completedRender?.outputUrl || completedRender?.output_url) {
        setPreviewFinishedFile(true);
      }
    } catch (err) {
      const exportFailureMessage = `Export failed: ${err?.message || "Unknown render error"}`;
      if (scanSessionId) {
        void trackClipWorkflowEvent("scanner_clip_export_failed", {
          scanSessionId,
          clipId: String(selectedClipId),
          destination: destination || "general",
          message: err?.message || "Export failed",
        });
      }
      setStudioActionMessage(exportFailureMessage);
      setExportErrorMessage(exportFailureMessage);
      toast.error(exportFailureMessage);
    } finally {
      setExportStatusLabel("Render Final Clip");
      setIsExporting(false);
    }
  };

  // Dragging State
  const [isDragging, setIsDragging] = useState(false);
  const dragItem = useRef(null);
  const dragOffset = useRef({ x: 0, y: 0 });

  useEffect(() => {
    const scanSessionId = selectedClip?.scanSessionId;
    if (!scanSessionId || loggedScannerEntryRef.current.has(scanSessionId)) return;

    loggedScannerEntryRef.current.add(scanSessionId);
    void trackClipWorkflowEvent("scanner_clip_opened_in_editor", {
      scanSessionId,
      clipId: String(selectedClip?.id ?? "unknown"),
      score: Number(selectedClip?.guidedScore ?? selectedClip?.score ?? 0),
      improveInEditor: Boolean(selectedClip?.improveInEditor),
    });
  }, [selectedClip]);

  // Handle video element duration load to set clip max duration
  const handleLoadedMetadata = () => {
    // A finished render has its own duration; it must not rewrite source clips.
    if (videoRef.current && !renderedOutputUrl) {
      const dur = videoRef.current.duration;
      setTimeline(prev =>
        prev.map((item, idx) =>
          idx === activeTimelineIndex
            ? applyStudioClipMetadata(item, dur, item.id === "main" ? selectedClip : {})
            : item
        )
      );
      if (activeTimelineIndex === 0) {
        setProjectMedia(current => current.map(item => item.isPrimary
          ? { ...item, duration: Number(dur || 0) }
          : item));
      }
    }
  };

  useEffect(() => {
    setOrderedClips(clips || []);
  }, [clips]);

  useEffect(() => {
    if (orderedClips && orderedClips.length > 0) {
      if (!selectedClip || !orderedClips.some(clip => clip.id === selectedClip.id)) {
        setSelectedClip(orderedClips[0]);
      }
      return;
    }

    if (selectedClip) {
      setSelectedClip(null);
    }
  }, [orderedClips, selectedClip]);

  useEffect(() => {
    if (!selectedClip) return;

    const pendingAction = pendingClipActionRef.current;
    if (!pendingAction || pendingAction.clipId !== selectedClip.id) return;

    pendingClipActionRef.current = null;

    if (pendingAction.type === "apply-hook") {
      applyGuidedHookToClip(selectedClip, { preview: true });
      return;
    }

    if (pendingAction.type === "improve") {
      applyClipImprovements(selectedClip);
      return;
    }

    if (pendingAction.type === "apply-variant") {
      applyClipVariant(selectedClip, pendingAction.variant);
      return;
    }

    if (pendingAction.type === "export") {
      void handleExportRender(pendingAction.destination);
    }
  }, [selectedClip]);

  useEffect(() => {
    const snapshot = cloneSnapshot(getEditorSnapshot());
    // Inspector selection and playhead navigation are not edits. Excluding
    // them keeps Undo focused on visible media/timeline changes instead of
    // silently undoing which layer happened to be selected.
    const serializedSnapshot = serializeSnapshot(getHistoryRelevantSnapshot(snapshot));

    if (lastSnapshotRef.current === null) {
      lastSnapshotRef.current = snapshot;
      lastSnapshotSignatureRef.current = serializedSnapshot;
      syncHistoryAvailability();
      return;
    }

    // Dragging can emit many overlay updates per second. Record a single history snapshot
    // when the drag completes instead of pushing one entry for every mouse move.
    if (isDragging) {
      return;
    }

    if (serializedSnapshot === lastSnapshotSignatureRef.current) {
      syncHistoryAvailability();
      return;
    }

    // A finished file represents the edit that was submitted. Once the edit
    // changes, return the monitor to the current editable programme.
    // Refreshing/materializing transport fields may change the legacy snapshot
    // while retaining exactly the same canonical decisions and revision.
    const previousDocument = lastSnapshotRef.current?.studioDocument;
    const currentDocument = snapshot.studioDocument;
    if (!previousDocument || !currentDocument ||
        serializeSnapshot(previousDocument) !== serializeSnapshot(currentDocument)) {
      onEditInvalidated?.();
    }
    if (renderedOutputUrl) {
      const position = getCutPlayheadPosition();
      if (position) continueCutInSourcePreview(position);
    }

    if (pendingHistoryBaselineRef.current) {
      const baseline = pendingHistoryBaselineRef.current;
      pendingHistoryBaselineRef.current = null;
      const baselineSignature = serializeSnapshot(getHistoryRelevantSnapshot(baseline));
      const currentUndoTop = undoStackRef.current[undoStackRef.current.length - 1];
      const currentUndoTopSignature = currentUndoTop
        ? serializeSnapshot(getHistoryRelevantSnapshot(currentUndoTop))
        : null;
      if (baselineSignature !== currentUndoTopSignature) {
        undoStackRef.current.push(cloneSnapshot(baseline));
        if (undoStackRef.current.length > 50) undoStackRef.current.shift();
      }
      redoStackRef.current = [];
      lastSnapshotRef.current = snapshot;
      lastSnapshotSignatureRef.current = serializedSnapshot;
      if (cutHistoryTransactionRef.current) {
        cutHistoryTransactionRef.current.appliedSignature = serializedSnapshot;
        cutHistoryTransactionRef.current.appliedTimelineSignature = serializeSnapshot(
          snapshot.timeline || []
        );
      }
      syncHistoryAvailability();
      scheduleProjectAutosave(snapshot);
      return;
    }

    if (isRestoringHistoryRef.current) {
      isRestoringHistoryRef.current = false;
      lastSnapshotRef.current = snapshot;
      lastSnapshotSignatureRef.current = serializedSnapshot;
      syncHistoryAvailability();
      return;
    }

    undoStackRef.current.push(cloneSnapshot(lastSnapshotRef.current));
    if (undoStackRef.current.length > 50) {
      undoStackRef.current.shift();
    }
    redoStackRef.current = [];
    lastSnapshotRef.current = snapshot;
    lastSnapshotSignatureRef.current = serializedSnapshot;
    syncHistoryAvailability();
    scheduleProjectAutosave(snapshot);
  }, [
    orderedClips,
    projectMedia,
    creativeTemplateInstance,
    angleGroups,
    activeAngleGroupId,
    selectedClip,
    overlays,
    activeOverlayId,
    videoFit,
    safeFaceFraming,
    faceAnchorPreset,
    autoCaptions,
    captionStyle,
    captionPosition,
    captionScale,
    captionTextOverride,
    captionCoverageMode,
    captionSegments,
    translateCaptionsToEnglish,
    captionSourceLanguage,
    previewSpeed,
    pacingLevel,
    creativeIntent,
    creativeEffectsEnabled,
    creativePreset,
    creativeIntensity,
    finishFx,
    podcastVisualizer,
    finishKeyframes,
    beatSnapEnabled,
    contentProfile,
    cutRangeStart,
    cutRangeEnd,
    joinTransition,
    joinTransitionDuration,
    smartCrop,
    smartCropMode,
    reframeModeCuts,
    reframeAspect,
    speakerTrackZoom,
    reframeKeyframes,
    speakerStackFraming,
    speakerStackSourceIds,
    speakerStackOffsets,
    speakerStackSources,
    speakerStackCameraCount,
    speakerStackLayout,
    speakerStackSyncConfirmed,
    speakerFocusCuts,
    activeReframeKeyframeId,
    enhanceQuality,
    silenceRemoval,
    silencePreview,
    silenceThreshold,
    minSilenceDuration,
    removeWatermark,
    watermarkMode,
    brandWatermark,
    mainFrame,
    exportSettings,
    manualWatermarkRegions,
    activeWatermarkRegionId,
    addHook,
    hookText,
    hookTemplate,
    hookIntroSeconds,
    hookStartTime,
    hookEndTime,
    hookBlurBackground,
    hookDarkOverlay,
    hookFreezeFrame,
    hookZoomScale,
    hookTextAnimation,
    hookPreviewLoop,
    hookFocusPoint,
    addMusic,
    muteOriginalAudio,
    musicSelection,
    musicSearchMode,
    safeSearch,
    musicVolume,
    musicDucking,
    musicDuckingStrength,
    musicTrack,
    soundEffects,
    activeSoundEffectId,
    extractedAudio,
    audioRemix,
    creatorPreview,
    bRollCadence,
    bRollGenerationJobId,
    timeline,
    activeTimelineIndex,
    timelineZoom,
    timelineEditTool,
    timelineSnapping,
    linkedSelection,
    rippleMode,
    trackStates,
    motionKeyframes,
    motionTargetId,
    compositeTargetId,
    mainTransform,
    audioKeyframes,
    speedKeyframes,
    voiceovers,
    adjustmentLayers,
    nodeFlow,
    hideShyLayers,
    compoundClips,
    audioAutomationSource,
    audioRestoration,
    proxySettings,
    stabilization,
    advancedColor,
    splitExportEnabled,
    splitExportHooks,
    isDragging,
    motionScenes,
    threeDScenes,
    subjectVfxScenes,
    compositionOrder,
  ]);

  useEffect(() => {
    const handleHistoryKeyDown = event => {
      const targetTag = event.target?.tagName;
      const isTypingTarget =
        event.target?.isContentEditable || ["INPUT", "TEXTAREA", "SELECT"].includes(targetTag);
      if (isTypingTarget) return;

      const isUndo =
        (event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "z" && !event.shiftKey;
      const isRedoShortcut =
        (event.metaKey || event.ctrlKey) &&
        ((event.key.toLowerCase() === "z" && event.shiftKey) || event.key.toLowerCase() === "y");

      if (isUndo) {
        event.preventDefault();
        studioShortcutActionsRef.current.undo?.();
      } else if (isRedoShortcut) {
        event.preventDefault();
        studioShortcutActionsRef.current.redo?.();
      }
    };

    window.addEventListener("keydown", handleHistoryKeyDown);
    return () => window.removeEventListener("keydown", handleHistoryKeyDown);
  }, [
    orderedClips,
    selectedClip,
    overlays,
    activeOverlayId,
    videoFit,
    autoCaptions,
    captionStyle,
    smartCrop,
    smartCropMode,
    reframeModeCuts,
    silenceRemoval,
    silencePreview,
    silenceThreshold,
    minSilenceDuration,
    removeWatermark,
    watermarkMode,
    manualWatermarkRegions,
    activeWatermarkRegionId,
    addHook,
    hookText,
    hookTemplate,
    hookIntroSeconds,
    hookStartTime,
    hookEndTime,
    hookBlurBackground,
    hookDarkOverlay,
    hookFreezeFrame,
    hookZoomScale,
    hookTextAnimation,
    hookPreviewLoop,
    addMusic,
    muteOriginalAudio,
    musicSelection,
    musicSearchMode,
    safeSearch,
    musicVolume,
    musicDucking,
    musicDuckingStrength,
    musicTrack,
    soundEffects,
    activeSoundEffectId,
    extractedAudio,
    timeline,
    activeTimelineIndex,
  ]);

  useEffect(() => {
    if (!activeOverlayId) return undefined;

    const handleKeyDown = event => {
      if (event.defaultPrevented) return;
      const targetTag = event.target?.tagName;
      const isTypingTarget =
        event.target?.isContentEditable ||
        ["INPUT", "TEXTAREA", "SELECT", "BUTTON"].includes(targetTag);
      if (isTypingTarget) return;

      const step = event.shiftKey ? 5 : 1;

      if (event.key === "Delete" || event.key === "Backspace") {
        event.preventDefault();
        deleteOverlay(activeOverlayId);
        return;
      }

      if (event.key === "Escape") {
        event.preventDefault();
        setActiveOverlayId(null);
        return;
      }

      if (event.key === "ArrowLeft") {
        event.preventDefault();
        updateOverlayPosition(activeOverlayId, "x", -step);
      } else if (event.key === "ArrowRight") {
        event.preventDefault();
        updateOverlayPosition(activeOverlayId, "x", step);
      } else if (event.key === "ArrowUp") {
        event.preventDefault();
        updateOverlayPosition(activeOverlayId, "y", -step);
      } else if (event.key === "ArrowDown") {
        event.preventDefault();
        updateOverlayPosition(activeOverlayId, "y", step);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [activeOverlayId]);

  useEffect(() => {
    if (!directorModeActive) return;

    const handleDirectorKeyDown = (event) => {
      const targetTag = event.target?.tagName;
      const isTyping =
        event.target?.isContentEditable ||
        ["INPUT", "TEXTAREA"].includes(targetTag);
      if (isTyping) return;

      if (event.key === "1") {
        event.preventDefault();
        switchDirectorAngle(0);
      } else if (event.key === "2") {
        event.preventDefault();
        switchDirectorAngle(1);
      } else if (event.key === "3") {
        event.preventDefault();
        switchDirectorAngle(2);
      } else if (event.key === "4") {
        event.preventDefault();
        switchDirectorAngle(3);
      } else if (event.key === "Escape") {
        event.preventDefault();
        setDirectorModeActive(false);
      }
    };

    window.addEventListener("keydown", handleDirectorKeyDown);
    return () => window.removeEventListener("keydown", handleDirectorKeyDown);
  }, [directorModeActive, activeDirectorAngle, previewTimelineTime, directorAngles]);

  // Playback Logic: Handle loop of single clip OR sequence of timeline
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    let boundaryPending = false;

    const handlePlay = () => {
      const currentClip = timeline[activeTimelineIndex];
      if (
        renderedOutputUrl ||
        !currentClip ||
        activeTimelineIndex !== 0 ||
        !addHook ||
        hookPreviewLoop ||
        trimPreviewLoop
      )
        return;

      const currentWindow = getTimelineClipWindow(currentClip);
      const startTime = Number(currentWindow.start || 0);
      const absoluteHookStart = startTime + resolvedHookStart;
      const absoluteHookEnd = startTime + hookEnd;

      if (absoluteHookEnd <= absoluteHookStart + 0.05) return;
      if (
        hookPreviewSequenceRef.current.active ||
        hookPreviewSequenceRef.current.phase === "skip-duplicate"
      )
        return;

      const startingFromClipStart = Math.abs(video.currentTime - startTime) <= 0.12;
      const startingFromHookStart = Math.abs(video.currentTime - absoluteHookStart) <= 0.12;

      if (!startingFromClipStart && !startingFromHookStart) return;

      hookPreviewSequenceRef.current = {
        active: true,
        mode: "opening-sequence",
        phase: "opening",
        timelineIndex: activeTimelineIndex,
        absoluteStart: absoluteHookStart,
        absoluteEnd: absoluteHookEnd,
      };

      if (startingFromClipStart) {
        video.currentTime = absoluteHookStart;
      }
    };

    const handleTimeUpdate = event => {
      if (boundaryPending) return;
      const expectedSource = getSafeMediaSource(renderedOutputUrl || timeline[activeTimelineIndex]?.url);
      if (video.readyState < 2 || video.currentSrc !== expectedSource) return;
      setVideoTime(video.currentTime);
      // Scrubbing a paused edit must not trigger playback loops or skip footage.
      // Rendered output is already a complete sequence with its own clock.
      const naturalEnd = event?.type === "ended" && previewPlaybackIntentRef.current;
      if ((video.paused && !naturalEnd) || renderedOutputUrl) return;

      const currentClip = timeline[activeTimelineIndex];
      if (!currentClip) return;

      const currentWindow = getTimelineClipWindow(currentClip);
      const startTime = Number(currentWindow.start || 0);
      const endTime = Number(currentWindow.end || video.duration || 0);
      const absoluteHookStart = startTime + resolvedHookStart;
      const absoluteHookEnd = startTime + hookEnd;
      const hookPreviewSequence = hookPreviewSequenceRef.current;

      if (hookPreviewSequence.active && hookPreviewSequence.timelineIndex === activeTimelineIndex) {
        if (video.currentTime >= hookPreviewSequence.absoluteEnd - 0.02) {
          const nextSequenceState =
            hookPreviewSequence.mode === "opening-sequence"
              ? {
                  active: false,
                  mode: "opening-sequence",
                  phase: "skip-duplicate",
                  timelineIndex: activeTimelineIndex,
                  absoluteStart: absoluteHookStart,
                  absoluteEnd: absoluteHookEnd,
                }
              : { active: false };
          hookPreviewSequenceRef.current = nextSequenceState;
          video.currentTime = startTime;
          if (previewPlaybackIntentRef.current) {
            safePlayMediaElement(video);
          }
          return;
        }
      }

      if (
        hookPreviewSequence.phase === "skip-duplicate" &&
        hookPreviewSequence.timelineIndex === activeTimelineIndex &&
        video.currentTime >= absoluteHookStart - 0.02 &&
        video.currentTime < absoluteHookEnd - 0.02
      ) {
        hookPreviewSequenceRef.current = { active: false };
        video.currentTime = absoluteHookEnd;
        if (previewPlaybackIntentRef.current) {
          safePlayMediaElement(video);
        }
        return;
      }

      if (
        activeTimelineIndex === 0 &&
        addHook &&
        hookPreviewLoop &&
        absoluteHookEnd > absoluteHookStart + 0.05
      ) {
        if (video.currentTime < absoluteHookStart || video.currentTime >= absoluteHookEnd) {
          video.currentTime = absoluteHookStart;
          if (previewPlaybackIntentRef.current) {
            safePlayMediaElement(video);
          }
          return;
        }
      }

      if (trimPreviewLoop && endTime > startTime + 0.05) {
        if (video.currentTime < startTime || video.currentTime >= endTime) {
          video.currentTime = startTime;
          if (previewPlaybackIntentRef.current) {
            safePlayMediaElement(video);
          }
          return;
        }
      }

      if (
        silenceRemoval &&
        silencePreview?.clipId === currentClip.id &&
        Array.isArray(silencePreview.silenceSegments)
      ) {
        const activeSilence = silencePreview.silenceSegments.find(segment => {
          const segmentStart = Number(segment?.start || 0);
          const segmentEnd = Number(segment?.end || 0);
          return video.currentTime >= segmentStart && video.currentTime < segmentEnd;
        });

        if (activeSilence) {
          const jumpTarget = Math.min(
            endTime,
            Math.max(startTime, Number(activeSilence.end || video.currentTime || 0))
          );
          if (jumpTarget > video.currentTime + 0.05) {
            video.currentTime = jumpTarget;
            return;
          }
        }
      }

      // If we reach the end of this clip's designated playtime
      const decodedBoundaryReached = event?.type === "frame-boundary" &&
        Number.isFinite(event.mediaTime) && event.mediaTime >= endTime;
      if (video.currentTime >= endTime || decodedBoundaryReached) {
        if (trimPreviewLoop) {
          video.currentTime = startTime;
          if (previewPlaybackIntentRef.current) {
            safePlayMediaElement(video);
          }
          return;
        }

        // If there is a NEXT clip in timeline, play it
        if (activeTimelineIndex < timeline.length - 1) {
          const nextIndex = activeTimelineIndex + 1;
          const nextVideo = sourceSequenceDoubleBuffer
            ? programmeVideoSlotsRef.current[nextIndex % 3] : null;
          const nextClip = timeline[nextIndex];
          const nextStart = getTimelineClipWindow(nextClip).start;
          const ready = nextVideo && nextVideo.currentSrc === getSafeMediaSource(nextClip.url) &&
            nextVideo.readyState >= 3 && !nextVideo.seeking &&
            Math.abs(nextVideo.currentTime - nextStart) <= .001;
          preparedCutHandoffRef.current = ready && previewPlaybackIntentRef.current
            ? { from: video, to: nextVideo } : null;
          boundaryPending = true;
          // Start the prepared decoder before the expensive editor commit.
          // Waiting until layout effects run leaves the picture clock paused
          // while the continuous song/cues advance through every hard cut.
          if (preparedCutHandoffRef.current) safePlayMediaElement(nextVideo);
          video.pause();
          // Native frame/media events occur outside React's commit. Promote
          // the already prepared decoder now instead of leaving a paused
          // programme visible until a later batched render/passive effect.
          try { flushSync(() => setActiveTimelineIndex(nextIndex)); }
          finally { preparedCutHandoffRef.current = null; }
        } else {
          // Ordinary playback ends on the last valid programme frame. Explicit
          // trim/hook loops have already been handled above. Stop the intent
          // before seeking so a late canplay cannot restart the sequence.
          boundaryPending = true;
          pauseSynchronizedPreview();
          hookPreviewSequenceRef.current = { active: false };
          const fps = Math.max(1, Number(exportSettings.fps) || 24);
          const lastFrame = Math.max(startTime, endTime - 1 / fps);
          video.currentTime = lastFrame;
          setVideoTime(lastFrame);
        }
      }
    };

    video.addEventListener("play", handlePlay);
    video.addEventListener("timeupdate", handleTimeUpdate);
    video.addEventListener("ended", handleTimeUpdate);
    video.addEventListener("loadedmetadata", handleLoadedMetadata);
    const stopBoundaryWatch = watchStudioClipBoundary(video, {
      end: getTimelineClipWindow(timeline[activeTimelineIndex]).end,
      enabled: !renderedOutputUrl && !trimPreviewLoop && !hookPreviewLoop && !addHook,
      onBoundary: mediaTime => handleTimeUpdate({ type: "frame-boundary", mediaTime }),
    });
    return () => {
      stopBoundaryWatch();
      video.removeEventListener("play", handlePlay);
      video.removeEventListener("timeupdate", handleTimeUpdate);
      video.removeEventListener("ended", handleTimeUpdate);
      video.removeEventListener("loadedmetadata", handleLoadedMetadata);
    };
  }, [
    addHook,
    activeTimelineIndex,
    hookEnd,
    hookPreviewLoop,
    resolvedHookStart,
    selectedClip,
    silencePreview,
    silenceRemoval,
    timeline,
    trimPreviewLoop,
    renderedOutputUrl,
    exportSettings.fps,
  ]);

  useEffect(() => {
    const cleanups = [];
    for (let ahead = 1; ahead <= 2; ahead++) {
      const standby = programmeVideoSlotsRef.current[(activeProgrammeSlot + ahead) % 3];
      if (!standby) continue;
      standby.pause(); standby.muted = true;
      const next = timeline[activeTimelineIndex + ahead];
      if (!sourceSequenceDoubleBuffer || !next || navigator.connection?.saveData || isWorkflowPaused) {
        applySafeMediaSource(standby, null);
        continue;
      }
      cleanups.push(primeStudioSourceVideo(standby, { url: next.url, start: getTimelineClipWindow(next).start }));
    }
    return () => cleanups.forEach(cleanup => cleanup());
  }, [timeline, activeTimelineIndex, activeProgrammeSlot, sourceSequenceDoubleBuffer, isWorkflowPaused]);

  // Effect: Switch video Source when activeTimelineIndex changes OR Jump when selecting a viral clip
  useLayoutEffect(() => {
    let removePendingSourceSeek;
    if (videoRef.current && timeline[activeTimelineIndex]) {
      const clip = timeline[activeTimelineIndex];
      const clipWindow = getTimelineClipWindow(clip);
      const pendingSeek = pendingTimelineSeekRef.current;
      const hasPendingSeek = pendingSeek?.index === activeTimelineIndex;
      const previous = previewWindowRef.current;
      const sameClip = previous?.clipId === clip.id && previous.renderedOutputUrl === renderedOutputUrl;
      const windowChanged = !sameClip || previous.start !== clipWindow.start || previous.end !== clipWindow.end;
      const targetStart = renderedOutputUrl
        ? 0
        : hasPendingSeek
          ? Number(pendingSeek.sourceTime || clipWindow.start || 0)
          : Number(clipWindow.start || 0);

      // 1. Handle SRC changes
      // Use property .src for comparison as it is always absolute, just like our Firebase URLs
      const currentSrc = videoRef.current.src;
      const afterSource = getSafeMediaSource(renderedOutputUrl || clip.url);
      if (currentSrc !== afterSource && afterSource) {
        // Refreshing a private source link preserves the current editing frame.
        const refreshedTime = sameClip && !hasPendingSeek
          ? clampNumber(videoRef.current.currentTime, clipWindow.start, clipWindow.end, targetStart)
          : targetStart;
        setIsAfterPreviewReady(Boolean(videoRef.current?.readyState >= 2));
        const switchingVideo = videoRef.current;
        const seekVersion = programmeSeekIntentRef.current.version;
        const restoreSeek = () => {
          if (videoRef.current !== switchingVideo || switchingVideo.src !== afterSource ||
              previewWindowRef.current?.clipId !== clip.id) return;
          const latestSeek = programmeSeekIntentRef.current;
          const restoredTime = !renderedOutputUrl && latestSeek.version !== seekVersion &&
            latestSeek.sourceId === (clip.sourceClipId || clip.id)
              ? latestSeek.time : refreshedTime;
          switchingVideo.currentTime = restoredTime;
          setVideoTime(restoredTime);
          if (previewPlaybackIntentRef.current) safePlayMediaElement(switchingVideo);
        };
        switchingVideo.addEventListener("loadedmetadata", restoreSeek, { once: true });
        removePendingSourceSeek = () => switchingVideo.removeEventListener("loadedmetadata", restoreSeek);
        applySafeMediaSource(videoRef.current, afterSource);
        videoRef.current.currentTime = refreshedTime;
        setVideoTime(refreshedTime);
        if (previewPlaybackIntentRef.current) {
          safePlayMediaElement(videoRef.current);
        }
      }
      // 2. Handle JUMP within the same file when the active timeline window changes
      else {
        // Metadata and unrelated timeline edits must not rewind the monitor.
        const advancingHandoff = !hasPendingSeek && previewPlaybackIntentRef.current &&
          preparedCutHandoffRef.current?.to === videoRef.current;
        const nextTime = (sameClip && !hasPendingSeek) || advancingHandoff
          ? clampNumber(videoRef.current.currentTime, clipWindow.start, clipWindow.end, targetStart)
          : targetStart;
        if (windowChanged && Math.abs(videoRef.current.currentTime - nextTime) > 0.001 && !isDragging) {
          videoRef.current.currentTime = nextTime;
          setVideoTime(nextTime);
          // Ensure playing
          if (previewPlaybackIntentRef.current && videoRef.current.paused) {
            safePlayMediaElement(videoRef.current);
          }
        }
      }

      if (hasPendingSeek) {
        pendingTimelineSeekRef.current = null;
        videoRef.current.currentTime = targetStart;
        setVideoTime(targetStart);
        // A newer pause overrides a play request queued before a source switch.
        if (pendingSeek.play && previewPlaybackIntentRef.current) safePlayMediaElement(videoRef.current);
      }
      if (!isDragging) previewWindowRef.current = {
        clipId: clip.id, start: clipWindow.start, end: clipWindow.end, renderedOutputUrl,
      };
      if (videoRef.current.videoWidth > 0 && videoRef.current.videoHeight > 0) {
        setProgrammeMediaAspectRatio(videoRef.current.videoWidth / videoRef.current.videoHeight);
      }
      if (previewPlaybackIntentRef.current && videoRef.current.paused) safePlayMediaElement(videoRef.current);
    }
    return () => removePendingSourceSeek?.();
  }, [activeTimelineIndex, timeline, selectedClip, isDragging, renderedOutputUrl]);

  useEffect(() => {
    const programme = videoRef.current, subject = subjectProgrammeVideoRef.current;
    if (!programme || !subject || !subjectProgrammePreview) return undefined;
    const start = subjectProgrammePreview.scene.sourceRange.start;
    const sync = () => {
      const target = Math.max(0, programme.currentTime - start);
      subject.muted = true;
      subject.playbackRate = programme.playbackRate;
      if (subject.readyState >= 1 && Math.abs(subject.currentTime - target) > .04) {
        setIsAfterPreviewReady(false);
        subject.currentTime = target;
      }
      if (programme.paused || programme.ended) subject.pause();
      else if (subject.paused) safePlayMediaElement(subject);
    };
    const confirm = () => {
      if (subject.readyState >= 2 && !subject.seeking &&
          Math.abs(subject.currentTime - (programme.currentTime - start)) <= .085) {
        setIsAfterPreviewReady(true);
      }
    };
    const events = ["seeked", "timeupdate", "play", "pause", "ratechange", "loadeddata"];
    events.forEach(event => programme.addEventListener(event, sync));
    subject.addEventListener("loadedmetadata", sync);
    subject.addEventListener("loadeddata", confirm);
    subject.addEventListener("seeked", confirm);
    sync(); confirm();
    return () => {
      events.forEach(event => programme.removeEventListener(event, sync));
      subject.removeEventListener("loadedmetadata", sync);
      subject.removeEventListener("loadeddata", confirm);
      subject.removeEventListener("seeked", confirm);
      subject.pause();
    };
  }, [subjectProgrammePreview, activeTimelineIndex, renderedOutputUrl]);

  // Exact paused pictures on the shared continuous delivery clock. Source/audio
  // playback remains native; reviewed subject/composition decoders retain their
  // own authority. A late response must never replace a newer seek/project.
  const pausedSourceGeneration = sourceAccessGenerations.projectId === projectId
    ? sourceAccessGenerations.byPath[currentTimelineClip?.sourceStoragePath || currentTimelineClip?.storagePath]
    : undefined;
  const pausedFrameRequestKey = !renderedOutputUrl && isPreviewPaused && !projectSourcesRefreshing &&
    !subjectProgrammePreview && !activeSideBySideOverlay && !speakerStackPreviewIsLive &&
    (!smartCrop || ["center", "speaker_track"].includes(smartCropMode))
      ? pausedStudioFrameKey(pausedStudioMappedFrameRequest(currentTimelineClip, Number(videoTime),
        exportSettings.fps, timeline, previewOutputTimeMap, pausedSourceGeneration))
      : "";
  useEffect(() => {
    if (!pausedFrameRequestKey) { setPausedSourceFrame(null); return undefined; }
    const key = pausedFrameRequestKey;
    const request = JSON.parse(key);
    const controller = new AbortController();
    let disposed = false;
    setPausedSourceFrame({ key, status: "loading" });
    const deadline = setTimeout(() => {
      controller.abort();
      if (!disposed) setPausedSourceFrame({ key, status: "unavailable" });
    }, 30000);
    const timer = setTimeout(async () => {
      try {
        const frame = await loadStudioSourceFrame(request, controller.signal);
        clearTimeout(deadline);
        if (!disposed && !controller.signal.aborted) setPausedSourceFrame({ key, ...frame, status: "accurate" });
      } catch (_) { clearTimeout(deadline); if (!disposed) setPausedSourceFrame({ key, status: "unavailable" }); }
    }, 200);
    return () => { disposed = true; clearTimeout(timer); clearTimeout(deadline); controller.abort(); };
  }, [pausedFrameRequestKey, projectId]);

  useEffect(() => {
    const video = videoRef.current;
    const canvas = afterCanvasRef.current;
    if (!video || !canvas) return undefined;
    // The completed movie already contains the composition. Let the native
    // video presenter keep picture/audio together instead of copying every
    // decoded frame through the live editor's canvas and layout effects.
    if (renderedOutputUrl) return undefined;

    let stopProgrammeFrames = null;
    const paintProgrammeFrame = decodedSourceTime => {
      // CSS effects do not alter drawImage's decoded pixels. Use the actual
      // programme element, not the lagging Before comparison clock: a 100 ms
      // scrub across a camera cut must not show the previous shot's pixels.
      const programmeVideo = video;
      if (programmeVideo.readyState < 2 || !programmeVideo.videoWidth || !programmeVideo.videoHeight) return;
      // Size the decoded programme to the visible inner frame. Measuring the
      // outer monitor here stretches an inset canvas and makes preview framing
      // diverge from the backend's scale/crop result.
      const rect = canvas.getBoundingClientRect();
      if (rect.width < 1 || rect.height < 1) return;
      const pixelRatio = Math.min(1.5, Math.max(1, window.devicePixelRatio || 1));
      const canvasWidth = Math.max(2, Math.round(rect.width * pixelRatio));
      const canvasHeight = Math.max(2, Math.round(rect.height * pixelRatio));
      if (canvas.width !== canvasWidth) canvas.width = canvasWidth;
      if (canvas.height !== canvasHeight) canvas.height = canvasHeight;
      const context = canvas.getContext("2d", { alpha: false });
      if (!context) return;
      // Use the decoded programme's clock for its crop and Director cut. React
      // state can arrive a frame later than the video at a hard source edit.
      const pausedRequest = programmeVideo.paused && !programmeVideo.seeking && !renderedOutputUrl &&
        !subjectProgrammePreview && !activeSideBySideOverlay && !speakerStackPreviewIsLive && pausedSourceFrame?.image
        ? pausedStudioMappedFrameRequest(currentTimelineClip, Number(programmeVideo.currentTime),
          exportSettings.fps, timeline, previewOutputTimeMap, pausedSourceGeneration) : null;
      const pausedClockIsCurrent = pausedRequest && pausedSourceFrame.key === pausedStudioFrameKey(pausedRequest);
      const sampledSourceTime = pausedClockIsCurrent
        ? pausedRequest.start + pausedRequest.frameIndex / pausedRequest.fps
        : Number.isFinite(decodedSourceTime) ? decodedSourceTime : studioPresentedSourceTime(programmeVideo);
      // Crop/finish automation was applied to this input picture before final
      // retiming. Evaluate it on that picture's sampled clock, rather than
      // the fractional seek position between it and the next source frame.
      const frameOutputTime = getPreviewTimelineTime(sampledSourceTime);
      const frameMainMotion = buildMotionAutomationStyle(
        previewMotionKeyframes.filter(keyframe => keyframe.targetId === "main-video"),
        frameOutputTime, mainTransform
      );
      canvas.dataset.programmeTime = String(frameOutputTime);
      const frameCropMode = resolveReframeModeAtTime(
        reframeModeCuts, frameOutputTime, smartCrop ? smartCropMode : "off"
      );
      const frameModeCut = resolveReframeCutAtTime(reframeModeCuts, frameOutputTime);
      const frameSoloOffset = frameCropMode === "speaker_track"
        ? Number(frameModeCut?.sourceTimeOffsetSeconds || 0)
        : 0;
      const alternateVideo = sourceSplitAlternateVideoRef.current;
      const alternateTargetTime = Number(programmeVideo.currentTime || 0) + frameSoloOffset;
      const cachedSoloFrame = sourceSplitAlternateFrameRef.current;
      const alternateIsCurrent = Math.abs(frameSoloOffset) > .001 && alternateVideo?.readyState >= 2 &&
        alternateVideo.videoWidth && Math.abs(Number(alternateVideo.currentTime || 0) - alternateTargetTime) <= .36;
      const cachedAlternateIsCurrent = Math.abs(frameSoloOffset) > .001 && cachedSoloFrame?.canvas &&
        Math.abs(Number(cachedSoloFrame.sourceTime || 0) - alternateTargetTime) <= .5;
      const reviewedSubject = renderedOutputUrl ? null : resolveSubjectVfxProgrammePreview(
        subjectVfxScenes, currentTimelineClip?.sourceClipId || currentTimelineClip?.id,
        Number(programmeVideo.currentTime || 0));
      const subjectVideo = subjectProgrammeVideoRef.current;
      const subjectIsCurrent = reviewedSubject &&
        subjectVideo?.dataset.subjectJob === reviewedSubject.preview.jobId &&
        subjectVideo.readyState >= 2 && !subjectVideo.seeking &&
        Math.abs(subjectVideo.currentTime -
          (programmeVideo.currentTime - reviewedSubject.scene.sourceRange.start)) <= .085;
      canvas.dataset.programmeSource = reviewedSubject
        ? subjectIsCurrent ? "subject-vfx" : "subject-loading"
        : renderedOutputUrl ? "final" : "source";
      // Show the loading state rather than silently replacing valid effect pixels
      // with the original source while its decoder catches up to an edit.
      if (reviewedSubject && !subjectIsCurrent) return;
      const pausedFrameIsCurrent = programmeVideo.paused && !programmeVideo.seeking && !renderedOutputUrl &&
        ["off", "center", "speaker_track"].includes(frameCropMode) && Math.abs(frameSoloOffset) <= .001 &&
        !reviewedSubject && !alternateIsCurrent && !cachedAlternateIsCurrent &&
        pausedSourceFrame?.image && pausedSourceFrame.key === pausedStudioFrameKey(
          pausedStudioMappedFrameRequest(currentTimelineClip, Number(programmeVideo.currentTime),
            exportSettings.fps, timeline, previewOutputTimeMap, pausedSourceGeneration));
      canvas.dataset.sourceSampling = pausedFrameIsCurrent ? "accurate-paused" : "native";
      canvas.dataset.sourceContentHash = pausedFrameIsCurrent ? pausedSourceFrame.receipt.sourceContentHash : "";
      canvas.dataset.sourceFrameIndex = pausedFrameIsCurrent ? String(pausedSourceFrame.receipt.frameIndex) : "";
      canvas.dataset.sourceGeneration = pausedFrameIsCurrent ? pausedSourceFrame.receipt.sourceGeneration : "";
      const sourceVideo = subjectIsCurrent ? subjectVideo : pausedFrameIsCurrent ? pausedSourceFrame.image : alternateIsCurrent
        ? alternateVideo
        : cachedAlternateIsCurrent ? cachedSoloFrame.canvas : programmeVideo;
      const frameProgrammeFit = renderedOutputUrl || activeSideBySideOverlay
        ? previewProgrammeFit
        : frameCropMode === "speaker_track" || frameCropMode === "group_stack"
          ? "cover"
          : frameCropMode !== "off" ? "contain" : previewProgrammeFit;
      const frameReframePosition = interpolateReframeKeyframes(reframeKeyframes, frameOutputTime);
      const frameProgrammePosition = frameCropMode === "speaker_track" && !renderedOutputUrl
        ? `${clampNumber(frameReframePosition.x, 5, 95, 50)}% ${clampNumber(frameReframePosition.y, 8, 92, 50)}%`
        : previewProgrammePosition;
      const [rawPositionX = "50", rawPositionY = "50"] = String(
        frameProgrammePosition || "50% 50%"
      ).split(/\s+/);
      const positionX = clampNumber(parseFloat(rawPositionX), 0, 100, 50) / 100;
      const positionY = clampNumber(parseFloat(rawPositionY), 0, 100, 50) / 100;
      const sourceFrameWidth = Number(sourceVideo.videoWidth || sourceVideo.width || programmeVideo.videoWidth);
      const sourceFrameHeight = Number(sourceVideo.videoHeight || sourceVideo.height || programmeVideo.videoHeight);
      const widthScale = canvasWidth / sourceFrameWidth;
      const heightScale = canvasHeight / sourceFrameHeight;
      const scale = frameProgrammeFit === "cover"
        ? Math.max(widthScale, heightScale) * (frameCropMode === "speaker_track" && !renderedOutputUrl ? speakerTrackZoom : 1)
        : Math.min(widthScale, heightScale);
      const drawWidth = sourceFrameWidth * scale;
      const drawHeight = sourceFrameHeight * scale;
      context.fillStyle = "#000";
      context.fillRect(0, 0, canvasWidth, canvasHeight);
      const isDualCamShowEveryone =
        frameCropMode === "center" &&
        reframeAspect !== "16:9";
      if (frameCropMode === "center" && !isDualCamShowEveryone && canvasHeight > drawHeight + 8) {
        context.save();
        const bgScale = Math.max(widthScale, heightScale);
        const bgW = sourceFrameWidth * bgScale;
        const bgH = sourceFrameHeight * bgScale;
        context.filter = "blur(18px) brightness(0.42) saturate(1.25)";
        context.drawImage(
          sourceVideo,
          (canvasWidth - bgW) * 0.5,
          (canvasHeight - bgH) * 0.5,
          bgW,
          bgH
        );
        context.restore();
      }
      context.save();
      context.filter = previewProgrammeFilter;
      const currentDirectorAngle = directorModeActive ? directorAngles[activeDirectorAngle] : null;
      const timelineDirectorZoom = normalizeReframeModeCuts(reframeModeCuts).reduce(
        (zoom, cut) => cut.time <= frameOutputTime ? Number(cut.zoom || 1) : zoom,
        1
      );
      const directorZoom = renderedOutputUrl ? 1 : directorModeActive
        ? Number(currentDirectorAngle?.zoom || 1)
        : timelineDirectorZoom;
      const programmeScale = (renderedOutputUrl || activeSideBySideOverlay
        ? 1
        : hookVisualScale * smartCropBackgroundScale * finishZoom * frameMainMotion.scale) * directorZoom;
      const translateX = renderedOutputUrl || activeSideBySideOverlay
        ? 0
        : ((frameMainMotion.x - 50) / 100) * canvasWidth;
      const translateY = renderedOutputUrl || activeSideBySideOverlay
        ? 0
        : ((frameMainMotion.y - 50) / 100) * canvasHeight;
      const rotation = renderedOutputUrl || activeSideBySideOverlay
        ? 0
        : (frameMainMotion.rotation * Math.PI) / 180;
      context.translate(canvasWidth / 2 + translateX, canvasHeight / 2 + translateY);
      context.rotate(rotation);
      context.scale(programmeScale, programmeScale);
      context.translate(-canvasWidth / 2, -canvasHeight / 2);
      context.globalAlpha = renderedOutputUrl
        ? 1
        : hookPrimaryVideoOpacity * frameMainMotion.opacity;
      const isDualCamStackLive =
        (isDualCamShowEveryone || (frameCropMode === "group_stack" && speakerStackIsReady)) &&
        !renderedOutputUrl &&
        !activeSideBySideOverlay;
      if (isDualCamStackLive) {
        const gapPercent = isDualCamShowEveryone
          ? clampNumber(speakerStackFraming.cardGapPercent, 0, 3, 2.5)
          : clampNumber(speakerStackFraming.gapPercent, 0, 3, 1.2);
        const gap = Math.max(2, Math.round(canvasHeight * gapPercent / 100));
        const drawSpeakerPanel = (
          panelVideo,
          panelX,
          panelY,
          panelWidth,
          panelHeight,
          framing,
          alternateOffset = 0
        ) => {
          const wantsAlternate = Math.abs(alternateOffset) > 0.001;
          const targetAlternateTime = Number(programmeVideo.currentTime || 0) + alternateOffset;
          const alternateIsCurrent = wantsAlternate && panelVideo?.readyState >= 2 &&
            panelVideo?.videoWidth &&
            Math.abs(Number(panelVideo.currentTime || 0) - targetAlternateTime) <= 0.36;
          const cachedAlternate = sourceSplitAlternateFrameRef.current;
          const cachedAlternateIsCurrent = wantsAlternate && cachedAlternate?.canvas &&
            Math.abs(Number(cachedAlternate.sourceTime || 0) - targetAlternateTime) <= 0.5;
          // A flattened programme's reaction inset is never a second camera.
          // While the clean alternate catches up, hold its last decoded frame
          // instead of flashing a black card or enlarging the reaction inset.
          const activeVideo = wantsAlternate
            ? (alternateIsCurrent ? panelVideo : cachedAlternateIsCurrent ? cachedAlternate.canvas : null)
            : (panelVideo?.readyState >= 2 && panelVideo?.videoWidth ? panelVideo : sourceVideo);
          const activeWidth = Number(activeVideo?.videoWidth || activeVideo?.width || 0);
          const activeHeight = Number(activeVideo?.videoHeight || activeVideo?.height || 0);
          if (!activeVideo || !activeWidth || !activeHeight) return;
          const sourceRect = framing?.sourceRect || framing?.source_rect;
          const sourceX = sourceRect ? clampNumber(sourceRect.x, 0, activeWidth - 2, 0) : 0;
          const sourceY = sourceRect ? clampNumber(sourceRect.y, 0, activeHeight - 2, 0) : 0;
          const sourceWidth = sourceRect
            ? clampNumber(sourceRect.width ?? sourceRect.w, 2, activeWidth - sourceX, activeWidth - sourceX)
            : activeWidth;
          const sourceHeight = sourceRect
            ? clampNumber(sourceRect.height ?? sourceRect.h, 2, activeHeight - sourceY, activeHeight - sourceY)
            : activeHeight;
          const visibleTop = sourceRect ? 0 : clampNumber(
            framing?.source_visible_top_percent, 0, 15, 0
          ) / 100;
          const visibleBottom = sourceRect ? 0 : clampNumber(
            framing?.source_visible_bottom_percent, 0, 15, 0
          ) / 100;
          const visibleSourceY = sourceY + sourceHeight * visibleTop;
          const visibleSourceHeight = sourceHeight * (1 - visibleTop - visibleBottom);
          const zoom = clampNumber(framing?.zoom, 1, 7, 1.25);
          const focusX = sourceRect ? 0.5 : clampNumber(framing?.x, 0, 100, 50) / 100;
          const focusY = sourceRect ? 0.5 : clampNumber(framing?.y, 0, 100, 50) / 100;
          const coverScale =
            Math.max(panelWidth / sourceWidth, panelHeight / visibleSourceHeight) *
            zoom;
          const panelDrawWidth = sourceWidth * coverScale;
          const panelDrawHeight = visibleSourceHeight * coverScale;
          const unclampedX = panelX + panelWidth / 2 - focusX * panelDrawWidth;
          const unclampedY = panelY + panelHeight / 2 - focusY * panelDrawHeight;
          const drawX = clampNumber(
            unclampedX,
            panelX + panelWidth - panelDrawWidth,
            panelX,
            panelX
          );
          const drawY = clampNumber(
            unclampedY,
            panelY + panelHeight - panelDrawHeight,
            panelY,
            panelY
          );
          context.save();
          context.beginPath();
          const shouldRoundPanel =
            (isDualCamShowEveryone && speakerStackFraming.roundedCards !== false) ||
            (!isDualCamShowEveryone &&
              ["pip_2", "active_2"].includes(speakerStackLayout) &&
              panelWidth < canvasWidth);
          if (shouldRoundPanel && typeof context.roundRect === "function") {
            context.roundRect(
              panelX,
              panelY,
              panelWidth,
              panelHeight,
              Math.max(
                1,
                Math.min(panelWidth, panelHeight) *
                  clampNumber(speakerStackFraming.cardRadiusPercent, 2, 18, 8) / 100
              )
            );
          } else {
            context.rect(panelX, panelY, panelWidth, panelHeight);
          }
          context.clip();
          if (sourceRect || visibleTop || visibleBottom) {
            context.drawImage(
              activeVideo,
              sourceX,
              visibleSourceY,
              sourceWidth,
              visibleSourceHeight,
              drawX,
              drawY,
              panelDrawWidth,
              panelDrawHeight
            );
          } else {
            context.drawImage(activeVideo, drawX, drawY, panelDrawWidth, panelDrawHeight);
          }
          context.restore();
        };

        if (isDualCamShowEveryone) {
          const dividerPercent = clampNumber(
            speakerStackFraming.dividerPercent, 35, 65, 50
          );
          const cardInset = speakerStackFraming.roundedCards === false
            ? 0
            : canvasWidth * clampNumber(speakerStackFraming.cardInsetPercent, 0, 8, 2.5) / 100;
          const cardWidth = Math.max(1, canvasWidth - cardInset * 2);
          const availableHeight = Math.max(2, canvasHeight - cardInset * 2 - gap);
          const topHeight = Math.max(1, availableHeight * dividerPercent / 100);
          const topFraming = {
            ...speakerStackFraming.top,
            x: clampNumber(speakerStackFraming?.top?.x ?? 30, 0, 100, 30),
            y: clampNumber(speakerStackFraming?.top?.y ?? 50, 0, 100, 50),
            zoom: clampNumber(speakerStackFraming?.top?.zoom ?? 1.25, 1, 7, 1.25),
          };
          const bottomFraming = {
            ...speakerStackFraming.bottom,
            x: clampNumber(speakerStackFraming?.bottom?.x ?? 70, 0, 100, 70),
            y: clampNumber(speakerStackFraming?.bottom?.y ?? 50, 0, 100, 50),
            zoom: clampNumber(speakerStackFraming?.bottom?.zoom ?? 1.25, 1, 7, 1.25),
          };
          if (speakerStackFraming.trackSpeakers) {
            if (topFraming.keyframes?.length) Object.assign(topFraming, interpolateReframeKeyframes(topFraming.keyframes, frameOutputTime));
            if (bottomFraming.keyframes?.length) Object.assign(bottomFraming, interpolateReframeKeyframes(bottomFraming.keyframes, frameOutputTime));
          }
          const alternateVideo = sourceSplitAlternateVideoRef.current;
          const frameSplitTopOffset = resolveSourceSplitTimeOffset(speakerStackFraming.top, frameOutputTime);
          const frameSplitBottomOffset = resolveSourceSplitTimeOffset(speakerStackFraming.bottom, frameOutputTime);
          const topPanel = {
            video: Math.abs(frameSplitTopOffset) > 0.001 ? alternateVideo : sourceVideo,
            framing: topFraming,
            offset: frameSplitTopOffset,
          };
          const bottomPanel = {
            video: Math.abs(frameSplitBottomOffset) > 0.001 ? alternateVideo : sourceVideo,
            framing: bottomFraming,
            offset: frameSplitBottomOffset,
          };
          const primaryPanel =
            resolveSpeakerFocusSlot(speakerFocusCuts, frameOutputTime) === "bottom" ? bottomPanel : topPanel;
          const secondaryPanel =
            resolveSpeakerFocusSlot(speakerFocusCuts, frameOutputTime) === "bottom" ? topPanel : bottomPanel;
          // A reviewed speaker cut swaps panel order without leaving Show Everyone.
          drawSpeakerPanel(primaryPanel.video, cardInset, cardInset, cardWidth, topHeight, primaryPanel.framing, primaryPanel.offset);
          drawSpeakerPanel(
            secondaryPanel.video,
            cardInset,
            cardInset + topHeight + gap,
            cardWidth,
            Math.max(1, canvasHeight - cardInset * 2 - topHeight - gap),
            secondaryPanel.framing,
            secondaryPanel.offset
          );
        } else {
          const cameraCount = clampNumber(speakerStackCameraCount, 2, 4, 2);
          const halfWidth = Math.max(1, (canvasWidth - gap) / 2);
          const halfHeight = Math.max(1, (canvasHeight - gap) / 2);
          let panelRects;
          if (cameraCount === 4 || speakerStackLayout === "grid_4") {
            panelRects = [
              [0, 0, halfWidth, halfHeight],
              [halfWidth + gap, 0, halfWidth, halfHeight],
              [0, halfHeight + gap, halfWidth, halfHeight],
              [halfWidth + gap, halfHeight + gap, halfWidth, halfHeight],
            ];
          } else if (cameraCount === 3 || speakerStackLayout === "hero_3") {
            const heroHeight = Math.max(1, canvasHeight * 0.54 - gap / 2);
            const lowerY = heroHeight + gap;
            const lowerHeight = Math.max(1, canvasHeight - lowerY);
            panelRects = [
              [0, 0, canvasWidth, heroHeight],
              [0, lowerY, halfWidth, lowerHeight],
              [halfWidth + gap, lowerY, halfWidth, lowerHeight],
            ];
          } else if (speakerStackLayout === "split_2") {
            panelRects = [
              [0, 0, halfWidth, canvasHeight],
              [halfWidth + gap, 0, halfWidth, canvasHeight],
            ];
          } else if (speakerStackLayout === "pip_2" || speakerStackLayout === "active_2") {
            const secondarySize =
              clampNumber(speakerStackFraming.secondarySizePercent, 20, 46, 30) / 100;
            const secondaryWidth = canvasWidth * secondarySize;
            const secondaryHeight = canvasHeight * secondarySize;
            const secondaryCenterX =
              (clampNumber(speakerStackFraming.secondaryXPercent, 16, 84, 80) / 100) *
              canvasWidth;
            const secondaryCenterY =
              (clampNumber(speakerStackFraming.secondaryYPercent, 16, 84, 20) / 100) *
              canvasHeight;
            panelRects = [
              [0, 0, canvasWidth, canvasHeight],
              [
                clampNumber(
                  secondaryCenterX - secondaryWidth / 2,
                  0,
                  canvasWidth - secondaryWidth,
                  0
                ),
                clampNumber(
                  secondaryCenterY - secondaryHeight / 2,
                  0,
                  canvasHeight - secondaryHeight,
                  0
                ),
                secondaryWidth,
                secondaryHeight,
              ],
            ];
          } else if (speakerStackLayout === "spotlight_2") {
            panelRects = [[0, 0, canvasWidth, canvasHeight], [0, 0, 0, 0]];
          } else {
            const dividerRatio =
              clampNumber(speakerStackFraming.dividerPercent, 35, 65, 50) / 100;
            const topHeight = Math.max(1, canvasHeight * dividerRatio - gap / 2);
            const bottomY = topHeight + gap;
            panelRects = [
              [0, 0, canvasWidth, topHeight],
              [0, bottomY, canvasWidth, Math.max(1, canvasHeight - bottomY)],
            ];
          }
          const stackVideosBySlot = {
            top: speakerStackTopVideoRef.current,
            bottom: speakerStackBottomVideoRef.current,
            third: speakerStackThirdVideoRef.current,
            fourth: speakerStackFourthVideoRef.current,
          };
          const reviewedFocusSlot = resolveSpeakerFocusSlot(
            speakerFocusCuts,
            frameOutputTime,
            activeSpeakerStackSlots
          );
          const displaySlots = ["active_2", "spotlight_2"].includes(speakerStackLayout)
            ? [
                reviewedFocusSlot,
                ...activeSpeakerStackSlots.filter(slot => slot !== reviewedFocusSlot),
              ]
            : activeSpeakerStackSlots;
          displaySlots.forEach((slot, index) => {
            const [x, y, width, height] = panelRects[index] || [];
            if (!width || !height) return;
            drawSpeakerPanel(
              stackVideosBySlot[slot],
              x,
              y,
              width,
              height,
              speakerStackFraming[slot]
            );
          });
          context.fillStyle = "rgba(5, 8, 14, 0.96)";
          if (cameraCount === 2 && speakerStackLayout === "split_2") {
            context.fillRect(panelRects[0][2], 0, gap, canvasHeight);
          } else if (cameraCount === 2 && speakerStackLayout === "stack_2") {
            context.fillRect(0, panelRects[0][3], canvasWidth, gap);
          } else if (cameraCount === 3) {
            context.fillRect(0, panelRects[0][3], canvasWidth, gap);
            context.fillRect(halfWidth, panelRects[1][1], gap, panelRects[1][3]);
          } else if (cameraCount === 4) {
            context.fillRect(0, halfHeight, canvasWidth, gap);
            context.fillRect(halfWidth, 0, gap, canvasHeight);
          }
        }
      } else {
        const drawX = (canvasWidth - drawWidth) * positionX;
        const drawY = (canvasHeight - drawHeight) * positionY;
        const roundContainedFootage =
          mainFrame.enabled &&
          frameProgrammeFit === "contain" &&
          drawWidth > 8 &&
          drawHeight > 8;
        if (roundContainedFootage) {
          const containedRadius = Math.max(
            2,
            Math.min(drawWidth, drawHeight) *
              clampNumber(mainFrame.radiusPercent, 3, 16, 8) / 100
          );
          context.save();
          context.beginPath();
          if (typeof context.roundRect === "function") {
            context.roundRect(drawX, drawY, drawWidth, drawHeight, containedRadius);
          } else {
            const right = drawX + drawWidth;
            const bottom = drawY + drawHeight;
            context.moveTo(drawX + containedRadius, drawY);
            context.lineTo(right - containedRadius, drawY);
            context.quadraticCurveTo(right, drawY, right, drawY + containedRadius);
            context.lineTo(right, bottom - containedRadius);
            context.quadraticCurveTo(right, bottom, right - containedRadius, bottom);
            context.lineTo(drawX + containedRadius, bottom);
            context.quadraticCurveTo(drawX, bottom, drawX, bottom - containedRadius);
            context.lineTo(drawX, drawY + containedRadius);
            context.quadraticCurveTo(drawX, drawY, drawX + containedRadius, drawY);
            context.closePath();
          }
          context.clip();
          context.drawImage(sourceVideo, drawX, drawY, drawWidth, drawHeight);
          context.restore();
        } else {
          context.drawImage(sourceVideo, drawX, drawY, drawWidth, drawHeight);
        }
      }
      context.restore();
      const joinWindow = getTimelineClipWindow(currentTimelineClip);
      const joinFade = studioJoinFade({
        duration: joinWindow.duration,
        localTime: (pausedFrameIsCurrent ? sampledSourceTime : Number(programmeVideo.currentTime || 0)) - Number(joinWindow.start || 0),
        transitionIn: currentTimelineClip?.transitionIn,
        transitionOut: currentTimelineClip?.transitionOut,
        transitionDuration: currentTimelineClip?.transitionDuration,
        renderedOutput: Boolean(renderedOutputUrl),
      });
      canvas.dataset.joinTransition = joinFade.mode;
      canvas.dataset.joinOpacity = String(joinFade.opacity);
      if (joinFade.opacity > 0) {
        // Fade the underlying shot. Editable graphics stay above it, as they do
        // in the deterministic render composition; finished exports are exempt.
        context.save();
        context.setTransform(1, 0, 0, 1, 0, 0);
        context.filter = "none";
        context.globalAlpha = joinFade.opacity;
        context.fillStyle = joinFade.mode === "energy_flash" ? "#ffffff" : "#000000";
        context.fillRect(0, 0, canvasWidth, canvasHeight);
        context.restore();
      }
      if (precisionPreviewGrade || (finishPreviewIsLive && advancedColorCube)) {
        const pixels = context.getImageData(0, 0, canvasWidth, canvasHeight);
        if (precisionPreviewGrade) applyPrecisionGrade(pixels.data, precisionPreviewGrade);
        if (finishPreviewIsLive && advancedColorCube) applyColorCube(pixels.data, advancedColorCube);
        context.putImageData(pixels, 0, 0);
      }
      // Use this decoded picture's programme time at grade boundaries. React's
      // transport state can lag behind the frame callback during playback.
      for (const layer of finishPreviewIsLive ? compiledAdjustmentGrades : []) {
        if (frameOutputTime < layer.startTime || frameOutputTime >= layer.startTime + layer.duration) continue;
        const adjustment = layer.effects;
        const grade = adjustment.color;
        if (grade.precisionGrade) {
          const pixels = context.getImageData(0, 0, canvasWidth, canvasHeight);
          applyPrecisionGrade(pixels.data, buildPrecisionGrade(grade));
          if (adjustment.color_cube) applyColorCube(pixels.data, adjustment.color_cube);
          context.putImageData(pixels, 0, 0);
        } else {
          const scratch = adjustmentScratchRef.current || document.createElement("canvas");
          adjustmentScratchRef.current = scratch;
          if (scratch.width !== canvasWidth) scratch.width = canvasWidth;
          if (scratch.height !== canvasHeight) scratch.height = canvasHeight;
          const scratchContext = scratch.getContext("2d");
          if (!scratchContext) continue;
          scratchContext.drawImage(canvas, 0, 0);
          context.save();
          context.filter = buildCinematicCssFilter(grade) || "none";
          context.clearRect(0, 0, canvasWidth, canvasHeight);
          context.drawImage(scratch, 0, 0);
          context.restore();
        }
        if (adjustment.glow?.intensity > 0 && adjustment.glow.radius > 0) {
          const scratch = adjustmentScratchRef.current || document.createElement('canvas');
          adjustmentScratchRef.current = scratch; scratch.width = canvasWidth; scratch.height = canvasHeight;
          const soft = scratch.getContext('2d'); soft.clearRect(0, 0, canvasWidth, canvasHeight);
          soft.filter = `blur(${adjustment.glow.radius * canvasHeight / 720}px)`;
          soft.drawImage(canvas, 0, 0); soft.filter = 'none';
          context.save(); context.globalCompositeOperation = 'screen';
          context.globalAlpha = adjustment.glow.intensity; context.drawImage(scratch, 0, 0); context.restore();
        }
      }
    };
    const startProgrammeLoop = () => {
      if (!stopProgrammeFrames) stopProgrammeFrames = watchStudioProgrammeFrames(video, paintProgrammeFrame);
    };
    const stopProgrammeLoop = () => {
      stopProgrammeFrames?.();
      stopProgrammeFrames = null;
      paintProgrammeFrame();
    };

    video.addEventListener("loadeddata", paintProgrammeFrame);
    video.addEventListener("loadedmetadata", paintProgrammeFrame);
    video.addEventListener("seeked", paintProgrammeFrame);
    video.addEventListener("timeupdate", paintProgrammeFrame);
    video.addEventListener("play", startProgrammeLoop);
    video.addEventListener("pause", stopProgrammeLoop);
    const syncedSource = beforeVideoRef.current;
    syncedSource?.addEventListener("loadeddata", paintProgrammeFrame);
    syncedSource?.addEventListener("seeked", paintProgrammeFrame);
    const stackTopVideo = speakerStackTopVideoRef.current;
    const stackBottomVideo = speakerStackBottomVideoRef.current;
    const stackThirdVideo = speakerStackThirdVideoRef.current;
    const stackFourthVideo = speakerStackFourthVideoRef.current;
    const sourceSplitAlternateVideo = sourceSplitAlternateVideoRef.current;
    const stackVideos = [
      stackTopVideo,
      stackBottomVideo,
      stackThirdVideo,
      stackFourthVideo,
      sourceSplitAlternateVideo,
      subjectProgrammeVideoRef.current,
    ];
    stackVideos.forEach(stackVideo => {
      stackVideo?.addEventListener("loadeddata", paintProgrammeFrame);
      stackVideo?.addEventListener("seeked", paintProgrammeFrame);
    });
    const transport = phoneFrameRef.current?.parentElement?.querySelector(".preview-custom-controls");
    const resizeProgramme = () => {
      if (phoneFrameRef.current) {
        phoneFrameRef.current.style.setProperty("--caption-programme-width",
          `${phoneFrameRef.current.clientWidth}px`);
        phoneFrameRef.current.style.setProperty("--title-programme-height",
          `${phoneFrameRef.current.clientHeight}px`);
        phoneFrameRef.current.style.setProperty("--title-preview-scale",
          String(phoneFrameRef.current.clientHeight / 720));
        phoneFrameRef.current.style.setProperty("--title-layout-width",
          `${720 * phoneFrameRef.current.clientWidth / Math.max(1, phoneFrameRef.current.clientHeight)}px`);
      }
      if (transport && phoneFrameRef.current?.parentElement) {
        phoneFrameRef.current.parentElement.style.setProperty("--transport-space",
          `${Math.ceil(transport.getBoundingClientRect().height) + 8}px`);
      }
      paintProgrammeFrame();
    };
    const previewResizeObserver = typeof ResizeObserver === "function"
      ? new ResizeObserver(resizeProgramme) : null;
    if (phoneFrameRef.current) previewResizeObserver?.observe(phoneFrameRef.current);
    if (transport) previewResizeObserver?.observe(transport);
    resizeProgramme();
    window.addEventListener("resize", paintProgrammeFrame);
    paintProgrammeFrame();
    if (!video.paused) startProgrammeLoop();

    return () => {
      video.removeEventListener("loadeddata", paintProgrammeFrame);
      video.removeEventListener("loadedmetadata", paintProgrammeFrame);
      video.removeEventListener("seeked", paintProgrammeFrame);
      video.removeEventListener("timeupdate", paintProgrammeFrame);
      video.removeEventListener("play", startProgrammeLoop);
      video.removeEventListener("pause", stopProgrammeLoop);
      syncedSource?.removeEventListener("loadeddata", paintProgrammeFrame);
      syncedSource?.removeEventListener("seeked", paintProgrammeFrame);
      stackVideos.forEach(stackVideo => {
        stackVideo?.removeEventListener("loadeddata", paintProgrammeFrame);
        stackVideo?.removeEventListener("seeked", paintProgrammeFrame);
      });
      previewResizeObserver?.disconnect();
      window.removeEventListener("resize", paintProgrammeFrame);
      stopProgrammeFrames?.();
    };
  }, [
    pausedSourceFrame,
    pausedSourceGeneration,
    timeline,
    previewOutputTimeMap,
    previewMotionKeyframes,
    mainTransform,
    smartCrop,
    smartCropMode,
    speakerTrackZoom,
    activeSideBySideOverlay,
    activeTimelineIndex,
    currentTimelineClip,
    subjectVfxScenes,
    subjectProgrammePreview,
    previewProgrammeFit,
    previewProgrammeFilter,
    precisionPreviewGrade,
    compiledAdjustmentGrades,
    advancedColorCube,
    finishPreviewIsLive,
    previewProgrammePosition,
    reframeKeyframes,
    reframeAspect,
    renderedOutputUrl,
    reframeModeCuts,
    activeSpeakerStackSlots,
    speakerStackCameraCount,
    speakerStackFraming,
    speakerStackLayout,
    speakerStackPreviewIsLive,
    speakerFocusCuts,
    sourceSplitBottomOffset,
    sourceSplitTopOffset,
    previewTimelineTime,
    hookVisualScale,
    smartCropBackgroundScale,
    finishZoom,
    mainMotionAutomation.x,
    mainMotionAutomation.y,
    mainMotionAutomation.rotation,
    mainMotionAutomation.scale,
    mainMotionAutomation.opacity,
    hookPrimaryVideoOpacity,
    mainFrame.enabled,
    mainFrame.radiusPercent,
  ]);

  useEffect(() => {
    const afterVideo = videoRef.current;
    const beforeVideo = beforeVideoRef.current;
    const beforeCanvas = beforeCanvasRef.current;
    if (!afterVideo || !beforeVideo || !beforeCanvas || !currentTimelineClip) return undefined;
    // The hidden comparison decoder otherwise plays and paints a second copy
    // of every source during After-only review, competing with the programme.
    if (comparisonMode === "after") {
      beforeVideo.pause();
      applySafeMediaSource(beforeVideo, null);
      return undefined;
    }

    let beforeAnimationFrame = 0;
    const paintBeforeFrame = () => {
      if (renderedOutputUrl) return;
      if (beforeVideo.readyState < 2 || !beforeVideo.videoWidth || !beforeVideo.videoHeight) return;
      const context = beforeCanvas.getContext("2d", { alpha: false });
      if (!context) return;
      const canvasWidth = 960;
      const canvasHeight = 540;
      if (beforeCanvas.width !== canvasWidth) beforeCanvas.width = canvasWidth;
      if (beforeCanvas.height !== canvasHeight) beforeCanvas.height = canvasHeight;
      const scale = Math.min(
        canvasWidth / beforeVideo.videoWidth,
        canvasHeight / beforeVideo.videoHeight
      );
      const drawWidth = beforeVideo.videoWidth * scale;
      const drawHeight = beforeVideo.videoHeight * scale;
      context.fillStyle = "#000";
      context.fillRect(0, 0, canvasWidth, canvasHeight);
      context.drawImage(
        beforeVideo,
        (canvasWidth - drawWidth) / 2,
        (canvasHeight - drawHeight) / 2,
        drawWidth,
        drawHeight
      );
    };
    const paintBeforeLoop = () => {
      paintBeforeFrame();
      if (!afterVideo.paused) {
        beforeAnimationFrame = window.requestAnimationFrame(paintBeforeLoop);
      } else {
        beforeAnimationFrame = 0;
      }
    };
    const startBeforeLoop = () => {
      if (renderedOutputUrl) return;
      if (!beforeAnimationFrame) {
        beforeAnimationFrame = window.requestAnimationFrame(paintBeforeLoop);
      }
    };

    applySafeMediaSource(beforeVideo, currentTimelineClip.url);
    beforeVideo.muted = true;
    beforeVideo.defaultMuted = true;

    const syncBeforePreview = () => {
      if (afterVideo.readyState < 2) {
        beforeVideo.pause();
        return;
      }
      const finishedPosition = renderedOutputUrl
        ? resolveOutputTimelinePosition(getPreviewTimelineTime(afterVideo.currentTime)) : null;
      const targetTime = finishedPosition?.sourceTime ?? Number(afterVideo.currentTime || currentTimelineWindow.start || 0);
      if (finishedPosition && beforeVideo.src !== getSafeMediaSource(finishedPosition.clip.url))
        applySafeMediaSource(beforeVideo,finishedPosition.clip.url);
      if (
        Number.isFinite(targetTime) &&
        Math.abs(Number(beforeVideo.currentTime || 0) - targetTime) > (afterVideo.paused ? .001 : .04)
      ) {
        try {
          beforeVideo.currentTime = targetTime;
        } catch (error) {
          console.log("Before preview seek skipped", error);
        }
      }
      const programmeTick = renderedOutputUrl ? secondsToTicks(getPreviewTimelineTime(afterVideo.currentTime)) : null;
      const rateSegment = renderedOutputUrl ? previewOutputTimeMap.find(item=>programmeTick>=item.programmeRange.startTick&&programmeTick<item.programmeRange.endTick) : null;
      beforeVideo.playbackRate = rateSegment ? rateSegment.rateNumerator/rateSegment.rateDenominator : afterVideo.playbackRate || 1;
      if (afterVideo.paused) {
        beforeVideo.pause();
        paintBeforeFrame();
      } else {
        safePlayMediaElement(beforeVideo);
        startBeforeLoop();
      }
    };
    const pauseBeforePreview = () => {
      beforeVideo.pause();
      paintBeforeFrame();
    };

    afterVideo.addEventListener("play", syncBeforePreview);
    afterVideo.addEventListener("loadeddata", syncBeforePreview);
    afterVideo.addEventListener("canplay", syncBeforePreview);
    afterVideo.addEventListener("timeupdate", syncBeforePreview);
    afterVideo.addEventListener("seeking", syncBeforePreview);
    afterVideo.addEventListener("seeked", syncBeforePreview);
    afterVideo.addEventListener("ratechange", syncBeforePreview);
    afterVideo.addEventListener("pause", pauseBeforePreview);
    // A source can finish loading after the programme has already sought or
    // paused. Reconcile its clock at that point; painting alone leaves a stale
    // Before frame (which is also used by the programme canvas).
    beforeVideo.addEventListener("loadedmetadata", syncBeforePreview);
    beforeVideo.addEventListener("loadeddata", syncBeforePreview);
    beforeVideo.addEventListener("canplay", syncBeforePreview);
    beforeVideo.addEventListener("seeked", paintBeforeFrame);
    beforeVideo.addEventListener("timeupdate", paintBeforeFrame);
    syncBeforePreview();

    return () => {
      afterVideo.removeEventListener("play", syncBeforePreview);
      afterVideo.removeEventListener("loadeddata", syncBeforePreview);
      afterVideo.removeEventListener("canplay", syncBeforePreview);
      afterVideo.removeEventListener("timeupdate", syncBeforePreview);
      afterVideo.removeEventListener("seeking", syncBeforePreview);
      afterVideo.removeEventListener("seeked", syncBeforePreview);
      afterVideo.removeEventListener("ratechange", syncBeforePreview);
      afterVideo.removeEventListener("pause", pauseBeforePreview);
      beforeVideo.removeEventListener("loadedmetadata", syncBeforePreview);
      beforeVideo.removeEventListener("loadeddata", syncBeforePreview);
      beforeVideo.removeEventListener("canplay", syncBeforePreview);
      beforeVideo.removeEventListener("seeked", paintBeforeFrame);
      beforeVideo.removeEventListener("timeupdate", paintBeforeFrame);
      if (beforeAnimationFrame) window.cancelAnimationFrame(beforeAnimationFrame);
      beforeVideo.pause();
    };
  }, [activeTimelineIndex, currentTimelineClip, currentTimelineWindow.start, comparisonMode, renderedOutputUrl, previewOutputTimeMap]);

  useEffect(() => {
    const video = videoRef.current;
    const audio = audioRef.current;
    if (!audio) return;

    if (!extractedAudio?.url) {
      audio.pause();
      audio.removeAttribute("src");
      audio.load();
      return;
    }

    const syncBackgroundAudio = () => {
      if (!video || !extractedAudio?.url) return;

      const previewGain = previewMuted ? 0 : clampAudioControl(previewVolume, 0, 1, 1);
      const masterAutomationGain =
        interpolateAutomationValue({
          keyframes: audioKeyframes.masterPodcast,
          property: "volume",
          time: getPreviewTimelineTime(video.currentTime || 0),
          fallback: 100,
        }) / 100;
      audio.volume =
        clampAudioControl(extractedAudio.volume, 0, 1, 0.7) *
        previewGain *
        masterAutomationGain;
      audio.playbackRate = video.playbackRate || 1;
      // The original-audio mixer below owns the programme gain and mute.
      // Synchronizing extracted audio must not override its track controls.

      if (extractedAudio.enabled === false) {
        audio.pause();
        return;
      }

      const previewTimelineTime = getPreviewTimelineTime(video.currentTime || 0);
      const targetTime = clampAudioControl(
        previewTimelineTime + Number(extractedAudio.trimStart || 0),
        0,
        (extractedAudio.duration || previewTimelineTime + Number(extractedAudio.trimStart || 0)) +
          1,
        0
      );

      if (Number.isFinite(targetTime) && Math.abs((audio.currentTime || 0) - targetTime) > 0.35) {
        try {
          audio.currentTime = targetTime;
        } catch (error) {
          console.log("Audio sync seek skipped", error);
        }
      }

      if (video.paused) {
        audio.pause();
      } else {
        safePlayMediaElement(audio);
      }
    };

    const pauseBackgroundAudio = () => audio.pause();

    if (video) {
      video.addEventListener("play", syncBackgroundAudio);
      video.addEventListener("pause", pauseBackgroundAudio);
      video.addEventListener("seeking", syncBackgroundAudio);
      video.addEventListener("seeked", syncBackgroundAudio);
      video.addEventListener("timeupdate", syncBackgroundAudio);
      video.addEventListener("loadedmetadata", syncBackgroundAudio);
      video.addEventListener("ratechange", syncBackgroundAudio);
    }

    syncBackgroundAudio();

    return () => {
      if (video) {
        video.removeEventListener("play", syncBackgroundAudio);
        video.removeEventListener("pause", pauseBackgroundAudio);
        video.removeEventListener("seeking", syncBackgroundAudio);
        video.removeEventListener("seeked", syncBackgroundAudio);
        video.removeEventListener("timeupdate", syncBackgroundAudio);
        video.removeEventListener("loadedmetadata", syncBackgroundAudio);
        video.removeEventListener("ratechange", syncBackgroundAudio);
      }
      audio.pause();
    };
  }, [
    extractedAudio,
    activeTimelineIndex,
    timeline,
    selectedClip,
    muteOriginalAudio,
    previewMuted,
    previewVolume,
    audioKeyframes.masterPodcast,
  ]);

  // ── B-Roll overlay audio ducking ──
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    // Find active B-roll overlays at current time
    const activeBRoll = overlays.filter(o => {
      if (o.startTime === undefined || o.duration === undefined) return false;
      const audibleDuration = getOverlayAudibleDuration(o);
      return (
        previewTimelineTime >= Number(o.startTime) &&
        previewTimelineTime < Number(o.startTime) + audibleDuration
      );
    });

    const anyMuteMain = activeBRoll.some(o => o.muteMainAudio);
    const anyDucking = activeBRoll.some(o => o.audioDucking && o.useOverlayAudio);
    const duckingStrength = activeBRoll.reduce(
      (max, o) => Math.max(max, o.audioDuckingStrength || 0.35),
      0
    );

    const baseGain = previewMuted ? 0 : clampAudioControl(previewVolume, 0, 1, 1);
    const extractedMode = normalizeAudioMode(extractedAudio?.mode);
    const extractedDuckingStrength = clampAudioControl(
      extractedAudio?.duckingStrength,
      0.15,
      0.95,
      0.45
    );
    const baseMuted =
      previewMuted ||
      muteOriginalAudio ||
      (!renderedOutputUrl && (currentTimelineClip?.audioDetached || (originalAudioWindow &&
        (previewTimelineTime < originalAudioWindow.audioTimelineStart ||
          previewTimelineTime >= originalAudioWindow.audioTimelineStart + originalAudioWindow.audioTimelineDuration)))) ||
      !isStudioAudioTrackAudible(trackStates, "originalAudio") ||
      (extractedAudio?.enabled !== false && extractedAudio?.url && extractedMode === "replace");
    const restoredBaseGain =
      extractedAudio?.enabled !== false && extractedAudio?.url && extractedMode === "duck_original"
        ? clampAudioControl(1 - extractedDuckingStrength, 0.05, 1, 0.55) * baseGain
        : baseGain;
    const originalAutomationGain =
      interpolateAutomationValue({
        keyframes: audioKeyframes.originalAudio,
        property: "volume",
        time: previewTimelineTime,
        fallback: 100,
      }) / 100;

    const applyAudio = (muted, vol) => {
      if (video.muted !== muted) video.muted = muted;
      if (Math.abs(video.volume - vol) > 0.005) video.volume = vol;
    };

    if (anyMuteMain) {
      applyAudio(true, 0);
    } else if (anyDucking) {
      applyAudio(!!baseMuted, clampAudioControl(1 - duckingStrength, 0.05, 1, 0.55) * baseGain * originalAutomationGain);
    } else {
      applyAudio(!!baseMuted, baseMuted ? 0 : restoredBaseGain * originalAutomationGain);
    }
  }, [
    previewTimelineTime,
    overlays,
    previewMuted,
    previewVolume,
    muteOriginalAudio,
    extractedAudio,
    audioKeyframes.originalAudio,
    trackStates,
    originalAudioWindow,
    currentTimelineClip,
    renderedOutputUrl,
  ]);

  useEffect(() => {
    const sourceVideo = videoRef.current;
    if (!sourceVideo) return undefined;

    // Finished playback already contains these layers and their audio. Never
    // seek or start the live decoders against the completed movie's clock.
    if (renderedOutputUrl) {
      overlayMediaRefsRef.current.forEach(media => media.pause());
      return undefined;
    }

    const syncOverlayMedia = () => {
      // Paused accurate-frame sampling can leave the source decoder at its old
      // position. The native programme playhead still owns the cutaway clock.
      const outputTime = isPreviewPaused
        ? previewTimelineTime
        : getPreviewTimelineTime(sourceVideo.currentTime || 0);
      overlayMediaRefsRef.current.forEach((overlayVideo, overlayId) => {
        if (String(soloPreviewOverlayId) === String(overlayId)) return;
        if(overlayVideo.dataset.sharedProgrammeFrame==='true'){overlayVideo.pause();return;}
        const overlay = overlays.find(item => String(item.id) === String(overlayId));
        if (!overlay || overlay.type !== "video") {
          overlayVideo.pause();
          return;
        }

        const start = Number(overlay.startTime ?? overlay.start_time ?? 0);
        const configuredDuration = Math.max(0, Number(overlay.duration || 0));
        const visibleDuration = getOverlayVisibleDuration(overlay);
        const active =
          visibleDuration > 0 && outputTime >= start && outputTime < start + visibleDuration;
        if (!active) {
          overlayVideo.pause();
          return;
        }

        const mediaDuration = Number(overlayVideo.duration || overlay.sourceDuration || 0);
        const elapsed = Math.max(0, outputTime - start);
        const sourceStart = clampNumber(
          overlay.sourceStartTime,
          0,
          Math.max(0, mediaDuration - 0.05),
          0
        );
        const availableSourceDuration = Math.max(0.05, mediaDuration - sourceStart);
        const sourceEndBehavior = normalizeBRollEndBehavior(overlay.sourceEndBehavior);
        const hasReachedSourceEnd = mediaDuration > 0.1 && elapsed >= availableSourceDuration;
        const targetTime =
          mediaDuration <= 0.1
            ? elapsed
            : sourceEndBehavior === "loop"
              ? sourceStart + (elapsed % availableSourceDuration)
              : sourceStart + Math.min(elapsed, Math.max(0, availableSourceDuration - 0.04));
        overlayVideo.playbackRate = sourceVideo.playbackRate || 1;
        overlayVideo.muted =
          !overlay.useOverlayAudio ||
          previewMuted ||
          !isStudioAudioTrackAudible(trackStates, "broll") ||
          (sourceEndBehavior === "hold" && hasReachedSourceEnd);
        overlayVideo.defaultMuted = overlayVideo.muted;
        const brollAutomationGain =
          interpolateAutomationValue({
            keyframes: audioKeyframes.broll,
            property: "volume",
            time: outputTime,
            fallback: 100,
          }) / 100;
        const remainingTime = Math.max(0, visibleDuration - elapsed);
        const microFadeDur = 0.06;
        let microFade = 1.0;
        // Fade playback edges to avoid audio clicks, but keep the exact mixer
        // gain while paused so changing the master control is deterministic.
        if (!sourceVideo.paused) {
          if (elapsed < microFadeDur) {
            microFade = Math.max(0.01, elapsed / microFadeDur);
          } else if (remainingTime < microFadeDur) {
            microFade = Math.max(0.01, remainingTime / microFadeDur);
          }
        }

        overlayVideo.volume =
          clampAudioControl(overlay.overlayAudioVolume, 0, 1, 0.7) * brollAutomationGain *
          clampAudioControl(previewVolume, 0, 1, 1) * microFade;

        if (
          Number.isFinite(targetTime) &&
          Math.abs(Number(overlayVideo.currentTime || 0) - targetTime) > (isPreviewPaused ? 0.001 : 0.18)
        ) {
          try {
            overlayVideo.currentTime = targetTime;
          } catch (error) {
            console.log("B-roll preview seek skipped", error);
          }
        }

        if (sourceVideo.paused || (sourceEndBehavior === "hold" && hasReachedSourceEnd)) {
          overlayVideo.pause();
        } else {
          safePlayMediaElement(overlayVideo);
        }
      });
    };

    sourceVideo.addEventListener("play", syncOverlayMedia);
    sourceVideo.addEventListener("pause", syncOverlayMedia);
    sourceVideo.addEventListener("seeking", syncOverlayMedia);
    sourceVideo.addEventListener("seeked", syncOverlayMedia);
    sourceVideo.addEventListener("timeupdate", syncOverlayMedia);
    sourceVideo.addEventListener("ratechange", syncOverlayMedia);
    syncOverlayMedia();

    return () => {
      sourceVideo.removeEventListener("play", syncOverlayMedia);
      sourceVideo.removeEventListener("pause", syncOverlayMedia);
      sourceVideo.removeEventListener("seeking", syncOverlayMedia);
      sourceVideo.removeEventListener("seeked", syncOverlayMedia);
      sourceVideo.removeEventListener("timeupdate", syncOverlayMedia);
      sourceVideo.removeEventListener("ratechange", syncOverlayMedia);
    };
  }, [
    activeTimelineIndex,
    isPreviewPaused,
    previewTimelineTime,
    overlays,
    previewMuted,
    soloPreviewOverlayId,
    timeline,
    audioKeyframes.broll,
    previewVolume,
    trackStates,
    renderedOutputUrl,
  ]);

  useEffect(
    () => () => {
      overlayMediaRefsRef.current.forEach(media => media.pause());
    },
    []
  );

  useEffect(() => {
    applySafeMediaSource(
      watermarkCleanupPreviewImageRef.current,
      shouldShowWatermarkCleanupOnVideo ? watermarkCleanupPreview?.cleanedImageUrl : null
    );
    applySafeMediaSource(
      smartCropForegroundVideoRef.current,
      smartCrop ? currentTimelineClip?.url : null
    );
    // Unused hidden decoders must not compete with the programme's byte ranges.
    // Prepare a hook source only on its actual first occurrence and treatment.
    const prepareHook = !renderedOutputUrl && addHook && activeTimelineIndex === 0;
    applySafeMediaSource(hookBackdropVideoRef.current, prepareHook && hookBlurBackground ? currentTimelineClip?.url : null);
    applySafeMediaSource(hookFreezeVideoRef.current, prepareHook && hookFreezeFrame ? currentTimelineClip?.url : null);
    beatEchoVideoRefsRef.current.forEach(element =>
      applySafeMediaSource(element, temporalPreviewIsLive ? currentTimelineClip?.url : null)
    );
    applySafeMediaSource(audioRef.current, extractedAudio?.url);
    applySafeMediaSource(
      musicPreviewRef.current,
      !musicSearchMode ? effectiveMusicPreviewUrl : null
    );
  }, [
    currentTimelineClip,
    activeTimelineIndex,
    renderedOutputUrl,
    addHook,
    hookBlurBackground,
    hookFreezeFrame,
    effectiveMusicPreviewUrl,
    extractedAudio,
    musicSearchMode,
    shouldShowWatermarkCleanupOnVideo,
    smartCrop,
    temporalPreviewIsLive,
    watermarkCleanupPreview,
  ]);

  useEffect(() => {
    const sourceVideo = videoRef.current;
    const echoVideos = beatEchoVideoRefsRef.current.filter(Boolean);
    if (!sourceVideo || !temporalPreviewIsLive || !echoVideos.length) {
      echoVideos.forEach(video => video.pause());
      return undefined;
    }

    const delayStep =
      motionSculpturePreviewIsLive
        ? creativeIntensity === "clean"
          ? 0.1
          : creativeIntensity === "unreal"
            ? 0.18
            : 0.14
        : creativeIntensity === "clean"
          ? 0.07
          : creativeIntensity === "unreal"
            ? 0.1
            : 0.085;
    const effectStart =
      Number(currentTimelineWindow.start || 0) + Number(activeLiveCreativeEffect?.start_time || 0);

    const syncBeatEchoPreview = () => {
      echoVideos.forEach((echoVideo, index) => {
        const targetTime = Math.max(
          effectStart,
          Number(sourceVideo.currentTime || 0) - delayStep * (index + 1)
        );
        echoVideo.playbackRate = sourceVideo.playbackRate || 1;
        echoVideo.muted = true;
        echoVideo.defaultMuted = true;

        if (Math.abs(Number(echoVideo.currentTime || 0) - targetTime) > 0.045) {
          try {
            echoVideo.currentTime = targetTime;
          } catch (error) {
            console.log("Beat Echo preview seek skipped", error);
          }
        }

        if (sourceVideo.paused) {
          echoVideo.pause();
        } else {
          safePlayMediaElement(echoVideo);
        }
      });
    };

    sourceVideo.addEventListener("play", syncBeatEchoPreview);
    sourceVideo.addEventListener("pause", syncBeatEchoPreview);
    sourceVideo.addEventListener("seeking", syncBeatEchoPreview);
    sourceVideo.addEventListener("seeked", syncBeatEchoPreview);
    sourceVideo.addEventListener("timeupdate", syncBeatEchoPreview);
    sourceVideo.addEventListener("ratechange", syncBeatEchoPreview);
    syncBeatEchoPreview();

    return () => {
      sourceVideo.removeEventListener("play", syncBeatEchoPreview);
      sourceVideo.removeEventListener("pause", syncBeatEchoPreview);
      sourceVideo.removeEventListener("seeking", syncBeatEchoPreview);
      sourceVideo.removeEventListener("seeked", syncBeatEchoPreview);
      sourceVideo.removeEventListener("timeupdate", syncBeatEchoPreview);
      sourceVideo.removeEventListener("ratechange", syncBeatEchoPreview);
      echoVideos.forEach(video => video.pause());
    };
  }, [
    activeLiveCreativeEffect?.start_time,
    temporalPreviewIsLive,
    motionSculpturePreviewIsLive,
    creativeIntensity,
    currentTimelineClip,
    currentTimelineWindow.start,
  ]);

  useEffect(() => {
    const video = videoRef.current;
    const backdrop = hookBackdropVideoRef.current;
    const freezeVideo = hookFreezeVideoRef.current;
    if (!video) return;

    const absoluteHookStart = Number(currentTimelineWindow.start || 0) + resolvedHookStart;
    const absoluteHookEnd = Number(currentTimelineWindow.start || 0) + hookEnd;

    const syncHookMedia = () => {
      const isHookVisibleNow =
        addHook &&
        activeTimelineIndex === 0 &&
        normalizedHookText &&
        video.currentTime >= absoluteHookStart &&
        video.currentTime <= absoluteHookEnd + hookLeadOut;

      if (backdrop && addHook && activeTimelineIndex === 0 && hookBlurBackground && !renderedOutputUrl) {
        backdrop.playbackRate = video.playbackRate || 1;
        if (Math.abs((backdrop.currentTime || 0) - (video.currentTime || 0)) > 0.08) {
          try {
            backdrop.currentTime = video.currentTime || 0;
          } catch (error) {
            console.log("Hook backdrop seek skipped", error);
          }
        }

        if (video.paused || !isHookVisibleNow || !hookBlurBackground) {
          backdrop.pause();
        } else {
          safePlayMediaElement(backdrop);
        }
      }

      if (freezeVideo && addHook && activeTimelineIndex === 0 && hookFreezeFrame && !renderedOutputUrl) {
        const freezeTarget = Number(currentTimelineWindow.start || 0) + resolvedHookStart;
        if (Math.abs((freezeVideo.currentTime || 0) - freezeTarget) > 0.08) {
          try {
            freezeVideo.currentTime = freezeTarget;
          } catch (error) {
            console.log("Hook freeze seek skipped", error);
          }
        }
        freezeVideo.pause();
      }
    };

    video.addEventListener("play", syncHookMedia);
    video.addEventListener("pause", syncHookMedia);
    video.addEventListener("seeking", syncHookMedia);
    video.addEventListener("seeked", syncHookMedia);
    video.addEventListener("timeupdate", syncHookMedia);
    video.addEventListener("loadedmetadata", syncHookMedia);
    video.addEventListener("ratechange", syncHookMedia);

    syncHookMedia();

    return () => {
      video.removeEventListener("play", syncHookMedia);
      video.removeEventListener("pause", syncHookMedia);
      video.removeEventListener("seeking", syncHookMedia);
      video.removeEventListener("seeked", syncHookMedia);
      video.removeEventListener("timeupdate", syncHookMedia);
      video.removeEventListener("loadedmetadata", syncHookMedia);
      video.removeEventListener("ratechange", syncHookMedia);
      backdrop?.pause();
      freezeVideo?.pause();
    };
  }, [
    addHook,
    activeTimelineIndex,
    hookBlurBackground,
    hookEnd,
    hookFreezeFrame,
    renderedOutputUrl,
    hookLeadOut,
    normalizedHookText,
    resolvedHookStart,
    currentTimelineWindow.start,
    timeline,
  ]);

  useEffect(() => {
    const video = videoRef.current;
    const foreground = smartCropForegroundVideoRef.current;
    if (!video || !foreground || !smartCrop) return undefined;

    const syncSmartCropPreview = () => {
      foreground.playbackRate = video.playbackRate || 1;
      if (Math.abs((foreground.currentTime || 0) - (video.currentTime || 0)) > 0.08) {
        try {
          foreground.currentTime = video.currentTime || 0;
        } catch (error) {
          console.log("Smart crop preview seek skipped", error);
        }
      }

      if (video.paused) {
        foreground.pause();
      } else {
        safePlayMediaElement(foreground);
      }
    };

    video.addEventListener("play", syncSmartCropPreview);
    video.addEventListener("pause", syncSmartCropPreview);
    video.addEventListener("seeking", syncSmartCropPreview);
    video.addEventListener("seeked", syncSmartCropPreview);
    video.addEventListener("timeupdate", syncSmartCropPreview);
    video.addEventListener("loadedmetadata", syncSmartCropPreview);
    video.addEventListener("ratechange", syncSmartCropPreview);

    syncSmartCropPreview();

    return () => {
      video.removeEventListener("play", syncSmartCropPreview);
      video.removeEventListener("pause", syncSmartCropPreview);
      video.removeEventListener("seeking", syncSmartCropPreview);
      video.removeEventListener("seeked", syncSmartCropPreview);
      video.removeEventListener("timeupdate", syncSmartCropPreview);
      video.removeEventListener("loadedmetadata", syncSmartCropPreview);
      video.removeEventListener("ratechange", syncSmartCropPreview);
      foreground.pause();
    };
  }, [smartCrop, activeTimelineIndex, timeline, currentTimelineClip]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return undefined;

    const syncPlaybackState = () => {
      if (!previewPlaybackIntentRef.current) {
        // A late play event from a replaced source must not undo Pause.
        video.pause();
        setIsPreviewPaused(true);
        return;
      }
      if (!renderedOutputUrl) {
        // Native pause/ended events also occur between sequence sources.
        // The control represents the creator's intention during that gap.
        setIsPreviewPaused(false);
        return;
      }
      setIsPreviewPaused(video.paused);
    };
    const returnToEditedMoment = () => {
      // The sequence boundary handler owns source completion and its held
      // final frame. Its Pause can precede the queued native ended event.
      if (!renderedOutputUrl) { syncPlaybackState(); return; }
      if (renderedOutputUrl) {
        previewPlaybackIntentRef.current = false;
        setIsPreviewPaused(true);
      }
      syncPlaybackState();
    };

    video.addEventListener("play", syncPlaybackState);
    video.addEventListener("pause", syncPlaybackState);
    video.addEventListener("ended", returnToEditedMoment);
    syncPlaybackState();

    return () => {
      video.removeEventListener("play", syncPlaybackState);
      video.removeEventListener("pause", syncPlaybackState);
      video.removeEventListener("ended", returnToEditedMoment);
    };
  }, [
    activeTimelineIndex,
    timeline,
    comparisonMode,
    studioInspectorTab,
    activeOverlayId,
    addHook,
    resolvedHookStart,
    hookDuration,
    renderedOutputUrl,
  ]);

  useEffect(() => {
    if (!isPreviewFullscreen) return undefined;
    const handleExpandedPreviewKey = event => {
      if (event.key !== "Escape") return;
      setIsPreviewFullscreen(false);
      setStudioActionMessage("Expanded preview closed. All studio controls are available again.");
    };
    window.addEventListener("keydown", handleExpandedPreviewKey);
    return () => window.removeEventListener("keydown", handleExpandedPreviewKey);
  }, [isPreviewFullscreen]);

  useLayoutEffect(() => {
    const video = videoRef.current;
    const music = musicPreviewRef.current;
    if (!music && !musicSearchMode) return;

    if (!addMusic || !effectiveMusicPreviewUrl || renderedOutputUrl) {
      stopMusicPreviewBufferPlayback();
      music?.pause();
      return;
    }

    let buffering = video?.readyState < 3;
    const syncMusicPreview = () => {
      if (!video || video !== videoRef.current || !effectiveMusicPreviewUrl) return;

      if (backgroundSoundPreviewSuppressedRef.current) {
        stopMusicPreviewBufferPlayback();
        music?.pause();
        return;
      }

      if (!isolatedMusicPreviewIntentRef.current && musicPreviewBufferRef.current &&
          programmeMusicRouteRef.current.decoded && programmeMusicRouteRef.current.sourceUrl === effectiveMusicPreviewUrl &&
          decodedMusicPreviewUrl === effectiveMusicPreviewUrl &&
          musicPreviewBufferUrlRef.current === effectiveMusicPreviewUrl) {
        music?.pause(); // The same selected song has one programme audio path.
        const previewTimelineTime = clampAudioControl(
          getPreviewTimelineTime(video.currentTime || 0),
          0,
          36000,
          0
        );
        const clip = timeline[activeTimelineIndex];
        const window = getTimelineClipWindow(clip);
        const musicTime = getPreviewMusicTime(previewTimelineTime);
        if (video.paused || video.seeking || buffering || renderedOutputUrl ||
            previewTimelineTime >= outputTimelineDuration ||
            !isStudioSourceClockCurrent(video, { activeVideo: videoRef.current, url: clip?.url,
              start: window.start, end: window.end, hasNext: activeTimelineIndex < timeline.length - 1 }) ||
            (musicTrack?.loop === false && musicTime >= musicPreviewBufferRef.current.duration)) {
          stopMusicPreviewBufferPlayback();
          return;
        }
        if (musicPreviewSourceRef.current) musicPreviewSourceRef.current.loop = musicTrack?.loop !== false;
        syncMusicPreviewGain(previewTimelineTime);
        // During synchronous promotion, the prepared decoder's play request
        // has started but its first advancing media-clock sample may still be
        // queued behind the editor commit. Keep the matching running song for
        // this one transition sample. Ordinary playing/frame events below
        // still enforce the same drift limit, and waiting/seek/Pause stop it.
        const preparedPromotion = preparedCutHandoffRef.current?.to === video &&
          musicPreviewSourceStateRef.current?.sourceKey === musicPreviewBufferUrlRef.current;
        const needsResync =
          !musicPreviewSourceRef.current ||
          (!preparedPromotion && musicBufferNeedsResync(musicPreviewSourceStateRef.current, {
            contextTime: musicPreviewAudioContextRef.current.currentTime,
            programmeTime: musicTime, playbackRate: 1,
            sourceKey: musicPreviewBufferUrlRef.current,
          }));

        if (needsResync) {
          startMusicPreviewBufferPlayback(musicTime, 1).catch(error => {
            console.log("Music preview buffer play prevented", error);
            setMusicPreviewNeedsGesture(true);
            setMusicPreviewStatus("failed");
            setMusicPreviewStatusMessage(
              error?.message || "Preview audio is ready but playback was blocked by the browser."
            );
          });
        }
        return;
      }

      const previewGain = previewMuted || !isStudioAudioTrackAudible(trackStates, "music")
        ? 0 : clampAudioControl(previewVolume, 0, 1, 1);
      const previewTimelineTime = clampAudioControl(
        getPreviewTimelineTime(video.currentTime || 0),
        0,
        36000,
        0
      );
      const fadeGain = studioMusicFadeGain(
        getPreviewMusicTime(previewTimelineTime),
        outputPlaybackDuration,
        musicTrack?.fadeIn,
        musicTrack?.fadeOut
      );
      const automationGain = interpolateAutomationValue({ keyframes: audioKeyframes.music,
        property: "volume", time: previewTimelineTime, fallback: 100 }) / 100;
      music.muted = false;
      music.defaultMuted = false;
      music.loop = musicTrack?.loop !== false;
      // HTML media volume is limited to unity. The decoded-buffer GainNode
      // retains the full automation gain; its fallback element must not throw
      // when a valid 0–200% automation key raises the combined gain above one.
      music.volume = clampAudioControl(
        clampAudioControl(musicTrack?.volume ?? musicVolume, 0, 0.6, 0.15) *
        previewGain *
        fadeGain * automationGain * constantMusicDuckGain(musicTrack), 0, 1, 0);
      music.playbackRate = 1;
      music.preservesPitch = true;

      const musicDuration = Number(music.duration || 0);
      const targetTime =
        Number.isFinite(musicDuration) && musicDuration > 0.25
          ? musicTrack?.loop !== false
            ? getPreviewMusicTime(previewTimelineTime) % musicDuration
            : Math.min(getPreviewMusicTime(previewTimelineTime), Math.max(0, musicDuration - 0.05))
          : getPreviewMusicTime(previewTimelineTime);
      if (Number.isFinite(targetTime) && Math.abs((music.currentTime || 0) - targetTime) > .35) {
        try {
          music.currentTime = targetTime;
        } catch (error) {
          console.log("Music preview seek skipped", error);
        }
      }

      if (video.paused) {
        music.pause();
      } else {
        try {
          const playResult = music.paused ? music.play() : null;
          if (playResult && typeof playResult.catch === "function") {
            playResult.catch(error => {
              console.log("Music preview play prevented", error);
              setMusicPreviewNeedsGesture(true);
              setMusicPreviewStatus("failed");
              setMusicPreviewStatusMessage(
                error?.message || "Preview audio is ready but playback was blocked by the browser."
              );
            });
          }
        } catch (error) {
          console.log("Music preview play prevented", error);
          setMusicPreviewNeedsGesture(true);
          setMusicPreviewStatus("failed");
          setMusicPreviewStatusMessage(
            error?.message || "Preview audio is ready but playback was blocked by the browser."
          );
        }
      }
    };

    const pauseMusicPreview = () => {
      // A prepared hard cut is a picture handoff, not a programme pause.
      // Keep the same decoded bed through its synchronous commit. Waiting,
      // seeking, operator Pause and an unprepared next source still stop it.
      const handoff = preparedCutHandoffRef.current;
      if (previewPlaybackIntentRef.current && handoff?.from === video) return;
      stopMusicPreviewBufferPlayback();
      music?.pause();
    };
    const waitForSource = () => { buffering = true; pauseMusicPreview(); };
    const sourceReady = () => { buffering = false; syncMusicPreview(); };

    if (video) {
      video.addEventListener("play", syncMusicPreview);
      video.addEventListener("playing", sourceReady);
      video.addEventListener("canplay", sourceReady);
      video.addEventListener("waiting", waitForSource);
      video.addEventListener("pause", pauseMusicPreview);
      video.addEventListener("seeking", syncMusicPreview);
      video.addEventListener("seeked", syncMusicPreview);
      video.addEventListener("timeupdate", syncMusicPreview);
      video.addEventListener("loadedmetadata", syncMusicPreview);
      video.addEventListener("ratechange", syncMusicPreview);
    }

    // Speech ducking already updates its gain per animation frame. Ordinary
    // music fades/automation need the decoded frame clock, not 4 Hz timeupdate.
    const stopMusicFrameClock = video && !renderedOutputUrl
      ? watchStudioAudioClock(video, () => {
          const decodedProgramme = !isolatedMusicPreviewIntentRef.current &&
            programmeMusicRouteRef.current.decoded && programmeMusicRouteRef.current.sourceUrl === effectiveMusicPreviewUrl &&
            musicPreviewBufferRef.current && decodedMusicPreviewUrl === effectiveMusicPreviewUrl;
          if (!decodedProgramme && (musicSearchMode || (musicTrack?.ducking && musicTrack.duckingMode === "speech"))) return;
          const clip = timeline[activeTimelineIndex];
          const window = getTimelineClipWindow(clip);
          if (isStudioSourceClockCurrent(video, { activeVideo: videoRef.current, url: clip?.url,
            start: window.start, end: window.end, hasNext: activeTimelineIndex < timeline.length - 1 })) {
            syncMusicPreview();
          }
        }) : () => {};
    syncMusicPreview();

    return () => {
      stopMusicFrameClock();
      if (video) {
        video.removeEventListener("play", syncMusicPreview);
        video.removeEventListener("playing", sourceReady);
        video.removeEventListener("canplay", sourceReady);
        video.removeEventListener("waiting", waitForSource);
        video.removeEventListener("pause", pauseMusicPreview);
        video.removeEventListener("seeking", syncMusicPreview);
        video.removeEventListener("seeked", syncMusicPreview);
        video.removeEventListener("timeupdate", syncMusicPreview);
        video.removeEventListener("loadedmetadata", syncMusicPreview);
        video.removeEventListener("ratechange", syncMusicPreview);
      }
      const preparedCut = previewPlaybackIntentRef.current && preparedCutHandoffRef.current?.from === video;
      const samePlayingSource = previewPlaybackIntentRef.current && video === videoRef.current &&
        !video?.paused && !video?.seeking;
      // Fader/fade/bus edits replace listeners, not the running song. The new
      // listener still validates source identity/readiness and the 80ms clock
      // guard; Pause, seeking, replacement and unmount retain their stop paths.
      if (!preparedCut && !samePlayingSource) {
        stopMusicPreviewBufferPlayback();
      }
      music?.pause();
    };
  }, [
    addMusic,
    effectiveMusicPreviewUrl,
    musicSearchMode,
    musicVolume,
    musicTrack?.fadeIn,
    musicTrack?.fadeOut,
    musicTrack?.loop,
    outputTimelineDuration,
    previewMuted,
    previewVolume,
    activeTimelineIndex,
    timeline,
    selectedClip,
    renderedOutputUrl,
    trackStates,
    audioKeyframes.music,
    decodedMusicPreviewUrl,
    previewOutputTimeMap,
    outputPlaybackDuration,
  ]);

  // Keep the SFX lane locked to the edited output timeline during After/Split playback.
  useLayoutEffect(() => {
    const video = videoRef.current;
    if (!video || renderedOutputUrl) return undefined;

    const pauseTimelineEffects = () => {
      if (previewPlaybackIntentRef.current && preparedCutHandoffRef.current?.from === video) return;
      soundEffectAudioRefsRef.current.forEach(audio => audio?.pause());
      stopSynthesizedSoundEffects();
      triggeredSoundEffectsRef.current.clear();
    };

    const syncSoundEffects = () => {
      if (video !== videoRef.current) return;
      const clip = timeline[activeTimelineIndex];
      const window = getTimelineClipWindow(clip);
      // An outgoing decoder can present a frame past its trim before React
      // promotes the prepared next decoder. Do not start next-shot cues early.
      if (!isStudioSourceClockCurrent(video, { activeVideo: videoRef.current, url: clip?.url,
        start: window.start, end: window.end, hasNext: activeTimelineIndex < timeline.length - 1 })) return;
      const outputTime = clampNumber(
        getSoundEffectProgrammeTime(video.currentTime || 0),
        0,
        outputTimelineDuration,
        0
      );
      const previewGain = previewMuted ? 0 : clampAudioControl(previewVolume, 0, 1, 1);
      const sfxAutomationGain =
        interpolateAutomationValue({
          keyframes: audioKeyframes.sfx,
          property: "volume",
          time: outputTime,
          fallback: 100,
        }) / 100;

      soundEffects.forEach(effect => {
        const start = Number(effect.startTime || 0);
        const duration = getSoundEffectDuration(effect);
        const elapsed = outputTime - start;
        const route = programmeSoundEffectRoutesRef.current.get(effect.id);
        const decoded = route?.key === studioCueSourceKey(activeProjectIdRef.current, effect) ? route : null;
        const nearbyOnset = (effect.builtIn || decoded) && elapsed < 0 && studioCueStartPlan({
          contextTime: soundEffectAudioContextRef.current?.currentTime || 0,
          programmeTime: outputTime, cueStart: start, duration,
          outputTimeMap: soundEffectGainTimeMapRef.current,
        });
        const isActive =
          isStudioAudioTrackAudible(trackStates, "sfx") &&
          effect.enabled !== false && (elapsed >= 0 || nearbyOnset) && elapsed < duration;
        const audio = soundEffectAudioRefsRef.current.get(effect.id);

        if (!isActive || video.paused || video.seeking || video.readyState < 2) {
          audio?.pause();
          if (!isActive) triggeredSoundEffectsRef.current.delete(effect.id);
          return;
        }

        const fadeGain = getAudioFadeGain(elapsed, duration, effect.fadeIn, effect.fadeOut);
        if (effect.builtIn || decoded) {
          audio?.pause(); // One route per cue; never double-play the HTML decoder.
          const playing = soundEffectTimelineGainsRef.current.get(effect.id);
          if (playing) {
            const timeMap = soundEffectGainTimeMapRef.current;
            if (playing.scheduledGain && playing.outputTimeMap !== timeMap) {
              playing.scheduledGain = scheduleStudioCueGain({
                param: playing.gain.gain, contextTime: soundEffectAudioContextRef.current.currentTime,
                programmeStart: outputTime, programmeEnd: start + duration,
                outputTimeMap: timeMap, keyframes: audioKeyframes.sfx,
                baseGain: clampAudioControl(effect.volume, 0, 1, 0.8) * previewGain,
              });
              playing.outputTimeMap = timeMap;
              playing.scheduledRate = scheduleStudioCueRate({ param: playing.source.playbackRate,
                contextTime: soundEffectAudioContextRef.current.currentTime, programmeStart: outputTime,
                programmeEnd: start + duration, outputTimeMap: timeMap });
              if (!playing.scheduledGain) playing.gain.gain.cancelScheduledValues?.(
                soundEffectAudioContextRef.current.currentTime);
            }
            // The cue's own fades are already embedded in its shared PCM.
            // Apply only the live bus/fader gain, once, on each decoded frame.
            if (!playing.scheduledGain) {
              playing.gain.gain.value = clampAudioControl(effect.volume, 0, 1, 0.8) *
                previewGain * sfxAutomationGain;
            }
            if (!playing.scheduledRate) playing.source.playbackRate.setValueAtTime(video.playbackRate || 1,
              soundEffectAudioContextRef.current?.currentTime || 0);
          }
          if (!triggeredSoundEffectsRef.current.has(effect.id)) {
            let cueBuffer = null;
            if (decoded) {
              const key = JSON.stringify([effect.trimStart, duration, effect.fadeIn, effect.fadeOut]);
              cueBuffer = decoded.cues.get(key);
              if (!cueBuffer) {
                cueBuffer = createStudioCueBuffer(ensureSoundEffectAudioContext(), decoded.decoded, effect);
                if (!cueBuffer) return;
                if (decoded.cues.size >= 8) decoded.cues.clear();
                decoded.cues.set(key, cueBuffer);
              }
            }
            triggeredSoundEffectsRef.current.add(effect.id);
            void playBuiltInSoundEffect(
              effect,
              elapsed,
              video.playbackRate || 1,
              true,
              cueBuffer
            ).then(started => {
              if (!started) triggeredSoundEffectsRef.current.delete(effect.id);
            }).catch(error => {
              triggeredSoundEffectsRef.current.delete(effect.id);
              console.log("Sound effect playback prevented", error);
            });
          }
          return;
        }

        if (!audio || !effect.url) return;
        const targetTime = Number(effect.trimStart || 0) + elapsed;
        audio.volume =
          clampAudioControl(effect.volume, 0, 1, 0.8) *
          previewGain *
          fadeGain *
          sfxAutomationGain;
        audio.playbackRate = video.playbackRate || 1;
        // Export resamples SFX against programme time; recorded voiceover
        // uses pitch-preserving time stretch instead.
        audio.preservesPitch = effect.kind === "voiceover";
        // A paused cue is starting a new source-clock anchor (including a shot
        // boundary). The continuous-play drift allowance must not retain its
        // old offset on resume.
        if (Math.abs(Number(audio.currentTime || 0) - targetTime) > (audio.paused ? .001 : .18)) {
          try {
            audio.currentTime = targetTime;
          } catch (error) {
            console.log("Sound effect seek skipped", error);
          }
        }
        safePlayMediaElement(audio);
      });
    };

    const restartTimelineEffects = () => {
      pauseTimelineEffects();
      syncSoundEffects();
    };
    const reanchorTimelineEffects = () => {
      if (video !== videoRef.current || video.paused || video.seeking ||
          isolatedMusicPreviewIntentRef.current) return;
      // Cancel pending starts and resume audible cues from the new anchor,
      // including imported buffers. Keep their original PCM/fades/envelopes.
      soundEffectAudioRefsRef.current.forEach(audio => audio?.pause());
      stopSynthesizedSoundEffects(musicPreviewSourceStateRef.current?.startedAt);
      triggeredSoundEffectsRef.current.clear();
      syncSoundEffects();
    };
    video.addEventListener("studio-audio-clock-resync", reanchorTimelineEffects);
    video.addEventListener("play", syncSoundEffects);
    video.addEventListener("timeupdate", syncSoundEffects);
    video.addEventListener("seeking", pauseTimelineEffects);
    video.addEventListener("waiting", pauseTimelineEffects);
    video.addEventListener("playing", syncSoundEffects);
    video.addEventListener("seeked", restartTimelineEffects);
    video.addEventListener("ratechange", syncSoundEffects);
    video.addEventListener("pause", pauseTimelineEffects);
    video.addEventListener("ended", pauseTimelineEffects);
    const stopFrameClock = watchStudioAudioClock(video, syncSoundEffects);
    syncSoundEffects();

    return () => {
      stopFrameClock();
      video.removeEventListener("studio-audio-clock-resync", reanchorTimelineEffects);
      video.removeEventListener("play", syncSoundEffects);
      video.removeEventListener("timeupdate", syncSoundEffects);
      video.removeEventListener("seeking", pauseTimelineEffects);
      video.removeEventListener("waiting", pauseTimelineEffects);
      video.removeEventListener("playing", syncSoundEffects);
      video.removeEventListener("seeked", restartTimelineEffects);
      video.removeEventListener("ratechange", syncSoundEffects);
      video.removeEventListener("pause", pauseTimelineEffects);
      video.removeEventListener("ended", pauseTimelineEffects);
      pauseTimelineEffects();
    };
  }, [
    soundEffects,
    outputTimelineDuration,
    previewMuted,
    previewVolume,
    renderedOutputUrl,
    activeTimelineIndex,
    timeline,
    audioKeyframes.sfx,
    trackStates,
  ]);

  // ── Speech-aware auto-ducking for music preview ──
  useEffect(() => {
    const music = musicPreviewRef.current;
    if (!music || !addMusic || !effectiveMusicPreviewUrl) return;
    if (!musicTrack?.ducking || musicTrack.duckingMode !== "speech") {
      // Reset volume to track setting when not in speech mode
      const baseGain = previewMuted || !isStudioAudioTrackAudible(trackStates, "music")
        ? 0 : clampAudioControl(previewVolume, 0, 1, 1);
      const previewTimelineTime = clampAudioControl(
        getPreviewTimelineTime(videoRef.current?.currentTime || 0),
        0,
        36000,
        0
      );
      const fadeGain = studioMusicFadeGain(
        getPreviewMusicTime(previewTimelineTime),
        outputPlaybackDuration,
        musicTrack?.fadeIn,
        musicTrack?.fadeOut
      );
      const automationGain =
        interpolateAutomationValue({
          keyframes: audioKeyframes.music,
          property: "volume",
          time: previewTimelineTime,
          fallback: 100,
        }) / 100;
      music.volume = clampAudioControl(
        clampAudioControl(musicTrack?.volume ?? musicVolume, 0, 0.6, 0.15) *
        baseGain *
        fadeGain *
        automationGain * constantMusicDuckGain(musicTrack), 0, 1, 0);
      return;
    }

    const analyser = startSpeechDetection();
    if (!analyser && !voiceovers.length) return;

    let lastDuckGain = 1;
    const duckingStrength = musicTrack.duckingStrength ?? 0.4;
    const baseVolume = clampAudioControl(musicTrack.volume ?? musicVolume, 0, 0.6, 0.15);
    const baseGain = previewMuted || !isStudioAudioTrackAudible(trackStates, "music")
      ? 0 : clampAudioControl(previewVolume, 0, 1, 1);
    const maxMusicVol = baseVolume * baseGain;

    const duckLoop = () => {
      if (preparedSpeechDuckRef.current) {
        // The verified post-retime voice bus supplies the final compressor's
        // sample-clock gain. Do not overwrite it with the legacy FFT estimate.
        syncMusicPreviewGain();
        speechDetectionRafRef.current = requestAnimationFrame(duckLoop);
        return;
      }
      if (speechAnalyserRef.current?.media !== videoRef.current) startSpeechDetection();
      const speechEnergy = getSpeechEnergyLevel();
      const previewTimelineTime = clampAudioControl(
        getPreviewTimelineTime(videoRef.current?.currentTime || 0),
        0,
        36000,
        0
      );
      const fadeGain = studioMusicFadeGain(
        musicPreviewSourceRef.current && musicPreviewSourceStateRef.current
          ? musicBufferPosition(musicPreviewSourceStateRef.current, musicPreviewAudioContextRef.current.currentTime)
          : getPreviewMusicTime(previewTimelineTime),
        outputPlaybackDuration,
        musicTrack?.fadeIn,
        musicTrack?.fadeOut
      );
      const automationGain =
        interpolateAutomationValue({
          keyframes: audioKeyframes.music,
          property: "volume",
          time: previewTimelineTime,
          fallback: 100,
        }) / 100;
      // Smooth transition: target volume inversely proportional to speech energy
      // Smooth only speech detection. Smoothing the whole mix delays faders,
      // automation and the output-clock fade, even when no speech is present.
      const targetDuckGain = 1 - speechEnergy * duckingStrength;
      const smoothedDuckGain = lastDuckGain + (targetDuckGain - lastDuckGain) * 0.08;
      music.volume = clampAudioControl(maxMusicVol * fadeGain * automationGain * smoothedDuckGain, 0, 0.6, 0);
      if (musicPreviewSourceRef.current && musicPreviewDuckGainNodeRef.current) {
        // Keep the scheduled sample-clock fade independent of frame-driven
        // speech attenuation. Updating one parameter must not cancel the other.
        musicPreviewDuckGainNodeRef.current.gain.value = smoothedDuckGain;
        syncMusicPreviewGain(previewTimelineTime);
      }
      lastDuckGain = smoothedDuckGain;
      speechDetectionRafRef.current = requestAnimationFrame(duckLoop);
    };

    duckLoop();

    return () => {
      if (speechDetectionRafRef.current) {
        cancelAnimationFrame(speechDetectionRafRef.current);
      }
    };
  }, [
    addMusic,
    effectiveMusicPreviewUrl,
    musicTrack?.ducking,
    musicTrack?.duckingMode,
    musicTrack?.volume,
    musicTrack?.duckingStrength,
    musicTrack?.fadeIn,
    musicTrack?.fadeOut,
    outputTimelineDuration,
    outputPlaybackDuration,
    musicVolume,
    previewMuted,
    previewVolume,
    audioKeyframes.music,
    muteOriginalAudio,
    trackStates,
    voiceovers,
  ]);

  const addTextOverlay = (preset = "headline", content = "") => {
    // Legacy toolbar buttons used to pass the React click event directly into
    // this function. Never retain that circular event object in project/history
    // state; non-string callers mean the default headline preset.
    const resolvedPreset = typeof preset === "string" ? preset : "headline";
    // START TIME: Use current video playback time
    // If paused, it's exact. If playing, it's roughly "now".
    const currentVideoTime = videoRef.current ? videoRef.current.currentTime : 0;

    // Adjust relative to the CLIP if we are in a multi-clip timeline?
    // For now, let's assume global timeline time or clip-relative.
    // The backend expects relative to the *output video* start (0.0).
    // If we are editing a single clip, 0.0 is the start of that clip.
    // If the user scrubbed to 5.0s, we want the text to appear at 5.0s.

    // However, if we trim the video (start=10, end=20), the backend trims FIRST.
    // So 0.0 in the output is 10.0 in the source.
    // We need to calculate the relative start time.
    const relativeStartTime = getPreviewTimelineTime(currentVideoTime);

    const isLowerThird = resolvedPreset === "lower_third";
    const isLabel = resolvedPreset === "label";
    const newOverlay = {
      id: createSecureId("title"),
      type: "text",
      text: content || (isLowerThird ? "Speaker name\nRole or context" : isLabel ? "NEW" : "Your headline"),
      // Headlines start in the upper-right story-safe area so a newly added
      // title does not cover the centred/left speaker before it is adjusted.
      x: isLowerThird || isLabel ? 50 : 72,
      y: isLowerThird ? 81 : 18,
      color: "#ffffff",
      bg: isLowerThird ? "rgba(7,10,20,0.88)" : isLabel ? "#7c3aed" : "transparent",
      scale: isLowerThird ? 0.82 : isLabel ? 0.68 : 1.15,
      isRainbow: false,
      titlePreset: resolvedPreset,
      textAlign: "center",
      fontWeight: 900,
      fontFamily: "Inter",
      width: isLowerThird || isLabel ? 90 : 45,
      strokeWidth: resolvedPreset === "headline" ? 2 : 0,
      shadow: resolvedPreset === "headline" ? "strong" : "soft",
      rotation: 0,
      startTime: relativeStartTime,
      duration: 3.0, // Default 3 seconds duration
      clipId: timeline[activeTimelineIndex]?.id || "main",
      // B-roll text overlay fields
      bRollMode: undefined,
      animation: { enter: "fade", exit: "fade", enterDuration: 0.3, exitDuration: 0.3 },
      opacity: 1.0,
      coverMainVideo: false,
      muteMainAudio: false,
      useOverlayAudio: false,
      mixAudio: false,
      overlayAudioVolume: 0.7,
      audioDucking: false,
      audioDuckingStrength: 0.35,
    };
    setOverlays(current => [...current, newOverlay]);
    setActiveOverlayId(newOverlay.id);
    setStudioInspectorTab("titles");
    setActiveCreativeTool("titles");
    setComparisonMode("after");
    setStudioActionMessage(
      `${isLowerThird ? "Lower third" : isLabel ? "Label" : "Headline"} added at ${relativeStartTime.toFixed(2)}s. Edit the copy and styling before render.`
    );
  };

  const applyCreatorMotionPreset = (preset, zoom = creatorPreview.zoom) => {
    if (!allowTimelineTimingEdit('video')) return;
    const points = buildCreatorMotionRecipe({ preset, zoom, time: previewTimelineTime, duration: outputTimelineDuration });
    if (!points.length) {
      setStudioActionMessage("Move the playhead earlier: a zoom needs at least 0.1 seconds of picture.");
      return;
    }
    setMotionKeyframes(current => mergeCreatorRecipe({ keyframes: current, points,
      targetId: "main-video", property: "scale", fallback: mainTransform.scale ?? 1,
      createId: () => createSecureId("creator-zoom") }));
    setCreatorPreview(current => ({ ...current, activeDemo: "" }));
    setMotionTargetId("main-video");
    setTimelineDockExpanded(true);
    setComparisonMode("after");
    setStudioActionMessage(`${preset.replace(/_/g, " ")} added at ${points[0].time.toFixed(2)}s. Three editable scale keys; the shot returns to its original framing.`);
  };

  const applyCreatorSpeedRamp = () => {
    if (!allowTimelineTimingEdit('video')) return;
    const points = buildCreatorSpeedRecipe({ time: previewTimelineTime, duration: outputTimelineDuration });
    if (!points.length) {
      setStudioActionMessage("Move the playhead earlier: a speed ramp needs at least 0.3 seconds of picture.");
      return;
    }
    setSpeedKeyframes(current => mergeCreatorRecipe({ keyframes: current, points,
      targetId: "main", property: "speed", fallback: previewSpeed,
      createId: () => createSecureId("creator-speed") }));
    setTimelineDockExpanded(true);
    setStudioActionMessage("Speed ramp added around the playhead. Existing speed keys outside this range are preserved.");
    setComparisonMode("after");
  };

  const commitCreatorProjectEdit = (changes, label) => {
    try {
      const baseline=getEditorSnapshot(),next={...baseline,...changes,
        creatorPreview:{...creatorPreview,activeDemo:''}};
      if(!baseline.studioDocument)throw Error('Wait for source timing before adding the effect.');
      const document=reconcileStudioDocument({snapshot:next,projectId,storedDocument:baseline.studioDocument});
      const saved=cloneSnapshot(baseline);
      pendingHistoryBaselineRef.current=saved;
      cutHistoryTransactionRef.current={baseline:saved,appliedSignature:null,appliedTimelineSignature:null};
      applyLinkedCommandSnapshot({snapshot:next,document});
      setCreatorPreview(next.creatorPreview);
      const position=getCutPlayheadPosition();if(position)continueCutInSourcePreview(position);
      setStudioActionMessage(`${label} added to the editable timeline. Undo restores the complete edit.`);
    }catch(error){setStudioActionMessage(error.message);}
  };
  const applyCreatorEffect = effect => {
    const time=previewTimelineTime,remaining=outputTimelineDuration-time;
    if(remaining<.05){setStudioActionMessage('Move the playhead before the end to add an effect.');return;}
    if(effect==='shake'){
      if(!allowTimelineTimingEdit('video'))return;
      const points=buildCreatorShakeRecipe({time,duration:outputTimelineDuration,intensity:creatorPreview.intensity});
      let keys=motionKeyframes;
      for(const [property,fallback,amount] of [['x',mainTransform.x??50,1],['y',mainTransform.y??50,-.6],['rotation',mainTransform.rotation??0,.3]])
        keys=mergeCreatorRecipe({keyframes:keys,points:points.map(point=>({...point,value:Number(fallback)+point.value*amount})),
          targetId:'main-video',property,fallback,createId:()=>createSecureId('creator-shake')});
      commitCreatorProjectEdit({motionKeyframes:keys},'Impact shake');return;
    }
    if(effect==='transition'){
      if(!allowTimelineTimingEdit('video')||!allowTimelineTimingEdit('originalAudio'))return;
      if(timeline.length<2){setStudioActionMessage('Split or add a second clip, then apply a transition to their cut.');return;}
      const position=getCutPlayheadPosition(),left=Math.min(position?.index??activeTimelineIndex,timeline.length-2);
      const transition=['clean_cut','soft_dip','energy_flash'].includes(creatorPreview.transition)?creatorPreview.transition:'soft_dip';
      const clips=timeline.map((clip,index)=>index===left?{...clip,transitionOut:transition,transitionDuration:.18}
        :index===left+1?{...clip,transitionIn:transition,transitionDuration:.18}:clip);
      commitCreatorProjectEdit({timeline:clips},'Cut transition');return;
    }
    const freeze=['micro_freeze','reaction_punch'].includes(effect);
    if(!allowTimelineTimingEdit(freeze?'broll':'graphics'))return;
    const canvas=document.createElement('canvas');
    try {
      const hold=Math.min(remaining,Math.max(.1,Math.min(2,Number(creatorPreview.duration)||.8)));
      if(freeze){
        const video=videoRef.current;
        if(renderedOutputUrl||!video||video.readyState<2||video.seeking||!video.videoWidth)
          throw Error('Pause on a loaded source frame before adding a freeze.');
        pauseSynchronizedPreview();
        canvas.width=Math.min(1280,video.videoWidth);canvas.height=Math.max(2,Math.round(canvas.width*video.videoHeight/video.videoWidth));
        canvas.getContext('2d').drawImage(video,0,0,canvas.width,canvas.height);
      }else if(effect==='emoji'){
        if(!String(creatorPreview.emoji||'').trim())throw Error('Enter an emoji before adding it.');
        canvas.width=512;canvas.height=512;const context=canvas.getContext('2d');
        context.font='380px "Noto Color Emoji", "Apple Color Emoji", sans-serif';context.textAlign='center';context.textBaseline='middle';
        context.fillText(creatorPreview.emoji,256,272,480);
      }else throw Error('Choose a supported timeline effect.');
      const id=createSecureId('creator-effect');
      const overlay={id,type:'image',name:freeze?'Micro-freeze':'Reaction emoji',src:canvas.toDataURL('image/png'),
        startTime:time,duration:freeze?hold:Math.min(remaining,2),x:freeze?50:Number(creatorPreview.x),y:freeze?50:Number(creatorPreview.y),
        width:freeze?100:18,height:freeze?100:18*previewAspectRatio,opacity:1,scale:1,rotation:0,
        frameShape:'edge',borderRadius:0,
        ...(freeze?{bRollMode:'fullscreen',coverMainVideo:true,objectFit:'contain',muteMainAudio:false}:{}),
        animation:{enter:'none',exit:'none',enterDuration:0,exitDuration:0}};
      const changes={overlays:[...overlays,overlay],
        ...(compositionOrder?{compositionOrder:[...compositionOrder,id]}:{})};
      if(effect==='reaction_punch')changes.motionKeyframes=[...motionKeyframes,
        {id:createSecureId('freeze-punch'),targetId:id,property:'scale',time,value:1,easing:'linear'},
        {id:createSecureId('freeze-punch'),targetId:id,property:'scale',time:time+hold*.35,value:Number(creatorPreview.zoom)||1.34,easing:'ease_out'},
        {id:createSecureId('freeze-punch'),targetId:id,property:'scale',time:time+hold,value:1,easing:'ease_out'}];
      commitCreatorProjectEdit(changes,freeze?'Freeze picture':'Reaction emoji');setActiveOverlayId(id);
    }catch(error){setStudioActionMessage(error.message);}
  };

  const addCreatorImpactSound = () => {
    if (!allowTimelineTimingEdit('sfx')) return;
    const impact = SOUND_EFFECT_PRESETS.find(preset => preset.id === "impact");
    if (impact) addSoundEffectPreset(impact);
  };

  const applyCreatorBeatSync = () => {
    if (!allowTimelineTimingEdit('sfx')) return;
    const beat = findCreatorBeat(musicBeatMarkers, previewTimelineTime, outputTimelineDuration);
    if (beat === null) {
      selectCreativeTool("sound");
      setStudioActionMessage("Choose or import music in Sound and let its audio analysis finish. No detected beats are available yet; no impact was added.");
      return;
    }
    const impact = SOUND_EFFECT_PRESETS.find(preset => preset.id === "impact");
    if (!impact) return;
    setBeatSnapEnabled(true);
    setCreatorPreview(current => ({ ...current, activeDemo: "" }));
    addSoundEffectPreset(impact, beat);
  };

  const applyCreatorLayerStack = () => {
    setComparisonMode("after");
    setTimelineDockExpanded(true);
    setCreatorPreview(current => ({ ...current, activeDemo: "" }));
    setStudioActionMessage(`${1 + overlays.filter(overlay => !overlay.isCaption).length} visual layers in this project. Add your own titles, graphics and media from the timeline.`);
  };

  const applyCreatorDirectorSuggestion = evidenceText => {
    const hasReviewedEvidence = Boolean(String(evidenceText || "").trim());
    setCreatorPreview(current => ({
      ...current,
      enabled: true,
      evidenceText: hasReviewedEvidence ? String(evidenceText).trim() : "",
    }));
    setStudioActionMessage(
      hasReviewedEvidence
        ? "Transcript-led visual suggestion accepted as an editable browser preview."
        : "Suggestion opened without a grounding claim. Review a timestamped caption line before using it."
    );
    setComparisonMode("after");
  };

  const addVideoLayer = event => {
    const file = event.target.files[0];
    if (!file) return;

    // Basic check for video file
    if (!file.type.startsWith("video/")) {
      toast.error("Please select a valid video file.");
      return;
    }

    const url = URL.createObjectURL(file);

    // Ask user type: Overlay or B-Roll or Append?
    const type = window.confirm(
      "Click OK for B-ROLL OVERLAY (fullscreen cutaway, timed).\nClick Cancel for more options."
    )
      ? "broll"
      : window.confirm(
            "Click OK for PIP OVERLAY (reaction, picture-in-picture).\nClick Cancel to APPEND to timeline."
          )
        ? "overlay"
        : "append";

    if (type === "broll") {
      addOverlayAsset({
        type: "video",
        src: url,
        file,
        isLocal: true,
        width: 100,
        height: 100,
        bRollMode: "fullscreen",
      });
    } else if (type === "overlay") {
      addOverlayAsset({
        type: "video",
        src: url,
        file,
        isLocal: true,
        width: 40,
        height: 30,
      });
    } else {
      // Add to Timeline (Sequencing)
      // Create temp video to get duration
      const tempId = Date.now();
      const tempVideo = document.createElement("video");
      if (!applySafeMediaSource(tempVideo, url)) {
        console.error("This clip source uses an unsupported preview URL.");
        return;
      }
      tempVideo.preload = "metadata";

      tempVideo.onloadedmetadata = () => {
        const duration = tempVideo.duration;
        setTimeline(prev =>
          prev.map(item =>
            item.id === tempId ? { ...item, duration: duration, endRequest: duration } : item
          )
        );
      };

      // Add immediately with 0 duration so user sees it right away
      setTimeline(prev => [
        ...prev,
        {
          id: tempId,
          url: url,
          duration: 0,
          file: file,
          name: file.name,
          isLocal: true,
        },
      ]);
    }

    // Reset input so same file can be selected again if needed
    event.target.value = null;
  };

  const addCleanSpeakerAngle = event => {
    const files = Array.from(event.target.files || []);
    if (!files.length) return;
    const videoFiles = files.filter(file => file.type.startsWith("video/")).slice(0, 12);
    if (!videoFiles.length) {
      toast.error("Choose a real video source for the clean speaker angle.");
      event.target.value = "";
      return;
    }
    const sources = videoFiles.map(file => ({
      id: createSecureId("speaker-angle"),
      url: URL.createObjectURL(file),
      file,
      name: file.name,
      isLocal: true,
      duration: 0,
      status: "uploading",
    }));
    setSpeakerStackSources(current => [...current, ...sources]);
    setSpeakerStackSyncConfirmed(false);
    if (sources.length > 1) {
      const cameraCount = clampNumber(sources.length, 2, 4, 2);
      setSpeakerStackCameraCount(cameraCount);
      setSpeakerStackLayout(getDefaultMulticamLayout(cameraCount));
      setSpeakerStackSourceIds(current =>
        MULTICAM_SLOT_KEYS.reduce(
          (next, slot, index) => ({
            ...next,
            [slot]: sources[index]?.id || current[slot] || null,
          }),
          {}
        )
      );
    } else {
      setSpeakerStackSourceIds(current => ({
        ...current,
        top: current.top || "programme-source",
        bottom: sources[0].id,
      }));
    }
    const cameraProjectId = activeProjectIdRef.current;
    sources.forEach(source => {
      void ensurePreviewableClipUrl(source).catch(error => {
        if (activeProjectIdRef.current !== cameraProjectId) return;
        setSpeakerStackSources(current => current.map(item => item.id === source.id
          ? { ...item, status: "failed", error: error.message } : item));
        setStudioActionMessage(`Camera upload failed: ${error.message}. Remove and reimport this angle before saving.`);
      });
      const probe = document.createElement("video");
      if (!applySafeMediaSource(probe, source.url)) return;
      probe.preload = "metadata";
      probe.onloadedmetadata = () => {
        setSpeakerStackSources(current =>
          current.map(item =>
            item.id === source.id ? { ...item, duration: Number(probe.duration || 0) } : item
          )
        );
      };
    });
    setStudioActionMessage(
      sources.length > 4
        ? `${sources.length} clean angles added to the source bank. The first four are routed to the programme grid; choose any replacements below.`
        : sources.length > 1
          ? `${sources.length} clean camera angles added. Choose the layout, align timecode, and confirm synchronization.`
        : `${sources[0].name} added as a clean camera angle. Select every required source before enabling multicamera.`
    );
    event.target.value = "";
  };

  const addImageLayer = event => {
    const file = event.target.files[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      toast.error("Please select a valid image file.");
      return;
    }

    const imageOverlay = addOverlayAsset({
      type: "image",
      src: URL.createObjectURL(file),
      file,
      isLocal: true,
      width: 42,
      height: 32,
      bRollMode: "pip",
    });

    setStudioInspectorTab("broll");
    setComparisonMode("after");
    if (imageOverlay) jumpToOutputTimelineTime(Number(imageOverlay.startTime || 0));
    setStudioActionMessage(
      "Image placed at the playhead as a timed picture-in-picture layer. Choose a layout, motion and crop below."
    );

    event.target.value = null;
  };

  const addVectorArtwork = async vectorShape => {
    if (!allowTimelineTimingEdit('graphics')) throw Error('Unlock the Graphics track.');
    if (!allowTimelineTimingEdit('broll')) throw Error('Unlock the image layer track.');
    if (overlays.length >= 128) throw Error('This project has reached its 128-layer limit.');
    const projectId=activeProjectIdRef.current,signature=lastSnapshotSignatureRef.current;
    const file=await rasterizeStudioArtwork(studioVectorSvg(vectorShape),'Vector artwork');
    if (activeProjectIdRef.current!==projectId || lastSnapshotSignatureRef.current!==signature)
      throw Error('The project changed while artwork was prepared. Add it again.');
    const id=createSecureId('vector'),aspect=previewAspectRatio||16/9;
    const layer={id,type:'image',name:'Vector artwork',vectorShape,file,src:URL.createObjectURL(file),isLocal:true,
      x:50,y:50,width:30,height:30*aspect,startTime:0,duration:liveTimelineDuration,anchorX:50,anchorY:50,
      bRollMode:'pip',mediaFit:'contain',frameShape:'edge',borderRadius:0,shadow:'none',motionDesign:true,
      opacity:1,scale:1,rotation:0,animation:{enter:'none',exit:'none',enterDuration:0,exitDuration:0}};
    setOverlays(previous=>[...previous,layer]);setActiveOverlayId(id);setComparisonMode('after');setPreviewFinishedFile(false);
    setStudioActionMessage('Vector artwork added. Shape its points here, then animate its layer in Motion.');
  };
  const updateVectorArtwork = (id,vectorShape) => {
    if (!allowTimelineTimingEdit('graphics')) throw Error('Unlock the Graphics track.');
    if (!allowTimelineTimingEdit('broll')) throw Error('Unlock the image layer track.');
    validateStudioVectorShape(vectorShape);
    setOverlays(previous=>previous.map(layer=>layer.id===id?{...layer,vectorShape}:layer));
    setPreviewFinishedFile(false);
  };

  const addLogoMotionLayer = async event => {
    const selectedFile = event.target.files?.[0];
    if (!selectedFile) return;
    if (!selectedFile.type.startsWith("image/")) {
      toast.error("Choose a PNG, WebP, JPEG or SVG logo asset.");
      event.target.value = "";
      return;
    }

    let file = selectedFile;
    if (selectedFile.type === "image/svg+xml" || selectedFile.name.toLowerCase().endsWith(".svg")) {
      try {
        const safeSvg = sanitizeLogoSvg(await selectedFile.text());
        const svgUrl = URL.createObjectURL(new Blob([safeSvg], { type: "image/svg+xml" }));
        const raster = await new Promise((resolve, reject) => {
          const image = new Image();
          image.onload = () => {
            const longest = Math.max(image.naturalWidth || 1, image.naturalHeight || 1);
            const scale = Math.min(1, 2048 / longest);
            const canvas = document.createElement("canvas");
            canvas.width = Math.max(1, Math.round((image.naturalWidth || 1) * scale));
            canvas.height = Math.max(1, Math.round((image.naturalHeight || 1) * scale));
            canvas.getContext("2d").drawImage(image, 0, 0, canvas.width, canvas.height);
            canvas.toBlob(blob => (blob ? resolve(blob) : reject(new Error("SVG rasterization failed"))), "image/png");
          };
          image.onerror = () => reject(new Error("SVG could not be decoded"));
          image.src = svgUrl;
        });
        URL.revokeObjectURL(svgUrl);
        file = new File([raster], selectedFile.name.replace(/\.svg$/i, ".png"), {
          type: "image/png",
        });
      } catch (error) {
        toast.error("That SVG could not be safely imported. Export it as a transparent PNG and retry.");
        event.target.value = "";
        return;
      }
    }

    const logo = addOverlayAsset({
      type: "image",
      src: URL.createObjectURL(file),
      file,
      isLocal: true,
      width: 52,
      height: 28,
      bRollMode: "pip",
    });
    if (!logo) return;
    const start = Number(logo.startTime || 0);
    const layerDuration = Math.max(0.5, Math.min(6, outputTimelineDuration - start));
    const end = start + layerDuration;
    const finalLogo = {
      ...logo,
      name: selectedFile.name,
      x: 50,
      y: 50,
      width: 52,
      height: 28,
      startTime: start,
      duration: layerDuration,
      mediaFit: "contain",
      frameShape: "edge",
      borderRadius: 0,
      shadow: "none",
      scale: 1,
      rotation: 0,
      opacity: 1,
      anchorX: 50,
      anchorY: 50,
      motionDesign: true,
      glow: { enabled: true, color: "#8b5cf6", radius: 16, intensity: 0.5 },
      layerShadow: { enabled: true, color: "#000000", blur: 18, opacity: 0.55, x: 0, y: 10 },
      motionBlur: { enabled: true, samples: 4, shutter: 0.5 },
    };
    setOverlays(current => current.map(layer => (layer.id === logo.id ? finalLogo : layer)));
    const pose = (time, property, value, easing = "ease_out", curve) => ({
      id: createSecureId("logo-key"),
      targetId: logo.id,
      time,
      property,
      value,
      easing,
      ...(curve ? { curve } : {}),
    });
    setMotionKeyframes(current => [
      ...current,
      pose(start, "x", 42, "bezier", [0.16, 1, 0.3, 1]),
      pose(start, "y", 53, "bezier", [0.16, 1, 0.3, 1]),
      pose(start, "scale", 0.55, "bezier", [0.16, 1, 0.3, 1]),
      pose(start, "rotation", -12, "bezier", [0.16, 1, 0.3, 1]),
      pose(start, "opacity", 0, "ease_out"),
      pose(start + 0.42, "x", 50, "bezier", [0.18, 1.25, 0.32, 1]),
      pose(start + 0.42, "y", 50, "bezier", [0.18, 1.25, 0.32, 1]),
      pose(start + 0.42, "scale", 1.12, "bezier", [0.18, 1.25, 0.32, 1]),
      pose(start + 0.42, "rotation", 2, "bezier", [0.18, 1.25, 0.32, 1]),
      pose(start + 0.18, "opacity", 1, "ease_out"),
      pose(start + 0.7, "scale", 1, "ease_out"),
      pose(start + 0.7, "rotation", 0, "ease_out"),
      pose(Math.max(start + 0.75, end - 0.22), "opacity", 1, "ease_in"),
      pose(end, "opacity", 0, "ease_in"),
    ]);
    setMotionTargetId(logo.id);
    setActiveOverlayId(logo.id);
    setMotionWorkspace("logo");
    setStudioInspectorTab("motion");
    setComparisonMode("after");
    jumpToOutputTimelineTime(start);
    setStudioActionMessage(
      "Logo imported as a real motion layer with editable transform keys, anchor, glow, shadow and motion blur."
    );
    event.target.value = "";
  };

  const addExistingImageOverlay = imageAsset => {
    const src = normalizeAssetUrl(imageAsset);
    if (!src) {
      toast.error("This image could not be added as an overlay.");
      return;
    }

    const imageOverlay = addOverlayAsset({
      type: "image",
      src,
      isLocal: false,
      width: 42,
      height: 32,
      bRollMode: "pip",
    });
    setStudioInspectorTab("broll");
    setComparisonMode("after");
    if (imageOverlay) jumpToOutputTimelineTime(Number(imageOverlay.startTime || 0));
  };

  const buildVideoBRollOverlay = (
    file,
    src,
    startTime,
    duration,
    sourceDuration = 0,
    poster = ""
  ) => ({
    id: createSecureId("overlay"),
    type: "video",
    src,
    file,
    name: file?.name || "B-roll clip",
    isLocal: true,
    x: 50,
    y: 50,
    width: 100,
    height: 100,
    aspectRatioLocked: true,
    aspectRatio: 1,
    clipId: timeline[activeTimelineIndex]?.id || "main",
    startTime,
    duration,
    sourceStartTime: 0,
    sourceDuration,
    poster,
    sourceEndBehavior: "return",
    bRollMode: "fullscreen",
    frameShape: "edge",
    borderRadius: 28,
    animation: { enter: "fade", exit: "fade", enterDuration: 0.3, exitDuration: 0.3 },
    opacity: 1,
    coverMainVideo: true,
    muteMainAudio: false,
    useOverlayAudio: false,
    mixAudio: false,
    overlayAudioVolume: 0.7,
    audioDucking: false,
    audioDuckingStrength: 0.35,
  });

  const getGeneratedBRollRequest = async () => {
    const segments = await buildExportTimeline();
    const mapped = mapCaptionSegmentsToTimeline({
      captionSegments: normalizeCaptionSegments(captionSegments), timelineSegments: segments,
      fallbackSourceClipId: currentTimelineClip?.sourceClipId || currentTimelineClip?.id || selectedClip?.id || null,
    });
    return {
      projectId,
      sourceStoragePath: segments[0]?.source_storage_path || segments[0]?.sourceStoragePath ||
        timeline[0]?.sourceStoragePath || timeline[0]?.storagePath || sourceStoragePath,
      captions: mapped.map(cue => ({ id: cue.id, start: cue.start_time, end: cue.end_time,
        text: cue.text, reviewRequired: Boolean(cue.review_required ?? cue.reviewRequired) })),
      excludedCaptionIds: overlays.filter(layer=>layer.generationRef && layer.bRollCaptionSegmentId)
        .map(layer=>layer.bRollCaptionSegmentId).filter(id=>mapped.some(cue=>cue.id===id)),
      duration: segments.reduce((sum,segment) => sum + Number(segment.duration || 0), 0),
    };
  };

  const acceptGeneratedBRoll = (shot, job) => {
    if (job.projectId !== activeProjectIdRef.current || shot.status !== "accepted" || !shot.asset?.storagePath) return;
    const asset = { ...shot.asset, mediaType: "video", file: null,
      generationRef: { jobId: job.id, shotId: shot.id, contentHash: shot.asset.contentHash },
      provenanceRef: { jobId: job.id, contentHash: shot.asset.contentHash } };
    const sourceIndex = Number(shot.captionId.match(/-timeline-(\d+)$/)?.[1] || 1) - 1;
    const overlay = {
      ...buildVideoBRollOverlay(null, asset.url, shot.start, shot.duration, asset.duration),
      name: asset.name, isLocal: false, sourceMediaId: asset.id,
      aspectRatio: asset.width / Math.max(1, asset.height),
      storagePath: asset.storagePath, sourceStoragePath: asset.storagePath, contentHash: asset.contentHash,
      generationRef: asset.generationRef, provenanceRef: asset.provenanceRef,
      generationProvenance: asset.generationProvenance,
      clipId: timeline[sourceIndex]?.id || timeline[0]?.id || "main",
      bRollEvidenceQuote: shot.evidenceQuote, bRollCaptionSegmentId: shot.captionId,
      bRollApprovalStatus: "generated_reviewed", bRollReviewRequired: false,
      animation: { enter: "none", exit: "none", enterDuration: 0, exitDuration: 0 },
    };
    setProjectMedia(current => current.some(item => item.id === asset.id) ? current : [...current, asset]);
    setOverlays(current => current.some(item => item.generationRef?.jobId === job.id && item.generationRef?.shotId === shot.id)
      ? current : [...current, overlay]);
    setActiveOverlayId(overlay.id);
    setComparisonMode("after");
    jumpToOutputTimelineTime(shot.start);
    setStudioActionMessage(`Generated illustration placed at ${shot.start.toFixed(2)}s. Trim, reposition or remove it using the normal B-roll controls. Original speech continues.`);
  };

  // ── B-Roll video upload (fullscreen cutaways, auto-spread when multiple) ──
  const readLocalVideoDuration = src =>
    new Promise(resolve => {
      const probe = document.createElement("video");
      let settled = false;
      let timeoutId = null;
      const finish = duration => {
        if (settled) return;
        settled = true;
        if (timeoutId) window.clearTimeout(timeoutId);
        probe.removeAttribute("src");
        resolve(Number.isFinite(Number(duration)) ? Number(duration) : 0);
      };
      probe.preload = "metadata";
      probe.onloadedmetadata = () => finish(probe.duration);
      probe.onerror = () => finish(0);
      if (!applySafeMediaSource(probe, src)) {
        finish(0);
        return;
      }
      timeoutId = window.setTimeout(() => finish(0), 4000);
    });

  const captureLocalVideoPoster = src =>
    process.env.NODE_ENV === "test"
      ? Promise.resolve("")
      : new Promise(resolve => {
      const probe = document.createElement("video");
      let settled = false;
      let timeoutId = null;
      const finish = poster => {
        if (settled) return;
        settled = true;
        if (timeoutId) window.clearTimeout(timeoutId);
        probe.removeAttribute("src");
        resolve(poster || "");
      };
      probe.muted = true;
      probe.preload = "auto";
      probe.playsInline = true;
      probe.onloadeddata = () => {
        try {
          const posterCanvas = document.createElement("canvas");
          const sourceWidth = Number(probe.videoWidth || 0);
          const sourceHeight = Number(probe.videoHeight || 0);
          if (!sourceWidth || !sourceHeight) return finish("");
          const scale = Math.min(1, 640 / sourceWidth);
          posterCanvas.width = Math.max(2, Math.round(sourceWidth * scale));
          posterCanvas.height = Math.max(2, Math.round(sourceHeight * scale));
          posterCanvas.getContext("2d")?.drawImage(
            probe,
            0,
            0,
            posterCanvas.width,
            posterCanvas.height
          );
          finish(posterCanvas.toDataURL("image/jpeg", 0.82));
        } catch (_) {
          finish("");
        }
      };
      probe.onerror = () => finish("");
      if (!applySafeMediaSource(probe, src)) {
        finish("");
        return;
      }
      timeoutId = window.setTimeout(() => finish(""), 5000);
        });

  const makeProjectMediaPreviewUrl = file => {
    const url = URL.createObjectURL(file);
    projectMediaObjectUrlsRef.current.add(url);
    return url;
  };

  const releaseProjectMediaPreviewUrl = url => {
    if (!projectMediaObjectUrlsRef.current.has(url)) return;
    projectMediaObjectUrlsRef.current.delete(url);
    URL.revokeObjectURL(url);
  };

  const appendProjectMediaToSequence = asset => {
    if (!asset?.storagePath || !asset.url || !(Number(asset.duration) > 0)) {
      setMediaImportMessage("This video needs a readable duration before it can be placed. Use Retry duration on its card.");
      return;
    }
    const duration = Number(asset.duration || 0);
    const nextClip = {
      id: createSecureId("scene"),
      sourceClipId: asset.id,
      sourceMediaId: asset.id,
      name: asset.name,
      url: asset.url,
      storagePath: asset.storagePath,
      sourceStoragePath: asset.storagePath,
      contentHash: asset.contentHash || null,
      duration,
      startRequest: 0,
      endRequest: duration > 0 ? duration : null,
      isLocal: false,
    };
    setTimeline(current => [...current, nextClip]);
    setActiveTimelineIndex(timeline.length);
    setComparisonMode("after");
    setTimelineDockExpanded(true);
    setStudioActionMessage(`“${asset.name}” added to the end of your sequence. Select it to trim or split.`);
  };

  const replaceActiveTimelineSource = asset => {
    const occurrence = timeline[activeTimelineIndex];
    const duration = getTimelineClipWindow(occurrence).duration;
    if (!occurrence || !asset?.storagePath || !asset.url ||
        asset.status !== "ready" ||
        !["", "video"].includes(asset.mediaType || asset.type || "") || Number(asset.duration) < duration) {
      setMediaImportMessage("Choose a saved video long enough for the selected clip.");
      return;
    }
    const start = Math.max(0, Math.min(Number(occurrence.startRequest || 0), Number(asset.duration) - duration));
    const replace = clip => clip.id === occurrence.id ? {
      ...clip, sourceClipId: asset.id, sourceMediaId: asset.id, name: asset.name,
      url: asset.url, storagePath: asset.storagePath, sourceStoragePath: asset.storagePath,
      contentHash: asset.contentHash || null, startRequest: start, endRequest: start + duration,
      start, end: start + duration, duration,
    } : clip;
    pendingHistoryBaselineRef.current = cloneSnapshot(getEditorSnapshot());
    setTimeline(current => current.map(replace));
    setOrderedClips(current => current.map(replace));
    setSelectedClip(current => current ? replace(current) : current);
    setProjectSaveState("unsaved");
    setStudioActionMessage(`Selected clip now uses “${asset.name}”. Its timeline duration and effects are preserved.`);
  };

  const replaceSequenceSource = asset => {
    if (!allowTimelineTimingEdit('video')) return;
    if (!asset?.storagePath || !asset.url || asset.status !== 'ready' ||
        !['', 'video'].includes(asset.mediaType || asset.type || '') ||
        !Number.isFinite(Number(asset.duration)) || Number(asset.duration) < liveTimelineDuration) {
      setMediaImportMessage('Choose a saved video at least as long as the programme.'); return;
    }
    if (speedKeyframes.length || Number(previewSpeed) !== 1 || addHook || silenceRemoval) {
      setMediaImportMessage('Turn off source speed changes, the opening hook and silence removal before replacing the sequence.'); return;
    }
    const baseline = cloneSnapshot(getEditorSnapshot());
    const source = { id: createSecureId('source'), name: asset.name, url: asset.url,
      storagePath: asset.storagePath, sourceStoragePath: asset.storagePath, contentHash: asset.contentHash || null,
      sourceMediaId: asset.id, duration: liveTimelineDuration, sourceDuration: Number(asset.duration),
      start: 0, end: liveTimelineDuration, startRequest: 0, endRequest: liveTimelineDuration, isLocal: false };
    const occurrence = { ...source, id: createSecureId('scene'), sourceClipId: source.id };
    const removedPaths = new Set(timeline.flatMap(clip => [clip.storagePath, clip.sourceStoragePath]).filter(Boolean));
    const retainedPaths = new Set([asset.storagePath, ...overlays.flatMap(layer => [layer.storagePath, layer.sourceStoragePath])].filter(Boolean));
    pauseSynchronizedPreview();
    pendingHistoryBaselineRef.current = baseline;
    flushSync(() => {
      setTimeline([occurrence]); setOrderedClips([source]); setSelectedClip(source); setActiveTimelineIndex(0);
      setOverlays(current => current.map(layer => ({ ...layer, clipId: occurrence.id })));
      setMotionKeyframes(current => current.filter(key => key.targetId !== 'main-video'));
      setReframeKeyframes([]); setReframeModeCuts([]); setSpeakerFocusCuts([]); setSpeakerStackSources([]);
      setSubjectVfxScenes([]); setSmartCrop(false); setAutoCaptions(false); setCaptionSegments([]); setCaptionTextOverride('');
      setCreativeTemplateInstance(null); setAngleGroups([]); setActiveAngleGroupId(null);
      setProjectMedia(current => current.filter(item => item.id === asset.id ||
        !removedPaths.has(item.storagePath || item.sourceStoragePath) || retainedPaths.has(item.storagePath || item.sourceStoragePath)));
      setPreviewFinishedFile(false); setComparisonMode('after'); setVideoTime(0); setProjectSaveState('unsaved');
    });
    setStudioActionMessage(`Sequence replaced by “${asset.name}”. Graphic layers and soundtrack timing are preserved. Undo restores the previous sources.`);
  };

  const removeUnusedProjectMedia = () => {
    const ids = new Set(), paths = new Set();
    const visit = value => {
      if (!value || typeof value !== 'object') return;
      if (Array.isArray(value)) { value.forEach(visit); return; }
      for (const [field, item] of Object.entries(value)) {
        if (['id', 'sourceMediaId', 'sourceClipId', 'assetId'].includes(field) && typeof item === 'string') ids.add(item);
        else if (['storagePath', 'sourceStoragePath', 'assetStoragePath'].includes(field) && typeof item === 'string') paths.add(item);
        else if (item && typeof item === 'object' && !(item instanceof Blob)) visit(item);
      }
    };
    visit([timeline, orderedClips, overlays, speakerStackSources, angleGroups, soundEffects, voiceovers, musicTrack,
      extractedAudio, mainFrame, subjectVfxScenes, creativeTemplateInstance]);
    const retained = projectMedia.filter(item => ids.has(item.id) || paths.has(item.storagePath || item.sourceStoragePath) ||
      ['queued', 'uploading', 'failed', 'needs_upload'].includes(item.status));
    const targets = new Set(['main-video', ...overlays.map(layer => layer.id), ...motionScenes.map(layer => layer.id),
      ...threeDScenes.map(layer => layer.id), ...compositionGroups.groups.map(group => group.id)]);
    const retainedKeys = motionKeyframes.filter(key => targets.has(key.targetId));
    if (retained.length === projectMedia.length && retainedKeys.length === motionKeyframes.length) { setMediaImportMessage('Every saved asset and animation key is used by this edit.'); return; }
    pendingHistoryBaselineRef.current = cloneSnapshot(getEditorSnapshot());
    setProjectMedia(retained); setMotionKeyframes(retainedKeys); setSelectedProjectMediaId(null);
    setMediaImportMessage(`Removed ${projectMedia.length - retained.length} unused assets and ${motionKeyframes.length - retainedKeys.length} orphaned keys. Undo restores them; saved source files are retained.`);
  };

  const addProjectMediaAsBRoll = asset => {
    if (!asset?.storagePath || !asset.url || !(Number(asset.duration) > 0)) {
      setMediaImportMessage("This video needs a readable duration before it can be placed. Use Retry duration on its card.");
      return;
    }
    const sourceDuration = Number(asset.duration || 0);
    const currentVideoTime = Number(videoRef.current?.currentTime || 0);
    const startTime = getPreviewTimelineTime(currentVideoTime);
    const duration = Math.min(sourceDuration || 3, 3);
    const overlay = {
      ...buildVideoBRollOverlay(null, asset.url, startTime, duration, sourceDuration, asset.poster),
      name: asset.name,
      file: null,
      isLocal: false,
      sourceMediaId: asset.id,
      storagePath: asset.storagePath,
      sourceStoragePath: asset.storagePath,
    };
    setOverlays(current => [...current, overlay]);
    setActiveOverlayId(overlay.id);
    setStudioInspectorTab("broll");
    setComparisonMode("after");
    jumpToOutputTimelineTime(startTime);
    setStudioActionMessage(`“${asset.name}” placed as B-roll at the playhead. Adjust its length on the timeline.`);
  };

  const uploadProjectMediaAsset = async (asset, { appendToSequenceOnReady = false } = {}) => {
    if (!(asset?.file instanceof Blob)) return;
    const uploadProjectId = activeProjectIdRef.current;
    setProjectMedia(current => current.map(item => item.id === asset.id
      ? { ...item, status: "uploading", progress: 0, error: "" }
      : item));
    try {
      const token = await getMediaAuthToken();
      if (!token) throw new Error("Sign in to save this video to your project.");
      const uploaded = await uploadSourceFileViaBackend({
        file: asset.file,
        token,
        getToken: getMediaAuthToken,
        mediaType: "video",
        fileName: asset.name,
        purpose: "studio_project",
        onProgress: (transferred, total) => {
          if (activeProjectIdRef.current !== uploadProjectId) return;
          const progress = total > 0 ? Math.min(100, Math.round((transferred / total) * 100)) : 0;
          setProjectMedia(current => current.map(item => item.id === asset.id
            ? { ...item, progress }
            : item));
        },
      });
      if (!uploaded?.url || !uploaded?.storagePath) {
        throw new Error("Upload did not return a saved project source. Retry this video.");
      }
      if (activeProjectIdRef.current !== uploadProjectId) return;
      const measuredDuration = appendToSequenceOnReady && !(Number(asset.duration) > 0)
        ? await readLocalVideoDuration(asset.url)
        : Number(asset.duration || 0);
      const readyAsset = {
        ...asset,
        url: uploaded.url,
        storagePath: uploaded.storagePath,
        sourceStoragePath: uploaded.storagePath,
        ...(uploaded.contentHash || uploaded.sha256 ? {contentHash: uploaded.contentHash || uploaded.sha256} : {}),
        file: null,
        isLocal: false,
        duration: measuredDuration,
        status: "ready",
        progress: 100,
        error: "",
      };
      setProjectMedia(current => current.map(item => item.id === asset.id
        ? { ...item, ...readyAsset, duration: Number(item.duration || readyAsset.duration || 0), poster: item.poster || readyAsset.poster || "" }
        : item));
      if (appendToSequenceOnReady) {
        if (measuredDuration > 0) appendProjectMediaToSequence(readyAsset);
        else setMediaImportMessage("Combined video saved, but its duration is unreadable. Use Retry duration before adding it to your sequence.");
      }
      releaseProjectMediaPreviewUrl(asset.url);
    } catch (error) {
      if (activeProjectIdRef.current !== uploadProjectId) return;
      setProjectMedia(current => current.map(item => item.id === asset.id
        ? { ...item, status: "failed", progress: 0, error: error.message || "Upload failed. Retry this video." }
        : item));
      setMediaImportMessage(`“${asset.name}” could not be saved. Retry it from Project media.`);
    }
  };

  const importProjectFiles = files => {
    const selectedFiles = Array.from(files || []);
    const validFiles = selectedFiles.filter(file =>
      file instanceof Blob && file.size > 0 &&
      (file.type.startsWith("video/") || /\.(mp4|mov|m4v|webm|mkv)$/i.test(file.name || ""))
    );
    if (!validFiles.length) {
      setMediaImportMessage("Choose one or more nonempty video files to import.");
      return;
    }
    const assets = validFiles.map(file => ({
      id: createSecureId("project-media"),
      name: file.name || "Untitled video",
      url: makeProjectMediaPreviewUrl(file),
      file,
      isLocal: true,
      duration: 0,
      poster: "",
      status: "queued",
      progress: 0,
      error: "",
    }));
    setProjectMedia(current => [...current, ...assets]);
    setSelectedProjectMediaId(assets[0].id);
    setProjectRailTab("media");
    setProjectRailExpanded(true);
    setMediaImportMessage(
      `${assets.length} ${assets.length === 1 ? "video" : "videos"} added to your media library. Save completes per file; choose a ready video to place it in the edit.` +
      (validFiles.length !== selectedFiles.length ? ` ${selectedFiles.length - validFiles.length} unsupported or empty files skipped.` : "")
    );
    const queue = [...assets];
    const workers = Array.from({ length: Math.min(3, assets.length) }, async () => {
      while (queue.length) {
        const asset = queue.shift();
        await Promise.all([
          (async () => {
            const [duration, poster] = await Promise.all([
              readLocalVideoDuration(asset.url),
              captureLocalVideoPoster(asset.url),
            ]);
            setProjectMedia(current => current.map(item => item.id === asset.id
              ? { ...item, duration, poster }
              : item));
          })(),
          uploadProjectMediaAsset(asset),
        ]);
      }
    });
    void Promise.allSettled(workers);
  };

  const handleProjectMediaImport = event => {
    importProjectFiles(event.target.files);
    event.target.value = "";
  };

  const probeProjectMediaDuration = async asset => {
    if (!asset?.url) return;
    const duration = await readLocalVideoDuration(asset.url);
    if (duration > 0) {
      setProjectMedia(current => current.map(item => item.id === asset.id
        ? { ...item, duration, error: "" }
        : item));
      setMediaImportMessage(`Duration read for “${asset.name}”. It is ready to place in your edit.`);
      return;
    }
    setMediaImportMessage(`Could not read the duration of “${asset.name}”. Check that the video plays, then retry.`);
  };

  useEffect(() => () => {
    projectMediaObjectUrlsRef.current.forEach(url => URL.revokeObjectURL(url));
    projectMediaObjectUrlsRef.current.clear();
  }, []);

  useEffect(() => {
    const unseenFiles = Array.from(initialProjectFiles || []).filter(file => {
      if (!(file instanceof Blob) || consumedInitialFilesRef.current.has(file)) return false;
      consumedInitialFilesRef.current.add(file);
      return true;
    });
    if (unseenFiles.length) importProjectFiles(unseenFiles);
  }, [initialProjectFiles]);

  useEffect(() => {
    if (!importedCameraMaster) return;
    const key = String(importedCameraMaster.id || importedCameraMaster.storagePath || "");
    if (!key || consumedCameraMasterIdsRef.current.has(key)) return;
    consumedCameraMasterIdsRef.current.add(key);
    const file = importedCameraMaster.file instanceof Blob ? importedCameraMaster.file : null;
    if (!file && (!importedCameraMaster.storagePath || !importedCameraMaster.url)) {
      setMediaImportMessage("The combined camera video has no saved source. Reopen the camera workflow and retry its import.");
      return;
    }
    const asset = {
      id: key,
      name: importedCameraMaster.name || file?.name || "Combined camera take",
      url: file ? makeProjectMediaPreviewUrl(file) : importedCameraMaster.url,
      file,
      isLocal: !!file,
      storagePath: importedCameraMaster.storagePath || "",
      sourceStoragePath: importedCameraMaster.storagePath || "",
      duration: Number(importedCameraMaster.duration || 0),
      poster: "",
      status: file ? "queued" : "ready",
      progress: file ? 0 : 100,
      error: "",
    };
    setProjectMedia(current => current.some(item => item.id === key) ? current : [...current, asset]);
    setProjectRailExpanded(true);
    setSelectedProjectMediaId(key);
    if (file) {
      void uploadProjectMediaAsset(asset, { appendToSequenceOnReady: true });
    } else {
      if (asset.duration > 0) appendProjectMediaToSequence(asset);
      else {
        void readLocalVideoDuration(asset.url).then(duration => {
          if (!(duration > 0)) {
            setMediaImportMessage("Combined video saved, but its duration is unreadable. Use Retry duration before adding it to your sequence.");
            return;
          }
          const readyAsset = { ...asset, duration };
          setProjectMedia(current => current.map(item => item.id === asset.id ? readyAsset : item));
          appendProjectMediaToSequence(readyAsset);
        });
      }
    }
  }, [importedCameraMaster]);

  const toggleCameraMediaSelection = assetId => {
    setSelectedCameraMediaIds(current => {
      if (current.includes(assetId)) return current.filter(id => id !== assetId);
      if (current.length >= MAX_STUDIO_ANGLES) {
        setMediaImportMessage(`Choose up to ${MAX_STUDIO_ANGLES} camera angles for one take.`);
        return current;
      }
      return [...current, assetId];
    });
  };

  const createSelectedFilmTake = () => {
    const assets = selectedCameraMediaIds
      .map(id => projectMedia.find(item => item.id === id))
      .filter(Boolean);
    try {
      const group = createStudioAngleGroup({
        id: createSecureId("camera-take"),
        name: angleGroupName || `Camera take ${angleGroups.length + 1}`,
        assets,
      });
      setAngleGroups(current => [...current, group]);
      setActiveAngleGroupId(group.id);
      setSelectedCameraMediaIds([]);
      setAngleGroupName("");
      setMediaImportMessage(`“${group.name}” is ready. Choose shot times and a camera below.`);
    } catch (error) {
      setMediaImportMessage(error.message);
    }
  };

  const updateFilmTake = (groupId, updater) => {
    setAngleGroups(current => current.map(group => {
      if (group.id !== groupId) return group;
      try {
        return updater(group);
      } catch (error) {
        setMediaImportMessage(error.message);
        return group;
      }
    }));
  };

  const appendFilmAngleShot = (group, asset) => {
    try {
      const shot = createStudioAngleShot({
        group,
        asset,
        sharedStart: group.sharedStart,
        sharedEnd: group.sharedEnd,
        clipId: createSecureId("angle-shot"),
      });
      setTimeline(current => [...current, shot]);
      setActiveTimelineIndex(timeline.length);
      setTimelineDockExpanded(true);
      setComparisonMode("after");
      setMediaImportMessage(`Added ${asset.name} from ${Number(group.sharedStart).toFixed(1)}s–${Number(group.sharedEnd).toFixed(1)}s. Set the next shot range to switch cameras.`);
    } catch (error) {
      setMediaImportMessage(error.message);
    }
  };

  const pauseFilmTakePreview = () => {
    filmAnglePreviewRefsRef.current.forEach(video => video?.pause?.());
  };

  const playFilmTakePreview = async group => {
    pauseFilmTakePreview();
    try {
      const cameras = group.angles.map((angle, index) => ({
        angle,
        index,
        video: filmAnglePreviewRefsRef.current.get(angle.assetId),
      }));
      if (cameras.some(camera => !camera.video)) throw new Error("Camera previews are still opening. Try Play together again.");
      await Promise.all(cameras.map(({ video }) => video.readyState >= 1
        ? Promise.resolve()
        : new Promise((resolve, reject) => {
            const timer = window.setTimeout(() => {
              video.removeEventListener("loadedmetadata", onReady);
              video.removeEventListener("error", onError);
              reject(new Error("One camera preview could not load. Refresh its link and retry."));
            }, 5000);
            const onReady = () => {
              window.clearTimeout(timer);
              video.removeEventListener("error", onError);
              resolve();
            };
            const onError = () => {
              window.clearTimeout(timer);
              video.removeEventListener("loadedmetadata", onReady);
              reject(new Error("One camera preview could not load. Refresh its link and retry."));
            };
            video.addEventListener("loadedmetadata", onReady, { once: true });
            video.addEventListener("error", onError, { once: true });
            video.load?.();
          })));
      cameras.forEach(({ angle, index, video }) => {
        video.muted = index !== 0;
        video.currentTime = Number(angle.offsetSeconds || 0) + Number(group.sharedStart || 0);
      });
      await Promise.all(cameras.map(({ video }) => safePlayMediaElement(video)));
    } catch (error) {
      pauseFilmTakePreview();
      setMediaImportMessage(error.message || "Camera previews could not start together.");
    }
  };

  const syncFilmTakePreview = group => {
    const master = group.angles[0];
    const masterVideo = filmAnglePreviewRefsRef.current.get(master?.assetId);
    if (!masterVideo || masterVideo.paused) return;
    const sharedTime = Math.max(0, masterVideo.currentTime - Number(master.offsetSeconds || 0));
    group.angles.slice(1).forEach(angle => {
      const video = filmAnglePreviewRefsRef.current.get(angle.assetId);
      if (!video || video.paused) return;
      const target = sharedTime + Number(angle.offsetSeconds || 0);
      if (Math.abs(video.currentTime - target) > 0.2) video.currentTime = target;
    });
  };

  useEffect(() => () => pauseFilmTakePreview(), [activeAngleGroupId]);

  const openPodcastCamCombiner = async () => {
    const assets = selectedCameraMediaIds
      .map(id => projectMedia.find(item => item.id === id))
      .filter(item => item?.status === "ready" && item.storagePath);
    if (assets.length < 2 || assets.length > 3 || !onOpenCameraAngles) return;
    setCameraGroupStatus("opening");
    try {
      const refreshed = await Promise.all(assets.map(async asset => {
        const url = await refreshSourceUrlByPath(asset.storagePath);
        if (!url) throw new Error(`Could not reopen “${asset.name}”. Use Refresh link and retry.`);
        return { ...asset, url };
      }));
      onOpenCameraAngles({ assets: refreshed });
      setCameraGroupStatus("idle");
    } catch (error) {
      setCameraGroupStatus("idle");
      setMediaImportMessage(error.message || "Podcast cameras could not be opened. Retry from Project media.");
    }
  };

  const handleBRollVideoUpload = async event => {
    const input = event.target;
    const files = Array.from(event.target.files || []);
    if (!files.length) return;

    const videoFiles = files.filter(file => file.type.startsWith("video/"));
    if (videoFiles.length !== files.length) {
      toast.error("Please select valid video files for B-roll.");
      event.target.value = null;
      return;
    }

    const duration = Math.max(0, getTimelineDuration());
    const currentVideoTime = videoRef.current ? videoRef.current.currentTime : 0;
    const playheadTime = getPreviewTimelineTime(currentVideoTime);
    const previewDuration = Math.max(duration, videoFiles.length * 2);
    const selectedStoryPlaceholder = overlays.find(
      overlay => overlay.id === activeOverlayId && overlay.bRollPlaceholder
    );
    const slotDuration = previewDuration / Math.max(1, videoFiles.length);
    const fallbackShotDuration = clampNumber(
      slotDuration * 0.68,
      0.8,
      Math.max(0.9, slotDuration - 0.15),
      1.6
    );
    const safeEnd = Math.max(0, previewDuration - fallbackShotDuration - 0.1);
    const localSources = videoFiles.map(file => ({ file, url: URL.createObjectURL(file) }));
    const sourcePosterPromises = localSources.map(source => captureLocalVideoPoster(source.url));
    const sourceDurations = await Promise.all(
      localSources.map(source => readLocalVideoDuration(source.url))
    );
    const overlaysToAdd = localSources.map(({ file, url }, index) => {
      const evenStart =
        videoFiles.length === 1
          ? playheadTime
          : index * slotDuration + Math.max(0, (slotDuration - fallbackShotDuration) / 2);
      const startTime =
        videoFiles.length === 1
          ? Math.min(safeEnd || playheadTime, playheadTime)
          : clampNumber(evenStart, 0, safeEnd, evenStart);
      const shotDuration = Math.min(
        fallbackShotDuration,
        Math.max(0.8, previewDuration - startTime),
        sourceDurations[index] > 0 ? sourceDurations[index] : Number.POSITIVE_INFINITY
      );
      const plannedStart =
        index === 0 && selectedStoryPlaceholder
          ? Number(selectedStoryPlaceholder.startTime || 0)
          : startTime;
      const plannedDuration =
        index === 0 && selectedStoryPlaceholder
          ? Math.min(
              Math.max(0.1, Number(selectedStoryPlaceholder.duration || shotDuration)),
              sourceDurations[index] > 0 ? sourceDurations[index] : Number.POSITIVE_INFINITY
            )
          : shotDuration;
      const overlay = buildVideoBRollOverlay(
        file,
        url,
        plannedStart,
        plannedDuration,
        sourceDurations[index]
      );
      if (index === 0 && selectedStoryPlaceholder) {
        return {
          ...overlay,
          bRollConcept: selectedStoryPlaceholder.bRollConcept || null,
          bRollSearchQuery: selectedStoryPlaceholder.bRollSearchQuery || "",
          bRollEvidenceQuote: selectedStoryPlaceholder.bRollEvidenceQuote || "",
          bRollCaptionSegmentId: selectedStoryPlaceholder.bRollCaptionSegmentId || null,
          bRollApprovalStatus: "creator_footage_attached",
          bRollConfidence: selectedStoryPlaceholder.bRollConfidence || 0,
          bRollReviewRequired: false,
        };
      }
      return overlay;
    });

    setOverlays(prev =>
      selectedStoryPlaceholder
        ? prev.flatMap(overlay =>
            overlay.id === selectedStoryPlaceholder.id ? overlaysToAdd : [overlay]
          )
        : [...prev, ...overlaysToAdd]
    );
    setActiveOverlayId(overlaysToAdd[0]?.id || null);
    Promise.all(sourcePosterPromises).then(sourcePosters => {
      if (!sourcePosters.some(Boolean)) return;
      setOverlays(current =>
        current.map(overlay => {
          const matchedIndex = overlaysToAdd.findIndex(item => item.id === overlay.id);
          return matchedIndex >= 0 && sourcePosters[matchedIndex]
            ? { ...overlay, poster: sourcePosters[matchedIndex] }
            : overlay;
        })
      );
    });
    setStudioInspectorTab("broll");
    setComparisonMode("after");
    if (overlaysToAdd[0]) {
      jumpToOutputTimelineTime(Number(overlaysToAdd[0].startTime || 0));
    }
    setStudioActionMessage(
      selectedStoryPlaceholder
        ? `Real moving footage replaced the “${selectedStoryPlaceholder.bRollTitle || "story beat"}” plan at ${formatPreviewTimePrecise(Number(selectedStoryPlaceholder.startTime || 0))}. Original programme audio stays active.`
        : overlaysToAdd.length === 1
          ? "B-roll placed at the playhead. Set its layout, timing, and audio in one panel."
          : `${overlaysToAdd.length} B-roll clips were distributed across the short. Fine-tune any block below.`
    );
    toast.success(
      selectedStoryPlaceholder
        ? "Planned story beat replaced with real footage."
        : overlaysToAdd.length === 1
          ? "B-roll clip placed at the playhead."
          : `${overlaysToAdd.length} B-roll clips placed across the short.`
    );

    input.value = null;
  };

  const updateOverlayText = (id, newText) => {
    const safeText = normalizeStudioTitleText(newText);
    const target = overlays.find(layer => layer.id === id);
    if (target?.textAnimator) {
      try { validateStudioTextAnimation({ ...target, text: safeText }); }
      catch (error) { setStudioActionMessage(error.message); return; }
    }
    setOverlays(overlays.map(o => (o.id === id ? { ...o, text: safeText } : o)));
  };

  const deleteOverlay = id => {
    const nextCompounds = removeStudioGroupMembers(compoundClips, [id]);
    setCompoundClips(nextCompounds);
    setOverlays(current => {
      const nextOverlays = current.filter(overlay => overlay.id !== id);
      setActiveOverlayId(currentId => {
        if (currentId !== id) return currentId;
        return nextOverlays[nextOverlays.length - 1]?.id || null;
      });
      setMotionTargetId(currentId => {
        if (String(currentId) !== String(id)) return currentId;
        return nextOverlays[nextOverlays.length - 1]?.id || "main-video";
      });
      return nextOverlays;
    });
    setMotionKeyframes(current => pruneRemovedStudioGroupKeys(current, compoundClips, nextCompounds)
      .filter(keyframe => String(keyframe.targetId) !== String(id)));
  };

  // ── B-roll / cutaway overlay helpers ──
  const updateOverlayTimeRange = (id, startTime, duration) => {
    const magneticStart = resolveMagneticTimelineTime(startTime);
    setOverlays(prev =>
      prev.map(o => {
        if (o.id !== id) return o;
        const nextStart = Math.max(0, Number(magneticStart.time) || 0);
        const timelineRemaining = Math.max(0.1, getTimelineDuration() - nextStart);
        const nextDuration = Math.min(Math.max(0.1, Number(duration) || 0.1), timelineRemaining);
        return { ...o, startTime: nextStart, duration: nextDuration };
      })
    );
  };

  const applyOverlayDuration = (overlay, requestedDuration, label = "") => {
    if (!overlay) return;
    const requested = Math.max(0.1, Number(requestedDuration) || 0.1);
    const remaining = Math.max(0.1, getTimelineDuration() - Number(overlay.startTime || 0));
    const applied = Math.min(requested, remaining);
    updateOverlayTimeRange(overlay.id, overlay.startTime || 0, applied);
    setStudioActionMessage(
      requested > remaining
        ? `${label || "Duration"} was limited to ${formatEditorDuration(applied)}, the remaining output time.`
        : `${overlay.type === "image" ? "Image" : "B-roll"} duration set to ${formatEditorDuration(applied)}.`
    );
  };

  const commitOverlayDurationText = (overlay, value) => {
    const parsed = parseEditorDuration(value);
    if (parsed === null || parsed < 0.1) {
      setStudioActionMessage("Enter a duration like 10, 1:00, 2m or 5:00.");
      return;
    }
    applyOverlayDuration(overlay, parsed);
  };

  const setOverlaySourceEndBehavior = (id, behavior) => {
    const normalized = normalizeBRollEndBehavior(behavior);
    setOverlayStyleOption(id, "sourceEndBehavior", normalized);
    setStudioActionMessage(
      normalized === "loop"
        ? "Shorter B-roll will loop until the configured cutaway ends."
        : normalized === "hold"
          ? "The final B-roll frame will hold while the original voice returns."
          : "The original video and voice will return when the B-roll source footage ends."
    );
  };

  const rememberOverlaySourceDuration = (id, duration) => {
    const safeDuration = Math.max(0, Number(duration || 0));
    if (!safeDuration) return;
    setOverlays(prev => {
      const index = prev.findIndex(overlay => overlay.id === id);
      if (index < 0 || Math.abs(Number(prev[index].sourceDuration || 0) - safeDuration) <= .05)
        return prev;
      const next = [...prev];next[index] = { ...prev[index], sourceDuration: safeDuration };
      return next;
    });
  };

  const setOverlayBRollMode = (id, mode) => {
    setOverlays(prev =>
      prev.map(o => {
        if (o.id !== id) return o;
        const pipPlacement = getCollisionSafePipPlacement({
          overlays: prev,
          overlayId: id,
          startTime: o.startTime,
          duration: o.duration,
          width: 28,
          height: 20,
        });
        const modeLayout = {
          fullscreen: { width: 100, height: 100, x: 0, y: 0 },
          pip: pipPlacement,
          sideBySide: { width: 50, height: 100, x: 75, y: 50 },
        }[mode] || {
          width: o.width || 40,
          height: o.height || 30,
          x: o.x || 50,
          y: o.y || 50,
        };
        return clampOverlayPlacement({
          ...o,
          bRollMode: mode || undefined,
          coverMainVideo: mode === "fullscreen",
          mediaFit: "cover",
          frameShape: mode === "fullscreen" ? "edge" : "round",
          borderRadius: mode === "fullscreen" ? 0 : Number(o.borderRadius || 28),
          width: modeLayout.width,
          height: modeLayout.height,
          x: modeLayout.x,
          y: modeLayout.y,
          aspectRatio:
            mode === "pip" ? modeLayout.width / modeLayout.height : mode === "sideBySide" ? 0.5 : 1,
        });
      })
    );
    const selectedOverlay = overlays.find(overlay => overlay.id === id);
    if (selectedOverlay?.startTime !== undefined) {
      seekLiveEditTimelineItem(Number(selectedOverlay.startTime || 0), "broll", id);
    }
    setStudioActionMessage(
      `${mode === "fullscreen" ? "Full-screen cutaway" : mode === "sideBySide" ? "True side-by-side" : "Picture-in-picture"} applied at ${formatPreviewTimePrecise(Number(selectedOverlay?.startTime || 0))}.`
    );
  };

  const setOverlayAnimation = (id, animType, value) => {
    setOverlays(prev =>
      prev.map(o => {
        if (o.id !== id) return o;
        const anim = {
          ...(o.animation || {
            enter: "fade",
            exit: "fade",
            enterDuration: 0.3,
            exitDuration: 0.3,
          }),
        };
        if (animType === "enter" || animType === "exit") {
          anim[animType] = value;
        } else if (animType === "enterDuration" || animType === "exitDuration") {
          anim[animType] = Math.max(0.1, Math.min(2.0, Number(value) || 0.3));
        }
        return { ...o, animation: anim };
      })
    );
  };

  const setOverlayOpacity = (id, value) => {
    setOverlays(prev =>
      prev.map(o =>
        o.id === id ? { ...o, opacity: Math.max(0, Math.min(1, Number(value) || 1)) } : o
      )
    );
  };

  const commitCheckedOverlayChange = next => {
    try { validateStudioTrackMattes(next); }
    catch (error) { setStudioActionMessage(error.message); return false; }
    setOverlays(next); return true;
  };
  const setOverlayStyleOption = (id, key, value) => {
    const target = overlays.find(layer => layer.id === id);
    if(key==='mask'){
      try{validateStudioMaskAnimation({...target,mask:value},motionKeyframes.filter(frame=>frame.targetId===id));}
      catch(error){setStudioActionMessage(error.message);return;}
    }
    if (target?.perspective || key === 'perspective') {
      try { validateStudioPerspectiveLayer({...target,[key]:value},
        motionKeyframes.filter(k=>k.targetId===id && !(key==='perspective'&&!value&&(k.property==='perspectiveProgress'||/^planeCorner/.test(k.property)))),
        effectiveCompoundClips.some(g=>g.layerIds?.includes(id))); }
      catch(error){setStudioActionMessage(error.message);return;}
    }
    if (target?.textAnimator) {
      try { validateStudioTextAnimation({ ...target, [key]: value }); }
      catch (error) { setStudioActionMessage(error.message); return; }
    }
    commitCheckedOverlayChange(overlays.map(overlay => overlay.id === id ? { ...overlay, [key]: value } : overlay));
  };

  const setOverlayAudioOption = (id, key, value) => {
    commitCheckedOverlayChange(overlays.map(o => (o.id === id ? { ...o, [key]: value } : o)));
  };

  const getOverlayAudioMode = overlay => {
    if (!overlay?.useOverlayAudio) return "original";
    return overlay.muteMainAudio ? "overlay" : "mix";
  };

  const setOverlayAudioMode = (id, mode) => {
    const changed = commitCheckedOverlayChange(overlays.map(overlay => {
        if (overlay.id !== id) return overlay;
        if (mode === "overlay") {
          return {
            ...overlay,
            useOverlayAudio: true,
            muteMainAudio: true,
            mixAudio: false,
            audioDucking: false,
          };
        }
        if (mode === "mix") {
          return {
            ...overlay,
            useOverlayAudio: true,
            muteMainAudio: false,
            mixAudio: true,
          };
        }
        return {
          ...overlay,
          useOverlayAudio: false,
          muteMainAudio: false,
          mixAudio: false,
          audioDucking: false,
        };
      }));
    if (!changed) return;
    setStudioActionMessage(
      mode === "overlay"
        ? "Overlay audio is active only during this cutaway. Original audio returns automatically."
        : mode === "mix"
          ? "Original and overlay audio will play together during this cutaway."
          : "The original source audio stays continuous through this cutaway."
    );
  };

  const placeOverlayAtPlayhead = overlay => {
    if (!overlay) return;
    const magneticPlayhead = resolveMagneticTimelineTime(previewTimelineTime);
    const playhead = clampNumber(magneticPlayhead.time, 0, liveTimelineDuration, 0);
    updateOverlayTimeRange(overlay.id, playhead, overlay.duration || 3);
    setActiveOverlayId(overlay.id);
    jumpToOutputTimelineTime(playhead);
    setStudioActionMessage(
      `${overlay.type === "image" ? "Image" : "B-roll"} moved to ${formatPreviewTimePrecise(playhead)}${magneticPlayhead.snapped ? " and snapped to the music beat" : ""}.`
    );
  };

  const previewOverlayInTimeline = overlay => {
    if (!overlay) return;
    setSoloPreviewOverlayId(null);
    setComparisonMode("after");
    seekLiveEditTimelineItem(Number(overlay.startTime || 0), "broll", overlay.id);
    window.requestAnimationFrame(() => {
      previewPlaybackIntentRef.current = true;
      safePlayMediaElement(videoRef.current);
    });
    setStudioActionMessage(
      `Playing the complete ${overlay.type === "image" ? "image insert" : "B-roll cutaway"} in context from ${formatPreviewTimePrecise(overlay.startTime)}.`
    );
  };

  const toggleSoloOverlayPreview = overlay => {
    if (!overlay || overlay.type !== "video") return;
    const media = overlayMediaRefsRef.current.get(String(overlay.id));
    if (soloPreviewOverlayId === overlay.id) {
      media?.pause();
      setSoloPreviewOverlayId(null);
      setStudioActionMessage("B-roll-only preview paused. The source remains paused.");
      return;
    }

    pauseSynchronizedPreview();
    jumpToOutputTimelineTime(Number(overlay.startTime || 0));
    setSoloPreviewOverlayId(overlay.id);
    window.requestAnimationFrame(async () => {
      const selectedMedia = overlayMediaRefsRef.current.get(String(overlay.id));
      if (!selectedMedia) {
        setSoloPreviewOverlayId(null);
        setStudioActionMessage("Move to the cutaway cue, then try B-roll-only preview again.");
        return;
      }
      try {
        selectedMedia.currentTime = clampAudioControl(
          overlay.sourceStartTime,
          0,
          selectedMedia.duration || 36000,
          0
        );
      } catch (error) {
        console.log("B-roll-only seek skipped", error);
      }
      selectedMedia.muted = false;
      selectedMedia.defaultMuted = false;
      selectedMedia.volume = clampAudioControl(overlay.overlayAudioVolume, 0, 1, 0.7);
      const didPlay = await safePlayMediaElement(selectedMedia);
      if (!didPlay) {
        setSoloPreviewOverlayId(null);
        setStudioActionMessage(
          "B-roll-only preview could not start. Press play again after the media finishes loading."
        );
      }
    });
    setStudioActionMessage(
      "B-roll-only preview is playing. The original video and original audio are paused."
    );
  };

  const selectBRollOverlay = overlay => {
    if (!overlay) return;
    setOverlays(prev =>
      prev.map(item =>
        item.id === overlay.id && item.type === "video"
          ? {
              ...item,
              bRollMode: "fullscreen",
              coverMainVideo: true,
              x: 50,
              y: 50,
              width: 100,
              height: 100,
            }
          : item
      )
    );
    setActiveOverlayId(overlay.id);
  };

  const toggleBackgroundSoundPreview = () => {
    const music = musicPreviewRef.current;
    if (!addMusic || !effectiveMusicPreviewUrl || !music) {
      toast.error("Upload a background sound first.");
      return;
    }

    if (isBackgroundSoundPreviewing || !music.paused) {
      isolatedMusicPreviewIntentRef.current = false;
      backgroundSoundPreviewSuppressedRef.current = true;
      stopMusicPreviewBufferPlayback();
      music.pause();
      setIsBackgroundSoundPreviewing(false);
      setMusicPreviewStatusMessage(
        `Background sound paused: ${musicTrack?.name || currentMusicLabel}.`
      );
      return;
    }

    backgroundSoundPreviewSuppressedRef.current = false;
    isolatedMusicPreviewIntentRef.current = true;
    stopMusicPreviewBufferPlayback();

    const sourceApplied = applySafeMediaSource(music, effectiveMusicPreviewUrl);
    if (!sourceApplied) {
      toast.error("This background sound cannot be previewed.");
      setMusicPreviewStatus("failed");
      setMusicPreviewStatusMessage("Background sound source could not be loaded.");
      return;
    }
    if (music.readyState === 0) {
      music.load();
    }
    if (previewMuted) {
      setPreviewMuted(false);
    }

    music.muted = false;
    music.defaultMuted = false;
    const video = videoRef.current;
    const previewTimelineTime = video
      ? clampAudioControl(getPreviewTimelineTime(video.currentTime || 0), 0, 36000, 0)
      : 0;
    const fadeGain = studioMusicFadeGain(
      previewTimelineTime,
      outputTimelineDuration,
      musicTrack?.fadeIn,
      musicTrack?.fadeOut
    );
    music.loop = musicTrack?.loop !== false;
    music.volume =
      clampAudioControl(musicTrack?.volume ?? musicVolume, 0, 0.6, 0.15) *
      clampAudioControl(previewVolume, 0, 1, 1) *
      fadeGain;

    const musicDuration = Number(music.duration || 0);
    const targetTime =
      Number.isFinite(musicDuration) && musicDuration > 0.25
        ? musicTrack?.loop !== false
          ? previewTimelineTime % musicDuration
          : Math.min(previewTimelineTime, Math.max(0, musicDuration - 0.05))
        : previewTimelineTime;

    if (Number.isFinite(targetTime) && music.readyState >= 1) {
      try {
        music.currentTime = targetTime;
      } catch (error) {
        console.log("Background sound preview seek skipped", error);
      }
    }

    if (video?.paused) {
      safePlayMediaElement(video);
    }

    const playResult = music.play();
    // Reflect the user's play action immediately. Relying only on the media
    // element's `play` event can leave the inspector stuck on "Preview sound"
    // in browsers that delay or omit that event for object URLs.
    setIsBackgroundSoundPreviewing(true);
    setMusicPreviewStatus("ready");
    setMusicPreviewNeedsGesture(false);
    setMusicPreviewStatusMessage(
      `Background sound is playing: ${musicTrack?.name || currentMusicLabel}.`
    );
    if (playResult && typeof playResult.catch === "function") {
      playResult.catch(error => {
        setIsBackgroundSoundPreviewing(false);
        setMusicPreviewNeedsGesture(true);
        setMusicPreviewStatus("failed");
        setMusicPreviewStatusMessage(
          error?.message || "Background sound playback was blocked by the browser."
        );
        toast.error("Background sound could not play. Click Preview Sound again.");
      });
    }
  };

  const buildBRollOverlay = suggestion => {
    const style = BROLL_SHOT_STYLES[suggestion?.style] || BROLL_SHOT_STYLES.detail;
    const title = normalizePlainText(suggestion?.title || style.title);
    const subtitle = normalizePlainText(suggestion?.subtitle || style.subtitle);
    const kicker = normalizePlainText(suggestion?.kicker || style.kicker);

    return {
      id: createSecureId("overlay"),
      type: "text",
      text: [kicker, title, subtitle].filter(Boolean).join(" | "),
      x: 50,
      y: 50,
      color: "#ffffff",
      bg: style.bg,
      scale: 1,
      isRainbow: false,
      startTime: suggestion.time,
      duration: suggestion.duration,
      clipId: timeline[activeTimelineIndex]?.id || "main",
      bRollMode: "fullscreen",
      bRollPlaceholder: true,
      bRollTone: style.tone,
      bRollKicker: kicker,
      bRollTitle: title,
      bRollSubtitle: subtitle,
      bRollConcept: suggestion.concept || null,
      bRollSearchQuery: normalizePlainText(suggestion.searchQuery || ""),
      bRollEvidenceQuote: normalizePlainText(suggestion.evidenceQuote || ""),
      bRollCaptionSegmentId: suggestion.captionSegmentId || null,
      bRollApprovalStatus: suggestion.approvalStatus || "proposed",
      bRollConfidence: clampNumber(suggestion.confidence, 0, 1, 0),
      bRollReviewRequired: Boolean(suggestion.reviewRequired),
      animation: { enter: "zoom", exit: "fade", enterDuration: 0.22, exitDuration: 0.22 },
      opacity: 1,
      coverMainVideo: true,
      muteMainAudio: false,
      useOverlayAudio: false,
      mixAudio: false,
      overlayAudioVolume: 0.7,
      audioDucking: false,
      audioDuckingStrength: 0.35,
    };
  };

  const addBRollSuggestionOverlay = suggestion => {
    const newOverlay = buildBRollOverlay(suggestion);
    setOverlays(prev => [...prev, newOverlay]);
    setActiveOverlayId(newOverlay.id);
    toast.success(`B-roll beat added at ${suggestion.time.toFixed(1)}s`);
  };

  const getBRollStyleForClip = () => {
    const role = selectedClipGuidance?.narrativeRole?.id;
    const profile = selectedClipGuidance?.audienceProfile?.id;
    const descriptor = selectedClipGuidance?.descriptorText || getClipDescriptorText(selectedClip);

    if (
      role === "payoff" ||
      /(before|after|result|reveal|transformation|outcome)/i.test(descriptor)
    ) {
      return "payoff";
    }
    if (
      role === "reaction" ||
      profile === "reaction" ||
      /(laugh|shock|face|reaction|crowd)/i.test(descriptor)
    ) {
      return "reaction";
    }
    if (
      role === "proof" ||
      profile === "product" ||
      /(proof|demo|screen|example|product|receipt)/i.test(descriptor)
    ) {
      return "proof";
    }
    return "detail";
  };

  const getBRollSuggestionCopy = styleKey => {
    const style = BROLL_SHOT_STYLES[styleKey] || BROLL_SHOT_STYLES.detail;
    const hook = normalizePlainText(selectedClip?.hookText || selectedClipGuidance?.hookText || "");
    const descriptor = selectedClipGuidance?.descriptorText || getClipDescriptorText(selectedClip);
    const token =
      selectedClipGuidance?.semanticTokens?.[0] || getSemanticTokens(descriptor)[0] || "";

    if (hook && !isGenericHookText(hook)) {
      return {
        ...style,
        title: hook.slice(0, 34),
        subtitle: style.subtitle,
      };
    }

    if (token) {
      return {
        ...style,
        title: `${style.title}: ${token}`.slice(0, 34),
        subtitle: style.subtitle,
      };
    }

    return style;
  };

  // ── B-roll suggestion engine ──
  const suggestBRollMoments = () => {
    if (!selectedClip || !currentTimelineWindow.duration) return [];
    const cadence = BROLL_CADENCE_PRESETS[bRollCadence] || BROLL_CADENCE_PRESETS.balanced;
    return buildTranscriptGroundedBRollSuggestions({
      captionSegments,
      sourceStart: currentTimelineWindow.start,
      sourceEnd: currentTimelineWindow.end,
      maxBeats: cadence.maxBeats,
    });
  };

  const isBRollSuggestionCovered = (suggestion, overlayList = overlays) =>
    overlayList.some(overlay => {
      if (
        !overlay.bRollMode ||
        overlay.bRollPlaceholder ||
        overlay.startTime === undefined ||
        !["video", "image"].includes(overlay.type)
      )
        return false;

      const overlayStart = Number(overlay.startTime || 0);
      const overlayDuration = Math.max(0, Number(overlay.duration || 0));
      const overlayEnd = overlayStart + overlayDuration;
      const suggestionTime = Number(suggestion.time || 0);
      const coverageTolerance = 0.35;

      return (
        suggestionTime >= overlayStart - coverageTolerance &&
        suggestionTime <= overlayEnd + coverageTolerance
      );
    });

  const isBRollSuggestionPlanned = (suggestion, overlayList = overlays) =>
    overlayList.some(
      overlay =>
        overlay.bRollPlaceholder &&
        String(overlay.bRollCaptionSegmentId || "") === String(suggestion.captionSegmentId || "") &&
        String(overlay.bRollConcept || "") === String(suggestion.concept || "")
    );

  const getBRollPlanStatus = () => {
    const suggestions = suggestBRollMoments();
    const coveredSuggestions = suggestions.filter(suggestion =>
      isBRollSuggestionCovered(suggestion)
    );
    const plannedSuggestions = suggestions.filter(suggestion =>
      isBRollSuggestionPlanned(suggestion)
    );
    const missingSuggestions = suggestions.filter(
      suggestion => !isBRollSuggestionCovered(suggestion) && !isBRollSuggestionPlanned(suggestion)
    );

    return {
      suggestions,
      coveredCount: coveredSuggestions.length,
      plannedCount: plannedSuggestions.length,
      missingSuggestions,
    };
  };

  const bRollPlanStatus = getBRollPlanStatus();

  const searchLicensedStoryVisuals = async suggestions => {
    const searchableSuggestions = (suggestions || []).filter(
      suggestion => !suggestion.reviewRequired && suggestion.searchQuery && suggestion.evidenceQuote
    );
    if (!searchableSuggestions.length) {
      setStoryVisualSearchStatus("blocked");
      setStoryVisualSearchMessage(
        "Review the marked transcript lines before searching for matching footage."
      );
      return;
    }

    setStoryVisualSearchStatus("processing");
    setStoryVisualSearchMessage("Finding licensed moving footage for the exact spoken beats…");
    try {
      let token = await getMediaAuthToken();
      if (!token) throw new Error("Please log in before searching for story footage.");
      const requestBody = JSON.stringify({
        max_candidates: 3,
        beats: searchableSuggestions.map(suggestion => ({
          id: suggestion.id,
          start: suggestion.time,
          end: suggestion.time + suggestion.duration,
          search_query: suggestion.searchQuery,
          evidence_quote: suggestion.evidenceQuote,
          review_required: Boolean(suggestion.reviewRequired),
        })),
      });
      let response = await fetch(`${API_BASE_URL}/api/media/story-visuals/plan`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: requestBody,
      });
      if (response.status === 401) {
        token = await getMediaAuthToken(true);
        response = await fetch(`${API_BASE_URL}/api/media/story-visuals/plan`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: requestBody,
        });
      }
      const payload = await response.json().catch(() => null);
      if (!response.ok) {
        throw new Error(payload?.message || payload?.detail || "Moving-footage search failed.");
      }
      const beatResults = Array.isArray(payload?.beats) ? payload.beats : [];
      setStoryVisualCandidates(previous => ({
        ...previous,
        ...Object.fromEntries(beatResults.map(result => [String(result.beat_id), result])),
      }));
      const readyCount = beatResults.filter(result => result.candidates?.length).length;
      setStoryVisualSearchStatus(readyCount ? "ready" : "empty");
      setStoryVisualSearchMessage(
        readyCount
          ? `${readyCount} story ${readyCount === 1 ? "beat has" : "beats have"} licensed moving choices. Preview and approve the footage that fits.`
          : "No strong moving-footage match was found. Upload your own footage or refine the transcript evidence."
      );
    } catch (error) {
      setStoryVisualSearchStatus("failed");
      setStoryVisualSearchMessage(error?.message || "Moving-footage search failed.");
      toast.error(error?.message || "Moving-footage search failed.");
    }
  };

  const approveLicensedStoryVisual = (suggestion, candidate) => {
    const safeVideoUrl = getSafeMediaSource(candidate?.video_url);
    if (!safeVideoUrl) {
      toast.error("This moving-footage source is not safe to use.");
      return;
    }
    const sourceDuration = Math.max(0, Number(candidate?.duration || 0));
    const plannedDuration = Math.max(0.4, Number(suggestion?.duration || 1.6));
    const approvedOverlay = {
      ...buildVideoBRollOverlay(
        null,
        safeVideoUrl,
        Number(suggestion?.time || 0),
        plannedDuration,
        sourceDuration
      ),
      name: `${normalizePlainText(suggestion?.title || "Story visual")} · ${normalizePlainText(candidate?.creator || "Licensed creator")}`,
      isLocal: false,
      bRollConcept: suggestion?.concept || null,
      bRollSearchQuery: normalizePlainText(suggestion?.searchQuery || ""),
      bRollEvidenceQuote: normalizePlainText(suggestion?.evidenceQuote || ""),
      bRollCaptionSegmentId: suggestion?.captionSegmentId || null,
      bRollApprovalStatus: "creator_approved",
      bRollConfidence: clampNumber(suggestion?.confidence, 0, 1, 0),
      bRollReviewRequired: false,
      bRollProvider: normalizePlainText(candidate?.provider || "pexels"),
      bRollSourcePage: getSafeMediaSource(candidate?.source_page) || "",
      bRollLicense: normalizePlainText(candidate?.license || "Pexels License"),
      bRollCreator: normalizePlainText(candidate?.creator || "Licensed creator"),
      bRollSourceId: normalizePlainText(candidate?.source_id || candidate?.id || ""),
    };
    setOverlays(previous => {
      let replacedPlaceholder = false;
      const next = previous.flatMap(overlay => {
        const matchesBeat =
          overlay.bRollPlaceholder &&
          String(overlay.bRollCaptionSegmentId || "") ===
            String(suggestion?.captionSegmentId || "") &&
          String(overlay.bRollConcept || "") === String(suggestion?.concept || "");
        if (matchesBeat) {
          replacedPlaceholder = true;
          return [approvedOverlay];
        }
        return [overlay];
      });
      return replacedPlaceholder ? next : [...next, approvedOverlay];
    });
    setActiveOverlayId(approvedOverlay.id);
    setComparisonMode("after");
    jumpToOutputTimelineTime(Number(approvedOverlay.startTime || 0));
    setStudioActionMessage(
      `Approved licensed moving footage for “${suggestion?.title || "story beat"}”. It fills the frame while the original programme audio continues.`
    );
    toast.success("Moving story visual approved and placed.");
  };

  const addBRollPlan = async () => {
    const { suggestions, coveredCount, plannedCount, missingSuggestions } = getBRollPlanStatus();
    if (!suggestions.length) {
      setStudioInspectorTab("captions");
      setStudioActionMessage(
        "No literal B-roll evidence is available yet. Generate and review the transcript first; the studio will not invent evenly spaced visuals."
      );
      toast("Review captions before planning story visuals.");
      return;
    }

    if (!missingSuggestions.length) {
      setStudioActionMessage(
        coveredCount === suggestions.length
          ? `All ${coveredCount} grounded B-roll ${coveredCount === 1 ? "beat is" : "beats are"} covered by real footage.`
          : `${plannedCount} grounded ${plannedCount === 1 ? "beat is" : "beats are"} waiting for matching moving footage. No duplicate placeholders were added.`
      );
      toast(
        coveredCount === suggestions.length
          ? "B-roll coverage is complete."
          : "Story beats are already planned."
      );
      return;
    }

    const plannedOverlays = missingSuggestions.map((suggestion, index) => ({
      ...buildBRollOverlay(suggestion),
      id: `${createSecureId("overlay")}-plan-${index}-${Math.round(suggestion.time * 10)}`,
    }));
    setOverlays(prev => [...prev, ...plannedOverlays]);
    setActiveOverlayId(plannedOverlays[0]?.id || null);
    setStudioActionMessage(
      `${plannedOverlays.length} transcript-grounded B-roll ${plannedOverlays.length === 1 ? "beat" : "beats"} planned. ${coveredCount ? `${coveredCount} ${coveredCount === 1 ? "beat already has" : "beats already have"} real footage. ` : ""}Each open beat shows its exact spoken evidence and search query; unresolved placeholders cannot be rendered.`
    );
    toast.success(
      `${plannedOverlays.length} B-roll ${plannedOverlays.length === 1 ? "beat" : "beats"} added to the plan.`
    );
    await searchLicensedStoryVisuals(missingSuggestions);
  };

  // --- Dragging Logic ---
  const handleMouseMove = e => {
    if (!isDragging) return;

    // Support both mouse and touch events
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;

    // We must track mouse relative to the PHONE FRAME, not the window or element
    const container = phoneFrameRef.current || e.currentTarget;
    const bounds = container.getBoundingClientRect();

    // Calculate mouse position relative to container
    const relativeX = clientX - bounds.left;
    const relativeY = clientY - bounds.top;

    // Convert to percentage (0-100)
    let percentX = (relativeX / bounds.width) * 100;
    let percentY = (relativeY / bounds.height) * 100;

    // Clamp to boundaries (0-100)
    percentX = Math.max(0, Math.min(100, percentX));
    percentY = Math.max(0, Math.min(100, percentY));

    if (watermarkDragRef.current) {
      const interaction = watermarkDragRef.current;
      setManualWatermarkRegions(prev =>
        prev.map(region => {
          if (region.id !== interaction.id) return region;

          if (interaction.mode === "resize") {
            const deltaX = percentX - Number(interaction.startX || 0);
            const deltaY = percentY - Number(interaction.startY || 0);
            const direction = interaction.direction || "bottom-right";
            const nextRegion = { ...region };

            if (direction.includes("left")) {
              nextRegion.left = Number(interaction.initialLeft || region.left || 0) + deltaX;
              nextRegion.width = Number(interaction.initialWidth || region.width || 24) - deltaX;
            } else {
              nextRegion.width = Number(interaction.initialWidth || region.width || 24) + deltaX;
            }

            if (direction.includes("top")) {
              nextRegion.top = Number(interaction.initialTop || region.top || 0) + deltaY;
              nextRegion.height = Number(interaction.initialHeight || region.height || 8) - deltaY;
            } else {
              nextRegion.height = Number(interaction.initialHeight || region.height || 8) + deltaY;
            }

            return clampManualWatermarkRegion({
              ...nextRegion,
            });
          }

          return clampManualWatermarkRegion({
            ...region,
            left: percentX - Number(interaction.offsetX || 0),
            top: percentY - Number(interaction.offsetY || 0),
          });
        })
      );
      return;
    }

    if (!dragItem.current) return;

    const localPoint = studioGroupLocalPoint(compositionGroups, dragItem.current,
      (relativeX / bounds.width) * 100, (relativeY / bounds.height) * 100, bounds.width, bounds.height,
      motionKeyframes, previewTimelineTime);
    percentX = Math.max(0, Math.min(100, localPoint.x));
    percentY = Math.max(0, Math.min(100, localPoint.y));

    setOverlays(prev => {
      const currentOverlay = prev.find(o => o.id === dragItem.current);
      if (!currentOverlay) return prev;

      const currentX = Number(currentOverlay.x ?? 50);
      const currentY = Number(currentOverlay.y ?? 50);
      if (Math.abs(currentX - percentX) < 0.1 && Math.abs(currentY - percentY) < 0.1) {
        return prev;
      }

      return prev.map(o =>
        o.id === dragItem.current ? clampOverlayPlacement({ ...o, x: percentX, y: percentY }) : o
      );
    });
  };

  const handleDragStart = (e, overlay) => {
    e.stopPropagation(); // Prevent video click
    e.preventDefault(); // Prevent browser native drag
    setActiveWatermarkRegionId(null);
    setActiveOverlayId(overlay.id);
    if (overlay.bRollMode === "fullscreen") {
      return;
    }
    setIsDragging(true);
    dragItem.current = overlay.id;
  };

  const handleWatermarkDragStart = (e, region) => {
    e.stopPropagation();
    e.preventDefault();

    const frameBounds = phoneFrameRef.current?.getBoundingClientRect();
    if (!frameBounds) return;

    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    const percentX = ((clientX - frameBounds.left) / frameBounds.width) * 100;
    const percentY = ((clientY - frameBounds.top) / frameBounds.height) * 100;

    setActiveOverlayId(null);
    setActiveWatermarkRegionId(region.id);
    setIsDragging(true);
    watermarkDragRef.current = {
      mode: "move",
      id: region.id,
      offsetX: percentX - Number(region.left || 0),
      offsetY: percentY - Number(region.top || 0),
    };
  };

  const handleWatermarkResizeStart = (e, region, direction = "bottom-right") => {
    e.stopPropagation();
    e.preventDefault();

    const frameBounds = phoneFrameRef.current?.getBoundingClientRect();
    if (!frameBounds) return;

    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    const percentX = ((clientX - frameBounds.left) / frameBounds.width) * 100;
    const percentY = ((clientY - frameBounds.top) / frameBounds.height) * 100;

    setActiveOverlayId(null);
    setActiveWatermarkRegionId(region.id);
    setIsDragging(true);
    watermarkDragRef.current = {
      mode: "resize",
      direction,
      id: region.id,
      startX: percentX,
      startY: percentY,
      initialLeft: Number(region.left || 0),
      initialTop: Number(region.top || 0),
      initialWidth: Number(region.width || 24),
      initialHeight: Number(region.height || 8),
    };
  };

  const handleDragEnd = () => {
    setIsDragging(false);
    dragItem.current = null;
    watermarkDragRef.current = null;
  };

  const studioWorkflowStage = isExporting
    ? 3
    : overlays.some(overlay => overlay.bRollMode)
      ? 2
      : addHook
        ? 1
        : 0;

  const normalizedMediaBinSearch = normalizePlainText(mediaBinSearch).toLowerCase();
  const matchingProjectMedia = projectMedia.filter(item =>
    normalizePlainText(item.name || "").toLowerCase().includes(normalizedMediaBinSearch)
  );
  const visibleProjectMedia = matchingProjectMedia.slice(0, mediaBinVisibleCount);
  const selectedProjectMedia = projectMedia.find(item => item.id === selectedProjectMediaId) || null;
  const pendingProjectMediaCount = projectMedia.filter(item =>
    item.status === "queued" || item.status === "uploading"
  ).length;
  const activeFilmTake = angleGroups.find(group => group.id === activeAngleGroupId) || null;
  const activeFilmTakeAssets = activeFilmTake?.angles
    .map(angle => projectMedia.find(item => item.id === angle.assetId))
    .filter(Boolean) || [];
  const activeFilmTakeRange = getStudioAngleSharedRange(activeFilmTake, activeFilmTakeAssets);

  return createPortal(
    <div className="viral-studio-overlay ap-dashboard-redesign">
      <StudioVoiceoverPreview ref={preparedVoiceBusRef} takes={voiceovers} videoRef={videoRef}
        getTimelineTime={getSoundEffectProgrammeTime} activeClip={activeTimelineIndex}
        getAudioContext={ensureSoundEffectAudioContext}
        projectId={projectId} outputTimeMap={previewOutputTimeMap}
        loadPreparedBus={!addHook && !silenceRemoval && !speakerStackPreviewIsLive
          ? (input, context, signal) => loadStudioVoiceBus({ input, context, signal,
            apiBase: API_BASE_URL, getToken: getMediaAuthToken }) : undefined}
        onPreparation={status => {
          if (status === 'loading') {
            if (previewPlaybackIntentRef.current) pauseSynchronizedPreview();
            setStudioActionMessage('Preparing recorded speech for this edit…');
          } else if (status === 'ready') setStudioActionMessage('Recorded-speech preview is ready.');
        }}
        preparedDucking={{ enabled: !!(musicTrack?.ducking && musicTrack.duckingMode === 'speech' &&
          muteOriginalAudio && voiceoverTrackAudible(trackStates)), strength: musicTrack?.duckingStrength ?? .4 }}
        onPreparedDucking={value => {
          preparedSpeechDuckRef.current = value; preparedSpeechDuckClockRef.current = '';
          const control = preparedSpeechDuckSourceRef.current;
          if (control) { try { control.stop(); } catch (_) {} control.disconnect(); preparedSpeechDuckSourceRef.current = null; }
          const param = musicPreviewDuckGainNodeRef.current?.gain;
          if (param) { param.cancelScheduledValues(musicPreviewAudioContextRef.current.currentTime); param.value = 1; }
          if (value) syncMusicPreviewGain();
        }}
        getOutputPlan={(context, programmeTime) => {
          const state = musicPreviewSourceRef.current && musicPreviewSourceStateRef.current;
          if (addMusic && programmeMusicRouteRef.current.decoded && !state) return null;
          const when = Math.max(context.currentTime + .01, state?.startedAt || 0);
          const offset = state ? musicBufferPosition(state, when) : getPreviewMusicTime(programmeTime) + .01;
          return { when, offset: Math.round(offset*context.sampleRate)/context.sampleRate };
        }}
        muted={previewMuted || !voiceoverTrackAudible(trackStates)} volume={previewVolume}
        automationKeyframes={audioKeyframes.voiceover}
        onAnalyser={analyser => { voiceoverSpeechAnalyserRef.current = analyser; }}
        renderedOutputUrl={renderedOutputUrl} onError={setStudioActionMessage} />
      <div
        className={`viral-studio-container hook-broll-only-mode studio-mode-${workspaceMode} ${
          projectRailExpanded ? "project-rail-open" : "project-rail-closed"
        } ${timelineDockExpanded ? "timeline-dock-open" : "timeline-dock-closed"}`}
        data-testid="viral-studio-workspace"
        data-workspace-mode={workspaceMode}
      >
        <ViralStudioHeader
          projectTitle={normalizePlainText(projectName || "Untitled viral edit").slice(0, 52)}
          clipFinderCost={clipFinderCost}
          clipRenderCost={clipRenderCost}
          transcribeCost={transcribeCost}
          creditsRemaining={credits?.monthlyRemaining ?? 0}
          momentsCount={orderedClips.length}
          brollCount={overlays.filter(overlay => overlay.bRollMode).length}
          hookEnabled={addHook}
          workspaceMode={workspaceMode}
          onWorkspaceModeChange={changeWorkspaceMode}
          canUndo={canUndo}
          onUndo={handleUndo}
          canRedo={canRedo}
          onRedo={handleRedo}
          onClose={() => {
            if (
              projectSaveState === "saved" ||
              window.confirm("Close the studio? Unsaved changes will be lost.")
            )
              onCancel();
          }}
        />
        {(!renderedOutput?.url || renderRecoveryNeedsAttention) && renderRecoveryPanel}

        {commandPaletteOpen ? (
          <div className="studio-command-backdrop" onMouseDown={() => setCommandPaletteOpen(false)}>
            <section
              className="studio-command-palette"
              role="dialog"
              aria-modal="true"
              aria-label="Find an editing tool"
              onMouseDown={event => event.stopPropagation()}
            >
              <div className="studio-command-search">
                <span>⌘K</span>
                <input
                  autoFocus
                  type="search"
                  placeholder="Find a tool, effect or workflow…"
                  value={commandSearch}
                  onChange={event => setCommandSearch(event.target.value)}
                />
                <button type="button" onClick={() => setCommandPaletteOpen(false)}>
                  Esc
                </button>
              </div>
              <div className="studio-command-results">
                  {[
                  ...CREATIVE_STUDIO_TOOLS.map(tool => ({
                    id: tool.id,
                    label: tool.label,
                    helper: `Open the ${tool.label} workspace`,
                    action: () => selectCreativeTool(tool.id),
                  })),
                  {
                    id: "add-title",
                    label: "Add headline at playhead",
                    helper: "Create an independent animated title layer",
                    action: () => addTextOverlay("headline"),
                  },
                  {
                    id: "add-adjustment",
                    label: "Add adjustment layer",
                    helper: "Apply a finish across the tracks below",
                    action: addAdjustmentLayer,
                  },
                  {
                    id: "save-project",
                    label: "Save project checkpoint",
                    helper: "Store the editable timeline and a restorable version",
                    action: () => void persistProjectSnapshot({ checkpoint: true }),
                  },
                  {
                    id: "record-voiceover",
                    label: "Record voice-over",
                    helper: "Place a microphone take at the playhead",
                    action: () => void toggleVoiceoverRecording(),
                  },
                ]
                  .filter(command =>
                    `${command.label} ${command.helper}`
                      .toLowerCase()
                      .includes(commandSearch.trim().toLowerCase())
                  )
                  .map(command => (
                    <button
                      key={command.id}
                      type="button"
                      onClick={() => {
                        command.action();
                        setCommandPaletteOpen(false);
                        setCommandSearch("");
                      }}
                    >
                      <span>›</span>
                      <strong>{command.label}</strong>
                      <small>{command.helper}</small>
                    </button>
                  ))}
              </div>
              <footer>
                <span>↑↓ Navigate</span>
                <span>Enter Open</span>
                <strong>Every advanced tool remains searchable</strong>
              </footer>
            </section>
          </div>
        ) : null}

        <div
          className={`studio-layout preview-layout-${previewDockSide} ${
            comparisonMode === "split" ? "has-split-preview" : "has-single-preview"
          }`}
        >
          <CreativeToolRail
            tools={CREATIVE_STUDIO_TOOLS}
            mode={workspaceMode}
            activeTool={activeCreativeTool}
            onSelect={selectCreativeTool}
          />
          <aside className="studio-project-rail" aria-label="Project navigator" data-active-tab={projectRailTab}>
            <button type="button" className="studio-media-close" onClick={() => setProjectRailExpanded(false)}>
              Close media ×
            </button>
            <details className="studio-project-rail__head">
              <summary title={projectName}>{projectName || "Untitled project"}</summary>
              <span>Project</span>
              <label className="studio-project-name-field">
                <span className="sr-only">Project name</span>
                <input
                  aria-label="Project name"
                  disabled={projectSaveState === "saving"}
                  value={projectName}
                  maxLength={80}
                  onChange={event => {
                    setProjectName(event.target.value);
                    setProjectSaveState("unsaved");
                  }}
                />
              </label>
              <div className="studio-project-actions">
                <button
                  type="button"
                  className="is-primary"
                  onClick={() => void persistProjectSnapshot({ checkpoint: true })}
                  disabled={projectSaveState === "saving"}
                  data-testid="studio-save-project"
                >
                  {projectSaveState === "saving" ? "Saving…" : "Save project"}
                </button>
                <button
                  type="button"
                  onClick={() => void persistProjectSnapshot({ duplicate: true })}
                >
                  Duplicate
                </button>
              <button type="button" className="studio-project-backup" disabled={projectSaveState === "saving"}
                onClick={async () => {
                  const saved = await persistProjectSnapshot();
                  if (!saved) return;
                  // Export only this ordinary editable record. Authentication,
                  // other projects and render-recovery session state stay out.
                  let backup;
                  try { backup = await packageStudioBackup(saved, { includeVersions: backupIncludeVersions }); }
                  catch (error) { setStudioActionMessage(error.message); return; }
                  const url = URL.createObjectURL(backup);
                  const link = document.createElement("a");
                  link.href = url;
                  link.download = `${saved.name.replace(/[^a-zA-Z0-9_-]+/g, "-")}.autopromote.json`;
                  document.body.appendChild(link); link.click(); link.remove();
                  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
                }}>Download project backup</button>
              </div>
              <label style={{ display: "flex", alignItems: "center", gap: "6px", fontSize: "11px", marginTop: "5px" }}>
                <input type="checkbox" checked={backupIncludeVersions}
                  onChange={event => setBackupIncludeVersions(event.target.checked)} />
                Include saved versions in backup
              </label>
              <small className={`studio-project-save-state is-${projectSaveState}`}>
                {projectSaveState === "saved"
                  ? `Saved locally${projectSavedAt ? ` · ${new Date(projectSavedAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}` : ""}`
                  : projectSaveState === "saving"
                    ? "Saving the editable project…"
                    : projectSaveState === "error"
                      ? "Local save failed — retry"
                      : "Unsaved changes · autosave pending"}
              </small>
              {savedProjects.length ? (
                <details className="studio-saved-projects">
                  <summary>Open saved project ({savedProjects.length})</summary>
                  <div>
                    {savedProjects.map(project => (
                      <article
                        key={project.id}
                        className={project.id === projectId ? "is-current" : ""}
                      >
                        <button type="button" onClick={() => openSavedProject(project)}>
                          <strong>{project.name || "Untitled viral edit"}</strong>
                          <small>{new Date(project.updatedAt).toLocaleString()}</small>
                        </button>
                        {project.versions?.length ? (
                          <button
                            type="button"
                            className="studio-project-version"
                            aria-label={`Restore previous version of ${project.name || "saved project"}`}
                            title={`Restore ${project.versions[project.versions.length - 1].name}`}
                            onClick={() =>
                              restoreProjectVersion(
                                project,
                                project.versions[project.versions.length - 1]
                              )
                            }
                          >
                            ↶{project.versions.length}
                          </button>
                        ) : null}
                        <button
                          type="button"
                          className="studio-project-delete"
                          aria-label={`Delete ${project.name || "saved project"}`}
                          onClick={() => void removeSavedProject(project)}
                        >
                          ×
                        </button>
                      </article>
                    ))}
                  </div>
                </details>
              ) : null}
            </details>

            <div className="studio-library-tabs" role="tablist" aria-label="Project library">
              {[["media", "Media"], ["sequence", "Sequence"], ["moments", "Moments"]].map(([id, label]) => (
                <button key={id} type="button" role="tab" aria-selected={projectRailTab === id}
                  id={`studio-library-tab-${id}`} aria-controls={`studio-library-panel-${id}`}
                  tabIndex={projectRailTab === id ? 0 : -1}
                  onKeyDown={event => {
                    const ids = ["media", "sequence", "moments"];
                    const current = ids.indexOf(id);
                    const next = event.key === "ArrowRight" ? (current + 1) % ids.length
                      : event.key === "ArrowLeft" ? (current + ids.length - 1) % ids.length
                        : event.key === "Home" ? 0 : event.key === "End" ? ids.length - 1 : null;
                    if (next === null) return;
                    event.preventDefault();
                    event.stopPropagation();
                    setProjectRailTab(ids[next]);
                    event.currentTarget.parentElement.querySelector(`#studio-library-tab-${ids[next]}`).focus();
                  }}
                  onClick={() => setProjectRailTab(id)}>{label}</button>
              ))}
            </div>
            <nav className="studio-workflow-nav" aria-label="Clip Studio workflow" hidden>
              <div className="studio-workflow-nav__intro">
                <span>Editing route</span>
                <strong>Moment to finished short</strong>
              </div>
              <ol>
                {VIRAL_STUDIO_WORKFLOW.map((step, index) => {
                  const state =
                    index < studioWorkflowStage
                      ? "is-complete"
                      : index === studioWorkflowStage
                        ? "is-active"
                        : "is-pending";
                  return (
                    <li key={step.label} className={state}>
                      <span>{index < studioWorkflowStage ? "✓" : index + 1}</span>
                      <div>
                        <strong>{step.label}</strong>
                        <small>{step.helper}</small>
                      </div>
                    </li>
                  );
                })}
              </ol>
              <div className="studio-workflow-nav__status">
                <i aria-hidden="true" />
                <span>
                  <strong>Live preview</strong>
                  <small>Changes stay local until render</small>
                </span>
              </div>
            </nav>

            <section className="studio-project-list" id="studio-library-panel-sequence" role="tabpanel" aria-labelledby="studio-library-tab-sequence" hidden={projectRailTab !== "sequence"}>
              <div className="studio-project-list__heading">
                <div>
                  <span>Sequence</span>
                  <strong id="studio-sequence-heading">Source clips</strong>
                </div>
                <i>{timeline.length}</i>
              </div>
              <div className="studio-project-list__items">
                {timeline.map((clip, index) => (
                  <button
                    key={clip.id}
                    type="button"
                    className={`studio-project-item ${
                      activeTimelineIndex === index ? "is-active" : ""
                    }`}
                    onClick={() => setActiveTimelineIndex(index)}
                    aria-pressed={activeTimelineIndex === index}
                  >
                    <span>{String(index + 1).padStart(2, "0")}</span>
                    <div>
                      <strong>{clip.name || `Source clip ${index + 1}`}</strong>
                      <small>
                        {clip.name
                          ? `${clip.name} · ${
                              clip.duration
                                ? `${Math.round(clip.duration)} seconds`
                                : clip.startRequest
                                  ? "trimmed sequence"
                                  : "duration loading"
                            }`
                          : clip.duration
                            ? `${Math.round(clip.duration)} seconds`
                            : clip.startRequest
                              ? "Trimmed sequence"
                              : "Duration loading"}
                      </small>
                    </div>
                    <i aria-hidden="true">›</i>
                  </button>
                ))}
              </div>
            </section>

            <section className="studio-project-list" id="studio-library-panel-moments" role="tabpanel" aria-labelledby="studio-library-tab-moments" hidden={projectRailTab !== "moments"}>
              <div className="studio-project-list__heading">
                <div>
                  <span>Discovery</span>
                  <strong id="studio-moments-heading">Detected moments</strong>
                </div>
                <i>{orderedClips.length}</i>
              </div>
              <div className="studio-project-list__items studio-project-list__items--moments">
                {orderedClips.map((clip, index) => {
                  const guidance = clipGuidanceById.get(clip.id);
                  const isSelected = selectedClip?.id === clip.id;
                  return (
                    <button
                      key={clip.id}
                      type="button"
                      className={`studio-project-item studio-project-moment ${
                        isSelected ? "is-active" : ""
                      }`}
                      onClick={() => focusClipInEditor(clip, { boundary: "start", play: false })}
                      aria-pressed={isSelected}
                    >
                      <span>#{index + 1}</span>
                      <div>
                        <strong>
                          {normalizePlainText(
                            clip.hookText || clip.reason || `Moment ${index + 1}`
                          ).slice(0, 42)}
                        </strong>
                        <small>
                          {Number(clip.start || 0).toFixed(1)}s–
                          {Number(clip.end || 0).toFixed(1)}s
                        </small>
                        <em
                          className={`story-beat-tag is-${STORY_BEAT_LABELS[index % 4].toLowerCase()}`}
                        >
                          {STORY_BEAT_LABELS[index % 4]}
                        </em>
                      </div>
                      <i>{guidance?.score ?? 0}</i>
                    </button>
                  );
                })}
              </div>
            </section>

            <section className="studio-media-bin" id="studio-library-panel-media" role="tabpanel" aria-labelledby="studio-library-tab-media" hidden={projectRailTab !== "media"}>
              <div className="studio-project-list__heading">
                <div>
                  <span>Media</span>
                  <strong>Project media</strong>
                </div>
                <i>{projectMedia.length}</i>
              </div>
              <p className="studio-media-bin__help">
                Import your videos here, then choose what belongs in the edit. Sequence adds to the end; B-roll starts at the playhead.
              </p>
              <input
                ref={projectMediaInputRef}
                type="file"
                accept="video/*,.mp4,.mov,.m4v,.webm,.mkv"
                multiple
                data-testid="project-media-import-input"
                aria-label="Choose scene videos"
                style={{ display: "none" }}
                onChange={handleProjectMediaImport}
              />
              <button
                type="button"
                className="studio-media-bin__import"
                onClick={() => projectMediaInputRef.current?.click()}
              >
                ＋ Import scenes
              </button>
              <button type="button" className="studio-media-bin__import" onClick={removeUnusedProjectMedia}>Remove unused media</button>
              {pendingProjectMediaCount > 0 ? (
                <small className="studio-media-bin__pending" role="status">
                  Saving {pendingProjectMediaCount} {pendingProjectMediaCount === 1 ? "video" : "videos"}… You can keep editing.
                </small>
              ) : null}
              {mediaImportMessage ? <p className="studio-media-bin__notice" role="status">{mediaImportMessage}</p> : null}
              <input
                type="search"
                aria-label="Search project media"
                placeholder="Find a scene by filename…"
                value={mediaBinSearch}
                onChange={event => {
                  setMediaBinSearch(event.target.value);
                  setMediaBinVisibleCount(12);
                }}
              />
              {projectRailExpanded && selectedProjectMedia?.url ? (
                <details className="studio-media-bin__preview" key={selectedProjectMedia.id} aria-label="Selected project video preview">
                  <summary>Source preview · {selectedProjectMedia.name}</summary>
                  <video
                    key={`${selectedProjectMedia.id}-${selectedProjectMedia.url}`}
                    src={getSafeMediaSource(selectedProjectMedia.url) || undefined}
                    poster={selectedProjectMedia.poster || undefined}
                    controls
                    playsInline
                    preload="none"
                    onError={() => {
                      const path = selectedProjectMedia.storagePath;
                      if (path && !attemptedSourceRefreshRef.current.has(path)) {
                        attemptedSourceRefreshRef.current.add(path);
                        void refreshSourceUrlByPath(path);
                      }
                    }}
                  />
                  <strong title={selectedProjectMedia.name}>{selectedProjectMedia.name}</strong>
                  <small>Preview only · use the buttons below to place this video in your edit.</small>
                </details>
              ) : null}
              <div className="studio-media-bin__library" data-testid="project-media-library">
                {visibleProjectMedia.map(item => {
                  const ready = ["", "video"].includes(item.mediaType || item.type || "") &&
                    item.status === "ready" && !!item.storagePath && Number(item.duration) > 0;
                  const selectedAsCamera = selectedCameraMediaIds.includes(item.id);
                  return (
                    <article key={item.id} className={`studio-media-card is-${item.status || "existing"}`}>
                      <button
                        type="button"
                        className="studio-media-card__thumb"
                        aria-label={`Preview ${item.name}`}
                        aria-pressed={selectedProjectMediaId === item.id}
                        onClick={() => setSelectedProjectMediaId(item.id)}
                      >
                        {item.poster ? <img src={item.poster} alt="" loading="lazy" /> : <span aria-hidden="true">▶</span>}
                      </button>
                      <div className="studio-media-card__body">
                        <strong title={item.name}>{item.name}</strong>
                        <small>
                          {(item.mediaType || item.type) === "image" ? "Image" :
                            item.duration > 0 ? `${Number(item.duration).toFixed(1)}s` : "Duration loading"}
                          {item.isPrimary ? " · Original" : ""}
                        </small>
                        <small className={`studio-media-card__status is-${item.status || "existing"}`}>
                          {item.status === "ready" ? "Saved · ready to edit"
                            : item.status === "uploading" ? `Saving ${Number(item.progress || 0)}%`
                              : item.status === "queued" ? "Waiting to save"
                                : item.status === "failed" ? "Save failed"
                                  : item.status === "needs_upload" ? "Needs upload"
                                    : item.status === "unavailable" ? "Link needs refresh"
                                      : "In current edit"}
                        </small>
                        {timeline.some(clip => clip.sourceMediaId === item.id || (item.storagePath && (clip.storagePath || clip.sourceStoragePath) === item.storagePath)) ?
                          <small className="studio-media-card__usage">In sequence</small> : null}
                        {overlays.some(overlay => overlay.sourceMediaId === item.id) ?
                          <small className="studio-media-card__usage">Used as B-roll</small> : null}
                        {item.error ? <small className="studio-media-card__error">{item.error}</small> : null}
                      </div>
                      <div className="studio-media-card__actions" hidden={selectedProjectMediaId !== item.id && !["failed", "needs_upload", "unavailable"].includes(item.status)}>
                        {selectedProjectMediaId === item.id ? <>
                        <button type="button" onClick={() => {
                          setSelectedProjectMediaId(item.id);
                          window.requestAnimationFrame(() => {
                            const viewer = document.querySelector(".studio-media-bin__preview");
                            if (viewer) viewer.open = true;
                          });
                        }}>Preview</button>
                        <button type="button" disabled={!ready} onClick={() => appendProjectMediaToSequence(item)}>
                          Add to sequence
                        </button>
                        <button type="button" disabled={!ready} onClick={() => addProjectMediaAsBRoll(item)}>
                          Add as B-roll
                        </button>
                        <button type="button" disabled={!ready || Number(item.duration) < getTimelineClipWindow(timeline[activeTimelineIndex]).duration}
                          onClick={() => replaceActiveTimelineSource(item)}>
                          Replace selected clip
                        </button>
                        <button type="button" disabled={!ready || !Number.isFinite(Number(item.duration)) || Number(item.duration) < liveTimelineDuration || !!trackStates.video?.locked}
                          title="Replace all source clips; keep programme length, graphics and soundtrack. Source captions and camera treatments are removed."
                          onClick={() => replaceSequenceSource(item)}>Replace entire sequence</button>
                        <button
                          type="button"
                          disabled={!ready}
                          aria-pressed={selectedAsCamera}
                          onClick={() => toggleCameraMediaSelection(item.id)}
                        >
                          {selectedAsCamera ? "✓ In camera take" : "Select for camera take"}
                        </button>
                        </> : null}
                        {(item.status === "failed" || item.status === "needs_upload") && item.file instanceof Blob ? (
                          <button type="button" onClick={() => void uploadProjectMediaAsset(item)}>Retry upload</button>
                        ) : null}
                        {item.status === "unavailable" && item.storagePath ? (
                          <button type="button" onClick={() => void refreshSourceUrlByPath(item.storagePath)}>Refresh link</button>
                        ) : null}
                        {item.status === "ready" && !(Number(item.duration) > 0) ? (
                          <button type="button" onClick={() => void probeProjectMediaDuration(item)}>Retry duration</button>
                        ) : null}
                      </div>
                    </article>
                  );
                })}
                {!matchingProjectMedia.length ? (
                  <p className="studio-media-bin__empty">No videos match that filename.</p>
                ) : null}
              </div>
              {matchingProjectMedia.length > mediaBinVisibleCount ? (
                <button
                  type="button"
                  className="studio-media-bin__more"
                  onClick={() => setMediaBinVisibleCount(count => count + 12)}
                >
                  Show more videos ({matchingProjectMedia.length - mediaBinVisibleCount} left)
                </button>
              ) : null}
              <details className="studio-media-bin__camera" aria-label="Film camera takes" open={selectedCameraMediaIds.length > 0}>
                <summary>Group camera angles{selectedCameraMediaIds.length ? ` (${selectedCameraMediaIds.length} selected)` : ""}</summary>
                <strong>Camera takes for your movie</strong>
                <small>Choose 2–{MAX_STUDIO_ANGLES} videos of the same moment. Set where each starts, then choose each shot yourself. Studio will keep the cuts you place in the sequence.</small>
                <label>
                  Take name
                  <input
                    type="text"
                    value={angleGroupName}
                    placeholder={`Camera take ${angleGroups.length + 1}`}
                    onChange={event => setAngleGroupName(event.target.value)}
                  />
                </label>
                <button
                  type="button"
                  disabled={selectedCameraMediaIds.length < 2}
                  onClick={createSelectedFilmTake}
                >
                  Create camera take ({selectedCameraMediaIds.length})
                </button>
                {onOpenCameraAngles ? (
                  <button
                    type="button"
                    disabled={selectedCameraMediaIds.length < 2 || selectedCameraMediaIds.length > 3 || cameraGroupStatus === "opening"}
                    onClick={() => void openPodcastCamCombiner()}
                  >
                    {cameraGroupStatus === "opening" ? "Opening podcast editor…" : "Open Podcast Cam Combiner for automatic podcast cuts"}
                  </button>
                ) : null}
              </details>
              {angleGroups.length ? (
                <section className="studio-angle-groups" aria-label="Saved film camera takes">
                  <strong>Film camera takes</strong>
                  <div className="studio-angle-groups__tabs">
                    {angleGroups.map(group => (
                      <button
                        key={group.id}
                        type="button"
                        aria-pressed={activeAngleGroupId === group.id}
                        onClick={() => setActiveAngleGroupId(group.id)}
                      >
                        {group.name}
                      </button>
                    ))}
                  </div>
                  {activeFilmTake ? (
                    <div className="studio-angle-groups__editor">
                      <p>Pick a shared shot range. Each camera uses its own start offset, so the chosen moment stays aligned.</p>
                      <button type="button" className="studio-angle-groups__remove" onClick={() => {
                        setAngleGroups(current => current.filter(group => group.id !== activeFilmTake.id));
                        setActiveAngleGroupId(angleGroups.find(group => group.id !== activeFilmTake.id)?.id || null);
                        setMediaImportMessage("Camera take grouping removed. Shots already in your sequence stay editable.");
                      }}>Remove this take grouping</button>
                      <div className="studio-angle-groups__range">
                        <label>Shot starts at (seconds)
                          <input type="number" min="0" max={activeFilmTakeRange.end} step="0.1"
                            value={activeFilmTake.sharedStart}
                            onChange={event => updateFilmTake(activeFilmTake.id, group => ({
                              ...group,
                              sharedStart: clampNumber(event.target.value, 0, getStudioAngleSharedRange(group, projectMedia).end, 0),
                            }))} />
                        </label>
                        <label>Shot ends at (seconds)
                          <input type="number" min="0" max={activeFilmTakeRange.end} step="0.1"
                            value={activeFilmTake.sharedEnd}
                            onChange={event => updateFilmTake(activeFilmTake.id, group => ({
                              ...group,
                              sharedEnd: clampNumber(event.target.value, 0, getStudioAngleSharedRange(group, projectMedia).end, 0),
                            }))} />
                        </label>
                      </div>
                      <small>Shared footage available: 0–{activeFilmTakeRange.end.toFixed(1)} seconds</small>
                      <div className="studio-angle-groups__playback">
                        <button type="button" onClick={() => void playFilmTakePreview(activeFilmTake)}>
                          ▶ Play all angles together
                        </button>
                        <button type="button" onClick={pauseFilmTakePreview}>Pause all</button>
                      </div>
                      <div className="studio-angle-groups__angles">
                        {activeFilmTake.angles.map((angle, index) => {
                          const asset = projectMedia.find(item => item.id === angle.assetId);
                          if (!asset) return null;
                          return (
                            <article key={angle.assetId}>
                              <strong>Camera {index + 1}: {asset.name}</strong>
                              <SafeVideo
                                ref={video => {
                                  if (video) filmAnglePreviewRefsRef.current.set(asset.id, video);
                                  else filmAnglePreviewRefsRef.current.delete(asset.id);
                                }}
                                src={asset.url}
                                controls
                                playsInline
                                preload="metadata"
                                poster={asset.poster || undefined}
                                onTimeUpdate={index === 0 ? () => syncFilmTakePreview(activeFilmTake) : undefined}
                              />
                              <label>Skip from beginning (seconds)
                                <input type="number" min="0" max={Math.max(0, Number(asset.duration || 0) - 0.1)} step="0.1"
                                  value={angle.offsetSeconds}
                                  onChange={event => updateFilmTake(activeFilmTake.id, group =>
                                    setStudioAngleOffset({ group, assetId: asset.id, offsetSeconds: event.target.value, assets: projectMedia }))} />
                              </label>
                              <button type="button"
                                disabled={asset.status !== "ready" || !(Number(activeFilmTake.sharedEnd) > Number(activeFilmTake.sharedStart)) || Number(activeFilmTake.sharedEnd) > activeFilmTakeRange.end}
                                onClick={() => appendFilmAngleShot(activeFilmTake, asset)}>
                                Use this angle for this shot
                              </button>
                            </article>
                          );
                        })}
                      </div>
                    </div>
                  ) : null}
                </section>
              ) : null}
              <details className="studio-media-bin__placed">
                <summary>Items already in this edit</summary>
                <div className="studio-media-bin__actions">
                <button type="button" onClick={() => imageInputRef.current?.click()}>
                  ＋ Image
                </button>
                <button type="button" onClick={() => quickMusicFileInputRef.current?.click()}>
                  ＋ Audio
                </button>
                </div>
              <div className="studio-media-bin__items">
                {[
                  ...timeline.map((item, index) => ({
                    ...item,
                    assetId: `timeline-${item.id || index}`,
                    kind: "VIDEO",
                    label: item.name || `Source clip ${index + 1}`,
                    action: () => setActiveTimelineIndex(index),
                  })),
                  ...overlays.map((item, index) => ({
                    ...item,
                    assetId: `overlay-${item.id || index}`,
                    kind: item.type === "text" ? "TITLE" : item.type?.toUpperCase() || "LAYER",
                    label: item.file?.name || item.name || item.text || `Layer ${index + 1}`,
                    action: () => {
                      setActiveOverlayId(item.id);
                      selectCreativeTool(item.type === "text" ? "titles" : "broll");
                    },
                  })),
                  ...voiceovers.map((item, index) => ({
                    ...item,
                    assetId: `voice-${item.id || index}`,
                    kind: "VOICE",
                    label: item.name || `Voice-over ${index + 1}`,
                    action: () => selectCreativeTool("sound"),
                  })),
                  ...(musicTrack
                    ? [
                        {
                          ...musicTrack,
                          assetId: "music-active",
                          kind: "MUSIC",
                          label: musicTrack.name || "Background music",
                          action: () => selectCreativeTool("sound"),
                        },
                      ]
                    : []),
                ]
                  .filter(item =>
                    normalizePlainText(item.label)
                      .toLowerCase()
                      .includes(normalizePlainText(mediaBinSearch).toLowerCase())
                  )
                  .slice(0, 12)
                  .map(item => (
                    <button key={item.assetId} type="button" onClick={item.action}>
                      <span>{item.kind}</span>
                      <strong>{item.label}</strong>
                      <small>
                        {item.duration ? `${Number(item.duration).toFixed(1)}s` : "Editable asset"}
                      </small>
                    </button>
                  ))}
              </div>
              </details>
              <details className="studio-proxy-settings">
                <summary>Optimized media / proxies</summary>
                <label className="inspector-check-row">
                  <input
                    type="checkbox"
                    checked={proxySettings.enabled}
                    onChange={event =>
                      setProxySettings(current => ({
                        ...current,
                        enabled: event.target.checked,
                        status: event.target.checked ? "source_ready" : "off",
                      }))
                    }
                  />
                  <span>
                    <strong>Use optimized preview media</strong>
                    <small>Original files remain linked for final export.</small>
                  </span>
                </label>
                <select
                  aria-label="Proxy resolution"
                  value={proxySettings.resolution}
                  onChange={event =>
                    setProxySettings(current => ({
                      ...current,
                      resolution: event.target.value,
                    }))
                  }
                >
                  <option value="540p">540p · fastest</option>
                  <option value="720p">720p · balanced</option>
                  <option value="1080p">1080p · detailed</option>
                </select>
                <small>
                  {proxySettings.enabled
                    ? "Source-linked editing is active; proxy generation is queued only for heavy media."
                    : "The browser previews original media."}
                </small>
              </details>
              <details className="studio-template-library">
                <summary>Reusable edit templates ({editTemplates.length})</summary>
                <button type="button" className="studio-template-save" onClick={saveEditTemplate}>
                  ＋ Save current edit as template
                </button>
                {editTemplates.map(template => (
                  <button
                    key={template.id}
                    type="button"
                    className="studio-template-item"
                    onClick={() => applyEditTemplate(template)}
                  >
                    <strong>{template.name}</strong>
                    <small>Titles · captions · grade · audio · delivery</small>
                  </button>
                ))}
              </details>
              <StudioLaunchTemplatePanel projectId={projectId} media={projectMedia} instance={creativeTemplateInstance} onApply={snapshot => {
                // A new authored graph must immediately stop displaying the
                // preceding finished movie, including recovery still in flight.
                onEditInvalidated?.();
                previewPlaybackIntentRef.current = false;
                videoRef.current?.pause();
                setIsPreviewPaused(true);
                pendingHistoryBaselineRef.current = cloneSnapshot(getEditorSnapshot());
                applyEditorSnapshot(snapshot);
                setProjectSaveState("unsaved");
                setComparisonMode("after");
                setTimelineDockExpanded(true);
                setStudioActionMessage("Cinematic launch applied as editable clips, titles, keyframes and sound.");
              }} />
            </section>

            <div className="studio-project-rail__footer">
              <span>
                <i aria-hidden="true" />
                Local edit
              </span>
              <small>
                {overlays.length} layers · {addHook ? "Hook active" : "Hook not set"}
              </small>
            </div>
          </aside>

          <div className="phone-preview-container">
            <section className="studio-panel preview-panel">
              <div className="panel-heading">
                <div>
                  <span className="panel-kicker">Output canvas</span>
                  <h4>Preview</h4>
                  <p className="panel-description">
                    What you see here is the hook, B-roll timing, and layer stack that will render.
                  </p>
                </div>
                <div className="panel-chip-group">
                  <span className="panel-chip">9:16 output</span>
                  <span className="panel-chip">Clip {activeTimelineIndex + 1}</span>
                  <span className="panel-chip">
                    {effectiveVideoFit === "contain" ? "Safe full frame" : "Fill frame (may crop)"}
                  </span>
                  {showCropRiskIndicator ? (
                    <span className="panel-chip panel-chip-risk">Crop risk</span>
                  ) : null}
                </div>
                <div className="preview-mode-switch" aria-label="Preview comparison mode">
                  {["before", "after", "split"].map(mode => (
                    <button
                      key={mode}
                      type="button"
                      className={comparisonMode === mode ? "is-active" : ""}
                      onClick={() => {
                        setComparisonMode(mode);
                        setStudioActionMessage(
                          mode === "split"
                            ? "Before and After stay synchronized while playback continues."
                            : `${mode === "before" ? "Untouched source" : "Edited result"} preview is active.`
                        );
                      }}
                      aria-pressed={comparisonMode === mode}
                    >
                      {mode === "split" ? "Compare" : mode[0].toUpperCase() + mode.slice(1)}
                    </button>
                  ))}
                </div>
                <button
                  type="button"
                  className="comparison-play-button"
                  onClick={toggleComparisonPlayback}
                  aria-label={isPreviewPaused ? "Play comparison" : "Pause comparison"}
                  disabled={isPreviewPaused && projectSourcesRefreshing}
                >
                  <span aria-hidden="true">{isPreviewPaused ? "▶" : "Ⅱ"}</span>
                </button>
                <button
                  type="button"
                  className="studio-monitor-toggle"
                  aria-pressed={projectRailExpanded}
                  data-testid="preview-media-toggle"
                  onClick={() => setProjectRailExpanded(current => !current)}
                >
                  {projectRailExpanded ? "Hide media" : "Show media"}
                </button>
                <button
                  type="button"
                  className="studio-monitor-toggle"
                  aria-pressed={timelineDockExpanded}
                  data-testid="preview-timeline-toggle"
                  onClick={() => setTimelineDockExpanded(current => !current)}
                >
                  {timelineDockExpanded ? "Hide timeline" : "Show timeline"}
                </button>
                <StudioControlMenu className="studio-view-options" label="View options">
                <div className="preview-display-controls" aria-label="Preview display controls">
                  <div className="preview-reframe-quick-group" aria-label="Quick camera framing">
                    <button
                      type="button"
                      className={`preview-cam-btn ${effectiveSmartCrop && effectiveSmartCropMode === "center" ? "is-active" : ""}`}
                      onClick={showEveryoneSplit}
                      title="Show everyone in a vertical split using two editable source crops"
                      data-testid="preview-quick-both-cams"
                    >
                      👥 Show Everyone
                    </button>
                    <button
                      type="button"
                      className={`preview-cam-btn ${effectiveSmartCrop && effectiveSmartCropMode === "speaker_track" ? "is-active" : ""}`}
                      onClick={() => openSpeakerTracking()}
                      title="Open speaker framing and review tracking points"
                      data-testid="preview-quick-track-speaker"
                    >
                      🎯 Solo Speaker
                    </button>
                  </div>
                  <button
                    type="button"
                    className={`preview-dock-toggle-btn ${previewDockSide === "side" ? "is-side" : "is-center"}`}
                    aria-pressed={previewDockSide === "side"}
                      data-testid="preview-dock-toggle-btn"
                    onClick={() =>
                      setPreviewDockSide(current => (current === "side" ? "center" : "side"))
                    }
                    title={
                      previewDockSide === "side"
                        ? "Dock preview monitor to center"
                        : "Dock preview monitor to side"
                    }
                  >
                    {previewDockSide === "side" ? "⧉ Center canvas" : "⊞ Dock canvas right"}
                  </button>
                  {usesPanelFraming ? (
                    <button
                      type="button"
                      className="is-active"
                      data-testid="edit-panel-crops"
                      onClick={() => {
                        selectCreativeTool("reframe");
                        window.requestAnimationFrame(() =>
                          document
                            .querySelector("[data-testid='source-split-editor'], .speaker-stack-editor")
                            ?.scrollIntoView({ behavior: "smooth", block: "nearest" })
                        );
                        setStudioActionMessage(
                          effectiveSmartCropMode === "group_stack"
                            ? "Camera crop controls are open. Position and zoom each synchronized angle independently."
                            : "Split crop controls are open. Position and zoom both same-time views independently."
                        );
                      }}
                      title="Open the position and zoom controls used by this panel layout"
                    >
                      {effectiveSmartCropMode === "group_stack" ? "Adjust camera crops" : "Adjust split crops"}
                    </button>
                  ) : (
                    <>
                      <button
                        type="button"
                        className={effectiveVideoFit === "contain" ? "is-active" : ""}
                        aria-pressed={effectiveVideoFit === "contain"}
                        data-testid="preview-fit-full"
                        onClick={() => setPreviewFillMode("contain")}
                        title="Show the entire source frame"
                      >
                        Fit full
                      </button>
                      <button
                        type="button"
                        className={effectiveVideoFit === "cover" ? "is-active" : ""}
                        aria-pressed={effectiveVideoFit === "cover"}
                        data-testid="preview-fill-canvas"
                        onClick={() => setPreviewFillMode("cover")}
                        title="Fill the vertical canvas without side gaps"
                      >
                        Fill canvas
                      </button>
                    </>
                  )}
                  <button
                    type="button"
                    data-testid="main-footage-frame-toggle"
                    className={mainFrame.enabled ? "is-active" : ""}
                    aria-pressed={mainFrame.enabled}
                    title="Toggle the margin and rounded corners in preview and export"
                    onClick={() => setMainFrame(current => normalizeMainFrame({
                      ...current, enabled: !current.enabled,
                    }))}
                  >
                    Rounded frame
                  </button>
                  <button
                    type="button"
                    data-testid="preview-fullscreen-button"
                    className={isPreviewFullscreen ? "is-active" : ""}
                    aria-pressed={isPreviewFullscreen}
                    onClick={() => void togglePreviewFullscreen()}
                  >
                    {isPreviewFullscreen ? "Exit fullscreen" : "Fullscreen"}
                  </button>
                  <button
                    type="button"
                    className={showSafeZones ? "is-active" : ""}
                    aria-pressed={showSafeZones}
                    data-testid="preview-safe-zones"
                    onClick={() => setShowSafeZones(current => !current)}
                  >
                    Safe zones
                  </button>
                  <button
                    type="button"
                    className={showCompositionGrid ? "is-active" : ""}
                    aria-pressed={showCompositionGrid}
                    data-testid="preview-grid"
                    onClick={() => setShowCompositionGrid(current => !current)}
                  >
                    Grid
                  </button>
                  <button type="button" data-testid="preview-tools" onClick={() => setCommandPaletteOpen(true)}>
                    ⌘K Tools
                  </button>
                </div>
                </StudioControlMenu>
              </div>

              <div className="preview-device-column">
                <div className="preview-intelligence-stack" aria-label="Live edit intelligence">
                  <span className={hookPreviewLoop || silenceRemoval ? "is-ready" : ""}>
                    {silenceRemoval
                      ? "≋ Smart pacing"
                      : hookPreviewLoop
                        ? "↻ Loop ready"
                        : "↻ Preview synced"}
                  </span>
                  <span
                    className={!previewMuted && (!muteOriginalAudio || extractedAudio?.url) ? "is-ready" : ""}
                    data-testid="preview-audio-status"
                  >
                    {previewMuted
                      ? "♪ Monitoring muted"
                      : muteOriginalAudio
                        ? extractedAudio?.url
                          ? "♪ Extracted audio on"
                          : "♪ Source audio muted"
                        : "♪ Source audio on"}
                  </span>
                  <span
                    className={effectiveSmartCrop ? "is-ready" : ""}
                    data-testid="reframe-preview-status"
                  >
                    {effectiveSmartCrop
                      ? effectiveSmartCropMode === "center"
                        ? reframeAspect === "9:16"
                          ? "⌗ Everyone in frame · source split"
                          : "⌗ Everyone in frame"
                        : effectiveSmartCropMode === "group_stack"
                          ? speakerStackIsReady
                            ? speakerStackCameraCount === 2
                              ? speakerStackLayout === "stack_2"
                                ? "⌗ 2 cameras · stacked"
                                : speakerStackLayout === "split_2"
                                  ? "⌗ 2 cameras · split"
                                  : speakerStackLayout === "pip_2"
                                    ? "⌗ 2 cameras · PIP"
                                    : speakerStackLayout === "active_2"
                                      ? speakerFocusCuts.length
                                        ? `⌗ ${speakerFocusCuts.length} reviewed speaker cut${speakerFocusCuts.length === 1 ? "" : "s"}`
                                        : "⌗ Speaker focus needs marks"
                                      : speakerFocusCuts.length
                                        ? `⌗ ${speakerFocusCuts.length} reviewed spotlight cut${speakerFocusCuts.length === 1 ? "" : "s"}`
                                        : "⌗ Spotlight needs marks"
                              : `⌗ ${speakerStackCameraCount} cameras live`
                            : "⌗ Stack angles not synchronized"
                        : reframeKeyframes.length
                        ? `⌗ ${reframeKeyframes.length} framing correction${reframeKeyframes.length === 1 ? "" : "s"}`
                        : "⌗ Follow path needs data"
                      : "⌗ Reframe off"}
                  </span>
                  <span className="is-ready">
                    ◆{" "}
                    {
                      overlays.filter(overlay => {
                        const start = Number(overlay.startTime || 0);
                        const end = start + Number(overlay.duration || 0);
                        return previewTimelineTime >= start && previewTimelineTime <= end;
                      }).length
                    }{" "}
                    layers on this frame
                  </span>
                  <span className={multicamExportNeedsSupport ? "" : "is-safe"}>
                    {multicamExportNeedsSupport
                      ? "◇ Camera layout · preview only"
                      : "◇ Edit plan saved for export"}
                  </span>
                </div>
                <div className={`preview-player-shell comparison-${comparisonMode}`}>
                  <div
                    ref={phoneFrameRef}
                    data-testid="hook-preview-frame"
                    className={`phone-frame ${smartCrop ? reframeAspectClass : ""} ${isPreviewFullscreen ? "preview-expanded" : ""} ${hookFocusMode ? "hook-focus-enabled" : ""} ${creativePreviewClass} ${renderedOutputUrl ? "has-rendered-output" : ""}`}
                    style={{
                      "--frame-ratio": !renderedOutputUrl && smartCrop
                        ? previewAspectWidth / previewAspectHeight
                        : programmeMediaAspectRatio,
                      "--main-frame-inset": mainFrame.enabled ? `${mainFrame.insetPercent}%` : "0%",
                      "--main-frame-inset-x": mainFrame.enabled ? `${mainFrameInsetXPercent}%` : "0%",
                      "--main-frame-inset-y": mainFrame.enabled ? `${mainFrameInsetYPercent}%` : "0%",
                      "--main-frame-radius": mainFrame.enabled
                        ? `${mainFrame.radiusPercent}%`
                        : "0px",
                      "--main-frame-radius-x": mainFrame.enabled
                        ? `${mainFrameRadiusXPercent}%`
                        : "0px",
                      "--main-frame-radius-y": mainFrame.enabled
                        ? `${mainFrameRadiusYPercent}%`
                        : "0px",
                      borderRadius: mainFrame.enabled
                        ? `${mainFrameRadiusXPercent}% / ${mainFrameRadiusYPercent}%`
                        : "0px",
                      backgroundColor: "#000",
                    }}
                    onClick={handlePreviewFrameClick}
                    onMouseMove={handleMouseMove}
                    onMouseUp={handleDragEnd}
                    onMouseLeave={handleDragEnd}
                    onTouchMove={handleMouseMove}
                    onTouchEnd={handleDragEnd}
                  >
                    <span className="preview-version-label is-after">
                      {renderedOutputUrl ? "Rendered" : "After"}
                    </span>
                    {[0, 1, 2].map(slot => <video
                      key={slot}
                      ref={element => { programmeVideoSlotsRef.current[slot] = element; }}
                      crossOrigin="anonymous"
                      data-testid={slot === activeProgrammeSlot ? "studio-after-video"
                        : slot === (activeProgrammeSlot + 1) % 3 ? "studio-next-video" : "studio-later-video"}
                      muted={slot !== activeProgrammeSlot}
                      className="studio-video main-video-frame-preview"
                      playsInline
                      preload="auto"
                      onLoadStart={event => {
                        if (event.currentTarget === videoRef.current) setIsAfterPreviewReady(false);
                      }}
                      onLoadedMetadata={event => {
                        const media = event.currentTarget;
                        if (media !== videoRef.current) return;
                        if (media.videoWidth > 0 && media.videoHeight > 0) {
                          setProgrammeMediaAspectRatio(media.videoWidth / media.videoHeight);
                        }
                      }}
                      onLoadedData={event => {
                        const media = event.currentTarget;
                        if (media !== videoRef.current) return;
                        if (media.videoWidth > 0 && media.videoHeight > 0)
                          setProgrammeMediaAspectRatio(media.videoWidth / media.videoHeight);
                        setIsAfterPreviewReady(true);
                      }}
                      onCanPlay={event => {
                        if (event.currentTarget !== videoRef.current) return;
                        const media = event.currentTarget;
                        if (media.videoWidth > 0 && media.videoHeight > 0)
                          setProgrammeMediaAspectRatio(media.videoWidth / media.videoHeight);
                        setIsAfterPreviewReady(true);
                        // A source switch can reject play() while its decoder loads.
                        // Retry once playable, unless the creator has paused.
                        if (previewPlaybackIntentRef.current && videoRef.current?.paused) {
                          safePlayMediaElement(videoRef.current);
                        }
                      }}
                      onError={event => {
                        if (event.currentTarget !== videoRef.current) return;
                        if (renderedOutputUrl) {
                          onRenderedPreviewError?.();
                          setStudioActionMessage("The finished preview could not load. Retry access to the completed render; your edit and finished job are retained.");
                          return;
                        }
                        const path = currentTimelineClip?.sourceStoragePath || currentTimelineClip?.storagePath;
                        if (path && !attemptedSourceRefreshRef.current.has(path)) {
                          attemptedSourceRefreshRef.current.add(path);
                          void refreshSourceUrlByPath(path);
                        }
                      }}
                      onSeeked={event => {
                        if (event.currentTarget === videoRef.current) confirmAfterPreviewFrame(event);
                      }}
                      style={{
                        display: renderedOutputUrl && slot === activeProgrammeSlot ? "block" : "none",
                        objectFit: renderedOutputUrl ? "contain" : previewProgrammeFit,
                        objectPosition: previewProgrammePosition,
                        width: renderedOutputUrl
                          ? "100%"
                          : activeSideBySideOverlay
                            ? "50%"
                            : "100%",
                        height: "100%",
                        background: "transparent",
                        position: "absolute",
                        inset: "0 auto 0 0",
                        zIndex: renderedOutputUrl ? 1 : -1,
                        visibility: renderedOutputUrl && slot === activeProgrammeSlot ? "visible" : "hidden",
                        pointerEvents: "none",
                        transformOrigin: activeSideBySideOverlay
                          ? "center center"
                          : finishTransformOrigin,
                        transform: "none",
                        opacity: renderedOutputUrl && slot === activeProgrammeSlot ? 1 : 0,
                        mixBlendMode: renderedOutputUrl ? "normal" : mainTransform.blendMode || "normal",
                        clipPath: renderedOutputUrl
                          ? undefined
                          : getOverlayMaskClipPath({
                              ...mainTransform,
                              cropX: mainMotionAutomation.cropX ?? mainTransform.cropX,
                              cropY: mainMotionAutomation.cropY ?? mainTransform.cropY,
                            }),
                        filter: "none",
                        transition:
                          "width 180ms ease, transform 150ms linear, opacity 160ms linear, filter 160ms linear",
                      }}
                    />)}
                    <SafeVideo
                      ref={subjectProgrammeVideoRef}
                      src={getSafeMediaSource(subjectProgrammePreview?.preview.url)}
                      data-subject-job={subjectProgrammePreview?.preview.jobId}
                      data-testid="subject-programme-video"
                      muted
                      playsInline
                      preload="auto"
                      tabIndex={-1}
                      aria-hidden="true"
                      onError={() => void refreshSubjectProgrammePreview()}
                      style={{ display: "none" }}
                    />
                    <SafeVideo
                      ref={speakerStackTopVideoRef}
                      src={prepareSpeakerStackMedia ? speakerStackTopSource?.source : undefined}
                      muted
                      playsInline
                      preload="auto"
                      tabIndex={-1}
                      aria-hidden="true"
                      data-testid="speaker-stack-top-video"
                      style={{ display: "none" }}
                    />
                    <SafeVideo
                      ref={speakerStackBottomVideoRef}
                      src={prepareSpeakerStackMedia ? speakerStackBottomSource?.source : undefined}
                      muted
                      playsInline
                      preload="auto"
                      tabIndex={-1}
                      aria-hidden="true"
                      data-testid="speaker-stack-bottom-video"
                      style={{ display: "none" }}
                    />
                    <SafeVideo
                      ref={speakerStackThirdVideoRef}
                      src={prepareSpeakerStackMedia ? speakerStackThirdSource?.source : undefined}
                      muted
                      playsInline
                      preload="auto"
                      tabIndex={-1}
                      aria-hidden="true"
                      data-testid="speaker-stack-third-video"
                      style={{ display: "none" }}
                    />
                    <SafeVideo
                      ref={speakerStackFourthVideoRef}
                      src={prepareSpeakerStackMedia ? speakerStackFourthSource?.source : undefined}
                      muted
                      playsInline
                      preload="auto"
                      tabIndex={-1}
                      aria-hidden="true"
                      data-testid="speaker-stack-fourth-video"
                      style={{ display: "none" }}
                    />
                    <SafeVideo
                      ref={sourceSplitAlternateVideoRef}
                      src={prepareSourceAlternateMedia ? getSafeMediaSource(currentTimelineClip?.url || videoUrl) : undefined}
                      muted
                      playsInline
                      preload="auto"
                      tabIndex={-1}
                      aria-hidden="true"
                      data-testid="source-split-alternate-video"
                      style={{ display: "none" }}
                    />
                    <canvas
                      ref={afterCanvasRef}
                      data-testid="studio-program-canvas"
                      data-programme-filter={previewProgrammeFilter}
                      className={`studio-video studio-program-canvas ${getCreatorCanvasClass(
                        creatorPreview
                      )}`}
                      aria-label="Edited programme monitor"
                      aria-busy={pausedSourceFrame?.status === "loading"}
                      style={{
                        display: renderedOutputUrl ? "none" : undefined,
                        position: "absolute",
                        top:
                          renderedOutputUrl || !mainFrame.enabled
                            ? "0"
                            : `${mainFrameInsetYPercent}%`,
                        right:
                          renderedOutputUrl || !mainFrame.enabled
                            ? activeSideBySideOverlay
                              ? "50%"
                              : "0"
                            : activeSideBySideOverlay
                              ? "50%"
                              : `${mainFrameInsetXPercent}%`,
                        bottom:
                          renderedOutputUrl || !mainFrame.enabled
                            ? "0"
                            : `${mainFrameInsetYPercent}%`,
                        left:
                          renderedOutputUrl || !mainFrame.enabled
                            ? "0"
                            : `${mainFrameInsetXPercent}%`,
                        width: renderedOutputUrl
                          ? "100%"
                          : activeSideBySideOverlay
                            ? mainFrame.enabled
                              ? `calc(50% - ${mainFrameInsetXPercent}%)`
                              : "50%"
                            : mainFrame.enabled
                              ? `calc(100% - ${mainFrameInsetXPercent * 2}%)`
                              : "100%",
                        height:
                          renderedOutputUrl || !mainFrame.enabled
                            ? "100%"
                            : `calc(100% - ${mainFrameInsetYPercent * 2}%)`,
                        background: "#000",
                        borderRadius: renderedOutputUrl
                          ? "inherit"
                          : mainFrame.enabled
                            ? `${mainFrameRadiusXPercent}% / ${mainFrameRadiusYPercent}%`
                            : "0",
                      }}
                    />
                    {isPreviewPaused && pausedSourceFrame && pausedSourceFrame.status !== "accurate" && (
                      <small role="status" style={{ position: "absolute", bottom: 8, left: 8, zIndex: 12,
                        padding: "3px 6px", background: "#08080ddd", color: "#ddd", pointerEvents: "none" }}>
                        {pausedSourceFrame.status === "loading" ? "Preparing paused frame…" : "Accurate paused frame unavailable"}
                      </small>
                    )}
                    {comparisonMode !== "before" && !renderedOutputUrl ? (
                      <StudioCreatorPreviewLayer
                        value={creatorPreview}
                        source={liveTimelineSource}
                        reactionSources={resolvedSpeakerStackSources
                          .map(item => item?.source)
                          .filter(Boolean)}
                        time={videoTime}
                        playing={!isPreviewPaused}
                        playbackRate={Number(videoRef.current?.playbackRate || previewSpeed || 1)}
                      />
                    ) : null}
                    {studioInspectorTab === "motion" &&
                    showMotionPath &&
                    motionInspectorTarget &&
                    !renderedOutputUrl ? (
                      <StudioMotionPathEditor key={motionTargetId} keyframes={activeMotionTargetKeyframes}
                        target={{ ...motionInspectorTarget, id: motionTargetId }} time={previewTimelineTime}
                        locked={Boolean(trackStates[motionKeyframeTrack(motionTargetId)]?.locked)}
                        onCommit={editMotionPositionPose} onPathChange={editMotionSpatialPath}
                        onSeek={time => { pauseSynchronizedPreview(); jumpToOutputTimelineTime(time); }}
                        onPreview={point => setMotionPathPreview(point ? { ...point, targetId: motionTargetId } : null)}
                        toDisplay={point => studioGroupProgrammePoint(compositionGroups, motionTargetId,
                          point.x, point.y, previewAspectRatio, 1, motionKeyframes, point.time)}
                        fromDisplay={point => studioGroupLocalPoint(compositionGroups, motionTargetId,
                          point.x, point.y, previewAspectRatio, 1, motionKeyframes, point.time)} />
                    ) : null}
                    {!isAfterPreviewReady && !renderedOutputUrl ? (
                      <div className="after-preview-loading" role="status" aria-live="polite">
                        <span />
                        Loading edited preview…
                      </div>
                    ) : null}
                    {isPreviewFullscreen ? (
                      <button
                        type="button"
                        className="preview-expanded-exit"
                        onClick={event => {
                          event.stopPropagation();
                          void togglePreviewFullscreen();
                        }}
                      >
                        Exit preview
                      </button>
                    ) : null}
                    {temporalPreviewIsLive && !activeSideBySideOverlay ? (
                      <div
                        className={`beat-echo-preview-layer ${
                          motionSculpturePreviewIsLive ? "is-motion-sculpture" : "is-beat-echo"
                        } is-${creativeIntensity}`}
                        aria-hidden="true"
                        data-testid="beat-echo-preview-layer"
                      >
                        {[0, 1, 2].map(index => (
                          <video
                            key={`beat-echo-preview-${index}`}
                            ref={element => {
                              beatEchoVideoRefsRef.current[index] = element;
                            }}
                            muted
                            playsInline
                            preload="auto"
                            tabIndex={-1}
                            style={{
                              objectFit: effectiveVideoFit,
                              objectPosition: safeObjectPosition,
                            }}
                          />
                        ))}
                      </div>
                    ) : null}
                    {creativeEffectIsLive ? (
                      <div
                        className="creative-effect-live-layer"
                        aria-hidden="true"
                        data-testid="creative-effect-live-layer"
                      >
                        <span />
                        <span />
                        <span />
                      </div>
                    ) : null}
                    {shouldShowWatermarkCleanupOnVideo ? (
                      <img
                        ref={watermarkCleanupPreviewImageRef}
                        alt="Cleaned watermark preview on video"
                        className="watermark-cleanup-video-overlay"
                      />
                    ) : null}
                    {showSafeZones || showCompositionGrid ? (
                      <div
                        className={`studio-canvas-guides ${showSafeZones ? "show-safe" : ""} ${showCompositionGrid ? "show-grid" : ""}`}
                        aria-hidden="true"
                      >
                        {showSafeZones ? <i className="studio-action-safe" /> : null}
                        {showSafeZones ? <i className="studio-title-safe" /> : null}
                      </div>
                    ) : null}
                    <video
                      ref={hookBackdropVideoRef}
                      className="hook-preview-backdrop"
                      preload="auto"
                      muted
                      playsInline
                      style={{
                        display: "none",
                        opacity: hookBackdropOpacity,
                        visibility: hookBackdropOpacity > 0.001 ? "visible" : "hidden",
                        objectPosition: safeObjectPosition,
                      }}
                    />
                    <video
                      ref={hookFreezeVideoRef}
                      className="hook-preview-freeze"
                      preload="auto"
                      muted
                      playsInline
                      style={{
                        display: "none",
                        opacity: hookFreezeOpacity,
                        visibility: hookFreezeOpacity > 0.001 ? "visible" : "hidden",
                        objectPosition: safeObjectPosition,
                        transformOrigin: hookTransformOrigin,
                        transform: `scale(${effectiveHookZoomTarget.toFixed(3)})`,
                      }}
                    />
                    <audio ref={audioRef} preload="auto" style={{ display: "none" }} />

                    {finishPreviewIsLive ? (
                      <StudioFinishLayers
                        fx={effectiveFinishFx}
                        edgeBlurStyle={finishEdgeBlurStyle}
                        vignetteStyle={finishVignetteStyle}
                        overlayStyle={finishOverlayStyle}
                        grainStyle={finishGrainStyle}
                        letterboxStyle={finishLetterboxStyle}
                        fadeStyle={finishFadeStyle}
                        visualizer={podcastVisualizer}
                        getAnalyser={startSpeechDetection}
                      />
                    ) : null}

                    <div
                      className="video-bg-layer"
                      style={{
                        display: "none",
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "100%",
                        zIndex: 0,
                        overflow: "hidden",
                        background:
                          mainFrame.background === "soft_blur"
                            ? "radial-gradient(circle at 50% 42%, #1b2030 0%, #080a10 58%, #020305 100%)"
                            : "#020305",
                      }}
                    />

                    <div className="overlays-layer">
                      {hookFocusMode ? (
                        <div
                          className={`hook-focus-target ${hookFocusMode ? "active" : ""}`}
                          data-testid="hook-focus-target"
                          style={{
                            left: `${resolvedHookFocusPoint.x}%`,
                            top: `${resolvedHookFocusPoint.y}%`,
                          }}
                        >
                          <span className="hook-focus-target-dot" />
                          <span className="hook-focus-target-label">
                            {hookFocusMode ? "Tap face or object" : "Opening focus"}
                          </span>
                        </div>
                      ) : null}
                      {showHookPreview && hookDarkOverlay ? (
                        <div
                          className={`hook-preview-shade hook-preview-shade-${hookTemplate.replace(/_/g, "-")}`}
                          style={{ opacity: hookOverlayOpacity }}
                        />
                      ) : null}
                      {showHookPreview ? (
                        <div
                          className={`hook-preview-accent hook-preview-accent-${hookTemplate.replace(/_/g, "-")}`}
                          style={{
                            opacity: hookAccentOpacity,
                            transform: `translate(-50%, ${hookAccentTranslate}) scale(${(0.94 + hookProgress * 0.08).toFixed(3)})`,
                          }}
                        />
                      ) : null}
                      {showHookPreview && isFreezeTextTemplate ? (
                        <div className="hook-preview-bars" />
                      ) : null}
                      {removeWatermark
                        ? resolvedWatermarkPreviewRegions.map((region, index) => (
                            <div
                              key={region.id || `watermark-preview-${index}`}
                              className={`watermark-preview-cleanup ${region.isManual ? "watermark-preview-cleanup-manual" : ""} ${
                                activeWatermarkRegionId === region.id ? "active" : ""
                              }`}
                              style={region.style}
                              onMouseDown={
                                region.isManual
                                  ? e => handleWatermarkDragStart(e, region)
                                  : undefined
                              }
                              onTouchStart={
                                region.isManual
                                  ? e => handleWatermarkDragStart(e, region)
                                  : undefined
                              }
                              onClick={
                                region.isManual
                                  ? e => {
                                      e.stopPropagation();
                                      setActiveWatermarkRegionId(region.id);
                                      setActiveOverlayId(null);
                                    }
                                  : undefined
                              }
                            >
                              <span className="watermark-preview-feather" />
                              <span className="watermark-preview-sheen" />
                              {region.isManual && activeWatermarkRegionId === region.id ? (
                                <>
                                  <button
                                    type="button"
                                    className="watermark-preview-delete"
                                    onClick={event => {
                                      event.stopPropagation();
                                      deleteManualWatermarkRegion(region.id);
                                    }}
                                    title="Delete cleanup box"
                                    aria-label="Delete cleanup box"
                                  >
                                    ×
                                  </button>
                                  {[
                                    { direction: "top-left", icon: "↖" },
                                    { direction: "top-right", icon: "↗" },
                                    { direction: "bottom-left", icon: "↙" },
                                    { direction: "bottom-right", icon: "↘" },
                                  ].map(handle => (
                                    <button
                                      key={handle.direction}
                                      type="button"
                                      className={`watermark-preview-resize watermark-preview-resize-${handle.direction}`}
                                      onMouseDown={event =>
                                        handleWatermarkResizeStart(event, region, handle.direction)
                                      }
                                      onTouchStart={event =>
                                        handleWatermarkResizeStart(event, region, handle.direction)
                                      }
                                      title={`Resize cleanup box from ${handle.direction}`}
                                      aria-label={`Resize cleanup box from ${handle.direction}`}
                                    >
                                      {handle.icon}
                                    </button>
                                  ))}
                                </>
                              ) : null}
                            </div>
                          ))
                        : null}
                      {showHookPreview && hasHookText ? (
                        <div
                          data-testid="hook-preview-banner"
                          className={`hook-preview-banner hook-preview-banner-${hookTemplate.replace(/_/g, "-")} hook-preview-banner-position-${hookBannerSide} hook-preview-banner-subject-${hookBannerSubjectType} hook-text-${hookTextAnimation}`}
                          style={{
                            "--hook-copy-scale":
                              normalizedHookText.length > 54
                                ? 0.68
                                : normalizedHookText.length > 38
                                  ? 0.8
                                  : normalizedHookText.length > 26
                                    ? 0.9
                                    : 1,
                            opacity: hookOutroOpacity * Math.max(0.35, hookTextIntroProgress),
                            top: hookBannerTop,
                            left: `${hookBannerAnchorX}%`,
                            textAlign: hookBannerTextAlign,
                            transform: `translate(${hookBannerTranslateX}, ${Math.round((1 - hookTextIntroProgress) * 12)}px) scale(${hookBannerScale.toFixed(3)})`,
                          }}
                        >
                          <span
                            className="hook-preview-text-glow"
                            style={{ opacity: hookTextGlowOpacity }}
                          />
                          <span
                            className="hook-preview-banner-accent"
                            style={{ opacity: hookBannerAccentOpacity }}
                          />
                          <div className="hook-preview-copy">{normalizedHookText}</div>
                        </div>
                      ) : null}
                      {!renderedOutputUrl && autoCaptions && captionPreviewState.currentChunk ? (
                        <div
                          className={`caption-preview-stack ${
                            captionStoryPreviewTreatment
                              ? `caption-placement-${captionStoryPreviewTreatment.placement}`
                              : `caption-position-${captionPosition}`
                          } caption-style-${captionStyle || "classic"}`}
                          data-testid="live-caption-preview"
                          data-caption-render-style={captionStyle}
                          style={{
                            zIndex: Math.max(480, 100 + compositionEntries.length),
                            "--caption-preview-scale": 1,
                            "--caption-story-accent":
                              captionStoryPreviewTreatment?.accent || "#ffb33d",
                            "--caption-custom-x": `${captionStoryPreviewTreatment?.x ?? 50}%`,
                            "--caption-custom-y": `${captionStoryPreviewTreatment?.y ?? 82}%`,
                            "--caption-custom-shift-x":
                              (captionStoryPreviewTreatment?.x ?? 50) <= 33
                                ? "0%"
                                : (captionStoryPreviewTreatment?.x ?? 50) >= 67
                                  ? "-100%"
                                  : "-50%",
                            "--caption-custom-shift-y":
                              (captionStoryPreviewTreatment?.y ?? 82) <= 33
                                ? "0%"
                                : (captionStoryPreviewTreatment?.y ?? 82) >= 67
                                  ? "-100%"
                                  : "-50%",
                          }}
                        >
                          <div className="caption-preview-pill caption-preview-pill-active" style={captionRenderPreviewStyles.pill}>
                            {captionStoryPreviewTreatment?.icon ? (
                              <span className="caption-preview-story-icon" aria-hidden="true" style={captionRenderPreviewStyles.icon}>
                                {captionStoryPreviewTreatment.icon}
                              </span>
                            ) : null}
                            {captionPreviewState.currentChunk.words.map((word, index) => (
                              <React.Fragment
                                key={`${captionPreviewState.currentChunk.id}-${word}-${index}`}
                              >
                                <span
                                  className={`caption-preview-word caption-rainbow-${index % 7} ${
                                    index === captionPreviewState.activeWordIndex
                                      ? "caption-preview-word-active"
                                      : ""
                                  }`}
                                  style={{ ...captionRenderPreviewStyles.word,
                                    ...(index === captionPreviewState.activeWordIndex
                                      ? captionRenderPreviewStyles.activeWord : {}) }}
                                >
                                  {word}
                                </span>
                                {index < captionPreviewState.currentChunk.words.length - 1
                                  ? " "
                                  : null}
                              </React.Fragment>
                            ))}
                          </div>
                          {captionPreviewState.nextChunk ? (
                            <div className="caption-preview-pill caption-preview-pill-next">
                              {captionPreviewState.nextChunk.text}
                            </div>
                          ) : null}
                        </div>
                      ) : null}
                      {!renderedOutputUrl && <StudioProgrammeFrame clockRef={videoRef} mapTime={getPreviewTimelineTime}
                        time={previewTimelineTime} enabled={presentedFramePreview && !renderedOutputUrl} layers={overlays}
                        keyframes={previewMotionKeyframes} groups={effectiveCompoundClips} mainTransform={mainTransform}
                        programmeWidth={720 * previewAspectRatio} getDuration={getOverlayVisibleDuration}
                        getClipPath={getOverlayMaskClipPath} soloPreviewOverlayId={soloPreviewOverlayId}
                        getSourceFrame={sourceTime=>{
                          const video=videoRef.current;
                          if(renderedOutputUrl||subjectProgrammePreview||activeSideBySideOverlay||speakerStackPreviewIsLive||
                            !isStudioSourceClockCurrent(video,{activeVideo:video,url:currentTimelineClip?.url,start:getTimelineClipWindow(currentTimelineClip).start,
                              end:getTimelineClipWindow(currentTimelineClip).end,hasNext:activeTimelineIndex<timeline.length-1}))return null;
                          const request=video.paused?pausedStudioMappedFrameRequest(currentTimelineClip,Number(video.currentTime),
                            exportSettings.fps,timeline,previewOutputTimeMap,pausedSourceGeneration):null;
                          const exact=request&&pausedSourceFrame?.image&&pausedSourceFrame.key===pausedStudioFrameKey(request);
                          return {picture:exact?pausedSourceFrame.image:video,sourceTime:exact?request.start+request.frameIndex/request.fps:sourceTime,
                            storagePath:currentTimelineClip?.sourceStoragePath||currentTimelineClip?.storagePath,
                            contentHash:currentTimelineClip?.contentHash||currentTimelineClip?.sha256};
                        }}>
                        {compositionTime => (
                      <StudioGroupPreview compounds={effectiveCompoundClips} order={compositionIds}
                        keyframes={previewMotionKeyframes} time={compositionTime}
                        getTime={presentedFramePreview ? undefined : () => getPreviewTimelineTime(videoRef.current?.currentTime || 0)}>
                      {!renderedOutputUrl && motionScenes.length > 0 ? (
                        (Array.isArray(compositionOrder) ? motionScenes.map(scene => [scene]) : [motionScenes]).map(scenes => <MotionCanvas
                          key={Array.isArray(compositionOrder) ? scenes[0].id : "motion-stage"}
                          scenes={scenes}
                          zIndex={Math.max(...scenes.map(scene => compositionRank.get(scene.id) ?? 100))}
                          time={previewTimelineTime}
                          getTime={() => getPreviewTimelineTime(videoRef.current?.currentTime || 0)}
                        />)
                      ) : null}
                      {!renderedOutputUrl && threeDScenes.length > 0 ? (
                        <React.Suspense fallback={null}>
                          {(Array.isArray(compositionOrder) ? threeDScenes.map(scene => [scene]) : [threeDScenes]).map(scenes => <Studio3DCanvas
                            key={Array.isArray(compositionOrder) ? scenes[0].id : "3d-stage"}
                            scenes={scenes}
                            zIndex={Math.max(...scenes.map(scene => compositionRank.get(scene.id) ?? 100))}
                            time={previewTimelineTime}
                            getTime={() => getPreviewTimelineTime(presentedFramePreview ? studioPresentedSourceTime(videoRef.current) : videoRef.current?.currentTime || 0)}
                            getSourceVideo={() => ({ video: videoRef.current,
                              sourceTime: studioPresentedSourceTime(videoRef.current),
                              storagePath: currentTimelineClip?.sourceStoragePath || currentTimelineClip?.storagePath,
                              contentHash: currentTimelineClip?.contentHash || currentTimelineClip?.sha256 })}
                            playing={!isPreviewPaused}
                            playbackRate={Number(videoRef.current?.playbackRate || previewSpeed || 1)}
                          />)}
                        </React.Suspense>
                      ) : null}
                      {silenceRemoval && !showHookPreview ? (
                        <div className="silence-preview-indicator">
                          <span />
                          <span />
                          <span />
                          <strong>Smart pacing</strong>
                        </div>
                      ) : null}
                      {overlays
                        .filter(o => {
                          if (!studioOverlayBelongsToClip(o, timeline[activeTimelineIndex], timeline)) return false;
                          if (o.enabled === false) return false;
                          // Hidden video mattes still need their ordinary source
                          // decoder and programme-clock transport, never a visible leaf.
                          if (isHiddenTrackMatteSource(o, overlays) && o.type !== 'video') return false;
                          if (o.isNullRig && (comparisonMode === "split" || activeOverlayId !== o.id)) return false;
                          const anySolo = overlays.some(cand => cand && !cand.isCaption && cand.solo);
                          if (anySolo && !o.isCaption && !o.solo) return false;

                          const overlayStart =
                            o.startTime !== undefined && o.startTime !== null
                              ? o.startTime
                              : o.start_time;
                          if (o.isNullRig) return true;
                          if (overlayStart !== undefined && o.duration !== undefined) {
                            if (!presentedFramePreview) return !!o.perspective ||
                              (compositionTime >= Number(overlayStart) &&
                               compositionTime < Number(overlayStart) + getOverlayVisibleDuration(o));
                            // Legacy CSS entrances start at mount. Preserve their
                            // original cue until that separate path is paired.
                            if (['image', 'video'].includes(o.type) && o.animation?.enter &&
                                !previewMotionKeyframes.some(key => String(key.targetId) === String(o.id)))
                              return compositionTime >= Number(overlayStart) &&
                                compositionTime < Number(overlayStart) + getOverlayVisibleDuration(o);
                            // Keep the native graphic available for its exact
                            // decoded-frame cue; visibility follows that clock.
                            // Limit additional decoder allocation on long edits.
                            if (o.type === 'video') return compositionTime >= Number(overlayStart) - .5 &&
                              compositionTime < Number(overlayStart) + getOverlayVisibleDuration(o) + .25;
                            return true;
                          }
                          return true;
                        })
                        .map((overlay, index) => {
                          const safeOverlayText = overlay.type === 'text' && !overlay.isCaption && !overlay.bRollMode
                            ? normalizeStudioTitleText(overlay.text) : normalizePlainText(overlay.text);
                          const isFullscreen = overlay.bRollMode === "fullscreen";
                          const isMediaOverlay = ["video", "image"].includes(overlay.type);
                          const frameShape = normalizeOverlayFrameShape(
                            overlay.frameShape,
                            overlay.bRollMode
                          );
                          const frameRadius = getOverlayFrameRadius(overlay);
                          const anim = overlay.animation || {};
                          const overlayKeys = previewMotionKeyframes.filter(
                            keyframe => String(keyframe.targetId) === String(overlay.id)
                          );
                          // Canonical media keys own transform and opacity.
                          // CSS entrance fill otherwise freezes opacity at1 or
                          // replaces the authored scale/rotation after the entry.
                          const hasRigOrDrivers = Boolean(overlay.parentId) || layerHasActiveMotionDrivers(overlay);
                          const keyedMedia = isMediaOverlay && (overlayKeys.length > 0 || hasRigOrDrivers);
                          const animClass = anim.enter && !keyedMedia && !hasRigOrDrivers ? `broll-anim-${anim.enter}` : "";
                          const overlayMotion = hasRigOrDrivers
                            ? evaluateWorldLayerPose(
                                overlay,
                                overlays,
                                previewMotionKeyframes,
                                compositionTime,
                                mainTransform
                              )
                            : buildMotionAutomationStyle(
                                overlayKeys,
                                compositionTime,
                                overlay
                              );
                          const safeOverlaySrc = overlay.vectorShape ? studioVectorUri(overlay.vectorShape, overlayMotion) : getSafeMediaSource(overlay.src);
                          const isStyledTitle = overlay.type === "text" && !isFullscreen && !overlay.isNullRig;
                          const titlePose = isStyledTitle
                            ? titleAnimationPose(overlay, compositionTime)
                            : { x: 0, y: 0, scale: 1, opacity: 1 };
                          const matteSourceLayer =
                            overlay.trackMatte?.mode &&
                            overlay.trackMatte.mode !== "none" &&
                            overlay.trackMatte.sourceLayerId
                              ? overlays.find(
                                  cand => String(cand?.id) === String(overlay.trackMatte.sourceLayerId)
                                )
                              : null;
                          const trackMatteUri = matteSourceLayer
                            ? buildTrackMatteStageSvgDataUri({
                                matteLayer: matteSourceLayer,
                                overlays,
                                motionKeyframes: previewMotionKeyframes,
                                mainTransform,
                                mode: overlay.trackMatte.mode,
                                time: compositionTime,
                                stageAspect: previewAspectRatio || 9 / 16,
                              })
                            : "";

                          const overlayElement = (
                            <div
                              key={overlay.id}
                              data-studio-layer-id={overlay.id}
                              data-studio-matte-source-only={isHiddenTrackMatteSource(overlay, overlays) && overlay.type === 'video' ? 'true' : undefined}
                              data-programme-time={compositionTime}
                              className={`draggable-overlay ${
                                activeOverlayId === overlay.id && isPreviewPaused && !isPreviewFullscreen && comparisonMode !== "split"
                                  ? "active"
                                  : ""
                              } ${isFullscreen ? "broll-fullscreen" : ""} ${
                                overlay.bRollMode === "sideBySide" ? "broll-side-by-side" : ""
                              } ${isMediaOverlay ? "is-media-overlay" : ""} ${
                                isMediaOverlay ? `frame-shape-${frameShape}` : ""
                              } ${overlay.type === "text" && !overlay.isNullRig ? "is-text-overlay" : ""} ${overlay.isNullRig ? "is-null-rig" : ""} ${isStyledTitle ? "" : animClass}`}
                              style={{
                                top: isFullscreen ? "0%" : `${overlayMotion.y + titlePose.y}%`,
                                left: isFullscreen ? "0%" : `${overlayMotion.x + titlePose.x}%`,
                                width: isFullscreen
                                  ? "100%"
                                  : overlay.isNullRig
                                    ? "16%"
                                    : overlay.type === "video" || overlay.type === "image"
                                      ? `${overlay.width || 35}%`
                                      : isStyledTitle ? "max-content" : "auto",
                                maxWidth: isStyledTitle
                                  ? `calc(var(--title-layout-width, 576px) * ${Math.max(15, Math.min(90, Number(overlay.width || 90))) / 100})` : isMediaOverlay ? "none" : undefined,
                                height: isFullscreen
                                  ? "100%"
                                  : overlay.isNullRig
                                    ? "16%"
                                    : overlay.type === "video" || overlay.type === "image"
                                      ? `${overlay.height || 35}%`
                                      : "auto",
                                ...(overlay.isNullRig ? { width: '64px', height: '64px', padding: 0,
                                  transform: `translate(-50%, -50%) rotate(${overlayMotion.rotation}deg) scale(${overlayMotion.scale})`, transition: 'none' } : {}),
                                backgroundColor:
                                  overlay.isNullRig
                                    ? "transparent"
                                    : overlay.type === "text"
                                      ? isFullscreen
                                        ? "rgba(0,0,0,0.92)" // near-opaque for B-Roll cutaways
                                        : overlay.bg
                                      : "transparent",
                                color: overlay.color,
                                "--title-scale": Number(overlayMotion.scale || 1) * titlePose.scale,
                                "--title-rotation": `${Number(overlayMotion.rotation || 0)}deg`,
                                "--layer-translate-x": `${-Number(overlay.anchorX ?? 50)}%`,
                                "--layer-translate-y": `${-Number(overlay.anchorY ?? 50)}%`,
                                "--layer-anchor-x": `${Number(overlay.anchorX ?? 50)}%`,
                                "--layer-anchor-y": `${Number(overlay.anchorY ?? 50)}%`,
                                "--title-stroke": `${Number(overlay.strokeWidth || 0)}px`,
                                "--title-stroke-color": overlay.strokeColor || 'rgba(0,0,0,0.92)',
                                textAlign: overlay.textAlign || "center",
                                fontWeight: Number(overlay.fontWeight || 900),
                                fontFamily: (overlay.fontFamily || "Inter") === "Inter"
                                  ? '"Studio Inter", sans-serif' : `${overlay.fontFamily}, sans-serif`,
                                textShadow:
                                  overlay.shadow === "none"
                                    ? "none"
                                    : overlay.shadow === "strong"
                                      ? "0 4px 14px rgba(0,0,0,.95), 0 1px 2px rgba(0,0,0,1)"
                                      : "0 3px 10px rgba(0,0,0,.72)",
                                // Canvas coverage changes geometry, not layer
                                // priority. Export uses this same saved stack.
                                zIndex: trackMatteUri ? undefined : (compositionRank.get(overlay.id) ?? 100 + index),
                                opacity: overlay.isNullRig ? 1 : overlayMotion.opacity * titlePose.opacity,
                                display: overlay.isNullRig && (activeOverlayId !== overlay.id || !isPreviewPaused || isPreviewFullscreen) ? 'none' : undefined,
                                visibility: (isHiddenTrackMatteSource(overlay, overlays) && overlay.type === 'video') ||
                                  (overlay.perspective && (compositionTime < Number(overlay.startTime || 0) || compositionTime >= Number(overlay.startTime || 0)+Number(overlay.duration || 0))) ? 'hidden' : undefined,
                                "--layer-opacity": overlay.type === "image"
                                  ? overlayMotion.opacity * titlePose.opacity : undefined,
                                mixBlendMode: trackMatteUri ? "normal" : (overlay.blendMode || "normal"),
                                pointerEvents: overlay.locked ? "none" : "auto",
                                filter: isMediaOverlay ? studioMediaEffectFilter(overlay) : undefined,
                                clipPath: getOverlayMaskClipPath({
                                  ...overlay,
                                  mask: overlayMotion.mask || overlay.mask,
                                  cropX: overlayMotion.cropX,
                                  cropY: overlayMotion.cropY,
                                }),
                                borderRadius: isFullscreen ? "0" : frameRadius,
                                animation: keyedMedia ? "none" : undefined,
                                transition: keyedMedia ? "none" : !isStyledTitle && anim.enterDuration
                                  ? `opacity ${anim.enterDuration}s ease, transform ${anim.enterDuration}s ease`
                                  : undefined,
                              }}
                              onMouseDown={
                                comparisonMode === "split" || overlay.locked
                                  ? undefined
                                  : e => handleDragStart(e, overlay)
                              }
                              onTouchStart={
                                comparisonMode === "split" || overlay.locked
                                  ? undefined
                                  : e => handleDragStart(e, overlay)
                              }
                              onDoubleClick={
                                comparisonMode === "split"
                                  ? undefined
                                  : () => {
                                      if (overlay.type === "text" && !overlay.isNullRig) {
                                        const newText = window.prompt(
                                          "Edit Text:",
                                          safeOverlayText
                                        );
                                        if (newText !== null)
                                          updateOverlayText(overlay.id, newText);
                                      }
                                    }
                              }
                              onClick={event => {
                                event.stopPropagation();
                                if (comparisonMode !== "split") setActiveOverlayId(overlay.id);
                              }}
                            >
                              <StudioLayerMask mask={overlayMotion.mask || overlay.mask} cropX={overlayMotion.cropX}
                                cropY={overlayMotion.cropY} title={isStyledTitle} stageRef={phoneFrameRef} />
                              <StudioPerspectiveSurface value={overlayMotion.perspective || overlay.perspective} progress={overlayMotion.perspectiveProgress ?? 100}
                                clockRef={presentedFramePreview ? undefined : videoRef} mapTime={getPreviewTimelineTime}
                                layer={overlay} keyframes={overlayKeys}>
                              {overlay.isNullRig ? (
                                <StudioRigController layer={overlay} layers={overlays} keys={motionKeyframes}
                                  mainTransform={mainTransform} time={previewTimelineTime} stageRef={phoneFrameRef}
                                  onInteractionStart={() => { pauseSynchronizedPreview(); setIsDragging(true); }}
                                  onInteractionEnd={() => setIsDragging(false)}
                                  onChange={patch => {
                                    const keyed = Object.keys(patch).filter(property => motionKeyframes.some(key =>
                                      String(key.targetId) === String(overlay.id) && key.property === property));
                                    const base = Object.fromEntries(Object.entries(patch).filter(([property]) => !keyed.includes(property)));
                                    if (Object.keys(base).length) setOverlays(current => current.map(item => item.id === overlay.id ? { ...item, ...base } : item));
                                    if (keyed.length) setMotionKeyframes(current => keyed.reduce((next, property) =>
                                      upsertAutomationKeyframe(next, { id: createSecureId('rig-key'), targetId: overlay.id,
                                        property, time: previewTimelineTime, value: patch[property], easing: 'linear' }), current));
                                  }} />
                              ) : overlay.type === "text" ? (
                                overlay.bRollPlaceholder ? (
                                  <div
                                    className={`broll-shot-card tone-${overlay.bRollTone || "detail"}`}
                                  >
                                    <span>
                                      {normalizePlainText(overlay.bRollKicker || "B-ROLL")}
                                    </span>
                                    <strong>
                                      {normalizePlainText(overlay.bRollTitle || safeOverlayText)}
                                    </strong>
                                    <small>
                                      {normalizePlainText(
                                        overlay.bRollSubtitle ||
                                          "Replace with matching footage when ready."
                                      )}
                                    </small>
                                  </div>
                                ) : overlay.textAnimator && isStyledTitle ? (
                                  <StudioAnimatedText layer={overlay} programmeWidth={720 * previewAspectRatio} progress={overlayMotion.textProgress} />
                                ) : overlay.isRainbow ? (
                                  <RainbowText
                                    text={safeOverlayText}
                                    offset={overlay.rainbowOffset || 0}
                                  />
                                ) : (
                                  safeOverlayText
                                )
                              ) : overlay.type === "image" && safeOverlaySrc ? (
                                <img
                                  ref={element => {
                                    applySafeMediaSource(element, safeOverlaySrc);
                                  }}
                                  alt="Overlay"
                                  className={overlay.kenBurns && overlay.kenBurns !== "none" ? `ken-burns-${overlay.kenBurns}` : ""}
                                  style={{
                                    width: "100%",
                                    height: "100%",
                                    objectFit:
                                      overlay.perspective?.sourceRect || overlay.mediaFit === "stretch"
                                        ? "fill"
                                        : overlay.mediaFit || (isFullscreen ? "cover" : "contain"),
                                    borderRadius: isFullscreen ? "0" : frameRadius,
                                    // Authored media shadows use layerShadow above.
                                    // An absent text-shadow choice must not paint a
                                    // rectangular backing behind transparent media.
                                    boxShadow: "none",
                                    pointerEvents: "none",
                                  }}
                                />
                              ) : safeOverlaySrc ? (
                                <><canvas data-studio-shared-video="true" aria-hidden="true"
                                  style={{display:'none',width:'100%',height:'100%',objectFit:overlay.perspective?.sourceRect||overlay.mediaFit==='stretch'?'fill':overlay.mediaFit||'contain',pointerEvents:'none'}} />
                                <video data-studio-overlay-decoder="true"
                                  data-testid={`broll-preview-${overlay.id}`}
                                  poster={overlay.poster || undefined}
                                  ref={element => {
                                    applySafeMediaSource(element, safeOverlaySrc);
                                    if (element) {
                                      overlayMediaRefsRef.current.set(String(overlay.id), element);
                                      element.volume =
                                        clampAudioControl(
                                          overlay.overlayAudioVolume,
                                          0,
                                          1,
                                          0.7
                                        ) * clampAudioControl(previewVolume, 0, 1, 1);
                                    } else {
                                      overlayMediaRefsRef.current.delete(String(overlay.id));
                                    }
                                  }}
                                  muted={!overlay.useOverlayAudio || previewMuted}
                                  onLoadedMetadata={event =>
                                    rememberOverlaySourceDuration(
                                      overlay.id,
                                      event.currentTarget.duration
                                    )
                                  }
                                  style={{
                                    width: "100%",
                                    height: "100%",
                                    objectFit:
                                      overlay.perspective?.sourceRect || overlay.mediaFit === "stretch"
                                        ? "fill"
                                        : overlay.mediaFit || (isFullscreen ? "cover" : "contain"),
                                    borderRadius: isFullscreen ? "0" : frameRadius,
                                    pointerEvents: "none",
                                  }}
                                />
                                </>
                              ) : null}

                              </StudioPerspectiveSurface>
                              {comparisonMode !== "split" &&
                                activeOverlayId === overlay.id &&
                                isPreviewPaused &&
                                !isPreviewFullscreen &&
                                !isFullscreen && (
                                  <div className="overlay-controls">
                                    <button
                                      className="overlay-delete-btn"
                                      onClick={e => {
                                        e.stopPropagation();
                                        deleteOverlay(overlay.id);
                                      }}
                                    >
                                      &times;
                                    </button>
                                    {!isFullscreen &&
                                      (overlay.type === "video" || overlay.type === "image") && (
                                        <div
                                          className="resize-handle"
                                          onMouseDown={e => {
                                            e.stopPropagation();
                                          }}
                                        >
                                          <button
                                            className="resize-btn"
                                            onClick={e => {
                                              e.stopPropagation();
                                              e.preventDefault();
                                              updateOverlaySize(overlay.id, "width", -5);
                                            }}
                                          >
                                            W-
                                          </button>
                                          <button
                                            className="resize-btn"
                                            onClick={e => {
                                              e.stopPropagation();
                                              e.preventDefault();
                                              updateOverlaySize(overlay.id, "width", 5);
                                            }}
                                          >
                                            W+
                                          </button>
                                          <button
                                            className="resize-btn"
                                            onClick={e => {
                                              e.stopPropagation();
                                              e.preventDefault();
                                              updateOverlaySize(overlay.id, "height", -5);
                                            }}
                                          >
                                            H-
                                          </button>
                                          <button
                                            className="resize-btn"
                                            onClick={e => {
                                              e.stopPropagation();
                                              e.preventDefault();
                                              updateOverlaySize(overlay.id, "height", 5);
                                            }}
                                          >
                                            H+
                                          </button>
                                          <button
                                            className="resize-btn"
                                            onClick={e => {
                                              e.stopPropagation();
                                              e.preventDefault();
                                              toggleOverlayAspectRatioLock(overlay.id);
                                            }}
                                            title={
                                              overlay.aspectRatioLocked
                                                ? "Unlock aspect ratio"
                                                : "Lock aspect ratio"
                                            }
                                          >
                                            {overlay.aspectRatioLocked ? "Lock" : "Free"}
                                          </button>
                                          <button
                                            className="resize-btn"
                                            onClick={e => {
                                              e.stopPropagation();
                                              e.preventDefault();
                                              centerOverlay(overlay.id);
                                            }}
                                            title="Center overlay"
                                          >
                                            Center
                                          </button>
                                          <button
                                            className="resize-btn"
                                            onClick={e => {
                                              e.stopPropagation();
                                              e.preventDefault();
                                              duplicateOverlay(overlay.id);
                                            }}
                                            title="Duplicate overlay"
                                          >
                                            Copy
                                          </button>
                                          <button
                                            className="resize-btn"
                                            onClick={e => {
                                              e.stopPropagation();
                                              e.preventDefault();
                                              moveOverlay(overlay.id, "backward");
                                            }}
                                            title="Move layer backward"
                                          >
                                            Down
                                          </button>
                                          <button
                                            className="resize-btn"
                                            onClick={e => {
                                              e.stopPropagation();
                                              e.preventDefault();
                                              moveOverlay(overlay.id, "forward");
                                            }}
                                            title="Move layer forward"
                                          >
                                            Up
                                          </button>
                                          <button
                                            className="resize-btn"
                                            onClick={e => {
                                              e.stopPropagation();
                                              e.preventDefault();
                                              moveOverlay(overlay.id, "back");
                                            }}
                                            title="Send layer to back"
                                          >
                                            Back
                                          </button>
                                          <button
                                            className="resize-btn"
                                            onClick={e => {
                                              e.stopPropagation();
                                              e.preventDefault();
                                              moveOverlay(overlay.id, "front");
                                            }}
                                            title="Bring layer to front"
                                          >
                                            Front
                                          </button>
                                        </div>
                                      )}
                                  </div>
                                )}
                            </div>
                          );
                          if (!trackMatteUri) return overlayElement;
                          return (
                            <div
                              key={overlay.id}
                              data-studio-matte-wrap={overlay.id}
                              data-track-matte-mode={overlay.trackMatte?.mode || "alpha"}
                              className="studio-track-matte-stage"
                              style={{
                                position: "absolute",
                                inset: 0,
                                pointerEvents: "none",
                                zIndex: compositionRank.get(overlay.id) ?? 100 + index,
                                mixBlendMode: overlay.blendMode || "normal",
                                maskImage: trackMatteUri,
                                WebkitMaskImage: trackMatteUri,
                                maskSize: "100% 100%",
                                WebkitMaskSize: "100% 100%",
                                maskRepeat: "no-repeat",
                                WebkitMaskRepeat: "no-repeat",
                              }}
                            >
                              {overlayElement}
                            </div>
                          );
                        })}
                      </StudioGroupPreview>
                        )}
                      </StudioProgrammeFrame>}
                    </div>
                  </div>
                    {(
                      <div
                        className={`preview-custom-controls${isPreviewFullscreen ? " is-expanded" : ""}`}
                        onMouseDown={event => event.stopPropagation()}
                        onClick={event => event.stopPropagation()}
                      >
                        <button
                          type="button"
                          onClick={toggleComparisonPlayback}
                          aria-label={
                            isPreviewPaused ? "Play edited preview" : "Pause edited preview"
                          }
                          disabled={isPreviewPaused && projectSourcesRefreshing}
                        >
                          {isPreviewPaused ? "▶" : "❚❚"}
                        </button>
                        <input
                          className="preview-playhead-range"
                          type="range"
                          min={0}
                          max={Math.max(0.1, outputPlaybackDuration)}
                          step={1 / Math.max(1, Number(exportSettings.fps) || 30)}
                          value={clampNumber(
                            previewPlaybackTime,
                            0,
                            Math.max(0.1, outputPlaybackDuration),
                            0
                          )}
                          onChange={event =>
                            jumpToOutputTimelineTime(
                              ticksToSeconds(mapOutputTickToProgrammeTick(previewOutputTimeMap,
                                Math.min(previewOutputTimeMap.at(-1)?.outputRange.endTick || 0,
                                  secondsToTicks(Math.max(0, Number(event.target.value || 0))))) || 0)
                            )
                          }
                          aria-label="Edited output position"
                        />
                        <span className="preview-timecode">
                          {formatPreviewTimePrecise(previewPlaybackTime)} /{" "}
                          {formatPreviewTimePrecise(outputPlaybackDuration)}
                        </span>
                        <div className="preview-audio-controls" aria-label="Preview audio monitoring">
                          <button
                            type="button"
                            data-testid="preview-audio-toggle"
                            className={previewMuted ? "is-muted" : ""}
                            aria-pressed={previewMuted}
                            aria-label={previewMuted ? "Unmute preview audio" : "Mute preview audio"}
                            title={`${previewMuted ? "Unmute" : "Mute"} preview audio (M)`}
                            onClick={() => setPreviewMuted(current => !current)}
                          >
                            {previewMuted || previewVolume <= 0 ? "🔇" : "🔊"}
                          </button>
                          <input
                            type="range"
                            min={0}
                            max={100}
                            step={1}
                            value={Math.round(previewVolume * 100)}
                            aria-label="Preview master volume"
                            title={`Preview volume ${Math.round(previewVolume * 100)}%`}
                            onChange={event => {
                              const nextVolume = Number(event.target.value || 0) / 100;
                              setPreviewVolume(nextVolume);
                              setPreviewMuted(nextVolume <= 0);
                            }}
                          />
                          <span aria-live="polite">{previewMuted ? "Muted" : `${Math.round(previewVolume * 100)}%`}</span>
                        </div>
                      </div>
                    )}
                  <div className="before-preview-card" data-testid="before-preview-frame">
                    <span className="preview-version-label is-before">Before</span>
                    <div className="phone-frame phone-frame-before">
                      <video
                        ref={beforeVideoRef}
                        muted
                        playsInline
                        preload="auto"
                        aria-label="Untouched source preview"
                        style={{ display: renderedOutputUrl ? "block" : "none",width:"100%",height:"100%",objectFit:"contain" }}
                      />
                      <canvas
                        ref={beforeCanvasRef}
                        className="studio-video before-preview-canvas"
                        aria-label="Untouched source monitor"
                        style={{ display: renderedOutputUrl ? "none" : undefined }}
                      />
                    </div>
                  </div>
                </div>

                {directorModeActive && (
                  <div className="director-mode-hud" data-testid="director-mode-hud">
                    <div className="director-hud-header">
                      <div className="director-hud-status">
                        <span
                          className={`director-rec-badge ${
                            videoRef.current && !videoRef.current.paused ? "is-recording" : ""
                          }`}
                        >
                          ● {videoRef.current && !videoRef.current.paused ? "REC LIVE CUTS" : "DIRECTOR STANDBY"}
                        </span>
                        <span className="director-cuts-count">
                          {directorSessionCuts.length} cut{directorSessionCuts.length === 1 ? "" : "s"} in session
                        </span>
                        <span className="director-hud-tip">
                          (Play and tap keys 1, 2, 3, 4 to switch angles live)
                        </span>
                      </div>
                      <div className="director-hud-actions">
                        <button
                          type="button"
                          className="director-hud-btn is-commit"
                          onClick={commitDirectorCutsToTimeline}
                          disabled={!directorSessionCuts.length}
                          title="Bake all recorded cuts into your story timeline"
                        >
                          ⚡ Commit Cuts ({directorSessionCuts.length})
                        </button>
                        <button
                          type="button"
                          className="director-hud-btn is-clear"
                          onClick={clearDirectorCuts}
                          disabled={!directorSessionCuts.length}
                          title="Clear current session cuts"
                        >
                          ↺ Reset Take
                        </button>
                        <button
                          type="button"
                          className="director-hud-btn is-close"
                          onClick={() => setDirectorModeActive(false)}
                          title="Exit Director Mode (Esc)"
                        >
                          ✕ Exit
                        </button>
                      </div>
                    </div>

                    <div className="director-multi-view-grid">
                      {directorAngles.map((angle, idx) => {
                        const isLive = activeDirectorAngle === idx;
                        return (
                          <button
                            type="button"
                            key={angle.id}
                            className={`director-camera-tile ${isLive ? "is-live" : "is-standby"}`}
                            onClick={() => switchDirectorAngle(idx)}
                            title={`Click or press [${idx + 1}] to cut to ${angle.label}`}
                          >
                            <div className="camera-tile-header">
                              <span className={`camera-tally-pill ${isLive ? "is-live" : ""}`}>
                                {isLive ? "🔴 LIVE" : "STANDBY"}
                              </span>
                              <span className="camera-hotkey-badge">[{idx + 1}]</span>
                            </div>
                            <div className="camera-tile-viewport">
                              <div className={`camera-simulated-view angle-${angle.type}`}>
                                <span className="camera-view-icon">
                                  {idx === 0 ? "🎥" : idx === 1 ? "🔍" : idx === 2 ? "👀" : "📐"}
                                </span>
                                <span className="camera-zoom-badge">
                                  {angle.type === "video" ? "Alternate Cam" : `${angle.zoom}x Focal`}
                                </span>
                              </div>
                            </div>
                            <div className="camera-tile-footer">
                              <strong>{angle.label}</strong>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                <div className="preview-signal-grid">
                  <div className="signal-card">
                    <span className="signal-label">Selected moment</span>
                    <strong>
                      {selectedClip ? `${Math.round(selectedClip.duration)}s hook` : "Full video"}
                    </strong>
                    <span>
                      {selectedClip
                        ? normalizePlainText(selectedClip.reason || "Primary detected moment")
                        : "Choose a detected moment to bias the first clip."}
                    </span>
                  </div>
                  <div className="signal-card">
                    <span className="signal-label">Overlay stack</span>
                    <strong>{overlays.length} active layers</strong>
                    <span>
                      {activeOverlay
                        ? `Editing ${activeOverlay.type === "text" ? "text" : activeOverlay.type} overlay`
                        : "Select a layer to fine-tune size and position."}
                    </span>
                  </div>
                  <div className="signal-card">
                    <span className="signal-label">Original audio</span>
                    <strong>{muteOriginalAudio ? "Muted on export" : "Kept live"}</strong>
                    <span>
                      {muteOriginalAudio
                        ? "Use this when the visual story is stronger than the raw source sound."
                        : "Keep live voice, reactions, and ambient energy when they help the hook land."}
                    </span>
                  </div>
                </div>
                <div
                  className="studio-compact-timeline"
                  aria-label="After preview edit timeline"
                  data-testid="live-edit-timeline"
                >
                  <div className="compact-timeline-head">
                    <div>
                      <strong>Live edit timeline</strong>
                      <span>{formatPreviewTimePrecise(previewPlaybackTime)} / </span>
                      <span data-testid="timeline-output-time">
                        {formatPreviewTimePrecise(outputPlaybackDuration)}
                      </span>
                    </div>
                    <div className="timeline-quick-toolbar" role="toolbar" aria-label="Quick editing tools">
                      <button
                        type="button"
                        className="timeline-quick-btn is-primary"
                        onClick={splitCurrentClipAtPlayhead}
                        disabled={Boolean(trackStates[activeOverlay ? (activeOverlay.bRollMode ? "broll" : "graphics") : "video"]?.locked)}
                        title="Split clip at playhead"
                        data-testid="timeline-quick-split"
                      >
                        ✂️ Split
                      </button>
                      <button
                        type="button"
                        className="timeline-quick-btn"
                        onClick={trimClipStartToPlayhead}
                        disabled={!!trackStates.video?.locked}
                        title="Cut everything before playhead"
                        data-testid="timeline-quick-trim-start"
                      >
                        ⏮️ Trim Start
                      </button>
                      <button
                        type="button"
                        className="timeline-quick-btn"
                        onClick={trimClipEndToPlayhead}
                        disabled={!!trackStates.video?.locked}
                        title="Cut everything after playhead"
                        data-testid="timeline-quick-trim-end"
                      >
                        ⏭️ Trim End
                      </button>
                      {timeline.length > 1 && (
                        <button
                          type="button"
                          className="timeline-quick-btn is-danger"
                          onClick={deleteActiveTimelineClip}
                          disabled={!!trackStates.video?.locked}
                          title="Delete selected clip"
                          data-testid="timeline-quick-delete"
                        >
                          🗑️ Delete
                        </button>
                      )}
                      <button
                        type="button"
                        className={`timeline-quick-btn ${autoCaptions ? "is-active" : ""}`}
                        onClick={() => {
                          const next = !autoCaptions;
                          setAutoCaptions(next);
                          if (next && !captionSegments.length && !captionTextOverride) {
                            const initial = resolveInitialCaptionSegments(selectedClip, selectedClip?.duration);
                            if (initial.length) setCaptionSegments(initial);
                          }
                        }}
                        title="Toggle animated captions"
                        data-testid="timeline-quick-captions"
                      >
                        💬 Captions {autoCaptions ? "On" : "Off"}
                      </button>
                      <button
                        type="button"
                        className={`timeline-quick-btn ${silenceRemoval ? "is-active" : ""}`}
                        onClick={() => setSilenceRemoval(!silenceRemoval)}
                        title="Automatically remove silent dead pauses"
                        data-testid="timeline-quick-silence"
                      >
                        ⚡ Cut Silences
                      </button>
                      <button
                        type="button"
                        className="timeline-quick-btn is-history"
                        onClick={handleUndo}
                        disabled={!canUndo}
                        title="Undo (Ctrl+Z)"
                        data-testid="timeline-quick-undo"
                      >
                        ↩ Undo
                      </button>
                      <button
                        type="button"
                        className="timeline-quick-btn is-history"
                        onClick={handleRedo}
                        disabled={!canRedo}
                        title="Redo (Ctrl+Y)"
                        data-testid="timeline-quick-redo"
                      >
                        ↪ Redo
                      </button>
                    </div>
                    <span className="compact-timeline-sync">
                      <i aria-hidden="true" /> After preview · {liveTimelineEditCount} live edits
                    </span>
                  </div>
                  <p className="compact-timeline-trust-copy">
                    These are the same media, timings and audio decisions shown in After. Click a
                    track to inspect that exact frame.
                  </p>

                  <div className="compact-timeline-row compact-source-row">
                    <span>Video</span>
                    <button
                      type="button"
                      className="compact-timeline-track compact-source-track"
                      onClick={seekLiveEditTimeline}
                      aria-label="Seek source video timeline"
                      data-testid="timeline-source-track"
                    >
                      <i
                        className="compact-timeline-playhead"
                        style={{ left: `${liveTimelinePlayheadLeft}%` }}
                      />
                      <span className="compact-source-filmstrip" aria-hidden="true">
                        {!timelineDockExpanded ? (
                          <TimelineSourceFilmstrip frames={liveTimelineFilmstripFrames} />
                        ) : null}
                      </span>
                      <span className="compact-source-scrim" aria-hidden="true" />
                      {normalizedPendingCutRange ? (
                        <span
                          className="pending-cut-range-overlay"
                          style={{
                            left: `${((currentTimelineOffset + normalizedPendingCutRange.start) / liveTimelineDuration) * 100}%`,
                            width: `${((normalizedPendingCutRange.end - normalizedPendingCutRange.start) / liveTimelineDuration) * 100}%`,
                          }}
                          data-testid="timeline-pending-cut-range"
                        >
                          Remove
                        </span>
                      ) : null}
                      {liveTimelineCutMarkers.map(marker => (
                        <span
                          key={marker.id}
                          className="compact-cut-marker"
                          style={{
                            left: `${(marker.outputTime / liveTimelineDuration) * 100}%`,
                          }}
                          data-testid="timeline-applied-cut"
                          title={`${marker.removedDuration.toFixed(1)} seconds removed here`}
                          aria-hidden="true"
                        >
                          CUT
                        </span>
                      ))}
                      {musicBeatMarkers.map((marker, index) => {
                        const markerTime = Number(marker.time || marker);
                        if (!Number.isFinite(markerTime) || markerTime > liveTimelineDuration) {
                          return null;
                        }
                        return (
                          <span
                            key={`music-beat-${index}-${markerTime}`}
                            className={`compact-music-beat ${beatSnapEnabled ? "is-magnetic" : ""}`}
                            style={{ left: `${(markerTime / liveTimelineDuration) * 100}%` }}
                            data-testid="timeline-music-beat"
                            title={`Music beat ${markerTime.toFixed(2)}s`}
                            aria-hidden="true"
                          />
                        );
                      })}
                      <span className="compact-source-beats" aria-hidden="true">
                        {STORY_BEAT_LABELS.map(label => (
                          <i key={label}>{label}</i>
                        ))}
                      </span>
                    </button>
                  </div>

                  <div className="compact-timeline-ruler" aria-hidden="true">
                    <span />
                    {Array.from({ length: 6 }, (_, index) => (
                      <i key={`timeline-tick-${index}`}>
                        {formatPreviewTimePrecise(
                          ((index / 5) * liveTimelineDuration) / Math.max(0.01, previewSpeed)
                        )}
                      </i>
                    ))}
                  </div>

                  <div className="compact-timeline-row">
                    <span>Hook</span>
                    <div
                      className="compact-timeline-track compact-seek-track"
                      onClick={seekLiveEditTimeline}
                      role="presentation"
                    >
                      <i
                        className="compact-timeline-playhead"
                        style={{ left: `${liveTimelinePlayheadLeft}%` }}
                      />
                      {addHook ? (
                        <button
                          type="button"
                          className="compact-timeline-block is-hook"
                          style={{
                            left: `${(resolvedHookStart / liveTimelineDuration) * 100}%`,
                            width: `${Math.max(3, (hookDuration / liveTimelineDuration) * 100)}%`,
                          }}
                          data-testid="timeline-hook-block"
                          aria-label="Inspect opening hook in live timeline"
                          onClick={event => {
                            event.stopPropagation();
                            seekLiveEditTimelineItem(resolvedHookStart, "hook");
                          }}
                          title={`${hookTemplateConfig.label || "Opening hook"} · ${hookDuration.toFixed(1)} seconds`}
                        >
                          <b>✦ {hookTemplateConfig.label || "Opening hook"}</b>
                          <small>{hookDuration.toFixed(1)}s</small>
                        </button>
                      ) : (
                        <button
                          type="button"
                          className="compact-timeline-empty-action"
                          onClick={event => {
                            event.stopPropagation();
                            selectCreativeTool("hook");
                          }}
                        >
                          + Add opening hook
                        </button>
                      )}
                    </div>
                  </div>

                  <div className="compact-timeline-row">
                    <span>Magic</span>
                    <div
                      className="compact-timeline-track compact-creative-track"
                      onClick={seekLiveEditTimeline}
                      role="presentation"
                    >
                      <i
                        className="compact-timeline-playhead"
                        style={{ left: `${liveTimelinePlayheadLeft}%` }}
                      />
                      {creativeEffectsEnabled ? (
                        liveCreativeEffects.map(effect => {
                          const left =
                            ((currentTimelineOffset + effect.start_time) / liveTimelineDuration) *
                            100;
                          const width =
                            ((effect.end_time - effect.start_time) / liveTimelineDuration) * 100;
                          const style = SIGNATURE_CREATIVE_STYLES.find(
                            item => item.id === effect.preset
                          );
                          return (
                            <button
                              key={effect.id}
                              type="button"
                              className={`compact-creative-block is-${effect.preset}`}
                              style={{ left: `${left}%`, width: `${Math.max(3, width)}%` }}
                              onClick={event => {
                                event.stopPropagation();
                                seekLiveEditTimelineItem(
                                  currentTimelineOffset + effect.start_time,
                                  null
                                );
                              }}
                              data-testid={`timeline-creative-block-${effect.id}`}
                              title={`${style?.label || effect.preset} · ${effect.start_time.toFixed(1)}s–${effect.end_time.toFixed(1)}s`}
                            >
                              <b>{style?.icon || "✦"}</b>
                              <span>{style?.label || effect.preset.replace(/_/g, " ")}</span>
                            </button>
                          );
                        })
                      ) : (
                        <button
                          type="button"
                          className="compact-timeline-empty-action"
                          onClick={event => {
                            event.stopPropagation();
                            setCreativeEffectsEnabled(true);
                            setCreativePreset("auto_story");
                            setComparisonMode("split");
                          }}
                        >
                          + Add signature transformation
                        </button>
                      )}
                    </div>
                  </div>

                  <div className="compact-timeline-row">
                    <span>
                      B-roll {liveTimelineBRoll.length ? `(${liveTimelineBRoll.length})` : ""}
                    </span>
                    <div
                      className={`compact-timeline-track compact-broll-track ${
                        liveTimelineBRoll.length > 1 ? "has-layer-lanes" : ""
                      }`}
                      style={{ height: `${Math.max(46, liveTimelineBRoll.length * 34 + 8)}px` }}
                      onClick={seekLiveEditTimeline}
                      role="presentation"
                    >
                      <i
                        className="compact-timeline-playhead"
                        style={{ left: `${liveTimelinePlayheadLeft}%` }}
                      />
                      {liveTimelineBRoll.length ? (
                        liveTimelineBRoll.map((overlay, index) => {
                          const left =
                            (Number(overlay.startTime || 0) / liveTimelineDuration) * 100;
                          const width =
                            (Number(overlay.duration || 0) / liveTimelineDuration) * 100;
                          const mediaSource = getSafeMediaSource(overlay.src);
                          const displayName =
                            overlay.file?.name || overlay.name || `B-roll ${index + 1}`;
                          return (
                            <button
                              key={overlay.id}
                              type="button"
                              className={`compact-timeline-block is-broll ${activeOverlayId === overlay.id ? "is-active" : ""}`}
                              style={{
                                left: `${left}%`,
                                width: `${Math.max(3, width)}%`,
                                top: `${4 + index * 34}px`,
                                zIndex: activeOverlayId === overlay.id ? 3 : 2,
                              }}
                              data-testid={`timeline-broll-block-${overlay.id}`}
                              aria-label={`${overlay.type === "image" ? "Image" : "B-roll"}: ${displayName}, ${Number(
                                overlay.startTime || 0
                              ).toFixed(1)} to ${(
                                Number(overlay.startTime || 0) + Number(overlay.duration || 0)
                              ).toFixed(1)} seconds, ${
                                overlay.bRollMode === "fullscreen"
                                  ? "full-screen cutaway"
                                  : overlay.bRollMode === "sideBySide"
                                    ? "side by side"
                                    : "picture in picture"
                              }`}
                              onClick={event => {
                                event.stopPropagation();
                                selectLiveTimelineOverlay(overlay.id);
                              }}
                              title={`${displayName} · ${Number(overlay.startTime || 0).toFixed(1)}s–${(
                                Number(overlay.startTime || 0) + Number(overlay.duration || 0)
                              ).toFixed(1)}s · ${overlay.bRollMode}`}
                            >
                              {mediaSource && overlay.type === "image" ? (
                                <SafeImage src={mediaSource} alt="" />
                              ) : mediaSource ? (
                                <SafeVideo
                                  src={mediaSource}
                                  muted
                                  playsInline
                                  preload="metadata"
                                  tabIndex={-1}
                                  aria-hidden="true"
                                />
                              ) : null}
                              <span>
                                <b>{displayName}</b>
                                <small>
                                  {overlay.bRollMode === "fullscreen"
                                    ? "Cutaway"
                                    : overlay.bRollMode === "sideBySide"
                                      ? "Split"
                                      : "PIP"}
                                  {` · ${Number(overlay.startTime || 0).toFixed(1)}–${(
                                    Number(overlay.startTime || 0) + Number(overlay.duration || 0)
                                  ).toFixed(1)}s`}
                                </small>
                              </span>
                            </button>
                          );
                        })
                      ) : (
                        <button
                          type="button"
                          className="compact-timeline-empty-action"
                          onClick={event => {
                            event.stopPropagation();
                            selectCreativeTool("broll");
                          }}
                        >
                          + Add proof B-roll
                        </button>
                      )}
                    </div>
                  </div>

                  <div className="compact-timeline-row">
                    <span>Words</span>
                    <div
                      className="compact-timeline-track compact-caption-track"
                      onClick={seekLiveEditTimeline}
                      role="presentation"
                    >
                      <i
                        className="compact-timeline-playhead"
                        style={{ left: `${liveTimelinePlayheadLeft}%` }}
                      />
                      {autoCaptions && liveTimelineCaptionBlocks.length ? (
                        liveTimelineCaptionBlocks.map(chunk => (
                          <button
                            key={chunk.blockId}
                            type="button"
                            className={`compact-caption-block ${
                              captionPreviewState.currentChunk?.id === chunk.id ? "is-current" : ""
                            }`}
                            style={{
                              left: `${(chunk.outputStart / liveTimelineDuration) * 100}%`,
                              width: `${(chunk.outputDuration / liveTimelineDuration) * 100}%`,
                            }}
                            data-testid="timeline-caption-block"
                            onClick={event => {
                              event.stopPropagation();
                              seekLiveEditTimelineItem(chunk.outputStart, "captions");
                            }}
                          >
                            {chunk.text}
                          </button>
                        ))
                      ) : (
                        <button
                          type="button"
                          className="compact-timeline-empty-action"
                          onClick={event => {
                            event.stopPropagation();
                            selectCreativeTool("captions");
                          }}
                        >
                          {autoCaptions ? "+ Generate or type captions" : "+ Turn on live captions"}
                        </button>
                      )}
                    </div>
                  </div>

                  {motionScenes.map(scene => (
                    <div className="compact-timeline-row" key={scene.id}>
                      <span>Motion</span>
                      <div className="compact-timeline-track motion-timeline-track">
                        <button
                          type="button"
                          className="motion-timeline-block"
                          aria-label={`Inspect motion ${scene.text}`}
                          style={{
                            left: `${(100 * scene.startTime) / Math.max(0.1, liveTimelineDuration)}%`,
                            width: `${(100 * scene.duration) / Math.max(0.1, liveTimelineDuration)}%`,
                          }}
                          onClick={() => {
                            setSelectedMotionId(scene.id);
                            seekLiveEditTimelineItem(scene.startTime, "motion");
                          }}
                        >
                          ◆ {scene.text} {scene.sound !== "none" ? "♫" : ""}
                        </button>
                      </div>
                    </div>
                  ))}
                  {threeDScenes.map(scene => (
                    <div className="compact-timeline-row" key={scene.id}>
                      <span>3D</span>
                      <div className="compact-timeline-track motion-timeline-track">
                        <button type="button" className="motion-timeline-block" aria-label={`Inspect 3D motion ${scene.text}`}
                          style={{ left: `${(100 * scene.startTime) / Math.max(0.1, liveTimelineDuration)}%`, width: `${(100 * scene.duration) / Math.max(0.1, liveTimelineDuration)}%` }}
                          onClick={() => { setSelectedThreeDId(scene.id); setMotionWorkspace("3d"); seekLiveEditTimelineItem(scene.startTime, "motion"); }}>
                          ◈ {scene.text}
                        </button>
                      </div>
                    </div>
                  ))}
                  <div className="compact-timeline-row">
                    <span>Speed</span>
                    <div
                      className="compact-timeline-track compact-speed-track"
                      onClick={seekLiveEditTimeline}
                      role="presentation"
                    >
                      <i
                        className="compact-timeline-playhead"
                        style={{ left: `${liveTimelinePlayheadLeft}%` }}
                      />
                      <button
                        type="button"
                        className="compact-speed-block"
                        aria-label={`${previewSpeed.toFixed(2)} times speed, ${pacingLevel} energy, ${formatPreviewTimePrecise(outputPlaybackDuration)} edited output${silenceRemoval ? ", silence tightening on" : ""}`}
                        onClick={event => {
                          event.stopPropagation();
                          selectCreativeTool("pacing");
                        }}
                        data-testid="timeline-speed-block"
                      >
                        <b>{previewSpeed.toFixed(2).replace(/0$/, "")}×</b>
                        <span>{pacingLevel} pacing</span>
                        <small>{formatPreviewTimePrecise(outputPlaybackDuration)} output</small>
                        {silenceRemoval ? <small>Silence tightening on</small> : null}
                      </button>
                    </div>
                  </div>

                  <div className="compact-timeline-row audio-remix-timeline-row">
                    <span>Voice</span>
                    <div
                      className="compact-timeline-track compact-audio-track is-voice-track"
                      onClick={seekLiveEditTimeline}
                      role="presentation"
                    >
                      <i
                        className="compact-timeline-playhead"
                        style={{ left: `${liveTimelinePlayheadLeft}%` }}
                      />
                      <div
                        className={`compact-audio-lane is-voice ${
                          muteOriginalAudio ? "is-muted" : ""
                        }`}
                        data-testid="timeline-original-audio"
                      >
                        <i className="compact-audio-wave" aria-hidden="true" />
                        <span>{muteOriginalAudio ? "Original muted" : "Original voice"}</span>
                      </div>
                      {liveTimelineBRoll
                        .filter(overlay => getOverlayAudioMode(overlay) !== "original")
                        .map(overlay => {
                          const left =
                            (Number(overlay.startTime || 0) / liveTimelineDuration) * 100;
                          const width =
                            (Number(overlay.duration || 0) / liveTimelineDuration) * 100;
                          const overlayAudioMode = getOverlayAudioMode(overlay);
                          return (
                            <button
                              key={`audio-${overlay.id}`}
                              type="button"
                              className={`compact-overlay-audio is-${overlayAudioMode}`}
                              style={{ left: `${left}%`, width: `${Math.max(3, width)}%` }}
                              onClick={event => {
                                event.stopPropagation();
                                seekLiveEditTimelineItem(
                                  Number(overlay.startTime || 0),
                                  "broll",
                                  overlay.id
                                );
                              }}
                              data-testid={`timeline-overlay-audio-${overlay.id}`}
                            >
                              {overlayAudioMode === "mix" ? "Mix" : "Overlay"}
                            </button>
                          );
                        })}
                    </div>
                  </div>

                  <div className="compact-timeline-row audio-remix-timeline-row">
                    <span>Music</span>
                    <div
                      className="compact-timeline-track compact-audio-track is-music-track"
                      onClick={seekLiveEditTimeline}
                      role="presentation"
                    >
                      <i
                        className="compact-timeline-playhead"
                        style={{ left: `${liveTimelinePlayheadLeft}%` }}
                      />
                      {addMusic ? (
                        <button
                          type="button"
                          className="compact-audio-lane is-music"
                          onClick={event => {
                            event.stopPropagation();
                            selectCreativeTool("sound");
                          }}
                          data-testid="timeline-music-audio"
                          title={`${musicTrack?.name || currentMusicLabel} · ${Math.round(
                            (musicTrack?.volume ?? musicVolume) * 100
                          )}%${musicTrack?.ducking ? " · speech ducking" : ""}`}
                        >
                          <i className="compact-audio-wave" aria-hidden="true" />
                          <span>{musicTrack?.name || currentMusicLabel || "Background sound"}</span>
                        </button>
                      ) : (
                        <button
                          type="button"
                          className="compact-audio-lane is-music is-empty"
                          onClick={event => {
                            event.stopPropagation();
                            selectCreativeTool("sound");
                          }}
                          data-testid="timeline-music-audio-empty"
                        >
                          <i className="compact-audio-wave" aria-hidden="true" />
                          <span>No music selected</span>
                        </button>
                      )}
                    </div>
                  </div>

                  <div className="compact-timeline-row audio-remix-timeline-row">
                    <span>SFX</span>
                    <div
                      className="compact-timeline-track compact-audio-track is-sfx-track"
                      onClick={seekLiveEditTimeline}
                      role="presentation"
                    >
                      <i
                        className="compact-timeline-playhead"
                        style={{ left: `${liveTimelinePlayheadLeft}%` }}
                      />
                      {soundEffects.length === 0 ? (
                        <button
                          type="button"
                          className="compact-audio-lane is-sfx-empty"
                          onClick={event => {
                            event.stopPropagation();
                            selectCreativeTool("sound");
                          }}
                        >
                          <i className="compact-audio-wave" aria-hidden="true" />
                          <span>Add sound effects</span>
                        </button>
                      ) : null}
                      {soundEffects.map(effect => {
                        const left = (Number(effect.startTime || 0) / liveTimelineDuration) * 100;
                        const width = (getSoundEffectDuration(effect) / liveTimelineDuration) * 100;
                        return (
                          <button
                            key={`sfx-${effect.id}`}
                            type="button"
                            className={`compact-sfx-audio ${activeSoundEffectId === effect.id ? "is-active" : ""} ${effect.enabled === false ? "is-disabled" : ""}`}
                            style={{
                              left: `${left}%`,
                              width: `${Math.max(2.4, width)}%`,
                            }}
                            onClick={event => {
                              event.stopPropagation();
                              setActiveSoundEffectId(effect.id);
                              seekLiveEditTimelineItem(Number(effect.startTime || 0), "sound");
                            }}
                            data-testid={`timeline-sfx-${effect.id}`}
                            title={`${effect.name} · ${formatPreviewTimePrecise(effect.startTime)} · ${Math.round(effect.volume * 100)}%`}
                          >
                            <span>
                              {effect.emoji || "🔊"} {effect.name}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  <div className="compact-timeline-footer">
                    <span>
                      <i className="is-hook" /> Hook
                    </span>
                    <span>
                      <i className="is-broll" /> B-roll
                    </span>
                    <span>
                      <i className="is-caption" /> Captions
                    </span>
                    <span>
                      <i className="is-creative" /> Signature effect
                    </span>
                    <strong>Preview and timeline share one edit state</strong>
                  </div>
                </div>
              </div>
            </section>

            <section className="studio-panel studio-timeline-container">
              <div className="panel-heading compact">
                <div>
                  <span className="panel-kicker">Sequence</span>
                  <h4>Timeline</h4>
                </div>
                <div className="panel-chip-group">
                  <span className="panel-chip">{timeline.length} clips</span>
                  <span className="panel-chip">Playing clip {activeTimelineIndex + 1}</span>
                </div>
              </div>
              <div className="timeline-info">
                <span>Drag to reorder the final sequence.</span>
                <span>Add supporting footage when the primary cut needs help.</span>
              </div>
              <div className="timeline-scroll-area">
                {timeline.map((clip, index) => (
                  <div
                    key={clip.id}
                    data-testid={`timeline-clip-${clip.id}`}
                    onClick={() => setActiveTimelineIndex(index)}
                    draggable={timeline.length > 1}
                    onDragStart={() => setDraggedTimelineClipId(clip.id)}
                    onDragEnd={() => setDraggedTimelineClipId(null)}
                    onDragOver={e => {
                      e.preventDefault();
                    }}
                    onDrop={e => {
                      e.preventDefault();
                      if (draggedTimelineClipId === null || draggedTimelineClipId === clip.id)
                        return;
                      moveTimelineClipToIndex(draggedTimelineClipId, index);
                      setDraggedTimelineClipId(null);
                    }}
                    className={`timeline-clip-thumb ${activeTimelineIndex === index ? "active" : ""}`}
                    title={clip.name || `Clip ${index + 1}`}
                    style={
                      draggedTimelineClipId === clip.id
                        ? { borderStyle: "dashed", borderColor: "#e52e71" }
                        : undefined
                    }
                  >
                    {/* If clip has a name, show first few chars, otherwise show index */}
                    <span
                      className="clip-thumb-label"
                      style={{ fontSize: clip.name ? "12px" : "16px" }}
                    >
                      {clip.name
                        ? clip.name.length > 8
                          ? clip.name.substring(0, 6) + ".."
                          : clip.name
                        : index + 1}
                    </span>

                    {/* Tiny video preview if possible? Too heavy. Use duration. */}
                    <span className="clip-dur-label">
                      {clip.duration
                        ? Math.round(clip.duration) + "s"
                        : clip.startRequest
                          ? "Trimmed"
                          : "..."}
                    </span>

                    {/* Controls Row */}
                    <div
                      className="clip-mini-controls"
                      style={{ display: "flex", gap: "4px", marginTop: "4px" }}
                    >
                      {timeline.length > 1 && (
                        <>
                          <button
                            className="clip-caption-btn"
                            title="Move clip earlier"
                            data-testid={`timeline-move-left-${clip.id}`}
                            onClick={e => {
                              e.stopPropagation();
                              moveTimelineClip(clip.id, "backward");
                            }}
                            disabled={index === 0}
                            style={{
                              fontSize: "10px",
                              padding: "2px 5px",
                              borderRadius: "4px",
                              border: "1px solid #ccc",
                              background: "#fff",
                              cursor: index === 0 ? "default" : "pointer",
                              opacity: index === 0 ? 0.5 : 1,
                            }}
                          >
                            ←
                          </button>
                          <button
                            className="clip-caption-btn"
                            title="Move clip later"
                            data-testid={`timeline-move-right-${clip.id}`}
                            onClick={e => {
                              e.stopPropagation();
                              moveTimelineClip(clip.id, "forward");
                            }}
                            disabled={index === timeline.length - 1}
                            style={{
                              fontSize: "10px",
                              padding: "2px 5px",
                              borderRadius: "4px",
                              border: "1px solid #ccc",
                              background: "#fff",
                              cursor: index === timeline.length - 1 ? "default" : "pointer",
                              opacity: index === timeline.length - 1 ? 0.5 : 1,
                            }}
                          >
                            →
                          </button>
                        </>
                      )}
                      {/* Auto-Caption Button */}
                      <button
                        className="clip-caption-btn"
                        title="Auto-Generate Captions"
                        onClick={async e => {
                          e.stopPropagation();
                          if (
                            !window.confirm(
                              `Generate captions for ${clip.name || "this clip"}?\n(This uses AI to detect speech and may need manual cleanup, especially for mixed South African languages. It might take 10-30s.)`
                            )
                          )
                            return;

                          // 1. Get file blob
                          if (!clip.file) {
                            toast.error(
                              "Can only caption freshly uploaded files. (No file data found)"
                            );
                            return;
                          }

                          // 2. Upload to /api/media/transcribe
                          const formData = new FormData();
                          formData.append("file", clip.file);
                          formData.append(
                            "translate_to_english",
                            translateCaptionsToEnglish ? "true" : "false"
                          );

                          // Show loading state?
                          e.target.innerText = "⏳ AI Listening...";
                          e.target.disabled = true;

                          try {
                            const token = await getMediaAuthToken();

                            // Use configured API BASE URL
                            const res = await fetch(`${API_BASE_URL}/api/media/transcribe`, {
                              method: "POST",
                              headers: {
                                Authorization: `Bearer ${token}`,
                              },
                              body: formData,
                            });

                            if (!res.ok) {
                              const err = await res.json();
                              throw new Error(err.error || "Upload failed");
                            }

                            let data = await res.json();

                            // ASYNC POLLING (Transcription)
                            if (data.jobId) {
                              const jobId = data.jobId;
                              e.target.innerText = "⏳ Transcribing...";

                              let attempts = 0;
                              while (true) {
                                if (attempts > 120) throw new Error("Transcription timed out");
                                await new Promise(r => setTimeout(r, 2000));
                                attempts++;

                                const sRes = await fetch(
                                  `${API_BASE_URL}/api/media/status/${jobId}`,
                                  {
                                    headers: { Authorization: `Bearer ${token}` },
                                  }
                                );

                                if (!sRes.ok) continue;
                                const sData = await sRes.json();

                                if (sData.status === "failed")
                                  throw new Error(sData.error || "Transcription failed");
                                if (sData.status === "completed") {
                                  data = sData.result; // Expects { segments: [...] }
                                  break;
                                }
                              }
                            }

                            // data.segments = [{ start: 0.0, end: 2.0, text: "Hello" }]
                            if (!data.segments) throw new Error("No segments returned");

                            const filteredSegments = data.segments.filter(seg => {
                              const t = seg.text.toLowerCase().trim();

                              // 1. Filter out known Whisper hallucinations/descriptions
                              const invalidPhrases = [
                                "music outro",
                                "music intro",
                                "background music",
                                "subtitles by",
                                "captioned by",
                                "transcribed by",
                                "copyright",
                                "all rights reserved",
                                "thank you",
                              ];
                              if (invalidPhrases.some(bad => t.includes(bad))) return false;

                              // 2. Filter purely non-verbal brackets like [Music] or (Silence) or (Music Outro)
                              if (
                                (t.startsWith("[") && t.endsWith("]")) ||
                                (t.startsWith("(") && t.endsWith(")"))
                              )
                                return false;

                              // 3. Filter single junk characters or words
                              if (t === "music" || t === "." || t === "you" || t.length < 2)
                                return false;

                              return true;
                            });

                            if (filteredSegments.length === 0) {
                              toast(
                                "Audio processed but no clear speech detected (music/noise filtered).",
                                { icon: "🔇" }
                              );
                              return;
                            }

                            const newCaptions = filteredSegments.map((seg, i) => ({
                              id: createSecureId("caption"),
                              type: "text",
                              text: seg.text.trim(),
                              x: 50,
                              y: i % 2 === 0 ? 80 : 75, // Slight vertical jitter for dynamic feel
                              color: "#ffffff",
                              bg: "rgba(0,0,0,0.6)",
                              scale: 1,
                              isRainbow: false,
                              startTime:
                                (clip.startRequest !== null && clip.startRequest !== undefined
                                  ? clip.startRequest
                                  : 0) + seg.start,
                              duration: seg.end - seg.start,
                              isCaption: true,
                              clipId: clip.id,
                              rainbowOffset: i * 3,
                            }));

                            setOverlays(prev => [...prev, ...newCaptions]);
                            toast.success(
                              "Captions generated via AI draft! Review the text before export."
                            );
                          } catch (err) {
                            toast.error("Error generating captions: " + err.message);
                          } finally {
                            e.target.innerText = "💬 CC";
                            e.target.disabled = false;
                          }
                        }}
                        style={{
                          fontSize: "10px",
                          padding: "2px 5px",
                          borderRadius: "4px",
                          border: "1px solid #ccc",
                          background: "#fff",
                          cursor: "pointer",
                        }}
                      >
                        💬 CC
                      </button>

                      {/* Delete Btn */}
                      {timeline.length > 1 && (
                        <button
                          className="clip-delete-btn-mini"
                          title="Remove Clip"
                          onClick={e => {
                            e.stopPropagation();
                            deleteActiveTimelineClip(index);
                          }}
                          style={{
                            fontSize: "10px",
                            padding: "2px 5px",
                            borderRadius: "4px",
                            border: "1px solid #ff4757",
                            color: "#ff4757",
                            background: "#fff",
                            cursor: "pointer",
                          }}
                        >
                          🗑️
                        </button>
                      )}
                    </div>
                  </div>
                ))}
                <label className="add-clip-btn" title="Add Video to Timeline">
                  +
                  <input
                    data-testid="timeline-add-clip-input"
                    type="file"
                    accept="video/*"
                    style={{ display: "none" }}
                    onChange={addVideoLayer}
                  />
                </label>
              </div>
            </section>

            {timeline[activeTimelineIndex] && (
              <section className="studio-panel studio-trim-controls">
                <div className="panel-heading compact">
                  <div>
                    <span className="panel-kicker">Timing</span>
                    <h4>Trim active clip</h4>
                    <p className="panel-description">
                      Tighten the current clip window before it reaches render.
                    </p>
                  </div>
                  <div className="panel-chip-group">
                    <span className="panel-chip">
                      Start {currentTimelineWindow.start.toFixed(1)}s
                    </span>
                    <span className="panel-chip">End {currentTimelineWindow.end.toFixed(1)}s</span>
                    <span className="panel-chip">
                      {currentTimelineWindow.duration.toFixed(1)}s live
                    </span>
                    {trimPreviewLoop ? <span className="panel-chip">Trim loop on</span> : null}
                  </div>
                </div>
                <div className="slider-stack">
                  <label className="studio-slider-label">
                    <span>Clip start</span>
                    <input
                      type="range"
                      min={0}
                      max={(timeline[activeTimelineIndex].sourceDuration || timeline[activeTimelineIndex].duration || 10) - 0.5}
                      step={0.1}
                      value={
                        timeline[activeTimelineIndex].startRequest !== null &&
                        timeline[activeTimelineIndex].startRequest !== undefined
                          ? timeline[activeTimelineIndex].startRequest
                          : 0
                      }
                      disabled={!!trackStates.video?.locked}
                      onChange={event => commitSequenceEdit({ mode: 'ripple', edge: 'start',
                        clipId: timeline[activeTimelineIndex].id,
                        delta: Number(event.target.value) - Number(currentTimelineWindow.start) })}
                    />
                  </label>
                  <div className="split-audio-panel" style={{ borderTop: "1px solid var(--border)", paddingTop: "10px", marginTop: "10px" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <strong style={{ fontSize: "12px", color: "var(--text)" }}>🎧 J-Cut & L-Cut Dialogue Transitions</strong>
                      <span style={{ fontSize: "10px", color: "#38bdf8" }}>Broadcast Rhythm</span>
                    </div>

                    {/* Preset Pills */}
                    <div className="split-audio-pill-group">
                      {SPLIT_AUDIO_PRESETS.map(preset => {
                        const curJ = Math.abs(Number(timeline[activeTimelineIndex]?.audioTrimOffsetStart || 0));
                        const curL = Number(timeline[activeTimelineIndex]?.audioTrimOffsetEnd || 0);
                        const isPresetActive =
                          preset.id === "straight"
                            ? curJ < 0.05 && curL < 0.05
                            : (preset.jCut > 0 && Math.abs(curJ - preset.jCut) < 0.1) ||
                              (preset.lCut > 0 && Math.abs(curL - preset.lCut) < 0.1);

                        return (
                          <button
                            key={preset.id}
                            type="button"
                            className={`split-audio-pill ${isPresetActive ? "is-active" : ""}`}
                            onClick={() => {
                              preset.id === 'straight' ? commitSequenceEdit({ mode:'audio-reset',clipId:timeline[activeTimelineIndex].id })
                                : handleAudioTrim(activeTimelineIndex, { audioTrimOffsetStart: -preset.jCut, audioTrimOffsetEnd: preset.lCut });
                            }}
                            title={preset.description}
                          >
                            {preset.label}
                          </button>
                        );
                      })}
                    </div>

                    {/* J-Cut Slider */}
                    <label className="studio-slider-label">
                      <div style={{ display: "flex", justifyContent: "space-between" }}>
                        <span>J-Cut Lead (Dialogue Pre-lap)</span>
                        <strong style={{ color: "#22d3ee" }}>
                          {Number(timeline[activeTimelineIndex]?.audioTrimOffsetStart || 0) < -0.04
                            ? `-${Math.abs(timeline[activeTimelineIndex].audioTrimOffsetStart).toFixed(1)}s`
                            : "0.0s (Straight)"}
                        </strong>
                      </div>
                      <input
                        type="range"
                        min={-3}
                        max={0}
                        step={0.1}
                        value={timeline[activeTimelineIndex].audioTrimOffsetStart || 0}
                        onChange={e => {
                          const val = parseFloat(e.target.value);
                          handleAudioTrim(activeTimelineIndex, { audioTrimOffsetStart: val });
                        }}
                      />
                    </label>

                    {/* L-Cut Slider */}
                    <label className="studio-slider-label">
                      <div style={{ display: "flex", justifyContent: "space-between" }}>
                        <span>L-Cut Trail (Dialogue Hold)</span>
                        <strong style={{ color: "#a78bfa" }}>
                          {Number(timeline[activeTimelineIndex]?.audioTrimOffsetEnd || 0) > 0.04
                            ? `+${Number(timeline[activeTimelineIndex].audioTrimOffsetEnd).toFixed(1)}s`
                            : "0.0s (Straight)"}
                        </strong>
                      </div>
                      <input
                        type="range"
                        min={0}
                        max={3}
                        step={0.1}
                        value={timeline[activeTimelineIndex].audioTrimOffsetEnd || 0}
                        onChange={e => {
                          const val = parseFloat(e.target.value);
                          handleAudioTrim(activeTimelineIndex, { audioTrimOffsetEnd: val });
                        }}
                      />
                    </label>

                    {/* Auto-Apply Pre-laps Button */}
                    <button
                      type="button"
                      className="smart-zoom-btn-primary"
                      onClick={autoApplyAllDialoguePreLaps}
                      style={{
                        background: "linear-gradient(135deg, rgba(6, 182, 212, 0.2), rgba(139, 92, 246, 0.2))",
                        borderColor: "rgba(56, 189, 248, 0.4)",
                        color: "#e0f2fe",
                        padding: "6px 12px",
                        fontSize: "11px",
                        cursor: "pointer",
                        borderRadius: "6px",
                        marginTop: "2px",
                      }}
                      title="Auto-apply 0.6s dialogue pre-laps across all cuts in the sequence"
                    >
                      ⚡ Auto-Dialogue Pre-lap (All Cuts)
                    </button>
                  </div>
                  <label className="studio-slider-label">
                    <span>Clip end</span>
                    <input
                      type="range"
                      min={
                        (timeline[activeTimelineIndex].startRequest !== null &&
                        timeline[activeTimelineIndex].startRequest !== undefined
                          ? timeline[activeTimelineIndex].startRequest
                          : 0) + 0.5
                      }
                      max={timeline[activeTimelineIndex].sourceDuration || timeline[activeTimelineIndex].duration || 100}
                      step={0.1}
                      value={
                        timeline[activeTimelineIndex].endRequest !== null &&
                        timeline[activeTimelineIndex].endRequest !== undefined
                          ? timeline[activeTimelineIndex].endRequest
                          : timeline[activeTimelineIndex].duration || 10
                      }
                      onChange={e => {
                        const val = parseFloat(e.target.value);
                        if (!commitSequenceEdit({ mode: 'ripple', edge: 'end',
                          clipId: timeline[activeTimelineIndex].id,
                          delta: val - Number(currentTimelineWindow.end) })) return;
                        if (videoRef.current) {
                          setHookPreviewLoop(false);
                          setTrimPreviewLoop(true);
                          const previewTarget = Math.min(
                            Math.max(
                              Number(currentTimelineWindow.start || 0),
                              videoRef.current.currentTime || 0
                            ),
                            Math.max(Number(currentTimelineWindow.start || 0), val - 0.05)
                          );
                          videoRef.current.currentTime = previewTarget;
                          safePlayMediaElement(videoRef.current);
                        }
                      }}
                    />
                  </label>
                </div>
                <div className="mini-toggle-row">
                  <button
                    type="button"
                    className="mini-toggle-btn active"
                    onClick={() => previewTrimWindow(false)}
                  >
                    Preview trim once
                  </button>
                  <button
                    type="button"
                    className={`mini-toggle-btn ${trimPreviewLoop ? "active" : ""}`}
                    onClick={() => previewTrimWindow(!trimPreviewLoop)}
                  >
                    {trimPreviewLoop ? "Stop trim loop" : "Loop trimmed clip"}
                  </button>
                </div>
              </section>
            )}

            <section className="studio-panel studio-trim-controls audio-panel">
              <div className="panel-heading compact">
                <div>
                  <span className="panel-kicker">Sound</span>
                  <h4>Original audio control</h4>
                  <p className="panel-description">
                    Keep the source audio when it helps the clip, or mute it when the visual story
                    is strong enough on its own.
                  </p>
                </div>
              </div>
              <label style={sidebarCheckboxLabelStyle}>
                <input
                  type="checkbox"
                  checked={muteOriginalAudio}
                  onChange={e => setMuteOriginalAudio(e.target.checked)}
                  style={{ marginRight: "8px" }}
                />
                Mute Original Audio
              </label>
              <div style={{ ...sidebarBodyTextStyle, marginTop: "10px" }}>
                {muteOriginalAudio
                  ? "Original audio will be muted for export."
                  : "Original audio stays active for preview and export."}
              </div>
            </section>
          </div>

          <div className="studio-sidebar" ref={studioSidebarRef}>
            <section className="studio-tool-guide" aria-label="Editing control guide">
              <strong>{CREATIVE_STUDIO_TOOLS.find(tool => tool.id === activeCreativeTool)?.label} controls</strong>
              <p>{CREATIVE_STUDIO_TOOLS.find(tool => tool.id === activeCreativeTool)?.helper}</p>
            </section>
            {/* Review must stay reachable when the compact timeline is hidden. */}
                  {incomingDirectorProposalRequest ? (
                    <section
                      className="studio-director-review"
                      aria-label="Incoming Director edit review"
                      data-testid="studio-director-review"
                    >
                      <div className="studio-director-review__heading">
                        <div>
                          <span className="panel-kicker">Incoming proposal</span>
                          <h4>Review one suggested cut</h4>
                          <p>The timeline stays unchanged until you approve this exact preview.</p>
                        </div>
                        {(!visibleDirectorReview || ["stale", "error"].includes(directorReviewState)) ? (
                          <button
                            type="button"
                            onClick={() => void openDirectorReview()}
                            disabled={directorReviewState === "working"}
                            data-testid="studio-director-review-open"
                          >
                            {visibleDirectorReview ? "Review again" : "Review incoming edit"}
                          </button>
                        ) : null}
                      </div>
                      {directorReviewError ? (
                        <p className="studio-director-review__error" role="alert">
                          {directorReviewError}
                        </p>
                      ) : null}
                      {directorReviewState === "working" ? (
                        <p role="status">Checking the proposal and signed-in reviewer…</p>
                      ) : null}
                      {visibleDirectorReview ? (
                        <>
                          <div className="studio-director-review__meta">
                            <span>Proposal <code>{visibleDirectorReview.proposal.proposalId}</code></span>
                            <span>Base revision {visibleDirectorReview.proposal.baseRevision}</span>
                            <span>{visibleDirectorReview.diff.operationLabel}</span>
                          </div>
                          {visibleDirectorReview.proposal.evidence?.type === "source_shot_boundary" ? (
                            <div className="studio-director-review__evidence"
                              data-testid="studio-director-review-evidence">
                              <strong>Detected source-shot boundary — proposed split requiring review</strong>
                              <span>Source boundary {formatDirectorReviewRange({
                                atTick: visibleDirectorReview.proposal.evidence.boundaryTick,
                              })}</span>
                              <span>Provider {visibleDirectorReview.proposal.evidence.provider}</span>
                              <span>Engine {visibleDirectorReview.proposal.evidence.engine}</span>
                              <span>Analysis artifact <code title={visibleDirectorReview.proposal.evidence.artifactHash}>
                                {visibleDirectorReview.proposal.evidence.artifactHash.slice(0, 12)}…
                              </code></span>
                              <span>Source bytes SHA-256 <code title={visibleDirectorReview.proposal.evidence.sourceSha256}>
                                {visibleDirectorReview.proposal.evidence.sourceSha256.slice(0, 12)}…
                              </code></span>
                              <span>Sample coverage {(visibleDirectorReview.proposal.evidence.sampleCoverage * 100).toFixed(1)}% (coverage, not model confidence)</span>
                              <span>Source identity {visibleDirectorReview.proposal.evidence.sourceIdentityState}</span>
                            </div>
                          ) : null}
                          <div className="studio-director-review__table-wrap">
                            <table>
                              <caption>Exact before and after source and programme timing, in ticks</caption>
                              <thead>
                                <tr>
                                  <th scope="col">Item</th>
                                  <th scope="col">Source before</th>
                                  <th scope="col">Source after</th>
                                  <th scope="col">Programme before</th>
                                  <th scope="col">Programme after</th>
                                  <th scope="col">Cue trim before</th>
                                  <th scope="col">Cue trim after</th>
                                </tr>
                              </thead>
                              <tbody>
                                {[...visibleDirectorReview.diff.clipRows,
                                  ...visibleDirectorReview.diff.timingRows].map((row, index) => (
                                  <tr key={`${row.label}-${index}`} data-testid={`studio-director-review-row-${index}`}>
                                    <th scope="row">{row.label}</th>
                                    <td>{formatDirectorReviewRange(row.beforeSourceRange)}</td>
                                    <td>{formatDirectorReviewRange(row.afterSourceRange)}</td>
                                    <td>{formatDirectorReviewRange(row.beforeProgrammeRange)}</td>
                                    <td>{formatDirectorReviewRange(row.afterProgrammeRange)}</td>
                                    <td>{formatDirectorReviewRange(Number.isSafeInteger(row.beforeTrimStartTick)
                                      ? { atTick: row.beforeTrimStartTick } : null)}</td>
                                    <td>{formatDirectorReviewRange(Number.isSafeInteger(row.afterTrimStartTick)
                                      ? { atTick: row.afterTrimStartTick } : null)}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                          {visibleDirectorReview.diff.timingRows.length === 0 ? (
                            <p>No linked cue or key timing changes.</p>
                          ) : null}
                          {directorReviewState === "ready" ? (
                            <div className="studio-director-review__actions">
                              <button
                                type="button"
                                className="is-approve"
                                onClick={() => void decideDirectorReview("approve")}
                                data-testid="studio-director-review-approve"
                              >
                                Approve and apply
                              </button>
                              <button
                                type="button"
                                onClick={() => void decideDirectorReview("reject")}
                                data-testid="studio-director-review-reject"
                              >
                                Reject proposal
                              </button>
                            </div>
                          ) : null}
                          {["approved", "rejected"].includes(directorReviewState) ? (
                            <p role="status">
                              {directorReviewState === "approved"
                                ? "Approval recorded on the server; edit applied to this project."
                                : "Rejection recorded on the server. The timeline is unchanged."}
                            </p>
                          ) : null}
                          <small>The server records the signed-in reviewer and decision; this project keeps a linked copy.</small>
                        </>
                      ) : null}
                    </section>
                  ) : null}

            <section
              className={`studio-panel clip-inspector-panel ${
                activeCreativeTool === "export" ? "is-export-hidden" : ""
              }`}
              data-testid="clip-studio-inspector"
            >
              <div
                className={`creative-director-panel ${
                  activeCreativeTool === "moments" ? "is-expanded" : "is-compact"
                }`}
              >
                <button
                  type="button"
                  className="creative-director-heading"
                  aria-expanded={activeCreativeTool === "moments"}
                  onClick={() => selectCreativeTool("moments")}
                >
                  <div>
                    <span>AI Creative Director</span>
                    <strong>
                      {activeCreativeTool === "moments"
                        ? "Tell it how the clip should feel"
                        : "Return to story direction"}
                    </strong>
                  </div>
                  <i aria-hidden="true">☷</i>
                </button>
                <button
                  type="button"
                  className="make-it-hit-button"
                  onClick={applyMakeItHit}
                  data-testid="make-it-hit-button"
                >
                  ✦ MAKE IT HIT
                </button>
                <div className="creator-mode-guide">
                  <div className="creator-mode-guide__heading">
                    <span>What are you making?</span>
                    <small>Pick the closest style. Every suggestion remains editable.</small>
                  </div>
                  <div className="creator-mode-grid">
                    {CREATOR_CONTENT_PROFILES.map(profile => (
                      <button
                        key={profile.id}
                        type="button"
                        className={contentProfile === profile.id ? "is-active" : ""}
                        onClick={() => applyContentProfile(profile.id)}
                        title={profile.helper}
                      >
                        {profile.label}
                      </button>
                    ))}
                  </div>
                  <p>{activeContentProfile.helper}</p>
                </div>
                <div className="signature-effects-panel">
                  <div className="signature-effects-heading">
                    <span>Signature transformation</span>
                    <button
                      type="button"
                      className={creativeEffectsEnabled ? "is-on" : ""}
                      aria-pressed={creativeEffectsEnabled}
                      onClick={() => {
                        setCreativeEffectsEnabled(current => !current);
                        setStudioActionMessage(
                          creativeEffectsEnabled
                            ? "Signature transformation removed. The clean edit stays untouched."
                            : "Signature transformation is live in After and protected by clean fallback on export."
                        );
                      }}
                    >
                      {creativeEffectsEnabled ? "On" : "Off"}
                    </button>
                  </div>
                  <div className="signature-style-grid">
                    {SIGNATURE_CREATIVE_STYLES.map(style => (
                      <button
                        key={style.id}
                        type="button"
                        className={creativePreset === style.id ? "is-active" : ""}
                        onClick={() => {
                          setCreativePreset(style.id);
                          setCreativeEffectsEnabled(true);
                          setComparisonMode("split");
                          window.setTimeout(() => focusComparisonPreview("moments", false), 0);
                          setStudioActionMessage(
                            `${style.label} is live. Before and After remain synchronized.`
                          );
                        }}
                        title={style.helper}
                      >
                        <span aria-hidden="true">{style.icon}</span>
                        <strong>{style.label}</strong>
                      </button>
                    ))}
                  </div>
                  {creativePreset === "motion_sculpture" ? (
                    <figure className="motion-sculpture-look-target">
                      <img
                        src={
                          SIGNATURE_CREATIVE_STYLES.find(style => style.id === "motion_sculpture")
                            ?.conceptImages?.[creativeIntensity]
                        }
                        alt={`Motion Sculpture ${creativeIntensity} visual target`}
                      />
                      <figcaption>
                        <span>Look target</span>
                        <strong>{creativeIntensity}</strong>
                        <small>Sharp subject · temporal silhouettes · stable background</small>
                      </figcaption>
                    </figure>
                  ) : null}
                  <div className="signature-intensity-row" aria-label="Creative intensity">
                    {CREATIVE_INTENSITIES.map(level => (
                      <button
                        key={level.id}
                        type="button"
                        className={creativeIntensity === level.id ? "is-active" : ""}
                        aria-pressed={creativeIntensity === level.id}
                        onClick={() => {
                          setCreativeIntensity(level.id);
                          setCreativeEffectsEnabled(true);
                        }}
                      >
                        {level.label}
                      </button>
                    ))}
                  </div>
                  <small>
                    One tap, editable intensity, clean fallback. Original and overlay audio stay
                    independent.
                  </small>
                </div>
                {workspaceMode === "signature" ? (
                  <button
                    type="button"
                    className="signature-color-shortcut"
                    onClick={() => selectCreativeTool("finish")}
                  >
                    <span>◐</span>
                    <strong>Open Color & Finish</strong>
                    <small>Grade, scopes, motion, texture and the complete rounded frame.</small>
                  </button>
                ) : null}
                <span className="creative-intent-label">Choose your intent</span>
                <div className="creative-intent-grid">
                  {CREATIVE_INTENTS.map(intent => (
                    <button
                      key={intent.id}
                      type="button"
                      className={creativeIntent === intent.id ? "is-active" : ""}
                      onClick={() => applyCreativeIntent(intent.id)}
                    >
                      <span aria-hidden="true">{intent.icon}</span>
                      {intent.label}
                    </button>
                  ))}
                </div>
                <div className="retention-score-card">
                  <div>
                    <span>Retention Score</span>
                    <strong>{retentionScore}</strong>
                  </div>
                  <p>
                    {retentionScore >= 84
                      ? "This edit keeps visual change and speech moving."
                      : "Apply the suggested improvements to strengthen retention."}
                    <small>↗ Live estimate · no render needed</small>
                  </p>
                </div>
                <div className="director-pacing-row">
                  <span>Pacing</span>
                  <div>
                    {["calm", "balanced", "energetic"].map(level => (
                      <button
                        key={level}
                        type="button"
                        aria-label={`${level[0].toUpperCase() + level.slice(1)} creative pacing`}
                        className={pacingLevel === level ? "is-active" : ""}
                        onClick={() => {
                          setPacingLevel(level);
                          setStudioActionMessage(
                            `${level[0].toUpperCase() + level.slice(1)} creative energy selected. Playback speed remains ${previewSpeed.toFixed(2)}×.`
                          );
                        }}
                      >
                        {level[0].toUpperCase() + level.slice(1)}
                      </button>
                    ))}
                  </div>
                </div>
                <button
                  type="button"
                  className="creative-improvements-card"
                  aria-label={
                    creativeImprovementsReady.length
                      ? `${creativeImprovementsReady.length} creative improvements ready to review`
                      : "All creative improvements applied"
                  }
                  onClick={() =>
                    selectCreativeTool(
                      creativeImprovementsReady[0] === "proof B-roll" ? "broll" : "captions"
                    )
                  }
                >
                  <span>
                    <strong>
                      {creativeImprovementsReady.length
                        ? `${creativeImprovementsReady.length} improvements ready`
                        : "All improvements applied"}
                    </strong>
                    <small>
                      {creativeImprovementsReady.length
                        ? "Review every suggestion before render"
                        : "Your live edit currently meets the director checks"}
                    </small>
                  </span>
                  <b aria-hidden="true">›</b>
                </button>
              </div>
              <StudioInspectorTabs
                mode={workspaceMode}
                activeTab={studioInspectorTab}
                onSelect={tabId => {
                  setStudioInspectorTab(tabId);
                  setActiveCreativeTool(tabId);
                }}
              />

              {studioInspectorTab === "cut" ? (
                <div className="clip-inspector-body cut-inspector-body" role="tabpanel">
                  <div className="inspector-heading-row">
                    <div>
                      <span className="panel-kicker">Remove unwanted parts</span>
                      <h4>Play, mark, remove, preview</h4>
                    </div>
                    <span className={`inspector-status-dot ${cutRangeIsReady ? "is-ready" : ""}`}>
                      {cutRangeIsReady ? "Ready" : "Choose range"}
                    </span>
                  </div>

                  <div className="cut-how-it-works">
                    <span>
                      <b>1</b> Play to the bad part
                    </span>
                    <span>
                      <b>2</b> Mark start and end
                    </span>
                    <span>
                      <b>3</b> Remove and preview the join
                    </span>
                  </div>

                  <section className="simple-cut-card">
                    <div className="simple-cut-heading">
                      <div>
                        <span className="panel-kicker">1-Tap Editing</span>
                        <strong>Trim or split at the playhead line</strong>
                      </div>
                    </div>
                    <div className="simple-cut-buttons">
                      <button
                        type="button"
                        className="simple-cut-btn is-primary"
                        onClick={splitCurrentClipAtPlayhead}
                        title="Split clip into two at the current playhead"
                        disabled={Boolean(trackStates[activeOverlay ? (activeOverlay.bRollMode ? "broll" : "graphics") : "video"]?.locked)}
                        data-testid="inspector-cut-split"
                      >
                        ✂️ Split at playhead
                      </button>
                      <button
                        type="button"
                        className="simple-cut-btn"
                        onClick={trimClipStartToPlayhead}
                        title="Cut everything before the playhead"
                        disabled={!!trackStates.video?.locked}
                        data-testid="inspector-cut-trim-start"
                      >
                        ⏮️ Trim start to here
                      </button>
                      <button
                        type="button"
                        className="simple-cut-btn"
                        onClick={trimClipEndToPlayhead}
                        title="Cut everything after the playhead"
                        disabled={!!trackStates.video?.locked}
                        data-testid="inspector-cut-trim-end"
                      >
                        ⏭️ Trim end to here
                      </button>
                      {timeline.length > 1 && (
                        <button
                          type="button"
                          className="simple-cut-btn is-danger"
                          onClick={deleteActiveTimelineClip}
                          title="Delete the currently selected clip"
                          disabled={!!trackStates.video?.locked}
                          data-testid="inspector-cut-delete"
                        >
                          🗑️ Delete this clip
                        </button>
                      )}
                    </div>
                  </section>

                  <details className="advanced-tool-section precision-edit-details">
                    <summary>⚙️ Advanced Pro Controls (Ripple, Slip, Slide, J/L-Cuts)</summary>
                    <section className="precision-edit-card">
                      <div className="sound-effects-heading">
                        <div>
                          <span className="panel-kicker">Precision edit</span>
                          <strong>Ripple, roll, slip, slide and split audio</strong>
                        </div>
                        <label>
                          <span>Nudge</span>
                          <select
                            aria-label="Precision edit nudge"
                            value={precisionNudge}
                            onChange={event => setPrecisionNudge(event.target.value === 'frame' ? 'frame' : Number(event.target.value))}
                          >
                            <option value="frame">1 frame</option>
                            <option value={0.1}>0.10s</option>
                            <option value={0.25}>0.25s</option>
                            <option value={1}>1.00s</option>
                          </select>
                        </label>
                      </div>
                      <div className="precision-edit-grid">
                        {["ripple", "roll", "slip", "slide", "jcut", "lcut"].map(mode => (
                          <div key={mode}>
                            <strong>
                              {mode === "jcut"
                                ? "J-cut"
                                : mode === "lcut"
                                  ? "L-cut"
                                  : mode[0].toUpperCase() + mode.slice(1)}
                            </strong>
                            <button
                              type="button"
                              aria-label={`${mode} earlier`}
                              onClick={() => applyPrecisionTimelineEdit(mode, -1)}
                              disabled={!!trackStates[['jcut', 'lcut'].includes(mode) ? 'originalAudio' : 'video']?.locked}
                            >
                              −
                            </button>
                            <button
                              type="button"
                              aria-label={`${mode} later`}
                              onClick={() => applyPrecisionTimelineEdit(mode, 1)}
                              disabled={!!trackStates[['jcut', 'lcut'].includes(mode) ? 'originalAudio' : 'video']?.locked}
                            >
                              +
                            </button>
                          </div>
                        ))}
                      </div>
                      <label className="inspector-check-row">
                        <input
                          type="checkbox"
                          checked={linkedSelection}
                          onChange={event => setLinkedSelection(event.target.checked)}
                        />
                        <span>
                          <strong>Link source video and audio</strong>
                          <small>Turn off for independent J-cuts, L-cuts and audio slips.</small>
                        </span>
                      </label>
                    </section>
                  </details>

                  <div className="cut-mark-actions">
                    <button type="button" onClick={() => markCutBoundary("start")}>
                      [ Mark remove start
                      <small>
                        {cutRangeStart === null
                          ? "At playhead"
                          : formatPreviewTimePrecise(cutRangeStart)}
                      </small>
                    </button>
                    <button type="button" onClick={() => markCutBoundary("end")}>
                      Mark remove end ]
                      <small>
                        {cutRangeEnd === null
                          ? "At playhead"
                          : formatPreviewTimePrecise(cutRangeEnd)}
                      </small>
                    </button>
                  </div>

                  <div className="inspector-time-grid cut-exact-range">
                    <label>
                      <span>Remove from</span>
                      <input
                        aria-label="Remove from time"
                        type="number"
                        min={0}
                        max={trimAwareDuration}
                        step={0.05}
                        placeholder="0.00"
                        value={cutRangeStart === null ? "" : cutRangeStart}
                        onChange={event =>
                          setCutRangeStart(
                            event.target.value === ""
                              ? null
                              : clampNumber(event.target.value, 0, trimAwareDuration, 0)
                          )
                        }
                      />
                    </label>
                    <label>
                      <span>Remove to</span>
                      <input
                        aria-label="Remove to time"
                        type="number"
                        min={0}
                        max={trimAwareDuration}
                        step={0.05}
                        placeholder={trimAwareDuration.toFixed(2)}
                        value={cutRangeEnd === null ? "" : cutRangeEnd}
                        onChange={event =>
                          setCutRangeEnd(
                            event.target.value === ""
                              ? null
                              : clampNumber(
                                  event.target.value,
                                  0,
                                  trimAwareDuration,
                                  trimAwareDuration
                                )
                          )
                        }
                      />
                    </label>
                  </div>
                  <p className="cut-exact-help">
                    Enter the exact bad section. The red range appears on the video timeline before
                    anything is removed.
                  </p>

                  {normalizedPendingCutRange ? (
                    <div className="pending-cut-summary" data-testid="pending-cut-summary">
                      <span>
                        Removing {formatPreviewTimePrecise(normalizedPendingCutRange.start)}–
                        {formatPreviewTimePrecise(normalizedPendingCutRange.end)}
                      </span>
                      <strong>
                        {(normalizedPendingCutRange.end - normalizedPendingCutRange.start).toFixed(
                          1
                        )}
                        s
                      </strong>
                    </div>
                  ) : null}

                  <div className="inspector-field">
                    <span>How should the remaining parts meet?</span>
                    <div className="join-transition-grid">
                      {JOIN_TRANSITIONS.map(transition => (
                        <button
                          key={transition.id}
                          type="button"
                          className={joinTransition === transition.id ? "is-active" : ""}
                          onClick={() => setJoinTransition(transition.id)}
                          title={transition.helper}
                        >
                          {transition.label}
                        </button>
                      ))}
                    </div>
                    <small>
                      {JOIN_TRANSITIONS.find(item => item.id === joinTransition)?.helper}
                      {joinTransition === "auto"
                        ? ` Recommended here: ${JOIN_TRANSITIONS.find(item => item.id === resolvedJoinTransition)?.label}.`
                        : ""}
                    </small>
                    {resolvedJoinTransition !== "clean_cut" ? (
                      <label className="inspector-range">
                        <span>
                          <b>Transition duration</b>
                          <strong>{joinTransitionDuration.toFixed(2)}s</strong>
                        </span>
                        <input
                          type="range"
                          aria-label="Transition duration"
                          min={0.04}
                          max={2}
                          step={0.02}
                          value={joinTransitionDuration}
                          onChange={event => setJoinTransitionDuration(Number(event.target.value))}
                        />
                      </label>
                    ) : null}
                  </div>

                  <div className="inspector-inline-actions">
                    <button
                      type="button"
                      onClick={clearPendingCutRange}
                      disabled={!cutRangeIsReady}
                    >
                      Clear marks
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        if (!normalizedPendingCutRange || !videoRef.current) return;
                        const sourceStart = Number(currentTimelineWindow.start || 0);
                        videoRef.current.currentTime =
                          sourceStart + Math.max(0, normalizedPendingCutRange.start - 0.65);
                        setVideoTime(videoRef.current.currentTime);
                        safePlayMediaElement(videoRef.current);
                        setStudioActionMessage(
                          "Previewing the lead-in. Remove the range to hear the finished join."
                        );
                      }}
                      disabled={!cutRangeIsReady}
                    >
                      Preview lead-in
                    </button>
                  </div>

                  <details className="advanced-tool-section stabilization-section">
                    <summary>Video stabilization</summary>
                    <label className="inspector-toggle-row">
                      <span>
                        <b>Stabilize shaky footage</b>
                        <small>Motion analysis smooths camera shake before reframing.</small>
                      </span>
                      <input
                        type="checkbox"
                        checked={stabilization.enabled}
                        onChange={event =>
                          setStabilization(current => ({
                            ...current,
                            enabled: event.target.checked,
                          }))
                        }
                      />
                    </label>
                    {stabilization.enabled ? (
                      <>
                        <label className="inspector-range">
                          <span>
                            <b>Smoothing</b>
                            <strong>{stabilization.strength}%</strong>
                          </span>
                          <input
                            type="range"
                            min={5}
                            max={100}
                            value={stabilization.strength}
                            onChange={event =>
                              setStabilization(current => ({
                                ...current,
                                strength: Number(event.target.value),
                              }))
                            }
                          />
                        </label>
                        <label className="inspector-select-field">
                          <span>Edge crop</span>
                          <select
                            value={stabilization.crop}
                            onChange={event =>
                              setStabilization(current => ({
                                ...current,
                                crop: event.target.value,
                              }))
                            }
                          >
                            <option value="adaptive">Adaptive crop</option>
                            <option value="minimal">Minimal crop</option>
                            <option value="scale">Scale to hide edges</option>
                          </select>
                        </label>
                        <label className="inspector-check-row">
                          <input
                            type="checkbox"
                            checked={stabilization.rollingShutter}
                            onChange={event =>
                              setStabilization(current => ({
                                ...current,
                                rollingShutter: event.target.checked,
                              }))
                            }
                          />
                          <span>
                            <strong>Rolling-shutter repair</strong>
                            <small>Corrects phone-camera wobble before stabilization.</small>
                          </span>
                        </label>
                      </>
                    ) : null}
                  </details>

                  <button
                    type="button"
                    className="inspector-primary-action is-danger-safe"
                    onClick={removePendingCutRange}
                    disabled={!cutRangeIsReady || !!trackStates.video?.locked}
                    data-testid="remove-marked-range"
                  >
                    ✂ Remove marked part & preview join
                  </button>
                  <p className="cut-safety-copy">
                    Non-destructive: Undo brings the removed part back.
                  </p>
                </div>
              ) : null}

              {studioInspectorTab === "hook" ? (
                <div className="clip-inspector-body" role="tabpanel">
                  <div className="inspector-heading-row">
                    <div>
                      <span className="panel-kicker">Opening moment</span>
                      <h4>Make the first seconds impossible to skip</h4>
                    </div>
                    <span className={`inspector-status-dot ${addHook ? "is-ready" : ""}`}>
                      {addHook ? "Live" : "Off"}
                    </span>
                  </div>

                  <label className="inspector-toggle-row">
                    <span>
                      <b>Enable opening hook</b>
                      <small>
                        Off removes every hook treatment while keeping Signature effects.
                      </small>
                    </span>
                    <input
                      type="checkbox"
                      checked={addHook}
                      onChange={event => {
                        setOpeningHookEnabled(event.target.checked);
                        setStudioActionMessage(
                          event.target.checked
                            ? "Opening hook enabled. Choose its exact range and treatment below."
                            : "Opening hook removed. Signature effects and other approved edits remain."
                        );
                      }}
                    />
                  </label>

                  <div className="inspector-ai-card">
                    <div>
                      <span>AI hook suggestion</span>
                      <strong>{currentHookSuggestion.textSuggestion || normalizedHookText}</strong>
                      <small>
                        {hookAnalysisStatus === "ready"
                          ? hookAnalysisMessage || "Strong opening range detected."
                          : "Finds the sharpest visual and spoken opening in this moment."}
                      </small>
                    </div>
                    <button
                      type="button"
                      onClick={runSmartHookSuggestion}
                      disabled={hookAnalysisStatus === "analyzing"}
                    >
                      {hookAnalysisStatus === "analyzing" ? "Finding…" : "Try another"}
                    </button>
                  </div>

                  <label className="inspector-field">
                    <span>Hook text</span>
                    <textarea
                      aria-label="Hook text"
                      value={hookText}
                      onChange={event => setHookText(normalizeHookText(event.target.value))}
                      rows={3}
                      maxLength={120}
                      placeholder="Give viewers a reason to stay"
                    />
                    <small>
                      {hookText.length}/120 · keep it clear enough to read in one glance
                    </small>
                  </label>

                  <div className="inspector-field">
                    <span>Motion preset</span>
                    <div className="inspector-choice-grid is-three">
                      {[
                        ["blur_reveal", "Blur Reveal"],
                        ["zoom_focus", "Zoom Focus"],
                        ["freeze_text", "Freeze frame"],
                      ].map(([value, label]) => (
                        <button
                          key={value}
                          type="button"
                          className={hookTemplate === value ? "is-active" : ""}
                          aria-pressed={hookTemplate === value}
                          onClick={() => {
                            setAddHook(true);
                            applyHookTemplate(value);
                            setStudioActionMessage(`${label} is live in the After preview.`);
                          }}
                        >
                          {label}
                        </button>
                      ))}
                    </div>
                    <small className="inspector-preset-note">
                      {hookTemplateConfig.description}
                    </small>
                  </div>

                  <label className="inspector-range">
                    <span>
                      <b>Duration</b>
                      <strong>{hookDuration.toFixed(1)}s</strong>
                    </span>
                    <input
                      type="range"
                      min={hookMinDuration}
                      max={hookMaxDuration}
                      step={0.1}
                      value={hookDuration}
                      onChange={event => setHookDuration(Number(event.target.value))}
                    />
                  </label>

                  <div className="inspector-inline-actions">
                    <button type="button" onClick={() => previewHookSegment(false)}>
                      Preview once
                    </button>
                    <button
                      type="button"
                      className={hookFocusMode ? "is-active" : ""}
                      onClick={() => setHookFocusMode(value => !value)}
                    >
                      {hookFocusMode ? "Tap preview now" : "Select focal point"}
                    </button>
                  </div>

                  <button
                    type="button"
                    className="inspector-primary-action"
                    onClick={() => {
                      setAddHook(true);
                      setComparisonMode("split");
                      previewHookSegment(false);
                      window.setTimeout(() => focusComparisonPreview("hook", true), 0);
                      setStudioActionMessage(
                        "Hook applied. Compare the untouched source and edited opening side by side."
                      );
                    }}
                  >
                    ✦ Apply Hook
                  </button>
                </div>
              ) : null}

              {studioInspectorTab === "reframe" ? (
                <div
                  className="clip-inspector-body reframe-inspector-body"
                  role="tabpanel"
                  data-testid="auto-reframe-inspector"
                >
                  <div className="inspector-heading-row">
                    <div>
                      <span className="panel-kicker">Auto Reframe</span>
                      <h4>Frame the speaker or the complete conversation</h4>
                    </div>
                    <span className={`inspector-status-dot ${effectiveSmartCrop ? "is-ready" : ""}`}>
                      {effectiveSmartCrop ? "Enabled" : "Off"}
                    </span>
                  </div>

                  <label className="inspector-toggle-row">
                    <span>
                      <b>Enable Auto Reframe</b>
                      <small>
                        Follow a reviewed subject, preserve the full source, or compose synchronized cameras.
                      </small>
                    </span>
                    <input
                      type="checkbox"
                      data-testid="auto-reframe-toggle"
                      checked={effectiveSmartCrop}
                      onChange={event => {
                        const enabled = event.target.checked;
                        const mode = smartCropMode === "off" ? "speaker_track" : smartCropMode;
                        if (enabled) setSmartCropMode(mode);
                        recordReframeModeAtPlayhead(enabled ? mode : "off");
                        setSmartCrop(enabled);
                        setComparisonMode("after");
                        setStudioActionMessage(
                          enabled
                            ? "Auto Reframe enabled."
                            : "Auto Reframe disabled. The source framing is preserved."
                        );
                      }}
                    />
                  </label>

                  <div className="inspector-field">
                    <span>Output frame</span>
                    <div className="reframe-aspect-grid">
                      {REFRAME_ASPECT_OPTIONS.map(option => (
                        <button
                          type="button"
                          key={option.value}
                          data-testid={`reframe-aspect-${option.value.replace(":", "-")}`}
                          title={`${option.label} · ${option.helper}`}
                          className={reframeAspect === option.value ? "is-active" : ""}
                          aria-pressed={reframeAspect === option.value}
                          onClick={() => {
                            setReframeAspect(option.value);
                            setSmartCrop(true);
                            setComparisonMode("after");
                            setStudioActionMessage(
                              `${option.value} framing selected. Preview and export now use the same canvas.`
                            );
                          }}
                        >
                          <strong>{option.label}</strong>
                          <small>{option.helper}</small>
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="inspector-field">
                    <span>Framing behaviour</span>
                    <div className="inspector-choice-grid is-three reframe-behaviour-grid">
                      <button
                        type="button"
                        data-testid="reframe-follow-subject"
                        className={effectiveSmartCropMode === "speaker_track" ? "is-active" : ""}
                        aria-pressed={effectiveSmartCropMode === "speaker_track"}
                        onClick={() => openSpeakerTracking(true)}
                      >
                        Solo Speaker
                      </button>
                      <button
                        type="button"
                        data-testid="reframe-preserve-frame"
                        className={effectiveSmartCropMode === "center" ? "is-active" : ""}
                        aria-pressed={effectiveSmartCropMode === "center"}
                        onClick={showEveryoneSplit}
                      >
                        Show Everyone
                      </button>
                      <button
                        type="button"
                        data-testid="reframe-speaker-stack"
                        className={effectiveSmartCropMode === "group_stack" ? "is-active" : ""}
                        aria-pressed={effectiveSmartCropMode === "group_stack"}
                        disabled={!speakerStackIsReady}
                        title={
                          speakerStackIsReady
                            ? `Compose multi-camera layout for participants`
                            : `Load video to enable multi-camera layout`
                        }
                        onClick={() => {
                          setSmartCrop(true);
                          setSmartCropMode("group_stack");
                          recordReframeModeAtPlayhead("group_stack");
                          setComparisonMode("after");
                          setStudioActionMessage(
                            `${speakerStackCameraCount}-camera composition selected. Every participant is live in preview and export.`
                          );
                        }}
                      >
                        Multi-Camera
                      </button>
                    </div>
                    <small>
                      {effectiveSmartCropMode === "speaker_track"
                        ? "Use reviewed framing points to keep the speaker comfortably in view."
                        : effectiveSmartCropMode === "group_stack"
                          ? `Use the ${speakerStackCameraCount}-camera layout so every participant remains live and visible together.`
                          : reframeAspect === "9:16"
                            ? "Two crops of the same source, stacked vertically. Adjust both speakers below; no second camera file is required."
                            : "Use the untouched wide composition when the whole set and every participant must remain visible."}
                    </small>
                  </div>

                  <details
                    className={`speaker-source-setup ${speakerStackIsReady ? "is-ready" : ""}`}
                    data-testid="speaker-source-setup"
                  >
                    <summary>
                      <span>Multi-Camera setup</span>
                      <small>
                        {speakerStackIsReady
                          ? speakerStackSources.length > speakerStackCameraCount
                            ? `${speakerStackSources.length} in bank · ${speakerStackCameraCount} live`
                            : `${speakerStackCameraCount} synchronized angles ready`
                          : `Needs ${speakerStackCameraCount} synchronized angles`}
                      </small>
                    </summary>
                    <div className="speaker-source-setup-body">
                    <div className="speaker-stack-editor-heading">
                      <div>
                        <strong>Clean camera sources</strong>
                        <small>
                          Add 2–12 real full-size angles, then route any four into the live grid.
                          Reaction insets are never treated as independent cameras.
                        </small>
                      </div>
                      <button
                        type="button"
                        data-testid="add-clean-speaker-angle"
                        onClick={() => speakerStackSourceInputRef.current?.click()}
                      >
                        + Add angle
                      </button>
                    </div>
                    <input
                      ref={speakerStackSourceInputRef}
                      type="file"
                      accept="video/*"
                      multiple
                      data-testid="clean-speaker-angle-input"
                      onChange={addCleanSpeakerAngle}
                      hidden
                    />
                    <div className="multicam-layout-picker" role="group" aria-label="Camera layout">
                      {[
                        [2, "stack_2", "2 cameras", "Top / bottom"],
                        [3, "hero_3", "3 cameras", "Hero + duo"],
                        [4, "grid_4", "4+ sources", "Route any 4 · 2 × 2"],
                      ].map(([count, layout, label, helper]) => (
                        <button
                          key={layout}
                          type="button"
                          data-testid={`multicam-layout-${count}`}
                          className={speakerStackCameraCount === count ? "is-active" : ""}
                          aria-pressed={speakerStackCameraCount === count}
                          onClick={() => {
                            setSpeakerStackCameraCount(count);
                            setSpeakerStackLayout(layout);
                            setSpeakerStackSyncConfirmed(false);
                          }}
                        >
                          <strong>{label}</strong>
                          <small>{helper}</small>
                        </button>
                      ))}
                    </div>
                    {speakerStackCameraCount === 2 ? (
                      <div
                        className="multicam-composition-picker"
                        role="group"
                        aria-label="Two camera composition"
                      >
                        {[
                          ["stack_2", "Top / Bottom", "Equal stacked speakers"],
                          ["split_2", "50 / 50", "Left and right split"],
                          ["pip_2", "Picture in picture", "One full, one rounded PIP"],
                          ["active_2", "Speaker + reaction", "Reviewed focus cuts"],
                          ["spotlight_2", "Speaker spotlight", "Full-frame reviewed cuts"],
                        ].map(([layout, label, helper]) => (
                          <button
                            key={layout}
                            type="button"
                            data-testid={`multicam-composition-${layout}`}
                            className={speakerStackLayout === layout ? "is-active" : ""}
                            aria-pressed={speakerStackLayout === layout}
                            onClick={() => {
                              setSpeakerStackLayout(layout);
                              setSmartCrop(true);
                              setSmartCropMode("group_stack");
                              setComparisonMode("after");
                              setStudioActionMessage(`${label} camera composition is live in preview.`);
                            }}
                          >
                            <strong>{label}</strong>
                            <small>{helper}</small>
                          </button>
                        ))}
                      </div>
                    ) : null}
                    {speakerStackCameraCount === 2 &&
                    ["active_2", "spotlight_2"].includes(speakerStackLayout) ? (
                      <section
                        className="speaker-focus-director"
                        aria-labelledby="speaker-focus-director-title"
                      >
                        <div className="speaker-focus-director-heading">
                          <div>
                            <strong id="speaker-focus-director-title">Reviewed speaker cuts</strong>
                            <small>
                              Mark who is speaking at the playhead to direct the preview.
                              These reviewed cuts are saved with the project and sent to the media worker.
                            </small>
                          </div>
                          {speakerFocusCuts.length ? (
                            <button
                              type="button"
                              className="speaker-focus-clear"
                              onClick={() => setSpeakerFocusCuts([])}
                            >
                              Clear
                            </button>
                          ) : null}
                        </div>
                        <div className="speaker-focus-actions" role="group" aria-label="Mark active speaker">
                          {["top", "bottom"].map(slot => (
                            <button
                              key={slot}
                              type="button"
                              data-testid={`speaker-focus-${slot}`}
                              onClick={() => addSpeakerFocusCutAtPlayhead(slot)}
                            >
                              <span>{slot === "top" ? "1" : "2"}</span>
                              Mark {MULTICAM_SLOT_LABELS[slot]}
                            </button>
                          ))}
                        </div>
                        {speakerFocusCuts.length ? (
                          <div className="speaker-focus-cut-list" aria-label="Reviewed speaker cut list">
                            {speakerFocusCuts.map(cut => (
                              <div key={cut.id} className="speaker-focus-cut">
                                <button
                                  type="button"
                                  onClick={() => seekLiveEditTimelineItem(cut.time, { play: false })}
                                >
                                  <strong>{MULTICAM_SLOT_LABELS[cut.slot]}</strong>
                                  <span>{cut.time.toFixed(2)}s</span>
                                </button>
                                <button
                                  type="button"
                                  className="speaker-focus-cut-remove"
                                  aria-label={`Remove ${MULTICAM_SLOT_LABELS[cut.slot]} focus cut at ${cut.time.toFixed(2)} seconds`}
                                  onClick={() =>
                                    setSpeakerFocusCuts(current =>
                                      current.filter(currentCut => currentCut.id !== cut.id)
                                    )
                                  }
                                >
                                  ×
                                </button>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="speaker-focus-empty">
                            Until a mark is added, Camera 1 remains the explicit fallback.
                          </p>
                        )}
                      </section>
                    ) : null}
                    <div className="speaker-source-selectors">
                      {activeSpeakerStackSlots.map(slot => {
                        const label =
                          slot === "top"
                            ? "Top speaker source"
                            : slot === "bottom"
                              ? "Bottom speaker source"
                              : `${MULTICAM_SLOT_LABELS[slot]} source`;
                        return (
                        <label key={slot}>
                          <span>{label}</span>
                          <select
                            aria-label={label}
                            value={resolvedSpeakerStackSourceIds[slot] || ""}
                            onChange={event => {
                              setSpeakerStackSyncConfirmed(false);
                              setSpeakerStackSourceIds(current => ({
                                ...current,
                                [slot]: event.target.value,
                              }));
                            }}
                          >
                            <option value="" disabled>
                              Choose a clean angle
                            </option>
                            {speakerStackSourceOptions.map(option => (
                              <option
                                key={`${slot}-${option.id}`}
                                value={option.id}
                                disabled={
                                  activeSpeakerStackSlots.some(
                                    otherSlot =>
                                      otherSlot !== slot &&
                                      option.id === resolvedSpeakerStackSourceIds[otherSlot]
                                  )
                                }
                              >
                                {option.label}
                              </option>
                            ))}
                          </select>
                        </label>
                        );
                      })}
                    </div>
                    <div className="speaker-source-offsets">
                      {activeSpeakerStackSlots.map(slot => {
                        const label =
                          slot === "top"
                            ? "Top angle start"
                            : slot === "bottom"
                              ? "Bottom angle start"
                              : `${MULTICAM_SLOT_LABELS[slot]} start`;
                        return (
                        <label key={slot}>
                          <span>{label}</span>
                          <input
                            type="number"
                            min="0"
                            step="0.1"
                            aria-label={label}
                            value={speakerStackOffsets[slot]}
                            onChange={event => {
                              setSpeakerStackSyncConfirmed(false);
                              setSpeakerStackOffsets(current => ({
                                ...current,
                                [slot]: Math.max(0, Number(event.target.value || 0)),
                              }));
                            }}
                          />
                          <small>seconds</small>
                        </label>
                        );
                      })}
                    </div>
                    <label className="speaker-stack-sync-confirmation">
                      <input
                        type="checkbox"
                        checked={speakerStackSyncConfirmed}
                        disabled={!hasCleanSpeakerStackSources}
                        aria-label="Angles share the same source timecode"
                        onChange={event => setSpeakerStackSyncConfirmed(event.target.checked)}
                      />
                      <span>
                        <strong>Angles share the same source timecode</strong>
                        <small>
                          Confirm only after every preview shows the exact same spoken moment.
                          Unrelated excerpts are invalid camera feeds.
                        </small>
                      </span>
                    </label>
                    <span className={`speaker-source-readiness ${speakerStackIsReady ? "is-ready" : ""}`}>
                      {speakerStackIsReady
                        ? `${speakerStackCameraCount} synchronized moving camera angles ready${
                            speakerStackSources.length > speakerStackCameraCount
                              ? ` · ${speakerStackSources.length} in source bank`
                              : ""
                          }`
                        : hasCleanSpeakerStackSources
                          ? "Align the same spoken moment, then confirm synchronization"
                          : `Add and select ${speakerStackCameraCount} distinct clean angles to unlock multicamera`}
                    </span>
                    </div>
                  </details>

                  <div className="reframe-safety-card">
                    <span aria-hidden="true">⌗</span>
                    <div>
                      <strong>
                        {effectiveSmartCropMode === "speaker_track"
                          ? reframeKeyframes.length
                            ? "Reviewed follow path controls the crop"
                            : "Centered close-up · add speaker marks"
                          : effectiveSmartCropMode === "group_stack"
                            ? ["active_2", "spotlight_2"].includes(speakerStackLayout)
                              ? speakerFocusCuts.length
                                ? `${speakerFocusCuts.length} reviewed speaker cut${speakerFocusCuts.length === 1 ? "" : "s"}`
                                : "Manual speaker focus needs marks"
                              : `${speakerStackCameraCount} live camera frames`
                          : reframeAspect !== "16:9" ? "Two editable speaker crops" : "Full source frame"}
                      </strong>
                      <small>
                        {effectiveSmartCropMode === "speaker_track"
                          ? reframeKeyframes.length
                            ? "The After canvas interpolates the reviewed framing points below, and export receives the same timed path."
                            : "No face or speaker tracking data exists for this clip yet. Preview and export remain centered until reviewed framing points are added."
                          : effectiveSmartCropMode === "group_stack"
                            ? ["active_2", "spotlight_2"].includes(speakerStackLayout)
                              ? speakerFocusCuts.length
                                ? "Each reviewed mark switches the programme monitor. The same timed camera choices are sent to export."
                                : "No automated speaker claim is being made. Add reviewed speaker marks at the playhead to direct each focus change."
                              : `${speakerStackCameraCount} synchronized camera angles show the same source moment in the selected layout. Programme audio remains shared, and every crop anchor is included in export.`
                          : reframeAspect !== "16:9"
                            ? "Preview and export use the same top and bottom crop positions. Check both faces; these are crops of your source, not detected speaker identities."
                            : "The complete source is fitted into the landscape canvas."}
                      </small>
                    </div>
                  </div>

                  {effectiveSmartCropMode === "group_stack" ? (
                    <div className="speaker-stack-editor" data-testid="speaker-stack-editor">
                      <div className="speaker-stack-editor-heading">
                        <div>
                          <strong>{speakerStackCameraCount}-camera framing</strong>
                          <small>Adjust every real moving crop—no generated fill.</small>
                        </div>
                        <button
                          type="button"
                          onClick={() => setSpeakerStackFraming(DEFAULT_SPEAKER_STACK_FRAMING)}
                        >
                          Reset
                        </button>
                      </div>
                      <fieldset className="speaker-stack-frame-controls speaker-layout-controls">
                        <legend>Layout geometry</legend>
                        {speakerStackLayout === "stack_2" ? (
                          <label>
                            <span>
                              Split point <b>{Math.round(speakerStackFraming.dividerPercent)}%</b>
                            </span>
                            <input
                              type="range"
                              min="35"
                              max="65"
                              value={speakerStackFraming.dividerPercent}
                              aria-label="Stack split point"
                              onChange={event =>
                                setSpeakerStackFraming(current => ({
                                  ...current,
                                  dividerPercent: Number(event.target.value),
                                }))
                              }
                            />
                          </label>
                        ) : null}
                        <label>
                          <span>
                            Panel gap <b>{Number(speakerStackFraming.gapPercent).toFixed(1)}%</b>
                          </span>
                          <input
                            type="range"
                            min="0"
                            max="3"
                            step="0.1"
                            value={speakerStackFraming.gapPercent}
                            aria-label="Camera panel gap"
                            onChange={event =>
                              setSpeakerStackFraming(current => ({
                                ...current,
                                gapPercent: Number(event.target.value),
                              }))
                            }
                          />
                        </label>
                        {["pip_2", "active_2"].includes(speakerStackLayout) ? (
                          <>
                            <div className="speaker-secondary-presets" role="group" aria-label="Reaction frame position">
                              {[
                                ["Top left", 20, 20],
                                ["Top right", 80, 20],
                                ["Bottom left", 20, 80],
                                ["Bottom right", 80, 80],
                              ].map(([label, x, y]) => (
                                <button
                                  key={label}
                                  type="button"
                                  aria-label={`Place reaction frame ${label.toLowerCase()}`}
                                  className={
                                    Math.abs(speakerStackFraming.secondaryXPercent - x) < 2 &&
                                    Math.abs(speakerStackFraming.secondaryYPercent - y) < 2
                                      ? "is-active"
                                      : ""
                                  }
                                  onClick={() =>
                                    setSpeakerStackFraming(current => ({
                                      ...current,
                                      secondaryXPercent: x,
                                      secondaryYPercent: y,
                                    }))
                                  }
                                >
                                  {label}
                                </button>
                              ))}
                            </div>
                            <label>
                              <span>
                                Reaction size <b>{Math.round(speakerStackFraming.secondarySizePercent)}%</b>
                              </span>
                              <input
                                type="range"
                                min="20"
                                max="46"
                                value={speakerStackFraming.secondarySizePercent}
                                aria-label="Reaction frame size"
                                onChange={event =>
                                  setSpeakerStackFraming(current => ({
                                    ...current,
                                    secondarySizePercent: Number(event.target.value),
                                  }))
                                }
                              />
                            </label>
                          </>
                        ) : null}
                      </fieldset>
                      {activeSpeakerStackSlots.map(speaker => {
                        const label =
                          speaker === "top"
                            ? "Top speaker"
                            : speaker === "bottom"
                              ? "Bottom speaker"
                              : MULTICAM_SLOT_LABELS[speaker];
                        return (
                        <fieldset key={speaker} className="speaker-stack-frame-controls">
                          <legend>{label}</legend>
                          <label>
                            <span>
                              Horizontal <b>{Math.round(speakerStackFraming[speaker].x)}%</b>
                            </span>
                            <input
                              type="range"
                              min="0"
                              max="100"
                              value={speakerStackFraming[speaker].x}
                              aria-label={`${label} horizontal position`}
                              onChange={event =>
                                updateSpeakerStackFrame(speaker, "x", event.target.value)
                              }
                            />
                          </label>
                          <label>
                            <span>
                              Vertical <b>{Math.round(speakerStackFraming[speaker].y)}%</b>
                            </span>
                            <input
                              type="range"
                              min="0"
                              max="100"
                              value={speakerStackFraming[speaker].y}
                              aria-label={`${label} vertical position`}
                              onChange={event =>
                                updateSpeakerStackFrame(speaker, "y", event.target.value)
                              }
                            />
                          </label>
                          <label>
                            <span>
                              Crop size <b>{Number(speakerStackFraming[speaker].zoom).toFixed(2)}×</b>
                            </span>
                            <input
                              type="range"
                              min="1"
                              max="7"
                              step="0.05"
                              value={speakerStackFraming[speaker].zoom}
                              aria-label={`${label} crop size`}
                              onChange={event =>
                                updateSpeakerStackFrame(speaker, "zoom", event.target.value)
                              }
                            />
                          </label>
                        </fieldset>
                        );
                      })}
                    </div>
                  ) : null}

                  {effectiveSmartCropMode === "center" && reframeAspect !== "16:9" ? (
                    <div className="speaker-stack-editor" data-testid="source-split-editor">
                      <strong>Show Everyone · split framing</strong>
                      <small>Position each crop over a real full-size speaker view. Small reaction windows are never enlarged into split panels.</small>
                      <div className="multicam-layout-grid" aria-label="Show Everyone source layout">
                        <button type="button" onClick={() => setSpeakerStackFraming(current => ({
                          ...current, trackSpeakers: false,
                          top: { ...SOURCE_SPLIT_SIDE_BY_SIDE_FRAMING.top, keyframes: [] },
                          bottom: { ...SOURCE_SPLIT_SIDE_BY_SIDE_FRAMING.bottom, keyframes: [] },
                        }))}>Side-by-side source</button>
                      </div>
                      <div className="speaker-focus-director" data-testid="show-everyone-order-director">
                        <strong>Active speaker placement</strong>
                        <small>Keep both panels and record who appears on top at the playhead.</small>
                        <div className="speaker-focus-actions" role="group" aria-label="Put active speaker on top">
                          <button
                            type="button"
                            className={activeShowEveryonePrimarySlot === "top" ? "is-active" : ""}
                            aria-pressed={activeShowEveryonePrimarySlot === "top"}
                            data-testid="show-everyone-top-first"
                            onClick={() => addSpeakerFocusCutAtPlayhead("top")}
                          >
                            Speaker 1 on top
                          </button>
                          <button
                            type="button"
                            className={activeShowEveryonePrimarySlot === "bottom" ? "is-active" : ""}
                            aria-pressed={activeShowEveryonePrimarySlot === "bottom"}
                            data-testid="show-everyone-bottom-first"
                            onClick={() => addSpeakerFocusCutAtPlayhead("bottom")}
                          >
                            Speaker 2 on top
                          </button>
                        </div>
                        <small>{speakerFocusCuts.length} timed speaker-order cut{speakerFocusCuts.length === 1 ? "" : "s"} on the framing track.</small>
                      </div>
                      <button type="button" data-testid="analyze-speaker-faces" disabled={faceTrackingStatus === "processing"}
                        onClick={() => analyzeSpeakerFaces()}>{faceTrackingStatus === "processing" ? "Analyzing faces…" : "Detect & follow both faces"}</button>
                      <p role="status">{faceTrackingMessage || "Set each panel over its speaker, then analyze. Review the detected follow paths before export."}</p>
                      <label className="inspector-toggle-row">
                        <span><b>Track both speakers</b><small>Follow each panel’s reviewed timed positions while keeping the split.</small></span>
                        <input type="checkbox" aria-label="Track both speakers" checked={!!speakerStackFraming.trackSpeakers}
                          onChange={event => setSpeakerStackFraming(current => ({ ...current, trackSpeakers: event.target.checked }))} />
                      </label>
                      {["top", "bottom"].map(slot => (
                        <fieldset key={slot} className="speaker-stack-frame-controls">
                          <legend>{slot === "top" ? "Top speaker" : "Bottom speaker"}</legend>
                          {speakerStackFraming.trackSpeakers ? <>
                            <button type="button" onClick={() => updateSplitSpeakerFraming(slot, {}, true)}
                              aria-label={`Add ${slot} speaker tracking point`}>+ Track here · {previewTimelineTime.toFixed(2)}s</button>
                            <small data-testid={`${slot}-tracking-status`}>{speakerStackFraming[slot].keyframes?.length || 0} reviewed tracking points</small>
                            <button type="button" aria-label={`Clear ${slot} speaker tracking points`}
                              onClick={() => setSpeakerStackFraming(current => ({ ...current, [slot]: { ...current[slot], keyframes: [] } }))}>Clear points</button>
                            <div className="reframe-keyframe-list">
                              {(speakerStackFraming[slot].keyframes || []).map(mark => <button key={mark.time} type="button"
                                onClick={() => seekLiveEditTimelineItem(mark.time, { play: false })}>{mark.time.toFixed(2)}s</button>)}
                            </div>
                          </> : null}
                          {[["x", "Horizontal", 0, 100, 1], ["y", "Vertical", 0, 100, 1], ["zoom", "Zoom", 1, 7, 0.05]].map(([key, label, min, max, step]) => (
                            <label key={key}>
                              <span>{label} <b>{Number(key !== "zoom" && speakerStackFraming.trackSpeakers && speakerStackFraming[slot].keyframes?.length
                                ? interpolateReframeKeyframes(speakerStackFraming[slot].keyframes, previewTimelineTime)[key] : speakerStackFraming[slot][key]).toFixed(key === "zoom" ? 2 : 0)}{key === "zoom" ? "×" : "%"}</b></span>
                              <input type="range" min={min} max={max} step={step}
                                aria-label={`${slot} split ${label.toLowerCase()}`}
                                value={key !== "zoom" && speakerStackFraming.trackSpeakers && speakerStackFraming[slot].keyframes?.length
                                  ? interpolateReframeKeyframes(speakerStackFraming[slot].keyframes, previewTimelineTime)[key] : speakerStackFraming[slot][key]}
                                onChange={event => updateSplitSpeakerFraming(slot, { [key]: Number(event.target.value) })} />
                            </label>
                          ))}
                        </fieldset>
                      ))}
                    </div>
                  ) : null}

                  {effectiveSmartCropMode === "speaker_track" ? (
                  <div className="reframe-corrections-card">
                    <button type="button" className="mini-toggle-btn" data-testid="analyze-speaker-faces" disabled={faceTrackingStatus === "processing"}
                      onClick={() => analyzeSpeakerFaces()}>{faceTrackingStatus === "processing" ? "Analyzing faces…" : "Detect & follow face"}</button>
                    <button type="button" className="mini-toggle-btn" data-testid="analyze-source-shots" disabled={faceTrackingStatus === "processing"}
                      onClick={() => analyzeSpeakerFaces("source_shots")}>Follow existing camera cuts</button>
                    <small>For an already-edited video: follow the foreground face in each shot. No separate camera files needed.</small>
                    <p role="status">{faceTrackingMessage || "Position the crop over your speaker, then analyze real face movement."}</p>
                    <label className="inspector-field">
                      <span>Speaker zoom <b>{speakerTrackZoom.toFixed(2)}×</b></span>
                      <input type="range" min="1" max="3" step="0.05" aria-label="Speaker zoom"
                        value={speakerTrackZoom} onChange={event => {
                          setSpeakerTrackZoom(Number(event.target.value));
                          setSafeFaceFraming(false);
                          setVideoFit("cover");
                        }} />
                    </label>
                    <label className="inspector-field">
                      <span>Picture fill for this camera shot <b>{Number(
                        [...normalizeReframeModeCuts(reframeModeCuts)].reverse().find(
                          cut => cut.time <= previewTimelineTime + 0.001 && cut.mode === "speaker_track"
                        )?.zoom || 1
                      ).toFixed(2)}×</b></span>
                      <input type="range" min="1" max="1.5" step="0.01"
                        aria-label="Picture fill for this camera shot"
                        value={Number([...normalizeReframeModeCuts(reframeModeCuts)].reverse().find(
                          cut => cut.time <= previewTimelineTime + 0.001 && cut.mode === "speaker_track"
                        )?.zoom || 1)}
                        onChange={event => setPictureFillForCurrentShot(Number(event.target.value))} />
                      <small>Use this to remove black bars inside the rounded frame. The value stays on the Director timeline until the next camera cut.</small>
                    </label>
                    <div className="reframe-corrections-heading">
                      <div>
                        <strong>Framing corrections</strong>
                        <small>Add a crop point at the playhead or use multi-speaker shortcuts.</small>
                      </div>
                      <button
                        type="button"
                        data-testid="add-reframe-keyframe"
                        onClick={addReframeKeyframeAtPlayhead}
                      >
                        + Add here
                      </button>
                    </div>
                    <div style={{ display: "flex", gap: "6px", flexWrap: "wrap", margin: "6px 0 10px 0" }}>
                      <button
                        type="button"
                        className="mini-toggle-btn"
                        onClick={() => {
                          const time = Number(previewTimelineTime || 0);
                          const id = createSecureId("reframe-keyframe");
                          const keyframe = { id, time, x: 28, y: 50 };
                          setReframeKeyframes(cur => [...cur.filter(k => Math.abs(k.time - time) >= 0.1), keyframe].sort((a,b) => a.time - b.time));
                          setActiveReframeKeyframeId(id);
                          setStudioActionMessage("Camera framed on Speaker 1 (Left, 28%).");
                        }}
                      >
                        👤 Left Speaker
                      </button>
                      <button
                        type="button"
                        className="mini-toggle-btn"
                        onClick={() => {
                          const time = Number(previewTimelineTime || 0);
                          const id = createSecureId("reframe-keyframe");
                          const keyframe = { id, time, x: 72, y: 50 };
                          setReframeKeyframes(cur => [...cur.filter(k => Math.abs(k.time - time) >= 0.1), keyframe].sort((a,b) => a.time - b.time));
                          setActiveReframeKeyframeId(id);
                          setStudioActionMessage("Camera framed on Speaker 2 (Right, 72%).");
                        }}
                      >
                        👤 Right Speaker
                      </button>
                      <button
                        type="button"
                        className="mini-toggle-btn"
                        onClick={() => {
                          const duration = liveTimelineDuration || 15;
                          const step = Math.max(2.5, Math.min(5, duration / 6));
                          const cuts = [];
                          let isLeft = true;
                          for (let t = 0; t < duration; t += step) {
                            cuts.push({
                              id: createSecureId("reframe-keyframe"),
                              time: Number(t.toFixed(2)),
                              x: isLeft ? 28 : 72,
                              y: 50,
                            });
                            isLeft = !isLeft;
                          }
                          setReframeKeyframes(cuts);
                          setStudioActionMessage(`Generated ${cuts.length} alternating speaker cuts across the conversation.`);
                        }}
                      >
                        🔄 Alternate Speakers
                      </button>
                    </div>
                    {reframeKeyframes.length ? (
                      <div
                        className="reframe-keyframe-list"
                        aria-label="Manual framing corrections"
                      >
                        {reframeKeyframes.map((keyframe, index) => (
                          <button
                            type="button"
                            key={keyframe.id}
                            className={activeReframeKeyframeId === keyframe.id ? "is-active" : ""}
                            onClick={() => {
                              setActiveReframeKeyframeId(keyframe.id);
                              seekLiveEditTimelineItem(keyframe.time, { play: false });
                            }}
                          >
                            <span>{index + 1}</span>
                            {Number(keyframe.time || 0).toFixed(2)}s
                          </button>
                        ))}
                      </div>
                    ) : (
                      <small className="reframe-empty-corrections">
                        No tracking data yet. Preview stays centered; add reviewed framing points to create a timed follow path.
                      </small>
                    )}
                    {activeReframeKeyframe ? (
                      <div className="reframe-keyframe-editor">
                        <label>
                          <span>
                            Horizontal <b>{Math.round(activeReframeKeyframe.x)}%</b>
                          </span>
                          <input
                            type="range"
                            min="5"
                            max="95"
                            value={activeReframeKeyframe.x}
                            aria-label="Manual frame horizontal position"
                            onChange={event =>
                              updateActiveReframeKeyframe({ x: Number(event.target.value) })
                            }
                          />
                        </label>
                        <label>
                          <span>
                            Headroom <b>{Math.round(activeReframeKeyframe.y)}%</b>
                          </span>
                          <input
                            type="range"
                            min="8"
                            max="92"
                            value={activeReframeKeyframe.y}
                            aria-label="Manual frame vertical position"
                            onChange={event =>
                              updateActiveReframeKeyframe({ y: Number(event.target.value) })
                            }
                          />
                        </label>
                        <button
                          type="button"
                          className="reframe-remove-correction"
                          onClick={removeActiveReframeKeyframe}
                        >
                          Remove correction
                        </button>
                      </div>
                    ) : null}
                  </div>
                  ) : null}

                  <button
                    type="button"
                    className="inspector-primary-action"
                    onClick={() => {
                      setSmartCrop(true);
                      setComparisonMode("after");
                      focusComparisonPreview("reframe", true);
                      setStudioActionMessage(
                        "Framing preview is live. Follow Subject uses reviewed timed points, Show Everyone keeps the wide set, and Multi-Camera keeps every synchronized angle visible."
                      );
                    }}
                  >
                    ▶ Preview {reframeAspect} Framing
                  </button>
                </div>
              ) : null}

              {studioInspectorTab === "captions" ? (
                <div className="clip-inspector-body" role="tabpanel">
                  <div className="inspector-heading-row">
                    <div>
                      <span className="panel-kicker">Live captions</span>
                      <ProductHelpButton featureId="studio.captions" label="Captions" workspaceType="STUDIO" />
                      <h4>Make every word land before you render</h4>
                    </div>
                    <span className={`inspector-status-dot ${autoCaptions ? "is-ready" : ""}`}>
                      {autoCaptions
                        ? captionSegments.length || normalizedCaptionOverride
                          ? "Live"
                          : "Needs captions"
                        : "Off"}
                    </span>
                  </div>

                  <label className="inspector-toggle-row">
                    <span>
                      <b>Preview captions</b>
                      <small>Visible instantly in After and Split. Before stays untouched.</small>
                    </span>
                    <input
                      type="checkbox"
                      checked={autoCaptions}
                      onChange={event => {
                        const enabled = event.target.checked;
                        setAutoCaptions(enabled);
                        if (
                          enabled &&
                          !normalizedCaptionOverride &&
                          !normalizedTimedCaptionSegments.length &&
                          !captionPreviewSourceText
                        ) {
                          void generateLiveTranscript();
                        }
                      }}
                    />
                  </label>

                  <label className="inspector-select-field">
                    <span>Caption coverage</span>
                    <select aria-label="Caption coverage" value={captionCoverageMode}
                      onChange={event => { setCaptionCoverageMode(event.target.value); setCaptionCoverageMessage(""); }}>
                      <option value="full">Full transcript — check for missing phrases</option>
                      <option value="selected">Selected phrases — intentional gaps</option>
                    </select>
                    <small>{captionCoverageMode === "selected"
                      ? "Only your reviewed cues are burned in. Uncaptioned audio is intentional."
                      : "Reviewed wording still needs an audio coverage check before final render."}</small>
                  </label>
                  <button type="button" disabled={isExporting}
                    onClick={async () => {
                      setCaptionCoverageMessage("Checking source audio…");
                      try { await validateCaptionCoverage(await buildExportTimeline(), await getMediaAuthToken()); }
                      catch (error) { setCaptionCoverageMessage(error.message); }
                    }}>Check caption coverage</button>
                  {captionCoverageMessage ? <p role="status" data-testid="caption-coverage-result">{captionCoverageMessage}</p> : null}

                  <label className="inspector-toggle-row">
                    <span>
                      <b>Translate captions to English</b>
                      <small>Off preserves multilingual speech exactly as spoken.</small>
                    </span>
                    <input
                      type="checkbox"
                      checked={translateCaptionsToEnglish}
                      disabled={captionGenerationStatus === "processing"}
                      onChange={event => {
                        const enabled = event.target.checked;
                        const previousMode = translateCaptionsToEnglish;
                        setTranslateCaptionsToEnglish(enabled);
                        setCaptionGenerationMessage(
                          enabled
                            ? "Starting an editable English translation…"
                            : "Restoring captions in the spoken languages…"
                        );
                        void generateLiveTranscript({
                          translateToEnglish: enabled,
                        }).then(succeeded => {
                          if (!succeeded) setTranslateCaptionsToEnglish(previousMode);
                        });
                      }}
                    />
                  </label>

                  <label className="inspector-field">
                    <span>Speech language</span>
                    <select aria-label="Speech language" value={captionSourceLanguage}
                      disabled={captionGenerationStatus === "processing" || isExporting}
                      onChange={event => setCaptionSourceLanguage(event.target.value)}>
                      <option value="auto">Automatic detection</option>
                      <option value="en">English</option>
                      <option value="af">Afrikaans</option>
                      <option value="za-mixed">South African languages / mixed English</option>
                      <option value="xh">isiXhosa</option><option value="zu">isiZulu</option>
                      <option value="st">Sesotho</option><option value="tn">Setswana</option>
                      <option value="ts">Xitsonga</option><option value="nr">isiNdebele</option>
                      <option value="ve">Tshivenda</option>
                    </select>
                    <small>Choose the spoken language if detection misses words. Changing this keeps your current captions; regenerate when ready.</small>
                  </label>
                  <div className="caption-transcript-actions">
                    <button
                      type="button"
                      className="inspector-primary-action"
                      data-testid="generate-live-transcript"
                      disabled={captionGenerationStatus === "processing"}
                      onClick={() => void generateLiveTranscript()}
                    >
                      {captionGenerationStatus === "processing"
                        ? translateCaptionsToEnglish
                          ? "Translating…"
                          : "Listening…"
                        : captionSegments.length
                          ? "Regenerate speech captions"
                          : "Generate speech captions"}
                    </button>
                    <small>
                      Creates editable, timestamped lines from clear speech. For songs or choirs,
                      import an SRT/VTT subtitle file or type lyrics below.
                    </small>
                    {captionGenerationMessage ? (
                      <p
                        className={`caption-generation-status is-${captionGenerationStatus}`}
                        role={captionGenerationStatus === "failed" ? "alert" : "status"}
                      >
                        {captionGenerationMessage}
                      </p>
                    ) : null}
                  </div>

                  <label className="inspector-field">
                    <span>Quick manual caption</span>
                    <textarea
                      aria-label="Caption copy"
                      value={captionTextOverride}
                      onChange={event => setCaptionTextOverride(event.target.value.slice(0, 240))}
                      rows={4}
                      maxLength={240}
                      placeholder="Type a short caption, or generate the full spoken transcript above"
                    />
                    <small>
                      This short override updates the preview instantly. Leave it empty to use the
                      full timestamped transcript.
                    </small>
                    {captionTextOverride.trim().length > 0 && (
                      <button
                        type="button"
                        className="caption-split-script-btn"
                        style={{
                          marginTop: "6px",
                          width: "100%",
                          padding: "6px 10px",
                          borderRadius: "8px",
                          border: "1px solid rgba(121, 87, 255, 0.4)",
                          background: "rgba(121, 87, 255, 0.16)",
                          color: "#e0d8ff",
                          fontWeight: 600,
                          cursor: "pointer",
                          fontSize: "0.82rem",
                        }}
                        onClick={() => {
                          const fallbackDuration = Math.max(
                            3,
                            Number(currentTimelineWindow?.duration || selectedClip?.duration || currentTimelineClip?.duration || 30)
                          );
                          const clientSegments = generateClientSideCaptionSegments({
                            text: captionTextOverride,
                            duration: fallbackDuration,
                            sourceClipId:
                              currentTimelineClip?.sourceClipId ||
                              currentTimelineClip?.id ||
                              selectedClip?.id ||
                              null,
                          });
                          if (clientSegments.length) {
                            setCaptionSegments(clientSegments);
                            setAutoCaptions(true);
                            setComparisonMode("after");
                            setCaptionGenerationStatus("ready");
                            setCaptionGenerationMessage(
                              `${clientSegments.length} timed lines created from your text!`
                            );
                            focusCaptionSegmentsForReview(clientSegments);
                          }
                        }}
                      >
                        ⚡ Split text into timed lines
                      </button>
                    )}
                  </label>

                  <div className="inspector-field caption-script-draft" data-testid="caption-script-draft">
                    <span>Song lyrics or full script</span>
                    <small>
                      Paste the words one line at a time. Studio spreads them across this clip as
                      editable timing drafts; listen and check each line before export.
                    </small>
                    <textarea
                      aria-label="Full lyrics or script"
                      value={captionScriptDraft}
                      onChange={event => setCaptionScriptDraft(event.target.value.slice(0, 20000))}
                      rows={6}
                      maxLength={20000}
                      placeholder="Paste the full lyrics or script here, one caption line per row"
                    />
                    <label>
                      <span>Words are in</span>
                      <select
                        aria-label="Draft caption language"
                        value={captionDraftLanguage}
                        onChange={event => setCaptionDraftLanguage(event.target.value)}
                      >
                        {CAPTION_LANGUAGE_OPTIONS.map(option => (
                          <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                      </select>
                    </label>
                    <label>
                      <span>Voice</span>
                      <select
                        aria-label="Draft caption voice"
                        value={captionDraftSpeaker}
                        onChange={event => setCaptionDraftSpeaker(event.target.value)}
                      >
                        {CAPTION_SPEAKER_OPTIONS.map(option => (
                          <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                      </select>
                    </label>
                    <button
                      type="button"
                      className="inspector-primary-action"
                      disabled={!captionScriptDraft.trim()}
                      onClick={() => activateCaptionScriptDraft(captionScriptDraft, "your lyrics or script")}
                    >
                      Create timed lyric draft
                    </button>
                    {captionSegments.some(segment =>
                      String(segment.id || "").startsWith("script-caption") &&
                      String(segment.sourceClipId || "") === String(
                        currentTimelineClip?.sourceClipId || currentTimelineClip?.id || selectedClip?.id || ""
                      ) &&
                      Number(segment.end || 0) > Number(currentTimelineWindow.start || 0) &&
                      Number(segment.start || 0) < Number(currentTimelineWindow.end || 0)
                    ) && (
                      <button
                        type="button"
                        className="inspector-secondary-action"
                        onClick={() => {
                          const draftSourceClipId =
                            currentTimelineClip?.sourceClipId || currentTimelineClip?.id || selectedClip?.id || "";
                          const isCurrentDraftLine = segment =>
                            String(segment.id || "").startsWith("script-caption") &&
                            String(segment.sourceClipId || "") === String(draftSourceClipId) &&
                            Number(segment.end || 0) > Number(currentTimelineWindow.start || 0) &&
                            Number(segment.start || 0) < Number(currentTimelineWindow.end || 0);
                          const draftLines = captionSegments.filter(isCurrentDraftLine);
                          const missingIdentity = draftLines.filter(segment =>
                            segment.speaker === "unknown" || segment.language === "und"
                          ).length;
                          setCaptionSegments(previous => previous.map(segment =>
                            isCurrentDraftLine(segment)
                              ? {
                                  ...segment,
                                  textReviewed: true,
                                  reviewRequired:
                                    segment.speaker === "unknown" || segment.language === "und",
                                }
                              : segment
                          ));
                          setCaptionGenerationStatus(missingIdentity ? "review" : "ready");
                          setCaptionGenerationMessage(missingIdentity
                            ? `Wording approved. Choose the speaker and language for ${missingIdentity} draft line${missingIdentity === 1 ? "" : "s"} before export.`
                            : `${draftLines.length} creator-supplied draft line${draftLines.length === 1 ? "" : "s"} approved after review.`
                          );
                        }}
                      >
                        I checked the lyric draft — approve wording
                      </button>
                    )}
                    <button type="button" className="inspector-secondary-action" onClick={addChoirMusicCue}>
                      Add [Choir singing] sound cue
                    </button>
                    <small>
                      A sound cue describes the music. It does not invent lyrics when speech
                      recognition cannot hear reliable words.
                    </small>
                  </div>

                  <div className="caption-segment-editor" data-testid="caption-segment-editor">
                    {captionSegments.length ? <div className="inspector-field" aria-label="Bulk caption controls">
                      <label>Language for all lines
                        <select aria-label="Bulk caption language" value={captionBulkLanguage}
                          onChange={event => setCaptionBulkLanguage(event.target.value)}>
                          {CAPTION_LANGUAGE_OPTIONS.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
                        </select>
                      </label>
                      <button type="button" onClick={() => setCaptionSegments(previous =>
                        applyCaptionBulkEdit(previous, "language", captionBulkLanguage))}>Apply language to all captions</button>
                      <button type="button" onClick={() => setCaptionSegments(previous =>
                        applyCaptionBulkEdit(previous, "clear_reactions"))}>Remove all caption reactions</button>
                      <button type="button" onClick={() => setCaptionSegments(previous =>
                        applyCaptionBulkEdit(previous, "approve_wording"))}>I checked every line — approve wording</button>
                      <small>Wording approval applies to every line. Unknown speakers or languages still require correction.</small>
                    </div> : null}
                    <div className="caption-segment-heading">
                      <span>Transcript lines</span>
                      <div style={{ display: "flex", gap: "6px", alignItems: "center" }}>
                        <label
                          className="caption-import-srt-btn"
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            cursor: "pointer",
                            padding: "4px 8px",
                            borderRadius: "6px",
                            background: "rgba(32, 200, 222, 0.12)",
                            border: "1px solid rgba(32, 200, 222, 0.3)",
                            fontSize: "0.78rem",
                            color: "#8be9fd",
                            fontWeight: 600,
                          }}
                          title="Import timed SRT/VTT subtitles or an untimed TXT lyric draft"
                        >
                          📁 Import SRT/VTT/TXT
                          <input
                            type="file"
                            accept=".srt,.vtt,.txt"
                            style={{ display: "none" }}
                            onChange={event => {
                              const file = event.target.files?.[0];
                              if (!file) return;
                              const reader = new FileReader();
                              reader.onload = e => {
                                const rawContent = e.target?.result;
                                if (typeof rawContent === "string") {
                                  const parsed = parseSrtOrVtt(rawContent);
                                  if (parsed.length) {
                                    const normalized = normalizeCaptionSegments(parsed);
                                    setCaptionSegments(normalized);
                                    setAutoCaptions(true);
                                    setComparisonMode("after");
                                    setCaptionGenerationStatus("ready");
                                    setCaptionGenerationMessage(
                                      `${normalized.length} subtitle lines imported directly from ${file.name}`
                                    );
                                    focusCaptionSegmentsForReview(normalized);
                                    setStudioActionMessage("Subtitles imported and active in preview.");
                                  } else if (file.name.toLowerCase().endsWith(".txt")) {
                                    activateCaptionScriptDraft(rawContent, file.name);
                                  } else {
                                    setCaptionGenerationStatus("failed");
                                    setCaptionGenerationMessage("No timed subtitles were found. Check the SRT/VTT timestamps, or import plain lyrics as a .txt file.");
                                  }
                                }
                              };
                              reader.onerror = () => {
                                setCaptionGenerationStatus("failed");
                                setCaptionGenerationMessage("The subtitle file could not be read. Please try again.");
                              };
                              reader.readAsText(file);
                              event.target.value = "";
                            }}
                          />
                        </label>
                        <button
                          type="button"
                          onClick={() => {
                            const start = Math.max(0, Number(videoTime || 0));
                            setCaptionSegments(previous => [
                              ...previous,
                              {
                                id: createSecureId("caption-line"),
                                start,
                                end: start + 2,
                                duration: 2,
                                text: "New caption",
                                words: ["New", "caption"],
                                speaker: "unknown",
                                speakerLabel: "Speaker needs review",
                                language: "und",
                                languageLabel: "Language needs review",
                                languages: [],
                                languageConfidence: 0,
                                textReviewRequired: true,
                                textReviewed: false,
                                captionPlacement: "auto",
                                captionIcon: "auto",
                                captionAccent: "",
                                captionX: 50,
                                captionY: 82,
                                reviewRequired: true,
                                sourceClipId:
                                  currentTimelineClip?.sourceClipId ||
                                  currentTimelineClip?.id ||
                                  selectedClip?.id ||
                                  null,
                              },
                            ]);
                            setCaptionTextOverride("");
                            setAutoCaptions(true);
                          }}
                        >
                          + Add line
                        </button>
                      </div>
                    </div>
                    {/* Feature #6: Descript-Style Script Slicer & Filler Word Killer Banner */}
                    <div className="script-slicer-banner">
                      <div className="script-slicer-info">
                        <span className="script-slicer-icon">⚡</span>
                        <div className="script-slicer-text">
                          <strong>Descript-Style Script Slicer</strong>
                          <span>
                            {detectedFillerWords.length > 0
                              ? `${detectedFillerWords.length} filler word${detectedFillerWords.length > 1 ? "s" : ""} detected (${Array.from(new Set(detectedFillerWords.map(f => `"${f.matchWord}"`))).slice(0, 3).join(", ")})`
                              : "Speech pacing clean · No filler words detected"}
                          </span>
                        </div>
                      </div>
                      <div className="script-slicer-actions">
                        <button
                          type="button"
                          className="script-slicer-auto-btn"
                          onClick={handleRemoveAllFillerWords}
                          disabled={!detectedFillerWords.length}
                          title="Physically slice all detected filler words out of video and ripple timeline"
                        >
                          ⚡ Remove All Filler Words ({detectedFillerWords.length})
                        </button>
                      </div>
                    </div>
                    {captionSegments.length ? (
                      <div className="caption-segment-list">
                        {captionSegments.map((segment, index) => {
                          const segmentFillers = detectedFillerWords.filter(f => f.segmentId === segment.id);
                          return (
                            <div
                              className={`caption-segment-row ${captionPreviewSegmentIndex === index ? "is-active-caption-segment" : ""}`}
                              key={segment.id}
                            >
                            <div className="caption-segment-time">
                              <button
                                type="button"
                                className="caption-jump-playhead-btn"
                                title="Jump video to this subtitle line"
                                onClick={() => {
                                  const clipStart = Number(currentTimelineWindow.start || selectedClip?.start || 0);
                                  const targetTime = segment.start >= clipStart ? segment.start : clipStart + segment.start;
                                  if (videoRef.current) {
                                    videoRef.current.currentTime = targetTime;
                                    setVideoTime(targetTime);
                                  }
                                }}
                              >
                                ▶
                              </button>
                              <label>
                                <span>In</span>
                                <input
                                  aria-label={`Caption ${index + 1} start`}
                                  type="number"
                                  min="0"
                                  step="0.1"
                                  value={Number(segment.start || 0).toFixed(1)}
                                  onChange={event => {
                                    const start = Math.max(0, Number(event.target.value || 0));
                                    setCaptionSegments(previous =>
                                      previous.map(item =>
                                        item.id === segment.id
                                          ? {
                                              ...item,
                                              start,
                                              end: Math.max(start + 0.1, Number(item.end || 0)),
                                            }
                                          : item
                                      )
                                    );
                                  }}
                                />
                              </label>
                              <label>
                                <span>Out</span>
                                <input
                                  aria-label={`Caption ${index + 1} end`}
                                  type="number"
                                  min="0.1"
                                  step="0.1"
                                  value={Number(segment.end || segment.start + 2).toFixed(1)}
                                  onChange={event => {
                                    const end = Math.max(
                                      Number(segment.start || 0) + 0.1,
                                      Number(event.target.value || 0)
                                    );
                                    setCaptionSegments(previous =>
                                      previous.map(item =>
                                        item.id === segment.id ? { ...item, end } : item
                                      )
                                    );
                                  }}
                                />
                              </label>
                            </div>
                            <div className="caption-segment-content">
                              <div className="caption-segment-meta">
                                <label>
                                  <span>Speaker</span>
                                  <select
                                    aria-label={`Caption ${index + 1} speaker`}
                                    value={segment.speaker || "unknown"}
                                    onChange={event => {
                                      const speaker = normalizeCaptionSpeaker(event.target.value);
                                      setCaptionSegments(previous =>
                                        previous.map(item =>
                                          item.id === segment.id
                                            ? {
                                                ...item,
                                                speaker,
                                                speakerLabel: getCaptionSpeakerLabel(speaker),
                                                reviewRequired:
                                                  speaker === "unknown" ||
                                                  item.language === "und" ||
                                                  (item.textReviewRequired && !item.textReviewed),
                                              }
                                            : item
                                        )
                                      );
                                    }}
                                  >
                                    {!CAPTION_SPEAKER_OPTIONS.some(
                                      option => option.value === segment.speaker
                                    ) && segment.speaker ? (
                                      <option value={segment.speaker}>
                                        {segment.speakerLabel ||
                                          getCaptionSpeakerLabel(segment.speaker)}
                                      </option>
                                    ) : null}
                                    {CAPTION_SPEAKER_OPTIONS.map(option => (
                                      <option key={option.value} value={option.value}>
                                        {option.label}
                                      </option>
                                    ))}
                                  </select>
                                </label>
                                <label>
                                  <span>Language</span>
                                  <select
                                    aria-label={`Caption ${index + 1} language`}
                                    value={segment.language || "und"}
                                    onChange={event => {
                                      const language = normalizeCaptionLanguage(event.target.value);
                                      setCaptionSegments(previous =>
                                        previous.map(item =>
                                          item.id === segment.id
                                            ? {
                                                ...item,
                                                language,
                                                languageLabel: getCaptionLanguageLabel(language),
                                                languages: language === "und" ? [] : [language],
                                                reviewRequired:
                                                  language === "und" ||
                                                  item.speaker === "unknown" ||
                                                  (item.textReviewRequired && !item.textReviewed),
                                              }
                                            : item
                                        )
                                      );
                                    }}
                                  >
                                    {CAPTION_LANGUAGE_OPTIONS.map(option => (
                                      <option key={option.value} value={option.value}>
                                        {option.label}
                                      </option>
                                    ))}
                                  </select>
                                </label>
                                {segment.reviewRequired ? (
                                  <span className="caption-review-badge">
                                    {segment.textReviewRequired && !segment.textReviewed
                                      ? "Review wording"
                                      : "Review"}
                                  </span>
                                ) : (
                                  <span className="caption-review-badge is-ready">Verified</span>
                                )}
                              </div>
                              <textarea
                                aria-label={`Caption ${index + 1} text`}
                                value={segment.text}
                                rows={2}
                                onFocus={() => {
                                  const clipStart = Number(currentTimelineWindow.start || selectedClip?.start || 0);
                                  const targetTime = segment.start >= clipStart ? segment.start : clipStart + segment.start;
                                  if (videoRef.current) {
                                    videoRef.current.currentTime = targetTime;
                                    setVideoTime(targetTime);
                                  }
                                }}
                                onChange={event => {
                                  const text = event.target.value;
                                  setCaptionSegments(previous =>
                                    previous.map(item =>
                                      item.id === segment.id
                                        ? {
                                            ...item,
                                            text,
                                            words: normalizePlainText(text)
                                              .split(/\s+/)
                                              .filter(Boolean),
                                            textReviewRequired: false,
                                            textReviewed: true,
                                            reviewRequired:
                                              item.speaker === "unknown" || item.language === "und",
                                          }
                                        : item
                                    )
                                  );
                                }}
                              />
                              {segmentFillers.length > 0 && (
                                <div className="caption-filler-tags">
                                  <span className="caption-filler-label">Filler words:</span>
                                  {segmentFillers.map(f => (
                                    <button
                                      key={f.id}
                                      type="button"
                                      className="caption-filler-chip"
                                      title={`Physically cut "${f.matchWord}" out of video`}
                                      onClick={() => handleCutSingleFillerWord(f)}
                                    >
                                      ✂️ &quot;{f.matchWord}&quot;
                                    </button>
                                  ))}
                                </div>
                              )}
                              <div className="caption-line-creative-controls">
                                <label>
                                  <span>Placement</span>
                                  <select
                                    aria-label={`Caption ${index + 1} placement`}
                                    value={segment.captionPlacement || "auto"}
                                    onChange={event =>
                                      setCaptionSegments(previous =>
                                        previous.map(item =>
                                          item.id === segment.id
                                            ? { ...item, captionPlacement: event.target.value }
                                            : item
                                        )
                                      )
                                    }
                                  >
                                    {CAPTION_PLACEMENT_OPTIONS.map(option => (
                                      <option key={option.value} value={option.value}>
                                        {option.label}
                                      </option>
                                    ))}
                                  </select>
                                </label>
                                <fieldset className="caption-emoji-picker">
                                  <legend>Emoji reaction</legend>
                                  <div role="group" aria-label={`Caption ${index + 1} emoji reaction`}>
                                    {CAPTION_ICON_OPTIONS.map(option => (
                                      <button
                                        key={option.value}
                                        type="button"
                                        className={(segment.captionIcon || "auto") === option.value ? "is-active" : ""}
                                        aria-label={option.label}
                                        aria-pressed={(segment.captionIcon || "auto") === option.value}
                                        title={option.label}
                                        onClick={() =>
                                          setCaptionSegments(previous =>
                                            previous.map(item =>
                                              item.id === segment.id
                                                ? { ...item, captionIcon: option.value }
                                                : item
                                            )
                                          )
                                        }
                                      >
                                        {option.glyph}
                                      </button>
                                    ))}
                                  </div>
                                </fieldset>
                                <label>
                                  <span>Accent</span>
                                  <input
                                    type="color"
                                    aria-label={`Caption ${index + 1} accent`}
                                    value={segment.captionAccent || captionStoryPreviewTreatment?.accent || "#ffb33d"}
                                    onChange={event =>
                                      setCaptionSegments(previous =>
                                        previous.map(item =>
                                          item.id === segment.id
                                            ? { ...item, captionAccent: event.target.value }
                                            : item
                                        )
                                      )
                                    }
                                  />
                                </label>
                                {segment.captionPlacement === "custom" ? (
                                  <div className="caption-custom-position" data-testid={`caption-${index + 1}-custom-position`}>
                                    <label>
                                      <span>Horizontal <b>{Math.round(segment.captionX ?? 50)}%</b></span>
                                      <input
                                        type="range"
                                        min="5"
                                        max="95"
                                        value={segment.captionX ?? 50}
                                        aria-label={`Caption ${index + 1} horizontal position`}
                                        onChange={event =>
                                          setCaptionSegments(previous => previous.map(item =>
                                            item.id === segment.id ? { ...item, captionX: Number(event.target.value) } : item
                                          ))
                                        }
                                      />
                                    </label>
                                    <label>
                                      <span>Vertical <b>{Math.round(segment.captionY ?? 82)}%</b></span>
                                      <input
                                        type="range"
                                        min="5"
                                        max="95"
                                        value={segment.captionY ?? 82}
                                        aria-label={`Caption ${index + 1} vertical position`}
                                        onChange={event =>
                                          setCaptionSegments(previous => previous.map(item =>
                                            item.id === segment.id ? { ...item, captionY: Number(event.target.value) } : item
                                          ))
                                        }
                                      />
                                    </label>
                                  </div>
                                ) : null}
                                {segment.textReviewRequired && !segment.textReviewed ? (
                                  <button
                                    type="button"
                                    className="caption-wording-approve"
                                    onClick={() =>
                                      setCaptionSegments(previous =>
                                        previous.map(item =>
                                          item.id === segment.id
                                            ? {
                                                ...item,
                                                textReviewed: true,
                                                reviewRequired:
                                                  item.speaker === "unknown" ||
                                                  item.language === "und",
                                              }
                                            : item
                                        )
                                      )
                                    }
                                  >
                                    Approve wording
                                  </button>
                                ) : null}
                              </div>
                            </div>
                            <div className="caption-segment-row-actions">
                              <button
                                type="button"
                                className="caption-cut-video-btn"
                                title="Physically slice this line from video & ripple timeline"
                                onClick={() => handleCutCaptionFromVideo(segment)}
                              >
                                ✂️ Cut Video
                              </button>
                              <button
                                type="button"
                                className="caption-segment-delete"
                                aria-label={`Delete caption ${index + 1}`}
                                title="Remove subtitle text only"
                                onClick={() =>
                                  setCaptionSegments(previous =>
                                    previous.filter(item => item.id !== segment.id)
                                  )
                                }
                              >
                                ×
                              </button>
                            </div>
                          </div>
                        );
                      })}
                      </div>
                    ) : (
                      <p className="caption-segment-empty">
                        No speech transcript yet. Generate it automatically or add a line at the
                        playhead.
                      </p>
                    )}
                  </div>

                  <div className="inspector-field">
                    <span>Creator style</span>
                    <div className="inspector-choice-grid caption-style-grid">
                      {CAPTION_STYLE_OPTIONS.map(([value, label, helper]) => (
                        <button
                          key={value}
                          type="button"
                          aria-label={label}
                          className={`caption-style-option caption-style-option-${value} ${
                            captionStyle === value ? "is-active" : ""
                          }`}
                          aria-pressed={captionStyle === value}
                          onClick={() => {
                            setCaptionStyle(value);
                            setAutoCaptions(true);
                            setComparisonMode("after");
                            focusCaptionSegmentsForReview(captionSegments);
                          }}
                        >
                          <span className="caption-style-swatch" aria-hidden="true">
                            <i>Aa</i>
                            <b>WORD</b>
                          </span>
                          <span className="caption-style-option-copy">
                            <strong>{label}</strong>
                            <small>{helper}</small>
                          </span>
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="inspector-field">
                    <span>Default placement</span>
                    <small>Each transcript line can override this at its own timestamp.</small>
                    <div className="caption-placement-grid" role="group" aria-label="Default caption placement">
                      {[
                        ["top_left", "↖"], ["top_center", "↑"], ["top_right", "↗"],
                        ["middle_left", "←"], ["middle_center", "•"], ["middle_right", "→"],
                        ["bottom_left", "↙"], ["bottom_center", "↓"], ["bottom_right", "↘"],
                      ].map(([value, label]) => (
                        <button
                          key={value}
                          type="button"
                          className={captionPosition === value ? "is-active" : ""}
                          aria-pressed={captionPosition === value}
                          aria-label={CAPTION_PLACEMENT_OPTIONS.find(option => option.value === value)?.label}
                          onClick={() => setCaptionPosition(value)}
                        >
                          {label}
                        </button>
                      ))}
                    </div>
                    <div className="caption-stage-zone-grid" role="group" aria-label="Creative caption zones">
                      {[
                        ["shoulder_left", "↙", "Left shoulder"],
                        ["shoulder_right", "↘", "Right shoulder"],
                        ["background_left", "◧", "Wall left"],
                        ["background_center", "▣", "Wall centre"],
                        ["background_right", "◨", "Wall right"],
                      ].map(([value, icon, label]) => (
                        <button
                          key={value}
                          type="button"
                          className={captionPosition === value ? "is-active" : ""}
                          aria-pressed={captionPosition === value}
                          aria-label={label}
                          onClick={() => setCaptionPosition(value)}
                        >
                          <b aria-hidden="true">{icon}</b>
                          <span>{label}</span>
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="inspector-field">
                    <span>Quick accent palette</span>
                    <div className="caption-accent-palette" role="group" aria-label="Caption accent palette">
                      {CAPTION_ACCENT_OPTIONS.map(([color, label]) => (
                        <button
                          key={color}
                          type="button"
                          aria-label={label}
                          title={label}
                          style={{ "--caption-swatch": color }}
                          onClick={() => setCaptionSegments(previous => previous.map(item => ({ ...item, captionAccent: color })))}
                        />
                      ))}
                    </div>
                  </div>

                  <label className="inspector-range">
                    <span>
                      <b>Caption size</b>
                      <strong>{Math.round(captionScale * 100)}%</strong>
                    </span>
                    <input
                      type="range"
                      min={0.8}
                      max={1.35}
                      step={0.05}
                      value={captionScale}
                      onChange={event => setCaptionScale(Number(event.target.value))}
                    />
                  </label>

                  <button
                    type="button"
                    className="inspector-primary-action"
                    onClick={() => {
                      setAutoCaptions(true);
                      setComparisonMode("after");
                      previewHookSegment(false);
                      setStudioActionMessage("Captions are live in the After preview.");
                    }}
                  >
                    CC Preview Captions
                  </button>
                </div>
              ) : null}

              {studioInspectorTab === "titles" ? (
                <div className="clip-inspector-body graphics-inspector-body" role="tabpanel">
                  <div className="inspector-heading-row">
                    <div>
                      <span className="panel-kicker">Titles & graphics</span>
                      <h4>Build headlines, labels and lower thirds</h4>
                    </div>
                    <span className="inspector-status-dot is-ready">
                      {
                        overlays.filter(overlay => overlay.type === "text" && !overlay.isCaption)
                          .length
                      }{" "}
                      layers
                    </span>
                  </div>

                  <StudioLogoDesignPanel locked={!!trackStates.graphics?.locked||!!trackStates.broll?.locked}
                    duration={liveTimelineDuration} aspect={previewAspectRatio||16/9}
                    groups={effectiveCompoundClips} layers={overlays} order={compositionIds}
                    onBuild={async input => {
                      if (!allowTimelineTimingEdit('broll')) throw Error('Unlock the image layer track.');
                      return importGraphicsComposition(input);
                    }} />
                  <StudioLogoMotionPanel locked={!!trackStates.graphics?.locked} programmeDuration={liveTimelineDuration}
                    onBuild={async options => {
                      const projectId = activeProjectIdRef.current, signature = lastSnapshotSignatureRef.current;
                      await document.fonts.load('800 36px "Studio Inter"');
                      if (activeProjectIdRef.current !== projectId || lastSnapshotSignatureRef.current !== signature)
                        throw Error('The edit changed while the brand font loaded. Build the logo motion again.');
                      const canvas = document.createElement('canvas'), context = canvas.getContext('2d');
                      context.font = '800 36px "Studio Inter"';
                      const stage = phoneFrameRef.current, aspect = (stage?.clientWidth || 1280) / (stage?.clientHeight || 720);
                      return importGraphicsComposition(buildStudioLogoMotion({ ...options, programmeDuration:liveTimelineDuration,
                        width:Math.round(720*aspect), height:720, measure:value=>context.measureText(value).width }));
                    }} />
                  <StudioGraphicsImport onImport={importGraphicsComposition} locked={!!trackStates.graphics?.locked} />
                  <StudioVectorShapePanel layers={overlays.filter(layer=>layer.vectorShape)} selectedId={activeOverlayId}
                    onSelect={setActiveOverlayId} onAdd={addVectorArtwork} onChange={updateVectorArtwork}
                    locked={!!trackStates.graphics?.locked||!!trackStates.broll?.locked} />

                  <div className="title-preset-grid" aria-label="Add title layer">
                    <button type="button" onClick={() => addTextOverlay("headline")}>
                      <span>H1</span>
                      <strong>Headline</strong>
                      <small>Bold hook copy</small>
                    </button>
                    <button type="button" onClick={() => addTextOverlay("lower_third")}>
                      <span>▤</span>
                      <strong>Lower third</strong>
                      <small>Name and context</small>
                    </button>
                    <button type="button" onClick={() => addTextOverlay("label")}>
                      <span>TAG</span>
                      <strong>Label</strong>
                      <small>Short callout</small>
                    </button>
                  </div>

                  {overlays.some(overlay => overlay.type === "text" && !overlay.isCaption) ? (
                    <div className="title-layer-list" aria-label="Title layers">
                      {overlays
                        .filter(overlay => overlay.type === "text" && !overlay.isCaption)
                        .map((overlay, index) => (
                          <button
                            key={overlay.id}
                            type="button"
                            className={activeOverlayId === overlay.id ? "is-active" : ""}
                            onClick={() => {
                              setSelectedGroupId(null);
                              setActiveOverlayId(overlay.id);
                              jumpToOutputTimelineTime(Number(overlay.startTime || 0));
                            }}
                          >
                            <span>T{index + 1}</span>
                            <strong>{normalizePlainText(overlay.text || "Untitled title")}</strong>
                            <small>
                              {formatPreviewTimePrecise(overlay.startTime)} ·{" "}
                              {Number(overlay.duration || 0).toFixed(1)}s
                            </small>
                          </button>
                        ))}
                    </div>
                  ) : null}

                  {activeOverlay?.type === "text" && !activeOverlay.isCaption ? (
                    <div className="title-editor-card" data-testid="title-editor">
                      <label className="inspector-text-field">
                        <span>Copy</span>
                        <textarea
                          aria-label="Title text"
                          rows={3}
                          maxLength={180}
                          value={activeOverlay.text || ""}
                          onChange={event =>
                            updateOverlayText(activeOverlay.id, event.target.value)
                          }
                        />
                      </label>

                      {!activeOverlay.bRollMode && <StudioTextAnimatorPanel key={activeOverlay.id} layer={activeOverlay}
                        locked={Boolean(trackStates.graphics?.locked)}
                        onChange={spec => { setOverlayStyleOption(activeOverlay.id, "textAnimator", spec); setPreviewFinishedFile(false); }}
                        onBuild={spec => {
                          if (!allowTimelineTimingEdit("graphics")) return;
                          try {
                            validateStudioTextAnimation({ ...activeOverlay, textAnimator: spec });
                            const start = Number(activeOverlay.startTime || 0), end = start + Math.min(1.8, Number(activeOverlay.duration || 3));
                            setOverlays(layers => layers.map(l => l.id === activeOverlay.id ? { ...l, textAnimator: spec, textProgress: 100 } : l));
                            setMotionKeyframes(keys => [...keys.filter(k => k.targetId !== activeOverlay.id || k.property !== "textProgress"),
                              ...[start, end].map((time, i) => ({ id: createSecureId("motion-key"), targetId: activeOverlay.id, property: "textProgress", time, value: i * 100, easing: "linear" }))]);
                            setPreviewFinishedFile(false); setStudioActionMessage("Text choreography created. Edit Text progress in the Motion timeline.");
                          } catch (e) { setStudioActionMessage(e.message); }
                        }}
                        onDisable={() => {
                          if (!allowTimelineTimingEdit("graphics")) return;
                          setOverlays(layers => layers.map(l => { if (l.id !== activeOverlay.id) return l; const { textAnimator, textProgress, ...rest } = l; return rest; }));
                          setMotionKeyframes(keys => keys.filter(k => k.targetId !== activeOverlay.id || k.property !== "textProgress"));
                          setPreviewFinishedFile(false);
                        }} />}
                      <div className="title-typography-row">
                        <label className="inspector-select-field">
                          <span>Font</span>
                          <select
                            value={activeOverlay.fontFamily || "Inter"}
                            onChange={event =>
                              setOverlayStyleOption(
                                activeOverlay.id,
                                "fontFamily",
                                event.target.value
                              )
                            }
                          >
                            {[
                              "Inter",
                              "Arial Black",
                              "Impact",
                              "Georgia",
                              "Trebuchet MS",
                              "Courier New",
                            ].map(font => (
                              <option key={font} value={font} disabled={font !== "Inter"}>
                                {font}{font !== "Inter" ? " · export unavailable" : ""}
                              </option>
                            ))}
                          </select>
                        </label>
                        <label className="inspector-select-field">
                          <span>Weight</span>
                          <select
                            value={Number(activeOverlay.fontWeight || 900)}
                            onChange={event =>
                              setOverlayStyleOption(
                                activeOverlay.id,
                                "fontWeight",
                                Number(event.target.value)
                              )
                            }
                          >
                            <option value={400}>Regular</option>
                            <option value={600}>Semibold</option>
                            <option value={800}>Bold</option>
                            <option value={900}>Black</option>
                          </select>
                        </label>
                        <label className="inspector-select-field">
                          <span>Shadow</span>
                          <select
                            value={activeOverlay.shadow || "soft"}
                            onChange={event =>
                              setOverlayStyleOption(activeOverlay.id, "shadow", event.target.value)
                            }
                          >
                            <option value="none">None</option>
                            <option value="soft">Soft</option>
                            <option value="strong">Strong</option>
                          </select>
                        </label>
                      </div>
                      {(activeOverlay.fontFamily || "Inter") !== "Inter" ? (
                        <p role="status">This saved font is unavailable for export. Choose Inter for matching preview and render.</p>
                      ) : null}

                      <div className="inspector-field">
                        <span>Alignment</span>
                        <div className="inspector-choice-grid is-three">
                          {["left", "center", "right"].map(alignment => (
                            <button
                              key={alignment}
                              type="button"
                              className={
                                (activeOverlay.textAlign || "center") === alignment
                                  ? "is-active"
                                  : ""
                              }
                              onClick={() =>
                                setOverlayStyleOption(activeOverlay.id, "textAlign", alignment)
                              }
                            >
                              {alignment}
                            </button>
                          ))}
                        </div>
                      </div>

                      <div className="title-color-row">
                        <label>
                          <span>Text</span>
                          <input
                            aria-label="Title text color"
                            type="color"
                            value={activeOverlay.color || "#ffffff"}
                            onChange={event =>
                              setOverlayStyleOption(activeOverlay.id, "color", event.target.value)
                            }
                          />
                        </label>
                        <label>
                          <span>Background</span>
                          <input
                            aria-label="Title background color"
                            type="color"
                            value={
                              /^#[0-9a-f]{6}$/i.test(activeOverlay.bg || "")
                                ? activeOverlay.bg
                                : "#080b16"
                            }
                            onChange={event =>
                              setOverlayStyleOption(activeOverlay.id, "bg", event.target.value)
                            }
                          />
                        </label>
                        <button
                          type="button"
                          className={activeOverlay.bg === "transparent" ? "is-active" : ""}
                          onClick={() =>
                            setOverlayStyleOption(activeOverlay.id, "bg", "transparent")
                          }
                        >
                          No background
                        </button>
                      </div>

                      <label className="inspector-range">
                        <span>
                          <b>Text box width</b>
                          <strong>{Number(activeOverlay.width || 90)}%</strong>
                        </span>
                        <input aria-label="Title text box width" type="range" min={15} max={90} step={1}
                          value={Number(activeOverlay.width || 90)}
                          onChange={event => setOverlayStyleOption(activeOverlay.id, "width", Number(event.target.value))} />
                      </label>

                      <label className="inspector-range">
                        <span>
                          <b>Outline</b>
                          <strong>{Number(activeOverlay.strokeWidth || 0)}px</strong>
                        </span>
                        <input
                          aria-label="Title outline width"
                          type="range"
                          min={0}
                          max={8}
                          step={1}
                          value={Number(activeOverlay.strokeWidth || 0)}
                          onChange={event =>
                            setOverlayStyleOption(
                              activeOverlay.id,
                              "strokeWidth",
                              Number(event.target.value)
                            )
                          }
                        />
                      </label>

                      <label className="inspector-range">
                        <span><b>Outline color</b></span>
                        <input type="color" aria-label="Title outline color" value={activeOverlay.strokeColor || '#000000'}
                          onChange={event => setOverlayStyleOption(activeOverlay.id, 'strokeColor', event.target.value)} />
                      </label>

                      <label className="inspector-range">
                        <span>
                          <b>Size</b>
                          <strong>{Math.round(Number(activeOverlay.scale || 1) * 100)}%</strong>
                        </span>
                        <input
                          aria-label="Title size"
                          type="range"
                          min={0.5}
                          max={2}
                          step={0.05}
                          value={Number(activeOverlay.scale || 1)}
                          onChange={event =>
                            setOverlayStyleOption(
                              activeOverlay.id,
                              "scale",
                              Number(event.target.value)
                            )
                          }
                        />
                      </label>

                      <label className="inspector-range">
                        <span>
                          <b>Rotation</b>
                          <strong>{Math.round(Number(activeOverlay.rotation || 0))}°</strong>
                        </span>
                        <input
                          aria-label="Title rotation"
                          type="range"
                          min={-25}
                          max={25}
                          step={1}
                          value={Number(activeOverlay.rotation || 0)}
                          onChange={event =>
                            setOverlayStyleOption(
                              activeOverlay.id,
                              "rotation",
                              Number(event.target.value)
                            )
                          }
                        />
                      </label>

                      <div className="inspector-field">
                        <span>Position</span>
                        <div className="inspector-choice-grid is-three">
                          {[
                            [18, "Top"],
                            [50, "Center"],
                            [82, "Lower"],
                          ].map(([position, label]) => (
                            <button
                              key={label}
                              type="button"
                              className={
                                Math.abs(Number(activeOverlay.y || 50) - position) < 4
                                  ? "is-active"
                                  : ""
                              }
                              onClick={() => {
                                setOverlayStyleOption(activeOverlay.id, "x", 50);
                                setOverlayStyleOption(activeOverlay.id, "y", position);
                              }}
                            >
                              {label}
                            </button>
                          ))}
                        </div>
                      </div>

                      <div className="inspector-time-grid">
                        <label>
                          <span>Start</span>
                          <input
                            aria-label="Title start time"
                            type="number"
                            min={0}
                            max={liveTimelineDuration}
                            step={0.05}
                            value={Number(activeOverlay.startTime || 0).toFixed(2)}
                            onChange={event =>
                              updateOverlayTimeRange(
                                activeOverlay.id,
                                Number(event.target.value),
                                activeOverlay.duration
                              )
                            }
                          />
                        </label>
                        <label>
                          <span>Duration</span>
                          <input
                            aria-label="Title duration"
                            type="number"
                            min={0.2}
                            max={liveTimelineDuration}
                            step={0.05}
                            value={Number(activeOverlay.duration || 0).toFixed(2)}
                            onChange={event =>
                              updateOverlayTimeRange(
                                activeOverlay.id,
                                activeOverlay.startTime,
                                Number(event.target.value)
                              )
                            }
                          />
                        </label>
                      </div>

                      <div className="inspector-field">
                        <span>Entrance motion</span>
                        <div className="inspector-choice-grid is-three">
                          {[
                            ["fade", "Fade"],
                            ["slideUp", "Slide"],
                            ["zoom", "Pop"],
                          ].map(([value, label]) => (
                            <button
                              key={value}
                              type="button"
                              className={
                                (activeOverlay.animation?.enter || "fade") === value
                                  ? "is-active"
                                  : ""
                              }
                              onClick={() => setOverlayAnimation(activeOverlay.id, "enter", value)}
                            >
                              {label}
                            </button>
                          ))}
                        </div>
                      </div>

                      <StudioKineticTypePanel onBuild={createKineticTitle} locked={!!trackStates.graphics?.locked} />

                      <div className="inspector-inline-actions">
                        <button type="button" onClick={() => duplicateOverlay(activeOverlay.id)}>
                          Duplicate
                        </button>
                        <button type="button" onClick={() => deleteOverlay(activeOverlay.id)}>
                          Remove
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="inspector-empty-state">
                      <span>T</span>
                      <strong>Add or select a title</strong>
                      <small>Every title is timed, positioned and animated independently.</small>
                    </div>
                  )}
                </div>
              ) : null}

              {studioInspectorTab === "finish" ? (
                <div className="clip-inspector-body finish-inspector-body" role="tabpanel">
                  <div className="inspector-heading-row">
                    <div>
                      <span className="panel-kicker">Color & finish</span>
                      <h4>Grade the real footage with live scopes</h4>
                    </div>
                    <span className={`inspector-status-dot ${hasFinishEffects || hasAdvancedGrade(advancedColor) ? "is-ready" : ""}`}>
                      {hasFinishEffects || hasAdvancedGrade(advancedColor) ? "Grade on" : "Neutral"}
                    </span>
                  </div>
                  <section className="advanced-color-lab">
                    <div className="sound-effects-heading">
                      <div>
                        <span className="panel-kicker">Color shaping</span>
                        <strong>Tone curve, global HSL &amp; 3D LUT</strong>
                      </div>
                      <span className="sound-effect-count">
                        {advancedColor.lutName || "No LUT"}
                      </span>
                    </div>
                    <label className="lut-import-button">
                      <span>＋ Import .cube LUT</span>
                      <input
                        type="file"
                        accept=".cube,text/plain"
                        onChange={async event => {
                          const file = event.target.files?.[0];
                          if (!file) return;
                          try {
                            if (file.size > 12*1024*1024) throw new Error("Choose a .cube LUT smaller than 12 MB.");
                            const lutData = parseColorCube(await file.text());
                            setAdvancedColor(current => ({ ...current, lutName: file.name, lutFile: null, lutData }));
                            setComparisonMode("after");
                            setStudioActionMessage(`${file.name} applied to preview and export. LUT data is saved with this project.`);
                          } catch (error) { setStudioActionMessage(error.message); }
                        }}
                      />
                    </label>
                    {advancedColor.lutName && !advancedColor.lutData ? <p role="status">This older project contains a LUT name without its data. Reimport the .cube file to apply it.</p> : null}
                    {advancedColor.lutData ? <button type="button" onClick={() => setAdvancedColor(current => ({ ...current, lutName: "", lutData: null, lutFile: null }))}>Remove LUT</button> : null}
                    {advancedColor.lutName ? (
                      <label className="inspector-range">
                        <span>
                          <b>LUT mix</b>
                          <strong>{advancedColor.lutIntensity}%</strong>
                        </span>
                        <input
                          type="range"
                          min={0}
                          max={100}
                          value={advancedColor.lutIntensity}
                          onChange={event =>
                            setAdvancedColor(current => ({
                              ...current,
                              lutIntensity: Number(event.target.value),
                            }))
                          }
                        />
                      </label>
                    ) : null}
                    <div className="advanced-color-sliders">
                      {Object.entries(advancedColor.curve).map(([field, value]) => (
                        <label key={`curve-${field}`} className="inspector-range">
                          <span>
                            <b>Curve · {field}</b>
                            <strong>
                              {value > 0 ? "+" : ""}
                              {value}
                            </strong>
                          </span>
                          <input
                            type="range"
                            min={-100}
                            max={100}
                            value={value}
                            onChange={event =>
                              setAdvancedColor(current => ({
                                ...current,
                                curve: {
                                  ...current.curve,
                                  [field]: Number(event.target.value),
                                },
                              }))
                            }
                          />
                        </label>
                      ))}
                      {Object.entries(advancedColor.hsl).map(([field, value]) => (
                        <label key={`hsl-${field}`} className="inspector-range">
                          <span>
                            <b>HSL · {field}</b>
                            <strong>
                              {value > 0 ? "+" : ""}
                              {value}
                            </strong>
                          </span>
                          <input
                            type="range"
                            min={-100}
                            max={100}
                            value={value}
                            onChange={event =>
                              setAdvancedColor(current => ({
                                ...current,
                                hsl: {
                                  ...current.hsl,
                                  [field]: Number(event.target.value),
                                },
                              }))
                            }
                          />
                        </label>
                      ))}
                    </div>
                  </section>
                  <StudioFinishRack
                    fx={finishFx}
                    hasEffects={hasFinishEffects || hasAdvancedGrade(advancedColor)}
                    onUpdateFx={(field, value) => {
                      updateFinishFx(field, value);
                      setComparisonMode("after");
                      setStudioActionMessage(
                        "Color and finish updated instantly in After. No render credits used."
                      );
                    }}
                    onApplyPreset={applyStudioFinishPreset}
                    onReset={resetStudioFinish}
                    visualizer={podcastVisualizer}
                    onUpdateVisualizer={updatePodcastVisualizer}
                    keyframes={finishKeyframes}
                    currentTime={previewTimelineTime}
                    duration={liveTimelineDuration}
                    onAddKeyframe={addFinishKeyframeAtPlayhead}
                    onRemoveKeyframe={removeFinishKeyframe}
                    onSeekKeyframe={seekFinishKeyframe}
                    scopeMode={scopeMode}
                    onScopeModeChange={setScopeMode}
                    scopes={<VideoScopes videoRef={videoRef} canvasRef={afterCanvasRef} mode={scopeMode} />}
                    beatCount={musicBeatMarkers.length}
                    beatSnapEnabled={beatSnapEnabled}
                    onBeatSnapChange={setBeatSnapEnabled}
                    mainFrame={mainFrame}
                    onUpdateMainFrame={(field, value) => {
                      setMainFrame(current => normalizeMainFrame({
                        ...current,
                        [field]: value,
                      }));
                      setComparisonMode("after");
                      setStudioActionMessage(
                        "Main footage frame updated in preview and final export."
                      );
                    }}
                  />
                </div>
              ) : null}

              {studioInspectorTab === "motion" ? (
                <>
                <div className="motion-workspace-switch" role="group" aria-label="Motion workspace">
                  <button type="button" aria-pressed={motionWorkspace === "design"} onClick={() => setMotionWorkspace("design")}>Motion + sound</button>
                  <button type="button" aria-pressed={motionWorkspace === "3d"} onClick={() => setMotionWorkspace("3d")}>3D Motion</button>
                  <ProductHelpButton featureId="studio.three_d" label="3D Motion" workspaceType="STUDIO" />
                  <button type="button" aria-pressed={motionWorkspace === "logo"} onClick={() => setMotionWorkspace("logo")}>Logo & layers</button>
                  <button type="button" aria-pressed={motionWorkspace === "smart_zoom"} onClick={() => setMotionWorkspace("smart_zoom")}>🎯 Viral Punch-Ins {activePunchZones.length ? `(${activePunchZones.length})` : ""}</button>
                  <button type="button" aria-pressed={motionWorkspace === "transform"} onClick={() => setMotionWorkspace("transform")}>Transform keyframes</button>
                </div>
                <input
                  ref={logoMotionInputRef}
                  type="file"
                  accept="image/png,image/webp,image/jpeg,image/svg+xml"
                  onChange={addLogoMotionLayer}
                  data-testid="motion-logo-input"
                  hidden
                />
                {motionWorkspace === "3d" ? (
                  <Studio3DPanel
                    scenes={threeDScenes}
                    projectMedia={projectMedia}
                    onChange={setThreeDScenes}
                    focusId={selectedThreeDId}
                    onSelect={setSelectedThreeDId}
                    playhead={previewTimelineTime}
                    duration={outputTimelineDuration}
                    onSeek={time => seekLiveEditTimelineItem(time, "motion")}
                    onUploadAsset={uploadThreeDAsset}
                    onGeneratePreview={generateThreeDPreview}
                    onRestorePreview={restoreThreeDPreview}
                    previewState={threeDPreviewState.sceneId === (selectedThreeDId || threeDScenes[0]?.id) ? threeDPreviewState : { status: "idle" }}
                  />
                ) : motionWorkspace === "smart_zoom" ? (
                  <div className="smart-zoom-panel">
                    <div className="smart-zoom-card">
                      <div className="smart-zoom-header">
                        <div>
                          <span className="panel-kicker">Hormozi / MrBeast Retention Engine</span>
                          <strong>AI Smart Zoom & Dynamic Punch-Ins</strong>
                        </div>
                        <span className="smart-zoom-count-badge">
                          {activePunchZones.length} {activePunchZones.length === 1 ? "Punch" : "Punches"} Active
                        </span>
                      </div>
                      <p className="smart-zoom-desc">
                        Automatically punch in the camera angle (1.15×–1.35×) on vocal emphasis and rhythmic cadence to destroy drop-off and maximize viewer retention.
                      </p>

                      <div className="smart-zoom-config-section">
                        <label className="smart-zoom-label">
                          <span>Punch Rhythm & Strategy</span>
                          <div className="smart-zoom-pill-group">
                            {PUNCH_MODES.map(mode => (
                              <button
                                key={mode.id}
                                type="button"
                                className={`smart-zoom-pill ${punchMode === mode.id ? "is-active" : ""}`}
                                onClick={() => setPunchMode(mode.id)}
                                title={mode.desc}
                              >
                                {mode.label}
                              </button>
                            ))}
                          </div>
                        </label>

                        <label className="smart-zoom-label">
                          <span>Punch Scale Intensity</span>
                          <div className="smart-zoom-pill-group">
                            {PUNCH_SCALES.map(s => (
                              <button
                                key={s.id}
                                type="button"
                                className={`smart-zoom-pill ${punchScale === s.id ? "is-active" : ""}`}
                                onClick={() => setPunchScale(s.id)}
                              >
                                {s.label}
                              </button>
                            ))}
                          </div>
                        </label>

                        <label className="smart-zoom-label">
                          <span>Zoom Transition Easing</span>
                          <div className="smart-zoom-pill-group">
                            {PUNCH_STYLES.map(style => (
                              <button
                                key={style.id}
                                type="button"
                                className={`smart-zoom-pill ${punchStyle === style.id ? "is-active" : ""}`}
                                onClick={() => setPunchStyle(style.id)}
                                title={style.desc}
                              >
                                {style.label}
                              </button>
                            ))}
                          </div>
                        </label>

                        <label className="inspector-range" style={{ marginTop: "4px" }}>
                          <span>
                            <b>Hold duration</b>
                            <strong>{Number(punchDuration).toFixed(1)}s</strong>
                          </span>
                          <input
                            type="range"
                            min={1.2}
                            max={4.5}
                            step={0.1}
                            value={punchDuration}
                            onChange={e => setPunchDuration(Number(e.target.value))}
                          />
                        </label>
                      </div>

                      <div className="smart-zoom-actions">
                        <button
                          type="button"
                          className="inspector-primary-action smart-zoom-btn-primary"
                          onClick={autoGeneratePunchIns}
                          data-testid="smart-zoom-auto-generate"
                        >
                          ⚡ Auto-Generate Viral Punch-Ins
                        </button>
                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px" }}>
                          <button
                            type="button"
                            className={`inspector-secondary-action ${isPlayheadPunched ? "is-punched" : ""}`}
                            onClick={togglePunchInAtPlayhead}
                            data-testid="smart-zoom-toggle-playhead"
                          >
                            {isPlayheadPunched ? "🎯 Remove Punch at Playhead" : "🎯 Punch In at Playhead (Z)"}
                          </button>
                          <button
                            type="button"
                            className="inspector-secondary-action is-danger"
                            onClick={clearAllPunchIns}
                            disabled={!activePunchZones.length}
                            data-testid="smart-zoom-clear"
                          >
                            ↺ Clear All Punches
                          </button>
                        </div>
                      </div>

                      {activePunchZones.length > 0 && (
                        <div className="smart-zoom-zones-list">
                          <span className="panel-kicker">Active Punch Points</span>
                          <div className="smart-zoom-zones-scroll">
                            {activePunchZones.map((zone, zIdx) => (
                              <div
                                key={zone.id || zIdx}
                                className="smart-zoom-zone-item"
                                onClick={() => jumpToOutputTimelineTime(zone.startTime)}
                              >
                                <span className="smart-zoom-zone-badge">🎯 {zone.maxScale || punchScale}×</span>
                                <strong>{formatPreviewTimePrecise(zone.startTime)} → {formatPreviewTimePrecise(zone.endTime)}</strong>
                                <small>({zone.duration}s hold)</small>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ) : motionWorkspace === "design" ? (
                <MotionPanel
                  scenes={motionScenes}
                  onChange={setMotionScenes}
                  focusId={selectedMotionId}
                  onSelect={setSelectedMotionId}
                  playhead={previewTimelineTime}
                  duration={outputTimelineDuration}
                  transcript={captionSegments}
                  onAutoGenerateMotionBeats={handleAutoGenerateMotionBeats}
                  onSeek={(time, play = false) => {
                    seekLiveEditTimelineItem(time, "motion");
                    if (play) safePlayMediaElement(videoRef.current);
                  }}
                />
                ) : (
                  <>
                  {motionWorkspace === "logo" ? (
                    <section className="motion-logo-workspace" aria-label="Logo motion designer">
                      <div>
                        <span className="panel-kicker">Layer motion designer</span>
                        <h4>Animate your real logo asset</h4>
                        <p>
                          Import transparent PNG, WebP or SVG artwork. Each import becomes an independent
                          timed layer with editable keys and a transparent export path.
                        </p>
                      </div>
                      <button
                        type="button"
                        className="inspector-primary-action"
                        onClick={() => logoMotionInputRef.current?.click()}
                      >
                        ＋ Import logo layer
                      </button>
                      <div className="motion-layer-stack" aria-label="Motion layer stack">
                        {overlays.filter(layer => layer.motionDesign).map((layer, index, motionLayers) => (
                          <div
                            key={layer.id}
                            className={String(motionTargetId) === String(layer.id) ? "is-active" : ""}
                            role="group"
                            aria-label={`${layer.name || "Logo layer"} controls`}
                          >
                          <button
                            type="button"
                            className="motion-layer-select"
                            onClick={() => {
                              setMotionTargetId(layer.id);
                              setActiveOverlayId(layer.id);
                              jumpToOutputTimelineTime(Number(layer.startTime || 0));
                            }}
                          >
                            <span>{String(index + 1).padStart(2, "0")}</span>
                            <strong>{layer.name || "Logo layer"}</strong>
                            <small>{Number(layer.startTime || 0).toFixed(2)}s · {Number(layer.duration || 0).toFixed(2)}s</small>
                          </button>
                          <div className="motion-layer-actions">
                            <button type="button" title="Move layer down" aria-label={`Move ${layer.name || "logo layer"} down`}
                              disabled={index === 0} onClick={() => moveOverlay(layer.id, "backward")}>↓</button>
                            <button type="button" title="Move layer up" aria-label={`Move ${layer.name || "logo layer"} up`}
                              disabled={index === motionLayers.length - 1} onClick={() => moveOverlay(layer.id, "forward")}>↑</button>
                            <button type="button" title="Duplicate layer with animation" aria-label={`Duplicate ${layer.name || "logo layer"}`}
                              onClick={() => duplicateOverlay(layer.id)}>⧉</button>
                            <button type="button" title="Delete layer and animation" aria-label={`Delete ${layer.name || "logo layer"}`}
                              onClick={() => deleteOverlay(layer.id)}>×</button>
                          </div>
                          </div>
                        ))}
                      </div>
                    </section>
                  ) : null}
                  <MotionInspector
                    activeOverlay={motionInspectorTarget ? { ...motionInspectorTarget, ...buildMotionAutomationStyle(activeMotionTargetKeyframes, previewTimelineTime, motionInspectorTarget) } : null}
                    overlays={[mainTransform, ...overlays.filter(overlay => !overlay.isCaption)]}
                    playhead={previewTimelineTime}
                    keyframes={motionKeyframes}
                    focusKeyframeId={focusedMotionKeyframeId}
                    keyframesLocked={Boolean(trackStates[motionKeyframeTrack(motionTargetId)]?.locked)}
                    onSelectOverlay={id => { setSelectedGroupId(null); setMotionTargetId(id); }}
                    onUpdateOverlay={(field, value) => {
                      if (!motionInspectorTarget) return;
                      if (studioMotionPropertiesForTarget(motionInspectorTarget).some(property => property.id === field)) {
                        editMotionPropertyAtPlayhead(motionTargetId, field, value); return;
                      }
                      if (motionInspectorTarget.id === "main-video") setMainTransform(current => ({ ...current, [field]: value }));
                      else setOverlayStyleOption(motionInspectorTarget.id, field, value);
                      setComparisonMode("after");
                    }}
                    onAddKeyframe={addMotionKeyframesAtPlayhead}
                    onAddPositionKeyframe={() => editMotionPositionPose(previewTimelineTime, buildMotionAutomationStyle(activeMotionTargetKeyframes, previewTimelineTime, motionInspectorTarget))}
                    onTogglePropertyKeyframe={property => editMotionPropertyAtPlayhead(motionTargetId, property, undefined, true)}
                    frameRate={exportSettings.fps} displayRate={previewSpeed}
                    onUpdateKeyframe={updateMotionKeyframe}
                    onRemoveKeyframe={removeMotionKeyframe}
                    onSeek={jumpToOutputTimelineTime}
                    showMotionPath={showMotionPath}
                    onToggleMotionPath={() => setShowMotionPath(current => !current)}
                    onResetMotion={() => {
                      const reset = { x: 50, y: 50, scale: 1, rotation: 0, opacity: 1, cropX: 0, cropY: 0,
                        ...(motionInspectorTarget?.textAnimator ? { textProgress: 100 } : {}),
                        ...(motionInspectorTarget?.vectorShape ? { strokeStart: 0, strokeEnd: 100, strokeOffset: 0 } : {}) };
                      if (motionTargetId === "main-video") setMainTransform(current => ({ ...current, ...reset }));
                      else setOverlays(current => current.map(overlay => overlay.id === motionTargetId ? { ...overlay, ...reset } : overlay));
                      setMotionKeyframes(current => current.filter(key => key.targetId !== motionTargetId));
                    }}
                  />
                  </>
                )}
                </>
              ) : null}

              {studioInspectorTab === "composite" ? (
                <>
                <StudioSubjectVfxPanel
                  items={subjectVfxScenes} onChange={setSubjectVfxScenes}
                  media={projectMedia}
                  getDocument={() => getEditorSnapshot().studioDocument}
                  onMediaAdded={asset => setProjectMedia(current => current.some(value => value.id === asset.id)
                    ? current.map(value => value.id === asset.id ? asset : value) : [...current, asset])}
                  sources={timeline.map(clip => ({ id: String(clip.sourceClipId || clip.id), name: clip.name || clip.file?.name || "Source clip", url: clip.url || videoUrl, storagePath: clip.sourceStoragePath || clip.storagePath || (clip.id === "main" ? sourceStoragePath : null), start: getTimelineClipWindow(clip).start, end: getTimelineClipWindow(clip).end })).filter((clip,index,all) => all.findIndex(value => value.id === clip.id && value.start === clip.start && value.end === clip.end) === index)}
                  getToken={getMediaAuthToken} apiBase={API_BASE_URL} createId={createSecureId}
                  proposal={directorSubjectVfxProposal?.projectId === projectId && directorSubjectVfxProposal?.baseRevision === studioDocumentRef.current?.revision ? directorSubjectVfxProposal : null}
                />
                <CompositeInspector
                  layerEntries={compositionRoots}
                  groupTree={compositionGroups}
                  groupEntries={compositionEntries}
                  onCompositeGroup={(id, changes) => {
                    if (!allowTimelineTimingEdit("graphics")) return;
                    const next = effectiveCompoundClips.map(group => group.id === id ? { ...group, ...changes } : group);
                    try { studioGroupTree(next, compositionIds); setCompoundClips(next); }
                    catch (error) { setStudioActionMessage(error.message); }
                  }}
                  onTransformGroup={(id, transform) => setCompoundClips(current => current.map(group =>
                    group.id === id ? { ...group, transform } : group))}
                  onRemoveGroup={id => {
                    if (!allowTimelineTimingEdit("graphics")) return;
                    setCompoundClips(current => removeStudioGroup(current, id));
                    setMotionKeyframes(current => current.filter(key => key.targetId !== id));
                  }}
                  groupAnimation={{ keyframes: motionKeyframes, playhead: previewTimelineTime,
                    timingRows: groupClipRows, onEditTiming: editGroupClipTiming, onFitTiming: fitGroupClipTiming, onDeleteGroup: deleteGroupClip,
                    keyTolerance: motionKeyframeTolerance(exportSettings.fps, previewSpeed),
                    locked: Boolean(trackStates.graphics?.locked), focusKeyId: focusedMotionKeyframeId,
                    onProperty: editMotionPropertyAtPlayhead,
                    onToggleKey: (id, property) => editMotionPropertyAtPlayhead(id, property, undefined, true),
                    onSeek: jumpToOutputTimelineTime, onUpdateKey: updateMotionKeyframe,
                    onReset: id => {
                      if (!allowTimelineTimingEdit("graphics")) return;
                      setCompoundClips(current => current.map(group => group.id === id ? { ...group, transform: { ...GROUP_TRANSFORM } } : group));
                      setMotionKeyframes(current => current.filter(key => key.targetId !== id));
                    } }}
                  onMoveLayer={moveCompositionLayer}
                  activeOverlay={compositeInspectorTarget}
                  overlays={[mainTransform, ...overlays.filter(overlay => !overlay.isCaption)]}
                  adjustmentLayers={adjustmentLayers}
                  onUpdateAdjustmentLayer={updateAdjustmentLayer}
                  onDeleteAdjustmentLayer={id => setAdjustmentLayers(current => current.filter(layer => layer.id !== id))}
                  onSelectOverlay={targetId => {
                    setSelectedGroupId(null);
                    setCompositeTargetId(targetId);
                    if (targetId !== "main-video") setActiveOverlayId(targetId);
                  }}
                  onUpdateOverlay={(field, value) => {
                    if (!compositeInspectorTarget) return;
                    if (field === 'perspective') {
                      const candidate = { ...compositeInspectorTarget, perspective: value };
                      try { validateStudioPerspectiveLayer(candidate, value ? motionKeyframes.filter(k=>k.targetId===candidate.id) : [],
                        effectiveCompoundClips.some(g=>g.layerIds?.includes(candidate.id))); }
                      catch (error) { setStudioActionMessage(error.message); return; }
                      if (!value) setMotionKeyframes(current=>current.filter(k=>k.targetId!==candidate.id||(k.property!=='perspectiveProgress'&&!/^planeCorner/.test(k.property))));
                    }
                    if (compositeInspectorTarget.id === "main-video") {
                      setMainTransform(current => ({ ...current, [field]: value }));
                    } else {
                      setOverlayStyleOption(compositeInspectorTarget.id, field, value);
                    }
                    setComparisonMode("after");
                  }}
                  onAddAdjustmentLayer={addAdjustmentLayer}
                  onCreateCompound={createCompoundFromSelection}
                />
                </>
              ) : null}

              {studioInspectorTab === "flow" ? (
                <div className="clip-inspector-body" role="tabpanel" data-testid="studio-flow-inspector">
                  <div className="inspector-heading-row">
                    <div>
                      <span className="panel-kicker">AutoPromote Comp &amp; Signal Flow</span>
                      <h4>Parenting, Mattes, Drivers &amp; Node Graph</h4>
                    </div>
                  </div>
                  <StudioSceneActionsPanel getSnapshot={getEditorSnapshot} projectMedia={projectMedia} time={previewTimelineTime}
                    locked={!!(trackStates.graphics?.locked || trackStates.broll?.locked || trackStates.video?.locked)} onApply={async plan => {
                    if (trackStates.graphics?.locked || trackStates.broll?.locked || trackStates.video?.locked)
                      throw Error('Unlock the Video, Graphics and B-roll tracks to apply a complete scene.');
                    const projectId = activeProjectIdRef.current, signature = lastSnapshotSignatureRef.current;
                    const stage = phoneFrameRef.current;
                    const result = await prepareStudioSceneActions(plan, getEditorSnapshot(), createSecureId, {
                      aspect: (stage?.clientWidth || 1280) / (stage?.clientHeight || 720),
                      isCurrent: () => activeProjectIdRef.current === projectId && lastSnapshotSignatureRef.current === signature,
                    });
                    const next = result.snapshot;
                    result.urls.forEach(url => projectMediaObjectUrlsRef.current.add(url));
                    pendingHistoryBaselineRef.current = cloneSnapshot(getEditorSnapshot());
                    flushSync(() => {
                      setOverlays(next.overlays); setMotionKeyframes(next.motionKeyframes);
                      setAdjustmentLayers(next.adjustmentLayers); setCompositionOrder(next.compositionOrder);
                      setCompoundClips(next.compoundClips || []);
                      setComparisonMode('after'); setPreviewFinishedFile(false);
                    });
                    setStudioActionMessage('Reviewed cinematic scene applied as editable layers and motion keys.');
                  }} />
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 10 }}>
                    <button
                      type="button"
                      className={`ap-mfx-btn ${bottomDockView === "comp_rig" ? "is-active" : "is-accent"}`}
                      onClick={() => {
                        setBottomDockView("comp_rig");
                        setTimelineDockExpanded(true);
                      }}
                    >
                      Open Comp &amp; Rig Dock
                    </button>
                    <button
                      type="button"
                      className={`ap-mfx-btn ${bottomDockView === "comp_graph" ? "is-active" : ""}`}
                      onClick={() => {
                        setBottomDockView("comp_graph");
                        setTimelineDockExpanded(true);
                      }}
                    >
                      Open Speed / Value Graph
                    </button>
                    <button
                      type="button"
                      className={`ap-mfx-btn ${bottomDockView === "node_flow" ? "is-active" : ""}`}
                      onClick={() => {
                        setBottomDockView("node_flow");
                        setTimelineDockExpanded(true);
                      }}
                    >
                      Open Signal Node Flow
                    </button>
                  </div>
                  {bottomDockView === "node_flow" ? (
                    <StudioCompWorkbench
                      overlays={overlays}
                      mainTransform={mainTransform}
                      motionKeyframes={motionKeyframes}
                      adjustmentLayers={adjustmentLayers}
                      activeOverlayId={activeOverlayId}
                      hideShyLayers={hideShyLayers}
                      currentTime={previewTimelineTime}
                      programmeDuration={liveTimelineDuration}
                      onSelectOverlay={id => {
                        setSelectedGroupId(null);
                        setActiveOverlayId(id);
                        setComparisonMode("after");
                      }}
                      onChangeOverlays={next => {
                        if (!commitCheckedOverlayChange(next)) return;
                        setComparisonMode("after");
                      }}
                      onChangeMainTransform={next => {
                        setMainTransform(next);
                        setComparisonMode("after");
                      }}
                      onChangeMotionKeyframes={next => {
                        setMotionKeyframes(next);
                        setComparisonMode("after");
                      }}
                      onChangeAdjustmentLayers={next => {
                        setAdjustmentLayers(next);
                        setComparisonMode("after");
                      }}
                      onInteractionStart={() => setIsDragging(true)}
                      onInteractionEnd={() => setIsDragging(false)}
                      onChangeHideShyLayers={setHideShyLayers}
                      onSeek={jumpToOutputTimelineTime}
                    />
                  ) : (
                    <StudioNodeFlow videoRef={videoRef} mapTime={getPreviewTimelineTime} advancedColor={advancedColor}
                      overlays={overlays}
                      mainTransform={mainTransform}
                      finishFx={finishFx}
                      compositionOrder={compositionOrder}
                      adjustmentLayers={adjustmentLayers}
                      subjectVfxScenes={subjectVfxScenes}
                      motionKeyframes={motionKeyframes}
                      nodeFlow={nodeFlow}
                      activeOverlayId={activeOverlayId}
                      currentTime={previewTimelineTime}
                      programmeDuration={liveTimelineDuration}
                      onSelectOverlay={id => {
                        setSelectedGroupId(null);
                        setActiveOverlayId(id);
                        setComparisonMode("after");
                      }}
                      onChangeOverlays={next => {
                        if (!commitCheckedOverlayChange(next)) return;
                        setComparisonMode("after");
                      }}
                      onChangeMainTransform={next => {
                        setMainTransform(next);
                        setComparisonMode("after");
                      }}
                      onChangeFinishFx={next => {
                        replaceFinishFx(next);
                        setComparisonMode("after");
                      }}
                      onChangeCompositionOrder={next => {
                        setCompositionOrder(next);
                        setComparisonMode("after");
                      }}
                      onChangeAdjustmentLayers={next => {
                        setAdjustmentLayers(next);
                        setComparisonMode("after");
                      }}
                      onChangeNodeFlow={setNodeFlow}
                    />
                  )}
                </div>
              ) : null}

              {studioInspectorTab === "creatorfx" ? (
                <StudioCreatorWorkbench
                  value={creatorPreview}
                  onChange={next => {
                    setCreatorPreview(next);
                    setComparisonMode("after");
                    setProjectSaveState("unsaved");
                  }}
                  onOpenTool={selectCreativeTool}
                  onFreeze={() => {
                    videoRef.current?.pause?.();
                    setIsPreviewPaused(true);
                    setStudioActionMessage("Programme paused on the selected reaction frame.");
                  }}
                  onResume={() => {
                    safePlayMediaElement(videoRef.current);
                    setStudioActionMessage("Programme resumed from the held reaction frame.");
                  }}
                  onApplyMotionPreset={applyCreatorMotionPreset}
                  onApplyEffect={applyCreatorEffect}
                  onApplySpeedRamp={applyCreatorSpeedRamp}
                  onAddImpactSound={addCreatorImpactSound}
                  onApplyBeatSync={applyCreatorBeatSync}
                  onApplyLayerStack={applyCreatorLayerStack}
                  onSaveTemplate={saveEditTemplate}
                  onApplyDirectorSuggestion={applyCreatorDirectorSuggestion}
                  onInsertTitle={({ preset, text, position }) => {
                    addTextOverlay(preset, text);
                    if (position) setOverlays(current => current.map((overlay, index) => index === current.length - 1
                      ? { ...overlay, x: clampNumber(position.x, 5, 95, 50), y: clampNumber(position.y, 5, 95, 50) }
                      : overlay));
                    setCreatorPreview(current => ({ ...current, activeDemo: "" }));
                    setTimelineDockExpanded(true);
                    setStudioActionMessage("Editable title added to the timeline. Its text, timing and style are included in export.");
                  }}
                  cameraCount={new Set(resolvedSpeakerStackSources.map(source => source?.source).filter(Boolean)).size}
                  beatCount={musicBeatMarkers.length}
                  layerCount={1 + overlays.filter(overlay => !overlay.isCaption).length}
                  transcriptEvidence={
                    normalizedTimedCaptionSegments.find(
                      segment =>
                        !segment.reviewRequired &&
                        previewTimelineTime >= Number(segment.start || 0) &&
                        previewTimelineTime <= Number(segment.end || 0)
                    )?.text ||
                    normalizedTimedCaptionSegments.find(segment => !segment.reviewRequired)?.text ||
                    ""
                  }
                />
              ) : null}

              {studioInspectorTab === "pacing" ? (
                <div className="clip-inspector-body" role="tabpanel">
                  <div className="inspector-heading-row">
                    <div>
                      <span className="panel-kicker">Pacing engine</span>
                      <h4>Control energy without making editing complicated</h4>
                    </div>
                    <span className="inspector-status-dot is-ready">
                      {previewSpeed.toFixed(2).replace(/\.00$/, "")}×
                    </span>
                  </div>

                  <div className="inspector-field">
                    <span>Quick speed</span>
                    <div className="speed-preset-grid">
                      {PREVIEW_SPEED_OPTIONS.map(speed => (
                        <button
                          key={speed}
                          type="button"
                          className={Math.abs(previewSpeed - speed) < 0.01 ? "is-active" : ""}
                          aria-pressed={Math.abs(previewSpeed - speed) < 0.01}
                          onClick={() => changePreviewSpeed(speed)}
                        >
                          {speed}×
                        </button>
                      ))}
                    </div>
                  </div>

                  <label className="inspector-range">
                    <span>
                      <b>Fine speed</b>
                      <strong>{previewSpeed.toFixed(2)}×</strong>
                    </span>
                    <input
                      type="range"
                      min={0.5}
                      max={2}
                      step={0.05}
                      value={previewSpeed}
                      onChange={event => changePreviewSpeed(Number(event.target.value))}
                    />
                    <small>Pitch stays preserved so voices remain natural.</small>
                  </label>

                  <div className="inspector-field">
                    <span>Energy</span>
                    <div className="inspector-choice-grid is-three">
                      {[
                        ["calm", "Calm"],
                        ["balanced", "Balanced"],
                        ["energetic", "Energetic"],
                      ].map(([value, label]) => (
                        <button
                          key={value}
                          type="button"
                          className={pacingLevel === value ? "is-active" : ""}
                          aria-pressed={pacingLevel === value}
                          onClick={() => {
                            setPacingLevel(value);
                            setStudioActionMessage(
                              `${label} energy selected. Playback speed remains ${previewSpeed.toFixed(2)}×; adjust it separately above.`
                            );
                          }}
                        >
                          {label}
                        </button>
                      ))}
                    </div>
                  </div>

                  <label className="inspector-toggle-row">
                    <span>
                      <b>Remove dead air</b>
                      <small>
                        Marks long pauses for the final edit without changing your source.
                      </small>
                    </span>
                    <input
                      type="checkbox"
                      checked={silenceRemoval}
                      onChange={event => setSilenceRemoval(event.target.checked)}
                    />
                  </label>

                  {silenceRemoval ? (
                    <div className="pacing-fine-controls">
                      <label className="inspector-range">
                        <span>
                          <b>Speech threshold</b>
                          <strong>{silenceThreshold} dB</strong>
                        </span>
                        <input
                          type="range"
                          min={-55}
                          max={-20}
                          step={1}
                          value={silenceThreshold}
                          onChange={event => setSilenceThreshold(Number(event.target.value))}
                        />
                      </label>
                      <label className="inspector-range">
                        <span>
                          <b>Minimum pause</b>
                          <strong>{Number(minSilenceDuration).toFixed(2)}s</strong>
                        </span>
                        <input
                          type="range"
                          min={0.25}
                          max={2.5}
                          step={0.05}
                          value={minSilenceDuration}
                          onChange={event => setMinSilenceDuration(Number(event.target.value))}
                        />
                      </label>
                    </div>
                  ) : null}

                  <div className="dead-air-slicer-card">
                    <div className="dead-air-slicer-header">
                      <span className="panel-kicker">Dead-Air Ripple Slicer</span>
                      <strong>Physical Silence Elimination</strong>
                    </div>
                    <p className="dead-air-slicer-desc">
                      {silencePreview?.silenceSegments?.length
                        ? `Detected ${silencePreview.silenceSegments.length} pauses (${silencePreview.silenceSegments.reduce((a, s) => a + Number(s.duration || 0), 0).toFixed(1)}s dead air). Slices the timeline and pulls speech takes together.`
                        : "Physically slices detected pauses out of the primary video track, adjusts downstream overlays, and removes dead air from your sequence."}
                    </p>
                    <button
                      type="button"
                      className="inspector-primary-action dead-air-slicer-btn"
                      onClick={rippleCutAllSilences}
                      disabled={isRippleCuttingSilence}
                      data-testid="pacing-ripple-cut-silences"
                    >
                      {isRippleCuttingSilence
                        ? "⏳ Slicing Dead Air..."
                        : silencePreview?.silenceSegments?.length
                          ? `⚡ Ripple-Cut All Silences (${silencePreview.silenceSegments.reduce((a, s) => a + Number(s.duration || 0), 0).toFixed(1)}s saved)`
                          : "⚡ Detect & Ripple-Cut Dead Air"}
                    </button>
                  </div>

                  <section className="speed-ramp-card">
                    <div className="sound-effects-heading">
                      <div>
                        <span className="panel-kicker">Speed ramp curve</span>
                        <strong>Smooth acceleration and slow-down points</strong>
                      </div>
                      <span className="sound-effect-count">{speedKeyframes.length} points</span>
                    </div>
                    <button
                      type="button"
                      className="inspector-primary-action"
                      onClick={addSpeedKeyframeAtPlayhead}
                    >
                      ◆ Add {previewSpeed.toFixed(2)}× point at {previewTimelineTime.toFixed(2)}s
                    </button>
                    <div className="automation-keyframe-list">
                      {speedKeyframes.map(keyframe => (
                        <article key={keyframe.id}>
                          <button
                            type="button"
                            onClick={() => jumpToOutputTimelineTime(Number(keyframe.time || 0))}
                          >
                            <span>◆</span>
                            <strong>{Number(keyframe.value || 1).toFixed(2)}×</strong>
                            <small>{Number(keyframe.time || 0).toFixed(2)}s</small>
                          </button>
                          <select
                            aria-label="Speed point easing"
                            value={keyframe.easing || "bezier"}
                            onChange={event =>
                              setSpeedKeyframes(current =>
                                current.map(item =>
                                  item.id === keyframe.id
                                    ? { ...item, easing: event.target.value }
                                    : item
                                )
                              )
                            }
                          >
                            {STUDIO_EASINGS.map(easing => (
                              <option key={easing.id} value={easing.id}>
                                {easing.label}
                              </option>
                            ))}
                          </select>
                          <button
                            type="button"
                            aria-label="Remove speed point"
                            onClick={() =>
                              setSpeedKeyframes(current =>
                                current.filter(item => item.id !== keyframe.id)
                              )
                            }
                          >
                            ×
                          </button>
                        </article>
                      ))}
                    </div>
                  </section>
                </div>
              ) : null}

              {studioInspectorTab === "broll" ? (
                <div className="clip-inspector-body" role="tabpanel">
                  <StudioGeneratedBRollPanel projectId={projectId} apiBase={API_BASE_URL}
                    getToken={getMediaAuthToken} getRequest={getGeneratedBRollRequest}
                    jobId={bRollGenerationJobId} onRemember={setBRollGenerationJobId}
                    onAccept={acceptGeneratedBRoll} autoRequest={autoBRollRequest}
                    onReject={(shot,job) => {
                      setOverlays(current => current.filter(item => !(item.generationRef?.jobId === job.id && item.generationRef?.shotId === shot.id)));
                      setProjectMedia(current => current.filter(item => item.id !== shot.asset?.id));
                    }} />
                  <div className="inspector-heading-row">
                    <div>
                      <span className="panel-kicker">Visual layers</span>
                      <h4>Place video or images exactly where the story needs them</h4>
                    </div>
                    <span className="inspector-status-dot is-ready">
                      {overlays.filter(overlay => overlay.bRollMode).length} clips
                    </span>
                  </div>

                  <div className="inspector-field broll-cadence-control">
                    <span>B-roll pacing</span>
                    <div className="inspector-choice-grid is-three">
                      {Object.entries(BROLL_CADENCE_PRESETS).map(([value, preset]) => (
                        <button
                          key={value}
                          type="button"
                          className={bRollCadence === value ? "is-active" : ""}
                          aria-pressed={bRollCadence === value}
                          onClick={() => {
                            setBRollCadence(value);
                            setStudioActionMessage(
                              `${preset.label} B-roll pacing selected. ${preset.helper}`
                            );
                          }}
                        >
                          {preset.label}
                        </button>
                      ))}
                    </div>
                    <small>
                      {bRollPlanStatus.suggestions.length} suggested beats across{" "}
                      {formatPreviewTimePrecise(currentTimelineWindow.duration)} ·{" "}
                      {bRollPlanStatus.coveredCount
                        ? `${bRollPlanStatus.coveredCount} already covered · `
                        : ""}
                      {bRollPlanStatus.plannedCount
                        ? `${bRollPlanStatus.plannedCount} awaiting footage · `
                        : ""}
                      {bRollPlanStatus.missingSuggestions.length
                        ? `${bRollPlanStatus.missingSuggestions.length} open · `
                        : "Coverage complete · "}
                      {BROLL_CADENCE_PRESETS[bRollCadence].helper}
                    </small>
                    <button
                      type="button"
                      className="inspector-secondary-action"
                      onClick={addBRollPlan}
                    >
                      Plan whole clip
                    </button>
                  </div>

                  {!bRollPlanStatus.suggestions.length ? (
                    <div className="broll-evidence-empty" data-testid="broll-evidence-empty">
                      <strong>No story visual has enough spoken evidence yet.</strong>
                      <span>
                        Generate and review the multilingual transcript. The studio will keep the
                        real speaker on screen instead of inventing generic B-roll.
                      </span>
                      <button
                        type="button"
                        onClick={() => {
                          setStudioInspectorTab("captions");
                          setActiveCreativeTool("captions");
                        }}
                      >
                        Review transcript
                      </button>
                    </div>
                  ) : null}

                  <button
                    type="button"
                    className="inspector-upload-card"
                    onClick={() => brollVideoInputRef.current?.click()}
                  >
                    <span>＋</span>
                    <div>
                      <strong>Upload B-roll</strong>
                      <small>One or multiple MP4, MOV or WEBM files</small>
                    </div>
                  </button>

                  <button
                    type="button"
                    className="inspector-upload-card"
                    onClick={() => imageInputRef.current?.click()}
                  >
                    <span>🖼</span>
                    <div>
                      <strong>Attach an image</strong>
                      <small>Timed full screen, picture-in-picture or side by side</small>
                    </div>
                  </button>

                  {activeOverlayIsTimedMedia ? (
                    <>
                      <div className="inspector-selected-asset">
                        {activeOverlay.type === "video" ? (
                          <video
                            ref={element => applySafeMediaSource(element, activeOverlaySafeSrc)}
                            muted
                            playsInline
                            preload="metadata"
                          />
                        ) : (
                          <img
                            ref={element => applySafeMediaSource(element, activeOverlaySafeSrc)}
                            alt="Selected visual insert"
                          />
                        )}
                        <div>
                          <span>
                            Selected {activeOverlay.type === "image" ? "image" : "cutaway"}
                          </span>
                          <strong>{activeOverlayDisplayName}</strong>
                          <small>
                            {formatEditorDuration(activeOverlayStartTime)}–
                            {formatEditorDuration(activeOverlayEndTime)}
                          </small>
                        </div>
                      </div>

                      <div className="inspector-inline-actions broll-placement-actions">
                        <button type="button" onClick={() => placeOverlayAtPlayhead(activeOverlay)}>
                          Place at playhead
                        </button>
                        <button
                          type="button"
                          onClick={() => previewOverlayInTimeline(activeOverlay)}
                        >
                          Play in timeline
                        </button>
                        {activeOverlay.type === "video" ? (
                          <button
                            type="button"
                            className={soloPreviewOverlayId === activeOverlay.id ? "is-active" : ""}
                            data-testid="preview-broll-only"
                            onClick={() => toggleSoloOverlayPreview(activeOverlay)}
                          >
                            {soloPreviewOverlayId === activeOverlay.id
                              ? "Pause B-roll only"
                              : "Play B-roll only"}
                          </button>
                        ) : null}
                      </div>

                      <div className="inspector-time-grid">
                        <label>
                          <span>Start</span>
                          <input
                            type="number"
                            min={0}
                            step={0.1}
                            value={activeOverlayStartTime.toFixed(1)}
                            onChange={event =>
                              updateOverlayTimeRange(
                                activeOverlay.id,
                                Number(event.target.value),
                                activeOverlayDuration
                              )
                            }
                          />
                        </label>
                        <label>
                          <span>Duration (MM:SS)</span>
                          <input
                            key={`${activeOverlay.id}-${activeOverlayDuration}`}
                            aria-label="B-roll duration"
                            type="text"
                            inputMode="decimal"
                            defaultValue={formatEditorDuration(activeOverlayDuration)}
                            placeholder="2:00 or 120"
                            onBlur={event =>
                              commitOverlayDurationText(activeOverlay, event.target.value)
                            }
                            onKeyDown={event => {
                              if (event.key === "Enter") event.currentTarget.blur();
                            }}
                          />
                        </label>
                      </div>

                      <div className="broll-duration-editor">
                        <div
                          className="broll-duration-presets"
                          aria-label="B-roll duration presets"
                        >
                          {[
                            [5, "5 sec"],
                            [10, "10 sec"],
                            [30, "30 sec"],
                            [60, "60 sec"],
                            [120, "2 min"],
                            [300, "5 min"],
                          ].map(([seconds, label]) => (
                            <button
                              key={seconds}
                              type="button"
                              aria-label={`Set duration to ${label}`}
                              onClick={() => applyOverlayDuration(activeOverlay, seconds, label)}
                            >
                              {label}
                            </button>
                          ))}
                          <button
                            type="button"
                            onClick={() =>
                              applyOverlayDuration(
                                activeOverlay,
                                activeOverlayTimelineRemaining,
                                "To end"
                              )
                            }
                          >
                            To end
                          </button>
                        </div>
                        <small>
                          Up to {formatEditorDuration(activeOverlayTimelineRemaining)} remains after
                          this start point. Type seconds, MM:SS, “2m” or “5:00”.
                        </small>
                      </div>

                      {activeOverlay.type === "video" ? (
                        <label className="inspector-range">
                          <span>
                            <b>Use B-roll from</b>
                            <strong>
                              {Number(activeOverlay.sourceStartTime || 0).toFixed(1)}s
                            </strong>
                          </span>
                          <input
                            aria-label="B-roll source start"
                            type="range"
                            min={0}
                            max={Math.max(0, Number(activeOverlay.sourceDuration || 0) - 0.05)}
                            step={0.1}
                            value={Number(activeOverlay.sourceStartTime || 0)}
                            onChange={event =>
                              setOverlayStyleOption(
                                activeOverlay.id,
                                "sourceStartTime",
                                Number(event.target.value)
                              )
                            }
                          />
                          <small>Choose which moment inside the uploaded B-roll should play.</small>
                        </label>
                      ) : null}

                      {activeOverlay.type === "image" ? (
                        <div className="inspector-field">
                          <span>Image motion (Ken Burns)</span>
                          <div className="inspector-choice-grid is-three">
                            {[
                              ["none", "Static"],
                              ["zoomIn", "Zoom in"],
                              ["zoomOut", "Zoom out"],
                              ["panLeft", "Pan left"],
                              ["panRight", "Pan right"],
                              ["panUp", "Pan up"],
                            ].map(([value, label]) => (
                              <button
                                key={value}
                                type="button"
                                className={(activeOverlay.kenBurns || "none") === value ? "is-active" : ""}
                                aria-pressed={(activeOverlay.kenBurns || "none") === value}
                                onClick={() => setOverlayStyleOption(activeOverlay.id, "kenBurns", value)}
                              >
                                {label}
                              </button>
                            ))}
                          </div>
                          <small className="inspector-reassurance">
                            {(activeOverlay.kenBurns || "none") !== "none"
                              ? "✓ Slow cinematic motion keeps the frame alive during static images."
                              : "Enable motion to avoid a frozen frame."}
                          </small>
                        </div>
                      ) : null}

                      {activeOverlay.type === "video" ? (
                        <div className="inspector-field broll-source-end-control">
                          <span>When B-roll footage ends</span>
                          <div className="inspector-choice-grid is-three">
                            {[
                              ["return", "Return to original"],
                              ["loop", "Loop B-roll"],
                              ["hold", "Hold last frame"],
                            ].map(([value, label]) => (
                              <button
                                key={value}
                                type="button"
                                className={
                                  normalizeBRollEndBehavior(activeOverlay.sourceEndBehavior) ===
                                  value
                                    ? "is-active"
                                    : ""
                                }
                                aria-pressed={
                                  normalizeBRollEndBehavior(activeOverlay.sourceEndBehavior) ===
                                  value
                                }
                                onClick={() => setOverlaySourceEndBehavior(activeOverlay.id, value)}
                              >
                                {label}
                              </button>
                            ))}
                          </div>
                          <small className="inspector-reassurance">
                            {Number.isFinite(activeOverlayAvailableSourceDuration) ? (
                              activeOverlayDuration > activeOverlayAvailableSourceDuration ? (
                                <>
                                  Source has{" "}
                                  {formatEditorDuration(activeOverlayAvailableSourceDuration)}{" "}
                                  available. “
                                  {normalizeBRollEndBehavior(activeOverlay.sourceEndBehavior) ===
                                  "loop"
                                    ? "Loop"
                                    : normalizeBRollEndBehavior(activeOverlay.sourceEndBehavior) ===
                                        "hold"
                                      ? "Hold"
                                      : "Return"}
                                  ” controls the remaining{" "}
                                  {formatEditorDuration(
                                    activeOverlayDuration - activeOverlayAvailableSourceDuration
                                  )}
                                  .
                                </>
                              ) : (
                                <>
                                  This source covers the complete{" "}
                                  {formatEditorDuration(activeOverlayVisibleDuration)} cutaway.
                                </>
                              )
                            ) : (
                              "Source length will be detected from the uploaded video."
                            )}
                          </small>
                        </div>
                      ) : null}

                      <div className="inspector-field">
                        <span>Placement mode</span>
                        <div className="inspector-choice-grid is-two" style={{ marginBottom: "8px" }}>
                          {[
                            ["overwrite", "Overwrite (Cover)"],
                            ["insert", "Insert (Pause Main)"],
                          ].map(([value, label]) => (
                            <button
                              key={value}
                              type="button"
                              className={(activeOverlay.placementMode || "overwrite") === value ? "is-active" : ""}
                              aria-pressed={(activeOverlay.placementMode || "overwrite") === value}
                              onClick={() => setOverlayStyleOption(activeOverlay.id, "placementMode", value)}
                            >
                              {label}
                            </button>
                          ))}
                        </div>
                        <small className="inspector-reassurance">
                          {(activeOverlay.placementMode || "overwrite") === "insert"
                            ? "✓ Main video pauses during this clip, then resumes exactly where it left off."
                            : "Main video continues playing underneath this overlay."}
                        </small>
                        {(activeOverlay.placementMode || "overwrite") === "insert" ? (
                          <button
                            type="button"
                            className="inspector-primary-action is-accent"
                            style={{
                              marginTop: "10px",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              gap: "8px",
                              background: "linear-gradient(135deg, #0284c7, #2563eb)",
                              color: "#fff",
                              fontWeight: 600,
                              borderRadius: "8px",
                              padding: "10px 14px",
                              border: "none",
                              cursor: "pointer",
                              width: "100%",
                              boxShadow: "0 4px 12px rgba(37, 99, 235, 0.35)",
                            }}
                            onClick={() => rippleInsertOverlayIntoSequence(activeOverlay.id)}
                            title="Slice the main sequence at this point and insert this B-roll clip as a story cut"
                          >
                            <span>⚡ Ripple-Insert into Story Sequence</span>
                          </button>
                        ) : null}
                      </div>

                      <div className="inspector-field">
                        <span>Layout</span>
                        <div className="inspector-choice-grid is-three">
                          {[
                            ["fullscreen", "Full screen"],
                            ["pip", "Picture-in-picture"],
                            ["sideBySide", "Side by side"],
                          ].map(([value, label]) => (
                            <button
                              key={value}
                              type="button"
                              className={activeOverlay.bRollMode === value ? "is-active" : ""}
                              aria-pressed={activeOverlay.bRollMode === value}
                              onClick={() => setOverlayBRollMode(activeOverlay.id, value)}
                            >
                              {label}
                            </button>
                          ))}
                        </div>
                      </div>

                      {activeOverlay.bRollMode !== "fullscreen" ? (
                        <div className="inspector-field">
                          <span>Rounded frame</span>
                          <label className="inspector-range">
                            <span>
                              <b>Corner curve</b>
                              <strong>{Number(activeOverlay.borderRadius ?? 28)}px</strong>
                            </span>
                            <input
                              aria-label="Frame corner curve"
                              type="range"
                              min={12}
                              max={48}
                              step={1}
                              value={Number(activeOverlay.borderRadius ?? 28)}
                              onChange={event =>
                                setOverlayStyleOption(
                                  activeOverlay.id,
                                  "borderRadius",
                                  Number(event.target.value)
                                )
                              }
                            />
                          </label>
                          <small className="inspector-reassurance">
                            ✓ Smooth rounded corners match in preview and final export.
                          </small>
                        </div>
                      ) : null}

                      {activeOverlay.type === "video" ? (
                        <div className="inspector-field">
                          <span>Cutaway audio</span>
                          <div className="inspector-choice-grid is-three audio-mode-grid">
                            {[
                              ["original", "Keep original"],
                              ["overlay", "Use overlay"],
                              ["mix", "Mix both"],
                            ].map(([value, label]) => (
                              <button
                                key={value}
                                type="button"
                                className={
                                  getOverlayAudioMode(activeOverlay) === value ? "is-active" : ""
                                }
                                aria-pressed={getOverlayAudioMode(activeOverlay) === value}
                                onClick={() => setOverlayAudioMode(activeOverlay.id, value)}
                              >
                                {label}
                              </button>
                            ))}
                          </div>
                          <small className="inspector-reassurance">
                            ✓ Original audio returns automatically after the cutaway.
                          </small>
                        </div>
                      ) : null}

                      {activeOverlay.type === "video" &&
                      getOverlayAudioMode(activeOverlay) === "mix" ? (
                        <>
                          <label className="inspector-check-row">
                            <input
                              type="checkbox"
                              checked={!!activeOverlay.audioDucking}
                              onChange={event =>
                                setOverlayAudioOption(
                                  activeOverlay.id,
                                  "audioDucking",
                                  event.target.checked
                                )
                              }
                            />
                            <span>
                              <strong>Duck original under overlay</strong>
                              <small>Keeps both sources clear when voices overlap.</small>
                            </span>
                          </label>
                          {activeOverlay.audioDucking ? (
                            <label className="inspector-range">
                              <span>
                                <b>Original ducking</b>
                                <strong>
                                  {Math.round((activeOverlay.audioDuckingStrength ?? 0.35) * 100)}%
                                </strong>
                              </span>
                              <input
                                aria-label="Original ducking strength"
                                type="range"
                                min={10}
                                max={90}
                                step={5}
                                value={Math.round(
                                  (activeOverlay.audioDuckingStrength ?? 0.35) * 100
                                )}
                                onChange={event =>
                                  setOverlayAudioOption(
                                    activeOverlay.id,
                                    "audioDuckingStrength",
                                    Number(event.target.value) / 100
                                  )
                                }
                              />
                            </label>
                          ) : null}
                        </>
                      ) : null}

                      {activeOverlay.type === "video" &&
                      getOverlayAudioMode(activeOverlay) !== "original" ? (
                        <label className="inspector-range">
                          <span>
                            <b>Overlay volume</b>
                            <strong>
                              {Math.round((activeOverlay.overlayAudioVolume ?? 0.7) * 100)}%
                            </strong>
                          </span>
                          <input
                            aria-label="B-roll overlay volume"
                            type="range"
                            min={0}
                            max={100}
                            step={5}
                            value={Math.round((activeOverlay.overlayAudioVolume ?? 0.7) * 100)}
                            onChange={event =>
                              setOverlayAudioOption(
                                activeOverlay.id,
                                "overlayAudioVolume",
                                Number(event.target.value) / 100
                              )
                            }
                          />
                        </label>
                      ) : null}

                      {activeOverlay.type === "image" ? (
                        <>
                          <div className="inspector-field">
                            <span>Image crop</span>
                            <div className="inspector-choice-grid is-three">
                              {[
                                ["cover", "Fill frame"],
                                ["contain", "Show all"],
                                ["stretch", "Stretch"],
                              ].map(([value, label]) => (
                                <button
                                  key={value}
                                  type="button"
                                  className={
                                    (activeOverlay.mediaFit || "cover") === value ? "is-active" : ""
                                  }
                                  aria-pressed={(activeOverlay.mediaFit || "cover") === value}
                                  onClick={() =>
                                    setOverlayStyleOption(activeOverlay.id, "mediaFit", value)
                                  }
                                >
                                  {label}
                                </button>
                              ))}
                            </div>
                          </div>
                          <label className="inspector-range">
                            <span>
                              <b>Rotation</b>
                              <strong>{Math.round(activeOverlay.rotation || 0)}°</strong>
                            </span>
                            <input
                              aria-label="Image rotation"
                              type="range"
                              min={-20}
                              max={20}
                              step={1}
                              value={activeOverlay.rotation || 0}
                              onChange={event =>
                                setOverlayStyleOption(
                                  activeOverlay.id,
                                  "rotation",
                                  Number(event.target.value)
                                )
                              }
                            />
                          </label>
                          <label className="inspector-range">
                            <span>
                              <b>Opacity</b>
                              <strong>{Math.round((activeOverlay.opacity ?? 1) * 100)}%</strong>
                            </span>
                            <input
                              aria-label="Image opacity"
                              type="range"
                              min={10}
                              max={100}
                              step={5}
                              value={Math.round((activeOverlay.opacity ?? 1) * 100)}
                              onChange={event =>
                                setOverlayOpacity(
                                  activeOverlay.id,
                                  Number(event.target.value) / 100
                                )
                              }
                            />
                          </label>
                          <div className="inspector-field">
                            <span>Entrance motion</span>
                            <div className="inspector-choice-grid is-three">
                              {[
                                ["fade", "Fade"],
                                ["slideUp", "Slide up"],
                                ["slideLeft", "Slide left"],
                                ["zoom", "Zoom"],
                                ["pop", "Pop"],
                                ["wipe", "Wipe"],
                                ["blur", "Blur in"],
                              ].map(([value, label]) => (
                                <button
                                  key={value}
                                  type="button"
                                  className={
                                    (activeOverlay.animation?.enter || "fade") === value
                                      ? "is-active"
                                      : ""
                                  }
                                  aria-pressed={
                                    (activeOverlay.animation?.enter || "fade") === value
                                  }
                                  onClick={() =>
                                    setOverlayAnimation(activeOverlay.id, "enter", value)
                                  }
                                >
                                  {label}
                                </button>
                              ))}
                            </div>
                          </div>
                        </>
                      ) : null}

                      <button
                        type="button"
                        className="inspector-primary-action"
                        onClick={() => {
                          setComparisonMode("split");
                          focusComparisonPreview("broll", false);
                          setStudioActionMessage(
                            `${activeOverlay.type === "image" ? "Image" : "B-roll"} applied and previewing at its exact timeline position.`
                          );
                        }}
                      >
                        ▣ Apply {activeOverlay.type === "image" ? "image" : "B-roll"}
                      </button>
                    </>
                  ) : (
                    <div className="inspector-empty-state">
                      <span>▣</span>
                      <strong>No B-roll selected</strong>
                      <small>Upload a clip or select a cutaway block in the timeline.</small>
                    </div>
                  )}
                </div>
              ) : null}

              {studioInspectorTab === "sound" ? (
                <div className="clip-inspector-body" role="tabpanel">
                  <div className="inspector-heading-row">
                    <div>
                      <span className="panel-kicker">Sound mix</span>
                      <h4>Keep speech clear and energy consistent</h4>
                    </div>
                    <span className={`inspector-status-dot ${addMusic ? "is-ready" : ""}`}>
                      {addMusic ? "Music on" : "Voice only"}
                    </span>
                  </div>

                  <AudioRemixPanel
                    value={audioRemix}
                    bypass={audioRemixBypass}
                    previewMediaRef={videoRef}
                    previewMediaKey={`${activeTimelineIndex}:${activeProgrammeSlot}:${renderedOutputUrl || "live"}`}
                    loopActive={!!audioRemixLoop}
                    hasMusic={addMusic && !!musicTrack}
                    exactPreviewStatus={exactAudioRemixPreview.status}
                    exactPreviewError={exactAudioRemixPreview.error}
                    livePreviewError={audioRemixLiveError}
                    onChange={next => {
                      const normalized = normalizeAudioRemix(next);
                      setPreviewFinishedFile(false);
                      setAudioRemix(normalized);
                      setAudioRemixBypass(false);
                      setPreviewSpeed(normalized.speed);
                      setComparisonMode("after");
                      const video = videoRef.current;
                      if (video) {
                        unlockAudioRemixContext(video);
                      }
                      setAudioRemixPlaybackRequest(request => request + 1);
                      setStudioActionMessage(
                        normalized.preset === "chipmunk"
                          ? "Chipmunk voice is playing at the original picture timing. Preview Remix compares the final mastering."
                          : `${normalized.preset.replaceAll("_", " ")} remix is live. EQ, reverb and speed are active in After.`
                      );
                    }}
                    onBefore={() => {
                      setPreviewFinishedFile(false);
                      exactAudioRemixPreviewRef.current?.pause();
                      backgroundSoundPreviewSuppressedRef.current = false;
                      setAudioRemixBypass(true);
                      setComparisonMode("after");
                      setAudioRemixPlaybackRequest(request => request + 1);
                      setStudioActionMessage("Before is playing with the original audio tone.");
                    }}
                    onPreview={handleExactAudioRemixPreview}
                    onToggleLoop={() => {
                      setPreviewFinishedFile(false);
                      const video = videoRef.current;
                      if (audioRemixLoop) {
                        setAudioRemixLoop(null);
                        setStudioActionMessage("Remix preview loop is off.");
                        return;
                      }
                      const start = Math.max(0, Number(video?.currentTime || 0));
                      const duration = Number(
                        video?.duration || getClipDurationSeconds(currentTimelineClip) || 0
                      );
                      const end = Math.min(duration || start + 8, start + 8);
                      setAudioRemixLoop({ start, end: Math.max(start + 0.5, end) });
                      setAudioRemixBypass(false);
                      setAudioRemixPlaybackRequest(request => request + 1);
                      setStudioActionMessage(
                        `Looping ${formatPreviewTimePrecise(start)}–${formatPreviewTimePrecise(
                          Math.max(start + 0.5, end)
                        )} for an exact preset comparison.`
                      );
                    }}
                  />

                  {exactAudioRemixPreview.url ? (
                    <SafeAudio
                      ref={exactAudioRemixPreviewRef}
                      data-testid="exact-audio-remix-preview"
                      className="audio-remix-exact-player"
                      controls
                      preload="auto"
                      src={exactAudioRemixPreview.url}
                      onEnded={() => {
                        backgroundSoundPreviewSuppressedRef.current = false;
                        setStudioActionMessage("Exact Remix Audio preview finished.");
                      }}
                      onError={() => {
                        backgroundSoundPreviewSuppressedRef.current = false;
                        setExactAudioRemixPreview(current => ({
                          ...current,
                          status: "failed",
                          error: "The exact preview audio failed during browser playback.",
                        }));
                      }}
                    />
                  ) : null}

                  <section className="advanced-tool-section beat-sync-panel" aria-label="Music Beat Sync & Viral Rhythm" style={{ marginBottom: "16px" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" }}>
                      <div>
                        <span className="panel-kicker">Rhythm engine</span>
                        <h5 style={{ margin: 0 }}>🎵 Cut to Music Beats & Transient Drops</h5>
                      </div>
                      <span className="smart-zoom-zone-badge" style={{ color: "#38bdf8", fontSize: "11px" }}>
                        {musicBeatMarkers.length ? `${musicBeatMarkers.length} rhythm points (${calculateBpm(musicBeatMarkers) || 120} BPM)` : "Add music to detect rhythm"}
                      </span>
                    </div>

                    <p style={{ fontSize: "11px", color: "var(--muted)", margin: "0 0 10px 0" }}>
                      Automatically lock timeline cuts, B-roll overlays, and camera punch-ins to background music kick and snare transients.
                    </p>

                    <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", marginBottom: "10px" }}>
                      <button
                        type="button"
                        className={`split-audio-pill ${beatSnapEnabled ? "is-active" : ""}`}
                        onClick={() => setBeatSnapEnabled(prev => !prev)}
                        title="Toggle magnetic snapping to musical beats across the timeline (Hotkey: B)"
                      >
                        {beatSnapEnabled ? "🧲 Magnetic Beats ON" : "🧲 Enable Beat Snap (Shift+B)"}
                      </button>
                      <button
                        type="button"
                        className="split-audio-pill"
                        onClick={handleAlignCutsToBeat}
                        disabled={!musicBeatMarkers.length || timeline.length <= 1}
                        title="Shift sequence cuts to nearest musical beat transients with downstream ripple"
                      >
                        🎵 Align Sequence Cuts to Beats
                      </button>
                      <button
                        type="button"
                        className="split-audio-pill"
                        onClick={handleAlignBRollToBeat}
                        disabled={!musicBeatMarkers.length || !overlays.some(o => o.bRollMode)}
                        title="Snap B-roll clips to musical beat points"
                      >
                        ⚡ Snap B-Roll to Beats
                      </button>
                    </div>

                    <div style={{ display: "flex", gap: "6px", alignItems: "center", borderTop: "1px solid var(--border)", paddingTop: "8px" }}>
                      <span style={{ fontSize: "11px", color: "var(--muted)" }}>Auto-Punch on drops:</span>
                      <button
                        type="button"
                        className="split-audio-pill"
                        onClick={() => handleGenerateBeatPunchIns("drops")}
                        disabled={!musicBeatMarkers.length}
                        title="Generate punch-ins on major beat drops (every 4 bars)"
                      >
                        🎯 On Drops (4 Bars)
                      </button>
                      <button
                        type="button"
                        className="split-audio-pill"
                        onClick={() => handleGenerateBeatPunchIns("bars")}
                        disabled={!musicBeatMarkers.length}
                        title="Generate punch-ins on every measure (1 Bar)"
                      >
                        🎯 Every Bar
                      </button>
                    </div>
                  </section>

                  <AudioRestorationPanel
                    settings={audioRestoration}
                    onUpdate={(field, value) =>
                      setAudioRestoration(current => ({ ...current, [field]: value }))
                    }
                    source={audioAutomationSource}
                    keyframesLocked={Boolean(trackStates[audioAutomationSource]?.locked)}
                    onSourceChange={setAudioAutomationSource}
                    keyframes={audioKeyframes}
                    playhead={previewTimelineTime}
                    onAddKeyframe={addAudioAutomationKeyframe}
                    onUpdateKeyframe={(keyframeId, changes) => {
                      if (!allowTimelineTimingEdit(audioAutomationSource)) return;
                      setAudioKeyframes(current => ({
                        ...current,
                        [audioAutomationSource]: (current[audioAutomationSource] || []).map(
                          keyframe =>
                            keyframe.id === keyframeId ? { ...keyframe, ...changes } : keyframe
                        ),
                      }));
                    }}
                    onRemoveKeyframe={removeAudioAutomationKeyframe}
                    onSeek={time => jumpToOutputTimelineTime(Number(time || 0))}
                    recording={isVoiceoverRecording}
                    onToggleRecording={() => void toggleVoiceoverRecording()}
                    voiceoverCount={voiceovers.length}
                  />

                  <label className="inspector-upload-card">
                    <strong>Import voice-over recording</strong>
                    <input type="file" accept="audio/*" aria-label="Import voice-over recording"
                      data-testid="voiceover-recording-input" onChange={event => {
                        const file = event.target.files?.[0]; event.target.value = "";
                        void importVoiceoverRecording(file);
                      }} />
                  </label>

                  {voiceovers.length > 0 && <section className="advanced-tool-section" aria-label="Voice-over takes">
                    <h5>Voice-over takes</h5>
                    {voiceovers.map(take => <div key={take.id} className="sound-effect-item voiceover-take">
                      <label>Take name<input aria-label={`Name for ${take.name}`} value={take.name}
                        onChange={event => updateVoiceoverTake(take.id, { name: event.target.value })} /></label>
                      <div className="voiceover-take-fields">
                        <label>Start (s)<input type="number" min="0" step="0.1" aria-label={`Start for ${take.name}`}
                          value={take.startTime} onChange={event => updateVoiceoverTake(take.id, { startTime: Number(event.target.value) })} /></label>
                        <label>Source in (s)<input type="number" min="0" step="0.1" aria-label={`Source in for ${take.name}`}
                          value={take.trimStart || 0} onChange={event => updateVoiceoverTake(take.id, { trimStart: Number(event.target.value) })} /></label>
                        <label>Length (s)<input type="number" min="0.05" step="0.1" aria-label={`Length for ${take.name}`}
                          value={Number(take.duration.toFixed(2))} onChange={event => updateVoiceoverTake(take.id, { duration: Number(event.target.value) })} /></label>
                      </div>
                      <label>Volume {Math.round(voiceoverGain(take.volume) * 100)}%
                        <input type="range" min="0" max="1" step="0.01" aria-label={`Volume for ${take.name}`}
                          value={voiceoverGain(take.volume)} onChange={event => updateVoiceoverTake(take.id, { volume: Number(event.target.value) })} /></label>
                      <div className="inspector-heading-row">
                        <button type="button" className="clip-action-btn" onClick={() => jumpToOutputTimelineTime(take.startTime)}>Go to take</button>
                        <button type="button" className="clip-action-btn" aria-pressed={take.enabled === false}
                          onClick={() => updateVoiceoverTake(take.id, { enabled: take.enabled === false })}>{take.enabled === false ? "Unmute" : "Mute"} take</button>
                        <button type="button" className="clip-action-btn" onClick={() => setVoiceovers(current => current.filter(item => item.id !== take.id))}>Remove take</button>
                      </div>
                    </div>)}
                  </section>}

                  <label
                    htmlFor="studio-background-sound-input"
                    role="button"
                    tabIndex={0}
                    className="inspector-upload-card"
                    onKeyDown={event => {
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        quickMusicFileInputRef.current?.click();
                      }
                    }}
                  >
                    <span>♫</span>
                    <div>
                      <strong>
                        {addMusic && musicTrack ? musicTrack.name : "Add background sound"}
                      </strong>
                      <small>
                        {addMusic ? "Click to replace this track" : "MP3, WAV, OGG, M4A or AAC"}
                      </small>
                    </div>
                  </label>

                  {addMusic && musicTrack ? (
                    <>
                      <label className="inspector-range">
                        <span>
                          <b>Background volume</b>
                          <strong>{Math.round((musicTrack.volume ?? musicVolume) * 100)}%</strong>
                        </span>
                        <input
                          type="range"
                          min={0}
                          max={60}
                          step={1}
                          value={Math.round((musicTrack.volume ?? musicVolume) * 100)}
                          onChange={event => {
                            const value = Number(event.target.value) / 100;
                            setMusicVolume(value);
                            setMusicTrackField("volume", value);
                          }}
                        />
                      </label>

                      <label className="inspector-check-row">
                        <input
                          type="checkbox"
                          checked={musicTrack.ducking ?? true}
                          onChange={event => setMusicTrackField("ducking", event.target.checked)}
                        />
                        <span>
                          <strong>Duck under speech</strong>
                          <small>Music lowers automatically while someone is talking.</small>
                        </span>
                      </label>

                      {musicTrack.ducking ? (
                        <>
                        <label className="inspector-select-field">
                          <span>Music ducking mode</span>
                          <select aria-label="Music ducking mode" value={musicTrack.duckingMode || "speech"}
                            onChange={event => setMusicTrackField("duckingMode", event.target.value)}>
                            <option value="speech">Speech-aware</option>
                            <option value="constant">Constant reduction</option>
                          </select>
                        </label>
                        <label className="inspector-range">
                          <span>
                            <b>Speech ducking</b>
                            <strong>
                              {Math.round((musicTrack.duckingStrength ?? 0.4) * 100)}%
                            </strong>
                          </span>
                          <input
                            aria-label="Speech ducking strength"
                            type="range"
                            min={15}
                            max={90}
                            step={5}
                            value={Math.round((musicTrack.duckingStrength ?? 0.4) * 100)}
                            onChange={event =>
                              setMusicTrackField(
                                "duckingStrength",
                                Number(event.target.value) / 100
                              )
                            }
                          />
                        </label>
                        </>
                      ) : null}

                      <div className="inspector-dual-range">
                        <label className="inspector-range">
                          <span>
                            <b>Fade in</b>
                            <strong>{(musicTrack.fadeIn ?? 0.5).toFixed(1)}s</strong>
                          </span>
                          <input
                            aria-label="Music fade in"
                            type="range"
                            min={0}
                            max={5}
                            step={0.1}
                            value={musicTrack.fadeIn ?? 0.5}
                            onChange={event =>
                              setMusicTrackField("fadeIn", Number(event.target.value))
                            }
                          />
                        </label>
                        <label className="inspector-range">
                          <span>
                            <b>Fade out</b>
                            <strong>{(musicTrack.fadeOut ?? 0.5).toFixed(1)}s</strong>
                          </span>
                          <input
                            aria-label="Music fade out"
                            type="range"
                            min={0}
                            max={5}
                            step={0.1}
                            value={musicTrack.fadeOut ?? 0.5}
                            onChange={event =>
                              setMusicTrackField("fadeOut", Number(event.target.value))
                            }
                          />
                        </label>
                      </div>

                      <label className="inspector-check-row">
                        <input
                          type="checkbox"
                          checked={musicTrack.loop ?? true}
                          onChange={event => setMusicTrackField("loop", event.target.checked)}
                        />
                        <span>
                          <strong>Loop for the full clip</strong>
                          <small>Repeats shorter tracks across longer edits.</small>
                        </span>
                      </label>

                      <div className="inspector-inline-actions">
                        <button type="button" onClick={toggleBackgroundSoundPreview}>
                          {isBackgroundSoundPreviewing ? "Stop sound" : "Preview sound"}
                        </button>
                        <button type="button" onClick={removeMusic}>
                          Remove
                        </button>
                      </div>
                    </>
                  ) : null}

                  <section className="sound-effects-section" aria-label="Sound effects">
                    <div className="sound-effects-heading">
                      <div>
                        <span className="panel-kicker">Sound effects</span>
                        <strong>Punch up cuts, reveals and proof moments</strong>
                      </div>
                      <span className="sound-effect-count">{soundEffects.length} placed</span>
                    </div>

                    <div className="sound-effect-preset-grid">
                      {SOUND_EFFECT_PRESETS.map(preset => (
                        <button
                          key={preset.id}
                          type="button"
                          className="sound-effect-preset"
                          onClick={() => addSoundEffectPreset(preset)}
                          data-testid={`sound-effect-preset-${preset.id}`}
                          aria-label={`Add ${preset.name} at playhead`}
                        >
                          <span>{preset.emoji}</span>
                          <strong>{preset.name}</strong>
                          <small>+ at playhead</small>
                        </button>
                      ))}
                      <button
                        type="button"
                        className="sound-effect-preset is-upload"
                        onClick={() => soundEffectFileInputRef.current?.click()}
                      >
                        <span>＋</span>
                        <strong>Upload</strong>
                        <small>Your own SFX</small>
                      </button>
                    </div>
                    <input
                      type="file"
                      ref={soundEffectFileInputRef}
                      data-testid="sound-effect-input"
                      accept="audio/*,.mp3,.wav,.ogg,.m4a,.aac"
                      style={{ display: "none" }}
                      onChange={handleSoundEffectUpload}
                    />

                    {soundEffects.length ? (
                      <div className="sound-effect-list" aria-label="Placed sound effects">
                        {soundEffects.map(effect => (
                          <button
                            key={effect.id}
                            type="button"
                            className={`sound-effect-chip ${activeSoundEffectId === effect.id ? "is-active" : ""}`}
                            onClick={() => {
                              setActiveSoundEffectId(effect.id);
                              seekLiveEditTimelineItem(Number(effect.startTime || 0), "sound");
                            }}
                          >
                            <span>{effect.emoji || "🔊"}</span>
                            <b>{effect.name}</b>
                            <small>{formatPreviewTimePrecise(effect.startTime)}</small>
                          </button>
                        ))}
                      </div>
                    ) : (
                      <p className="sound-effect-empty">
                        Put the playhead on a cut, reveal or caption hit, then add an effect.
                      </p>
                    )}

                    {activeSoundEffect ? (
                      <div className="sound-effect-editor-card" data-testid="sound-effect-editor">
                        <div className="sound-effect-editor-title">
                          <span>{activeSoundEffect.emoji || "🔊"}</span>
                          <div>
                            <strong>{activeSoundEffect.name}</strong>
                            <small>
                              {activeSoundEffect.builtIn
                                ? "Built-in · instant preview"
                                : "Uploaded effect"}
                            </small>
                          </div>
                          <label className="sound-effect-enable">
                            <input
                              type="checkbox"
                              checked={activeSoundEffect.enabled !== false}
                              onChange={event =>
                                updateSoundEffect(activeSoundEffect.id, {
                                  enabled: event.target.checked,
                                })
                              }
                            />
                            On
                          </label>
                        </div>

                        {activeSoundEffect.builtIn ? (
                          <label className="inspector-select">
                            <span>Effect sound</span>
                            <select aria-label="Effect sound" value={activeSoundEffect.tone}
                              onChange={event => {
                                const preset = SOUND_EFFECT_PRESETS.find(item => item.tone === event.target.value);
                                if (preset) updateSoundEffect(activeSoundEffect.id,
                                  { tone: preset.tone, name: preset.name, emoji: preset.emoji });
                              }}>
                              {SOUND_EFFECT_PRESETS.map(preset => (
                                <option key={preset.id} value={preset.tone}>{preset.name}</option>
                              ))}
                            </select>
                          </label>
                        ) : null}
                        <div className="inspector-time-grid sound-effect-time-grid">
                          <label>
                            <span>Start</span>
                            <input
                              data-testid="sound-effect-start"
                              type="number"
                              min={0}
                              max={outputTimelineDuration}
                              step={0.05}
                              value={Number(activeSoundEffect.startTime || 0).toFixed(2)}
                              onChange={event =>
                                updateSoundEffect(activeSoundEffect.id, {
                                  startTime: Number(event.target.value),
                                })
                              }
                            />
                          </label>
                          <label>
                            <span>Duration</span>
                            <input
                              data-testid="sound-effect-duration"
                              type="number"
                              min={0.05}
                              max={15}
                              step={0.05}
                              value={getSoundEffectDuration(activeSoundEffect).toFixed(2)}
                              onChange={event =>
                                updateSoundEffect(activeSoundEffect.id, {
                                  duration: Number(event.target.value),
                                })
                              }
                            />
                          </label>
                          <label>
                            <span>Trim start</span>
                            <input
                              type="number"
                              min={0}
                              step={0.05}
                              value={Number(activeSoundEffect.trimStart || 0).toFixed(2)}
                              disabled={activeSoundEffect.builtIn}
                              onChange={event =>
                                updateSoundEffect(activeSoundEffect.id, {
                                  trimStart: Number(event.target.value),
                                })
                              }
                            />
                          </label>
                        </div>

                        <label className="inspector-range">
                          <span>
                            <b>Effect volume</b>
                            <strong>{Math.round(activeSoundEffect.volume * 100)}%</strong>
                          </span>
                          <input
                            data-testid="sound-effect-volume"
                            type="range"
                            min={0}
                            max={100}
                            step={1}
                            value={Math.round(activeSoundEffect.volume * 100)}
                            onChange={event =>
                              updateSoundEffect(activeSoundEffect.id, {
                                volume: Number(event.target.value) / 100,
                              })
                            }
                          />
                        </label>

                        <div className="inspector-dual-range">
                          <label className="inspector-range">
                            <span>
                              <b>Fade in</b>
                              <strong>{Number(activeSoundEffect.fadeIn || 0).toFixed(2)}s</strong>
                            </span>
                            <input
                              data-testid="sound-effect-fade-in"
                              type="range"
                              min={0}
                              max={Math.min(2, getSoundEffectDuration(activeSoundEffect))}
                              step={0.01}
                              value={Number(activeSoundEffect.fadeIn || 0)}
                              onChange={event =>
                                updateSoundEffect(activeSoundEffect.id, {
                                  fadeIn: Number(event.target.value),
                                })
                              }
                            />
                          </label>
                          <label className="inspector-range">
                            <span>
                              <b>Fade out</b>
                              <strong>{Number(activeSoundEffect.fadeOut || 0).toFixed(2)}s</strong>
                            </span>
                            <input
                              data-testid="sound-effect-fade-out"
                              type="range"
                              min={0}
                              max={Math.min(2, getSoundEffectDuration(activeSoundEffect))}
                              step={0.01}
                              value={Number(activeSoundEffect.fadeOut || 0)}
                              onChange={event =>
                                updateSoundEffect(activeSoundEffect.id, {
                                  fadeOut: Number(event.target.value),
                                })
                              }
                            />
                          </label>
                        </div>

                        <div className="inspector-inline-actions">
                          <button
                            type="button"
                            data-testid="sound-effect-preview"
                            onClick={() => void toggleSoundEffectPreview(activeSoundEffect)}
                          >
                            {previewingSoundEffectId === activeSoundEffect.id
                              ? "Stop SFX"
                              : "Preview SFX"}
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              seekLiveEditTimelineItem(
                                Number(activeSoundEffect.startTime || 0),
                                "sound"
                              );
                              setComparisonMode("after");
                            }}
                          >
                            Go to cue
                          </button>
                          <button
                            type="button"
                            data-testid="sound-effect-remove"
                            onClick={() => removeSoundEffect(activeSoundEffect.id)}
                          >
                            Remove SFX
                          </button>
                          <button type="button" data-testid="sound-effect-duplicate"
                            onClick={() => duplicateSoundEffect(activeSoundEffect)}>
                            Duplicate SFX
                          </button>
                        </div>
                      </div>
                    ) : null}
                  </section>

                  <label className="inspector-check-row">
                    <input
                      type="checkbox"
                      checked={!muteOriginalAudio}
                      onChange={event => setMuteOriginalAudio(!event.target.checked)}
                    />
                    <span>
                      <strong>Keep original audio</strong>
                      <small>Voice and room sound remain present outside cutaway rules.</small>
                    </span>
                  </label>

                  <div className="sound-flow-summary">
                    <span>Source voice</span>
                    <i>→</i>
                    <span>B-roll rules</span>
                    <i>→</i>
                    <span>SFX hits</span>
                    <i>→</i>
                    <span>Music ducking</span>
                  </div>
                </div>
              ) : null}

              <div className="clip-inspector-feedback" role="status">
                <span>✓</span>
                <p>{studioActionMessage}</p>
              </div>
            </section>
            {/* ── B-Roll / Overlay Timeline Track ── */}
            <section className="studio-panel broll-timeline-panel">
              <div className="panel-heading compact">
                <div>
                  <span className="panel-kicker">B-Roll</span>
                  <h4>Main · Hook · B-Roll</h4>
                  <p className="panel-description">
                    Upload clips to place real cutaways, or add a placeholder beat while planning.
                  </p>
                </div>
              </div>
              <div className="broll-sound-panel">
                <div className="broll-sound-panel-header">
                  <div>
                    <span className="panel-kicker">Background Sound</span>
                    <strong>
                      {addMusic && musicTrack
                        ? musicTrack.name || "Custom sound"
                        : "No sound uploaded"}
                    </strong>
                  </div>
                  {addMusic && musicTrack ? (
                    <span className="broll-sound-state">
                      {isBackgroundSoundPreviewing ? "Playing" : "Ready"}
                    </span>
                  ) : null}
                </div>
                <input
                  id="studio-background-sound-input"
                  type="file"
                  ref={quickMusicFileInputRef}
                  data-testid="background-sound-input"
                  accept="audio/*,.mp3,.wav,.ogg,.m4a,.aac"
                  className="visually-hidden-file-input"
                  onChange={handleMusicFileUpload}
                />
                <div className="broll-sound-actions">
                  <label
                    htmlFor="studio-background-sound-input"
                    role="button"
                    tabIndex={0}
                    className="clip-action-btn broll-sound-btn"
                    onKeyDown={event => {
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        quickMusicFileInputRef.current?.click();
                      }
                    }}
                  >
                    {addMusic && musicTrack ? "Change Background Sound" : "Upload Background Sound"}
                  </label>
                  {addMusic && musicTrack ? (
                    <>
                      <button
                        type="button"
                        className="clip-action-btn broll-sound-active-btn"
                        onClick={toggleBackgroundSoundPreview}
                        title="Play or pause background sound preview"
                      >
                        {isBackgroundSoundPreviewing ? "Stop Sound" : "Preview Sound"}
                      </button>
                      <button
                        type="button"
                        className="clip-action-btn broll-sound-active-btn"
                        onClick={removeMusic}
                        title="Remove background sound"
                      >
                        Remove Sound
                      </button>
                    </>
                  ) : null}
                </div>
                <SafeAudio
                  ref={musicPreviewRef}
                  data-testid="background-sound-preview"
                  className={`broll-sound-player ${addMusic && effectiveMusicPreviewUrl ? "" : "empty"}`}
                  controls={addMusic && !!effectiveMusicPreviewUrl}
                  preload="auto"
                  src={addMusic ? effectiveMusicPreviewUrl : undefined}
                />
                {soundEffects
                  .filter(effect => !effect.builtIn && effect.url)
                  .map(effect => (
                    <SafeAudio
                      key={effect.id}
                      ref={element => {
                        if (element) soundEffectAudioRefsRef.current.set(effect.id, element);
                        else soundEffectAudioRefsRef.current.delete(effect.id);
                      }}
                      data-testid={`sound-effect-audio-${effect.id}`}
                      preload="auto"
                      src={effect.url}
                      style={{ display: "none" }}
                      onLoadedMetadata={event => {
                        const availableDuration = Math.max(
                          0.05,
                          Number(event.currentTarget.duration || 0) - Number(effect.trimStart || 0)
                        );
                        if (Number.isFinite(availableDuration) && availableDuration > 0 &&
                            availableDuration < getSoundEffectDuration(effect)) {
                          updateSoundEffect(effect.id, {
                            duration: Math.min(getSoundEffectDuration(effect), availableDuration),
                          });
                        }
                      }}
                      onEnded={() =>
                        setPreviewingSoundEffectId(current =>
                          current === effect.id ? null : current
                        )
                      }
                    />
                  ))}
                {addMusic && musicPreviewStatusMessage ? (
                  <p className="broll-sound-message">{musicPreviewStatusMessage}</p>
                ) : null}
              </div>
              <div className="broll-timeline-tracks">
                {/* Track: Main Video */}
                <div className="broll-track-row">
                  <span className="broll-track-label">🎬 Main</span>
                  <div className="broll-track-bar">
                    <div
                      className="broll-main-block"
                      style={{ width: "100%" }}
                      title="Main A-roll video"
                    />
                  </div>
                </div>

                {/* Track: Captions */}
                <div className="broll-track-row">
                  <span className="broll-track-label">💬 Captions</span>
                  <div className="broll-track-bar">
                    <div
                      className="broll-captions-block"
                      style={{ width: "100%" }}
                      title="Auto-captions track"
                    />
                  </div>
                </div>

                {/* Track: B-Roll Overlays */}
                <div className="broll-track-row">
                  <span className="broll-track-label">🎞️ B-Roll</span>
                  <div className="broll-track-bar" style={{ position: "relative" }}>
                    {/* Playhead */}
                    <div
                      className="broll-playhead"
                      style={{
                        left: liveTimelineDuration
                          ? `${Math.min(100, (previewTimelineTime / liveTimelineDuration) * 100)}%`
                          : "0%",
                      }}
                    />
                    {/* Overlay blocks */}
                    {overlays
                      .filter(o => o.startTime !== undefined && o.duration !== undefined)
                      .map(o => {
                        const leftPct = liveTimelineDuration
                          ? (Number(o.startTime) / liveTimelineDuration) * 100
                          : 0;
                        const widthPct = liveTimelineDuration
                          ? (Number(o.duration) / liveTimelineDuration) * 100
                          : 0;
                        const isActive = activeOverlayId === o.id;
                        const label =
                          o.type === "text"
                            ? (o.text || "").slice(0, 8) || "Text"
                            : o.type === "video"
                              ? "🎥"
                              : "🖼️";
                        return (
                          <div
                            key={o.id}
                            className={`broll-overlay-block ${isActive ? "active" : ""} ${o.bRollMode === "fullscreen" ? "fullscreen" : ""}`}
                            style={{
                              left: `${leftPct}%`,
                              width: `${Math.max(1, widthPct)}%`,
                              opacity: o.opacity !== undefined ? o.opacity : 1,
                            }}
                            onClick={() => setActiveOverlayId(o.id)}
                            title={`${label} — ${Number(o.startTime).toFixed(1)}s → ${(Number(o.startTime) + Number(o.duration)).toFixed(1)}s${o.bRollMode ? ` (${o.bRollMode})` : ""}`}
                          >
                            <span className="broll-overlay-block-label">{label}</span>
                          </div>
                        );
                      })}
                    {/* Empty state */}
                    {overlays.filter(o => o.startTime !== undefined && o.duration !== undefined)
                      .length === 0 && (
                      <div className="broll-empty-hint">
                        Add a B-roll beat or upload a B-roll video
                      </div>
                    )}
                  </div>
                </div>

                {/* Track: Audio */}
                <div className="broll-track-row">
                  <span className="broll-track-label">🔊 Audio</span>
                  <div className="broll-track-bar" style={{ position: "relative" }}>
                    {/* Main audio */}
                    <div
                      className="broll-audio-block"
                      style={{
                        width: muteOriginalAudio ? "0%" : "100%",
                        opacity: muteOriginalAudio ? 0.25 : 0.55,
                        height: addMusic ? "45%" : "100%",
                        top: addMusic ? 0 : undefined,
                      }}
                      title={muteOriginalAudio ? "Main audio muted" : "Main audio active"}
                    />
                    {/* Music track overlay */}
                    {addMusic && (
                      <div
                        className="broll-audio-block broll-music-block"
                        style={{
                          width: "100%",
                          height: "55%",
                          bottom: 0,
                          opacity: 0.7,
                          background: "linear-gradient(135deg, #a78bfa, #7c3aed)",
                        }}
                        title={`Music: ${musicTrack?.name || "Active"} · Vol: ${Math.round((musicTrack?.volume ?? musicVolume) * 100)}% · Ducking: ${musicTrack?.ducking ? (musicTrack.duckingMode === "speech" ? "Speech-aware" : "Constant") : "Off"}`}
                      />
                    )}
                    {/* Music fade indicators */}
                    {addMusic && musicTrack && (
                      <>
                        <div
                          style={{
                            position: "absolute",
                            left: 0,
                            top: 0,
                            bottom: 0,
                            width: `${Math.min(100, ((musicTrack.fadeIn || 0.5) / liveTimelineDuration) * 100)}%`,
                            background:
                              "linear-gradient(90deg, rgba(255,255,255,0.15), transparent)",
                            pointerEvents: "none",
                            zIndex: 2,
                          }}
                          title={`Fade In: ${musicTrack.fadeIn}s`}
                        />
                        <div
                          style={{
                            position: "absolute",
                            right: 0,
                            top: 0,
                            bottom: 0,
                            width: `${Math.min(100, ((musicTrack.fadeOut || 0.5) / liveTimelineDuration) * 100)}%`,
                            background:
                              "linear-gradient(270deg, rgba(255,255,255,0.15), transparent)",
                            pointerEvents: "none",
                            zIndex: 2,
                          }}
                          title={`Fade Out: ${musicTrack.fadeOut}s`}
                        />
                      </>
                    )}
                    {soundEffects.map(effect => {
                      const left = liveTimelineDuration
                        ? (Number(effect.startTime || 0) / liveTimelineDuration) * 100
                        : 0;
                      const width = liveTimelineDuration
                        ? (getSoundEffectDuration(effect) / liveTimelineDuration) * 100
                        : 0;
                      return (
                        <button
                          key={`broll-sfx-${effect.id}`}
                          type="button"
                          className={`broll-sfx-block ${activeSoundEffectId === effect.id ? "is-active" : ""}`}
                          style={{ left: `${left}%`, width: `${Math.max(1.8, width)}%` }}
                          onClick={() => {
                            setActiveSoundEffectId(effect.id);
                            seekLiveEditTimelineItem(Number(effect.startTime || 0), "sound");
                          }}
                          title={`${effect.name} sound effect at ${formatPreviewTimePrecise(effect.startTime)}`}
                        >
                          {effect.emoji || "🔊"}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
              {/* Quick actions */}
              <div className="broll-timeline-actions">
                <button
                  type="button"
                  className="clip-action-btn"
                  onClick={() => {
                    const suggestions = suggestBRollMoments();
                    if (suggestions.length === 0) {
                      toast("Clip too short for B-roll suggestions.");
                      return;
                    }
                    addBRollSuggestionOverlay(suggestions[0]);
                  }}
                >
                  Add Placeholder Beat
                </button>
                <button
                  type="button"
                  className="clip-action-btn"
                  onClick={() => {
                    const currentVideoTime = videoRef.current ? videoRef.current.currentTime : 0;
                    let relativeStartTime = currentVideoTime;
                    if (selectedClip && activeTimelineIndex === 0) {
                      relativeStartTime = Math.max(0, currentVideoTime - (selectedClip.start || 0));
                    }
                    const newOverlay = buildBRollOverlay({
                      ...getBRollSuggestionCopy(getBRollStyleForClip()),
                      style: getBRollStyleForClip(),
                      time: relativeStartTime,
                      duration: 1.8,
                    });
                    setOverlays(prev => [...prev, newOverlay]);
                    setActiveOverlayId(newOverlay.id);
                    toast.success("B-roll beat added at playhead.");
                  }}
                >
                  Add Beat at Playhead
                </button>
                <input
                  type="file"
                  ref={brollVideoInputRef}
                  data-testid="broll-video-input"
                  accept="video/*"
                  multiple
                  style={{ display: "none" }}
                  onChange={handleBRollVideoUpload}
                />
                <button
                  type="button"
                  className="clip-action-btn broll-upload-btn"
                  onClick={() => brollVideoInputRef.current?.click()}
                >
                  📹 Upload B-Roll Clips
                </button>
              </div>

              {(() => {
                const brollVideoOverlays = overlays.filter(
                  overlay => overlay.type === "video" && overlay.bRollMode
                );
                if (!brollVideoOverlays.length) return null;
                return (
                  <div className="broll-clip-library">
                    <div className="broll-clip-library-header">
                      <span>Uploaded B-roll clips</span>
                      <strong>{brollVideoOverlays.length}</strong>
                    </div>
                    <div className="broll-clip-grid">
                      {brollVideoOverlays.map((overlay, index) => {
                        const safeSrc = getSafeMediaSource(overlay.src);
                        const isActive = activeOverlayId === overlay.id;
                        const label =
                          overlay.file?.name || overlay.name || `B-roll clip ${index + 1}`;
                        return (
                          <div
                            key={overlay.id}
                            className={`broll-clip-card ${isActive ? "active" : ""}`}
                            onClick={() => selectBRollOverlay(overlay)}
                          >
                            <div className="broll-clip-preview">
                              {safeSrc ? (
                                <video
                                  ref={element => {
                                    applySafeMediaSource(element, safeSrc);
                                  }}
                                  controls
                                  muted
                                  playsInline
                                  preload="metadata"
                                />
                              ) : (
                                <span>No preview</span>
                              )}
                            </div>
                            <div className="broll-clip-meta">
                              <strong title={label}>{label}</strong>
                              <span>
                                {Number(overlay.startTime || 0).toFixed(1)}s ·{" "}
                                {Number(overlay.duration || 0).toFixed(1)}s
                              </span>
                            </div>
                            <div className="broll-clip-actions">
                              <button
                                type="button"
                                className="mini-toggle-btn"
                                onClick={event => {
                                  event.stopPropagation();
                                  selectBRollOverlay(overlay);
                                }}
                              >
                                Select
                              </button>
                              <button
                                type="button"
                                className="mini-toggle-btn"
                                onClick={event => {
                                  event.stopPropagation();
                                  deleteOverlay(overlay.id);
                                }}
                              >
                                Remove
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })()}

              {/* B-roll suggestions list */}
              {(() => {
                const suggestions = suggestBRollMoments();
                if (suggestions.length === 0) return null;
                return (
                  <div className="broll-suggestions">
                    <span className="broll-suggestions-title">
                      Story-matched moving footage · showing {Math.min(8, suggestions.length)} of{" "}
                      {suggestions.length}
                    </span>
                    {storyVisualSearchMessage ? (
                      <span
                        className={`story-visual-search-status is-${storyVisualSearchStatus}`}
                        role="status"
                      >
                        {storyVisualSearchMessage}
                      </span>
                    ) : null}
                    {suggestions.slice(0, 8).map((s, i) => {
                      const result = storyVisualCandidates[String(s.id)];
                      const candidates = Array.isArray(result?.candidates) ? result.candidates : [];
                      return (
                        <div
                          key={s.id || i}
                          className={`broll-suggestion-chip ${s.reviewRequired ? "needs-review" : ""}`}
                          data-testid={`story-beat-${s.id || i}`}
                        >
                          <strong>{s.time.toFixed(1)}s</strong> — {s.title}
                          <span className="broll-suggestion-reason">{s.reason}</span>
                          <span className="broll-suggestion-evidence">
                            Spoken: “{s.evidenceQuote}”
                          </span>
                          <span className="broll-suggestion-query">Search: {s.searchQuery}</span>
                          <span className="broll-suggestion-confidence">
                            {s.reviewRequired
                              ? "Transcript review required before footage can be approved"
                              : `${Math.round(Number(s.confidence || 0) * 100)}% grounded in the spoken story`}
                          </span>
                          {!s.reviewRequired && !result ? (
                            <button
                              type="button"
                              className="story-visual-find-button"
                              disabled={storyVisualSearchStatus === "processing"}
                              onClick={() => searchLicensedStoryVisuals([s])}
                            >
                              {storyVisualSearchStatus === "processing"
                                ? "Finding moving footage…"
                                : "Find moving footage"}
                            </button>
                          ) : null}
                          {candidates.length ? (
                            <div className="story-visual-candidates">
                              {candidates.map((candidate, candidateIndex) => (
                                <article
                                  key={candidate.id || candidateIndex}
                                  className="story-visual-candidate"
                                >
                                  <SafeVideo
                                    src={candidate.video_url}
                                    poster={candidate.preview_image || undefined}
                                    muted
                                    playsInline
                                    loop
                                    controls
                                    preload="metadata"
                                  />
                                  <div>
                                    <strong>
                                      Choice {candidateIndex + 1} · {candidate.creator}
                                    </strong>
                                    <span>
                                      {candidate.width}×{candidate.height} · licensed moving footage
                                    </span>
                                  </div>
                                  <button
                                    type="button"
                                    data-testid={`approve-story-visual-${s.id}-${candidateIndex}`}
                                    onClick={() => approveLicensedStoryVisual(s, candidate)}
                                  >
                                    {candidateIndex === 0
                                      ? "Approve this footage"
                                      : "Use this instead"}
                                  </button>
                                </article>
                              ))}
                            </div>
                          ) : null}
                          {result?.status === "no_match" ? (
                            <span className="story-visual-no-match">
                              No honest match found. Upload your own moving footage for this beat.
                            </span>
                          ) : null}
                        </div>
                      );
                    })}
                  </div>
                );
              })()}
            </section>

            <section className="studio-panel workflow-summary-panel">
              <div className="panel-heading compact">
                <div>
                  <span className="panel-kicker">Workflow</span>
                  <h4>One pass to publish</h4>
                </div>
              </div>
              <div className="workflow-summary-grid">
                <div className="workflow-summary-item active">
                  <strong>1. Choose the moment</strong>
                  <span>Start with the clip most likely to stop the scroll.</span>
                </div>
                <div className="workflow-summary-item">
                  <strong>2. Sharpen the frame</strong>
                  <span>Add only the framing, text, and sound that make the promise clearer.</span>
                </div>
                <div className="workflow-summary-item">
                  <strong>3. Render for publish</strong>
                  <span>Render your edit, then review the actual picture and sound before downloading.</span>
                </div>
              </div>
            </section>

            {campaignSet.length ? (
              <section className="studio-panel">
                <div className="panel-heading compact">
                  <div>
                    <span className="panel-kicker">Campaign Set</span>
                    <h4>Multiple angles from one source</h4>
                    <p className="panel-description">
                      Use different strategic cuts instead of betting everything on one export.
                    </p>
                  </div>
                </div>
                <div className="campaign-set-grid">
                  {campaignSet.map(item => (
                    <button
                      key={item.id}
                      type="button"
                      className="campaign-set-card"
                      onClick={() => handleClipAction(item.entry.clip, { type: "use" })}
                    >
                      <span className="campaign-set-label">{item.label}</span>
                      <strong>{item.entry.momentFamilyLabel}</strong>
                      <span className="campaign-set-summary">{item.summary}</span>
                      <span className="campaign-set-score">Viral Score {item.entry.score}</span>
                    </button>
                  ))}
                </div>
              </section>
            ) : null}

            {momentFamilies.length ? (
              <section className="studio-panel">
                <div className="panel-heading compact">
                  <div>
                    <span className="panel-kicker">Moment Families</span>
                    <h4>Clusters, not duplicates</h4>
                    <p className="panel-description">
                      Similar highlights are grouped so the best angle wins without flooding the
                      list.
                    </p>
                  </div>
                </div>
                <div className="family-cluster-list">
                  {momentFamilies.slice(0, 4).map(family => (
                    <button
                      key={family.id}
                      type="button"
                      className={`family-cluster-card ${
                        selectedMomentFamily?.id === family.id ? "active" : ""
                      }`}
                      onClick={() => handleClipAction(family.topEntry.clip, { type: "use" })}
                    >
                      <div className="family-cluster-topline">
                        <span className="family-cluster-label">{family.label}</span>
                        <span className="family-cluster-size">
                          {family.members.length} angle{family.members.length === 1 ? "" : "s"}
                        </span>
                      </div>
                      <strong>{family.headline}</strong>
                      <span className="family-cluster-summary">{family.summary}</span>
                    </button>
                  ))}
                </div>
              </section>
            ) : null}

            <section className="studio-panel clips-list">
              <div className="panel-heading compact">
                <div>
                  <span className="panel-kicker">Hook source</span>
                  <h4>Choose the opening moment</h4>
                </div>
              </div>
              {selectedClip && selectedClipGuidance ? (
                <div
                  className={`clip-guidance-card ${selectedClip.id === bestClipId ? "is-best" : ""}`}
                  data-testid="selected-clip-guidance"
                >
                  <div className="clip-guidance-head">
                    <div>
                      <span className="clip-guidance-kicker">
                        {selectedClip.id === bestClipId
                          ? "BEST CLIP"
                          : topPickIds.has(selectedClip.id)
                            ? "TOP PICK"
                            : "Selected clip"}
                      </span>
                      <h5>
                        Hook Score: {selectedClipGuidance.score}
                        <span className="clip-guidance-score-fire">🔥</span>
                      </h5>
                    </div>
                    <div className="clip-guidance-timing-pill">
                      #
                      {rankedClipGuidance.findIndex(entry => entry.clip.id === selectedClip.id) + 1}
                    </div>
                  </div>

                  <p className="clip-guidance-summary">
                    {selectedClip.id === bestClipId
                      ? "This has the highest viral potential right now."
                      : "This moment is ready for hook tuning and export."}
                  </p>

                  <div className="clip-guidance-timing">
                    <span>Start: {Number(selectedClip.start || 0).toFixed(1)}s</span>
                    <span>End: {Number(selectedClip.end || 0).toFixed(1)}s</span>
                    <span>{selectedClipGuidance.duration.toFixed(1)}s</span>
                  </div>

                  <div className="clip-guidance-tag-row">
                    <span className="clip-tag-pill compact clip-tag-pill-family">
                      Family: {selectedClipGuidance.momentFamilyLabel}
                    </span>
                    {selectedClipGuidance.bestFor.map((fit, index) => (
                      <span
                        key={`${selectedClip.id}-fit-${fit}-${index}`}
                        className="clip-tag-pill compact"
                      >
                        {fit}
                      </span>
                    ))}
                    <span className="clip-tag-pill compact">
                      Export fit: {selectedClipGuidance.exportFit}
                    </span>
                  </div>

                  <div className="clip-guidance-tag-row">
                    {selectedClipGuidance.categories.map((category, index) => (
                      <span
                        key={`${selectedClip.id}-${category.label}-${index}`}
                        className="clip-tag-pill"
                      >
                        {category.icon} {category.label}
                      </span>
                    ))}
                  </div>

                  <div className="clip-guidance-packaging">
                    <strong>Packaging move</strong>
                    <p>{selectedClipGuidance.openingMove}</p>
                    <div className="clip-guidance-storyline">
                      <span className="clip-guidance-storyline-label">Why this can travel</span>
                      <p>{selectedClipGuidance.travelReason}</p>
                    </div>
                    <div className="clip-guidance-scoreboard">
                      {selectedClipGuidance.scoreBreakdown.slice(0, 6).map(entry => (
                        <div
                          key={`${selectedClip.id}-${entry.label}`}
                          className="clip-guidance-score-item"
                        >
                          <span>{entry.label}</span>
                          <div className="clip-guidance-score-track">
                            <span
                              className="clip-guidance-score-fill"
                              style={{ width: `${entry.value}%` }}
                            />
                          </div>
                          <strong>{entry.value}</strong>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="clip-guidance-variants">
                    <strong>Alternate recuts</strong>
                    <div className="clip-guidance-variant-grid">
                      {selectedClipGuidance.recutVariants.map(variant => (
                        <button
                          key={`${selectedClip.id}-${variant.id}`}
                          type="button"
                          className="clip-guidance-variant-card"
                          onClick={() =>
                            handleClipAction(selectedClip, {
                              type: "apply-variant",
                              variant,
                            })
                          }
                        >
                          <span className="clip-guidance-variant-label">{variant.label}</span>
                          <span className="clip-guidance-variant-summary">{variant.summary}</span>
                          <span className="clip-guidance-variant-range">
                            {variant.start.toFixed(1)}s - {variant.end.toFixed(1)}s
                          </span>
                        </button>
                      ))}
                    </div>
                  </div>

                  {familySiblingEntries.length ? (
                    <div className="clip-guidance-family">
                      <strong>Sibling angles in this family</strong>
                      <div className="clip-guidance-family-grid">
                        {familySiblingEntries.map(entry => (
                          <button
                            key={`${selectedClip.id}-family-${entry.clip.id}`}
                            type="button"
                            className="clip-guidance-family-card"
                            onClick={() => handleClipAction(entry.clip, { type: "use" })}
                          >
                            <span className="clip-guidance-family-label">
                              {entry.narrativeRole.label}
                            </span>
                            <span className="clip-guidance-family-summary">
                              {entry.openingMove}
                            </span>
                            <span className="clip-guidance-family-score">Score {entry.score}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  ) : null}

                  <div className="clip-guidance-actions compact">
                    <button
                      type="button"
                      className="clip-action-btn"
                      onClick={() => handleClipAction(selectedClip, { type: "use" })}
                    >
                      Use in Editor
                    </button>
                    <button
                      type="button"
                      className="clip-action-btn clip-action-btn-primary"
                      onClick={() => handleClipAction(selectedClip, { type: "apply-hook" })}
                    >
                      Apply Hook
                    </button>
                  </div>

                  <div className="clip-jump-row">
                    <button
                      type="button"
                      className="clip-jump-btn"
                      onClick={() => jumpToSourceTime(Number(selectedClip.start || 0))}
                    >
                      Jump to start
                    </button>
                    <button
                      type="button"
                      className="clip-jump-btn"
                      onClick={() => jumpToSourceTime(Number(selectedClip.end || 0))}
                    >
                      Jump to end
                    </button>
                  </div>

                  <div className="clip-guidance-reasons">
                    <strong>Why this clip</strong>
                    {selectedClipGuidance.reasons.slice(0, 4).map((reason, index) => (
                      <div
                        key={`${selectedClip.id}-${reason}-${index}`}
                        className="clip-guidance-reason"
                      >
                        ✔ {reason}
                      </div>
                    ))}
                  </div>

                  {selectedClipGuidance.score < 60 ? (
                    <div className="clip-fix-card">
                      <strong>This clip can perform better</strong>
                      {selectedClipGuidance.improvements.map((item, index) => (
                        <div key={`${selectedClip.id}-${item}-${index}`} className="clip-fix-item">
                          • {item}
                        </div>
                      ))}
                      <button
                        type="button"
                        className="clip-action-btn clip-action-btn-primary"
                        onClick={() => handleClipAction(selectedClip, { type: "improve" })}
                      >
                        Improve Clip
                      </button>
                    </div>
                  ) : null}
                </div>
              ) : null}
              <div className="clips-scroller">
                {orderedClips.map((clip, idx) => {
                  const clipGuidance = clipGuidanceById.get(clip.id);
                  const isBestClip = clip.id === bestClipId;
                  const isTopPick = topPickIds.has(clip.id);

                  return (
                    <div
                      key={clip.id}
                      data-testid={`detected-clip-${clip.id}`}
                      className={`clip-card ${selectedClip && selectedClip.id === clip.id ? "active" : ""} ${isBestClip ? "top-pick" : ""} ${isTopPick && !isBestClip ? "runner-up" : ""}`}
                      draggable={orderedClips.length > 1}
                      onDragStart={() => setDraggedDetectedClipId(clip.id)}
                      onDragEnd={() => setDraggedDetectedClipId(null)}
                      onDragOver={e => {
                        e.preventDefault();
                      }}
                      onDrop={e => {
                        e.preventDefault();
                        if (draggedDetectedClipId === null || draggedDetectedClipId === clip.id)
                          return;
                        moveDetectedClipToIndex(draggedDetectedClipId, idx);
                        setDraggedDetectedClipId(null);
                      }}
                      onClick={() => focusClipInEditor(clip, { boundary: "start", play: false })}
                      style={
                        draggedDetectedClipId === clip.id
                          ? { borderStyle: "dashed", borderColor: "#e52e71" }
                          : undefined
                      }
                    >
                      <div className="clip-card-main">
                        <div className="clip-card-header-row">
                          <div className="clip-card-title-group">
                            <span className="clip-badge">#{idx + 1}</span>
                            {isBestClip ? (
                              <span className="clip-priority-badge best">BEST CLIP</span>
                            ) : null}
                            {!isBestClip && isTopPick ? (
                              <span className="clip-priority-badge">TOP PICK</span>
                            ) : null}
                          </div>
                          <div className="clip-score-stack">
                            <span className="clip-score-label">Viral Score</span>
                            <strong>🔥 {clipGuidance?.score ?? 0}</strong>
                          </div>
                        </div>

                        <div className="clip-timing-row">
                          <span>Start {Number(clip.start || 0).toFixed(1)}s</span>
                          <span>End {Number(clip.end || 0).toFixed(1)}s</span>
                          <span>
                            {(clipGuidance?.duration ?? getClipDurationSeconds(clip)).toFixed(1)}s
                          </span>
                        </div>

                        <p>{normalizePlainText(clip.reason || "Primary detected moment")}</p>

                        <div className="clip-tag-row">
                          {clipGuidance?.momentFamilyLabel ? (
                            <span className="clip-tag-pill compact clip-tag-pill-family">
                              {clipGuidance.momentFamilyLabel}
                            </span>
                          ) : null}
                          {(clipGuidance?.bestFor || []).map((fit, index) => (
                            <span
                              key={`${clip.id}-best-for-${fit}-${index}`}
                              className="clip-tag-pill compact"
                            >
                              {fit}
                            </span>
                          ))}
                          {(clipGuidance?.categories || []).map((category, index) => (
                            <span
                              key={`${clip.id}-${category.label}-${index}`}
                              className="clip-tag-pill compact"
                            >
                              {category.icon} {category.label}
                            </span>
                          ))}
                        </div>

                        <div className="clip-guidance-mini-list">
                          {clipGuidance?.travelReason ? (
                            <div className="clip-guidance-mini-item emphasis">
                              ★ {clipGuidance.travelReason}
                            </div>
                          ) : null}
                          {clipGuidance?.openingMove ? (
                            <div className="clip-guidance-mini-item">
                              ↳ {clipGuidance.openingMove}
                            </div>
                          ) : null}
                          {(clipGuidance?.reasons || []).slice(0, 3).map((reason, index) => (
                            <div
                              key={`${clip.id}-${reason}-${index}`}
                              className="clip-guidance-mini-item"
                            >
                              ✔ {reason}
                            </div>
                          ))}
                        </div>

                        <div className="clip-guidance-actions">
                          <button
                            type="button"
                            className="clip-action-btn"
                            data-testid={`clip-action-use-${clip.id}`}
                            onClick={e => {
                              e.stopPropagation();
                              handleClipAction(clip, { type: "use" });
                            }}
                          >
                            Use in Editor
                          </button>
                          <button
                            type="button"
                            className="clip-action-btn clip-action-btn-primary"
                            data-testid={`clip-action-hook-${clip.id}`}
                            onClick={e => {
                              e.stopPropagation();
                              handleClipAction(clip, { type: "apply-hook" });
                            }}
                          >
                            Apply Hook
                          </button>
                          {clipGuidance?.recutVariants?.[0] ? (
                            <button
                              type="button"
                              className="clip-action-btn"
                              onClick={e => {
                                e.stopPropagation();
                                handleClipAction(clip, {
                                  type: "apply-variant",
                                  variant: clipGuidance.recutVariants[0],
                                });
                              }}
                            >
                              {clipGuidance.recutVariants[0].label}
                            </button>
                          ) : null}
                        </div>

                        {clipGuidance && clipGuidance.score < 60 ? (
                          <div className="clip-card-warning">
                            <strong>This clip can perform better</strong>
                            <button
                              type="button"
                              className="clip-action-btn clip-action-btn-primary"
                              data-testid={`clip-action-improve-${clip.id}`}
                              onClick={e => {
                                e.stopPropagation();
                                handleClipAction(clip, { type: "improve" });
                              }}
                            >
                              Improve Clip
                            </button>
                          </div>
                        ) : null}
                      </div>
                      {orderedClips.length > 1 && (
                        <div style={{ display: "flex", gap: "4px", marginLeft: "8px" }}>
                          <button
                            type="button"
                            className="resize-btn"
                            title="Move moment earlier"
                            data-testid={`detected-move-left-${clip.id}`}
                            onClick={e => {
                              e.stopPropagation();
                              moveDetectedClip(clip.id, "backward");
                            }}
                            disabled={idx === 0}
                          >
                            ←
                          </button>
                          <button
                            type="button"
                            className="resize-btn"
                            title="Move moment later"
                            data-testid={`detected-move-right-${clip.id}`}
                            onClick={e => {
                              e.stopPropagation();
                              moveDetectedClip(clip.id, "forward");
                            }}
                            disabled={idx === orderedClips.length - 1}
                          >
                            →
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </section>

            <section className="studio-panel editing-tools-panel">
              <div className="panel-heading compact">
                <div>
                  <span className="panel-kicker">Build</span>
                  <h4>Legacy tools</h4>
                  <p className="panel-description">
                    Keep the frame clean. Add only what earns attention or clarity.
                  </p>
                </div>
              </div>
              <div className="editing-tools">
                <button className="tool-btn" onClick={() => addTextOverlay("headline")}>
                  <span>📝</span> Add Text
                </button>
                <button
                  className="tool-btn"
                  onClick={() => document.getElementById("video-upload-input").click()}
                >
                  <span>📹</span> Add Video
                </button>
                <button className="tool-btn" onClick={() => imageInputRef.current?.click()}>
                  <span>🖼️</span> Add Image
                </button>
                <button
                  className="tool-btn"
                  onClick={() => setVideoFit(prev => (prev === "contain" ? "cover" : "contain"))}
                  disabled={safeFaceFraming}
                  title={
                    safeFaceFraming
                      ? "Disable Safe Face Framing to switch to Fill mode"
                      : "Switch between Full frame and Fill frame"
                  }
                >
                  <span>📐</span> Fit: {videoFit === "contain" ? "FULL" : "FILL"}
                </button>
                <button
                  className={`tool-btn ${safeFaceFraming ? "active" : ""}`}
                  onClick={() => setSafeFaceFraming(prev => !prev)}
                  title="Prevent stretched/cropped faces by preserving aspect ratio"
                >
                  <span>🧠</span> Safe Face Framing: {safeFaceFraming ? "ON" : "OFF"}
                </button>
                <button
                  className="tool-btn"
                  onClick={() =>
                    setFaceAnchorPreset(prev =>
                      prev === "center" ? "face_top" : prev === "face_top" ? "face_mid" : "center"
                    )
                  }
                  title="Choose a face-safe vertical anchor"
                >
                  <span>🎯</span> Anchor:{" "}
                  {faceAnchorPreset === "center"
                    ? "Center"
                    : faceAnchorPreset === "face_top"
                      ? "Face Top"
                      : "Face Mid"}
                </button>
              </div>
              <input
                id="video-upload-input"
                type="file"
                accept="video/*"
                style={{ display: "none" }}
                onChange={addVideoLayer}
              />
              <input
                ref={imageInputRef}
                data-testid="visual-image-input"
                type="file"
                accept="image/*"
                style={{ display: "none" }}
                onChange={addImageLayer}
              />
              {images.length > 0 && (
                <div className="asset-library-card">
                  <h5>Image library</h5>
                  <div className="asset-library-grid">
                    {images.slice(0, 6).map((imageAsset, index) => {
                      const imageSrc = normalizeAssetUrl(imageAsset);
                      if (!imageSrc) return null;
                      return (
                        <button
                          key={imageAsset.id || imageSrc || index}
                          type="button"
                          onClick={() => addExistingImageOverlay(imageAsset)}
                          style={{
                            width: "58px",
                            height: "58px",
                            padding: 0,
                            borderRadius: "8px",
                            border: "1px solid #d0d0d0",
                            overflow: "hidden",
                            cursor: "pointer",
                            background: "#fff",
                          }}
                          title="Add image overlay"
                        >
                          <img
                            src={getSafeMediaSource(imageSrc)}
                            alt="Overlay option"
                            style={{ width: "100%", height: "100%", objectFit: "cover" }}
                          />
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </section>

            <section className="studio-panel automation-panel">
              <div className="panel-heading compact">
                <div>
                  <span className="panel-kicker">Hook</span>
                  <h4>Opening hook builder</h4>
                  <p className="panel-description">
                    Choose the exact opening moment, write the hook text, and tune how it lands
                    before the B-roll takes over.
                  </p>
                </div>
              </div>
              <div className="ai-settings-card hook-settings-card">
                <h5
                  style={{
                    ...sidebarSectionTitleStyle,
                    cursor: "pointer",
                    userSelect: "none",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                  onClick={() => toggleSection("aiEnhancements")}
                >
                  <span>🤖 AI Enhancements</span>
                  <span style={{ fontSize: "12px", opacity: 0.6 }}>
                    {collapsedSections.aiEnhancements ? "▶" : "▼"}
                  </span>
                </h5>
                {!collapsedSections.aiEnhancements && (
                  <>
                    <label style={{ ...sidebarCheckboxLabelStyle, marginBottom: "8px" }}>
                      <input
                        type="checkbox"
                        checked={autoCaptions}
                        onChange={e => setAutoCaptions(e.target.checked)}
                        style={{ marginRight: "8px" }}
                      />
                      Auto-Captions (Burn-in)
                    </label>
                    {autoCaptions && (
                      <div style={{ marginBottom: "10px", paddingLeft: "4px" }}>
                        <label
                          style={{ ...sidebarBodyTextStyle, display: "block", marginBottom: "6px" }}
                        >
                          Caption Style:
                        </label>
                        <select
                          value={captionStyle}
                          onChange={e => setCaptionStyle(e.target.value)}
                          style={{
                            width: "100%",
                            padding: "6px 8px",
                            borderRadius: "6px",
                            border: "1px solid #444",
                            background: "#1a1a2e",
                            color: "#fff",
                            fontSize: "13px",
                          }}
                        >
                          {CAPTION_STYLE_OPTIONS.map(([value, label]) => (
                            <option key={value} value={value}>{label}</option>
                          ))}
                        </select>
                        <div
                          style={{
                            ...sidebarBodyTextStyle,
                            marginTop: "4px",
                            fontSize: "11px",
                            opacity: 0.6,
                          }}
                        >
                          {CAPTION_STYLE_OPTIONS.find(([value]) => value === captionStyle)?.[2]}
                        </div>
                      </div>
                    )}
                    <div style={{ ...sidebarBodyTextStyle, marginBottom: "10px" }}>
                      Captions are generated as an AI draft. Clean English and Afrikaans speech
                      usually performs best. Mixed South African languages and slang may need manual
                      review.
                    </div>
                    <label style={sidebarCheckboxLabelStyle}>
                      <input
                        type="checkbox"
                        checked={effectiveSmartCrop}
                        onChange={e => {
                          recordReframeModeAtPlayhead(e.target.checked ? smartCropMode : "off");
                          setSmartCrop(e.target.checked);
                        }}
                        style={{ marginRight: "8px" }}
                      />
                      Smart Crop (Vertical reframe)
                    </label>
                    {effectiveSmartCrop && (
                      <div style={{ marginBottom: "10px", paddingLeft: "4px", marginTop: "6px" }}>
                        <label
                          style={{ ...sidebarBodyTextStyle, display: "block", marginBottom: "4px" }}
                        >
                          Crop Mode:
                        </label>
                        <div style={{ display: "flex", gap: "6px" }}>
                          <button
                            onClick={showEveryoneSplit}
                            style={{
                              flex: 1,
                              padding: "6px 8px",
                              borderRadius: "6px",
                              fontSize: "12px",
                              cursor: "pointer",
                              border:
                                effectiveSmartCropMode === "center" ? "2px solid #6366f1" : "1px solid #444",
                              background: effectiveSmartCropMode === "center" ? "#2d2b55" : "#1a1a2e",
                              color: "#fff",
                            }}
                          >
                            👥 Show Everyone
                          </button>
                          <button
                            onClick={() => openSpeakerTracking(true)}
                            style={{
                              flex: 1,
                              padding: "6px 8px",
                              borderRadius: "6px",
                              fontSize: "12px",
                              cursor: "pointer",
                              border:
                                effectiveSmartCropMode === "speaker_track"
                                  ? "2px solid #6366f1"
                                  : "1px solid #444",
                              background: effectiveSmartCropMode === "speaker_track" ? "#2d2b55" : "#1a1a2e",
                              color: "#fff",
                            }}
                          >
                            👤 Reviewed Follow Path
                          </button>
                          <button
                            onClick={() => {
                              setSmartCrop(true);
                              setSmartCropMode("group_stack");
                              recordReframeModeAtPlayhead("group_stack");
                            }}
                            style={{
                              flex: 1,
                              padding: "6px 8px",
                              borderRadius: "6px",
                              fontSize: "12px",
                              cursor: "pointer",
                              border:
                                effectiveSmartCropMode === "group_stack"
                                  ? "2px solid #6366f1"
                                  : "1px solid #444",
                              background:
                                effectiveSmartCropMode === "group_stack" ? "#2d2b55" : "#1a1a2e",
                              color: "#fff",
                            }}
                          >
                            🎥 Multi-Camera
                          </button>
                        </div>
                        <div
                          style={{
                            ...sidebarBodyTextStyle,
                            marginTop: "4px",
                            fontSize: "11px",
                            opacity: 0.6,
                          }}
                        >
                          {effectiveSmartCropMode === "speaker_track"
                            ? reframeKeyframes.length
                              ? `${reframeKeyframes.length} reviewed framing point${reframeKeyframes.length === 1 ? "" : "s"} drive the follow path`
                              : "No tracking data yet; the crop stays centered until framing points are added"
                            : effectiveSmartCropMode === "group_stack"
                              ? `Shows ${speakerStackCameraCount} synchronized live camera crops in the selected layout`
                              : "Keeps the complete native source frame and every subject visible"}
                        </div>
                      </div>
                    )}
                    <label style={{ ...sidebarCheckboxLabelStyle, marginTop: "10px" }}>
                      <input
                        type="checkbox"
                        checked={enhanceQuality}
                        onChange={e => setEnhanceQuality(e.target.checked)}
                        style={{ marginRight: "8px" }}
                      />
                      Quality Enhancement (Safe clean-up)
                    </label>
                    {enhanceQuality ? (
                      <div
                        style={{ ...sidebarBodyTextStyle, marginTop: "8px", marginBottom: "10px" }}
                      >
                        Uses a conservative export pass to reduce noise and add light sharpening. It
                        is designed to improve soft footage gently, not to fake missing detail.
                      </div>
                    ) : null}
                    <label style={{ ...sidebarCheckboxLabelStyle, marginTop: "10px" }}>
                      <input
                        type="checkbox"
                        checked={silenceRemoval}
                        onChange={e => setSilenceRemoval(e.target.checked)}
                        style={{ marginRight: "8px" }}
                      />
                      Remove Silence
                    </label>
                    {silenceRemoval ? (
                      <div className="micro-settings-card">
                        <label className="studio-slider-label">
                          <span>Silence threshold {silenceThreshold} dB</span>
                          <input
                            type="range"
                            min={-55}
                            max={-20}
                            step={1}
                            value={silenceThreshold}
                            onChange={e => setSilenceThreshold(Number(e.target.value))}
                          />
                        </label>
                        <label className="studio-slider-label">
                          <span>Minimum pause {Number(minSilenceDuration).toFixed(2)}s</span>
                          <input
                            type="range"
                            min={0.25}
                            max={2.5}
                            step={0.05}
                            value={minSilenceDuration}
                            onChange={e => setMinSilenceDuration(Number(e.target.value))}
                          />
                        </label>
                      </div>
                    ) : null}
                    <label style={{ ...sidebarCheckboxLabelStyle, marginTop: "10px" }}>
                      <input
                        type="checkbox"
                        checked={removeWatermark}
                        onChange={e => setRemoveWatermark(e.target.checked)}
                        style={{ marginRight: "8px" }}
                      />
                      Remove Platform Watermarks
                    </label>
                    {removeWatermark ? (
                      <div className="micro-settings-card compact">
                        <label className="studio-slider-label">
                          <span>Watermark cleanup mode</span>
                          <select
                            value={watermarkMode}
                            onChange={e => setWatermarkMode(e.target.value)}
                          >
                            <option value="adaptive">Adaptive tracking</option>
                            <option value="manual">Manual cleanup boxes</option>
                            <option value="corners">Static opposite corners</option>
                            <option value="top_right">Top right only</option>
                            <option value="bottom_left">Bottom left only</option>
                            <option value="all">Aggressive all corners</option>
                          </select>
                        </label>
                        {watermarkMode === "manual" ? (
                          <div className="watermark-manual-tools">
                            <p style={{ ...sidebarBodyTextStyle, margin: 0 }}>
                              Add a cleanup box, drag it over the watermark in the preview, then
                              size it until it covers the badge cleanly.
                            </p>
                            <div className="watermark-manual-actions">
                              <button
                                type="button"
                                className="mini-toggle-btn"
                                onClick={() => addManualWatermarkRegion()}
                              >
                                Add cleanup box
                              </button>
                              {activeWatermarkRegionId ? (
                                <button
                                  type="button"
                                  className="mini-toggle-btn"
                                  onClick={() =>
                                    deleteManualWatermarkRegion(activeWatermarkRegionId)
                                  }
                                >
                                  Delete selected box
                                </button>
                              ) : null}
                            </div>
                            <div className="watermark-region-list">
                              {manualWatermarkRegions.length ? (
                                manualWatermarkRegions.map((region, index) => (
                                  <button
                                    key={region.id}
                                    type="button"
                                    className={`mini-toggle-btn ${
                                      activeWatermarkRegionId === region.id ? "active" : ""
                                    }`}
                                    onClick={() => setActiveWatermarkRegionId(region.id)}
                                  >
                                    Box {index + 1}
                                  </button>
                                ))
                              ) : (
                                <span style={sidebarBodyTextStyle}>No manual boxes yet.</span>
                              )}
                            </div>
                            {activeWatermarkRegionId
                              ? (() => {
                                  const activeRegion = manualWatermarkRegions.find(
                                    region => region.id === activeWatermarkRegionId
                                  );
                                  if (!activeRegion) return null;

                                  return (
                                    <div className="watermark-region-editor">
                                      <label
                                        style={{ ...sidebarCheckboxLabelStyle, marginTop: "2px" }}
                                      >
                                        <input
                                          type="checkbox"
                                          checked={activeRegion.track !== false}
                                          onChange={e =>
                                            updateManualWatermarkRegion(activeRegion.id, {
                                              track: e.target.checked,
                                            })
                                          }
                                          style={{ marginRight: "8px" }}
                                        />
                                        Track this cleanup box across the clip
                                      </label>
                                      <div className="watermark-manual-actions">
                                        <button
                                          type="button"
                                          className="mini-toggle-btn"
                                          onClick={() =>
                                            updateManualWatermarkRegion(activeRegion.id, {
                                              seedTime: clampNumber(videoTime, 0, 36000, 0),
                                            })
                                          }
                                        >
                                          Use current frame as seed
                                        </button>
                                      </div>
                                      <div className="watermark-cleanup-preview-status">
                                        Tracking seed:{" "}
                                        {Number(activeRegion.seedTime || 0).toFixed(2)}s
                                      </div>
                                      <label className="studio-slider-label">
                                        <span>Box width</span>
                                        <input
                                          type="range"
                                          min={8}
                                          max={58}
                                          step={1}
                                          value={activeRegion.width}
                                          onChange={e =>
                                            updateManualWatermarkRegion(activeRegion.id, {
                                              width: Number(e.target.value),
                                            })
                                          }
                                        />
                                      </label>
                                      <label className="studio-slider-label">
                                        <span>Box height</span>
                                        <input
                                          type="range"
                                          min={4}
                                          max={24}
                                          step={1}
                                          value={activeRegion.height}
                                          onChange={e =>
                                            updateManualWatermarkRegion(activeRegion.id, {
                                              height: Number(e.target.value),
                                            })
                                          }
                                        />
                                      </label>
                                    </div>
                                  );
                                })()
                              : null}
                          </div>
                        ) : null}
                        <div className="watermark-cleanup-preview-panel">
                          <div className="watermark-cleanup-preview-actions">
                            <p style={{ ...sidebarBodyTextStyle, margin: 0 }}>
                              Pause on the frame you want to inspect, then run a real cleanup
                              preview to verify the worker removes the watermark without damaging
                              nearby content.
                            </p>
                            {watermarkCleanupPreview ? (
                              <label style={{ ...sidebarCheckboxLabelStyle, marginTop: "2px" }}>
                                <input
                                  type="checkbox"
                                  checked={showWatermarkCleanupOnVideo}
                                  onChange={e => setShowWatermarkCleanupOnVideo(e.target.checked)}
                                  style={{ marginRight: "8px" }}
                                />
                                Show cleaned frame on video when paused
                              </label>
                            ) : null}
                            <button
                              type="button"
                              className="mini-toggle-btn"
                              onClick={handleGenerateWatermarkCleanupPreview}
                              disabled={
                                isWatermarkCleanupPreviewLoading ||
                                !currentTimelineClip ||
                                (watermarkMode === "manual" && !manualWatermarkRegions.length)
                              }
                            >
                              {isWatermarkCleanupPreviewLoading
                                ? "Rendering real preview..."
                                : "Preview real cleanup"}
                            </button>
                          </div>
                          {watermarkCleanupPreviewError ? (
                            <div className="watermark-cleanup-preview-status error">
                              {watermarkCleanupPreviewError}
                            </div>
                          ) : null}
                          {watermarkCleanupPreview ? (
                            <div className="watermark-cleanup-preview-grid">
                              <div className="watermark-cleanup-preview-card">
                                <span className="watermark-cleanup-preview-label">
                                  Original frame
                                </span>
                                {watermarkCleanupPreview.originalImageUrl ? (
                                  <img
                                    src={getSafeMediaSource(
                                      watermarkCleanupPreview.originalImageUrl
                                    )}
                                    alt="Original watermark frame"
                                    className="watermark-cleanup-preview-image"
                                  />
                                ) : null}
                              </div>
                              <div className="watermark-cleanup-preview-card">
                                <span className="watermark-cleanup-preview-label">
                                  Cleaned frame
                                </span>
                                {watermarkCleanupPreview.cleanedImageUrl ? (
                                  <img
                                    src={getSafeMediaSource(
                                      watermarkCleanupPreview.cleanedImageUrl
                                    )}
                                    alt="Watermark-cleaned frame preview"
                                    className="watermark-cleanup-preview-image"
                                  />
                                ) : null}
                              </div>
                              <div className="watermark-cleanup-preview-status">
                                Captured at{" "}
                                {Number(watermarkCleanupPreview.previewTime || 0).toFixed(2)}s. If
                                you move the box or scrub to another moment, run the preview again.
                              </div>
                              {showWatermarkCleanupOnVideo &&
                              !isWatermarkCleanupPreviewFrameAligned ? (
                                <div className="watermark-cleanup-preview-status">
                                  Pause near{" "}
                                  {Number(watermarkCleanupPreview.previewTime || 0).toFixed(2)}s to
                                  see the cleaned frame directly on the video preview.
                                </div>
                              ) : null}
                            </div>
                          ) : null}
                        </div>
                      </div>
                    ) : null}
                  </>
                )}
                <label style={{ ...sidebarCheckboxLabelStyle, marginTop: "10px" }}>
                  <input
                    type="checkbox"
                    checked={addHook}
                    onChange={e => setOpeningHookEnabled(e.target.checked)}
                    style={{ marginRight: "8px" }}
                  />
                  Add Hook
                </label>
                {addHook ? (
                  <div className="micro-settings-card">
                    <div className="hook-suggestion-card">
                      <strong>Suggested hook opening</strong>
                      <p>{currentHookSuggestion.message}</p>
                      <p>
                        Suggested range:{" "}
                        {formatPreviewTimePrecise(currentHookSuggestion.startTime || 0)} to{" "}
                        {formatPreviewTimePrecise(currentHookSuggestion.endTime || hookEnd)}
                        {currentHookSuggestion.confidenceLabel
                          ? ` • ${currentHookSuggestion.confidenceLabel}`
                          : ""}
                      </p>
                      {hookAnalysisMessage ? (
                        <div
                          className={`hook-analysis-status hook-analysis-status-${hookAnalysisStatus}`}
                        >
                          {hookAnalysisMessage}
                        </div>
                      ) : null}
                      <div className="mini-toggle-row">
                        <button
                          type="button"
                          className="mini-toggle-btn active"
                          onClick={() => applyCurrentHookSuggestion(false)}
                        >
                          Apply suggested segment
                        </button>
                        <button
                          type="button"
                          className={`mini-toggle-btn ${hookAnalysisStatus === "analyzing" ? "active" : ""}`}
                          onClick={runSmartHookSuggestion}
                          disabled={hookAnalysisStatus === "analyzing"}
                        >
                          {hookAnalysisStatus === "analyzing" ? "Suggesting..." : "Suggest Hook"}
                        </button>
                      </div>
                    </div>
                    <label className="studio-slider-label">
                      <span>Hook text</span>
                      <textarea
                        aria-label="Hook text"
                        value={hookText}
                        onChange={e => setHookText(normalizeHookText(e.target.value))}
                        placeholder="Type a curiosity hook that earns the next 3 seconds"
                        rows={3}
                      />
                    </label>
                    <div className="hook-preset-card">
                      <strong>Hook templates</strong>
                      <div className="mini-toggle-row">
                        {Object.entries(HOOK_TEMPLATES).map(([templateKey, template]) => (
                          <button
                            key={templateKey}
                            type="button"
                            className={`mini-toggle-btn ${hookTemplate === templateKey ? "active" : ""}`}
                            onClick={() => applyHookTemplate(templateKey)}
                          >
                            {template.label}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div className="hook-treatment-card">
                      <div className="hook-treatment-header">
                        <strong>Hook treatment</strong>
                        <p>{hookTemplateConfig.description}</p>
                      </div>
                      <div className="hook-treatment-toggle-grid">
                        <label style={sidebarCheckboxLabelStyle}>
                          <input
                            type="checkbox"
                            checked={hookBlurBackground}
                            onChange={e => setHookBlurBackground(e.target.checked)}
                            style={{ marginRight: "8px" }}
                          />
                          Blur background
                        </label>
                        <label style={sidebarCheckboxLabelStyle}>
                          <input
                            type="checkbox"
                            checked={hookDarkOverlay}
                            onChange={e => setHookDarkOverlay(e.target.checked)}
                            style={{ marginRight: "8px" }}
                          />
                          Dark overlay
                        </label>
                        <label style={sidebarCheckboxLabelStyle}>
                          <input
                            type="checkbox"
                            checked={hookFreezeFrame}
                            onChange={e => setHookFreezeFrame(e.target.checked)}
                            style={{ marginRight: "8px" }}
                          />
                          Freeze opening frame
                        </label>
                      </div>
                      <label className="studio-slider-label">
                        <span>Text animation</span>
                        <select
                          value={hookTextAnimation}
                          onChange={e => setHookTextAnimation(e.target.value)}
                        >
                          <option value="slide-up">Slide Up</option>
                          <option value="fade-in">Fade In</option>
                        </select>
                      </label>
                      <label className="studio-slider-label">
                        <span>Zoom intensity {hookZoomScale.toFixed(2)}x</span>
                        <input
                          type="range"
                          min={1}
                          max={1.24}
                          step={0.01}
                          value={hookZoomScale}
                          onChange={e => setHookZoomScale(Number(e.target.value))}
                        />
                      </label>
                    </div>
                    <label className="studio-slider-label">
                      <span>Hook duration {hookDuration.toFixed(2)}s</span>
                      <input
                        type="range"
                        min={hookMinDuration}
                        max={hookMaxDuration}
                        step={0.05}
                        value={hookDuration}
                        onChange={e => setHookDuration(Number(e.target.value))}
                      />
                    </label>
                    <div className="hook-preset-card">
                      <strong>Manual hook selection</strong>
                      <div className="mini-toggle-row">
                        <button
                          type="button"
                          className={`mini-toggle-btn ${hookPickMode ? "active" : ""}`}
                          onClick={() => {
                            setHookPickMode(prev => !prev);
                            setHookSelectionMode(false);
                            setHookFocusMode(false);
                          }}
                        >
                          {hookPickMode ? "Choosing hook" : "Choose Hook"}
                        </button>
                        <button
                          type="button"
                          className={`mini-toggle-btn ${hookFocusMode ? "active" : ""}`}
                          onClick={() => {
                            setAddHook(true);
                            setHookFocusMode(prev => !prev);
                            setHookSelectionMode(false);
                            setHookPickMode(false);
                          }}
                        >
                          {hookFocusMode ? "Picking focus" : "Pick Focus"}
                        </button>
                        <button
                          type="button"
                          className="mini-toggle-btn"
                          onClick={setCurrentTimeAsHook}
                        >
                          Set as Hook
                        </button>
                      </div>
                      <p className="hook-manual-copy">
                        Choose the exact opening moment on the timeline, then click the preview if
                        you want the frozen frame to zoom toward a face or object.
                      </p>
                      <div className="hook-manual-readout">
                        <span>Hook point {formatPreviewTimePrecise(trimAwareCurrentTime)}</span>
                        <span>
                          Focus target {Math.round(resolvedHookFocusPoint.x)}% x{" "}
                          {Math.round(resolvedHookFocusPoint.y)}%
                        </span>
                      </div>
                    </div>
                    <div className="hook-segment-card">
                      <div className="hook-segment-header">
                        <div>
                          <strong>Hook Source Span</strong>
                          <p>
                            This is the same opening hook, not a second one. It controls how much
                            source footage the opening uses before the clip continues without
                            replaying that hook section again.
                          </p>
                        </div>
                        <button
                          type="button"
                          className={`mini-toggle-btn ${hookSelectionMode ? "active" : ""}`}
                          onClick={() => {
                            setHookSelectionMode(prev => !prev);
                            setHookPickMode(false);
                            setHookFocusMode(false);
                          }}
                        >
                          {hookSelectionMode ? "Selection active" : "Select Hook Segment"}
                        </button>
                      </div>
                      <div
                        ref={hookSegmentTrackRef}
                        className={`hook-segment-track ${hookSelectionMode ? "selection-enabled" : ""} ${hookPickMode ? "playhead-enabled" : ""}`}
                        onMouseDown={handleHookTrackPointerDown}
                        role="presentation"
                      >
                        <span className="hook-segment-track-base" />
                        <span
                          className={`hook-segment-suggestion ${hookAnalysisStatus === "ready" ? "active" : ""}`}
                          style={{
                            left: `${hookSuggestionLeft}%`,
                            width: `${Math.max(2, hookSuggestionWidth)}%`,
                          }}
                        />
                        <span
                          className="hook-segment-playhead"
                          style={{ left: `${hookPlayheadLeft}%` }}
                        />
                        <button
                          type="button"
                          className={`hook-playhead-handle ${hookPickMode ? "active" : ""}`}
                          style={{ left: `${hookPlayheadLeft}%` }}
                          onMouseDown={hookPickMode ? beginHookPlayheadDrag : undefined}
                          aria-label="Drag hook playhead"
                        />
                        <span
                          className="hook-segment-marker"
                          style={{ left: `${hookSelectionLeft}%` }}
                        />
                        <span
                          className="hook-segment-selection"
                          style={{
                            left: `${hookSelectionLeft}%`,
                            width: `${Math.max(3, hookSelectionWidth)}%`,
                          }}
                          onMouseDown={event => beginHookSegmentDrag(event, "range")}
                          role="presentation"
                        >
                          <button
                            type="button"
                            className="hook-segment-handle hook-segment-handle-start"
                            onMouseDown={event => beginHookSegmentDrag(event, "start")}
                            aria-label="Adjust hook start"
                          />
                          <button
                            type="button"
                            className="hook-segment-handle hook-segment-handle-end"
                            onMouseDown={event => beginHookSegmentDrag(event, "end")}
                            aria-label="Adjust hook end"
                          />
                        </span>
                      </div>
                      <div className="hook-segment-readout">
                        <span>
                          Source range {formatPreviewTimePrecise(resolvedHookStart)} to{" "}
                          {formatPreviewTimePrecise(hookEnd)}
                        </span>
                        <span>{hookDuration.toFixed(2)}s</span>
                      </div>
                      <div className="hook-segment-scrubbers">
                        <label className="studio-slider-label">
                          <span>Hook start {resolvedHookStart.toFixed(2)}s</span>
                          <input
                            type="range"
                            min={0}
                            max={Math.max(0.1, hookStartLimit)}
                            step={0.01}
                            value={resolvedHookStart}
                            onChange={e =>
                              setHookSegmentRange(Number(e.target.value), hookEnd, {
                                preview: true,
                              })
                            }
                          />
                        </label>
                        <label className="studio-slider-label">
                          <span>Hook end {hookEnd.toFixed(2)}s</span>
                          <input
                            type="range"
                            min={Math.min(
                              hookEndMinimum,
                              Math.max(0.1, Number(currentTimelineWindow.duration || 0))
                            )}
                            max={Math.max(
                              hookEndMinimum,
                              Number(currentTimelineWindow.duration || hookEndMaximum || 0.1)
                            )}
                            step={0.01}
                            value={hookEnd}
                            onChange={e =>
                              setHookSegmentRange(resolvedHookStart, Number(e.target.value))
                            }
                          />
                        </label>
                      </div>
                    </div>
                    <div className="mini-toggle-row">
                      <button
                        type="button"
                        className="mini-toggle-btn active"
                        onClick={() => previewHookSegment(false)}
                      >
                        Preview hook once
                      </button>
                      <button
                        type="button"
                        className={`mini-toggle-btn ${hookPreviewLoop ? "active" : ""}`}
                        onClick={() => previewHookSegment(!hookPreviewLoop)}
                      >
                        {hookPreviewLoop ? "Stop hook loop" : "Loop hook segment"}
                      </button>
                    </div>
                  </div>
                ) : null}
              </div>
              <div className="ai-settings-card music-settings-card">
                <h5
                  style={{
                    ...sidebarSectionTitleStyle,
                    cursor: "pointer",
                    userSelect: "none",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                  onClick={() => toggleSection("musicAudio")}
                >
                  <span>🎵 Music &amp; Audio</span>
                  <span style={{ fontSize: "12px", opacity: 0.6 }}>
                    {collapsedSections.musicAudio ? "▶" : "▼"}
                  </span>
                </h5>
                {!collapsedSections.musicAudio && (
                  <>
                    {/* ── Original Audio ── */}
                    <label style={{ ...sidebarCheckboxLabelStyle, marginBottom: "6px" }}>
                      <input
                        type="checkbox"
                        checked={muteOriginalAudio}
                        onChange={e => setMuteOriginalAudio(e.target.checked)}
                        style={{ marginRight: "8px" }}
                      />
                      Mute Original Audio
                    </label>

                    {/* ── Background Music ── */}
                    <div
                      style={{
                        marginTop: "12px",
                        borderTop: "1px solid rgba(255,255,255,0.08)",
                        paddingTop: "10px",
                      }}
                    >
                      <label style={{ ...sidebarCheckboxLabelStyle, marginBottom: "4px" }}>
                        <input
                          type="checkbox"
                          checked={addMusic}
                          onChange={e => {
                            if (e.target.checked) {
                              if (!musicTrack) {
                                selectPresetMusic(MUSIC_PRESETS[0]);
                              } else {
                                setAddMusic(true);
                              }
                            } else {
                              setAddMusic(false);
                            }
                          }}
                          style={{ marginRight: "8px" }}
                        />
                        Background Music
                      </label>

                      {addMusic && (
                        <div style={{ marginTop: "8px", paddingLeft: "4px" }}>
                          {/* Current track info */}
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "6px",
                              padding: "6px 8px",
                              background: "rgba(16,185,129,0.1)",
                              borderRadius: "8px",
                              marginBottom: "8px",
                              fontSize: "0.75rem",
                              color: "#d1fae5",
                            }}
                          >
                            <span>🎵</span>
                            <span style={{ flex: 1, fontWeight: 600 }}>
                              {musicTrack?.name || musicSelection?.replace(".mp3", "") || "Custom"}
                            </span>
                            <button
                              type="button"
                              onClick={removeMusic}
                              style={{
                                background: "none",
                                border: "none",
                                color: "#f87171",
                                cursor: "pointer",
                                fontSize: "0.85rem",
                                padding: "0 4px",
                              }}
                              title="Remove music"
                            >
                              ✕
                            </button>
                          </div>

                          {/* Music Library toggle */}
                          <button
                            type="button"
                            className="mini-toggle-btn"
                            onClick={() => setMusicLibraryOpen(!musicLibraryOpen)}
                            style={{ width: "100%", marginBottom: "6px", fontSize: "0.75rem" }}
                          >
                            {musicLibraryOpen ? "📁 Close Library" : "📁 Browse Music Library"}
                          </button>

                          {/* Preset Library */}
                          {musicLibraryOpen && (
                            <div
                              style={{
                                display: "grid",
                                gridTemplateColumns: "1fr 1fr",
                                gap: "4px",
                                marginBottom: "8px",
                                maxHeight: "160px",
                                overflowY: "auto",
                                paddingRight: "2px",
                              }}
                            >
                              {MUSIC_PRESETS.map(preset => (
                                <button
                                  key={preset.file}
                                  type="button"
                                  className="mini-toggle-btn"
                                  onClick={() => selectPresetMusic(preset)}
                                  style={{
                                    fontSize: "0.65rem",
                                    padding: "5px 6px",
                                    textAlign: "left",
                                    background:
                                      musicSelection === preset.file
                                        ? "rgba(16,185,129,0.25)"
                                        : undefined,
                                  }}
                                >
                                  {preset.emoji} {preset.name}
                                </button>
                              ))}
                            </div>
                          )}

                          {/* Custom Upload */}
                          <input
                            ref={musicFileInputRef}
                            type="file"
                            accept="audio/*,.mp3,.wav,.ogg,.m4a,.aac"
                            onChange={handleMusicFileUpload}
                            style={{ display: "none" }}
                          />
                          <button
                            type="button"
                            className="mini-toggle-btn"
                            onClick={() => musicFileInputRef.current?.click()}
                            style={{ width: "100%", marginBottom: "8px", fontSize: "0.75rem" }}
                          >
                            📤 Upload Custom Music
                          </button>

                          {/* Music Volume */}
                          <label className="studio-slider-label" style={{ marginBottom: "6px" }}>
                            <span>
                              🎚️ Music Vol: {Math.round((musicTrack?.volume ?? musicVolume) * 100)}%
                            </span>
                            <input
                              type="range"
                              min={0}
                              max={60}
                              step={1}
                              value={Math.round((musicTrack?.volume ?? musicVolume) * 100)}
                              onChange={e => {
                                const v = Number(e.target.value) / 100;
                                setMusicVolume(v);
                                if (musicTrack) setMusicTrackField("volume", v);
                              }}
                              style={{ width: "100%" }}
                            />
                          </label>

                          {/* Fade In/Out */}
                          <div style={{ display: "flex", gap: "6px", marginBottom: "8px" }}>
                            <label className="studio-slider-label" style={{ flex: 1 }}>
                              <span>↗ Fade In: {(musicTrack?.fadeIn ?? 0.5).toFixed(1)}s</span>
                              <input
                                type="range"
                                min={0}
                                max={3}
                                step={0.1}
                                value={musicTrack?.fadeIn ?? 0.5}
                                onChange={e => setMusicTrackField("fadeIn", Number(e.target.value))}
                                style={{ width: "100%" }}
                              />
                            </label>
                            <label className="studio-slider-label" style={{ flex: 1 }}>
                              <span>↘ Fade Out: {(musicTrack?.fadeOut ?? 0.5).toFixed(1)}s</span>
                              <input
                                type="range"
                                min={0}
                                max={3}
                                step={0.1}
                                value={musicTrack?.fadeOut ?? 0.5}
                                onChange={e =>
                                  setMusicTrackField("fadeOut", Number(e.target.value))
                                }
                                style={{ width: "100%" }}
                              />
                            </label>
                          </div>

                          {/* Loop */}
                          <label
                            style={{
                              ...sidebarCheckboxLabelStyle,
                              marginBottom: "6px",
                              fontSize: "0.75rem",
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={musicTrack?.loop ?? true}
                              onChange={e => setMusicTrackField("loop", e.target.checked)}
                              style={{ marginRight: "6px" }}
                            />
                            🔁 Loop Music
                          </label>

                          {/* Ducking */}
                          <div
                            style={{
                              marginTop: "8px",
                              borderTop: "1px solid rgba(255,255,255,0.08)",
                              paddingTop: "8px",
                            }}
                          >
                            <span
                              style={{ fontSize: "0.73rem", color: "#94a3b8", fontWeight: 700 }}
                            >
                              🔊 Auto-Ducking
                            </span>

                            <label
                              style={{
                                ...sidebarCheckboxLabelStyle,
                                marginTop: "4px",
                                fontSize: "0.75rem",
                              }}
                            >
                              <input
                                type="checkbox"
                                checked={musicTrack?.ducking ?? true}
                                onChange={e => setMusicTrackField("ducking", e.target.checked)}
                                style={{ marginRight: "6px" }}
                              />
                              Enable Ducking
                            </label>

                            {musicTrack?.ducking && (
                              <>
                                {/* Ducking Mode */}
                                <div style={{ display: "flex", gap: "4px", margin: "4px 0 6px 0" }}>
                                  {[
                                    { value: "speech", label: "🗣 Speech-Aware" },
                                    { value: "constant", label: "📏 Constant" },
                                  ].map(mode => (
                                    <button
                                      key={mode.value}
                                      type="button"
                                      className={`mini-toggle-btn ${(musicTrack?.duckingMode || "speech") === mode.value ? "active" : ""}`}
                                      onClick={() => setMusicTrackField("duckingMode", mode.value)}
                                      style={{ flex: 1, fontSize: "0.65rem", padding: "4px 6px" }}
                                    >
                                      {mode.label}
                                    </button>
                                  ))}
                                </div>

                                <label
                                  className="studio-slider-label"
                                  style={{ marginBottom: "4px" }}
                                >
                                  <span>
                                    Duck Strength:{" "}
                                    {Math.round((musicTrack?.duckingStrength ?? 0.4) * 100)}%
                                  </span>
                                  <input
                                    type="range"
                                    min={10}
                                    max={90}
                                    step={5}
                                    value={Math.round((musicTrack?.duckingStrength ?? 0.4) * 100)}
                                    onChange={e =>
                                      setMusicTrackField(
                                        "duckingStrength",
                                        Number(e.target.value) / 100
                                      )
                                    }
                                    style={{ width: "100%" }}
                                  />
                                </label>

                                <div
                                  style={{
                                    ...sidebarBodyTextStyle,
                                    fontSize: "0.65rem",
                                    marginTop: "2px",
                                  }}
                                >
                                  {musicTrack?.duckingMode === "speech"
                                    ? "Music lowers smoothly when speech is detected and rises during silence."
                                    : "Music stays at a constant reduced level throughout the clip."}
                                </div>
                              </>
                            )}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Original audio context */}
                    <div
                      style={{ ...sidebarBodyTextStyle, marginTop: "10px", fontSize: "0.68rem" }}
                    >
                      {muteOriginalAudio
                        ? "Only background music will be heard."
                        : addMusic
                          ? "Voice + music mixed with auto-ducking."
                          : "Only original audio — no background music."}
                    </div>
                  </>
                )}
              </div>
            </section>

            <section
              className={`studio-panel export-panel ${activeCreativeTool === "export" ? "is-tool-visible" : ""}`}
              aria-hidden={activeCreativeTool !== "export"}
            >
              <div className="panel-heading compact">
                <div>
                  <span className="panel-kicker">Publish</span>
                  <h4>Render and export</h4>
                </div>
              </div>
              <p className="panel-description">
                Final export uses the hook treatment and B-roll layers you approved in Studio.
              </p>
              {exportErrorMessage ? <p role="alert" data-testid="studio-export-error">
                {exportErrorMessage}
              </p> : null}
              <section className="split-export-card">
                <div>
                  <span>A/B hook pack</span>
                  <strong>Export alternate first-three-second hooks</strong>
                  <small>One edit, up to three hook variants for real platform testing.</small>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    const suggestions = getHookCopySuggestions(selectedClip).slice(0, 3);
                    setSplitExportHooks(
                      suggestions.map((text, index) => ({
                        id: createSecureId("split-hook"),
                        text,
                        template: ["zoom_focus", "freeze_text", "blur_reveal"][index],
                      }))
                    );
                    setSplitExportEnabled(true);
                    setStudioActionMessage(
                      "Three editable hook variants are ready for split export."
                    );
                  }}
                >
                  {splitExportHooks.length ? "Regenerate hooks" : "Generate 3 hooks"}
                </button>
                {splitExportHooks.length ? (
                  <div className="split-export-variants">
                    {splitExportHooks.map((variant, index) => (
                      <label key={variant.id}>
                        <span>V{index + 1}</span>
                        <input
                          aria-label={`Split export hook ${index + 1}`}
                          value={variant.text}
                          onChange={event =>
                            setSplitExportHooks(current =>
                              current.map(item =>
                                item.id === variant.id
                                  ? { ...item, text: normalizeHookText(event.target.value) }
                                  : item
                              )
                            )
                          }
                        />
                      </label>
                    ))}
                    <label className="inspector-check-row">
                      <input
                        type="checkbox"
                        checked={splitExportEnabled}
                        onChange={event => setSplitExportEnabled(event.target.checked)}
                      />
                      <span>
                        <strong>Include all variants in this render job</strong>
                      </span>
                    </label>
                  </div>
                ) : null}
              </section>
              {onRestoreExport && !isExporting ? (
                <label className="inspector-check-row">
                  <span>Restore export receipt</span>
                  <input type="file" accept="application/json,.json" aria-label="Restore export receipt"
                    onChange={event => {
                      const file = event.target.files?.[0]; event.target.value = "";
                      if (file) void onRestoreExport(file, getEditorSnapshot().studioDocument);
                    }} />
                </label>
              ) : null}
              {completedOutputUrl ? (
                <div
                  className="rendered-output-ready"
                  role="status"
                  data-testid="rendered-output-ready"
                >
                  <div>
                    <strong>{renderedOutput?.captionReviewCopy ? "Caption review copy ready" : "Rendered video ready"}</strong>
                    <span>{!previewFinishedFile ? "After is showing your editable project. The finished download is retained." : renderedOutput?.captionReviewCopy
                      ? "Draft captions are burned in for review. Correct and approve the timeline before publishing."
                      : "The finished file is loaded in After above."}</span>
                  </div>
                  <div className="rendered-output-actions">
                    <button type="button" aria-pressed={!previewFinishedFile} onClick={() => {
                      pauseSynchronizedPreview();
                      setPreviewFinishedFile(current => !current);
                    }}>
                      {previewFinishedFile ? "Show Live Edit" : "Show Finished Video"}
                    </button>
                    <button type="button" onClick={onDownloadRendered}>
                      Download Rendered Clip
                    </button>
                    {!renderedOutput?.captionReviewCopy ? (
                      <button type="button" onClick={onUseRendered}>
                        Use in Publisher
                      </button>
                    ) : null}
                  </div>
                </div>
              ) : null}
              <div
                className="render-destination-row"
                role="radiogroup"
                aria-label="Export destination"
              >
                {[
                  { value: "general", label: "Download" },
                  { value: "tiktok", label: "TikTok" },
                  { value: "reels", label: "Reels" },
                  { value: "shorts", label: "Shorts" },
                ].map(destination => (
                  <button
                    key={destination.value}
                    type="button"
                    role="radio"
                    aria-checked={selectedExportDestination === destination.value}
                    className={selectedExportDestination === destination.value ? "is-active" : ""}
                    onClick={() => setSelectedExportDestination(destination.value)}
                    disabled={isExporting}
                  >
                    {destination.label}
                  </button>
                ))}
              </div>
              <div className="export-settings-grid" data-testid="export-settings-grid">
                <label>
                  <span>Resolution</span>
                  <select
                    aria-label="Export resolution"
                    value={exportSettings.resolution}
                    onChange={event =>
                      setExportSettings(current => ({
                        ...current,
                        resolution: event.target.value,
                      }))
                    }
                    disabled={isExporting}
                  >
                    <option value="720p">720p</option>
                    <option value="1080p">1080p</option>
                    <option value="2160p">4K</option>
                    <option value="source">Source size</option>
                  </select>
                </label>
                <label>
                  <span>Frame rate</span>
                  <select
                    aria-label="Export frame rate"
                    value={exportSettings.fps}
                    onChange={event =>
                      setExportSettings(current => ({ ...current, fps: event.target.value }))
                    }
                    disabled={isExporting}
                  >
                    <option value="source">Source</option>
                    {[24, 25, 30, 50, 60].map(fps => (
                      <option key={fps} value={String(fps)}>
                        {fps} fps
                      </option>
                    ))}
                  </select>
                </label>
                <label>
                  <span>Codec</span>
                  <select
                    aria-label="Export codec"
                    value={exportSettings.codec}
                    onChange={event =>
                      setExportSettings(current => ({ ...current, codec: event.target.value }))
                    }
                    disabled={isExporting}
                  >
                    <option value="h264">H.264 · compatible</option>
                    <option value="h265">H.265 · smaller</option>
                  </select>
                </label>
                <label>
                  <span>Quality</span>
                  <select
                    aria-label="Export quality"
                    value={exportSettings.quality}
                    onChange={event =>
                      setExportSettings(current => ({ ...current, quality: event.target.value }))
                    }
                    disabled={isExporting}
                  >
                    <option value="draft">Draft</option>
                    <option value="balanced">Balanced</option>
                    <option value="high">High</option>
                    <option value="master">Master</option>
                  </select>
                </label>
                <label>
                  <span>Video bitrate</span>
                  <select
                    aria-label="Export video bitrate"
                    value={exportSettings.videoBitrate}
                    onChange={event =>
                      setExportSettings(current => ({
                        ...current,
                        videoBitrate: event.target.value,
                      }))
                    }
                    disabled={isExporting}
                  >
                    <option value="auto">Auto for quality</option>
                    <option value="5">5 Mbps</option>
                    <option value="8">8 Mbps</option>
                    <option value="12">12 Mbps</option>
                    <option value="20">20 Mbps</option>
                    <option value="40">40 Mbps</option>
                  </select>
                </label>
                <label>
                  <span>Target size</span>
                  <input
                    aria-label="Target file size megabytes"
                    type="number"
                    min={1}
                    max={10000}
                    placeholder="Auto (MB)"
                    value={exportSettings.targetFileSizeMb}
                    onChange={event =>
                      setExportSettings(current => ({
                        ...current,
                        targetFileSizeMb: event.target.value,
                      }))
                    }
                    disabled={isExporting}
                  />
                </label>
                <label>
                  <span>Audio codec</span>
                  <select
                    aria-label="Export audio codec"
                    value={exportSettings.audioCodec}
                    onChange={event =>
                      setExportSettings(current => ({
                        ...current,
                        audioCodec: event.target.value,
                      }))
                    }
                    disabled={isExporting}
                  >
                    <option value="aac">AAC</option>
                    <option value="opus">Opus</option>
                    <option value="pcm">PCM · lossless</option>
                  </select>
                </label>
                <label>
                  <span>Audio bitrate</span>
                  <select
                    aria-label="Export audio bitrate"
                    value={exportSettings.audioBitrate}
                    onChange={event =>
                      setExportSettings(current => ({
                        ...current,
                        audioBitrate: event.target.value,
                      }))
                    }
                    disabled={isExporting}
                  >
                    {[128, 192, 256, 320].map(bitrate => (
                      <option key={bitrate} value={String(bitrate)}>
                        {bitrate} kbps
                      </option>
                    ))}
                  </select>
                </label>
                <label>
                  <span>Color space</span>
                  <select
                    aria-label="Export color space"
                    value={exportSettings.colorSpace}
                    onChange={event =>
                      setExportSettings(current => ({
                        ...current,
                        colorSpace: event.target.value,
                      }))
                    }
                    disabled={isExporting}
                  >
                    <option value="rec709">Rec.709</option>
                    <option value="srgb">sRGB</option>
                    <option value="display_p3">Display P3</option>
                    <option value="hdr10">HDR10</option>
                  </select>
                </label>
              </div>
              <button
                className="export-btn"
                onClick={() => void handleExportRender(selectedExportDestination)}
                disabled={isExporting}
              >
                {exportStatusLabel}
              </button>
              {autoCaptions && captionSegments.some(segment => segment.reviewRequired) ? (
                <button
                  type="button"
                  className="export-btn"
                  data-testid="render-caption-review-copy"
                  onClick={() => void handleExportRender(selectedExportDestination, { captionReviewCopy: true })}
                  disabled={isExporting}
                >
                  Render caption review copy (draft, not publishable)
                </button>
              ) : null}
              {isExporting && (
                <button
                  className="export-btn cancel-export-btn"
                  onClick={() => {
                    if (window.confirm("Cancel this render? You can retry after.")) {
                      onCancel();
                    }
                  }}
                  style={{
                    background: "rgba(239, 68, 68, 0.15)",
                    border: "1px solid rgba(239, 68, 68, 0.4)",
                    color: "#f87171",
                    marginTop: "8px",
                  }}
                >
                  ✕ Cancel Render
                </button>
              )}
              <p
                style={{
                  fontSize: "0.72rem",
                  color: "#94a3b8",
                  margin: "6px 0 0 0",
                  textAlign: "center",
                }}
              >
                One render includes your hook, B-roll, audio rules, captions, and selected
                destination.
              </p>
            </section>

            {overlays.length > 0 && (
              <section className="studio-panel layer-panel">
                <h5 style={{ margin: "0 0 10px 0" }}>Clips in edit</h5>
                <p
                  style={{
                    fontSize: "12px",
                    color: "rgba(247, 248, 251, 0.68)",
                    margin: "0 0 8px 0",
                  }}
                >
                  Select a clip to adjust when it appears.
                </p>
                <div className="layer-list">
                  {[...overlays].reverse().map((overlay, reversedIndex) => {
                    const actualIndex = overlays.length - 1 - reversedIndex;
                    const isActive = activeOverlayId === overlay.id;
                    const isTimedLayer =
                      overlay.startTime !== undefined && overlay.duration !== undefined;
                    const isVideoBRoll = isTimedLayer && overlay.type === "video";
                    const overlayStart = Number(overlay.startTime || 0);
                    const overlayEnd = overlayStart + Number(overlay.duration || 0);
                    const overlayName =
                      overlay.file?.name ||
                      overlay.name ||
                      (overlay.type === "text"
                        ? overlay.text || "Text layer"
                        : `${overlay.type === "image" ? "Image" : "Video"} layer`);
                    const modeLabel = isVideoBRoll
                      ? "Cutaway"
                      : isTimedLayer
                        ? "Timed layer"
                        : "Free layer";
                    const label = isVideoBRoll
                      ? `B-roll: ${overlayName}`
                      : overlay.type === "text"
                        ? `Text: ${(overlay.text || "").slice(0, 16) || "Untitled"}`
                        : `${overlay.type === "image" ? "Image" : "Video"} Overlay`;
                    const detail = isTimedLayer
                      ? `${modeLabel} - ${overlayStart.toFixed(1)}s to ${overlayEnd.toFixed(1)}s`
                      : overlay.type === "text"
                        ? "Edit copy and position"
                        : "Edit size and position";

                    return (
                      <div
                        key={overlay.id}
                        onClick={() => setActiveOverlayId(overlay.id)}
                        draggable
                        onDragStart={() => setDraggedOverlayId(overlay.id)}
                        onDragEnd={() => setDraggedOverlayId(null)}
                        onDragOver={e => {
                          e.preventDefault();
                        }}
                        onDrop={e => {
                          e.preventDefault();
                          if (draggedOverlayId === null || draggedOverlayId === overlay.id) return;
                          moveOverlayToIndex(draggedOverlayId, actualIndex);
                          setDraggedOverlayId(null);
                        }}
                        className={`layer-row ${isActive ? "active" : ""}`}
                        style={
                          draggedOverlayId === overlay.id
                            ? { borderStyle: "dashed", borderColor: "#e52e71" }
                            : undefined
                        }
                      >
                        <div className="layer-row-copy">
                          <span className="layer-row-label">{label}</span>
                          <span className="layer-row-detail">{detail}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </section>
            )}

            {activeOverlay && (
              <section
                className={`studio-panel active-overlay-panel ${activeOverlayIsVideoBRoll ? "broll-layer-controls" : ""} ${activeOverlayIsFullscreenBRoll ? "fullscreen-broll-controls" : ""}`}
              >
                <div className="active-layer-header">
                  <div>
                    <span className="panel-kicker">
                      {activeOverlayIsVideoBRoll ? "B-roll" : "Selected layer"}
                    </span>
                    <h5>{activeOverlayIsVideoBRoll ? "Selected Clip" : "Layer Controls"}</h5>
                  </div>
                  <span className="layer-type-pill">
                    {activeOverlayIsVideoBRoll ? "Cutaway" : activeOverlay.type}
                  </span>
                </div>
                <p
                  style={{
                    fontSize: "12px",
                    color: "rgba(247, 248, 251, 0.68)",
                    margin: "0 0 10px 0",
                  }}
                >
                  {activeOverlayIsVideoBRoll
                    ? "Choose when this uploaded clip appears in the preview."
                    : "Fine-tune the selected layer here. Arrow keys nudge it, and Shift nudges faster."}
                </p>
                {activeOverlayIsVideoBRoll && (
                  <div className="broll-layer-summary">
                    <div className="broll-layer-preview">
                      {activeOverlaySafeSrc ? (
                        <video
                          ref={element => {
                            applySafeMediaSource(element, activeOverlaySafeSrc);
                          }}
                          muted
                          playsInline
                          preload="metadata"
                        />
                      ) : (
                        <span>Video</span>
                      )}
                    </div>
                    <div className="broll-layer-copy">
                      <strong title={activeOverlayDisplayName}>{activeOverlayDisplayName}</strong>
                      <span>
                        {activeOverlayStartTime.toFixed(1)}s to {activeOverlayEndTime.toFixed(1)}s
                      </span>
                      <div className="broll-layer-chip-row">
                        <span>Cutaway</span>
                      </div>
                    </div>
                  </div>
                )}
                <div className="slider-stack">
                  {/* ── B-Roll Time Range ── */}
                  {activeOverlayHasTiming && (
                    <>
                      <label
                        className="studio-slider-label"
                        style={{ fontWeight: 700, color: "#fbbf24" }}
                      >
                        <span>⏱ Start: {Number(activeOverlay.startTime).toFixed(1)}s</span>
                        <input
                          type="range"
                          min={0}
                          max={Math.max(0, liveTimelineDuration - (activeOverlay.duration || 0.5))}
                          step={0.1}
                          value={activeOverlay.startTime}
                          onChange={e =>
                            updateOverlayTimeRange(
                              activeOverlay.id,
                              Number(e.target.value),
                              activeOverlay.duration
                            )
                          }
                          style={{ width: "100%" }}
                        />
                      </label>
                      <label
                        className="studio-slider-label"
                        style={{ fontWeight: 700, color: "#fbbf24" }}
                      >
                        <span>⏱ Duration: {Number(activeOverlay.duration).toFixed(1)}s</span>
                        <input
                          type="range"
                          min={0.3}
                          max={Math.max(0.5, liveTimelineDuration - (activeOverlay.startTime || 0))}
                          step={0.1}
                          value={activeOverlay.duration}
                          onChange={e =>
                            updateOverlayTimeRange(
                              activeOverlay.id,
                              activeOverlay.startTime,
                              Number(e.target.value)
                            )
                          }
                          style={{ width: "100%" }}
                        />
                      </label>
                    </>
                  )}

                  {!activeOverlayIsFullscreenBRoll && (
                    <>
                      <label className="studio-slider-label">
                        <span>Left: {Math.round(activeOverlay.x || 0)}%</span>
                        <input
                          type="range"
                          min={0}
                          max={100}
                          step={1}
                          value={activeOverlay.x || 0}
                          onChange={e =>
                            updateOverlayPosition(
                              activeOverlay.id,
                              "x",
                              Number(e.target.value) - Number(activeOverlay.x || 0)
                            )
                          }
                          style={{ width: "100%" }}
                        />
                      </label>
                      <label className="studio-slider-label">
                        <span>Top: {Math.round(activeOverlay.y || 0)}%</span>
                        <input
                          type="range"
                          min={0}
                          max={100}
                          step={1}
                          value={activeOverlay.y || 0}
                          onChange={e =>
                            updateOverlayPosition(
                              activeOverlay.id,
                              "y",
                              Number(e.target.value) - Number(activeOverlay.y || 0)
                            )
                          }
                          style={{ width: "100%" }}
                        />
                      </label>

                      {(activeOverlay.type === "video" || activeOverlay.type === "image") && (
                        <>
                          <label className="studio-slider-label">
                            <span>Width: {Math.round(activeOverlay.width || 0)}%</span>
                            <input
                              type="range"
                              min={10}
                              max={100}
                              step={1}
                              value={activeOverlay.width || 35}
                              onChange={e =>
                                updateOverlaySize(
                                  activeOverlay.id,
                                  "width",
                                  Number(e.target.value) - Number(activeOverlay.width || 35)
                                )
                              }
                              style={{ width: "100%" }}
                            />
                          </label>
                          <label className="studio-slider-label">
                            <span>Height: {Math.round(activeOverlay.height || 0)}%</span>
                            <input
                              type="range"
                              min={10}
                              max={100}
                              step={1}
                              value={activeOverlay.height || 35}
                              onChange={e =>
                                updateOverlaySize(
                                  activeOverlay.id,
                                  "height",
                                  Number(e.target.value) - Number(activeOverlay.height || 35)
                                )
                              }
                              style={{ width: "100%" }}
                            />
                          </label>
                        </>
                      )}
                    </>
                  )}

                  {/* ── Opacity ── */}
                  {activeOverlayHasTiming && !activeOverlayIsVideoBRoll && (
                    <label className="studio-slider-label" style={{ marginTop: "8px" }}>
                      <span>🔅 Opacity: {Math.round((activeOverlay.opacity ?? 1) * 100)}%</span>
                      <input
                        type="range"
                        min={0}
                        max={100}
                        step={5}
                        value={Math.round((activeOverlay.opacity ?? 1) * 100)}
                        onChange={e =>
                          setOverlayOpacity(activeOverlay.id, Number(e.target.value) / 100)
                        }
                        style={{ width: "100%" }}
                      />
                    </label>
                  )}

                  {/* ── Animation ── */}
                  {activeOverlayHasTiming &&
                    !activeOverlayIsVideoBRoll &&
                    activeOverlay.animation && (
                      <div style={{ marginTop: "8px" }}>
                        <span style={{ fontSize: "12px", color: "#94a3b8", fontWeight: 600 }}>
                          ✨ Animation
                        </span>
                        <div
                          style={{
                            display: "flex",
                            gap: "6px",
                            marginTop: "4px",
                            flexWrap: "wrap",
                          }}
                        >
                          <select
                            value={activeOverlay.animation.enter || "fade"}
                            onChange={e =>
                              setOverlayAnimation(activeOverlay.id, "enter", e.target.value)
                            }
                            style={{
                              padding: "3px 6px",
                              borderRadius: "6px",
                              border: "1px solid #444",
                              background: "#1a1a2e",
                              color: "#fff",
                              fontSize: "11px",
                              flex: 1,
                            }}
                          >
                            <option value="none">No Enter</option>
                            <option value="fade">Fade In</option>
                            <option value="slideLeft">Slide ←</option>
                            <option value="slideRight">Slide →</option>
                            <option value="slideUp">Slide ↑</option>
                            <option value="slideDown">Slide ↓</option>
                            <option value="zoom">Zoom In</option>
                          </select>
                          <select
                            value={activeOverlay.animation.exit || "fade"}
                            onChange={e =>
                              setOverlayAnimation(activeOverlay.id, "exit", e.target.value)
                            }
                            style={{
                              padding: "3px 6px",
                              borderRadius: "6px",
                              border: "1px solid #444",
                              background: "#1a1a2e",
                              color: "#fff",
                              fontSize: "11px",
                              flex: 1,
                            }}
                          >
                            <option value="none">No Exit</option>
                            <option value="fade">Fade Out</option>
                            <option value="slideLeft">Slide ←</option>
                            <option value="slideRight">Slide →</option>
                            <option value="slideUp">Slide ↑</option>
                            <option value="slideDown">Slide ↓</option>
                            <option value="zoom">Zoom Out</option>
                          </select>
                        </div>
                      </div>
                    )}

                  {/* ── Audio Options ── */}
                  {activeOverlayHasTiming && activeOverlay.type === "video" && (
                    <div className="broll-control-group">
                      <span className="broll-control-label">Audio rules</span>
                      <label className="broll-check-label">
                        <input
                          type="checkbox"
                          checked={activeOverlay.muteMainAudio || false}
                          onChange={e =>
                            setOverlayAudioOption(
                              activeOverlay.id,
                              "muteMainAudio",
                              e.target.checked
                            )
                          }
                        />
                        Mute main audio during overlay
                      </label>
                      <label className="broll-check-label">
                        <input
                          type="checkbox"
                          checked={activeOverlay.useOverlayAudio || false}
                          onChange={e =>
                            setOverlayAudioOption(
                              activeOverlay.id,
                              "useOverlayAudio",
                              e.target.checked
                            )
                          }
                        />
                        Use overlay audio
                      </label>
                      {activeOverlay.useOverlayAudio && (
                        <>
                          <label className="broll-check-label">
                            <input
                              type="checkbox"
                              checked={activeOverlay.audioDucking || false}
                              onChange={e =>
                                setOverlayAudioOption(
                                  activeOverlay.id,
                                  "audioDucking",
                                  e.target.checked
                                )
                              }
                            />
                            Duck main audio
                          </label>
                          <label className="studio-slider-label" style={{ marginTop: "4px" }}>
                            <span>
                              Overlay Vol:{" "}
                              {Math.round((activeOverlay.overlayAudioVolume ?? 0.7) * 100)}%
                            </span>
                            <input
                              type="range"
                              min={0}
                              max={100}
                              step={5}
                              value={Math.round((activeOverlay.overlayAudioVolume ?? 0.7) * 100)}
                              onChange={e =>
                                setOverlayAudioOption(
                                  activeOverlay.id,
                                  "overlayAudioVolume",
                                  Number(e.target.value) / 100
                                )
                              }
                              style={{ width: "100%" }}
                            />
                          </label>
                        </>
                      )}
                    </div>
                  )}

                  <div style={{ marginTop: "10px" }}>
                    <button
                      type="button"
                      className="mini-toggle-btn layer-remove-btn"
                      onClick={() => {
                        deleteOverlay(activeOverlay.id);
                        setActiveOverlayId(null);
                      }}
                    >
                      Remove Layer
                    </button>
                  </div>
                </div>
              </section>
            )}
          </div>

          <div
            style={{ "--timeline-dock-height": `${displayedTimelineHeight}px` }}
            className={`studio-pro-timeline-dock ${
              timelineDockExpanded ? "is-open" : "is-closed"
            }`}
          >
            <div className="studio-timeline-resize" role="separator" tabIndex={0}
              aria-label="Resize timeline" aria-orientation="horizontal"
              aria-valuemin={148} aria-valuemax={timelineHeightLimit} aria-valuenow={displayedTimelineHeight}
              onPointerDown={event => {
                timelineResizeRef.current = { y: event.clientY, height: displayedTimelineHeight };
                event.currentTarget.setPointerCapture(event.pointerId);
              }}
              onPointerMove={event => {
                if (!timelineResizeRef.current) return;
                setTimelineDockHeight(Math.max(148, Math.min(timelineHeightLimit,
                  timelineResizeRef.current.height + timelineResizeRef.current.y - event.clientY)));
              }}
              onPointerUp={() => { timelineResizeRef.current = null; }}
              onPointerCancel={() => { timelineResizeRef.current = null; }}
              onKeyDown={event => {
                if (!["ArrowUp", "ArrowDown"].includes(event.key)) return;
                event.preventDefault();
                event.stopPropagation();
                setTimelineDockHeight(current => Math.max(148, Math.min(timelineHeightLimit,
                  Math.min(current, timelineHeightLimit) + (event.key === "ArrowUp" ? 24 : -24))));
              }}
              title="Drag to resize the timeline; use arrow keys when focused">
              <span />
            </div>
            {workspaceMode !== "quick" ? (
              <div
                className="ap-mfx-toolbar"
                style={{ padding: "6px 12px", borderRadius: 0 }}
                data-testid="studio-bottom-dock-switcher"
              >
                <div className="ap-mfx-toolbar-group" role="tablist" aria-label="Studio bottom workbench">
                  {[
                    { id: "timeline", label: "Timeline" },
                    { id: "comp_rig", label: "Comp & Rig" },
                    { id: "comp_graph", label: "Speed / Value Graph" },
                    { id: "node_flow", label: "Signal Node Flow" },
                  ].map(tab => (
                    <button
                      key={tab.id}
                      type="button"
                      role="tab"
                      aria-selected={bottomDockView === tab.id}
                      data-testid={`dock-tab-${tab.id}`}
                      className={`ap-mfx-seg-btn ${bottomDockView === tab.id ? "is-active" : ""}`}
                      onClick={() => {
                        setBottomDockView(tab.id);
                        setTimelineDockExpanded(true);
                      }}
                    >
                      {tab.label}
                    </button>
                  ))}
                </div>
                <span style={{ fontSize: 10.5, color: "#94a3b8" }}>
                  Synced 1:1 with canonical Studio project &amp; FFmpeg render compiler
                </span>
              </div>
            ) : null}
            {bottomDockView === "comp_rig" || bottomDockView === "comp_graph" ? (
              <StudioCompWorkbench
                key={bottomDockView}
                initialGraphMode={bottomDockView === "comp_graph" ? "speed" : "value"}
                overlays={overlays}
                mainTransform={mainTransform}
                motionKeyframes={motionKeyframes}
                adjustmentLayers={adjustmentLayers}
                activeOverlayId={activeOverlayId}
                hideShyLayers={hideShyLayers}
                currentTime={previewTimelineTime}
                programmeDuration={liveTimelineDuration}
                onSelectOverlay={id => {
                  setSelectedGroupId(null);
                  setActiveOverlayId(id);
                  setComparisonMode("after");
                }}
                onChangeOverlays={next => {
                  setOverlays(next);
                  setComparisonMode("after");
                }}
                onChangeMainTransform={next => {
                  setMainTransform(next);
                  setComparisonMode("after");
                }}
                onChangeMotionKeyframes={next => {
                  setMotionKeyframes(next);
                  setComparisonMode("after");
                }}
                onChangeAdjustmentLayers={next => {
                  setAdjustmentLayers(next);
                  setComparisonMode("after");
                }}
                onInteractionStart={() => setIsDragging(true)}
                      onInteractionEnd={() => setIsDragging(false)}
                      onChangeHideShyLayers={setHideShyLayers}
                onSeek={jumpToOutputTimelineTime}
              />
            ) : bottomDockView === "node_flow" ? (
              <StudioNodeFlow videoRef={videoRef} mapTime={getPreviewTimelineTime} advancedColor={advancedColor}
                overlays={overlays}
                mainTransform={mainTransform}
                finishFx={finishFx}
                compositionOrder={compositionOrder}
                adjustmentLayers={adjustmentLayers}
                subjectVfxScenes={subjectVfxScenes}
                motionKeyframes={motionKeyframes}
                nodeFlow={nodeFlow}
                activeOverlayId={activeOverlayId}
                currentTime={previewTimelineTime}
                programmeDuration={liveTimelineDuration}
                onSelectOverlay={id => {
                  setSelectedGroupId(null);
                  setActiveOverlayId(id);
                  setComparisonMode("after");
                }}
                onChangeOverlays={next => {
                  setOverlays(next);
                  setComparisonMode("after");
                }}
                onChangeMainTransform={next => {
                  setMainTransform(next);
                  setComparisonMode("after");
                }}
                onChangeFinishFx={next => {
                  replaceFinishFx(next);
                  setComparisonMode("after");
                }}
                onChangeCompositionOrder={next => {
                  setCompositionOrder(next);
                  setComparisonMode("after");
                }}
                onChangeAdjustmentLayers={next => {
                  setAdjustmentLayers(next);
                  setComparisonMode("after");
                }}
                onChangeNodeFlow={setNodeFlow}
              />
            ) : (
            <StudioProTimeline
              duration={liveTimelineDuration}
              playbackRate={previewSpeed}
              playhead={previewTimelineTime}
              zoom={timelineZoom}
              onZoomChange={setTimelineZoom}
              editTool={timelineEditTool}
              onEditToolChange={setTimelineEditTool}
              snapping={timelineSnapping}
              onSnappingChange={setTimelineSnapping}
              linkedSelection={linkedSelection}
              onLinkedSelectionChange={setLinkedSelection}
              rippleMode={rippleMode}
              onRippleModeChange={setRippleMode}
              trackStates={trackStates}
              timelineSegments={timeline.map(clip => {
                const window = getTimelineClipWindow(clip);
                return { ...clip, startRequest: window.start, endRequest: window.end };
              })}
              activeTool={activeCreativeTool}
              sourceFrames={liveTimelineFilmstripFrames}
              onTrackStateChange={(trackId, changes) =>
                setTrackStates(current => ({
                  ...current,
                  [trackId]: { ...(current[trackId] || {}), ...changes },
                }))
              }
              overlays={overlays}
              captionSegments={liveTimelineCaptionBlocks.map(block => ({
                ...block,
                // One source caption can legitimately appear in more than one
                // trimmed timeline clip. The timeline identity must therefore
                // include the clip, not only the transcript line id.
                id: block.blockId || block.id,
                startTime: Number(block.outputStart || 0),
                duration: Number(block.outputDuration || 0),
              }))}
              soundEffects={[...soundEffects, ...linkedMotionCues]}
              motionScenes={[...motionScenes, ...threeDScenes.map(scene => ({ ...scene, is3D: true, color: scene.primaryColor, name: `3D · ${scene.text}` }))]}
              onSelectMotion={id => {
                setSelectedGroupId(null);
                if (threeDScenes.some(scene => scene.id === id)) {
                  setSelectedThreeDId(id);
                  setMotionWorkspace("3d");
                } else {
                  setSelectedMotionId(id);
                  setMotionWorkspace("design");
                }
                selectCreativeTool("motion");
              }}
              musicTrack={addMusic ? musicTrack : null}
              voiceovers={voiceovers}
              adjustmentLayers={adjustmentLayers}
              creativeEffects={
                [...(audioRemix.enabled ? [{id:'audio-remix-effect',name:`Remix · ${AUDIO_REMIX_PRESETS.find(preset=>preset.id===audioRemix.preset)?.name || audioRemix.preset}`,
                  startTime:0,duration:liveTimelineDuration,toolId:'sound'}] : []), ...(creativeEffectsEnabled
                  ? [
                      {
                        id: "signature-transformation",
                        name:
                          SIGNATURE_CREATIVE_STYLES.find(style => style.id === creativePreset)
                            ?.label || "Signature transformation",
                        startTime: 0,
                        duration: liveTimelineDuration,
                      },
                    ]
                  : []), ...subjectVfxScenes.filter(item => item.enabled !== false).flatMap(item =>
                    timeline.flatMap((clip, clipIndex) => {
                      if (String(clip.sourceClipId || clip.id) !== item.scene.sourceId) return [];
                      const window = getTimelineClipWindow(clip);
                      const start = Math.max(window.start, item.scene.sourceRange.start);
                      const end = Math.min(window.end, item.scene.sourceRange.end);
                      if (end <= start) return [];
                      return [{
                        id: `subject-${clip.id}-${item.scene.id}`,
                        name: `Subject effects · ${item.preview?.reviewed ? "Reviewed" : "Review needed"}`,
                        startTime: getTimelineOffsetForIndex(clipIndex) + start - window.start,
                        duration: end - start,
                        toolId: "composite",
                      }];
                    })
                  )]
              }
              motionKeyframes={motionKeyframes}
              graphicGroups={compositionGroups.groups}
              groupTimingRows={groupClipRows}
              compositionEntries={compositionEntries}
              defaultView="layers"
              selectedLayerId={activeOverlayId || motionTargetId}
              selectedGroupId={selectedGroupId}
              onSelectGroup={id => {
                setSelectedGroupId(id); setActiveOverlayId(null); setMotionTargetId(id);
                selectCreativeTool('composite'); setComparisonMode('after');
              }}
              onEditGroupTiming={editGroupClipTiming}
              mainTransform={mainTransform}
              onUpdateMotionProperty={(targetId, property, value) => editMotionPropertyAtPlayhead(targetId, property, value)}
              onToggleMotionPropertyKeyframe={(targetId, property) => editMotionPropertyAtPlayhead(targetId, property, undefined, true)}
              frameRate={Number(exportSettings.fps) || 30}
              onSelectMotionKeyframe={(keyId, targetId) => {
                setSelectedGroupId(compositionGroups.byId.has(targetId) ? targetId : null);
                setFocusedMotionKeyframeId(keyId);
                setMotionTargetId(targetId);
                selectCreativeTool(compositionGroups.byId.has(targetId) ? "composite" : "motion");
                if (!compositionGroups.byId.has(targetId)) setMotionWorkspace("transform");
                setComparisonMode("after");
              }}
              onUpdateMotionKeyframe={updateMotionKeyframe}
              onRemoveMotionKeyframe={removeMotionKeyframe}
              onEditMotionKeyframes={editMotionKeyframeSelection}
              motionKeyframeEditError={motionKeyframeEditError}
              audioKeyframes={audioKeyframes}
              speedKeyframes={speedKeyframes}
              onSeek={(event, exactTime = null) => {
                if (exactTime !== null && exactTime !== undefined && Number.isFinite(Number(exactTime))) {
                  jumpToOutputTimelineTime(Number(exactTime));
                  return;
                }
                const bounds = event.currentTarget.getBoundingClientRect();
                const ratio = clampNumber(
                  (event.clientX - bounds.left) / Math.max(1, bounds.width),
                  0,
                  1,
                  0
                );
                jumpToOutputTimelineTime(ratio * liveTimelineDuration);
              }}
              onOverlayMove={handleOverlayTimelineMove}
              onOverlayTrim={handleOverlayTimelineTrim}
              onOverlaySlip={handleOverlayTimelineSlip}
              onAdjustmentMove={handleAdjustmentTimelineMove}
              onAdjustmentTrim={handleAdjustmentTimelineTrim}
              onSelectOverlay={id => { setSelectedGroupId(null); setActiveOverlayId(id); }}
              onMotionMove={handleMotionTimelineMove}
              onMotionTrim={handleMotionTimelineTrim}
              onAutoGenerateMotionBeats={handleAutoGenerateMotionBeats}
              onSelectTool={toolId => toolId && selectCreativeTool(toolId)}
              onSplit={splitCurrentClipAtPlayhead}
              onRazorCut={(time, layerId) => splitCurrentClipAtPlayhead(time, layerId)}
              onSequenceEdit={commitSequenceEdit}
              splitLocked={Boolean(selectedGroupClip || trackStates[activeOverlay ? (activeOverlay.bRollMode ? "broll" : "graphics") : "video"]?.locked)}
              onTrimStart={selectedGroupClip ? () => editGroupClipTiming(selectedGroupId, 'trim-start', previewTimelineTime) : trimClipStartToPlayhead}
              onTrimEnd={selectedGroupClip ? () => editGroupClipTiming(selectedGroupId, 'trim-end', previewTimelineTime) : trimClipEndToPlayhead}
              trimLocked={selectedGroupClip ? selectedGroupClip.locked : Boolean(trackStates.video?.locked)}
              deleteLocked={selectedGroupClip ? selectedGroupClip.locked : Boolean(trackStates.video?.locked)}
              onDelete={selectedGroupClip ? () => deleteGroupClip(selectedGroupId) : deleteActiveTimelineClip}
              canDelete={selectedGroupClip ? !selectedGroupClip.locked : timeline.length > 1}
              onToggleCaptions={() => {
                const next = !autoCaptions;
                setAutoCaptions(next);
                if (next && !captionSegments.length && !captionTextOverride) {
                  const initial = resolveInitialCaptionSegments(selectedClip, selectedClip?.duration);
                  if (initial.length) setCaptionSegments(initial);
                }
              }}
              captionsActive={autoCaptions}
              onToggleSilence={() => setSilenceRemoval(!silenceRemoval)}
              silenceRemovalActive={silenceRemoval}
              onRippleCutSilence={rippleCutAllSilences}
              isRippleCutting={isRippleCuttingSilence}
              silenceRegions={silenceRegionsForTimeline}
              onUndo={handleUndo}
              onRedo={handleRedo}
              canUndo={canUndo}
              canRedo={canRedo}
              onSetDualCam={showEveryoneSplit}
              framingCuts={normalizeReframeModeCuts(reframeModeCuts)}
              speakerFocusCuts={normalizeSpeakerFocusCuts(speakerFocusCuts)}
              isDualCamActive={effectiveSmartCrop && effectiveSmartCropMode === "center"}
              onSetAutoReframe={() => openSpeakerTracking()}
              isAutoReframeActive={effectiveSmartCrop && effectiveSmartCropMode === "speaker_track"}
              onToggleDirectorMode={() => setDirectorModeActive(prev => !prev)}
              isDirectorModeActive={directorModeActive}
              onTogglePunchAtPlayhead={togglePunchInAtPlayhead}
              onAutoGeneratePunchIns={autoGeneratePunchIns}
              isPlayheadPunched={isPlayheadPunched}
              punchZones={activePunchZones}
              onAudioTrim={handleAudioTrim}
              onToggleJCutAtPlayhead={toggleJCutAtPlayhead}
              onToggleLCutAtPlayhead={toggleLCutAtPlayhead}
              hasActiveJCut={hasActiveJCut}
              hasActiveLCut={hasActiveLCut}
              musicBeatMarkers={musicBeatMarkers}
              beatSnapping={beatSnapEnabled}
              onBeatSnappingChange={setBeatSnapEnabled}
              onAlignCutsToBeat={handleAlignCutsToBeat}
              onAlignBRollToBeat={handleAlignBRollToBeat}
            />
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ViralClipStudio;
